import { unreachable } from '../shared/exhaustive';
import { appendGlazingCard } from './ui/glazingCard';
import { glazingOfPane } from './roomGlazing';
import { pickTip } from './tips';
import {
  clearSelection,
  dropFeature,
  dropFromSelection,
  featureOf,
  mergeSelections,
  MULTI_KINDS,
  ObjectSel,
  reconcileSelection as reconcileSelected,
  selectedCount,
  selectedItems,
  selectedObjects,
  selectedObjectsOf,
  selectedOf,
  selectFeature,
  SELECTION_KINDS,
  selectionCopies,
  selectionOfIds,
  selectObjects,
  selectOne,
  selectPolyEdge,
  selectRoomKeepingFeature,
  selectViewTitle,
  selectWallFeature,
  toggleObject,
} from './selection';
import {
  featureRefs,
  FEATURES,
  featureSel,
  featureTarget,
  featureTargets,
  type LiveFeature,
  liveFeature,
  partPick,
} from './roomFeatures';
import {
  createUndoHistory,
  type UndoMark as UndoMarkOf,
} from './undoHistory';
import {
  drawingOf,
  type DrawingSnapshot,
  sameSnapshot,
  snapshotOf,
} from './drawingSnapshot';
import { runTour } from './tourRunner';
import {
  drawingDimStyle,
  formatDimension,
} from './dimFormat';
import { type Pt3 } from './handles';
import {
  cursorOf,
  firstHandle,
  type HandleHit,
  handleOf,
  type HandleProbes,
  handleTakesPress,
  type HitOf,
  TAKEN_FIRST,
} from './handleHits';
import {
  CMD_CHANNEL,
  menuChannels,
} from '../shared/menus';
import {
  rotateTextBoxTo,
  textBoxCentre,
  textBoxContains,
  textBoxCorners,
} from './textBox';
import type {
  AppSettings,
  Arc,
  BackgroundImage,
  Bounds,
  Cartiglio,
  CeilingPlanView,
  ChangelogEntry,
  Connection,
  ConnectionType,
  ConnPort,
  DeletedProduct,
  DimAnchor,
  DimensionLine,
  DimKind,
  DimStyle,
  Discipline,
  Drawing,
  DrawingGroup,
  DrawingLayer,
  DrawingMeta,
  FloorDrain,
  Flooring,
  FloorKind,
  GlassSegment,
  HeaterWall,
  InnerWall,
  Lang,
  Leader,
  ObjConstraintType,
  PartMount,
  Polygon,
  Preparation,
  ProductCategory,
  ProductInstance,
  ProductTemplate,
  ProductVariant,
  ProductWriteResult,
  PropertyDef,
  Revision,
  RevisionCloud,
  SaunaDoor,
  SectionLine,
  Sheet,
  TextComment,
  Vec2,
  Wall,
  WallFeature,
  WallSide,
} from '../shared/types';
import { BUILTIN_PRODUCTS } from '../shared/builtinProducts';
import { migrateProductHeightFromGround } from '../shared/productHeights';
import {
  CATALOGUE_GROUPS,
  KNOWN_CATEGORIES,
  traitsOf,
} from '../shared/categories';
import { hashProduct } from '../main/baserow/sync';
import {
  copyVariant,
  refreshVariant,
  variantLabel,
  variantRange,
} from './variants';
import { conveyorPivot } from '../shared/steamConveyors';
import {
  aboveFloor,
  type Body,
  bodyOf,
  canTilt,
  isTilted,
  mirrorBody,
  type Placement as BodyPlacement,
  touchShift,
  turnBody,
  turnKeeping,
  type Vec3 as Point3,
  wallAgainst,
  type WallFace,
} from './tilt';
import {
  drawingsSummarized,
  productBefore,
  restorable,
  shownChange,
} from '../shared/productHistory';
import {
  CONNECTION_TYPES,
  DISCIPLINE_KINDS,
} from '../shared/types';
import { parseDxf } from './dxf';
import {
  copiedNames,
  dictDefaults,
  productName,
  renamedNames,
} from '../shared/names';
import { migrateBenchSkirting } from '../shared/benchPresets';
import {
  BEAM_DEFAULTS,
  coveKind,
  LIGHT_AMBER,
  setLightCatalogue,
  skiesOf,
  skyFibreAt,
} from './lights';
import { installIcons } from './icons';
import {
  initToolbar,
  refreshSectionVisibility,
  resetToolbar,
  setToolbarCallouts,
  setToolbarContext,
  setToolbarStyle,
  toolbarCalloutsEnabled,
  toolbarStyle,
} from './toolbar';
import {
  ANGLE_OPTION,
  CEILING_OPTION,
  commandForButton,
  CONNECTION_TYPE_OPTIONS,
  ERASE_OPTION,
  EXTENTS_OPTION,
  FIBRE_OPTION,
  FLIP_OPTION,
  HORIZONTAL_OPTION,
  KEEP_OPTION,
  OBJECT_OPTION,
  optionsPrompt,
  RIGHT_ANGLE_OPTION,
  searchKey,
  SECTION3D_OPTIONS,
  SKY_KIT_OPTIONS,
  SPOT_OPTION,
  STRAIGHT_OPTION,
  STRIP_BOTH_OPTION,
  STRIP_DOWN_OPTIONS,
  STRIP_UP_OPTIONS,
  THROUGH_OPTION,
  type ToolOption,
  UNDO_OPTION,
  ZONE_OPTION,
} from './commands';
import {
  addPoint,
  closeDraft,
  draftOptions,
  dropDraft,
  finishDraft,
  isPolylineTool,
  POLYLINE_RULES,
  type PolylineDraft,
  type PolylineRule,
  type PolylineTool,
  undoPoint,
} from './polylineDraft';
import { type Section3dPlane } from './section3d';
import {
  GESTURES,
  matchesShortcut,
  type ShortcutGroup,
  shortcutKeysLabel,
  SHORTCUTS,
} from '../shared/shortcuts';
import { setPaperDimFormat } from './schematic';
import {
  allSheetObjects,
  mirrorSheetObjects,
  schContent,
  sheetObjectsFlip,
} from './sheetObjects';
import {
  GRIP_HIT_PX,
  GRIP_SELECTED,
} from './grip';
import { renderPdfToBackground } from './pdf';
import {
  clearSharpCrops,
  scheduleSharpen,
  sharpCropFor,
} from './pdfSharpen';
import {
  dragPinnedVertex,
  polylineIsClosed,
  wallBand,
  wallFaces,
  wallFaceSnap,
  wallSnapVerts,
} from './wallTool';
import {
  addWallFeature as addDrawnWallFeature,
  WALL_DOOR_MIN,
  WALL_DOOR_WIDTH,
  WALL_FEATURE_MIN,
  wallFeatureAt,
  wallFeatureBase,
  wallFeatureQuad,
  type WallFeatureSpan,
  wallFeatureSpans,
  wallFeatureTop,
  type WallSegFrame,
  wallSegFrame,
  wallStationAt,
} from './wallFeatures';
import {
  innerWallBand,
  innerWallHitLocal,
} from './wall';
import {
  faceSnap,
  type LocalBox,
  roomFaceSnap,
  roomOuterWallFaces,
  type RoomSnap,
  roomWallFaces,
} from './roomSnap';
import {
  carryWith,
  hangsUnder,
  hangUnder,
  inWallBand,
  layOnTop,
  mountsByDrop,
  restsOnTop,
  seatBelowCeiling,
  standsBelowCeiling,
  wallMountBase,
  wallMountBox,
  wallMountTilt,
} from './mounting';
import {
  moveStep,
  type MoveStepIn,
} from './moveStep';
import {
  beginMove,
  type MoveHost,
  type MoveOptions,
  type MoveSession,
} from './moveSession';
import {
  connDiscipline,
  connectionRoute,
  connGeom,
  pipeTubes,
} from './connections';
import {
  type ConnBody,
  type ConnPick,
  connPickAt as connPickAtPure,
  type ConnPickEnv,
  connTargetAt as connTargetAtPure,
} from './connPick';
import {
  type PlanSnap,
  tagDragEdit,
} from './connDrag';
import { newConnection } from './connEdits';
import {
  addWallFeature,
  autoAxisLocks,
  benchesFollowPartitions,
  type BenchJoinState,
  DOOR_SIDE_MIN,
  dragDoorAlong,
  effectiveCornerRadius,
  equaliseGroup,
  GLASS_MIN,
  heaterWallBands,
  heaterWallsOf,
  innerWallThickness,
  isJoint,
  isPolyRoom,
  layoutWall,
  maxHeaterWallInset,
  partitionBenchCanMitre,
  partitionBenchJoinState,
  partitionFaces,
  polyBenchBandsDrawn,
  polyBenchCanMitre,
  polyBenchDepth,
  polyBenchFrame,
  polyBenchGuideVerts,
  polyBenchHandles,
  polyBenchJoinState,
  polyBenchPick,
  polyEdgeLengths,
  polyEdges,
  polygonArea,
  polyInnerBenchBands,
  polyInnerCarved,
  polyInnerWallBand,
  polyInnerWallHit,
  polyInnerWallMetrics,
  polyInnerWallSpan,
  polyOpeningBand,
  polyWallHit,
  rescaleEdgeFeatures,
  reshapeKeepsAnchor,
  roomReshapeBreak,
  segToWall,
  togglePartitionBenchJoin,
  togglePolyBenchJoin,
  wallFeatures,
  wallRuns,
  wallView,
} from './polyRoom';
import {
  Op as BoolOp,
  polygonBoolean,
} from './polybool';
import {
  addVec,
  arcThroughPoints,
  dimensionGeometry,
  dimensionOffset,
  distance,
  distToSegment,
  draftSeg,
  drawsHeaterBlock,
  ellipseFromRing,
  ellipseQuadrants,
  emptyBounds,
  expandBounds,
  gridStepMm,
  heaterGapBox,
  heaterGuard,
  inferDimKind,
  instanceToWorld,
  isFiniteBounds,
  levelTurnDeg,
  marqueeTakes,
  moveSnapOffset,
  nearestVertexWithin,
  partOuterCorners,
  pointInPolygon,
  polyBenchLength,
  ribbonContains,
  ribbonOutline,
  ringScaleBounds,
  ROTATE_STEP_DEG,
  rotatePoint,
  snapDraftGuides,
  snapRotationDeg,
  snapRotationRad,
  subVec,
  toInstanceLocal,
} from './geometry';
import { wallThicknessOf } from './dent';
import {
  benchDepthFromSlats,
  Box,
  featureAt,
  MAX_BENCH_TIERS,
  MIN_BENCH_DEPTH,
} from './benches';
import {
  clampDoorOffset,
  defaultDoor,
  doorById,
  doorFlipIconLocal,
  doorHitLocal,
  doorParts,
  flipDoor,
  mainRoomDoor,
  resizeDoorToJamb,
  roomDoors,
} from './door';
import {
  clampGlassToDoor,
  clampToWall,
  GLASS_STROKE,
  glassHitLocal,
  glassPolysLocal,
  mergeGlassSegments,
  openingHitLocal,
  wallHit,
} from './glass';
import { darkenHex } from './hexTone';
import {
  ANNOTATION_COLOR,
  ceilingViewTitle,
  COMMENT_FONT_MM,
  CONNECTION_COLORS,
  defaultWallColor,
  DIM_TAG_FACE_EDGE,
  DIM_TAG_WALL_OVERALL,
  dimLabelProbeAt,
  dimLabelTagAt,
  Handle,
  heatedBenchSideLengths,
  instanceCorners,
  instanceHandles,
  noHits,
  pdfBoxCorners,
  pdfBoxHandles,
  pdfRotationKnobScreen,
  render,
  renderBackground,
  type RenderHits,
  type RenderOptions,
  type RepeatPreview,
  REVCLOUD_COLOR,
  rotationHandleScreen,
  type SchedTableKind,
  setCursorReadout,
  shapeBBoxHandles,
  shapeRotationKnob,
  SNAP_KIND_LABEL,
  supportFigureAt,
  TEXTBOX_CORNER_DEFAULT,
} from './render';
import {
  heaterHoles,
  regenerateRoom,
  roomFixtureOf,
  syncRoomFixtures,
} from './roomRegen';
import { buildMultiPartPlacement } from './multipart';
import {
  adoptOnLoad,
  placeCopy,
} from './catalogueCopy';
import {
  clickTarget,
  groupables,
  groupMembers,
  groupSelected,
  outermostGroup,
  relate,
  relatedTo,
  ungroupSelected,
  unrelate,
} from './groups';
import {
  arrange as arrangeLayers,
  copyInnerOrder,
  hasObject,
  type ItemKind,
  type LayerEnv,
  layerItems,
  type LayerModel,
  layerModel,
  layerName,
  layerObjectIds,
  type LayerUnit,
  materializeLayers,
  moveToLayer,
  onHiddenLayer,
  sendSectionBelow,
  setLayerFlag,
} from './layers';
import { renderRegion } from './print';
import { noteRecoveryChange } from './recovery';
import {
  connectionSchedule,
  lightingRows,
  preparationRows,
  preparationSchedule,
  type ScheduleGroup,
  scheduleNumbers,
  scheduleStrings,
  typeLabel,
} from './schedule';
import {
  polygonWord,
  wallWord,
} from './naming';
import {
  keepInRoom,
  resizeByHandle,
  type ResizeEnv,
  resizeTo,
} from './resize';
import { defaultSaunaHeight } from './saunaLayout';
import {
  paperViewOf,
  screenToPaper,
  viewportAt,
  vpModelViewport,
} from './sheets';
import { STENAL_LOGO_DATA_URI } from './logo';
import {
  defaultCartiglio,
  objectCartiglio,
} from '../shared/cartiglio';
import {
  holdRoomSections,
  type SecPart,
  type SecSrc,
  SECTION_BOX_PAD_PX,
  sectionBoxRing,
  sectionLabel,
  sectionLineVertices,
  type SectionPipe,
  sectionRotateHandle,
  sectionSourceBoxes,
  sectionStackAt,
  sectionVertices,
  sectionView,
  sectionViewOutline,
  srcKey,
  srcOf,
} from './section';
import {
  acrossCut,
  fromPlaneFrame,
  levelOrPlumb,
  onCut,
  PLACE_RULES,
  type PlaceRules,
  PLAN_PLANE,
  planeMove,
  planePoint,
  sectionOf,
  sectionScaleMap,
  tiltAxis,
  type ToolPlace,
  toPlaneFrame,
  viewMirror,
  type WorkPlane,
} from './workPlane';
import {
  type FeatureSpot,
  featureSpot,
  placeFeature,
  placeWallFeature,
  wallFeatureSpot,
} from './featureMove';
import {
  ceilingViewDrawing,
  liesAboveCeiling,
  rcpHeightOf,
  rcpOutline,
  type RcpPiece,
  rcpWorld,
  setRcpHeight,
} from './rcp';
import {
  confineToPolygon,
  edgeDistances,
  floorIsSloping,
  floorLabel,
  polygonArea as floorPolyArea,
  polygonCentroid as floorCentroid,
} from './flooring';
import {
  clipboardHasContent,
  serializeClipboard,
} from './clipboardSerial';
import {
  arcSamplePts,
  copyObjects,
  type CopyResult,
  type KindObjects,
  movableOf,
  objectAdapters,
  objectsOf,
  type ObjItem,
  outlineOf,
  pastedSelection,
  type PointMap,
  scaling,
  STORED_KINDS,
  transformItems,
  translation,
} from './drawingObjects';
import {
  editAllows,
  type EditKind,
} from './editMode';
import {
  cycleNext,
  frontPicks,
  hoverOver,
} from './viewPicks';
import {
  type ClickTake,
  clickTake,
  type FrontPick,
  frontWhere,
  type ObjRef,
  orderPickStack,
  type PickStack,
  reachesRoom,
  reachesTable,
  type RefFeature,
  refItem,
  refKey,
} from './pickStack';
import { inlineEditor } from './ui/inlineText';
import {
  drawingWithDefaults,
  upgradeDrawing,
  type UpgradeEnv,
  upgradeParts,
} from './drawingUpgrade';
import {
  DEFAULT_WALL_HEIGHT,
  externalHeight,
  glassPaneBase,
  glassPaneTop,
  heaterHeight,
  heaterWallBase,
  innerWallBase,
  objectHeight,
  openingArchitrave,
  openingSill,
  partHeight,
  partSpan,
  polygonBase,
  polygonHeight,
  roomBase,
  setObjectHeight,
  wallBase,
  wallHeight,
} from './heights';
import { Viewer3D } from './three/viewer3d';
import { buildScene } from './three/scene3d';
import { Viewport } from './viewport';
// The host boundary: `platform` is Electron's preload bridge in the desktop
// build and the web adapter in the browser build (see platform.ts).
import { platform } from './platform';
import {
  applyStaticI18n,
  getUiLang,
  setButtonEnabled,
  setButtonNote,
  setUiLang,
  tr,
  trIn,
  trMessage,
  UI_LANG_NAMES,
} from './i18n';
import {
  ceilingCuts,
  ceilingSnapMarks,
  hasCeiling,
  roomCrown,
  roomVolumeM3,
  setCeilingHeight,
  setDirectionWall,
} from './ceiling';
import {
  badgeAt,
  CONSTRAINT_META,
} from './objConstraints';
import {
  installDialogKeys,
  showAlert,
  showConfirm,
  topDialog,
} from './dialog';
import {
  isElecRoom,
  preparationTypes,
} from './elecSchematic';
import {
  backdropChanged,
  beginGesture,
  cancelGesture,
  changes,
  commitGesture,
  type DragMode,
  dragTravelled,
  drawingRev,
  dropDrag,
  enterEdit,
  installCore,
  instLocked,
  invalidate,
  leaveEdit,
  type Movable,
  newId,
  notePress,
  objHidden,
  objLocked,
  objUnpickable,
  pickRoomCorner,
  pickRoomWall,
  pushHistory,
  redrawBackdrop,
  renderProperties,
  repaint,
  roomsToEdit,
  type SnapKind,
  startDrag,
  state,
  stepDrag,
  takeRepaint,
  type Tool,
  type ViewTitleRef,
  vp,
} from './ui/core';
import {
  paneReach,
  planAim,
  type ToolAim,
  wallReach,
} from './toolAim';
import {
  keyCommandLetter,
  keyCopy,
  keyCut,
  keyCycleConstraintPiece,
  keyCycleSelection,
  keyDelete,
  keyDuplicate,
  keyEditMode,
  keyEnterUnderWay,
  keyEscapeTool,
  keyEscArmedPick,
  keyEscUnderWay,
  keyFinishWork,
  keyGroup,
  keyHelp,
  keyInField,
  keyNudge,
  keyNudgeUpDown,
  keyRepeatLast,
  keyRotate,
  keySelectAll,
  keySheet,
  keyTextBoxTyping,
  keyTitleEditor,
  keyTypedEntry,
  keyUndoRedo,
} from './ui/globalKeys';
import {
  commitOffset,
  handleModifyClick,
  modifyTools,
  offsetPreview,
  pickModifyObject,
} from './ui/modifyTools';
import {
  activeSheet,
  addSchematicImageFromData,
  drawingSheets,
  exportAllSheetsPdf,
  exportSheetPdf,
  insertSchematicImage,
  paperView,
  paperZoomAt,
  printAllSheets,
  printSheet,
  renderSheet,
  resetPaperView,
  schemSel,
  schemSelSet,
  schLineClosed,
  type SchTool,
  schTool,
  selectedViewport,
  setSchemSel,
  setSchemSelSet,
  setSchTool,
  setViewTab,
  SHEET_OBJECTS,
  sheetDblClick,
  sheetMouseDown,
  sheetMouseMove,
  sheetMouseUp,
  sheetSpace,
  sheetWheel,
  toggleSchTool,
} from './ui/sheetSpace';
import {
  copySchematicSelection,
  deleteSchematicSelected,
  pasteSchematicClipboard,
  schFinishLine,
  schFinishTextBox,
  schWaypointAt,
  sheetSchematic,
  showSheetObjectMenu,
} from './ui/sheetSchematic';
import {
  armElecTool,
  cancelElecDrag,
  elecLetGo,
  elecModelNow,
  elecSheet,
  insertElecView,
} from './ui/elecSheet';
import {
  armed,
  armedCursor,
  armedIs,
  armedPrompt,
  armedTakesClick,
  armPick,
  cancelArmed,
  downArmedPick,
  moveArmedPick,
  settleArmed,
} from './ui/armedPick';
import {
  appendCeilingCard,
  cancelCeilingArrowDrag,
  ceilingArrowAt,
  ceilingArrowDropAt,
  ceilingMarksNow,
  ceilingNoteText,
  ceilingPickRoom,
  ceilingRoom,
  ceilingUi,
  ceilingWallHover,
} from './ui/ceilingUi';
import {
  deleteEditedUnderlay,
  deleteImportedPlan,
  deleteSelected,
  deleteSheetSelection,
  modelSelectionDeletable,
  removeObjectsById,
  sheetSelectionDeletable,
} from './ui/deleteSelection';
import {
  canRemoveVertex,
  type EditableShape,
  editingVertexShape,
  enterShapeVertexEdit,
  insertNode,
  insertRoomCorner,
  insertVertexInShape,
  isPolygonShape,
  nearestSegment,
  nearestSegmentRings,
  nodeShim,
  otherRingVertices,
  pointInShape,
  recenterPolyRoom,
  removeNode,
  removeRoomCorner,
  removeVertexFromShape,
  renderEdgeProperties,
  renderPolyRoomEdgeProperties,
  renderPolyRoomVertexProperties,
  selectedEditableShape,
  setShapeRing,
  settleNodeRun,
  shapeRings,
  vertexAtScreen,
  vertexAtScreenRings,
} from './ui/vertexEdit';
import {
  renderViewTabs,
  scheduleSheetThumbs,
} from './ui/sheetStrip';
import {
  appendLightingCopyCard,
  appendLightLevelsCard,
  appendProductPropertiesCard,
  appendRoomBenchLayoutCard,
  appendRoomColoursCard,
  appendRoomFinishCard,
  appendRoomWoodCard,
  luxOverlay,
  toggleLux,
} from './ui/roomCards';
import {
  appendSkirtingCard,
  applyBenchProfile,
  buildSlatSection,
  defaultSteamPreset,
  newPolyBench,
} from './ui/slatTables';
import {
  benchOf,
  type BenchPatch,
  type ProductBenchLeg,
  roomBenchOf,
  type SlatPatch,
} from './roomBench';
import {
  removeRoomBench,
  renderBenchPanel,
} from './ui/benchPanel';
import { downBenchRoundTool } from './ui/benchRoundTool';
import {
  downSculptBenchTool,
  dropSculptStart,
  sculptToolHint,
  sculptToolStarted,
} from './ui/sculptBenchUi';
import {
  alignSelection,
  attachSelection,
  distributeSelection,
  selectedItemCount,
} from './ui/arrange';
import {
  appendFloorGrilleCard,
  downFloorGrilleTool,
} from './ui/floorGrilleUi';
import {
  appendBenchEndsCard,
  downBackrestTool,
  downBenchEndTool,
  downFixture,
  fixtureHitTest,
  fixtureOutline,
  reconcileFixtures,
  renderFixturePanel,
  selectedFixtureNow,
} from './ui/roomFixtures';
import {
  downNodeBenchJoint,
  nodeBenchJointAt,
} from './ui/nodeBenchJoints';
import {
  addHeaterWall,
  applyHeaterCoverSide,
  heaterCoverHoverAt,
  heaterCoverHoverWorld,
  removeHeaterWall,
} from './ui/heaterWalls';
import {
  adoptSystemClipboard,
  type Clipboard,
  clipboardUi,
  type ClipSpace,
  copySelection,
  flashClipboardElsewhere,
  flashNotCopied,
  layerIdSet,
  PASTE_OFFSET,
  pasteObjectsFromSystem,
  restoreClipboards,
  saveClipboards,
  selectionClipboard,
} from './ui/clipboard';
import { showObjectContextMenu } from './ui/contextMenus';
import {
  nextPoint,
  updateDistEntry,
} from './ui/distanceEntry';
import {
  alignMovedUnderlay,
  centroidSkip,
  movingWith,
  resizeHandleSnap,
  snapCandidates,
  snapDimPoint,
  snapInstanceOffset,
  snapMoveOffset,
  snapPoint,
  snapPointAnchored,
  snapSetUi,
} from './ui/snapSet';
import {
  appendCategoryFields,
  runTakesHeightRow,
  saunaGlassAreaM2,
  saunaPowerKw,
} from './ui/categoryFields';
import { renderSheetProperties } from './ui/sheetPanel';
import {
  buttonRow,
  colorRow,
  deleteRow,
  evalFormula,
  heightFromGroundRow,
  infoRow,
  type LenUnit,
  nameRow,
  noteRow,
  numberRow,
  opacityRow,
  parseMeasure,
  sectionTitle,
  selectRow,
  sliderRow,
  textFieldRow,
} from './ui/panelRows';
import { renderMultiSelectionPanel } from './ui/multiSelectionPanel';
import {
  finishTextComment,
  openTextEditor,
  pendingTextBoxPreview,
  resizeText,
  setTextCommentHeight,
  textBoxes,
  textHandleAtScreen,
  textKnobAt,
  textLeaderHandleAt,
} from './ui/textBoxes';
import {
  deleteEditorShape,
  drawEditHost,
  drawEditInst,
  drawEditPolyAt,
  drawEditWallAt,
  drawingEditor,
  editingDrawEditShape,
  editorShapesZOrdered,
  enterDrawingEdit,
  exitDrawingEdit,
  hitDrawEditHandle,
  localPolyToWorld,
  localWallToWorld,
  nextDrawZ,
  reconcileDrawEditHost,
  reorderEditorShape,
  requestCancelDrawEdit,
  selectedDrawEditShape,
  selectedEditorShapeId,
  syncDrawEditChrome,
  wireDrawEditBanner,
} from './ui/drawingEditor';
import {
  appendConstraintStatus,
  gcsDragVertex,
  hoverConstraintSeg,
  polyRingCons,
  ringHasLocks,
  roomRingCons,
  tryApplyPendingRelation,
} from './ui/ringConstraints';
import {
  closeProductModal,
  finishPolygonPick,
  imageSize,
  npAddPart,
  openProductModal,
  saveNewProduct,
} from './ui/productEditor';
import {
  cartigli,
  loadStandards,
  wireStandards,
} from './ui/standards';
import {
  arcTab,
  arrayPolarTab,
  arrayRectTab,
  drawingTab,
  filletTab,
  transformTab,
} from './ui/drawingTab';
import {
  CLOSED_OUTLINE_TOOLS,
  FEATURE_ONLY_MOVES,
  type ToolContext,
  toolReason,
} from './toolNeeds';
import {
  closeCellEditor,
  curScheduleLayout,
  ensureScheduleTable,
  findPreparation,
  labelShown,
  openCellEditor,
  schedKindOf,
  schedTableId,
  scheduleCellHitAt,
  scheduleTableBodyAt,
  scheduleTableHandleAt,
  setPartName,
  setTypedLabel,
  tableFor,
} from './ui/scheduleTables';
import {
  endGripDrag,
  gripAt,
  gripOwner,
  gripUi,
  selectionGrips,
  type SelGrip,
  stepGripWork,
} from './ui/grips';
import {
  appendObjectConstraintsCard,
  constrainClick,
  constrainFirst,
  constrainOptions,
  constrainSym,
  constraintBadgeBoxes,
  constraintBadges,
  constraintFlash,
  constraintHighlight,
  constraintObjName,
  constraintPieceAt,
  constraintSolved,
  constraintUi,
  editConstraintValueAt,
  isFixedObject,
  pickConstraint,
  reconcileConstraints,
  renderConstraintPanel,
  renderConstraintsTab,
  resetConstrainTool,
  selectedConstraint,
  setConstraintSnapMarker,
  toggleFix,
  withConstrainedPartners,
} from './ui/constraintUi';
import {
  addPipeBendInView,
  connDraftSnap,
  connectionsAt,
  connectionUi,
  connEdit,
  connEditCtx,
  connGeomOf,
  connSticky,
  draftEnds,
  flashConnectionsDeleted,
  hitConnection,
  hitConnectionWaypoint,
  newConnectionId,
  pickableConnTagAt,
  pickUpBend,
  pipeTypeChoices,
  removePipeBendInView,
  renderAllConnections,
  renderConnectionPanel,
  renderConnectionsTab,
  renderPolyConnections,
  selectedConnection,
  undoableLiveEdit,
} from './ui/connectionUi';
import {
  endSectionHandleDrag,
  handleWork,
  paintSectionHandles,
  pressSectionHandle,
  sectionHandleAt,
  stepHandleWork,
} from './ui/handleWork';
import {
  cancelUnderWay,
  checkUnderWay,
  commitTypedUnderWay,
  dropUnderWay,
  escUnderWay,
  finishArmedOffUnderWay,
  finishUnderWay,
  moveUnderWayAt,
  pressUnderWay,
  underWayNow,
  underWayPrompt,
  underWayRow,
} from './ui/underWayUi';
import {
  type UnderWayRow,
  type WorkKind,
} from './underWay';
import {
  closeView3d,
  cycle3d,
  flipSection3d,
  openStandardViews,
  paintOverlay3d,
  renderSection3dPanel,
  scheduleView3dRefresh,
  section3dMode,
  setSection3dMode,
  startSection3d,
  syncView3dSection,
  syncView3dSelection,
  to3d,
  toggleXray3d,
  toPlan,
  view3d,
  view3dHiddenCategories,
  viewer3d,
  zoomToSelection3d,
} from './ui/view3d';
import { hover3dNow } from './ui/pointer3dUi';
import {
  armPlacement3d,
  catalogueDrag,
  clearDropPreview,
} from './ui/drop3d';
import {
  doorAlong,
  doorFootprint,
  doorHit,
  doorIcons,
  resizeDoorTo,
  roomDoor,
} from './roomDoor';
import {
  isolateSelection3d,
  showAll3d,
} from './ui/isolate3d';
import {
  measure3dCount,
  resetMeasure3d,
} from './ui/measure3dTool';
import {
  resetZoom3d,
  tool3dRowOf,
  zoom3dStarted,
} from './ui/tools3d';
import {
  benchSupportTool,
  cancelSupportMove,
  downBenchSupportTool,
  downSupportMark,
  dragMoveSupportMove,
  dragMoveSupportRow,
  reconcileSupports,
  removeSupport,
  renderSupportPanel,
  selectedSupportNow,
  supportFiguresShown,
  supportHoverAt,
  supportQuietDims,
  supportsAfterPress,
  supportsBaseline,
  supportSpacingsNow,
  supportTargetAt,
} from './ui/benchSupportTool';
import {
  cancelSculptTrestleDrag,
  dragMoveSculptTrestle,
  endSculptTrestleDrag,
  sculptTrestleTool,
} from './ui/sculptTrestleTool';
import {
  selectedColorTargets,
  startCopyColors,
} from './ui/copyColors';
import { curLang } from './ui/nameFields';
import {
  doc,
  exportName,
  regeneratePdfBaseRasters,
  wireDocument,
} from './ui/document';
import {
  applyChannelOption,
  channelHoverAt,
  channelToolHint,
  channelToolOptions,
  downChannel,
  downChannelTool,
  downCoveTool,
  downKitStep,
  downLightTag,
  downLightToggle,
  downRcpSpot,
  downSkyMarginGrip,
  downSkyTool,
  downSpot,
  downSpotFigure,
  downSpotTool,
  downStripTool,
  dragMoveChannel,
  dragMoveLightTag,
  dragMoveSpot,
  dropSpotDrag,
  endChannelDrag,
  endSpotDrag,
  finishChannel,
  finishKitStep,
  finishSkyZone,
  isStripPos,
  kitStep,
  kitStepHint,
  lightTemplate,
  lightTools,
  pickedLightsNow,
  skyToolHint,
  spotAidWorld,
  spotDrag,
  stripHoverAt,
  stripToolAim,
  stripToolFacing,
  stripToolHint,
} from './ui/lightTools';
import {
  addRingRelation,
  cornerRadiusDimAt,
  dimRingRef,
  dragWallSpanJoint,
  type FaceDimTarget,
  faceDimTarget,
  findDistanceRelation,
  innerWallDimKind,
  jambJoint,
  openDimEditor,
  openFaceDimEditor,
  openFigureEditor,
  openWallDimEditor,
  removeRingRelation,
  ringRefCons,
  ringRefPts,
  ringRefRelations,
  showWallDimMenu,
  wallDimKindAt,
  wallSpanJointAt,
  wireDimEditor,
} from './ui/dimEditor';
import {
  groupOutlinePts,
  hoverFeatureRing,
  hoverGroupId,
  hoverHeaterWall,
  hoverInnerWall,
  hoverPartId,
  numberedInstanceNames,
  partsTab,
  renderPartsTab,
  selectBackgroundGeom,
} from './ui/partsTab';
import {
  copyTitleObjSelection,
  deleteSelectedTitleObj,
  dropTitleObject,
  pasteTitleObjClipboard,
  renderTitleCatalog,
  selectedTitleObjs,
  showTitleObjMenu,
  swapTitleCart,
  titleEditor,
} from './ui/titleEditor';
import {
  adoptPipeTypes,
  drawingInstanceCount,
  postUpdateFixups,
  prepId,
  productGroupOf,
  productParts,
  reconcileGroupFromTemplate,
  updateDrawingFromTemplate,
} from './ui/catalogSync';
import {
  explodable,
  explodeSelection,
  joinSelection,
} from './ui/explodeJoin';
import {
  cmdLog,
  wireCommandBar,
} from './ui/commandBar';
import {
  type HitCtx,
  type HitKind,
  hitsOf,
  inSectionView,
  instanceContains,
  instanceHalfExtents,
  onSectionLine,
} from './hitRules';
import {
  type BenchTarget,
  benchTargetIn,
  doorToAdd,
  doorToAddPolys,
  heaterWallAt,
  heaterWallTargetAt,
  innerWallTargetIn,
  nearestWallAt,
  type NewTier,
  nextTierPolys,
  paneTargetAt,
  partitionAt,
  partitionBenchAt,
  roomAt,
} from './roomTargets';
import {
  anchorAt,
  followVertices,
  heaterOutlines,
  instanceSnapPoints,
  type SnapCandidate,
} from './anchors';

installCore({ undo: () => undoSteps, changed: () => scheduleView3dRefresh(), renderProperties: buildPropertiesPanel, updateStatus, flashStatus, flashToolStatus, layers });

// A category's name, colour, catalogue group and discipline are its traits
// (shared/categories.ts). The placed objects of a category with a discipline
// show and hide with that discipline's toggle.
export const CATEGORY_DISCIPLINE: Partial<Record<ProductCategory, Discipline>> = Object.fromEntries(
  KNOWN_CATEGORIES.flatMap((c) => { const d = traitsOf(c).discipline; return d ? [[c, d]] : []; }),
);
/** Whether an instance is hidden because its discipline's toggle is off. */
export function isDisciplineHidden(cat: ProductCategory): boolean {
  const disc = traitsOf(cat).discipline;
  return !!disc && state.disciplineFilters[disc] === false;
}

export const SNAP_PX = 12; // snap threshold in screen pixels
export const HANDLE_PX = GRIP_HIT_PX; // resize-handle pick radius in screen pixels (grip.ts)
// When an already-selected room is smaller than this on screen (i.e. zoomed out),
// a press anywhere over its body moves it, rather than the thin wall strip being
// the only grab region or a bench band stealing the click. Zoomed in past this it
// is large enough that benches stay individually selectable.
const GRAB_MOVE_PX = 220;
const EDGE_PICK_PX = 18; // segment (edge) pick distance in screen pixels — wider than a
                         // handle so a wall/edge is easy to click; corners still win, as
                         // polyEdgeAt reserves the edge ends for the vertex handles

// ---- Application state ----

/** The four flooring tools and the FloorKind each one draws. */
const FLOOR_TOOLS: Record<string, FloorKind> = {
  'floor-tiled': 'tiled-flat',
  'floor-tiled-slope': 'tiled-slope',
  'floor-screed': 'screed-flat',
  'floor-screed-slope': 'screed-slope',
};
function floorToolKind(tool: string): FloorKind | null { return FLOOR_TOOLS[tool] ?? null; }

/** Each toolbar tool and the button that starts it. `setTool` lights the active
 *  one from this, and repeating the last tool clicks it, so a repeat runs the
 *  exact setup the toolbar does. Every tool has an entry, so a new tool does not
 *  typecheck until it names its button; null for one with none (Select, room and
 *  row placement from a dropped product, the print window the print dialog arms),
 *  which is never offered as a repeat. */
const TOOL_BUTTONS: Record<Tool, string | null> = {
  dimension: 'btn-dimension',
  section: 'btn-section',
  'section-3d': 'btn-section-3d',
  rcp: 'btn-rcp',
  'ceiling-top': 'btn-ceiling-top',
  text: 'btn-text',
  glass: 'btn-glass',
  opening: 'btn-opening',
  door: 'btn-door',
  'inner-wall': 'btn-inner-wall',
  bench: 'btn-bench',
  'bench-end': 'btn-bench-end',
  'bench-round': 'btn-bench-round',
  'bench-support': 'btn-bench-support',
  'heater-wall': 'btn-heater-wall',
  backrest: 'btn-backrest',
  'sculpt-bench': 'btn-sculpt-bench',
  'floor-grille': 'btn-floor-grille',
  'light-channel': 'btn-light-channel',
  'light-cove': 'btn-light-cove',
  'light-sky': 'btn-light-sky',
  'light-spot': 'btn-light-spot',
  'light-strip': 'btn-light-strip',
  'light-strip-up': 'btn-light-strip-up',
  connection: 'btn-connection',
  'pdf-scale': 'btn-pdf-scale',
  polygon: 'btn-polygon',
  'floor-tiled': 'btn-floor-tiled',
  'floor-tiled-slope': 'btn-floor-tiled-slope',
  'floor-screed': 'btn-floor-screed',
  'floor-screed-slope': 'btn-floor-screed-slope',
  circle: 'btn-circle',
  wall: 'btn-wall',
  line: 'btn-line',
  'revision-cloud': 'btn-revcloud',
  rectangle: 'btn-rectangle',
  offset: 'btn-offset',
  trim: 'btn-trim',
  fillet: 'btn-fillet',
  transform: 'btn-transform',
  'array-rect': 'btn-array-rect',
  'array-polar': 'btn-array-polar',
  arc: 'btn-arc',
  leader: 'btn-leader',
  measure: 'btn-measure',
  mirror: 'btn-mirror',
  move: 'btn-move',
  copy: 'btn-copy',
  rotate: 'btn-rotate',
  zoom: 'btn-zoom-window',
  constrain: 'btn-constrain',
  'bench-poly': null,
  'poly-room': null,
  'print-window': null,
  'rect-room': null,
  repeat: null,
  select: null,
};

/** Where each tool works (workPlane.ts `ToolPlace`): on the plan's canvas as
 *  drawn, on the plan alone, in the plane of the view it is used in (by a base
 *  point), and in the 3D view. A Record over Tool, so a new tool does not compile
 *  until it says; `placeOf` reads what that means (`PLACE_RULES`). */
const TOOL_PLACE = {
  arc: 'canvas',
  'array-polar': 'plane+3d',
  'array-rect': 'plane+3d',
  backrest: 'canvas+3d',
  'sculpt-bench': 'canvas+3d',
  bench: 'canvas+3d',
  'bench-end': 'canvas+3d',
  'bench-round': 'canvas+3d',
  'bench-poly': 'canvas',
  'bench-support': 'canvas+3d',
  'ceiling-top': 'canvas',
  circle: 'canvas',
  connection: 'canvas',
  constrain: 'plan',
  copy: 'pick+3d',
  dimension: 'canvas',
  door: 'canvas+3d',
  fillet: 'plan',
  'floor-grille': 'canvas+3d',
  'floor-screed': 'canvas',
  'floor-screed-slope': 'canvas',
  'floor-tiled': 'canvas',
  'floor-tiled-slope': 'canvas',
  glass: 'canvas+3d',
  'heater-wall': 'canvas+3d',
  'inner-wall': 'canvas+3d',
  leader: 'canvas',
  'light-channel': 'canvas',
  'light-cove': 'canvas+3d',
  'light-sky': 'canvas+3d',
  'light-spot': 'canvas+3d',
  'light-strip': 'canvas+3d',
  'light-strip-up': 'canvas+3d',
  line: 'canvas',
  measure: 'canvas+3d',
  mirror: 'plane+3d',
  move: 'pick+3d',
  offset: 'plan',
  opening: 'canvas+3d',
  'pdf-scale': 'canvas',
  'poly-room': 'canvas',
  polygon: 'canvas',
  'print-window': 'canvas',
  rcp: 'canvas',
  'rect-room': 'canvas',
  rectangle: 'canvas',
  repeat: 'canvas',
  'revision-cloud': 'canvas',
  rotate: 'pick',
  section: 'canvas+3d',
  'section-3d': '3d',
  select: 'plane+3d',
  text: 'canvas',
  transform: 'plane+3d',
  trim: 'plan',
  wall: 'canvas',
  zoom: 'canvas+3d',
} as const satisfies Record<Tool, ToolPlace>;

/** The tools whose clicks in the 3D view are the scene's (pointer3d.ts):
 *  Select, Transform and the arrays — each a row of ui/tools3d.ts `SCENE_3D`. */
export type ToolOnScene3d = Extract<Tool, 'array-polar' | 'array-rect' | 'select' | 'transform'>;
/** The tools a click in the 3D view works (ui/tools3d.ts, `TOOL_3D`): every
 *  tool whose place works in 3D but those (`ToolOnScene3d`). */
export type ToolIn3d = Exclude<{ [K in Tool]: PlaceRulesOf<(typeof TOOL_PLACE)[K]>['in3d'] extends true ? K : never }[Tool], ToolOnScene3d>;
type PlaceRulesOf<P extends ToolPlace> = (typeof PLACE_RULES)[P];

/** What a tool's place means: whether it works in 3D, in a view's plane, on the
 *  plan alone, by a base point. */
export function placeOf(tool: Tool): PlaceRules {
  return PLACE_RULES[TOOL_PLACE[tool]];
}

/** The last toolbar tool started — what Enter / Space repeats from an idle bar. */
let lastTool: Tool | null = null;

// Anchors captured for the dimension tool's two pending endpoints (so the
// finished dimension stays attached to whatever instance it snapped to).
let pendingARef: DimAnchor | undefined;
let pendingBRef: DimAnchor | undefined;



// The Measure tool's picked points (read-only; never added to the drawing).
let measurePts: Vec2[] = [];
// Mirror tool: the mirror line's first point, and whether the source is erased
// (AutoCAD's default keeps it: the mirror is a copy).
let mirrorFrom: Vec2 | null = null;
/** The plane the mirror line's first point was clicked in. */
let mirrorPlane: WorkPlane = PLAN_PLANE;
let mirrorErase = false;
// ZOOM (window): its first corner.
let zoomFrom: Vec2 | null = null;
// Move / Copy / Rotate by a picked base point (AutoCAD MOVE / COPY / ROTATE). The
// selection previews live, in a move session (`pickMove`) begun at the base pick:
// what it has applied since, the room contents it carries, the copy Copy leaves.
export let pickBase: Vec2 | null = null;
/** The plane the pick works in: where its base point was clicked. */
export let pickPlane: WorkPlane = PLAN_PLANE;
/** In a section view, the room's (or drawn wall's) features the pick moves
 *  instead of whole objects, where each started. */
let pickFeatures: FeatureMove[] | null = null;
/** Rotate's Horizontal option: the second click is a point, and the selection
 *  turns so the line from the pivot to it is horizontal (level, in a section). */
let rotateLevel = false;
/** A Rotate picked in a section view: the tilt it is (where each part started)
 *  and the angle it has turned. */
let pickTilt: { run: TiltRun; deg: number } | null = null;
let pickExcept: Set<string> = new Set();
/** Where an undo step began (undoHistory.ts), over main.ts's snapshots. */
export type UndoMark = UndoMarkOf<DrawingSnapshot>;
/** The live pick's move. Its step starts at a mark, not a gesture: the base
 *  click's own mouseup commits (and clears) any open gesture. */
let pickMove: SelectionMove | null = null;



// Link width/height (in the panel AND when dragging resize handles): changing one
// scales the other proportionally. On by default.
let dimChained = true;
/** Link W & H for a handle drag: the toggle, or Shift held (PowerPoint keeps
 *  proportions with Shift) — `drawConstrain` is the live Shift state. */
function linkWH(): boolean { return dimChained || drawConstrain; }

// ---- Drawing editor mode ----
// Move / resize of a selected local shape (drawing editor). Snapshots are in the
// instance's local frame; the resize handle/bbox drive the bounding-box scale.
let drawEditDragStart: Vec2[] = [];
let drawEditHolesStart: Vec2[][] = [];

/** Show the distance and angle between the two Measure-tool points. */
function reportMeasure(): void {
  if (measurePts.length < 2) return;
  const [a, b] = measurePts;
  const dx = b.x - a.x, dy = b.y - a.y;
  const dist = Math.hypot(dx, dy);
  const ang = ((Math.atan2(dy, dx) * 180) / Math.PI + 360) % 360;
  const style = drawingDimStyle(state.drawing), len = (v: number): string => formatDimension(v, style);
  flashToolStatus(`${tr('Measure')}: ${len(dist)}  ·  Δx ${len(Math.abs(dx))}  Δy ${len(Math.abs(dy))}  ·  ${ang.toFixed(1)}°`);
}

/** The arrow keys: the selection (and what its rooms carry) moved (dx, dy) mm,
 *  one undo step. */
export function nudgeSelection(dx: number, dy: number): void {
  moveSelectionOnce((s) => s.moveTo({ x: dx, y: dy }));
}

/** Page Up / Page Down: raise the selection (and what its rooms carry) by `dz`
 *  mm, one undo step — nothing where nothing selected has a height. */
export function nudgeSelectionUp(dz: number): void {
  if (!selectedOf(state, 'instance').length && !selectedOf(state, 'polygon').length && !selectedOf(state, 'wall').length) return;
  moveSelectionOnce((s) => s.moveTo({ x: 0, y: 0, z: dz }));
}

/** Save the current view/drawing-aid toggles so they persist across launches. */
export function persistViewPrefs(): void {
  void platform.saveSettings({
    showGrid: state.showGrid, showHatches: state.showHatches, showLabels: state.showLabels, showBeams: state.showBeams, showLux: state.showLux, showCeilings: state.showCeilings, showConstraints: state.showConstraints,
    snapEnabled: state.snapEnabled, orthoEnabled: state.orthoEnabled, polarStep: state.polarStep,
    gridSnap: state.gridSnap, dynInput: state.dynInput, propertiesHidden: $('app').classList.contains('props-hidden'),
  });
}
/** Restore persisted view toggles at launch, syncing both state and buttons. */
/** The Polar button names its step while polar tracking is on (`15°`). The
 *  label's English source moves with it, so a language switch keeps the figure. */
function showPolarStep(b: HTMLElement, step: number): void {
  b.classList.toggle('active', step > 0);
  const label = b.querySelector<HTMLElement>('.tb-label');
  if (!label) return;
  label.dataset.en = step > 0 ? `${step}°` : 'Polar';
  label.textContent = tr(label.dataset.en);
}
async function loadViewPrefs(): Promise<void> {
  let s: AppSettings;
  try { s = await platform.getSettings(); } catch { return; }
  const sync = (id: string, on: boolean): void => { document.getElementById(id)?.classList.toggle('active', on); };
  if (typeof s.showGrid === 'boolean') { state.showGrid = s.showGrid; sync('btn-grid', s.showGrid); }
  if (typeof s.showHatches === 'boolean') { state.showHatches = s.showHatches; sync('btn-hatches', s.showHatches); }
  if (typeof s.showLabels === 'boolean') { state.showLabels = s.showLabels; sync('btn-labels', s.showLabels); }
  if (typeof s.showBeams === 'boolean') { state.showBeams = s.showBeams; sync('btn-beams', s.showBeams); }
  if (typeof s.showLux === 'boolean') { state.showLux = s.showLux; sync('btn-lux', s.showLux); }
  if (typeof s.showCeilings === 'boolean') state.showCeilings = s.showCeilings;
  if (typeof s.showConstraints === 'boolean') { state.showConstraints = s.showConstraints; sync('btn-show-constraints', s.showConstraints); }
  if (typeof s.snapEnabled === 'boolean') { state.snapEnabled = s.snapEnabled; sync('btn-snap', s.snapEnabled); }
  if (typeof s.orthoEnabled === 'boolean') { state.orthoEnabled = s.orthoEnabled; sync('btn-ortho', s.orthoEnabled); }
  if (typeof s.gridSnap === 'boolean') { state.gridSnap = s.gridSnap; sync('btn-grid-snap', s.gridSnap); }
  if (typeof s.dynInput === 'boolean') { state.dynInput = s.dynInput; setCursorReadout(s.dynInput); }
  if (typeof s.keepToolActive === 'boolean') state.keepToolActive = s.keepToolActive;
  if (typeof s.propertiesHidden === 'boolean') $('app').classList.toggle('props-hidden', s.propertiesHidden);
  if (typeof s.polarStep === 'number') {
    state.polarStep = s.polarStep;
    const b = document.getElementById('btn-polar');
    if (b) showPolarStep(b, s.polarStep);
  }
  if (s.uiLang === 'en' || s.uiLang === 'it' || s.uiLang === 'fr') applyUiLang(s.uiLang, false);
  redrawBackdrop();
  invalidate();
}

/** Switch the whole UI to `lang`: re-translate the static chrome, the menus, and
 *  the dynamically-built panels; optionally persist the choice. */
function applyUiLang(lang: Lang, persist = true): void {
  setUiLang(lang);
  applyStaticI18n();                                   // toolbar / panels / status buttons
  window.dispatchEvent(new Event('stenal-uilang'));    // the web in-app menu re-mounts itself
  void platform.setMenuLang?.(lang);                   // the desktop native menu rebuilds (no-op on web)
  renderProperties();
  renderViewTabs();
  renderProductList(); // product-category group headers follow the UI language
  updateStatus();
  invalidate();
  if (persist) void platform.saveSettings({ uiLang: lang });
}

// Temporary distance guides shown while dragging an object near walls/objects.
interface DragGuide { a: Vec2; b: Vec2; dist: number }
const DRAG_GUIDE_MAX = 1000; // mm: only show gaps to neighbours within 1 m

function centroidOfPts(pts: Vec2[]): Vec2 {
  let x = 0, y = 0;
  for (const p of pts) { x += p.x; y += p.y; }
  return pts.length ? { x: x / pts.length, y: y / pts.length } : { x: 0, y: 0 };
}

/** Geometric centre of an object: node objects use their node line, polygonal
 *  rooms their real polygon, everything else its footprint-box corners. */
export function objectCentroid(inst: ProductInstance): Vec2 {
  if (inst.nodes && inst.nodes.length) return centroidOfPts(inst.nodes);
  if (isPolyRoom(inst) && inst.polygon && inst.polygon.length) return centroidOfPts(inst.polygon.map((p) => instanceToWorld(inst, p)));
  return centroidOfPts(instanceCorners(inst));
}

/** Normalise a single-id / Set / null exclusion into a membership test. */
function excludes(exclude: ReadonlySet<string> | string | null): (id: string) => boolean {
  if (exclude == null) return () => false;
  if (typeof exclude === 'string') return (id) => id === exclude;
  return (id) => exclude.has(id);
}

/** Every other object's centroid (instances, and user-drawn polygons /
 *  circles), excluding `exclude` and — for a moving instance — its own group. */
function otherCentroids(exclude: ReadonlySet<string> | string | null, skip?: ReadonlySet<string>): Vec2[] {
  const isExcluded = snapExcludes(exclude);
  const out: Vec2[] = [];
  for (const o of state.drawing.instances) {
    if (isExcluded(o.id)) continue;
    if (skip?.has(o.id)) continue;
    out.push(objectCentroid(o));
  }
  for (const poly of state.drawing.polygons || []) {
    if (isExcluded(poly.id)) continue;
    out.push(centroidOfPts(poly.points)); // polygons and ellipses/circles
  }
  for (const wall of state.drawing.walls || []) {
    if (isExcluded(wall.id)) continue;
    out.push(centroidOfPts(wall.points)); // walls
  }
  return out;
}

/**
 * Orthogonal centroid alignment for a moving object whose centroid is `mc`: an
 * offset that lines it up (in X and/or Y, independently) with the nearest other
 * object's centroid within `tol`. Works for instances, polygons and circles.
 * Records the red guide line(s) for each axis that locked.
 */
export function centroidSnapForPoint(mc: Vec2, exclude: ReadonlySet<string> | string | null, tol: number, skip?: ReadonlySet<string>): { off: Vec2; lockX: boolean; lockY: boolean } {
  let dx = 0, dy = 0, bx = tol, by = tol;
  let vX: Vec2 | null = null, hY: Vec2 | null = null;
  for (const oc of otherCentroids(exclude, skip)) {
    const ax = Math.abs(oc.x - mc.x);
    if (ax < bx) { bx = ax; dx = oc.x - mc.x; vX = oc; }
    const ay = Math.abs(oc.y - mc.y);
    if (ay < by) { by = ay; dy = oc.y - mc.y; hY = oc; }
  }
  state.centroidGuides = [];
  const moved = { x: mc.x + (vX ? dx : 0), y: mc.y + (hY ? dy : 0) };
  if (vX) state.centroidGuides.push([{ x: vX.x, y: vX.y }, { x: vX.x, y: moved.y }]);
  if (hY) state.centroidGuides.push([{ x: hY.x, y: hY.y }, { x: moved.x, y: hY.y }]);
  return { off: { x: vX ? dx : 0, y: hY ? dy : 0 }, lockX: !!vX, lockY: !!hY };
}

export const ALIGN_TOL_PX = 6; // pixel tolerance for the alignment snap
// Axis-normalised rectangle (x0<x1, y0<y1) — orientation-agnostic so the same
// snap works for the model's Y-up world and the sheet's Y-down paper.
export interface AlignRect { x0: number; x1: number; y0: number; y1: number }

/** Vertical (`axis:'x'`) or horizontal guide at `c`, spanning both boxes. */
function alignGuideSeg(axis: 'x' | 'y', c: number, a: AlignRect, b: AlignRect): [Vec2, Vec2] {
  if (axis === 'x') {
    return [{ x: c, y: Math.min(a.y0, b.y0) }, { x: c, y: Math.max(a.y1, b.y1) }];
  }
  return [{ x: Math.min(a.x0, b.x0), y: c }, { x: Math.max(a.x1, b.x1), y: c }];
}

/** Every candidate edge line from the other boxes, tagged with its source rect. */
function alignCands(others: AlignRect[]): { xs: Array<{ v: number; r: AlignRect }>; ys: Array<{ v: number; r: AlignRect }> } {
  const xs: Array<{ v: number; r: AlignRect }> = [];
  const ys: Array<{ v: number; r: AlignRect }> = [];
  for (const r of others) {
    xs.push({ v: r.x0, r }, { v: r.x1, r });
    ys.push({ v: r.y0, r }, { v: r.y1, r });
  }
  return { xs, ys };
}

/**
 * Snap a moving box so a vertex lines up with another box's vertical/horizontal
 * projection. All four corners shift together, so each axis picks the single
 * nearest (own-edge → other-edge) pair within `tol`. Records the purple guides.
 */
export function alignMoveSnap(moving: AlignRect, others: AlignRect[], tol: number): { dx: number; dy: number } {
  const { xs, ys } = alignCands(others);
  let bx = tol, dx = 0, xr: AlignRect | null = null, xc = 0;
  for (const c of xs) for (const o of [moving.x0, moving.x1]) { const d = Math.abs(c.v - o); if (d < bx) { bx = d; dx = c.v - o; xr = c.r; xc = c.v; } }
  let by = tol, dy = 0, yr: AlignRect | null = null, yc = 0;
  for (const c of ys) for (const o of [moving.y0, moving.y1]) { const d = Math.abs(c.v - o); if (d < by) { by = d; dy = c.v - o; yr = c.r; yc = c.v; } }
  const moved: AlignRect = { x0: moving.x0 + dx, x1: moving.x1 + dx, y0: moving.y0 + dy, y1: moving.y1 + dy };
  state.alignGuides = [];
  if (xr) state.alignGuides.push(alignGuideSeg('x', xc, moved, xr));
  if (yr) state.alignGuides.push(alignGuideSeg('y', yc, moved, yr));
  return { dx, dy };
}

/**
 * Snap the grabbed edges of a resizing box (each `free` edge independently) to
 * the nearest other-box edge within `tol`. Returns the adjusted rect and records
 * the purple guides. `free` names numeric edges (x0=minX, x1=maxX, y0=minY, y1=maxY).
 */
export function alignResizeSnap(moving: AlignRect, others: AlignRect[], free: { x0?: boolean; x1?: boolean; y0?: boolean; y1?: boolean }, tol: number): AlignRect {
  const { xs, ys } = alignCands(others);
  const r: AlignRect = { ...moving };
  state.alignGuides = [];
  const pick = (val: number, cands: Array<{ v: number; r: AlignRect }>): { v: number; r: AlignRect } | null => {
    let b = tol, best: { v: number; r: AlignRect } | null = null;
    for (const c of cands) { const d = Math.abs(c.v - val); if (d < b) { b = d; best = c; } }
    return best;
  };
  if (free.x0) { const s = pick(r.x0, xs); if (s && s.v < r.x1) { r.x0 = s.v; state.alignGuides.push(alignGuideSeg('x', s.v, r, s.r)); } }
  if (free.x1) { const s = pick(r.x1, xs); if (s && s.v > r.x0) { r.x1 = s.v; state.alignGuides.push(alignGuideSeg('x', s.v, r, s.r)); } }
  if (free.y0) { const s = pick(r.y0, ys); if (s && s.v < r.y1) { r.y0 = s.v; state.alignGuides.push(alignGuideSeg('y', s.v, r, s.r)); } }
  if (free.y1) { const s = pick(r.y1, ys); if (s && s.v > r.y0) { r.y1 = s.v; state.alignGuides.push(alignGuideSeg('y', s.v, r, s.r)); } }
  return r;
}

/** Normalised rects for every model text box except `exceptId`. */
export function otherTextRects(exceptId: string | null): AlignRect[] {
  const out: AlignRect[] = [];
  for (const t of state.drawing.texts || []) {
    if (t.id === exceptId || t.rotation || objHidden(t.id)) continue; // edge alignment is for level boxes
    out.push({ x0: t.x, x1: t.x + t.w, y0: t.y - t.h, y1: t.y });
  }
  return out;
}

/**
 * Orthogonal distance guides between the dragged instance and the nearest wall
 * face / object edge on each side, within DRAG_GUIDE_MAX. Each guide is a short
 * horizontal or vertical segment annotated with the clear gap in mm.
 */
function computeDragGuides(inst: ProductInstance): DragGuide[] {
  const D = instanceAABB(inst);
  // Axis-aligned obstacle faces: object AABB edges + wall band edges.
  type VSeg = { axis: 'v'; c: number; lo: number; hi: number };
  type HSeg = { axis: 'h'; c: number; lo: number; hi: number };
  const vSegs: VSeg[] = [];
  const hSegs: HSeg[] = [];
  const addBox = (b: { x0: number; y0: number; x1: number; y1: number }): void => {
    vSegs.push({ axis: 'v', c: b.x0, lo: b.y0, hi: b.y1 }, { axis: 'v', c: b.x1, lo: b.y0, hi: b.y1 });
    hSegs.push({ axis: 'h', c: b.y0, lo: b.x0, hi: b.x1 }, { axis: 'h', c: b.y1, lo: b.x0, hi: b.x1 });
  };
  for (const o of state.drawing.instances) {
    if (o.id === inst.id || objHidden(o.id)) continue;
    if (inst.groupId && o.groupId === inst.groupId) continue; // skip its own group
    addBox(instanceAABB(o));
  }
  for (const wall of state.drawing.walls || []) {
    if (wall.style === 'line' || objHidden(wall.id)) continue;
    const band = wallBand(wall.points, wall.thickness, wall.align);
    for (let i = 0; i < band.length; i++) {
      const a = band[i], b = band[(i + 1) % band.length];
      if (Math.abs(a.x - b.x) < 1) vSegs.push({ axis: 'v', c: (a.x + b.x) / 2, lo: Math.min(a.y, b.y), hi: Math.max(a.y, b.y) });
      else if (Math.abs(a.y - b.y) < 1) hSegs.push({ axis: 'h', c: (a.y + b.y) / 2, lo: Math.min(a.x, b.x), hi: Math.max(a.x, b.x) });
    }
  }
  const out: DragGuide[] = [];
  type Near = { gap: number; c: number; lo: number; hi: number } | null;
  // Nearest vertical face to the left and right (object's left/right edge → face).
  let left: Near = null;
  let right: Near = null;
  for (const s of vSegs) {
    const lo = Math.max(s.lo, D.y0), hi = Math.min(s.hi, D.y1);
    if (hi - lo <= 0) continue; // no vertical overlap
    if (s.c <= D.x0) { const gap = D.x0 - s.c; if (gap >= 1 && gap < DRAG_GUIDE_MAX && (!left || gap < left.gap)) left = { gap, c: s.c, lo, hi }; }
    else if (s.c >= D.x1) { const gap = s.c - D.x1; if (gap >= 1 && gap < DRAG_GUIDE_MAX && (!right || gap < right.gap)) right = { gap, c: s.c, lo, hi }; }
  }
  let below: Near = null;
  let above: Near = null;
  for (const s of hSegs) {
    const lo = Math.max(s.lo, D.x0), hi = Math.min(s.hi, D.x1);
    if (hi - lo <= 0) continue; // no horizontal overlap
    if (s.c <= D.y0) { const gap = D.y0 - s.c; if (gap >= 1 && gap < DRAG_GUIDE_MAX && (!below || gap < below.gap)) below = { gap, c: s.c, lo, hi }; }
    else if (s.c >= D.y1) { const gap = s.c - D.y1; if (gap >= 1 && gap < DRAG_GUIDE_MAX && (!above || gap < above.gap)) above = { gap, c: s.c, lo, hi }; }
  }
  if (left) { const y = (left.lo + left.hi) / 2; out.push({ a: { x: D.x0, y }, b: { x: left.c, y }, dist: left.gap }); }
  if (right) { const y = (right.lo + right.hi) / 2; out.push({ a: { x: D.x1, y }, b: { x: right.c, y }, dist: right.gap }); }
  if (below) { const x = (below.lo + below.hi) / 2; out.push({ a: { x, y: D.y0 }, b: { x, y: below.c }, dist: below.gap }); }
  if (above) { const x = (above.lo + above.hi) / 2; out.push({ a: { x, y: D.y1 }, b: { x, y: above.c }, dist: above.gap }); }
  return out;
}


/** Signed perpendicular offset of point p from the line a→b (matches the renderer). */
function dimOffset(a: Vec2, b: Vec2, p: Vec2, kind: DimKind = 'aligned'): number {
  return dimensionOffset(a, b, p, kind);
}

// ---- Zoom-adaptive editing grid ----
// Dragging (moves, resizes, vertices) snaps to this step, which grows as you zoom
// out (1 mm zoomed in → 100 mm far out); the background grid is drawn at it too.
// Object-to-object snapping is applied on top and overrides the grid when near.
/** Current editing granularity in mm. */
function gridStep(): number { return gridStepMm(vp.scale); }
/** Round a scalar (mm) to the current grid step. */
function gsnap(v: number): number { if (!state.gridSnap) return v; const g = gridStep(); return Math.round(v / g) * g; }
/** Round a world point to the current grid step. */
export function gsnapPt(p: Vec2): Vec2 { if (!state.gridSnap) return p; const g = gridStep(); return { x: Math.round(p.x / g) * g, y: Math.round(p.y / g) * g }; }

// ---- DOM ----
export const $ = <T extends HTMLElement = HTMLElement>(id: string): T =>
  document.getElementById(id) as T;
export const canvas = $<HTMLCanvasElement>('canvas');
export const ctx = canvas.getContext('2d')!;
// Dedicated background canvas: the static backdrop (page, grid, imported DXF)
// is rendered here only when the viewport or the drawing changes, so panning a
// product or selecting an object doesn't re-rasterise a heavy DXF every frame.
export const bgCanvas = $<HTMLCanvasElement>('bgcanvas');
const bgCtx = bgCanvas.getContext('2d')!;
let bgVersion = 0; // bump when the imported DXF background changes
export const bumpBackground = (): void => { bgVersion++; };

// Viewport size in CSS pixels (the backing store is cssPx * devicePixelRatio).
export const viewW = (): number => canvas.clientWidth || canvas.width;
export const viewH = (): number => canvas.clientHeight || canvas.height;
/** The text box's in-place editor, over the canvas. */
export const textEditor = $('text-editor') as HTMLTextAreaElement;
/** The inline editor over a text box and over a sheet's box (ui/inlineText.ts). */
export const textEditorUi = inlineEditor(textEditor);

// The Stenal logo for sheet title blocks, decoded once. `stenalLogoReady` is set
// on load so the (synchronous) sheet render can draw it and print can await it.
const stenalLogo = new Image();
let stenalLogoReady = false;
export const stenalLogoLoaded = new Promise<void>((resolve) => {
  stenalLogo.onload = () => { stenalLogoReady = true; redrawBackdrop(); invalidate(); resolve(); };
  stenalLogo.onerror = () => resolve();
});
stenalLogo.src = STENAL_LOGO_DATA_URI;

// Custom cartiglio logos, decoded by data URI (built-in logo handled separately).
const cartiglioLogoCache = new Map<string, HTMLImageElement>();
export function cartiglioLogo(uri: string | undefined): HTMLImageElement | null {
  if (!uri) return stenalLogoReady ? stenalLogo : null;
  let img = cartiglioLogoCache.get(uri);
  if (!img) {
    img = new Image();
    img.onload = () => { redrawBackdrop(); invalidate(); };
    img.src = uri;
    cartiglioLogoCache.set(uri, img);
  }
  return img.complete && img.naturalWidth ? img : null;
}
export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
/** The cartiglio applied to a sheet: its assigned template, else the default. */
const CARTIGLIO_COMPANY = 'STENAL';
/** The object-model cartiglio for a sheet, keyed by company + paper + orientation.
 *  Generates a default layout the first time a (paper, orientation) is seen; it is
 *  persisted to Baserow when first edited. */
export function cartiglioForSheet(sh: Sheet): Cartiglio | null {
  if (sh.cartiglioId === '') return null; // explicitly no title block
  let c = cartigli.find((x) => x.objects && x.company === CARTIGLIO_COMPANY && x.paper === sh.paper && x.orientation === sh.orientation);
  if (!c) { c = objectCartiglio(CARTIGLIO_COMPANY, sh.paper, sh.orientation, sh.widthMm, sh.heightMm); cartigli.push(c); }
  return c;
}
/** The id assigned to newly created sheets: the current default cartiglio. */
export function defaultCartiglioId(): string {
  return (cartigli.find((c) => c.isDefault) || cartigli[0] || defaultCartiglio()).id;
}

// Decoded background underlays, keyed by data URL (several may coexist).
const bgImageCache = new Map<string, HTMLImageElement>();
/** Map of data URL → loaded underlay image for every background (loads lazily). */
export function bgImageMap(): Map<string, CanvasImageSource> {
  const ready = new Map<string, CanvasImageSource>();
  for (const bi of bgImages()) {
    const url = bi.dataUrl;
    if (ready.has(url)) continue;
    let img = bgImageCache.get(url);
    if (!img) {
      img = new Image();
      img.onload = () => { redrawBackdrop(); invalidate(); }; // force the backdrop to repaint
      img.src = url;
      bgImageCache.set(url, img);
    }
    if (img.complete && img.naturalWidth > 0) ready.set(url, img);
  }
  return ready;
}

// ---- Background underlays (imported PDFs / images) ----
let bgIdCounter = 0;
function genBgId(): string { return `bg-${Date.now()}-${bgIdCounter++}`; }
/** All background underlays in draw order. */
export function bgImages(): BackgroundImage[] { return state.drawing.backgroundImages ?? []; }
/** The underlay currently being transformed (double-clicked), or null. */
export function editedBg(): BackgroundImage | null {
  return state.editPdfId ? bgImages().find((b) => b.id === state.editPdfId) ?? null : null;
}
/** Target for the scale tools: the edited underlay, else the one captured when
 *  the scale tool was entered, else the topmost. */
function scaleTargetBg(): BackgroundImage | null {
  const imgs = bgImages();
  return editedBg()
    ?? (scaleTargetId ? imgs.find((b) => b.id === scaleTargetId) ?? null : null)
    ?? (imgs.length ? imgs[imgs.length - 1] : null);
}
/** Whether an imported DXF background is present. */
export function hasBgGeom(): boolean {
  return !!(state.drawing.background && isFiniteBounds(state.drawing.background.bounds));
}
/** World bounds of the imported DXF, shifted by its move offset (or null). */
export function bgGeomBounds(): Bounds | null {
  if (!hasBgGeom()) return null;
  const b = state.drawing.background!.bounds;
  const o = state.drawing.backgroundOffset ?? { x: 0, y: 0 };
  return { min: { x: b.min.x + o.x, y: b.min.y + o.y }, max: { x: b.max.x + o.x, y: b.max.y + o.y } };
}
/** Whether a world point lies over the imported DXF's (moved) bounding box. */
function pointInBgGeom(world: Vec2): boolean {
  const b = bgGeomBounds();
  return !!b && world.x >= b.min.x && world.x <= b.max.x && world.y >= b.min.y && world.y <= b.max.y;
}

/** World-space bounding box enclosing every underlay (their rotated corners). */
function bgImagesBounds(): Bounds {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const bi of bgImages()) {
    for (const c of pdfBoxCorners(bi)) {
      if (c.x < minX) minX = c.x;
      if (c.y < minY) minY = c.y;
      if (c.x > maxX) maxX = c.x;
      if (c.y > maxY) maxY = c.y;
    }
  }
  return { min: { x: minX, y: minY }, max: { x: maxX, y: maxY } };
}

/** World outline of the object whose Parts-tab row is hovered (a placed part, an
 *  underlay image, or the imported DXF), for the light-blue hover hatch. */
function hoverHighlightRing(): Vec2[] | null {
  if (hoverFeatureRing) {
    const ring = hoverFeatureRing.ring();
    if (ring.length >= 2) return ring;
  }
  if (hoverHeaterWall) {
    const room = state.drawing.instances.find((i) => i.id === hoverHeaterWall!.instId);
    const band = room ? heaterWallBands(room).find((b) => b.wall.id === hoverHeaterWall!.wallId) : null;
    if (room && band) return band.poly.map((p) => instanceToWorld(room, p));
  }
  if (hoverInnerWall) {
    const room = state.drawing.instances.find((i) => i.id === hoverInnerWall!.instId);
    const iw = room ? innerWallsOf(room).find((w) => w.id === hoverInnerWall!.wallId) : null;
    if (room && iw) return polyInnerWallBand(room, iw).map((p) => instanceToWorld(room, p));
  }
  if (hoverGroupId) {
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const c of groupOutlinePts(hoverGroupId)) {
      if (c.x < minX) minX = c.x; if (c.y < minY) minY = c.y;
      if (c.x > maxX) maxX = c.x; if (c.y > maxY) maxY = c.y;
    }
    if (!isFinite(minX)) return null;
    return [{ x: minX, y: minY }, { x: maxX, y: minY }, { x: maxX, y: maxY }, { x: minX, y: maxY }];
  }
  if (hoverPartId) return objectOutline(hoverPartId);
  return null;
}

/** What a Select click at the cursor would pick, outlined before the click —
 *  PowerPoint's hover highlight. `key` names it, so a frame is redrawn only when
 *  the target changes. Null over empty paper, a grip, or what is selected. */
let canvasHover: { key: string; ring: Vec2[]; closed: boolean } | null = null;
/** Set what the hover outlines; the canvas names it (`data-hover`, its key, and
 *  `data-hover-ring`, the ring in whole mm) for the browser tests, as the 3D
 *  view does. */
export function setCanvasHover(h: typeof canvasHover): void {
  canvasHover = h;
  const key = h?.key ?? '';
  if ((canvas.dataset.hover ?? '') !== key) canvas.dataset.hover = key;
  const ring = h ? h.ring.map((p) => `${Math.round(p.x)},${Math.round(p.y)}`).join(' ') : '';
  if ((canvas.dataset.hoverRing ?? '') !== ring) canvas.dataset.hoverRing = ring;
}

/** Can a press reach this room (or one of its features)? pickStack.ts `reachesRoom`
 *  over the plan's layer stacking. */
export function roomReachable(stack: ObjRef[], roomId: string, own?: number): boolean {
  return reachesRoom(layers(), stack, roomId, own);
}

/** Can a press reach a schedule table? pickStack.ts `reachesTable`. */
function tableReachable(kind: SchedTableKind, stack: ObjRef[]): boolean {
  return reachesTable(layers(), stack, schedTableId(kind));
}

/** Everything under the cursor, in the order a Select press picks it: what is
 *  DRAWN ON TOP first (the layer stacking, `refRank`), so the `head` is exactly
 *  what a plain click takes — none when what is on top is locked or frozen —
 *  and `tab` what Tab / Alt+click cycle to (pickStack.ts `orderPickStack`).
 *  Hidden layers' objects are left out, and so are underlays; locked ones are
 *  `seen`, in front of what they cover. */
export function objectStackAt(world: Vec2, sp: Vec2): PickStack {
  const out: ObjRef[] = [];
  const push = (r: ObjRef): void => { if (!out.some((o) => refKey(o) === refKey(r))) out.push(r); };
  // The SELECTED room's panes, openings and doors come first — only its own
  // (mousedown's rule: select the room, then its features are live).
  const selRoom = selectedInstance();
  if (selRoom && selRoom.autoBenches && !instLocked(selRoom)) {
    const local = toInstanceLocal(selRoom, world);
    const gi = (selRoom.glass || []).length ? glassHitLocal(selRoom, local, 4 / vp.scale) : -1;
    const gid = gi >= 0 ? glassPanesOf(selRoom)[gi].id : undefined;
    if (gid) push({ kind: 'glass', id: gid, instId: selRoom.id });
    const oi = (selRoom.openings || []).length ? openingHitLocal(selRoom, local, 4 / vp.scale) : -1;
    const oid = oi >= 0 ? openingsOf(selRoom)[oi].id : undefined;
    if (oid) push({ kind: 'opening', id: oid, instId: selRoom.id });
    const door = doorHitTest(world);
    if (door?.doorId) push({ kind: 'door', id: door.doorId, instId: door.instId });
    // Its bench supports too (the room rule, as for a pane).
    const sup = (selRoom.benchSupports?.length ?? 0) > 0 ? supportTargetAt(world, selRoom, 1 / vp.scale) : null;
    if (sup?.mark) push({ kind: 'support', id: sup.mark.support, instId: selRoom.id, run: sup.mark.run });
  }
  for (const t of objectsAt('text', world)) push({ kind: 'text', id: t.id });
  const frozen = new Set(state.drawing.instances.filter((i) => i.frozen).map((i) => i.id));
  for (const id of instancesAt(world, true, true)) push({ kind: 'instance', id });
  const rooms = instancesAt(world, false, true);
  const hit = rooms.find((id) => !frozen.has(id));
  // A small selected room is moved by a press anywhere over it (mousedown), so
  // its benches are not offered ahead of it.
  const small = !!hit && hit === state.selectedId && state.selectedIds.length <= 1
    && instanceScreenMinExtent(state.drawing.instances.find((i) => i.id === hit)!) < GRAB_MOVE_PX;
  const bench = small ? null : benchHitTest(world);
  if (bench) push({ kind: 'bench', id: bench.benchId, instId: bench.instId });
  // A room's backrest paints over its bench.
  const fixture = small ? null : fixtureHitTest(world);
  if (fixture) push({ kind: 'fixture', id: fixture.id, instId: fixture.instId });
  // A room's heater wall and partition, which a press takes over its benches and doors.
  const hw = heaterWallAt(roomsToEdit(), world);
  if (hw?.wall.id) push({ kind: 'heaterWall', id: hw.wall.id, instId: hw.room.id });
  const iw = partitionAt(roomsToEdit(), world);
  if (iw?.wall.id) push({ kind: 'innerWall', id: iw.wall.id, instId: iw.room.id });
  for (const id of rooms) push({ kind: 'instance', id });
  for (const d of objectsAt('dim', world)) push({ kind: 'dim', id: d.id });
  for (const h of connectionsAt(world, 5, true)) push({ kind: 'conn', id: h.conn.id });
  for (const sec of objectsAt('section', world)) push({ kind: 'section', id: sec.id });
  for (const v of objectsAt('rcp', world)) push({ kind: 'rcp', id: v.id });
  for (const f of objectsAt('flooring', world)) push({ kind: 'flooring', id: f.id });
  for (const p of objectsAt('polygon', world)) push({ kind: 'polygon', id: p.id });
  for (const w of objectsAt('wall', world)) push({ kind: 'wall', id: w.id });
  for (const l of objectsAt('leader', world)) push({ kind: 'leader', id: l.id });
  for (const a of objectsAt('arc', world)) push({ kind: 'arc', id: a.id });
  for (const c of objectsAt('cloud', world)) push({ kind: 'cloud', id: c.id });
  return orderPickStack(out, layers(), state.drawing.connections, (id) => frozen.has(id));
}

/** The selected room's feature checked against its room (roomFeatures.ts). */
export function liveFeatureNow(): LiveFeature | null {
  return liveFeature(state, state.drawing.instances);
}

/** What is selected now, as refs (every kind the stack can hold). */
export function selectedRefs(): ObjRef[] {
  const r: ObjRef[] = [];
  for (const kind of SELECTION_KINDS) if (kind !== 'table') for (const id of selectedOf(state, kind)) r.push({ kind, id });
  r.push(...featureRefs(liveFeatureNow()));
  return r;
}

/** Select exactly this object. A room's feature is selected with its room (a
 *  door, a pane or an opening sub-selected INSIDE it), a bench alone. */
export function selectRef(r: ObjRef): void {
  deselectAll();
  const kind = refItem(r.kind);
  if (kind) selectObjects(state, { [kind]: [r.id] });
  else {
    const f = featureSel({ kind: r.kind as RefFeature, id: r.id, instId: r.instId!, run: r.run });
    if (!FEATURES[f.kind].alone) selectObjects(state, { instance: [r.instId!] });
    selectFeature(state, f);
  }
  renderProperties();
  invalidate();
}

/** Select what a click takes (`clickTake`): the ref itself, or a group's members.
 *  A drill keeps the selection (the release of a press drills). */
export function applyClickTake(t: ClickTake): void {
  if ('ref' in t) { selectRef(t.ref); return; }
  if ('ids' in t) {
    deselectAll();
    setObjectSelection(selectionOfIds(state.drawing, t.ids));
    renderProperties();
    invalidate();
  }
}

/** Tab / Alt+click: select the next object under the cursor after the one that
 *  is selected (`dir` −1 goes back), so a shape buried under another is reachable
 *  without moving anything. From outside the stack it starts at the top. Returns
 *  false when nothing is there. */
export function cycleSelectionAt(world: Vec2, sp: Vec2, dir: 1 | -1, from: ObjRef[] = selectedRefs()): boolean {
  const stack = objectStackAt(world, sp).tab;
  if (!stack.length) return false;
  const had = new Set(from.map(refKey));
  const i = stack.findIndex((r) => had.has(refKey(r)));
  const n = stack.length;
  const next = i < 0 ? 0 : (i + dir + n) % n;
  selectRef(stack[next]);
  if (n > 1) flashToolStatus(`${tr('Overlapping objects')}: ${next + 1} / ${n}`);
  return true;
}

// ---- A click inside a section view (the plan's; a sheet's are not drilled into) ----
// A click selects the view and a drag moves it; a second click drills in and picks
// the object under the cursor by the plan's rules, and further clicks keep picking
// there. A drag on the drawing always moves the view, drilled in or not — only
// handles edit. Drilled in, the hover outlines what a click takes, Tab cycles, a
// click on blank space in the view goes back to it, and Esc deselects.

/** A press inside a selected or drilled-into view: a drill click if released
 *  where it was pressed (decided on mouseup, `drillSectionAt`). */
let sectionClick: { id: string; sp: Vec2; world: Vec2; alt: boolean } | null = null;

/** What the section views box for the app's selection: a room's selected
 *  feature (its part, roomFeatures.ts: a bench, pane(s), opening(s), a door, a
 *  partition, a heater wall, a support's trestles, a fixture), a drawn wall's
 *  selected pane — or every selected part, room, wall and shape. */
export function sectionTargets(): SecSrc[] {
  const conn = selectedConnection();
  if (conn) return [srcOf(conn.id)];
  const f = liveFeatureNow();
  if (f) return featureTargets(f);
  const wf = selectedWallFeature();
  if (wf) return [srcOf(wf.wall.id, 'wallFeature', wf.f.id)];
  return [...selectedOf(state, 'instance'), ...selectedOf(state, 'wall'), ...selectedOf(state, 'polygon')].map((id) => srcOf(id));
}

/** What a click on a section piece takes, and the source it is outlined by. */
export interface SectionPick { target: SecSrc; select: (exact: boolean) => void; ids?: string[] }

/** A section piece's pick by the plan's rules (roomFeatures.ts `partPick`): a
 *  room's bench, fixture, partition or heater wall straight away; its pane,
 *  opening, door or support — as a drawn wall's door, pane or opening — once the
 *  room (the wall) is selected, a support till then its bench; anything else its
 *  object, a group's member through `clickTarget` (its outermost group, then one
 *  level in) unless `exact` (Tab). Null for what no click may take (frozen, a
 *  hidden / locked layer). */
export function sectionPickOf(src: SecSrc): SectionPick | null {
  const d = state.drawing;
  const part = src.part;
  const object = (ref: ObjRef): SectionPick => {
    const fresh = clickTake(d, ref, new Set());
    return {
      target: srcOf(ref.id),
      ids: 'ids' in fresh ? fresh.ids : [ref.id],
      // A section view takes a drill at once (it has no release that drills).
      select: (exact) => {
        const t = clickTake(d, ref, selectedObjectIds(), exact);
        applyClickTake('drill' in t ? { ids: t.drill } : t);
      },
    };
  };
  const inst = d.instances.find((i) => i.id === src.id);
  if (inst) {
    if (instLocked(inst)) return null;
    const f = part ? partPick(inst.id, part, selectedInstance()?.id === inst.id) : null;
    return f ? { target: featureTarget(f), select: () => selectRef(f) } : object({ kind: 'instance', id: inst.id });
  }
  if (objUnpickable(src.id)) return null;
  const conn = d.connections.find((c) => c.id === src.id);
  if (conn) return { target: srcOf(conn.id), select: () => selectRef({ kind: 'conn', id: conn.id }) };
  const wall = (d.walls || []).find((w) => w.id === src.id);
  if (wall) {
    const f = part?.kind === 'wallFeature' && selectedBandWall()?.id === wall.id ? wall.features?.find((x) => x.id === part.id) : undefined;
    return f ? { target: src, select: () => pickWallFeature(wall, f) } : object({ kind: 'wall', id: wall.id });
  }
  return (d.polygons || []).some((p) => p.id === src.id) ? object({ kind: 'polygon', id: src.id }) : null;
}

/** The picks under a world point in a section view by THE click rule
 *  (viewPicks.ts `frontPicks`, the 3D view's too): what the view draws there,
 *  front first, each as `sectionPickOf` takes it — a frozen or locked one,
 *  drawn, is seen and takes nothing; a hidden one is not drawn. */
function sectionPicksAt(sec: SectionLine, world: Vec2): FrontPick<SectionPick> {
  const sv = sectionSources();
  return frontPicks(sectionStackAt(sec, sv.instances, sv.walls, sv.polygons, world, GRIP_HIT_PX / 2 / vp.scale, sv.pipes), sectionPickOf);
}

/** Whether a press on this section view drills in: it is selected, or drilled into. */
function sectionDrills(id: string): boolean {
  return state.selectedSectionId === id || state.sectionDrill === id;
}

/** A click released where it was pressed in a selected or drilled-into view:
 *  pick what is under it (`alt`: the next one under it), nothing over a locked
 *  one in front (the selection cleared), or — on blank space — go back to the
 *  view. */
function drillSectionAt(id: string, world: Vec2, alt: boolean): void {
  const sec = (state.drawing.sections || []).find((s) => s.id === id);
  if (!sec) return;
  if (alt) { cycleSectionAt(sec, world, 1); return; }
  const picks = sectionPicksAt(sec, world), top = picks.head;
  if (picks.blocked) deselectAll();
  else if (!top) {
    deselectAll();
    selectOne(state, 'section', id);
  } else {
    top.select(false);
    state.sectionDrill = id;
  }
  renderProperties();
  invalidate();
}

/** Tab / Shift+Tab (or Alt+click) in a drilled-into view: select the next pick
 *  under the point after the one selected, wrapping. */
export function cycleSectionAt(sec: SectionLine, world: Vec2, dir: 1 | -1): boolean {
  const picks = sectionPicksAt(sec, world).tab;
  const next = cycleNext(picks, sectionTargets(), dir), n = picks.length;
  if (next < 0) return false;
  picks[next].select(true);
  state.sectionDrill = sec.id;
  if (n > 1) flashToolStatus(`${tr('Overlapping objects')}: ${next + 1} / ${n}`);
  renderProperties();
  invalidate();
  return true;
}

/** The drilled-into (or selected) section view under a screen point, if any. */
export function drillingSectionAt(sp: Vec2): SectionLine | null {
  const id = state.sectionDrill ?? state.selectedSectionId;
  const sec = id ? (state.drawing.sections || []).find((s) => s.id === id) : undefined;
  if (!sec || objUnpickable(sec.id)) return null;
  return inSectionView(sec, vp.screenToWorld(sp), hitCtx()) ? sec : null;
}

/** The hover in a selected or drilled-into view: the box of what a click takes
 *  (nothing over what is selected, nor over a locked one in front), or — on
 *  blank space while drilled in — the view's own box, where a click goes back
 *  to it. */
function sectionHoverAt(sec: SectionLine, world: Vec2, sp: Vec2): { key: string; ring: Vec2[]; closed: boolean } | null {
  const h = hoverOver(sectionPicksAt(sec, world), sectionTargets());
  if (h.over === 'nothing') {
    if (state.selectedSectionId === sec.id) return null;
    const o = refOutline({ kind: 'section', id: sec.id }, sp);
    return o ? { key: `section:${sec.id}`, ...o } : null;
  }
  if (h.over !== 'pick') return null;
  const key = srcKey(h.pick.target), sv = sectionSources();
  const [box] = sectionSourceBoxes(sec, sv.instances, sv.walls, sv.polygons, [h.pick.target], sv.pipes);
  return box ? { key: `secpick:${sec.id}:${key}`, ring: sectionBoxRing(sec, box, SECTION_BOX_PAD_PX / vp.scale), closed: true } : null;
}

/** The outline a ref is highlighted with: a closed ring, or a line-like path. */
export function refOutline(r: ObjRef, sp: Vec2): { ring: Vec2[]; closed: boolean } | null {
  const closed = (ring: Vec2[] | null) => (ring && ring.length >= 3 ? { ring, closed: true } : null);
  const path = (ring: Vec2[]) => (ring.length >= 2 ? { ring, closed: false } : null);
  const host = r.instId ? state.drawing.instances.find((i) => i.id === r.instId) : undefined;
  switch (r.kind) {
    case 'glass': {
      const seg = host ? glassPanesOf(host).find((g) => g.id === r.id) : undefined;
      return host && seg ? closed(glassPolysLocal(host, seg).flat().map((p) => instanceToWorld(host, p))) : null;
    }
    case 'opening': {
      const seg = host ? openingsOf(host).find((g) => g.id === r.id) : undefined;
      return host && seg ? closed(polyOpeningBand(host, seg).map((p) => instanceToWorld(host, p))) : null;
    }
    case 'door': {
      const d = host && isPolyRoom(host) ? roomDoors(host).find((x) => x.id === r.id) : undefined;
      const rd = host && d ? roomDoor(host, d) : null;
      return host && rd ? closed(doorFootprint(rd).map((p) => instanceToWorld(host, p))) : null;
    }
    case 'bench': {
      const room = state.drawing.instances.find((i) => i.id === r.instId);
      const band = room && isPolyRoom(room)
        ? polyBenchBandsDrawn(room).find((b) => b.id === r.id) ?? polyInnerBenchBands(room).find((b) => b.id === r.id)
        : null;
      return band ? closed(band.poly.map((p) => instanceToWorld(room!, p))) : null;
    }
    case 'fixture': {
      const ring = host ? fixtureOutline(host, r.id) : null;
      return host && ring ? closed(ring.map((p) => instanceToWorld(host, p))) : null;
    }
    case 'heaterWall': {
      const band = host ? heaterWallBands(host).find((b) => b.wall.id === r.id) : undefined;
      return host && band ? closed(band.poly.map((p) => instanceToWorld(host, p))) : null;
    }
    case 'innerWall': {
      const wall = host ? (host.innerWalls || []).find((w) => w.id === r.id) : undefined;
      return host && wall ? closed(innerWallBand(host, wall).map((p) => instanceToWorld(host, p))) : null;
    }
    case 'support': return null; // its mark is drawn as a figure, not outlined
    case 'section': {
      const sec = (state.drawing.sections || []).find((x) => x.id === r.id);
      if (!sec) return null;
      return onSectionLine(sec, vp.screenToWorld(sp), hitCtx()) ? path([sec.a, sec.b])
        : closed(((sv) => sectionViewOutline(sec, sv.instances, sv.walls, sv.polygons, sv.pipes))(sectionSources()));
    }
    case 'wall': {
      const w = (state.drawing.walls || []).find((x) => x.id === r.id);
      return w ? (w.style === 'line' ? path(w.points) : closed(objectOutline(w.id))) : null;
    }
    case 'leader': { const l = (state.drawing.leaders || []).find((x) => x.id === r.id); return l ? path(l.points) : null; }
    case 'conn': { const c = state.drawing.connections.find((x) => x.id === r.id); const p = c ? connectionPath(c) : null; return p ? path(p) : null; }
    case 'arc': { const a = (state.drawing.arcs || []).find((x) => x.id === r.id); return a ? path(arcSamplePts(a)) : null; }
    case 'text': case 'instance': case 'dim': case 'flooring': case 'polygon': case 'cloud': case 'rcp': return closed(objectOutline(r.id));
    default: return unreachable(r.kind, 'stack kind');
  }
}

/** The hover target at the cursor: the head of `objectStackAt`, so the outline
 *  never promises one object while the click takes another. It yields (null) to
 *  the selection's handle under the cursor (`hit`, from `handleAt`: a grip, a
 *  knob, a heater's guard-cover edge, a polygon's edge…), a view's title or a
 *  wall-chain joint — those take the press first and have their own cue — and
 *  to what is already selected. */
function hoverTargetAt(world: Vec2, sp: Vec2, hit: HandleHit | null, headOf: () => ObjRef | null = () => objectStackAt(world, sp).head): { key: string; ring: Vec2[]; closed: boolean } | null {
  if (hit || viewTitleAt(sp) || wallSpanJointAt(sp)) return null;
  // Over a connection's tag: the press takes that connection, so outline it.
  const tagId = pickableConnTagAt(sp);
  if (tagId) {
    if (tagId === state.selectedConnId) return null;
    const ref: ObjRef = { kind: 'conn', id: tagId };
    const o = refOutline(ref, sp);
    return o ? { key: refKey(ref), ...o } : null;
  }
  const head = headOf();
  // In a selected or drilled-into section view, what a click there picks.
  if (head?.kind === 'section' && sectionDrills(head.id)) {
    const sec = drillingSectionAt(sp);
    if (sec && sec.id === head.id) return sectionHoverAt(sec, world, sp);
  }
  if (!head || selectedRefs().some((r) => refKey(r) === refKey(head))) return null;
  // What the click takes: a group's member outlines its whole group.
  const take = clickTake(state.drawing, head, selectedObjectIds());
  if ('drill' in take) return null;
  if ('ids' in take) {
    const pts = take.ids.flatMap((id) => objectOutline(id) ?? []);
    if (!pts.length) return null;
    const xs = pts.map((p) => p.x), ys = pts.map((p) => p.y);
    const x0 = Math.min(...xs), x1 = Math.max(...xs), y0 = Math.min(...ys), y1 = Math.max(...ys);
    return { key: `group:${take.ids.join(',')}`, ring: [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }], closed: true };
  }
  const o = refOutline(head, sp);
  return o ? { key: refKey(head), ...o } : null;
}

/** World outline of any drawing object by id (a placed part, an underlay, the
 *  imported DXF `__dxf__`, a polygon, wall, floor, text, dimension, section,
 *  leader, arc or revision cloud) — the Parts tab's hover ring. */
export function objectOutline(id: string): Vec2[] | null {
  if (id === '__dxf__') {
    const b = bgGeomBounds();
    return b ? [{ x: b.min.x, y: b.min.y }, { x: b.max.x, y: b.min.y }, { x: b.max.x, y: b.max.y }, { x: b.min.x, y: b.max.y }] : null;
  }
  const bi = bgImages().find((b) => b.id === id);
  if (bi) return pdfBoxCorners(bi);
  if (id.startsWith('table:')) return outlineOf(OBJECTS, 'table', null, id);
  const it = layerItems(state.drawing).find((x) => x.id === id);
  return it ? outlineOf(OBJECTS, it.kind, it.obj, id) : null;
}

/** Each drawing kind's adapter (drawingObjects.ts): where its objects live, its
 *  points and outline (the Parts-tab row's hover, Zoom to, the hover outline of
 *  what has no ring of its own), how it moves, transforms, mirrors, rises and is
 *  copied. A Record over ItemKind: a new kind does not typecheck until it says
 *  each. What only main.ts knows comes in here. */
export const OBJECTS = objectAdapters({
  drawing: () => state.drawing,
  instanceOutline,
  // A polygonal room's real corners, not its bounding box, so a reshaped room
  // snaps by an actual wall corner; node objects their node line; anything else
  // its footprint (a heater block its real outlines).
  instancePoints: (i) => (i.nodes && i.nodes.length ? i.nodes : isPolyRoom(i) ? i.polygon!.map((p) => instanceToWorld(i, p)) : instanceProbeCorners(i)),
  instanceBase,
  instanceHeight: (i) => partHeight(i, productOf(i)),
  regenerate: regenerateSauna,
  roomContents,
  resizeLocked,
  // A tilted part is mirrored whole: its tilt mirrors with it.
  mirrorTilted: (inst, a, b) => {
    if (!isTilted(inst)) return false;
    const dx = b.x - a.x, dy = b.y - a.y, l = Math.hypot(dx, dy) || 1;
    placePart(inst, mirrorBody(tiltBody(inst), { x: a.x, y: a.y, z: 0 }, { x: -dy / l, y: dx / l, z: 0 }));
    return true;
  },
  connectionPath,
  rcpOutline: rcpViewOutline,
  tableOutline: (id) => {
    const kind = schedKindOf(id);
    const L = kind ? curScheduleLayout(kind) : null;
    if (!L) return null;
    const a = vp.screenToWorld({ x: L.originX, y: L.originY }), b = vp.screenToWorld({ x: L.originX + L.widthPx, y: L.originY + L.totalH });
    return [{ x: a.x, y: a.y }, { x: b.x, y: a.y }, { x: b.x, y: b.y }, { x: a.x, y: b.y }];
  },
  textHeight: COMMENT_FONT_MM,
});

/** A part's outline as it is drawn: a poly-room its polygon, a heater block its
 *  outer edge, a node object its mitred ribbon, anything else its footprint. */
export function instanceOutline(inst: ProductInstance): Vec2[] {
  if (isPolyRoom(inst) && inst.polygon && inst.polygon.length >= 3) return inst.polygon.map((p) => instanceToWorld(inst, p));
  if (inst.nodes && inst.nodes.length >= 2) return ribbonOutline(inst.nodes, inst.depth);
  if (drawsHeaterBlock(inst)) return partOuterCorners(inst).map((p) => instanceToWorld(inst, p));
  return instanceCorners(inst);
}

/** The topmost underlay whose (possibly rotated) box contains the world point. */
function bgImageAt(world: Vec2): BackgroundImage | null {
  const imgs = bgImages();
  for (let i = imgs.length - 1; i >= 0; i--) {
    const bi = imgs[i];
    const lp = rotatePoint({ x: world.x - bi.x, y: world.y - bi.y }, -(bi.rotation || 0));
    if (Math.abs(lp.x) <= bi.widthMm / 2 && Math.abs(lp.y) <= bi.heightMm / 2) return bi;
  }
  return null;
}
/** Ready-to-draw underlays (decoded image + geometry), in order. */
export function bgImagesForRender(): RenderOptions['backgroundImages'] {
  const map = bgImageMap();
  const out: NonNullable<RenderOptions['backgroundImages']> = [];
  for (const bi of bgImages()) {
    const img = map.get(bi.dataUrl);
    if (img) { const sc = bi.id ? sharpCropFor(bi.id) : null; out.push({ img, ...bi, sharp: sc ? { canvas: sc.canvas, x: sc.x, y: sc.y, widthMm: sc.widthMm, heightMm: sc.heightMm } : undefined }); }
  }
  return out;
}

/** Re-rasterise the visible slice of any zoomed-in PDF underlay so it stays
 *  crisp. Debounced; repaints the backdrop when a sharper crop is ready. */
function sharpenPdfUnderlays(): void {
  if (activeSheet() || state.viewTab === '3d') return;
  const map = bgImageMap();
  const items = bgImages()
    .filter((b) => b.pdfData && b.id)
    .map((b) => ({ bi: b, baseNaturalW: (map.get(b.dataUrl) as HTMLImageElement | undefined)?.naturalWidth ?? 0 }));
  if (items.length) scheduleSharpen(items, vp, viewW(), viewH(), () => { redrawBackdrop(); invalidate(); });
}

// Loaded raster drawings for image-based product instances, keyed by data URL.
const instImageCache = new Map<string, HTMLImageElement>();
/** Map of data URL → loaded image for every image-based instance (loads lazily). */
export function instanceImageMap(): Map<string, CanvasImageSource> {
  const ready = new Map<string, CanvasImageSource>();
  for (const inst of state.drawing.instances) {
    const url = inst.imageDataUrl;
    if (!url || ready.has(url)) continue;
    let img = instImageCache.get(url);
    if (!img) {
      img = new Image();
      img.onload = () => invalidate();
      img.src = url;
      instImageCache.set(url, img);
    }
    if (img.complete && img.naturalWidth > 0) ready.set(url, img);
  }
  return ready;
}

// ---- Undo / redo history: undoHistory.ts owns the steps and their rules ----
/** The ONE undo stack (the title block's steps on it too) and the step under
 *  way. Nothing outside undoHistory.ts touches either (docs/claude/undo.md). */
export const undoSteps = createUndoHistory<string, DrawingSnapshot>({
  snapshot: snapshotDrawing,
  same: sameSnapshot,
  drawing: () => state.drawing,
  restore: restoreDrawing,
  swapForeign: swapTitleCart,
  recorded: () => {
    changes.touch();
    scheduleSheetThumbs();
    markDirty(); // an undoable mutation is about to happen (or a gesture's just did)
    noteRecoveryChange(); // schedule a local autosave for crash/reload recovery
  },
  gestureEnded: endGestureSetbacks,
  stacksChanged: updateUndoButtons,
});
/** The setbacks each truncated pyramid or coved ceiling had when the gesture under way began (a
 *  drag fits from them, so one that narrows a room past its flat and widens it
 *  again ends where it started), and the messages the gesture has flashed —
 *  each once. Empty outside a gesture. */
export const gestureSetbacks = new Map<string, number[]>();
const gestureFlashes = new Set<string>();
function endGestureSetbacks(): void { gestureSetbacks.clear(); gestureFlashes.clear(); }
export function snapshotDrawing(): DrawingSnapshot {
  return snapshotOf(state.drawing);
}
/** A change that belongs to the undo step already recorded — a kit's projector
 *  placed a click after the kit was laid: the drawing is dirty, saved again for
 *  recovery and its caches renewed, with no step of its own. */
export function noteChangeInStep(): void {
  changes.touch();
  markDirty();
  noteRecoveryChange();
}
/** The drawing now differs from its saved copy: say so in the title (`*`). */
function markDirty(): void {
  if (state.docDirty) return;
  state.docDirty = true;
  updateTitle();
}
export function restoreDrawing(snap: DrawingSnapshot): void {
  cancelArmed(); // what it would act on may be gone
  state.drawing = drawingOf<Drawing>(snap);
  endGestureSetbacks();
  elecLetGo();
  bumpBackground();
  updateTitle();
  if (!state.drawing.instances.some((i) => i.id === state.selectedId)) state.selectedId = null;
  if (!state.drawing.dimensions.some((d) => d.id === state.selectedDimId)) state.selectedDimId = null;
  // The sheets may have come, gone or moved: an open sheet that no longer exists
  // falls back to the model; either way the tabs and thumbnails follow.
  const open = state.viewTab;
  if (open !== 'model' && open !== '3d' && !drawingSheets().some((sh) => sh.id === open)) setViewTab('model');
  else renderViewTabs();
  constraintSolved(); // the restored drawing is the new baseline, not an edit
  supportsBaseline();
  renderProperties();
  noteRecoveryChange(); // undo/redo changed the drawing — refresh (or clear) the draft
  invalidate();
}
// An armed pick, a drag under way, or a handle being worked, is put back first:
// that is the step undo takes (a grip's, a drag on a schematic's view, too).
export function undo(): void { if (!cancelArmed() && !cancelUnderWay() && !cancelElecDrag()) undoSteps.undo(); }
export function redo(): void { if (!cancelArmed() && !cancelUnderWay() && !cancelElecDrag()) undoSteps.redo(); }
function updateUndoButtons(): void {
  const u = document.getElementById('btn-undo') as HTMLButtonElement | null;
  const r = document.getElementById('btn-redo') as HTMLButtonElement | null;
  if (u) setButtonEnabled(u, undoSteps.canUndo(), 'Nothing to undo');
  if (r) setButtonEnabled(r, undoSteps.canRedo(), 'Nothing to redo');
}

// ---- Ctrl-drag copies (AutoCAD and PowerPoint alike) ----
// A move drag with Ctrl held leaves a copy behind: on the drag's first frame the
// selection is captured as a clipboard payload (before anything has moved), and at
// the drop — Ctrl still held — that payload is inserted where the objects started,
// inside the drag's own gesture, so drag + copy is ONE undo step. The dragged
// objects keep the selection, as in PowerPoint. Each of these drags is a move
// session (`planMove`), which takes the copy and leaves it.
const COPY_DRAG_MODES = new Set(['move', 'move-all', 'move-text', 'move-floor']);

/** Paste the clipboard and select the new copy: centred on `at` (world) when
 *  given — Ctrl+V with the pointer on the drawing, or the right-click point —
 *  else stepped away from the originals (Edit ▸ Paste). New ids are minted
 *  throughout; a copied group becomes a fresh group and its connections are
 *  remapped to the new instance ids. */
export function pasteClipboard(at?: Vec2): void {
  if (!clipboardUi.clipboard) { flashClipboardElsewhere('model'); return; }
  const cb = clipboardUi.clipboard;
  if (!clipboardHasContent(cb)) return;
  const b = at ? clipboardBounds(cb) : null;
  const step = PASTE_OFFSET * (clipboardUi.pasteCount + 1);
  const off = at && b ? { x: at.x - (b.min.x + b.max.x) / 2, y: at.y - (b.min.y + b.max.y) / 2 } : { x: step, y: step };
  pushHistory();
  insertClipboard(cb, off);
  if (!at) clipboardUi.pasteCount++;
  renderProperties();
  invalidate();
}

/** The world box a payload covers — what Paste centres on the cursor. */
function clipboardBounds(cb: Clipboard): Bounds | null {
  const pts: Vec2[] = [];
  for (const kind of STORED_KINDS) for (const o of objectsOf(cb, kind) ?? []) pts.push(...(outlineOf(OBJECTS, kind, o, o.id) ?? []));
  for (const bg of cb.backgroundImages) pts.push(...pdfBoxCorners(bg));
  if (!pts.length) return null;
  return {
    min: { x: Math.min(...pts.map((p) => p.x)), y: Math.min(...pts.map((p) => p.y)) },
    max: { x: Math.max(...pts.map((p) => p.x)), y: Math.max(...pts.map((p) => p.y)) },
  };
}

/** Add copies of a payload's objects to the drawing, mapped through `m` and
 *  risen `rise` mm: the ONE copy (paste, Duplicate, a Ctrl-drag, Mirror's copy,
 *  the arrays, Transform ▸ Copy) — fresh ids, groups adopted, references pointed
 *  at the copies (drawingObjects.ts `copyObjects`). The copies keep their
 *  originals' layers (stamped in the payload) and inner stacking, and a part
 *  whose room was not copied anchors in the room it lands in. Selects nothing. */
function placeCopies(cb: Clipboard, m: PointMap, rise = 0, prepare?: (added: CopyResult['added']) => void): CopyResult {
  const before = layerIdSet();
  const res = copyObjects(state.drawing, cb, m, OBJECTS, { uid: (p) => newId(p), rise, prepare });
  for (const c of res.added.instances ?? []) if (!c.roomAnchor) updateRoomAnchor(c);
  if (cb.stack?.length) { dropLayerCache(); copyInnerOrder(state.drawing, LAYER_ENV, cb.stack, res.idMap); dropLayerCache(); }
  for (const id of layerIdSet()) if (!before.has(id)) freshCopies.add(id); // copies keep their layer
  const views = res.left.filter((it) => it.kind === 'rcp').length;
  if (views) flashStatus(tr('{n} ceiling plan(s) left out: their room is not in this drawing.').replace('{n}', String(views)));
  return res;
}

/** Add fresh copies of a clipboard payload to the drawing, moved by `off` (mm),
 *  and select them. No history of its own: Paste pushes one first, and a
 *  Ctrl-drag copy lands inside its drag's gesture. A payload may come from
 *  another window or app version: its parts are brought up to date as an opened
 *  drawing's are (`upgradeParts`, drawingUpgrade.ts). */
export function insertClipboard(cb: Clipboard, off: Vec2): void {
  leaveEditModes();
  clearSelection(state);

  const { added } = placeCopies(cb, translation(off.x, off.y), 0, (a) => upgradeParts(state.drawing, a.instances ?? [], UPGRADE_ENV));
  // Imported underlays (PDF / image): paste a fresh copy, offset, and enter its
  // transform mode so it's selected and immediately draggable.
  let lastBgId: string | null = null;
  if (cb.backgroundImages.length) revealImported(); // a pasted underlay never lands out of sight
  for (const src of cb.backgroundImages) {
    const copy = structuredClone(src);
    copy.id = genBgId();
    copy.x += off.x;
    copy.y += off.y;
    (state.drawing.backgroundImages ??= []).push(copy);
    lastBgId = copy.id;
  }
  if (lastBgId) enterEdit({ kind: 'underlay', id: lastBgId });
  // Select everything that was pasted, as each kind's adapter says (a section or
  // a ceiling plan alone, never in a multi-selection; a connection with its parts).
  const { sel, alone } = pastedSelection(added, OBJECTS);
  selectObjects(state, sel);
  if (alone && !lastBgId) selectRef(alone);
}

// ---- Copy the connection/preparation schedule to the clipboard ----

/** Build the schedule as an HTML table (for Word) plus a tab-separated fallback. */
function scheduleClipboard(): { html: string; text: string } {
  const lang = state.drawing.language ?? 'it';
  const s = scheduleStrings(lang);
  const filt = (g: ScheduleGroup): boolean => state.disciplineFilters[g.type] !== false;
  const conns = connectionSchedule(state.drawing).filter(filt);
  const preps = preparationSchedule(state.drawing).filter(filt);
  const esc = (str: string): string => str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  let html = '';
  let text = '';
  const section = (title: string, groups: ScheduleGroup[], col: string): void => {
    if (!groups.length) return;
    html += `<h3>${title}</h3><table border="1" cellspacing="0" cellpadding="4" style="border-collapse:collapse;font-family:sans-serif;font-size:11pt">`;
    html += `<tr><th>${s.no}</th><th>${s.discipline}</th><th>${col}</th><th>${s.description}</th></tr>`;
    text += `${title}\n${s.no}\t${s.discipline}\t${col}\t${s.description}\n`;
    for (const g of groups) for (const r of g.rows) {
      html += `<tr><td>${esc(r.num)}</td><td>${typeLabel(g.type, lang)}</td><td>${esc(r.text)}</td><td>${esc(r.label)}</td></tr>`;
      text += `${r.num}\t${typeLabel(g.type, lang)}\t${r.text}\t${r.label}\n`;
    }
    html += '</table>';
    text += '\n';
  };
  section(s.connectionsHeading, conns, s.connectionCol);
  section(s.preparationsHeading, preps, s.partCol);
  // The Lighting table: a row per tag in each room, then the total power.
  const lights = state.disciplineFilters.lighting !== false ? lightingRows(state.drawing) : [];
  if (lights.length) {
    const cols = [s.tagCol, s.lightCol, s.roomCol, s.qtyCol, s.powerCol];
    html += `<h3>${s.lightingTitle}</h3><table border="1" cellspacing="0" cellpadding="4" style="border-collapse:collapse;font-family:sans-serif;font-size:11pt">`;
    html += `<tr>${cols.map((c) => `<th>${c}</th>`).join('')}</tr>`;
    text += `${s.lightingTitle}\n${cols.join('\t')}\n`;
    const total = `${Math.round(lights.reduce((m, r) => m + r.watts, 0))} W`;
    for (const r of [...lights.map((l) => [l.tag, l.light, l.room, l.qty, l.power]), ['', s.total, '', '', total]]) {
      html += `<tr>${r.map((c) => `<td>${esc(c)}</td>`).join('')}</tr>`;
      text += `${r.join('\t')}\n`;
    }
    html += '</table>';
  }
  return { html, text };
}

/** Copy the schedule to the clipboard as a formatted table (pastes into Word). */
async function copySchedule(): Promise<void> {
  const { html, text } = scheduleClipboard();
  if (!text.trim()) { flashStatus(tr('Nothing to copy — the schedule is empty.')); return; }
  try {
    await navigator.clipboard.write([
      new ClipboardItem({
        'text/html': new Blob([html], { type: 'text/html' }),
        'text/plain': new Blob([text], { type: 'text/plain' }),
      }),
    ]);
    flashStatus(tr('Schedule copied to the clipboard.'));
  } catch {
    try { await navigator.clipboard.writeText(text); flashStatus(tr('Schedule copied (plain text).')); } catch { /* ignore */ }
  }
}

/** Briefly show a message in the status bar. */
let statusTimer: number | undefined;
export function flashStatus(msg: string): void {
  const el = $('status-info');
  el.textContent = msg;
  cmdLog(msg, true);
  if (statusTimer) clearTimeout(statusTimer);
  statusTimer = window.setTimeout(() => { updateStatus(); }, 2500);
}

// ---- Drawing properties (File ▸ Drawing Properties) ----

/** The window title: the app name, plus the commessa / descrizione when set. No
 *  version — it belongs in Help ▸ About, not in every browser tab and taskbar. */
export function updateTitle(): void {
  const m = state.drawing.meta;
  const tags = [m?.commessa, m?.descrizione].map((s) => (s || '').trim()).filter(Boolean);
  // A `*` marks unsaved changes, in the window title and beside the file name.
  const star = state.docDirty ? ' *' : '';
  document.title = (tags.length ? `StenalCAD — ${tags.join(' · ')}` : 'StenalCAD') + star;
  // The current file/project name, mirrored to the web menu bar. Stored on <body>
  // too so it survives a menu rebuild (language switch) without a stale value.
  const label = doc.savedProjectName ? doc.savedProjectName + star : '';
  document.body.dataset.projectName = label;
  const el = document.getElementById('webmenu-project');
  if (el) el.textContent = label;
}

/** Whether the current user may edit the shared standards (catalogue / benches /
 *  colours / title blocks). Desktop build ⇒ always true; web build ⇒ the user's
 *  `edit` claim from stenal.net. Read-only users can view but not save these, and
 *  don't see the controls to edit them. */
export function canEditStandards(): boolean {
  return platform.canEditStandards ? platform.canEditStandards() : true;
}

/** The signed-in user's id (web build), for owner-vs-member cloud-sharing UI.
 *  Empty on desktop / when signed out. */
export function myUserId(): string {
  return platform.currentUserId ? platform.currentUserId() : '';
}

/** My area manager's id, '' when I have none. The server puts it in the acl of
 *  every project I create, so it is access nobody granted by hand. */
export function myAreaManagerId(): string {
  return platform.currentUserAreaManager ? platform.currentUserAreaManager() : '';
}

/** Whether the signed-in user has the `commerciale` claim (sees every user's
 *  cloud projects, and may filter the cloud gallery by creator). */
export function amCommerciale(): boolean {
  return platform.isCommerciale ? platform.isCommerciale() : false;
}

function openDrawingProperties(): void {
  const m = state.drawing.meta || {};
  ($('dp-commessa') as HTMLInputElement).value = m.commessa || '';
  ($('dp-descrizione') as HTMLInputElement).value = m.descrizione || '';
  ($('dp-versione') as HTMLInputElement).value = m.versione || '';
  ($('dp-data') as HTMLInputElement).value = m.data || '';
  ($('dp-autore') as HTMLInputElement).value = m.autore || '';
  ($('dp-language') as HTMLSelectElement).value = state.drawing.language ?? 'it';
  ($('dp-dimstyle') as HTMLSelectElement).value = drawingDimStyle(state.drawing);
  const beams = state.drawing.lightBeams ?? BEAM_DEFAULTS;
  ($('dp-beam-linear-len') as HTMLInputElement).value = String(beams.linear.length);
  ($('dp-beam-linear-angle') as HTMLInputElement).value = String(beams.linear.angle);
  ($('dp-beam-spot-len') as HTMLInputElement).value = String(beams.spot.length);
  ($('dp-beam-spot-angle') as HTMLInputElement).value = String(beams.spot.angle);
  $('drawprops-modal').classList.remove('hidden');
  ($('dp-commessa') as HTMLInputElement).focus();
}

function applyDrawingProperties(): void {
  const val = (id: string): string | undefined => (($(id) as HTMLInputElement).value.trim() || undefined);
  const meta: DrawingMeta = {
    commessa: val('dp-commessa'),
    descrizione: val('dp-descrizione'),
    versione: val('dp-versione'),
    data: val('dp-data'),
    autore: val('dp-autore'),
  };
  const hasAny = !!(meta.commessa || meta.descrizione || meta.versione || meta.data || meta.autore);
  pushHistory();
  state.drawing.meta = hasAny ? meta : undefined;
  state.drawing.language = ($('dp-language') as HTMLSelectElement).value as Lang;
  state.drawing.dimStyle = ($('dp-dimstyle') as HTMLSelectElement).value as DimStyle;
  // The beams' sizes: kept only where they differ from the defaults.
  const cone = (len: string, ang: string, d: { length: number; angle: number }): { length: number; angle: number } => {
    const l = parseMeasure(($(len) as HTMLInputElement).value), a = parseFloat(($(ang) as HTMLInputElement).value);
    return { length: l != null && l > 0 ? Math.round(l) : d.length, angle: Number.isFinite(a) && a > 0 && a < 180 ? Math.round(a) : d.angle };
  };
  const beams = { linear: cone('dp-beam-linear-len', 'dp-beam-linear-angle', BEAM_DEFAULTS.linear), spot: cone('dp-beam-spot-len', 'dp-beam-spot-angle', BEAM_DEFAULTS.spot) };
  if (JSON.stringify(beams) === JSON.stringify(BEAM_DEFAULTS)) delete state.drawing.lightBeams; else state.drawing.lightBeams = beams;
  $('drawprops-modal').classList.add('hidden');
  updateTitle();
  renderProductList(); // product names follow the drawing language
  renderProperties();
  invalidate();
}

function closeDrawingProperties(): void {
  $('drawprops-modal').classList.add('hidden');
}


// =====================================================================
// Canvas sizing & render loop
// =====================================================================
function resizeCanvas(): void {
  const dpr = window.devicePixelRatio || 1;
  const rect = canvas.getBoundingClientRect();
  const pw = Math.max(1, Math.round(rect.width * dpr));
  const ph = Math.max(1, Math.round(rect.height * dpr));
  canvas.width = pw;
  canvas.height = ph;
  bgCanvas.width = pw;
  bgCanvas.height = ph;
  redrawBackdrop(); // force the backdrop to repaint at the new size
  invalidate();
}

/** Signature of everything the static backdrop depends on (viewport + backdrop). */
function backdropSignature(): string {
  if (state.viewTab === '3d') return '3d';
  const sh = activeSheet();
  if (sh) return `sheet:${sh.id}:${sh.widthMm}x${sh.heightMm}:${viewW()}x${viewH()}:${sheetSpace.paperCam.zoom.toFixed(3)}:${sheetSpace.paperCam.pan.x.toFixed(1)},${sheetSpace.paperCam.pan.y.toFixed(1)}`;
  const pdf = bgImages().map((bi) => `${bi.id}:${bi.x},${bi.y},${bi.widthMm},${bi.heightMm},${bi.rotation || 0},${bi.opacity ?? 1}`).join('|') || '0';
  const off = state.drawing.backgroundOffset;
  const dxf = off ? `${off.x},${off.y}` : '0';
  return `${vp.scale},${vp.tx},${vp.ty},${state.showGrid ? 1 : 0},${state.showHatches ? 1 : 0},${bgVersion},${pdf},${dxf},${importedHidden() ? 'H' : ''},${drawingEditor.drawingEditId ?? ''}`;
}

/** Repaint the background canvas only when the viewport or backdrop changed. */
function renderBackdropIfNeeded(): void {
  const sig = backdropSignature();
  if (!backdropChanged(sig)) return;
  if (state.viewTab === '3d') { const d = bgCanvas.width / (viewW() || 1); bgCtx.setTransform(1, 0, 0, 1, 0, 0); bgCtx.clearRect(0, 0, bgCanvas.width, bgCanvas.height); bgCtx.setTransform(d, 0, 0, d, 0, 0); return; }
  const sheet = activeSheet();
  if (sheet) { drawSheetBackdrop(sheet); return; }
  renderBackground(bgCtx, vp, state.drawing, {
    drawingEditId: drawingEditor.drawingEditId, // the editor's lighter page
    showGrid: state.showGrid,
    showHatches: state.showHatches,
    selectedId: null,
    backgroundImages: bgImagesForRender(),
    hideImported: importedHidden(),
  });
  sharpenPdfUnderlays(); // keep zoomed-in PDF underlays crisp (debounced)
}

/** Paper-space backdrop: grey field with a white page (drawn on the bg canvas). */
function drawSheetBackdrop(sh: Sheet): void {
  const cw = viewW(), ch = viewH();
  const dpr = bgCanvas.width / cw || 1;
  bgCtx.save();
  bgCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
  bgCtx.clearRect(0, 0, cw, ch);
  bgCtx.fillStyle = '#2b2e33';
  bgCtx.fillRect(0, 0, cw, ch);
  const pv = paperViewOf(sh, cw, ch, sheetSpace.paperCam); // zoom + pan, so the page scales with its content
  const x = pv.ox, y = pv.oy, w = sh.widthMm * pv.scale, h = sh.heightMm * pv.scale;
  bgCtx.fillStyle = 'rgba(0,0,0,0.35)';
  bgCtx.fillRect(x + 4, y + 6, w, h);
  bgCtx.fillStyle = '#ffffff';
  bgCtx.fillRect(x, y, w, h);
  bgCtx.restore();
}

/** Keep selectedIds consistent with selectedId (single-select setters only touch
 *  the latter) and drop ids for instances that no longer exist. */
function reconcileSelection(): void {
  dropUnpickableSelection();
  if (state.selectedViewTitle && (!viewOfTitle(state.selectedViewTitle) || viewOfTitle(state.selectedViewTitle)!.titleHidden || objUnpickable(state.selectedViewTitle.id))) state.selectedViewTitle = null;
  // Every kind's list kept in step with its primary, what is gone dropped
  // (selection.ts) — single-select setters only touch the primary.
  reconcileSelected(state, (kind, id) => hasObject(state.drawing, kind, id), state.drawing.instances);
  // Drop an edge selection whose polygon is gone, no longer the primary, or out of range.
  const ep = state.selectedPolyEdge;
  if (ep) {
    const poly = (state.drawing.polygons || []).find((p) => p.id === ep.id);
    if (!poly || ep.id !== state.selectedPolyId || ep.edge >= poly.points.length) state.selectedPolyEdge = null;
  }
}

/** A layer hidden or locked (by its toggle, an undo, a load) takes its objects
 *  out of the selection, and out of any edit mode they were in. */
function dropUnpickableSelection(): void {
  const gone = (id: string | null | undefined): boolean => !!id && objUnpickable(id);
  dropFromSelection(state, objUnpickable); // every kind, and a feature with its room
  // A drill ends with its view, or once nothing it picked is selected.
  if (state.sectionDrill && (gone(state.sectionDrill) || !(state.drawing.sections || []).some((s) => s.id === state.sectionDrill) || !sectionTargets().length)) state.sectionDrill = null;
  if (state.selectedScheduleTable && gone(schedTableId(state.selectedScheduleTable))) state.selectedScheduleTable = null;
  if (gone(state.polyRoomVertexEditId)) exitPolyRoomVertexEdit();
  if (gone(state.editVerticesId)) leaveEdit('shape');
  if (importedHidden() || importedLocked()) leaveEdit('underlay', 'plan');
}

/** Live preview of the circle/ellipse being drawn, or null. */
function circlePreview(): { cx: number; cy: number; rx: number; ry: number; center: Vec2; hover: Vec2 } | null {
  if (state.tool !== 'circle' || !circleCenter || !state.hoverWorld) return null;
  const c = circleCenter, h = state.hoverWorld;
  if (ellipseRx == null) {
    const r = Math.round(distance(c, h));
    return { cx: c.x, cy: c.y, rx: r, ry: r, center: c, hover: h };
  }
  return { cx: c.x, cy: c.y, rx: ellipseRx, ry: Math.round(distance(c, h)), center: c, hover: h };
}

/** Nothing in the model at all — no part, shape, wall, annotation or underlay. */
function drawingIsEmpty(): boolean {
  const d = state.drawing;
  return !d.instances.length && !(d.polygons?.length) && !(d.walls?.length) && !(d.texts?.length)
    && !d.dimensions.length && !(d.arcs?.length) && !(d.leaders?.length) && !(d.revisionClouds?.length)
    && !(d.floorings?.length) && !(d.sections?.length) && !(d.ceilingPlans?.length) && !hasBgGeom() && !bgImages().length;
}
const TIPS_OFF_KEY = 'stenal.tipsOff';
let tipShown = '';
/** The "Did you know?" line in the command bar (tips.ts): shown while Select is
 *  idle and nothing is typed, picked by what is on screen, turning every 40 s. */
function updateTip(): void {
  let off = false;
  try { off = localStorage.getItem(TIPS_OFF_KEY) === '1'; } catch { /* storage blocked */ }
  const input = document.getElementById('cmd-input') as HTMLInputElement | null;
  const idle = !off && state.tool === 'select' && !armed() && !input?.value && !(activeSheet() && schTool !== 'select');
  const inst = selectedInstance();
  const text = idle ? pickTip({
    heaterSelected: !!inst && traitsOf(inst.category).placedAsHeater,
    roomSelected: !!inst?.autoBenches,
    hasUnderlay: bgImages().length > 0 || hasBgGeom(),
    partsTabOpen: state.propTab === 'parts' && !inst,
    sheetOpen: !!activeSheet(),
  }, Math.floor(Date.now() / 40000)) : '';
  if (text === tipShown) return;
  tipShown = text;
  const el = $('cmd-tip');
  el.textContent = '';
  if (!text) return;
  const b = document.createElement('b'); b.textContent = tr('Did you know?');
  const close = document.createElement('button'); close.textContent = '×'; close.title = tr('Hide tips');
  close.addEventListener('click', () => { try { localStorage.setItem(TIPS_OFF_KEY, '1'); } catch { /* storage blocked */ } updateTip(); });
  el.append(b, ` ${tr(text)}`, close);
}

let emptyStateShown = false;
/** The start panel shows on an empty model while nothing is armed or drafting. */
function updateEmptyState(): void {
  const show = state.viewTab === 'model' && state.tool === 'select' && !armed() && drawingIsEmpty();
  if (show === emptyStateShown) return;
  emptyStateShown = show;
  $('empty-state').classList.toggle('hidden', !show);
}

// ---- Layers (renderer/layers.ts) ----
/** Parts being moved (a drag, a base-point Move): the room each stood in and
 *  the layer it sat on when the move began. Nothing changes layer under the
 *  hand — a part dragged into a room on a locked layer would otherwise lock
 *  (and leave the selection) half way. */
let dragHeld: Map<string, { host: ProductInstance | null; layer: string }> | null = null;
/** What layers.ts needs from here: what is a room, and the room a part stands in. */
export const LAYER_ENV: LayerEnv = {
  isRoom: (i) => !!i.autoBenches || isPolyRoom(i),
  // The part being drawn in the product drawing editor nests in nothing; a part
  // being moved keeps the nest it had when the move began (`holdDragLayers`).
  hostRoom: (i) => (i.id === drawingEditor.drawingEditId ? null : dragHeld?.has(i.id) ? dragHeld.get(i.id)!.host : hostRoomOf(i)),
};
function holdDragLayers(ids: readonly string[]): void {
  const m = layers();
  dragHeld = new Map();
  for (const id of ids) {
    const inst = state.drawing.instances.find((i) => i.id === id);
    const lid = m.layerOf.get(id);
    if (inst && lid && !LAYER_ENV.isRoom(inst)) dragHeld.set(id, { host: hostRoomOf(inst), layer: lid });
  }
  dropLayerCache();
}
/** The drop: a part now standing in a room on a hidden or locked layer — which
 *  would put it out of reach — stays on the layer it was moved from. */
function releaseDragLayers(): void {
  const held = dragHeld;
  dragHeld = null;
  dropLayerCache();
  if (!held) return;
  const out = [...held].filter(([id]) => state.drawing.instances.some((i) => i.id === id) && objUnpickable(id));
  if (!out.length) return;
  // Store the layers as they were UNDER the hold: the layer a part came from may
  // be a home recreated only for it (its own was deleted), which the model with
  // the part nested no longer supplies — the stamp would name nothing.
  dragHeld = held;
  materializeLayers(state.drawing, LAYER_ENV);
  dragHeld = null;
  for (const [id, { layer }] of out) state.drawing.instances.find((i) => i.id === id)!.layer = layer;
  dropLayerCache();
}

// ---- Moving the selection (moveSession.ts): the app's side ----
/** The copy a move leaves where the objects stood, and what was selected. */
type MoveCopy = { cb: Clipboard; sel: ObjectSel };
/** One move of the selection: every gesture's (docs/claude/objects.md, *Moving the selection*). */
export type SelectionMove = MoveSession<UndoMark>;
/** A move session holds the layers (a drag's leftovers must not let them go). */
let moveHolds = false;
/** What a copy left behind selects again: the selection's kinds, and a floor (selected alone). */
const COPY_KINDS: readonly ItemKind[] = [...MULTI_KINDS, 'flooring'];
const MOVE_HOST: MoveHost<MoveCopy, UndoMark> = {
  adapters: OBJECTS,
  carryOf: roomsCarry,
  hold: (ids) => { holdDragLayers(ids); moveHolds = true; },
  release: (keep) => {
    moveHolds = false;
    if (keep) releaseDragLayers();
    else { dragHeld = null; dropLayerCache(); }
  },
  remount: remountParts,
  syncDimensions,
  takeCopy: () => {
    const cb = roomFeatureSelected() ? null : selectionClipboard();
    return cb ? { cb, sel: selectedObjects(state, COPY_KINDS) } : null;
  },
  leaveCopy: leaveCopyBehind,
  steps: undoSteps,
};
/** Begin moving `items` — the selection's whole objects, by default. */
export function beginSelectionMove(opts: MoveOptions<UndoMark>, items: readonly ObjItem[] = selectedItems(state, state.drawing, MULTI_KINDS)): SelectionMove {
  return beginMove(MOVE_HOST, items, opts);
}
/** A move in one step (a key, a typed figure, Transform, Mirror, Align): one
 *  undo step, as `pushHistory` — from `from` when the drawing changed first. */
export function moveSelectionOnce(step: (s: SelectionMove) => void, items: readonly ObjItem[] = selectedItems(state, state.drawing, MULTI_KINDS), from?: UndoMark): void {
  if (!items.length) return;
  const s = beginSelectionMove({ step: 'push', from }, items);
  step(s);
  s.end('keep');
  renderProperties();
  invalidate();
}
/** A copy left where the objects stood (a Ctrl drag, Copy), the moved ones selected again. */
function leaveCopyBehind(c: { cb: Clipboard; sel: ObjectSel }): void {
  insertClipboard(c.cb, { x: 0, y: 0 });
  setObjectSelection(c.sel);
}
let layerCache: { drawing: Drawing; rev: number; model: LayerModel } | null = null;
/** Drop the cached layers: what they read outside the drawing changed (a drag's
 *  held layers), or the drawing just did (it is touched too). */
export function dropLayerCache(): void {
  layerCache = null;
  changes.touch();
}
/**
 * The drawing's layers and its ONE stacking order, shared by the painter, the
 * picker and the Parts tab. Rebuilt when the drawing's revision moves, or when
 * dropped (`dropLayerCache`).
 */
export function layers(): LayerModel {
  const d = state.drawing;
  const rev = drawingRev();
  if (layerCache && layerCache.drawing === d && layerCache.rev === rev) return layerCache.model;
  const model = layerModel(d, LAYER_ENV);
  layerCache = { drawing: d, rev, model };
  return model;
}
export function layerById(id: string | undefined): DrawingLayer | undefined {
  return id ? layers().layers.find((l) => l.id === id) : undefined;
}
/** The layer an object (or a group) sits on. */
export function layerOfObject(id: string): DrawingLayer | undefined {
  return layerById(layers().layerOf.get(id));
}
export function importedLayer(): DrawingLayer {
  return layers().layers[0];
}
export function importedHidden(): boolean {
  return !!importedLayer().hidden;
}
function importedLocked(): boolean {
  return !!importedLayer().locked;
}
export function layerLabel(l: DrawingLayer): string {
  return layerName(l, tr);
}
/** Ids that sat on a layer at the last look, and the drawing they belong to —
 *  so a new object is told apart from the ones that were there (an undo, a
 *  load or a new drawing replaces the drawing and starts the count again). */
let layerKnown: { drawing: Drawing; ids: Set<string> } | null = null;
let layerRevision = -1;
/** What a paste / copy just added: copies keep their original's layer. */
export const freshCopies = new Set<string>();

/**
 * Put what an edit just created on its layer: the current layer when one is
 * chosen (a copy keeps its original's instead). Runs on the frame after the edit,
 * inside the edit's own undo step. And a new object never lands out of sight:
 * on a hidden layer that layer is shown again; on a locked one the bar says so.
 */
function reconcileNewObjects(): void {
  const d = state.drawing;
  const now = layerIdSet();
  if (!layerKnown || layerKnown.drawing !== d) { layerKnown = { drawing: d, ids: now }; freshCopies.clear(); return; }
  const known = layerKnown.ids;
  layerKnown.ids = now;
  const fresh = layerItems(d).filter((it) => !known.has(it.id) && it.kind !== 'table');
  const copies = new Set(freshCopies);
  freshCopies.clear();
  if (!fresh.length) return;
  const cur = partsTab.currentLayerId ? layerById(partsTab.currentLayerId) : undefined;
  let changed = false;
  if (cur && !cur.hidden && !cur.locked) {
    for (const it of fresh) {
      if (copies.has(it.id)) continue;
      // A part added to a group that was already there follows that group.
      if (it.obj.groupId && known.has(it.obj.groupId)) continue;
      it.obj.layer = cur.id;
      changed = true;
    }
  }
  if (changed) dropLayerCache();
  const m = layers();
  const landed = new Set(fresh.map((it) => m.layerOf.get(it.id)).filter((x): x is string => !!x));
  for (const lid of landed) {
    const l = (d.layers || []).find((x) => x.id === lid) ?? m.layers.find((x) => x.id === lid);
    if (!l) continue;
    if (l.hidden) {
      setLayerFlag(d, LAYER_ENV, l.id, 'hidden', false);
      changed = true;
      flashToolStatus(tr('“{name}” was hidden: it is shown again, with what you just added.').replace('{name}', layerLabel(l)));
    } else if (l.locked) {
      flashToolStatus(tr('“{name}” is locked: what you just added can’t be selected until you unlock it.').replace('{name}', layerLabel(l)));
    }
  }
  if (changed) { dropLayerCache(); renderProperties(); }
}

/** Every object on a hidden layer (`imported` too when Imported is hidden). */
export function hiddenLayerObjectIds(): Set<string> {
  const m = layers();
  const out = new Set<string>();
  for (const l of m.layers) if (l.hidden) for (const id of layerObjectIds(m, l.id)) out.add(id);
  if (importedHidden()) out.add('imported');
  return out;
}

/** The room a reflected ceiling plan shows, or null once it is gone. */
export function rcpRoom(v: CeilingPlanView): ProductInstance | null {
  const r = state.drawing.instances.find((i) => i.id === v.roomId);
  return r && isPolyRoom(r) ? r : null;
}

// ---- View titles: a section's "A–A", a reflected ceiling plan's -------------------

/** The hit targets the plan's last render drew (screen px): what a press on the
 *  plan finds — its view titles, light tags, figures and tags (render.ts). */
export let planHits: RenderHits = noHits();

/** The view a title belongs to, while it is in the drawing. */
function viewOfTitle(t: ViewTitleRef): SectionLine | CeilingPlanView | undefined {
  return t.kind === 'section' ? (state.drawing.sections || []).find((x) => x.id === t.id) : (state.drawing.ceilingPlans || []).find((x) => x.id === t.id);
}
/** A title's text, as painted. */
function viewTitleText(t: ViewTitleRef): string {
  if (t.kind === 'section') { const L = sectionLabel((state.drawing.sections || []).findIndex((x) => x.id === t.id)); return `${L}–${L}`; }
  const v = viewOfTitle(t) as CeilingPlanView | undefined, room = v ? rcpRoom(v) : null;
  return v && room ? rcpPaintOf(v, room).title : '';
}
/** The title painted under a screen point (the topmost), of a view that takes picks. */
function viewTitleAt(sp: Vec2): ViewTitleRef | null {
  for (let i = planHits.viewTitles.length - 1; i >= 0; i--) {
    const b = planHits.viewTitles[i];
    if (sp.x >= b.x0 && sp.x <= b.x1 && sp.y >= b.y0 && sp.y <= b.y1 && !objUnpickable(b.id)) return { kind: b.kind, id: b.id };
  }
  return null;
}
/** Delete the selected title (one undo step): its view's panel shows it again. */
export function deleteViewTitle(): void {
  const t = state.selectedViewTitle, v = t ? viewOfTitle(t) : undefined;
  state.selectedViewTitle = null;
  if (v) { pushHistory(); v.titleHidden = true; }
  renderProperties();
  invalidate();
}
/** A view's title rows in its panel: Show title once deleted, Reset title position once moved. */
function viewTitleRows(body: HTMLElement, v: SectionLine | CeilingPlanView): void {
  if (v.titleHidden) body.appendChild(buttonRow('Show title', () => { pushHistory(); delete v.titleHidden; renderProperties(); invalidate(); }));
  if (v.titleOffset && !v.titleHidden) body.appendChild(buttonRow('Reset title position', () => { pushHistory(); delete v.titleOffset; renderProperties(); invalidate(); }));
}
/** A view's outline in the world: its room's outer outline where the view stands. */
export function rcpViewOutline(v: CeilingPlanView): Vec2[] | null {
  const r = rcpRoom(v);
  return r ? rcpOutline(v, r) : null;
}
/** A reflected ceiling plan's words and colour, as the canvas paints them: its
 *  title (its mirror's height in the dimension style), its figures, its room's
 *  wall colour (the DXF export). */
export function rcpPaintOf(v: CeilingPlanView, room: ProductInstance): { title: string; figure: (mm: number) => string; wall: string } {
  const style = drawingDimStyle(state.drawing), figure = (mm: number): string => formatDimension(mm, style);
  return { title: ceilingViewTitle(v, room, figure), figure, wall: room.wallColor || defaultWallColor(room) };
}
/** A view's pieces in the world, while its room is shown: what snaps to it. */
export function rcpWorldPieces(v: CeilingPlanView): RcpPiece[] {
  const r = rcpRoom(v);
  if (!r || objHidden(r.id)) return [];
  const W = rcpWorld(v, r);
  const source = { drawing: state.drawing, keepPart: (p: ProductInstance) => !objHidden(p.id), keepConn: (c: Connection) => !objHidden(c.id) };
  return ceilingViewDrawing(v, r, source).pieces.filter((pc) => !pc.dashed).map((pc) => ({ ...pc, pts: pc.pts.map(W) }));
}

/** A section "below objects" is the bottom item of the bottom layer above Imported. */
/** The sections not on a hidden layer: what the section tools align to. */
function shownSections(): SectionLine[] {
  return (state.drawing.sections || []).filter((x) => !objHidden(x.id));
}

/** A section is "below objects" while it sits in the run of sections at the
 *  bottom of the bottom layer (several migrated ones keep their order there). */
function sectionIsBelow(sec: SectionLine): boolean {
  const m = layers();
  const bottom = m.layers[1];
  if (!bottom || m.layerOf.get(sec.id) !== bottom.id) return false;
  const us = m.units.get(bottom.id) ?? [];
  const i = us.findIndex((u) => u.id === sec.id);
  return i >= 0 && us.slice(0, i + 1).every((u) => u.kind === 'section');
}

/** What an edit sets off on the next frame, in the plan and in 3D alike: a new
 *  object goes onto its layer, constrained neighbours follow, anchored dimensions
 *  follow their objects and the schedule table appears once it has rows. Each
 *  only when the drawing can have changed: a recorded edit (the revision) or a
 *  live drag — never on a hover frame. */
function modelHousekeeping(): void {
  // A drag that just ended gets one more look (a live solve gives way to the
  // final one), whatever the revision says.
  const dragging = state.dragMode !== 'none', dropped = housekeptDragging && !dragging;
  housekeptDragging = dragging;
  if (drawingRev() !== layerRevision) { reconcileNewObjects(); dropLayerCache(); layerRevision = drawingRev(); }
  // (A solve may put a fixed object back as a NEW object: the paint list is
  // re-read after it, never from before.)
  if (dropped || drawingRev() !== constraintUi.constraintRevision) { reconcileConstraints(); dropLayerCache(); constraintUi.constraintRevision = drawingRev(); }
  if (dropped || drawingRev() !== housekeptRevision) {
    syncDimensions();
    ensureScheduleTable();
    changes.touch();
    housekeptRevision = drawingRev();
  }
}
let housekeptRevision = -1;
let housekeptDragging = false;

function frame(): void {
  reconcileSupports(); // in every view: an edit in 3D or on a sheet's panel too
  reconcileFixtures();
  reconcileDrawEditHost(); // a steam profile being edited stays on the floor against the wall
  syncDrawEditChrome(); // the banner, the folded catalogue, the locked tabs
  reconcileSelection();
  // What is under way whose owner changed (a handle's object deselected, a
  // grip's selection or tool) is done; one whose drawing was replaced, dropped.
  checkUnderWay();
  updateEmptyState();
  renderBackdropIfNeeded(); // repaints the background canvas only when it changed
  if (state.viewTab === '3d') {
    if (takeRepaint()) {
      modelHousekeeping(); // an edit made in 3D sets off what one made in the plan does
      const d = canvas.width / (viewW() || 1); ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, canvas.width, canvas.height); ctx.setTransform(d, 0, 0, d, 0, 0);
      paintOverlay3d(); // where a press finds the handles, also before the scene's debounced rebuild
      updateStatus();
    }
    syncView3dSelection();
    syncView3dSection();
    requestAnimationFrame(frame);
    return;
  }
  const sheet = activeSheet();
  if (sheet) {
    if (takeRepaint()) { renderSheet(sheet); updateStatus(); }
    requestAnimationFrame(frame);
    return;
  }
  if (handleWork?.space === 'section') stepHandleWork(); // the latest pointer, once a frame
  if (takeRepaint()) {
    modelHousekeeping();
    cellEditorUi.place(); // keep the inline editors over what they edit
    textEditorUi.place();
    const grips = selectionGrips();
    planHits = render(ctx, vp, state.drawing, {
      layered: true,
      paint: layers().paint,
      revision: drawingRev(),
      // Model canvas only: lets dimensions lift their ink where they run over a
      // dark backdrop (sheets/print pass none and keep the stored colour).
      inkBackdrop: bgCanvas,
      showGrid: state.showGrid,
      selectedId: state.selectedId,
      selectedIds: state.selectedIds,
      selectedDimId: state.selectedDimId,
      selectionResizeLocked: (() => { const s = selectedInstance(); return !!s && (resizeLocked(s) || isTilted(s)); })(),
      selectedBenchId: featureOf(state, 'bench')?.benchId ?? null,
      selectedSupport: (() => { const s = selectedSupportNow(); return s ? { instId: s.inst.id, id: s.sup.id } : null; })(),
      benchHandles: featureOf(state, 'bench') ? selectedBenchHandles().map((h) => h.p) : null,
      benchAddHover: state.benchAddHover
        ? (() => {
          const inst = state.drawing.instances.find((i) => i.id === state.benchAddHover!.instId);
          // A hover in a reflected ceiling plan is drawn there (`shift`, its offset).
          const { shift = { x: 0, y: 0 } } = state.benchAddHover!;
          return inst ? { polys: state.benchAddHover!.polys.map((poly) => poly.map((p) => { const q = instanceToWorld(inst, p); return { x: q.x + shift.x, y: q.y + shift.y }; })), kind: state.benchAddHover!.kind } : null;
        })()
        : null,
      heaterCoverHover: heaterCoverHoverWorld(),
      blockedWall: blockedWallRing(),
      benchJoint: hoverBenchJoint,
      pendingDim: dimensionPreview(),
      selectedSectionId: state.selectedSectionId,
      selectedRcpId: state.selectedRcpId,
      pendingRcp: rcpPreview(),
      rcpGuide: state.rcpGuide,
      viewTitles: { selected: state.selectedViewTitle },
      sectionMarks: { selected: sectionTargets(), drilledId: state.sectionDrill },
      pendingSection: state.tool === 'section' && sectionDraft.a ? sectionPreview(state.hoverWorld ?? sectionDraft.a) : null,
      pendingSectionGuide: state.tool === 'section' && !sectionDraft.b && state.hoverWorld
        ? { hover: state.hoverWorld, verts: [...(sectionDraft.a ? [sectionDraft.a] : []), ...state.drawSnapVerts] }
        : null,
      alignGuide: alignGuide(),
      moveAlignGuides: snapSetUi.moveAlignGuides,
      pendingGlass: glassPreview(),
      pendingOpening: openingPreview(),
      pendingInnerWall: innerWallPreview(),
      pendingConnection: connectionUi.connDraft && state.tool === 'connection' ? connDraftPreview(connectionUi.connDraft) : null,
      connectionPorts: state.tool === 'connection' || gripUi.gripWork?.grip.drag ? state.connHoverDots : null,
      selectedConnId: state.selectedConnId,
      showSchedule: state.showSchedule,
      showScheduleTables: state.showSchedule,
      selectedScheduleTable: state.selectedScheduleTable,
      showHatches: state.showHatches,
      showBeams: state.showBeams,
      lux: luxOverlay(),
      showLabels: state.showLabels,
      showCeilings: state.showCeilings,
      ceilingSelectedIds: new Set(selectedOf(state, 'instance').filter((id) => { const i = state.drawing.instances.find((x) => x.id === id); return !!i && hasCeiling(i); })),
      ceilingRoomId: ceilingRoom()?.id ?? null,
      disciplineFilters: state.disciplineFilters,
      backgroundImages: bgImagesForRender(),
      instanceImages: instanceImageMap(),
      hoverHighlightRing: hoverHighlightRing(),
      hoverOutline: canvasHover,
      grips: grips.map((g, i) => ({ at: g.at, fill: i === gripUi.gripWork?.index ? GRIP_SELECTED : g.fill })),
      editBox: state.editPdf ? editedBg() : null,
      editBgBounds: state.editBg ? bgGeomBounds() : null,
      polygons: state.drawing.polygons ?? [],
      selectedPolyId: state.selectedPolyId,
      selectedPolyIds: state.selectedPolyIds,
      selectedPolyEdge: state.selectedPolyEdge,
      pendingPolygon:
        (state.tool === 'polygon' && (polyDraft.length || state.hoverWorld) ? { pts: polyDraft, hover: nextPoint(), snapVerts: state.drawSnapVerts } : null) ||
        (state.tool === 'poly-room' && (polyRoomDraft.length || state.hoverWorld) ? { pts: polyRoomDraft, hover: nextPoint(), snapVerts: state.drawSnapVerts } : null) ||
        (state.tool === 'light-sky' && lightTools.skyDraft.length ? { pts: lightTools.skyDraft, hover: nextPoint() } : null),
      floorings: (state.drawing.floorings ?? []).map((f) => ({
        floor: f,
        selected: f.id === state.selectedFloorId,
        drainInst: f.drain ? drainInstance(f.drain) : null,
        drainSel: f.id === state.selectedFloorId && state.selectedFloorDrain,
      })),
      pendingFloor: floorToolKind(state.tool) && (floorDraft.length || state.hoverWorld)
        ? { kind: floorToolKind(state.tool)!, pts: floorDraft, hover: nextPoint(), snapVerts: state.drawSnapVerts }
        : null,
      floorDrainDims: state.floorDrainDims,
      spotAid: spotAidWorld(),
      gripFigure: gripUi.gripWork?.moved ? gripUi.gripWork.grip.measure?.() ?? null : null,
      supportSpacings: supportSpacingsNow(),
      quietDimsOf: supportQuietDims(),
      walls: state.drawing.walls ?? [],
      selectedWallId: state.selectedWallId,
      selectedWallIds: state.selectedWallIds,
      selectedTextIds: state.selectedTextIds,
      selectedArcIds: state.selectedArcIds,
      selectedLeaderIds: state.selectedLeaderIds,
      multiSelectionBounds: selectedCount(state, MULTI_KINDS) >= 2 ? selectionWorldBounds() : null,
      editVerticesId: state.editVerticesId,
      snapVerts: state.drawSnapVerts,
      editGuide: state.editGuide,
      pendingRepeat: repeatPreview(),
      pendingWall: state.tool === 'wall' && wallDraft.length ? { pts: wallDraft, hover: nextPoint() } : null,
      pendingLine: state.tool === 'line' && lineDraft.length ? { pts: lineDraft, hover: nextPoint() }
        : state.tool === 'light-channel' && lightTools.channelDraft.length ? { pts: lightTools.channelDraft, hover: nextPoint() } : null,
      pendingBenchPoly: state.tool === 'bench-poly' && benchPolyDraft.length && benchPolyTemplate
        ? { pts: benchPolyDraft, hover: nextPoint(), depth: (benchPolyTemplate.category === 'saunabench' ? benchDepthFromSlats(benchPolyTemplate) : null) ?? benchPolyTemplate.depth, category: benchPolyTemplate.category, slats: benchPolyTemplate.slats, gaps: benchPolyTemplate.gaps, color: benchPolyTemplate.color, valid: !(nextPoint() && benchPolyVertexTooClose(nextPoint()!)) }
        : null,
      texts: state.drawing.texts ?? [],
      selectedTextId: state.selectedTextId,
      editingTextId: state.editingTextId,
      pendingTextBox: pendingTextBoxPreview(),
      selectedRevCloudIds: state.selectedRevCloudIds,
      pendingRevisionCloud: state.tool === 'revision-cloud' && (revCloudDraft.length || state.hoverWorld) ? { pts: revCloudDraft, hover: nextPoint() } : null,
      pendingScale: state.tool === 'pdf-scale' ? { pts: state.scalePts, hover: state.hoverWorld } : null,
      pendingMeasure: state.tool === 'measure' ? { pts: measurePts, hover: state.hoverWorld } : null,
      constraintPick: state.tool === 'constrain' ? { hover: constraintUi.constrainHover ? constraintUi.constrainHover.outline ?? constraintUi.constrainHover.pts : null, first: constrainFirst ? constrainFirst.outline ?? constrainFirst.pts : null } : null,
      constraintBadges: constraintBadges(),
      constraintHighlight: constraintHighlight(),
      pendingMirror: state.tool === 'mirror' && mirrorFrom ? { a: mirrorFrom, b: state.hoverWorld } : null,
      pendingRubber: placeOf(state.tool).picks && pickBase && nextPoint() ? { a: pickBase, b: nextPoint()! } : null,
      arrayCenter: state.tool === 'array-polar' ? drawingTab.arrayPolarCenter : null,
      pendingRect:
        state.tool === 'print-window' && state.printA && state.hoverWorld
          ? { a: state.printA, b: state.hoverWorld }
          : state.tool === 'rectangle' && rectStart && state.hoverWorld
            ? { a: rectStart, b: nextPoint() ?? state.hoverWorld }
            : state.tool === 'rect-room' && rectRoomStart && state.hoverWorld
              ? { a: rectRoomStart, b: nextPoint() ?? state.hoverWorld }
              : null,
      marquee:
        state.dragMode === 'marquee' && drag.marqueeStart && drag.marqueeEnd
          ? { a: drag.marqueeStart, b: drag.marqueeEnd, window: drag.marqueeStart.x <= drag.marqueeEnd.x }
          : state.tool === 'zoom' && zoomFrom && state.hoverWorld
            ? { a: zoomFrom, b: state.hoverWorld, window: true } // the zoom window shows as a window box
            : null,
      pendingEllipse: circlePreview(),
      snapIndicator: state.snapIndicator,
      snapIndicatorKind: state.snapIndicator ? state.snapIndicatorKind ?? undefined : undefined,
      vertexGrabHover: state.vertexGrabHover,
      modifyPreview: modifyTools.modifyState?.preview ?? null,
      selectedArcId: state.selectedArcId,
      pendingArc: arcPreview(),
      selectedLeaderId: state.selectedLeaderId,
      pendingLeader: state.tool === 'leader' && leaderDraft.length ? { pts: leaderDraft, hover: nextPoint() } : null,
      dropTargetId: drag.dropTargetId,
      dropTargetLabel: dropTargetLabel(),
      relatedIds: relatedToSelection(),
      dragGuides: drag.dragGuides,
      centroidGuides: state.centroidGuides,
      alignGuides: state.alignGuides,
      lang: curLang(),
      hiddenCategories: new Set((Object.keys(CATEGORY_DISCIPLINE) as ProductCategory[]).filter(isDisciplineHidden)),
      drawingEditId: drawingEditor.drawingEditId,
      drawingEditDims: drawEditDims(),
      drawEditVerticesId: state.drawEditVerticesId,
      drawEditSelectedPolyId: state.drawEditSelectedPolyId,
      drawEditSelectedWallId: state.drawEditSelectedWallId,
      polyRoomVertexEditId: state.polyRoomVertexEditId,
      polyRoomSelectedEdge: state.polyRoomSelectedEdge,
      polyRoomSelectedVertex: state.polyRoomSelectedVertex,
      // Resolve the sub-selection against the selected room before handing it to
      // the renderer: the door/pane halo is keyed on the id alone, so a stale id
      // left over after the room is deselected (a click on a bench, a dimension, a
      // polygon, …) would otherwise keep painting the halo on a feature whose room
      // is no longer selected.
      selectedDoorId: selectedDoor()?.door.id ?? null,
      selectedGlassIds: selectedGlassPanes().map((p) => p.seg.id!).filter(Boolean),
      selectedOpeningIds: selectedOpenings().map((p) => p.seg.id!).filter(Boolean),
      selectedWallFeatureId: selectedWallFeature()?.f.id ?? null,
      wallFeaturePreview: wallFeaturePreview(),
      selectedHeaterWallId: featureOf(state, 'heaterWall')?.wallId ?? null,
      selectedInnerWallId: featureOf(state, 'innerWall')?.wallId ?? null,
      selectedFixtureId: selectedFixtureNow()?.id ?? null,
      selectedFixtureIds: pickedLightsNow()?.lights.map((l) => l.id),
      dimStyle: drawingDimStyle(state.drawing),
      hoverConstraintSeg,
    });
    paintSectionHandles(); // over everything
    // The web cases read the grips where they are drawn, and the one worked.
    canvas.dataset.grips = grips.map((g) => { const q = vp.worldToScreen(g.at); return `${Math.round(q.x)},${Math.round(q.y)}`; }).join(' ');
    canvas.dataset.gripHot = gripUi.gripWork ? `${gripUi.gripWork.index}${gripUi.gripWork.armed ? ':armed' : ''}` : '';
    // Test seam: each view title painted, `kind:id:x,y` at its middle (canvas px).
    canvas.dataset.viewTitles = planHits.viewTitles.map((b) => `${b.kind}:${b.id}:${Math.round((b.x0 + b.x1) / 2)},${Math.round((b.y0 + b.y1) / 2)}`).join(' ');
    canvas.dataset.lightTags = planHits.lightTags.map((b) => `${b.tag}:${b.id}:${Math.round(b.x + b.w / 2)},${Math.round(b.y + b.h / 2)}`).join(' ');
    updateStatus();
  }
  if (drag.dropTargetId || hoverBenchJoint || drag.reshapeBlock) repaint(); // keep the flashing highlights animating
  requestAnimationFrame(frame);
}

export function flashToolStatus(text: string): void { state.toolStatusFlash = { text, until: Date.now() + 4000 }; $('status-tool').textContent = text; cmdLog(text, true); invalidate(); }

const TOOL_BUTTON_IDS = new Set(Object.values(TOOL_BUTTONS).filter((id): id is string => !!id));

/** The polyline-style draft in progress, with the options it offers: Undo drops
 *  its last point; Close closes a line back to its start, and finishes a shape
 *  that is closed anyway (polygon, cloud, room, floor). Null when none is live. */
/** Each click-by-click tool's draft as the polyline rules act on it
 *  (polylineDraft.ts): its points, its finish, its drop, what it refuses. */
/** The click-by-click tools of the model (the paper Line and Polygon are a
 *  sheet's, `paperPolyline`). */
type ModelPolylineTool = Exclude<PolylineTool, 'sheet-line' | 'sheet-polygon'>;
const POLYLINES: Record<ModelPolylineTool, () => PolylineDraft> = {
  connection: () => ({ pts: connectionUi.connDraft?.pts ?? [], finish: finishConnectionFree, clear: () => { connectionUi.connDraft = null; state.connHoverDots = []; } }),
  'bench-poly': () => ({
    pts: benchPolyDraft, finish: finishBenchPoly, clear: () => { benchPolyDraft = []; },
    refuse: (p) => (benchPolyVertexTooClose(p) ? tr('Too close — a vertex must be at least the bench depth from the others.') : null),
  }),
  'floor-screed': floorPolyline,
  'floor-screed-slope': floorPolyline,
  'floor-tiled': floorPolyline,
  'floor-tiled-slope': floorPolyline,
  leader: () => ({ pts: leaderDraft, finish: finishLeader, clear: () => { leaderDraft = []; } }),
  'light-channel': () => ({ pts: lightTools.channelDraft, finish: finishChannel, clear: () => { lightTools.channelDraft = []; lightTools.channelRoomId = null; } }),
  'light-sky': () => ({ pts: lightTools.skyDraft, finish: finishSkyZone, clear: () => { lightTools.skyDraft = []; lightTools.skyRoomId = null; } }),
  line: () => ({ pts: lineDraft, finish: finishLine, clear: () => { lineDraft = []; } }),
  'poly-room': () => ({
    pts: polyRoomDraft, finish: finishPolyRoom, clear: () => { polyRoomDraft = []; },
    // A corner within one wall thickness of another: the wall between would be
    // thinner than the wall itself.
    refuse: (p) => {
      const wt = wallThicknessOf({ category: polyRoomTemplate?.category ?? 'sauna' });
      return polyRoomDraft.some((q) => distance(p, q) < wt) ? tr('Corner too close to an existing one (min {n} mm apart).').replace('{n}', String(Math.round(wt))) : null;
    },
  }),
  polygon: () => ({ pts: polyDraft, finish: finishPolygon, clear: () => { polyDraft = []; } }),
  repeat: () => ({ pts: repeatDraft, finish: finishRepeat, clear: () => { repeatDraft = []; } }),
  'revision-cloud': () => ({ pts: revCloudDraft, finish: finishRevisionCloud, clear: () => { revCloudDraft = []; } }),
  wall: () => ({ pts: wallDraft, finish: finishWall, clear: () => { wallDraft = []; } }),
};
function floorPolyline(): PolylineDraft {
  return { pts: floorDraft, finish: finishFloor, clear: () => { floorDraft = []; } };
}

/** The click-by-click draft the armed tool is drawing, with its rule — on a
 *  sheet, the paper Line's or Polygon's; none for any other tool. */
export function activePolyline(): { rule: PolylineRule; draft: PolylineDraft } | null {
  const sh = activeSheet();
  if (sh) return schTool === 'line' && !titleEditor.titleEdit ? paperPolyline(sh) : null;
  const t = state.tool;
  return isPolylineTool(t) ? { rule: POLYLINE_RULES[t], draft: POLYLINES[t]() } : null;
}

/** The sheet's line draft as the polyline rules act on it: the Line, or — from
 *  the Polygon button — a closed ring. */
export function paperPolyline(sh: Sheet): { rule: PolylineRule; draft: PolylineDraft } {
  return {
    rule: POLYLINE_RULES[schLineClosed ? 'sheet-polygon' : 'sheet-line'],
    draft: { pts: sheetSchematic.schLineDraft, finish: () => schFinishLine(sh), clear: () => { sheetSchematic.schLineDraft = []; } },
  };
}

/** A click-by-click tool's point, clicked: on its first point it closes, on a
 *  leader's last it finishes, one the tool refuses says why. */
/** This press finished a draft (on its first or its last point): the dblclick
 *  of a double-click whose second press it was is spent, not a Select
 *  double-click on what was just made. */
let pressFinishedDraft = false;
export function clickPolyline(p: Vec2): void {
  const pl = activePolyline();
  if (!pl) return;
  const r = addPoint(pl.rule, pl.draft, p, 12 / vp.scale);
  if ('refused' in r) flashStatus(r.refused);
  if ('finished' in r) pressFinishedDraft = true;
}

/** Finish the armed tool's work in progress as it stands — Enter, a right-click
 *  and a double-click alike, and Esc unless the tool drops it there. */
export function finishWork(): boolean {
  return TOOL_LIFE[state.tool].finish?.() ?? false;
}

/** Every option the command in progress takes as a typed letter. */
export function toolOptions(): ToolOption[] {
  if (state.tool === 'section-3d') return [...SECTION3D_OPTIONS, ...(state.drawing.section3d ? [FLIP_OPTION] : [])];
  if (state.tool === 'mirror') return [mirrorErase ? KEEP_OPTION : ERASE_OPTION];
  if (state.tool === 'rotate') return [rotateLevel ? ANGLE_OPTION : HORIZONTAL_OPTION];
  if (state.tool === 'offset') return modifyTools.offsetDist != null ? [THROUGH_OPTION] : [];
  if (state.tool === 'zoom') return selectedCount(state, MULTI_KINDS) > 0 || state.selectedDimId ? [EXTENTS_OPTION, OBJECT_OPTION] : [EXTENTS_OPTION];
  if (state.tool === 'constrain') return constrainOptions();
  if (state.tool === 'light-strip') return [...STRIP_DOWN_OPTIONS, STRIP_BOTH_OPTION];
  if (state.tool === 'light-strip-up') return [...STRIP_UP_OPTIONS, STRIP_BOTH_OPTION];
  if (state.tool === 'light-spot') return kitStep ? [] : [lightTools.spotToolFibre ? SPOT_OPTION : FIBRE_OPTION];
  if (state.tool === 'light-sky' && !kitStep && !lightTools.skyDraft.length) return [lightTools.skyToolDraw ? CEILING_OPTION : ZONE_OPTION, SKY_KIT_OPTIONS[lightTools.skyToolKit === 100 ? 50 : 100]];
  if (state.tool === 'light-channel' && !lightTools.channelDraft.length) return channelToolOptions();
  if (state.tool === 'connection') return [...CONNECTION_TYPE_OPTIONS, connectionUi.lastConnRoute === 'orthogonal' ? STRAIGHT_OPTION : RIGHT_ANGLE_OPTION, ...((connectionUi.connDraft?.pts.length ?? 0) > 1 ? [UNDO_OPTION] : [])];
  const pl = activePolyline();
  return pl ? draftOptions(pl.rule, pl.draft.pts) : [];
}

/** Act on a typed option of the command in progress. */
export function applyToolOption(opt: ToolOption): void {
  if (state.tool === 'section-3d') {
    if (opt.id === 'flip') flipSection3d();
    else setSection3dMode(opt.id as Section3dPlane);
    return;
  }
  if (state.tool === 'constrain' && opt.id in CONSTRAINT_META) {
    const t = opt.id as ObjConstraintType;
    if (t === 'fix' && constrainFirst) { toggleFix(constrainFirst.ref.obj); resetConstrainTool(); }
    else constraintUi.constrainType = t;
    updateStatus(); invalidate();
    return;
  }
  if (state.tool === 'light-channel' && applyChannelOption(opt)) return;
  if (state.tool === 'light-sky' && (opt.id === 'zone' || opt.id === 'ceiling')) {
    lightTools.skyToolDraw = opt.id === 'zone';
    updateStatus(); invalidate();
    return;
  }
  if (state.tool === 'light-sky' && (opt.id === 'fifty' || opt.id === 'hundred')) {
    lightTools.skyToolKit = opt.id === 'fifty' ? 50 : 100;
    updateStatus(); invalidate();
    return;
  }
  if (state.tool === 'light-spot' && (opt.id === 'fibre' || opt.id === 'spot')) {
    lightTools.spotToolFibre = opt.id === 'fibre';
    updateStatus(); invalidate();
    return;
  }
  if (stripToolFacing() && opt.id === 'both') {
    // Typed again, back to one strip.
    lightTools.stripToolBoth = !lightTools.stripToolBoth;
    lightTools.stripToolPos = null;
    lightTools.stripToolHoverKey = null;
    state.benchAddHover = stripToolAim ? stripHoverAt(stripToolAim) : null;
    updateStatus(); invalidate();
    return;
  }
  if (stripToolFacing() && isStripPos(opt.id)) {
    // Typed again, a position goes back to the one the cursor is nearest.
    lightTools.stripToolPos = lightTools.stripToolPos === opt.id ? null : opt.id;
    lightTools.stripToolBoth = false;
    lightTools.stripToolHoverKey = null;
    state.benchAddHover = stripToolAim ? stripHoverAt(stripToolAim) : null;
    updateStatus(); invalidate();
    return;
  }
  if (opt.id === 'through') { modifyTools.offsetDist = null; updateStatus(); invalidate(); return; }
  if (opt.id === 'extents') { zoomFit(); endOneShot(); return; }
  if (opt.id === 'object') { if (state.viewTab === '3d') zoomToSelection3d(); else zoomToSelection(); endOneShot(); return; }
  if ((CONNECTION_TYPES as readonly string[]).includes(opt.id)) {
    connectionUi.lastConnType = opt.id as ConnectionType;
    connectionUi.connTypeTyped = true;
    updateStatus(); invalidate();
    return;
  }
  if (opt.id === 'straight' || opt.id === 'orthogonal') {
    connectionUi.lastConnRoute = opt.id;
    updateStatus(); invalidate();
    return;
  }
  if (opt.id === 'horizontal' || opt.id === 'angle') {
    rotateLevel = opt.id === 'horizontal';
    // The point to level is clicked where it stands: the preview turns back.
    if (rotateLevel && pickBase) applyPickAngle(0);
    else if (pickBase && state.hoverWorld) previewPick(state.hoverWorld);
    updateStatus(); invalidate();
    return;
  }
  if (opt.id === 'erase' || opt.id === 'keep') {
    mirrorErase = opt.id === 'erase';
    updateStatus(); invalidate();
    return;
  }
  const pl = activePolyline();
  if (!pl) return;
  if (opt.id === 'undo') undoPoint(pl.draft);
  else if (opt.id === 'close') closeDraft(pl.rule, pl.draft);
  state.distEntry = null; updateDistEntry();
  updateStatus(); renderProperties(); invalidate();
}

/** Click a command's toolbar button — exactly what the toolbar does. A tool
 *  already running is left alone (its button toggles, so a click would stop it). */
export function runCommandButton(id: string): void {
  const name = commandForButton(id)?.name ?? id;
  if (activeSheet()) { cmdLog(`${name}: ${tr('not available on a sheet')}`); return; }
  const btn = document.getElementById(id) as HTMLButtonElement | null;
  if (!btn) return;
  if (btn.disabled) { cmdLog(`${name}: ${tr(btn.dataset.disabledReason ?? 'not available now')}`); return; }
  if (TOOL_BUTTONS[state.tool] === id) return;
  // A tool echoes itself from setTool (toolbar-started ones too); an action doesn't.
  if (!TOOL_BUTTON_IDS.has(id)) cmdLog(`${tr('Command')}: ${name}`);
  btn.click();
}

/** A menu's toolbar command (`menu:cmd-<id>`, src/shared/menus.ts): a command
 *  runs exactly as typed; any other button (Arrange, the sheet tools) is clicked
 *  when it is live here, and otherwise says why not. */
function runMenuButton(id: string): void {
  if (commandForButton(id)) { runCommandButton(id); return; }
  const btn = document.getElementById(id) as HTMLButtonElement | null;
  if (!btn) return;
  // Its label, or — for an icon-only button (Arrange) — the head of its tooltip.
  const title = (btn.dataset.enTitle ?? '').split(' — ')[0].replace(/\s*\(.*$/, '');
  const name = tr(btn.dataset.en ?? btn.textContent ?? id).replace(/^[^\p{L}\p{N}]+/u, '') || tr(title);
  if (btn.disabled) { flashStatus(`${name}: ${tr(btn.dataset.disabledReason ?? 'not available now')}`); return; }
  // The sheet tools only act on a sheet (the rest may sit on a hidden toolbar tab).
  if (id.startsWith('btn-sch-') && !activeSheet()) { flashStatus(`${name}: ${tr('open a sheet first')}`); return; }
  btn.click();
}

/** Runs `fn` when `ok`, and says whether it did: the shape of a tool's `finish` / `drop` (TOOL_LIFE). */
function actIf(ok: boolean, fn: () => void): boolean {
  if (ok) fn();
  return ok;
}
/** A tool's life, from its button to the next tool: what arming it afresh sets
 *  up, what setTool lets go of, how its work in progress ends, and its prompt.
 *  Every tool has a row, so a new tool does not typecheck until it says how its
 *  draft ends and what it asks for. */
interface ToolLife {
  /** Its setup when armed afresh — its button, the command bar, a repeat and Keep
   *  drawing tools active alike (`armTool`); a bare setTool does not run it. */
  arm?: () => void;
  /** Its work in progress let go: setTool runs every tool's, whichever is armed. */
  reset?: () => void;
  /** What it keeps while re-armed and lets go when another tool is (a placing
   *  tool's product, the polar array's pivot, a measurement). */
  leave?: () => void;
  /** Makes what a draft that can already be committed draws — Enter, a right-click
   *  and a double-click (`finishWork`), and Esc unless `escDrops`; says whether it did. */
  finish?: () => boolean;
  /** Throws away the step in progress (Esc); says whether there was one. */
  drop?: () => boolean;
  escDrops?: true;
  /** Its prompt in the command bar: English, `updateStatus` translates it. */
  hint: () => string;
  /** Its inputs before the first click: the body of the properties panel's
   *  Drawing tab, which opens while it is armed (drawingTab.ts). */
  tab?: (body: HTMLElement) => void;
  /** The paper tool its button arms on a sheet instead (`closed`: the line drawn
   *  as a closed ring, the sheet's Polygon). With none its button does nothing
   *  there. */
  sheet?: { tool: SchTool; closed?: true };
}

const TOOL_LIFE: Record<Tool, ToolLife> = {
  arc: {
    drop: () => actIf(arcDraft.length > 0, () => { arcDraft = []; }),
    reset: () => { arcDraft = []; },
    hint: () => drawingTab.arcMode === 'sce' ? (arcDraft.length === 0 ? 'arc — click the start point' : arcDraft.length === 1 ? 'arc — click the centre' : 'arc — click the end point') : (arcDraft.length === 0 ? 'arc — click the start point' : arcDraft.length === 1 ? 'arc — click a point the arc passes through' : 'arc — click the end point'),
    tab: arcTab,
  },
  'array-polar': {
    leave: () => { drawingTab.arrayPolarCenter = null; drawingTab.arrayPolarSection = null; },
    hint: () => 'polar array — select objects, then set the count and angle in the panel and Apply',
    tab: arrayPolarTab,
  },
  'array-rect': {
    hint: () => 'array — select objects, then set rows, columns and spacing in the panel and Apply',
    tab: arrayRectTab,
  },
  backrest: {
    hint: () => 'backrest — click the green face to add a backrest over the high bench; click a red one to remove it',
  },
  'sculpt-bench': {
    drop: () => actIf(sculptToolStarted(), dropSculptStart),
    reset: dropSculptStart,
    hint: sculptToolHint,
  },
  'floor-grille': {
    hint: () => 'floor grille — click a sauna’s green floor to lay a slatted grille over it; click a red one to take it away',
  },
  bench: {
    reset: () => { hoverBenchJoint = null; },
    hint: () => hoverBenchJoint && typeof hoverBenchJoint.corner === 'object' && 'node' in hoverBenchJoint.corner ? 'bench — click the green corner to swap which segment’s frame runs through under the mitre' : hoverBenchJoint ? (hoverBenchJointCanMitre() ? 'bench — click the green corner to step its join: one bench wins, the other wins, mitred on the first one’s frame, mitred on the other’s' : 'bench — click the green corner to swap which bench wins the join') : 'bench — click the green area (a wall, or the strip in front of a bench) to add; click a bench to delete it; drag the selected bench’s handles to resize it; click a corner to swap the join',
  },
  'bench-end': {
    hint: () => 'bench end — click the green end of a bench to close it, its slats turned down to the floor; click a red one to open it',
  },
  'bench-round': {
    hint: () => 'round bench end — click the green end of a wall bench to round it, every tier there swept round its end on the wall; click a red one to square it',
  },
  'bench-poly': {
    ...polylineEnds('bench-poly'),
    leave: () => { benchPolyDraft = []; benchPolyTemplate = null; },
    hint: () => benchPolyDraft.length <= 1 ? `${toolName(benchPolyTemplate, 'Bench')} — ${tr('click to add the first vertex; snaps to 45° / corners (Alt to suspend, Shift for 15°)')}` : `${toolName(benchPolyTemplate, 'Bench')} — ${tr('click to add vertices; snaps to 45° / corners (Alt to suspend, Shift for 15°); Enter / Esc / right-click / double-click to finish')}`,
  },
  'bench-support': {
    drop: () => actIf(!!benchSupportTool.supportRowDrag, () => { benchSupportTool.supportRowDrag = null; dropDrag(); state.benchAddHover = null; }),
    reset: () => { benchSupportTool.supportRowDrag = null; benchSupportTool.supportAim = null; },
    hint: () => benchSupportTool.supportRowDrag ? 'bench support — release to add a row at most 2700 mm apart, the bench’s supports spread evenly along it (Alt: the row where dragged)' : 'bench support — click a slatted bench to add a support across every tier, its supports spread evenly along it (Alt: where clicked); drag along it for a row; click a sculpted bench to add a trestle under it, square to its wall; drag a support or a trestle to slide it; click one to remove it',
  },
  circle: {
    drop: () => actIf(!!circleCenter || ellipseRx != null, () => { circleCenter = null; ellipseRx = null; }),
    reset: () => { circleCenter = null; ellipseRx = null; },
    hint: () => !circleCenter ? 'circle — click the centre' : ellipseRx == null ? 'circle — click for the radius (hold Shift to make an ellipse)' : 'ellipse — click for the second radius',
    sheet: { tool: 'ellipse' },
  },
  connection: {
    // A connection ends on a free point, its last bend; with none it is let go.
    ...polylineEnds('connection'),
    reset: () => { connectionUi.connDraft = null; state.connHoverDots = []; connectionUi.connTypeTyped = false; },
    hint: () => connectionUi.connDraft ? 'connection — click the end (an object, or a point for a bend); Enter ends on a free point' : 'connection — click the start: an object, one of its points, or a free point',
  },
  constrain: {
    drop: () => actIf(!!constrainFirst || !!constraintUi.constrainType, resetConstrainTool),
    leave: () => { resetConstrainTool(); constraintUi.constrainScope = null; },
    hint: () => constraintUi.constrainType === 'fix' ? 'constrain — click the object to fix in place (or to free it)' : constrainSym ? 'constrain — click the line the two vertices mirror about' : !constrainFirst ? 'constrain — click a vertex or edge of the object that stays put' : 'constrain — click a vertex or edge of the object to move',
  },
  copy: {
    drop: () => actIf(!!pickBase, cancelPick),
    reset: () => { cancelPick(); },
    hint: () => !pickBase ? (selectedCount(state, MULTI_KINDS) === 0 ? 'copy — select the objects to copy first' : 'copy — click the base point') : pickPlane.kind === 'section' ? 'copy in the section — click where the copy goes along the cut and up, or type a distance / @along,up' : 'copy — click where the copy goes, or type a distance / @dx,dy',
  },
  dimension: {
    drop: () => {
      if (state.pendingB) { state.pendingB = null; return true; }
      if (state.pendingA) { state.pendingA = null; return true; }
      return false;
    },
    reset: () => { state.pendingA = null; state.pendingB = null; },
    hint: () => !state.pendingA ? 'dimension — click start point' : !state.pendingB ? 'dimension — click end point' : 'dimension — click to set offset',
    sheet: { tool: 'dim' },
  },
  door: {
    hint: () => 'door — click a wall (outer or inner partition) to add a door',
  },
  fillet: {
    drop: () => actIf(!!modifyTools.modifyState?.first, () => { modifyTools.modifyState = null; }),
    reset: () => { modifyTools.modifyState = null; },
    hint: () => drawingTab.filletMode === 'chamfer' ? (!modifyTools.modifyState?.first ? 'chamfer — click the first line' : 'chamfer — click the second line') : (!modifyTools.modifyState?.first ? 'fillet — click the first line' : 'fillet — click the second line'),
    tab: filletTab,
  },
  'floor-screed': floorLife('floor-screed'),
  'floor-screed-slope': floorLife('floor-screed-slope'),
  'floor-tiled': floorLife('floor-tiled'),
  'floor-tiled-slope': floorLife('floor-tiled-slope'),
  glass: {
    hint: () => 'glass — drag along a wall, outer or inner partition (click a pane to remove)',
  },
  'heater-wall': {
    hint: () => 'hidden heater wall — click the green wall area to add a false wall with an air gap; click one to remove it',
  },
  'inner-wall': {
    hint: () => 'inner wall — start on a wall, drag inward to draw it perpendicular; drag an inner wall to slide it; click one to remove',
  },
  leader: {
    ...polylineEnds('leader'),
    reset: () => { leaderDraft = []; },
    hint: () => leaderDraft.length === 0 ? 'leader — click the arrow tip' : 'leader — click to add bends; Enter / right-click (or click the last point again) to finish and type the label',
    sheet: { tool: 'leader' },
  },
  'light-channel': {
    ...polylineEnds('light-channel'),
    arm: () => { lightTools.channelMode = 'free'; },
    reset: () => { lightTools.channelDraft = []; lightTools.channelRoomId = null; lightTools.channelShift = { x: 0, y: 0 }; lightTools.channelAid = null; lightTools.channelInput = null; lightTools.channelChain = null; },
    hint: () => channelToolHint(),
  },
  'light-cove': {
    hint: () => 'cove light — click a wall to run the cove along it (green); click one of its walls (red) to take it out',
  },
  'light-sky': {
    // A kit's projector waiting for its click goes at its default place.
    finish: () => (kitStep ? actIf(true, () => finishKitStep(null)) : polylineEnds('light-sky').finish()),
    drop: () => polylineEnds('light-sky').drop(),
    arm: () => { lightTools.skyToolDraw = false; },
    reset: () => { lightTools.skyDraft = []; lightTools.skyRoomId = null; if (kitStep) finishKitStep(null); },
    leave: () => { lightTools.skyRezoneId = null; },
    hint: () => skyToolHint(),
  },
  'light-spot': {
    finish: () => actIf(!!kitStep, () => finishKitStep(null)),
    arm: () => { lightTools.spotToolFibre = false; },
    reset: () => { lightTools.spotAid = null; if (kitStep) finishKitStep(null); },
    hint: () => (kitStep ? kitStepHint() : lightTools.spotToolFibre ? 'fibre point — click the ceiling or its ceiling plan to put a single fibre end there (Alt: off the spots’ lines); drag one to move it, click one to remove it' : 'spot light — click the ceiling or its ceiling plan to put a spot there (Alt: off the other spots’ lines); drag one to move it, click one to remove it'),
  },
  'light-strip': {
    arm: () => { lightTools.stripToolPos = null; lightTools.stripToolBoth = false; },
    hint: () => stripToolHint(),
  },
  'light-strip-up': {
    arm: () => { lightTools.stripToolPos = null; },
    hint: () => stripToolHint(),
  },
  line: {
    ...polylineEnds('line'),
    reset: () => { lineDraft = []; },
    hint: () => lineDraft.length === 0 ? 'line — click to place the first vertex' : 'line — click to add vertices; snaps to corners / 45° (Alt to suspend, Shift for 15°); right-click / Enter / double-click to finish',
  },
  measure: {
    drop: () => actIf(measurePts.length > 0 || measure3dCount() > 0, () => { measurePts = []; resetMeasure3d(); }),
    leave: () => { measurePts = []; resetMeasure3d(); },
    hint: () => {
      const n = state.viewTab === '3d' ? measure3dCount() : measurePts.length; // in 3D, the points clicked there
      return n === 0 ? 'measure — click the first point' : n === 1 ? 'measure — click the second point (reads distance / angle)' : 'measure — click to start a new measurement';
    },
  },
  mirror: {
    drop: () => actIf(!!mirrorFrom, () => { mirrorFrom = null; }),
    reset: () => { mirrorFrom = null; },
    leave: () => { mirrorErase = false; },
    hint: () => selectedCount(state, MULTI_KINDS) === 0 ? 'mirror — select the objects to mirror first' : !mirrorFrom ? (mirrorErase ? 'mirror — click the first point of the mirror line (the source will be erased)' : 'mirror — click the first point of the mirror line (the source is kept)') : 'mirror — click the second point of the mirror line',
  },
  move: {
    drop: () => actIf(!!pickBase, cancelPick),
    reset: () => { cancelPick(); },
    hint: () => !pickBase ? (selectedCount(state, MULTI_KINDS) === 0 && !sectionTargets().some((t) => t.part) ? 'move — select the objects to move first' : 'move — click the base point') : pickPlane.kind === 'section' ? 'move in the section — click the destination along the cut and up, or type a distance / @along,up' : 'move — click the destination, or type a distance / @dx,dy',
  },
  offset: {
    drop: () => actIf(!!modifyTools.modifyState?.first, () => { modifyTools.modifyState = null; }),
    reset: () => { modifyTools.modifyState = null; },
    hint: () => modifyTools.offsetDist == null ? (!modifyTools.modifyState?.first ? 'offset — type a distance, or click the line, polyline or polygon to offset' : 'offset — click where the copy passes through (or type a distance)') : `${tr(!modifyTools.modifyState?.first ? 'offset — click the line, polyline or polygon to offset' : 'offset — click the side to place the copy')} · ${modifyTools.offsetDist} mm`,
  },
  opening: {
    hint: () => 'opening — drag along a wall to open it (click an opening to remove)',
  },
  'pdf-scale': {
    drop: () => actIf(state.scalePts.length > 0, () => { state.scalePts = []; }),
    arm: () => { scaleTargetId = editedBg()?.id ?? null; }, // the underlay being edited, if any
    reset: () => { state.scalePts = []; },
    leave: () => { scaleTargetId = null; },
    hint: () => state.scalePts.length === 0 ? 'Scale to measure — click the first point' : state.scalePts.length === 1 ? 'Scale to measure — click the second point' : 'Scale to measure — enter the real distance',
  },
  'poly-room': {
    ...polylineEnds('poly-room'),
    leave: () => { polyRoomDraft = []; polyRoomTemplate = null; },
    hint: () => polyRoomDraft.length === 0 ? `${toolName(polyRoomTemplate, 'Room')} — ${tr('click to add the first wall corner')}` : 'room — click to add corners; snaps to corners / 45° (Alt to suspend, Shift for 15°); right-click / double-click / Enter to close',
  },
  polygon: {
    ...polylineEnds('polygon'),
    reset: () => { polyDraft = []; },
    hint: () => polyDraft.length === 0 ? 'polygon — click to add the first vertex' : 'polygon — click to add vertices; snaps to corners / 45° (Alt to suspend, Shift for 15°); Enter / Esc / right-click / double-click to finish',
    sheet: { tool: 'line', closed: true },
  },
  'print-window': {
    drop: () => actIf(!!state.printA, () => { state.printA = null; }),
    reset: () => { state.printA = null; },
    hint: () => state.printA ? 'print window — click the opposite corner' : 'print window — click the first corner',
  },
  rcp: {
    reset: () => { state.rcpGuide = null; },
    hint: () => 'ceiling plan — click where the room’s reflected ceiling plan goes; it snaps in line with the room (Alt: anywhere)',
  },
  'ceiling-top': {
    reset: () => { state.rcpGuide = null; },
    hint: () => 'above ceiling — click where the plan of what lies on the room’s ceiling goes; it snaps in line with the room (Alt: anywhere)',
  },
  'rect-room': {
    drop: () => actIf(!!rectRoomStart, () => { rectRoomStart = null; }),
    leave: () => { rectRoomStart = null; rectRoomTemplate = null; },
    hint: () => !rectRoomStart ? `${toolName(rectRoomTemplate, 'Room')} — ${tr('click the first corner')}` : 'room — click the opposite corner',
  },
  rectangle: {
    drop: () => actIf(!!rectStart, () => { rectStart = null; }),
    reset: () => { rectStart = null; },
    hint: () => !rectStart ? 'rectangle — click the first corner (or type x,y)' : 'rectangle — click the opposite corner, or type @width,height',
    sheet: { tool: 'rect' },
  },
  repeat: {
    ...polylineEnds('repeat'),
    leave: () => { repeatDraft = []; repeatTemplate = null; },
    hint: () => repeatDraft.length === 0 ? `${toolName(repeatTemplate, 'Repeatable')} — ${tr('click to start the baseline; products tile toward the cursor')}` : 'repeat — click to add vertices; snaps to corners / 45° (Alt to suspend, Shift for 15°); Enter / Esc / right-click / double-click to place',
  },
  'revision-cloud': {
    ...polylineEnds('revision-cloud'),
    reset: () => { revCloudDraft = []; },
    hint: () => revCloudDraft.length === 0 ? 'revision cloud — click to add the first vertex' : 'revision cloud — click to add vertices; snaps to corners / 45° (Alt to suspend, Shift for 15°); Enter / Esc / right-click / double-click to finish',
  },
  rotate: {
    drop: () => actIf(!!pickBase, cancelPick),
    reset: () => { cancelPick(); rotateLevel = false; },
    hint: () => (rotateLevel && selectedCount(state, MULTI_KINDS) > 0 ? (!pickBase ? 'rotate to horizontal — click the pivot' : pickPlane.kind === 'section' ? 'rotate to horizontal — click a point: the line from the pivot to it tilts level in the view' : 'rotate to horizontal — click a point: the line from the pivot to it turns horizontal') : (!pickBase ? (selectedCount(state, MULTI_KINDS) === 0 ? 'rotate — select the objects to rotate first' : 'rotate — click the base point (the pivot)') : 'rotate — click to set the angle, or type it in degrees')),
  },
  section: {
    drop: () => actIf(!!sectionDraft.a, () => { sectionDraft = { a: null, b: null }; }),
    reset: () => { sectionDraft = { a: null, b: null }; },
    hint: () => !sectionDraft.a ? 'section — click the start of the cut line' : !sectionDraft.b ? 'section — click the end of the cut line' : 'section — click on the side to look toward; that is also where the section is drawn',
  },
  'section-3d': {
    reset: () => { view3d.section3dPreview = null; },
    hint: () => !state.drawing.section3d ? (section3dMode === 'surface' ? '3D section — click a surface to lay the section on it' : '3D section — click a point for the plane to pass through') : (section3dMode === 'surface' ? '3D section — drag its handle to move it, or click a surface to lay it there' : '3D section — drag its handle to move it, or click a point to lay it through'),
  },
  select: {
    hint: () => selectHint(),
  },
  text: {
    // Both corners placed, it is made (its text typed next); Esc lets the box go.
    finish: () => actIf(!!textBoxes.textDraft.a && !!textBoxes.textDraft.b, finishTextComment),
    drop: () => actIf(!!textBoxes.textDraft.a, () => { textBoxes.textDraft = {}; }),
    escDrops: true,
    reset: () => { textBoxes.textDraft = {}; },
    hint: () => !textBoxes.textDraft.a ? 'text — click the first box corner' : !textBoxes.textDraft.b ? 'text — click the opposite corner' : 'text — click a third point for a leader arrow, or Enter / right-click to add text',
  },
  transform: {
    hint: () => 'transform — select objects, then type the move / rotate / scale in the panel and Apply',
    tab: transformTab,
  },
  trim: {
    drop: () => actIf(!!modifyTools.modifyState?.first, () => { modifyTools.modifyState = null; }),
    reset: () => { modifyTools.modifyState = null; },
    hint: () => !modifyTools.modifyState?.first ? 'trim / extend — click the cutting edge (or boundary)' : 'trim / extend — click the part of the object to trim off, or the end to extend',
  },
  wall: {
    ...polylineEnds('wall'),
    reset: () => { wallDraft = []; },
    hint: () => wallDraft.length === 0 ? 'wall — click to start the centre line' : 'wall — click to add vertices; snaps to corners / 45° (Alt to suspend, Shift for 15°); Enter / Esc / right-click / double-click to finish',
  },
  zoom: {
    drop: () => actIf(!!zoomFrom || zoom3dStarted(), () => { zoomFrom = null; resetZoom3d(); }),
    reset: () => { zoomFrom = null; resetZoom3d(); },
    hint: () => !zoomFrom && !zoom3dStarted() ? 'zoom — click a corner of the window' : 'zoom — click the opposite corner',
  },
};
/** Every tool, from its row. */
const TOOLS = Object.keys(TOOL_LIFE) as Tool[];

/** A click-by-click tool's ends: its draft finished once it has its points
 *  (polylineDraft.ts), else dropped. */
function polylineEnds(t: ModelPolylineTool): Required<Pick<ToolLife, 'finish' | 'drop'>> {
  return { finish: () => finishDraft(POLYLINE_RULES[t], POLYLINES[t]()), drop: () => dropDraft(POLYLINES[t]()) };
}

/** A floor tool's row: the four draw an outline alike, their kind aside. */
function floorLife(t: ModelPolylineTool): ToolLife {
  return {
    ...polylineEnds(t),
    reset: () => { floorDraft = []; state.floorDrainDims = []; },
    hint: () => floorDraft.length === 0 ? 'flooring — click to add the first vertex' : 'flooring — click to add vertices; Enter / right-click / double-click to finish',
  };
}

/** Each paper tool as a row, the sheet twin of TOOL_LIFE: how its work in
 *  progress ends (`finish`: Enter, a right-click, a double-click, and Esc unless
 *  `escDrops`; `drop`: Esc, the step in progress) and its prompt. setSchTool lets
 *  go of every draft. */
const PAPER_LIFE: Record<SchTool, { finish?: (sh: Sheet) => boolean; drop?: () => boolean; escDrops?: true; hint: () => string }> = {
  connection: {
    drop: () => actIf(!!sheetSchematic.schConnDraft, () => { sheetSchematic.schConnDraft = null; }),
    hint: () => 'connection — drag from one box to another',
  },
  dim: {
    drop: () => actIf(!!sheetSchematic.schDimDraft?.a, () => { if (sheetSchematic.schDimDraft!.b) sheetSchematic.schDimDraft!.b = undefined; else sheetSchematic.schDimDraft = null; }),
    hint: () => (!sheetSchematic.schDimDraft?.a ? 'dimension — click start point' : !sheetSchematic.schDimDraft.b ? 'dimension — click end point' : 'dimension — click to set offset'),
  },
  elecBox: { hint: () => 'schematic box — click the cell of the electrical schematic it goes in' },
  elecWire: {
    drop: () => actIf(!!elecSheet.elecWireDraft?.from, () => { elecSheet.elecWireDraft = {}; }),
    hint: () => (!elecSheet.elecWireDraft?.from ? 'schematic wire — click the box it starts from' : 'schematic wire — click the box it goes to'),
  },
  ellipse: { drop: dropPaperBox, hint: () => (!sheetSchematic.schBoxDraft?.a ? 'circle — click the centre' : 'circle — click for the radius') },
  leader: {
    drop: dropPaperBox,
    hint: () => (!sheetSchematic.schBoxDraft?.leader ? 'leader — click the arrow tip' : !sheetSchematic.schBoxDraft.a ? 'leader — click the first corner of its text box' : 'leader — click the opposite corner, then type'),
  },
  line: {
    finish: (sh) => { const pl = paperPolyline(sh); return finishDraft(pl.rule, pl.draft); },
    drop: () => actIf(sheetSchematic.schLineDraft.length > 0, () => { sheetSchematic.schLineDraft = []; }),
    hint: () => (schLineClosed
      ? (sheetSchematic.schLineDraft.length === 0 ? 'polygon — click to add the first vertex' : 'polygon — click to add vertices; snaps to corners / 45° (Alt to suspend, Shift for 15°); Enter / Esc / right-click / double-click to finish')
      : (sheetSchematic.schLineDraft.length === 0 ? 'line — click to place the first vertex' : 'line — click to add vertices; snaps to corners / 45° (Alt to suspend, Shift for 15°); right-click / Enter / double-click to finish')),
  },
  rect: { drop: dropPaperBox, hint: () => (!sheetSchematic.schBoxDraft?.a ? 'rectangle — click the first corner' : 'rectangle — click the opposite corner') },
  select: { hint: () => TOOL_LIFE[state.tool].hint() },
  // Both corners placed, it is made (its text typed next); Esc lets the box go.
  text: {
    finish: (sh) => actIf(!!sheetSchematic.schBoxDraft?.a && !!sheetSchematic.schBoxDraft.b, () => schFinishTextBox(sh)),
    drop: dropPaperBox,
    escDrops: true,
    hint: () => (!sheetSchematic.schBoxDraft?.a ? 'text — click the first box corner' : !sheetSchematic.schBoxDraft.b ? 'text — click the opposite corner' : 'text — click a third point for a leader arrow, or Enter / right-click to add text'),
  },
  viewport: { drop: dropPaperBox, hint: () => (!sheetSchematic.schBoxDraft?.a ? 'viewport — click the first corner' : 'viewport — click the opposite corner') },
};
function dropPaperBox(): boolean { return actIf(!!sheetSchematic.schBoxDraft, () => { sheetSchematic.schBoxDraft = null; }); }

/** Finish the paper tool's work in progress as it stands — Enter, a right-click,
 *  a double-click. */
export function finishPaperWork(sh: Sheet): boolean {
  return PAPER_LIFE[schTool].finish?.(sh) ?? false;
}

/** Esc on a sheet, the model's one rule: a committable draft is kept, else the
 *  step in progress drops and the tool stays armed, else the tool goes, else the
 *  selection. Says whether it did anything. */
export function escapePaper(sh: Sheet): boolean {
  const row = PAPER_LIFE[schTool];
  const done = (): true => { updateStatus(); renderProperties(); invalidate(); return true; };
  if (cancelElecDrag()) return done(); // a drag on a schematic's view, put back
  if (!row.escDrops && row.finish?.(sh)) return done();
  if (row.drop?.()) { state.distEntry = null; updateDistEntry(); return done(); }
  if (schTool !== 'select') { setSchTool('select'); return done(); }
  if (schemSel) { setSchemSel(null); return done(); }
  return false;
}

/**
 * Esc, the same for every tool (AutoCAD): a draft that can already be committed is
 * KEPT — finished, exactly as Enter would; otherwise the first Esc drops the step
 * in progress and leaves the tool armed; with nothing in progress it leaves the
 * tool; in Select it clears the selection. Exactly one of those per press.
 */
export function escapeTool(): void {
  const t = state.tool;
  const done = (): void => { updateStatus(); renderProperties(); invalidate(); };
  if (cancelArmed()) return done(); // an armed pick waits no more
  if (cancelUnderWay()) return done(); // what is under way goes back where it began
  // 1. A committable draft is kept (a text box is let go).
  if (!TOOL_LIFE[t].escDrops && TOOL_LIFE[t].finish?.()) return done();
  // 2. A step in progress is dropped; the tool stays armed for another go.
  const dropped = TOOL_LIFE[t].drop?.() ?? false;
  if (dropped) { state.distEntry = null; updateDistEntry(); return done(); }
  // 3. Nothing in progress: leave the tool (the room-feature tools and the
  //    Transform / Array panels too — they stay armed after an action, not on Esc).
  if (t !== 'select') { setTool('select'); return done(); }
  // 4. Already in Select: clear the selection — every kind (texts, floors,
  //    annotations…); a selected object whose panel replaced the tabbed view was
  //    otherwise impossible to leave from the keyboard.
  deselectAll();
  done();
}

/** Enter / Space on an idle command bar: run the last toolbar tool again. */
export function repeatLastTool(): void {
  const id = lastTool ? TOOL_BUTTONS[lastTool] : undefined;
  if (!id) { cmdLog(tr('No previous command'), true); return; }
  runCommandButton(id);
}

/** A placing tool's product, as the prompt names it: its name in the drawing's
 *  language, or the English fallback translated. */
function toolName(t: ProductTemplate | null | undefined, fallback: string): string {
  return t ? productName(t, getUiLang()) : tr(fallback);
}

function updateStatus(): void {
  const d = state.drawing;
  // Left of the bottom bar: the active tool's hint.
  let hint = '';
  // On a sheet the schematic tools own the pointer, so their hints come first.
  const shHint = activeSheet();
  const armedHint = armedPrompt(), workHint = underWayPrompt();
  if (armedHint) hint = armedHint;
  else if (workHint != null) hint = workHint;
  else
  if (shHint) hint = PAPER_LIFE[schTool].hint();
  else hint = TOOL_LIFE[state.tool].hint();
  // A transient flash (e.g. a rejected relation) wins over the tool hint until it
  // expires, then the hint takes over again.
  if (state.toolStatusFlash && Date.now() < state.toolStatusFlash.until) hint = state.toolStatusFlash.text;
  else state.toolStatusFlash = null;
  // The options the draft in progress takes as typed letters: `[Close/Undo]`.
  const opts = state.toolStatusFlash ? '' : optionsPrompt(toolOptions(), tr);
  $('status-tool').textContent = `${tr(hint)}${opts ? ` ${opts}` : ''}`;
  updateTip();
  // Right of the bottom bar: document info, including the product count.
  const info: string[] = [];
  if (d.fileName) info.push(d.fileName);
  info.push(`${d.instances.length} ${tr(d.instances.length === 1 ? 'product' : 'products')}`);
  if (d.dimensions.length) info.push(`${d.dimensions.length} ${tr('dim')}`);
  if (state.viewTab !== '3d') info.push(`zoom ${(vp.scale * 1000).toFixed(0)}%`); // the plan's zoom
  $('status-info').textContent = info.join('  ·  ');
}

/** Select's prompt: what is being edited, else AutoCAD's idle prompt — type a
 *  command, or Enter / Space to repeat the last one. */
function selectHint(): string {
  if (state.editing) return EDIT_LIFE[state.editing.kind].hint();
  return activeSheet() ? '' : 'Command:';
}

/** Live cursor coordinates (world mm) in the status bar. */
function updateCoordReadout(w: Vec2): void {
  const el = document.getElementById('status-coord');
  if (el) el.textContent = `${Math.round(w.x)}, ${Math.round(w.y)}`;
}

// =====================================================================
// Products panel
// =====================================================================
/** Each stored product's `hashProduct` as the catalogue last listed it, before
 *  the app's own migrations: the version a write begins from, which the store
 *  compares with the row before it writes (docs/claude/products.md, *Saving a
 *  product*). A product not in it is not stored yet. */
export let storedHashes = new Map<string, string>();

export async function loadProducts(): Promise<void> {
  let user: ProductTemplate[] = [];
  try {
    user = await platform.listProducts();
  } catch {
    user = [];
  }
  storedHashes = new Map(user.map((t) => [t.id, hashProduct(t)]));
  for (const t of user) { migrateBenchSkirting(t); migrateProductHeightFromGround(t); }
  state.userProducts = user;
  rebuildTemplates(user);
  renderProductList();
}

// Bumped by the Baserow error handler so an explicit catalogue update can tell a
// real (fresh) download from one that quietly fell back to the offline cache.
let baserowErrorSeq = 0;

/** Menu action: pull a fresh copy of the product catalogue from the database.
 *  The database is the source of truth, so this re-runs the normal load (which
 *  refreshes the local cache) and confirms the result. On failure the Baserow
 *  error handler has already surfaced the reason, so no success popup is shown. */
async function updateCatalog(): Promise<void> {
  const errBefore = baserowErrorSeq;
  await loadProducts();
  await loadStandards();
  if (baserowErrorSeq === errBefore) {
    void showAlert(`${tr('Catalog updated.')}\n\n${state.userProducts.length} ${tr('product(s) loaded from the database.')}`);
  }
}

const BUILTIN_IDS = new Set(BUILTIN_PRODUCTS.map((b) => b.id));

/** Merge a saved override onto its built-in template. The built-in keeps its
 *  identity and behavioural flags (id, category, autoBenches, polyRoom); the
 *  edited content (name, translations, properties, preparations, parts,
 *  geometry, dimensions, colour) comes from the saved override. */
function mergeBuiltin(b: ProductTemplate, o: ProductTemplate): ProductTemplate {
  return { ...b, ...o, id: b.id, category: b.category, builtin: true, autoBenches: b.autoBenches, polyRoom: b.polyRoom };
}

/** Compose the visible template list from the built-ins and saved user
 *  products. A saved product whose id matches a built-in overrides that
 *  built-in (merged in place); the rest are appended as user products. */
/** Bumped whenever the catalogue is rebuilt: the 3D scene reads its products'
 *  defaults (a heater's height…), so an open 3D view rebuilds with it. */
export let templatesRevision = 0;
function rebuildTemplates(user: ProductTemplate[]): void {
  templatesRevision++;
  const overrides = new Map<string, ProductTemplate>();
  const extras: ProductTemplate[] = [];
  for (const p of user) {
    if (BUILTIN_IDS.has(p.id)) overrides.set(p.id, p);
    else extras.push(p);
  }
  state.templates = [
    ...BUILTIN_PRODUCTS.map((b) => {
      const o = overrides.get(b.id);
      return o ? mergeBuiltin(b, o) : b;
    }),
    ...extras,
  ];
  // A copy saved before it carried what the views draw it by (a fan's model, …)
  // takes it from its product once the catalogue is here — a drawing restored
  // before the catalogue loaded would otherwise keep drawing a box.
  for (const inst of state.drawing.instances) adoptOnLoad(inst, productOf(inst));
  scheduleView3dRefresh();
}
// A part's figures fall back to its product's (`partSpan`): the catalogue as loaded now.
let catalogueIndex: { of: ProductTemplate[]; byId: Map<string, ProductTemplate> } | null = null;
export function productOf(inst: ProductInstance): ProductTemplate | null {
  if (catalogueIndex?.of !== state.templates) catalogueIndex = { of: state.templates, byId: new Map(state.templates.map((t) => [t.id, t])) };
  return catalogueIndex.byId.get(inst.templateId) ?? null;
}
/** Where a part's body stands, as the 3D view builds it (`partSpan` with its product). */
function partSpanOf(inst: ProductInstance): [number, number] {
  return partSpan(inst, productOf(inst));
}

/** A part's body as it stands (tilt.ts): its lowest point, size and orientation. */
function tiltBody(inst: ProductInstance): Body {
  return bodyOf(inst, partSpanOf(inst)[0], { w: inst.width, d: inst.depth, h: partHeight(inst, productOf(inst)) });
}

/** Store where a part's body now stands: plan position, lowest point, its three
 *  rotations (a Rotation X / Y of none left out). */
function placePart(inst: ProductInstance, p: BodyPlacement): void {
  inst.x = p.x; inst.y = p.y; inst.rotation = p.rotation;
  if (p.rotationX) inst.rotationX = p.rotationX; else delete inst.rotationX;
  if (p.rotationY) inst.rotationY = p.rotationY; else delete inst.rotationY;
  inst.heightFromGround = p.base;
}

/** The inner wall faces of the room a part stands in, on the plan. */
function hostWallFaces(inst: ProductInstance): Array<{ a: Vec2; b: Vec2; inward: Vec2 }> {
  const room = hostRoomOf(inst);
  if (!room || !isPolyRoom(room)) return [];
  return polyEdges(room).map((e) => ({ a: instanceToWorld(room, e.ia), b: instanceToWorld(room, e.ib), inward: rotatePoint(e.normal, room.rotation) }));
}

/** Tilt a part to a new Rotation X and / or Y. It turns about its body's middle,
 *  its lowest point kept at its Height from ground — a steam conveyor about its
 *  duct's axis at the wall's inner face (it runs through the wall); an infrared
 *  panel against its wall stays against it, its nearest point touching it. */
export function tiltPart(inst: ProductInstance, next: { rotationX?: number; rotationY?: number }): void {
  if (!canTilt(inst)) return;
  const before = tiltBody(inst);
  const want = { rotation: inst.rotation, rotationX: next.rotationX ?? inst.rotationX ?? 0, rotationY: next.rotationY ?? inst.rotationY ?? 0 };
  if (inst.category === 'steamconveyor' && inst.throughWall != null) {
    placePart(inst, aboveFloor(turnKeeping(before, want, conveyorPivot(inst.throughWall)), before.base));
    return;
  }
  const wall = traitsOf(inst.category).tiltsAgainstWall ? wallAgainst(before, hostWallFaces(inst)) : null;
  if (want.rotationX) inst.rotationX = want.rotationX; else delete inst.rotationX;
  if (want.rotationY) inst.rotationY = want.rotationY; else delete inst.rotationY;
  if (wall) keepAgainst(inst, wall);
}

/** Shift a part square to its wall until its nearest point just touches it. */
function keepAgainst(inst: ProductInstance, wall: { a: Vec2; inward: Vec2 }): void {
  const s = touchShift(tiltBody(inst), wall.a, wall.inward);
  inst.x += s.x; inst.y += s.y;
}

/** An instance's height from ground as its row shows it: a room's lift, a
 *  part's underside (a heater's or a shower's catalogue default included). */
export function instanceBase(inst: ProductInstance): number {
  return inst.autoBenches ? roomBase(inst) : partSpanOf(inst)[0];
}

export function renderProductList(): void {
  const list = $('product-list');
  list.innerHTML = '';
  if (titleEditor.titleEdit) { renderTitleCatalog(list); return; }
  const q = state.search.trim().toLowerCase();
  // A product saved with no category (an editor that blanked it) is listed as
  // Custom, not left out of every group.
  const catOf = (t: ProductTemplate): string => t.category || 'custom';
  // A strip or a spot is chosen in its tool's panel, never placed as a part.
  const filtered = state.templates.filter(
    (t) => (!q || t.name.toLowerCase().includes(q) || catOf(t).includes(q)) && !(t.category === 'lighting' && t.lightKind && t.lightKind !== 'projector')
  );

  // Group list: the curated built-in taxonomy first (each category's `group`),
  // then one group per extra category found among the loaded products (e.g.
  // categories added in the cloud catalogue). Derived from the data, so a new
  // database category shows up as its own catalogue section with no code change;
  // its label is the category value.
  const extraCats = [...new Set(state.templates.map(catOf))]
    .filter((c) => traitsOf(c).group == null)
    .sort((a, b) => String(a).localeCompare(String(b)));
  const groups: Array<{ label: string; has: (c: string) => boolean }> = [
    ...CATALOGUE_GROUPS.map((label) => ({ label, has: (c: string) => traitsOf(c).group === label })),
    ...extraCats.map((c) => ({ label: String(c), has: (x: string) => x === c })),
  ];

  for (const group of groups) {
    const items = filtered
      .filter((t) => group.has(catOf(t)))
      .sort((a, b) => productName(a, getUiLang()).localeCompare(productName(b, getUiLang())));
    if (items.length === 0) continue;
    const key = group.label;
    // Every group starts collapsed; a search forces them open so matches show.
    const collapsed = !q && !state.openCats.has(key);
    const head = document.createElement('button');
    head.className = 'product-cat';
    head.setAttribute('aria-expanded', String(!collapsed));
    const caret = document.createElement('span');
    caret.className = 'cat-caret';
    caret.textContent = collapsed ? '▸' : '▾';
    const label = document.createElement('span');
    label.textContent = tr(group.label);
    const count = document.createElement('span');
    count.className = 'cat-count';
    count.textContent = String(items.length);
    head.append(caret, label, count);
    head.addEventListener('click', () => {
      if (state.openCats.has(key)) state.openCats.delete(key);
      else state.openCats.add(key);
      renderProductList();
    });
    list.appendChild(head);
    if (!collapsed) for (const t of items) list.appendChild(productCard(t));
  }
  if (list.children.length === 0) {
    const e = document.createElement('div');
    e.className = 'hint';
    e.textContent = tr('No products match your search.');
    list.appendChild(e);
  }
}

function productCard(t: ProductTemplate): HTMLElement {
  const card = document.createElement('div');
  card.className = 'product-card';
  card.draggable = true;
  card.dataset.id = t.id;

  const swatch = document.createElement('div');
  swatch.className = 'swatch';
  swatch.style.background = t.color;

  const info = document.createElement('div');
  info.className = 'product-info';
  const name = document.createElement('div');
  name.className = 'product-name';
  name.textContent = productName(t, getUiLang());
  const dims = document.createElement('div');
  dims.className = 'product-dims';
  dims.textContent = t.parts && t.parts.length > 1
    ? `${t.parts.length} parts · ${t.width} × ${t.depth} mm`
    : `${t.width} × ${t.depth} mm`;
  info.append(name, dims);
  // Sold in several powers: their range ("18–36 kW").
  const range = variantRange(t.variants, state.drawing.language ?? 'en');
  if (range) {
    const power = document.createElement('div');
    power.className = 'product-dims product-power';
    power.textContent = range;
    info.appendChild(power);
  }

  card.append(swatch, info);

  // Deletion is only offered via the right-click menu (with a confirmation), so
  // there's no always-present delete button that could be clicked by accident.
  card.addEventListener('contextmenu', (e) => {
    e.preventDefault();
    showProductMenu(e, t, card);
  });

  card.addEventListener('dragstart', (e) => {
    e.dataTransfer?.setData('text/stenal-product', t.id);
    e.dataTransfer!.effectAllowed = 'copy';
    catalogueDrag.product = t; // the 3D view's drop preview shows its footprint
  });
  card.addEventListener('dragend', () => { catalogueDrag.product = null; clearDropPreview(); });
  // A click arms the product (click on the drawing to place it); dragging it there
  // still works. Double-click opens the edit window (a built-in opens seeded as a
  // new, customisable product).
  card.title = tr('Click, then click on the drawing to place it — or drag it there. Double-click to edit.');
  card.addEventListener('click', () => armPlacement(t));
  card.addEventListener('dblclick', () => { setTool('select'); openProductModal(t); });

  return card;
}

/** Confirm (a blocking dialog) before permanently deleting a catalog product. */
async function confirmDeleteProduct(t: ProductTemplate): Promise<void> {
  const label = productName(t, getUiLang());
  if (!(await showConfirm(`“${label}”\n\n${tr('This removes it from the shared product database. It can be brought back from Tools ▸ Deleted products….')}`, { title: 'Delete from the catalog?', ok: 'Delete', danger: true }))) return;
  void deleteUserProduct(t.id);
}

async function deleteUserProduct(id: string): Promise<void> {
  if (!canEditStandards()) return;
  await reportWrite(await removeProduct(id));
}

/** Save one product to the catalogue, logged first, from the version last
 *  listed; the catalogue is reloaded after, so every view and hash follows. */
export async function writeProduct(p: ProductTemplate): Promise<ProductWriteResult> {
  const r = await platform.saveProduct(p, storedHashes.get(p.id) ?? null);
  if (r.ok) await loadProducts();
  return r;
}

/** Delete one product, as `writeProduct` saves one. */
async function removeProduct(id: string): Promise<ProductWriteResult> {
  const r = await platform.deleteProduct(id, storedHashes.get(id) ?? null);
  if (r.ok) await loadProducts();
  return r;
}

/** What a refused or failed catalogue write tells the user. */
export function writeProblem(r: ProductWriteResult): string {
  if (r.ok) return '';
  if (r.conflict) return tr('Someone else changed this product since the catalogue was loaded. Nothing was saved: update the catalogue and try again.');
  return `${tr('Nothing was saved.')} ${r.error}`;
}

/** Show a refused or failed catalogue write; reload the catalogue on a refusal so
 *  it shows what is stored now. */
async function reportWrite(r: ProductWriteResult): Promise<void> {
  if (r.ok) return;
  if (r.conflict) await loadProducts();
  await showAlert(writeProblem(r));
}

// ---- Right-click product menu (edit / rename / duplicate / delete) ----
export function hideCtxMenu(): void {
  $('ctx-menu').classList.add('hidden');
}

function showProductMenu(e: MouseEvent, t: ProductTemplate, card: HTMLElement): void {
  const menu = $('ctx-menu');
  menu.innerHTML = '';
  const item = (label: string, fn: () => void, danger = false) => {
    const b = document.createElement('button');
    b.textContent = label;
    if (danger) b.className = 'danger';
    b.addEventListener('click', () => { hideCtxMenu(); fn(); });
    menu.appendChild(b);
  };
  const sep = () => {
    const d = document.createElement('div');
    d.className = 'sep';
    menu.appendChild(d);
  };

  // A read-only user (no `edit` claim) can view/duplicate-into-drawing but not
  // change the shared catalogue: the write items (Duplicate / Rename / Delete /
  // Reset) are omitted; "Edit properties…" opens read-only (Save hidden).
  const canEdit = canEditStandards();
  if (canEdit) item(tr('Duplicate'), () => duplicateProduct(t));
  item(tr('Change log…'), () => void showChangelog(t));
  item(tr(canEdit ? 'Edit properties…' : 'View properties…'), () => openProductModal(t));
  // Push the catalog item's current properties onto every placed copy in the drawing.
  const placed = drawingInstanceCount(t);
  if (placed > 0) { sep(); item(tr('Update drawing ({n})').replace('{n}', String(placed)), () => { void updateDrawingFromTemplate(t); }); }
  if (canEdit && !t.builtin) {
    item(tr('Rename'), () => renameProduct(t, card));
    sep();
    item(tr('Delete'), () => { void confirmDeleteProduct(t); }, true);
  } else if (canEdit && state.userProducts.some((p) => p.id === t.id)) {
    // A built-in carrying a saved override can be reverted to its defaults.
    sep();
    item(tr('Reset to default'), () => void resetBuiltin(t), true);
  }

  menu.style.left = Math.min(e.clientX, window.innerWidth - 190) + 'px';
  menu.style.top = Math.min(e.clientY, window.innerHeight - 180) + 'px';
  menu.classList.remove('hidden');
}

/** Show a product's change log (who changed what, and when) in a modal. */
async function showChangelog(t: ProductTemplate): Promise<void> {
  const body = $('changelog-body');
  $('changelog-title').textContent = `${tr('Change log')} — ${productName(t, getUiLang())}`;
  const empty = (text: string): void => { const d = document.createElement('div'); d.className = 'prop-empty'; d.textContent = tr(text); body.replaceChildren(d); };
  empty('Loading…');
  $('changelog-modal').classList.remove('hidden');
  let entries: ChangelogEntry[] = [];
  try {
    entries = await platform.productChangelog(t.id);
  } catch {
    empty('Could not load the change log.');
    return;
  }
  if (!entries.length) {
    empty('No recorded changes for this product.');
    return;
  }
  const fmt = (iso: string): string => {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleString();
  };
  // Render one value compactly for the before/after columns: a drawing as its
  // entity count, never its JSON.
  const drawing = (n: number): string => tr('[drawing: {n} entities]').replace('{n}', String(n));
  const fmtVal = (k: string, raw: unknown): string => {
    const v = drawingsSummarized(raw, drawing, k);
    if (v === undefined) return '—';
    if (v === null) return 'null';
    if (typeof v === 'string') return v.length > 200 ? `${v.slice(0, 200)}…` : v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);
    try {
      const s = JSON.stringify(v);
      return s.length > 200 ? `${s.slice(0, 200)}…` : s;
    } catch {
      return String(v);
    }
  };
  // A product the catalogue has stored goes back as it was before any change
  // its log can rebuild, by whoever may edit the catalogue.
  const canRestore = canEditStandards() && state.userProducts.some((p) => p.id === t.id);
  const table = document.createElement('table');
  table.className = 'changelog-table';
  const head = document.createElement('thead'), headRow = document.createElement('tr');
  for (const h of ['When', 'Action', 'User', 'Version', 'Changes']) { const th = document.createElement('th'); th.textContent = tr(h); headRow.appendChild(th); }
  head.appendChild(headRow);
  table.appendChild(head);
  const tb = document.createElement('tbody');
  // The log's action, as the store writes it: created / updated / deleted.
  const action = (a: string): string => ({ created: tr('created'), updated: tr('updated'), deleted: tr('deleted') } as Record<string, string>)[a] ?? a;
  for (const [i, e] of entries.entries()) {
    const line = document.createElement('tr');
    const meta = [fmt(e.when), action(e.action), e.user, e.appVersion ? `v${e.appVersion}` : ''];
    for (const c of meta) {
      const td = document.createElement('td');
      td.textContent = c;
      line.appendChild(td);
    }
    // Changes cell: a per-field before → after list, falling back to the
    // plain changed-fields names when no values were recorded.
    const changeTd = document.createElement('td');
    const { fields: keys, before, after } = shownChange(e);
    if (keys.length) {
      const list = document.createElement('div');
      list.className = 'changelog-diff';
      for (const k of keys) {
        const row = document.createElement('div');
        row.className = 'changelog-diff-row';
        const name = document.createElement('span');
        name.className = 'changelog-field';
        name.textContent = k;
        const oldV = document.createElement('span');
        oldV.className = 'changelog-old';
        oldV.textContent = fmtVal(k, before[k]);
        const arrow = document.createElement('span');
        arrow.className = 'changelog-arrow';
        arrow.textContent = '→';
        const newV = document.createElement('span');
        newV.className = 'changelog-new';
        newV.textContent = fmtVal(k, after[k]);
        row.append(name, oldV, arrow, newV);
        list.appendChild(row);
      }
      changeTd.appendChild(list);
    } else {
      changeTd.textContent = e.changedFields || '—';
    }
    if (canRestore && restorable(entries, i)) {
      const btn = document.createElement('button');
      btn.className = 'changelog-restore';
      btn.textContent = tr('Restore as before this change');
      btn.addEventListener('click', () => void restoreProductVersion(t, entries, i));
      changeTd.appendChild(btn);
    }
    line.appendChild(changeTd);
    tb.appendChild(line);
  }
  table.appendChild(tb);
  body.innerHTML = '';
  body.appendChild(table);
}

/** Put a stored product back as it was before change `index` of its change log
 *  (newest first), undoing that change and every later one. Saved as an edit, so
 *  the restore is itself logged and can be undone the same way. */
async function restoreProductVersion(t: ProductTemplate, log: ChangelogEntry[], index: number): Promise<void> {
  if (!canEditStandards()) return;
  const at = state.userProducts.findIndex((p) => p.id === t.id);
  const r = at < 0 ? null : productBefore(state.userProducts[at], log, index);
  if (!r) return;
  const notes = [tr('This change and every later one are undone. The restore is logged as a change of its own.')];
  if (r.keptDrawing) notes.push(tr('Its drawing was not logged then, so the current drawing is kept.'));
  if (r.unlogged.length) notes.push(tr('Some changes were not logged, so these may not be as they were: {fields}').replace('{fields}', r.unlogged.join(', ')));
  const label = productName(t, getUiLang());
  if (!(await showConfirm(`“${label}”\n\n${notes.join('\n\n')}`, { title: 'Restore as before this change?', ok: 'Restore' }))) return;
  $('changelog-modal').classList.add('hidden');
  await reportWrite(await writeProduct(r.product));
}

/** Tools ▸ Deleted products…: every product deleted from the shared database,
 *  as its deletion logged it, newest first; whoever may edit the catalogue can
 *  bring one back. */
async function openDeletedProducts(): Promise<void> {
  const body = $('deleted-body');
  const note = (text: string): void => {
    const d = document.createElement('div');
    d.className = 'prop-empty';
    d.textContent = text;
    body.replaceChildren(d);
  };
  note(tr('Loading…'));
  $('deleted-modal').classList.remove('hidden');
  let list: DeletedProduct[];
  try {
    list = await platform.listDeletedProducts();
  } catch {
    note(tr('Could not load the deleted products.'));
    return;
  }
  if (!list.length) { note(tr('No deleted products.')); return; }
  const table = document.createElement('table');
  table.className = 'changelog-table deleted-table';
  const head = document.createElement('tr');
  for (const h of ['Product', 'Category', 'Deleted', 'By', '']) {
    const th = document.createElement('th');
    th.textContent = h ? tr(h) : '';
    head.appendChild(th);
  }
  const thead = document.createElement('thead');
  thead.appendChild(head);
  const tb = document.createElement('tbody');
  for (const d of list) {
    const tr_ = document.createElement('tr');
    tr_.dataset.product = d.product.id;
    const when = new Date(d.when);
    const name = traitsOf(d.product.category).name;
    const cat = name ? tr(name) : String(d.product.category ?? '');
    for (const c of [productName(d.product, getUiLang()), cat, isNaN(when.getTime()) ? d.when : when.toLocaleString(), d.user]) {
      const td = document.createElement('td');
      td.textContent = c;
      tr_.appendChild(td);
    }
    const act = document.createElement('td');
    if (canEditStandards()) {
      const btn = document.createElement('button');
      btn.className = 'changelog-restore deleted-restore';
      btn.textContent = tr('Restore');
      btn.addEventListener('click', () => void restoreDeletedProduct(d));
      act.appendChild(btn);
    }
    tr_.appendChild(act);
    tb.appendChild(tr_);
  }
  table.append(thead, tb);
  body.replaceChildren(table);
}

/** Bring a deleted product back under its own id, as its deletion logged it: a
 *  save of a product that must not exist yet, logged as its creation. */
async function restoreDeletedProduct(d: DeletedProduct): Promise<void> {
  if (!canEditStandards()) return;
  const label = productName(d.product, getUiLang());
  const notes = [tr('It comes back as it was when it was deleted. The restore is logged as a change of its own.')];
  if (d.lostDrawing) notes.push(tr('It was deleted before drawings were logged, so it comes back without its drawing.'));
  if (!(await showConfirm(`“${label}”\n\n${notes.join('\n\n')}`, { title: 'Restore this product?', ok: 'Restore' }))) return;
  const r = await writeProduct(d.product);
  if (!r.ok) { await reportWrite(r); return; }
  $('deleted-modal').classList.add('hidden');
  flashStatus(tr('Restored “{name}”.').replace('{name}', label));
}

async function duplicateProduct(t: ProductTemplate): Promise<void> {
  if (!canEditStandards()) return;
  const copy: ProductTemplate = JSON.parse(JSON.stringify(t));
  copy.id = `user-${Date.now()}`;
  Object.assign(copy, copiedNames(t, trIn));
  copy.builtin = false;
  await reportWrite(await writeProduct(copy));
}

/** Drop a built-in's saved override, reverting it to its hardcoded defaults. */
async function resetBuiltin(t: ProductTemplate): Promise<void> {
  if (!canEditStandards()) return;
  if (!state.userProducts.some((p) => p.id === t.id)) return;
  await reportWrite(await removeProduct(t.id));
}

function renameProduct(t: ProductTemplate, card: HTMLElement): void {
  if (!canEditStandards()) return;
  const nameEl = card.querySelector('.product-name');
  if (!nameEl) return;
  const input = document.createElement('input');
  input.className = 'rename-input';
  // The card shows the name in the UI's language: that one is renamed.
  const lang = getUiLang();
  input.value = productName(t, lang);
  nameEl.replaceWith(input);
  input.focus();
  input.select();
  let done = false;
  const commit = async () => {
    if (done) return;
    done = true;
    const nn = input.value.trim();
    const stored = state.userProducts.find((p) => p.id === t.id);
    if (nn && stored && nn !== productName(stored, lang)) await reportWrite(await writeProduct({ ...stored, ...renamedNames(stored, lang, nn) }));
    renderProductList();
  };
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') commit();
    else if (e.key === 'Escape') { done = true; renderProductList(); }
  });
  input.addEventListener('blur', commit);
}

// =====================================================================
// Placing & manipulating instances
// =====================================================================
export function placeInstance(t: ProductTemplate, world: Vec2): void {
  if (t.polyRoom) {
    startPolyRoom(t);
    return;
  }
  // Rectangular sauna / steam rooms are now polygonal rooms drawn with two
  // clicks (opposite corners) — the same polygon primitive as the poly-room tool.
  if (t.autoBenches && traitsOf(t.category).room) {
    startRectRoom(t);
    return;
  }
  if (t.repeatable) {
    startRepeat(t);
    return;
  }
  if (traitsOf(t.category).run) {
    startBenchPoly(t, world);
    return;
  }
  pushHistory();
  if (t.parts && t.parts.length > 0) {
    placeMultiPart(t, world);
    return;
  }
  if (traitsOf(t.category).placedAsHeater) {
    placeHeater(t, world);
    return;
  }
  const inst: ProductInstance = { ...placeCopy(t, prepId), id: newId('inst'), x: world.x, y: world.y, rotation: 0 };
  if (inst.throughWall != null) seatThroughWall(inst); // dropped by a room's wall: through it
  mountByDrop(inst, t);
  state.drawing.instances.push(inst);
  if (inst.category === 'steamroom') addSteamRoomFixtures(inst); // centre drain + door channel
  if (inst.category === 'steamcube') {
    updateCubeSnap(inst); // remember a wall/corner snap (legacy)
    updateRoomAnchor(inst); // anchor to the nearest room vertex so it follows on resize
    const room = saunaOfHeater(inst) ?? saunaAt({ x: inst.x, y: inst.y });
    if (room) regenerateSauna(room); // carve the benches around the freshly-placed cube
  }
  selectOne(state, 'instance', inst.id);
  setTool('select'); // a tool armed before the drop goes: its draft, its button, its pick
  renderProperties();
  invalidate();
}

/**
 * Place a multi-part product: each part becomes its own movable instance (so it
 * can be moved independently), tagged with a shared group id, and the template's
 * connections are instantiated between the new instances.
 */
function placeMultiPart(t: ProductTemplate, world: Vec2): void {
  const placement = buildMultiPartPlacement(t, world, () => newId('mp'));
  // Each part is seated by where it lands, by its own mount (a control unit's
  // display set into a wall, its sensor under the ceiling, …).
  for (const inst of placement.instances) {
    const m = t.parts?.find((p) => p.id === inst.partId);
    if (m?.backOnWall) seatGroupOnWall(inst, placement.instances.filter((o) => o !== inst));
    else if (m && mountsByDrop(m)) mountByDrop(inst, m);
  }
  // One group, linked to its catalogue product (named after it in the Parts tab).
  // Its variant is the group's: the parts are one product, sold as one.
  const variant = refreshVariant(t.variants, undefined);
  (state.drawing.groups ??= []).push({ id: placement.groupId, source: variant ? { templateId: t.id, variant } : { templateId: t.id } });
  state.drawing.instances.push(...placement.instances);
  state.drawing.connections.push(...placement.connections);
  adoptPipeTypes(placement.connections);
  const first = placement.instances[0];
  if (first) selectOne(state, 'instance', first.id); else clearSelection(state);
  setTool('select'); // a tool armed before the drop goes: its draft, its button, its pick
  renderProperties();
  invalidate();
}

// ---- Catalog → drawing sync ("Update drawing" in the product right-click menu) ----
// Which fields travel, for each purpose, is catalogueCopy.ts's: placeCopy,
// refreshCopy, productOfCopy / partOfCopy.

/**
 * Place a sauna heater. Dropped onto a sauna it stands in it (turned with the
 * room and snapped to its walls), and the sauna's benches are regenerated to
 * make room for it. Dropped elsewhere it is a standalone instance.
 */
function placeHeater(t: ProductTemplate, world: Vec2): void {
  const inst: ProductInstance = { ...placeCopy(t, prepId), id: newId('inst'), x: world.x, y: world.y, rotation: 0 };
  inst.height ??= heaterHeight(t); // a heater always carries its body's height, editable per heater
  const sauna = saunaAt(world);
  if (sauna) {
    inst.rotation = sauna.rotation;
    state.drawing.instances.push(inst);
    // Snap to the nearest wall / bench / interior feature rather than forcing the
    // heater inside the walls — it can sit anywhere it's dropped.
    const off = snapInstanceOffset(inst);
    inst.x += off.x;
    inst.y += off.y;
    state.centroidGuides = []; // a one-shot placement, not a live drag
    regenerateSauna(sauna);
    updateRoomAnchor(inst); // anchor to the nearest room vertex so it follows on resize
  } else {
    state.drawing.instances.push(inst);
  }
  selectOne(state, 'instance', inst.id);
  setTool('select'); // a tool armed before the drop goes: its draft, its button, its pick
  renderProperties();
  invalidate();
}

/** Grouped sauna heaters of a sauna (or empty), as local-frame footprint boxes.
 *  The box bounds the heater's actual (possibly rotated) footprint in the sauna's
 *  frame, so a heater turned relative to the sauna still carves the right area. */
/** Axis-aligned bounds of a polygon, for the legacy box-based bench maths. */
export function polyBBox(poly: Vec2[]): Box {
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const p of poly) {
    if (p.x < x0) x0 = p.x;
    if (p.x > x1) x1 = p.x;
    if (p.y < y0) y0 = p.y;
    if (p.y > y1) y1 = p.y;
  }
  return { x0, y0, x1, y1 };
}


/** Removing a heater / feature cube frees the space under it: the room it stood
 *  in rebuilds its benches so the carve disappears. */
export function recarveRoomsAfterRemoving(removed: ProductInstance[]): void {
  for (const r of removed) {
    const room = traitsOf(r.category).carvesBenches ? saunaAt({ x: r.x, y: r.y }) : null;
    if (room) regenerateSauna(room);
  }
}

/** The room a heater (or feature cube) stands in, and so carves, if any. */
export function saunaOfHeater(heater: ProductInstance): ProductInstance | null {
  return traitsOf(heater.category).carvesBenches ? hostRoomOf(heater) : null;
}

/**
 * When a heater is near a sauna wall, rotate it so its closest face is
 * collinear with that wall. Picks the rotation (0°/90°/180°/270° relative
 * to the wall direction) closest to the heater's current rotation so the
 * heater doesn't flip unexpectedly.
 */
/** A part that passes through a room's wall (`throughWall`, a steam conveyor):
 *  its room-side band, in its own frame — what sits flush on the wall's inner
 *  face, the rest of it running through the wall. Null for any other part. */
function throughWallBand(inst: ProductInstance): LocalBox | null {
  const d = inst.throughWall;
  if (d == null || !(d >= 0)) return null;
  return { x0: -inst.width / 2, y0: inst.depth / 2 - d, x1: inst.width / 2, y1: inst.depth / 2 };
}

/** Seat a through-wall part in the nearest room wall within its own depth,
 *  dropped or dragged there from inside the room or out: turned so its room side
 *  faces in, that side flush on the wall's inner face. */
function seatThroughWall(inst: ProductInstance): boolean {
  const band = throughWallBand(inst);
  if (!band || !state.snapEnabled) return false;
  const reach = Math.max(SNAP_PX / vp.scale, inst.depth);
  let best: RoomSnap | null = null;
  for (const room of state.drawing.instances) {
    if (!room.autoBenches || !isPolyRoom(room)) continue;
    const fs = roomFaceSnap(inst, room, (p) => instanceToWorld(room, p), reach, [band], { facing: true });
    if (fs && (!best || Math.abs(fs.gap) < Math.abs(best.gap))) best = fs;
  }
  if (!best) return false;
  inst.rotation = best.rotation;
  inst.x += best.dx;
  inst.y += best.dy;
  return true;
}

/** How a copy is seated where it is dropped (`PartMount`): its product's, or —
 *  a multi-part product's part — that part's own. */
function mountOf(inst: ProductInstance): (PartMount & { heightFromGround?: number }) | null {
  const t = productOf(inst);
  if (!t) return null;
  return inst.partId ? t.parts?.find((p) => p.id === inst.partId) ?? null : t;
}

/** A copy mounted by where it is dropped (its `PartMount`, mounting.ts): set
 *  into a wall it is dropped against (`inWall`); by a room's wall, inside or
 *  out, it stands upright against it — its middle at a height, or under the
 *  room's ceiling (an inner wall only; a part that hangs under a ceiling stands
 *  on inner walls only at its height too); inside a room's outline it hangs under
 *  the room's ceiling or lies on its top, turned with the room; elsewhere it
 *  keeps the catalogue's base (the floor). */
function mountByDrop(inst: ProductInstance, m: PartMount): void {
  if (m.inWall != null) { seatInWall(inst, m.wallMountCentre); return; }
  if (m.backOnWall) { seatBackOnWall(inst); return; }
  if (canTilt(inst) && m.wallMountBelowCeiling != null && seatWallMount(inst, { below: m.wallMountBelowCeiling, tilt: wallMountTilt(m) })) return;
  if (canTilt(inst) && m.wallMountCentre != null && seatWallMount(inst, { centre: m.wallMountCentre, inner: !!m.underCeiling, tilt: wallMountTilt(m) })) return;
  if (!m.onRoomTop && !m.underCeiling) return;
  const room = saunaAt({ x: inst.x, y: inst.y });
  if (!room || !isPolyRoom(room)) return;
  inst.rotation = room.rotation;
  if (m.underCeiling) hangUnder(room, inst);
  else layOnTop(room, inst);
}

/** A copy mounted by where it is dropped, dragged elsewhere: mounted again as
 *  if dropped there — set into a wall, upright on one, under a room's ceiling
 *  or on its top, or back on the floor (its tilt cleared, its catalogue base). */
function remountByDrag(inst: ProductInstance): void {
  const m = mountOf(inst);
  if (!m || !mountsByDrop(m)) return;
  delete inst.rotationX;
  delete inst.rotationY;
  if (m.heightFromGround != null) inst.heightFromGround = m.heightFromGround;
  else delete inst.heightFromGround;
  mountByDrop(inst, m);
}

/** The nearest wall face to a part, within `reach`, its `box` (in its own
 *  frame) flush on it and the part turned to face away: a room wall's inner face
 *  or its outer one (`inner`: the inner only), or either face of a wall drawn
 *  with the Wall tool (a plant room's). With the room the face is a room's. */
function nearestWallFace(inst: ProductInstance, box: LocalBox, reach: number, inner = false): (RoomSnap & { room: ProductInstance | null }) | null {
  let best: (RoomSnap & { room: ProductInstance | null }) | null = null;
  for (const room of state.drawing.instances) {
    if (!room.autoBenches || !isPolyRoom(room)) continue;
    const toWorld = (p: Vec2): Vec2 => instanceToWorld(room, p);
    const faces = inner ? roomWallFaces(room, toWorld) : [...roomWallFaces(room, toWorld), ...roomOuterWallFaces(room, toWorld)];
    const fs = faceSnap(inst, faces, reach, [box], { facing: true });
    if (fs && (!best || Math.abs(fs.gap) < Math.abs(best.gap))) best = { ...fs, room };
  }
  if (inner) return best;
  const drawn = (state.drawing.walls || []).filter((w) => !objHidden(w.id)).flatMap((w) => wallFaces(w));
  const fs = drawn.length ? faceSnap(inst, drawn, reach, [box], { facing: true }) : null;
  if (fs && (!best || Math.abs(fs.gap) < Math.abs(best.gap))) best = { ...fs, room: null };
  return best;
}

/** Stand a part upright against the nearest wall face within its own upright
 *  depth or the snap's reach (`nearestWallFace`): Rotation X `tilt` (its top
 *  face on the wall, or its underside), turned to face away from it, its middle
 *  `centre` mm up — or `below` mm under the ceiling of the room whose inner wall
 *  it is. A room's inner walls only with `below`, or `inner` (a part that hangs
 *  under a ceiling: a room's inside). False, and nothing changed, when no wall
 *  is in reach or snapping is off. */
function seatWallMount(inst: ProductInstance, at: { centre?: number; below?: number; inner?: boolean; tilt: number }): boolean {
  if (!state.snapEnabled) return false;
  const box = wallMountBox(inst);
  const best = nearestWallFace(inst, box, Math.max(SNAP_PX / vp.scale, box.y1 - box.y0), at.below != null || !!at.inner);
  if (!best) return false;
  inst.rotation = best.rotation;
  inst.x += best.dx;
  inst.y += best.dy;
  inst.rotationX = at.tilt;
  delete inst.rotationY;
  if (at.below != null && best.room) seatBelowCeiling(best.room, inst, at.below);
  else inst.heightFromGround = wallMountBase(inst, at.centre ?? 0);
  return true;
}

/** Set a part into the nearest wall face within its own depth or the snap's
 *  reach (`nearestWallFace`; its `inWall` front band flush on the face, out of
 *  the wall): upright, turned to face away, its middle `centre` mm up when
 *  given. False, and nothing changed, when no wall is in reach or snapping is
 *  off. */
function seatInWall(inst: ProductInstance, centre?: number): boolean {
  const band = inWallBand(inst);
  if (!band || !state.snapEnabled) return false;
  const best = nearestWallFace(inst, band, Math.max(SNAP_PX / vp.scale, inst.depth));
  if (!best) return false;
  inst.rotation = best.rotation;
  inst.x += best.dx;
  inst.y += best.dy;
  if (centre != null) inst.heightFromGround = Math.round(centre - partHeight(inst) / 2);
  return true;
}

/** Stand a part with its back (+y) flush on the nearest wall face within its
 *  depth or the snap's reach (`nearestWallFace`), its front facing away: a
 *  product made front to the south (`backOnWall`). Its footprint turned half
 *  round covers the same ground, so the face's seat holds. False, and nothing
 *  changed, when no wall is in reach or snapping is off. */
function seatBackOnWall(inst: ProductInstance): boolean {
  if (!state.snapEnabled) return false;
  const box = { x0: -inst.width / 2, y0: -inst.depth / 2, x1: inst.width / 2, y1: inst.depth / 2 };
  const best = nearestWallFace(inst, box, Math.max(SNAP_PX / vp.scale, inst.depth));
  if (!best) return false;
  inst.rotation = (best.rotation + 180) % 360;
  inst.x += best.dx;
  inst.y += best.dy;
  return true;
}

/** A part seated on a wall by its back (`backOnWall`) takes the rest of its
 *  product's parts in `others` with it, as one (`carryWith`). */
function seatGroupOnWall(inst: ProductInstance, others: ProductInstance[]): void {
  const before = { x: inst.x, y: inst.y, rotation: inst.rotation };
  if (seatBackOnWall(inst)) carryWith(before, inst, others);
}

function snapHeaterRotation(heater: ProductInstance, sauna: ProductInstance): void {
  const local = toInstanceLocal(sauna, { x: heater.x, y: heater.y });
  // Threshold: half the heater's larger dimension plus a pixel-scaled margin.
  const tol = 250 / vp.scale + Math.max(heater.width, heater.depth);

  let wallDirLocal: Vec2 | null = null;

  if (isPolyRoom(sauna)) {
    const hit = polyWallHit(sauna, local, tol);
    if (hit) {
      const edge = polyEdges(sauna)[hit.edge];
      if (edge) wallDirLocal = edge.dir;
    }
  } else {
    const hit = wallHit(sauna, local, tol);
    if (hit) {
      wallDirLocal = (hit.wall === 'front' || hit.wall === 'back')
        ? { x: 1, y: 0 }
        : { x: 0, y: 1 };
    }
  }

  if (!wallDirLocal) return;

  // Wall direction in world frame (the sauna itself may be rotated).
  const worldDir = rotatePoint(wallDirLocal, sauna.rotation);
  const wallAngle = Math.atan2(worldDir.y, worldDir.x) * 180 / Math.PI;

  // Four orientations that make a heater face parallel to the wall.
  const current = heater.rotation;
  let best = current;
  let bestDiff = Infinity;
  for (let i = 0; i < 4; i++) {
    const candidate = ((wallAngle + i * 90) % 360 + 360) % 360;
    let diff = Math.abs(candidate - current) % 360;
    if (diff > 180) diff = 360 - diff;
    if (diff < bestDiff) { bestDiff = diff; best = candidate; }
  }
  heater.rotation = best;
}


/** Resize a part to a new footprint (resize.ts `resizeTo`: what it can reach, what
 *  scales with it), then re-derive a room from it: its dimensions follow its
 *  corners, what is anchored to a corner follows, and it regenerates. */
export function resizeInstance(inst: ProductInstance, newW: number, newD: number): void {
  if (!inst.autoBenches) { resizeTo(inst, newW, newD, RESIZE_ENV); return; }
  followVertices(state.drawing, inst.id, () => resizeTo(inst, newW, newD, RESIZE_ENV));
  repositionAnchoredItems(inst); // heaters / cubes follow their nearest room vertex
  regenerateSauna(inst);
  if (inst.category === 'steamroom') repositionSnappedCubes(inst); // legacy cube snap (pre-roomAnchor files)
}

/** What resizing a part needs from the app: a room's contents, the locked parts,
 *  its top written with what rests on it. */
const RESIZE_ENV: ResizeEnv = {
  contents: (room) => roomContents(room),
  locked: (id) => objLocked(id),
  editTop: (room, write) => editRoomTop(room, write),
};

/** What regenerating a room needs from the app: what stands in it, the catalogue. */
const ROOM_ENV = {
  contents: (room: ProductInstance) => roomContents(room),
  template: (id: string) => state.templates.find((t) => t.id === id),
  newId,
};

/** Rebuild a room's derived state after an edit (roomRegen.ts `regenerateRoom`,
 *  docs/claude/rooms.md *Regenerating a room*): a gesture fits a shaped ceiling's
 *  setbacks from where it began and says what it found once; a connection that
 *  lost its end is deselected and counted. */
export function regenerateSauna(inst: ProductInstance): void {
  if (!inst.autoBenches) return;
  const inGesture = undoSteps.isOpen();
  // Never nullish: an absent list (every wall 600) must not fall through to
  // what the gesture's first frame wrote.
  if (inGesture && !gestureSetbacks.has(inst.id)) gestureSetbacks.set(inst.id, inst.ceiling?.setbacks?.slice() ?? []);
  const out = regenerateRoom(state.drawing, inst, { ...ROOM_ENV, setbacksFrom: inGesture ? gestureSetbacks.get(inst.id) : undefined });
  if (out.lost.length) {
    if (state.selectedConnId && out.lost.some((c) => c.id === state.selectedConnId)) state.selectedConnId = null;
    flashConnectionsDeleted(out.lost.length);
  }
  const msg = ceilingNoteText(out.note);
  if (!msg || gestureFlashes.has(msg)) return;
  if (inGesture) gestureFlashes.add(msg);
  flashStatus(msg);
}

/** One edit of a room as one undo step: the step, the write, the room
 *  re-derived (`regenerateSauna`), the panel and the plan redrawn. */
export function editRoom<T>(room: ProductInstance, write: () => T): T {
  pushHistory();
  const out = write();
  regenerateSauna(room);
  renderProperties();
  invalidate();
  return out;
}

/** The footprints that carve a room's benches, in its local frame. */
export function saunaHeaterHoles(sauna: ProductInstance): Vec2[][] {
  return heaterHoles(sauna, ROOM_ENV);
}

/** Convert a world point into an instance's local (un-rotated) frame. */
/** Distance of a local point projected onto a polygon-room edge, clamped to it. */
function edgeAlong(inst: ProductInstance, edgeIndex: number, local: Vec2): number {
  const e = polyEdges(inst)[edgeIndex];
  if (!e) return 0;
  const t = (local.x - e.a.x) * e.dir.x + (local.y - e.a.y) * e.dir.y;
  return Math.max(0, Math.min(e.len, t));
}

/** Hit-test the draggable heater/cube feature. Returns instance id or null. */
function featureHitTest(world: Vec2): string | null {
  const margin = 6 / vp.scale;
  for (let i = state.drawing.instances.length - 1; i >= 0; i--) {
    const inst = state.drawing.instances[i];
    if (!inst.autoBenches || instLocked(inst)) continue; // frozen / locked rooms
    if (featureAt(inst, toInstanceLocal(inst, world), margin)) return inst.id;
  }
  return null;
}

/** Hit-test room doors (their swing region). Returns the room + door hit, or null. */
function doorHitTest(world: Vec2): { instId: string; doorId: string } | null {
  // Scoped to the SELECTED room, exactly like a glazed pane: a door sits in a
  // wall, so picking one out of an unselected room would mean a click on that
  // wall could never select the room itself. Select the room first, then its
  // doors are live.
  const inst = selectedInstance();
  if (!inst || instLocked(inst)) return null; // frozen / locked rooms
  const margin = 8 / vp.scale;
  const doors = roomDoors(inst);
  const local = toInstanceLocal(inst, world);
  for (let k = doors.length - 1; k >= 0; k--) {
    const d = doors[k];
    const rd = isPolyRoom(inst) ? roomDoor(inst, d) : null;
    const hit = rd ? doorHit(rd, local, margin) : !isPolyRoom(inst) && doorHitLocal(inst, d, local, margin);
    if (hit) return { instId: inst.id, doorId: d.id ?? '' };
  }
  return null;
}

/** Hit-test the selected door's jamb handles (for resizing it). 'left' is the
 *  run-start jamb, 'right' the run-end jamb; with where it stands and the door's
 *  middle on screen (its cursor runs along the wall). */
function doorJambHit(sp: Vec2): { side: 'left' | 'right'; at: Vec2; centre: Vec2 } | null {
  const inst = selectedInstance();
  const door = inst ? doorById(inst, selDoorId()) : null;
  if (!inst || !door) return null;
  let jambs: [Vec2, Vec2] | null = null;
  if (isPolyRoom(inst)) {
    const j = roomDoor(inst, door)?.jambs;
    if (j) jambs = [j.start, j.end];
  } else {
    const parts = doorParts(inst, door);
    jambs = [parts.jambL, parts.jambR];
  }
  if (!jambs) return null;
  const [l, r] = jambs.map((j) => vp.worldToScreen(instanceToWorld(inst, j)));
  const centre = { x: (l.x + r.x) / 2, y: (l.y + r.y) / 2 };
  for (const [side, s] of [['left', l], ['right', r]] as const) {
    if (Math.abs(s.x - sp.x) <= HANDLE_PX && Math.abs(s.y - sp.y) <= HANDLE_PX) return { side, at: s, centre };
  }
  return null;
}

/** Leave a text being typed and whatever the plan was editing in place (a
 *  shape's vertices, a room's corners, an underlay, the imported plan) — a
 *  part's drawing editor stays open. */
function leaveEditModes(): void {
  if (state.editingTextId) closeTextEditor(true);
  leaveEdit('plan', 'room', 'shape', 'underlay');
}

export function selectedInstance(): ProductInstance | null {
  return state.drawing.instances.find((i) => i.id === state.selectedId) || null;
}

/** The one selected drawn wall that can carry doors, panes and openings — a band,
 *  not a line. */
export function selectedBandWall(): Wall | null {
  if (selectedOf(state, 'wall').length !== 1 || selectedCount(state, MULTI_KINDS) > 1) return null;
  const w = (state.drawing.walls || []).find((x) => x.id === state.selectedWallId);
  return w && w.style !== 'line' && !objUnpickable(w.id) ? w : null;
}

// The door / pane / opening of the selected drawn wall that is sub-selected. It is
// resolved against the wall selection, so an id left over once another object is
// selected is simply nothing.
export function selectedWallFeature(): { wall: Wall; f: WallFeature } | null {
  if (!state.selectedWallFeatureId) return null;
  const wall = selectedBandWall();
  const f = wall?.features?.find((x) => x.id === state.selectedWallFeatureId);
  return wall && f ? { wall, f } : null;
}

/** Select a drawn wall alone, and one of its features (or none). */
function pickWallFeature(wall: Wall, f: WallFeature | null): void {
  deselectAll();
  selectWallFeature(state, wall.id, f?.id ?? null);
  renderProperties();
  invalidate();
}

/** The topmost drawn band wall under a point, and where along it. */
function bandWallStationAt(world: Vec2): { wall: Wall; seg: number; s: number; fr: WallSegFrame } | null {
  const tol = 4 / vp.scale;
  for (const w of topFirst((state.drawing.walls || []).filter((x) => x.style !== 'line' && !objLocked(x.id)))) {
    const at = wallStationAt(w, world, tol);
    if (at) return { wall: w, ...at };
  }
  return null;
}

/** The door tool on a drawn wall: a door centred on the click, as wide as the
 *  stretch allows up to `WALL_DOOR_WIDTH`. Refused (and said why) where it would
 *  overlap another feature or the stretch is too short. */
function addWallDoorAt(world: Vec2): { wall: Wall; f: WallFeature } | null {
  const at = bandWallStationAt(world);
  if (!at) return null;
  const width = Math.min(WALL_DOOR_WIDTH, at.fr.hi - at.fr.lo);
  const s = Math.max(at.fr.lo, Math.min(at.fr.hi - width, at.s - width / 2));
  const probe: Wall = { ...at.wall, features: [...(at.wall.features ?? [])] };
  if (width < WALL_DOOR_MIN || !addDrawnWallFeature(probe, 'door', at.seg, s, s + width, '')) {
    flashToolStatus(tr('No room for a door here'));
    return null;
  }
  pushHistory();
  const f = addDrawnWallFeature(at.wall, 'door', at.seg, s, s + width, newId('wf'));
  return f ? { wall: at.wall, f } : null;
}

/** The glass / opening tool pressed on a drawn wall: the drag that sizes the new
 *  pane or opening along that wall's segment (`drag.wallFeat`). */
function startWallFeatureDrag(world: Vec2, kind: 'glass' | 'opening'): boolean {
  const at = bandWallStationAt(world);
  if (!at) return false;
  beginGesture();
  pickWallFeature(at.wall, null);
  drag.wallFeat = { wallId: at.wall.id, kind, seg: at.seg, s0: at.s, s1: at.s };
  startDrag('wall-feature-add');
  return true;
}

/** Live preview of the pane / opening being dragged out on a drawn wall. */
function wallFeaturePreview(): { wallId: string; feature: WallFeature } | null {
  if (!drag.wallFeat || Math.abs(drag.wallFeat.s1 - drag.wallFeat.s0) < 1) return null;
  const d = drag.wallFeat;
  return { wallId: d.wallId, feature: { id: '', kind: d.kind, seg: d.seg, start: Math.min(d.s0, d.s1), end: Math.max(d.s0, d.s1) } };
}

/** The station along segment `seg` of `wall` the cursor projects to. */
function wallSegStation(wall: Wall, seg: number, world: Vec2): number | null {
  const fr = wallSegFrame(wall, seg);
  if (!fr) return null;
  return (world.x - fr.a.x) * fr.u.x + (world.y - fr.a.y) * fr.u.y;
}

/** Drop every model selection — instances, shapes, room features, dimensions,
 *  sections and edit modes. Used when leaving the model (a view-tab switch), so a
 *  selection never lingers invisibly on a space it can't be seen in. */
export function deselectAll(): void {
  clearSelection(state); // everything selected (selection.ts)
  leaveEditModes();
  state.sectionDrill = null;
}

// ===================== Unified multi-selection =====================
// The selection can span every movable object kind at once (instances, polygons,
// walls/lines, text comments, free arcs, leaders, revision clouds: `MULTI_KINDS`).
// Each kind keeps its own primary + list (selection.ts `SLOTS`), read through the
// one query there (`selectedOf`, `selectedItems`, …) so the group can be moved,
// bounded and deleted as one.

/** Every selected object (a dimension follows what it measures), as a uniform
 *  {verts, move, raise} handle for bounds + drag — each through its adapter. */
export function selectedMovables(): Movable[] {
  return selectedItems(state, state.drawing, MULTI_KINDS).map((it) => movableOf(OBJECTS, it));
}

/** Combined world bounding box of the whole selection, or null if empty. */
function selectionWorldBounds(): Bounds | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const take = (p: Vec2): void => { if (p.x < minX) minX = p.x; if (p.y < minY) minY = p.y; if (p.x > maxX) maxX = p.x; if (p.y > maxY) maxY = p.y; };
  for (const m of selectedMovables()) for (const p of m.verts()) take(p);
  // A node object moves by its node line, but its body is the run in front of it.
  for (const id of selectedOf(state, 'instance')) { const i = state.drawing.instances.find((x) => x.id === id); if (i?.nodes && i.nodes.length >= 2) instanceOutline(i).forEach(take); }
  if (!isFinite(minX)) return null;
  return { min: { x: minX, y: minY }, max: { x: maxX, y: maxY } };
}
/** True when the cursor is over any currently-selected object (so a drag there
 *  moves the whole group rather than re-picking a single object). */
export function pointOverSelection(world: Vec2): boolean {
  // Each part by its outline as drawn, a few px around it too — so a press right
  // ON a room's concave corner still grabs it, while one in an L's empty notch
  // does not (its bounding box took that); a node object by its mitred run.
  const near = 4 / vp.scale;
  for (const id of selectedOf(state, 'instance')) {
    const i = state.drawing.instances.find((x) => x.id === id);
    if (!i) continue;
    if (i.nodes && i.nodes.length >= 2) { if (ribbonContains(i.nodes, i.depth, world, near)) return true; continue; }
    const ring = instanceOutline(i);
    if (pointInPolygon(world, ring) || ring.some((a, k) => distToSegment(world, a, ring[(k + 1) % ring.length]) <= near)) return true;
  }
  for (const id of selectedOf(state, 'polygon')) { const p = (state.drawing.polygons || []).find((x) => x.id === id); if (p && pointInPolygon(world, p.points)) return true; }
  const w = objectAt('wall', world); if (w && selectedOf(state, 'wall').includes(w.id)) return true;
  for (const id of selectedOf(state, 'text')) { const t = (state.drawing.texts || []).find((x) => x.id === id); if (t && textBoxContains(t, world)) return true; }
  const a = objectAt('arc', world); if (a && selectedOf(state, 'arc').includes(a.id)) return true;
  const l = objectAt('leader', world); if (l && selectedOf(state, 'leader').includes(l.id)) return true;
  const c = objectAt('cloud', world); if (c && selectedOf(state, 'cloud').includes(c.id)) return true;
  return false;
}

/** True when the selection is 2+ instances only (its own rich panel handles it). */
export function isPureInstanceMulti(): boolean {
  const n = selectedOf(state, 'instance').length;
  return n >= 2 && n === selectedCount(state, MULTI_KINDS);
}
/** True when the selection is 2+ polygons only (its own boolean panel handles it). */
export function isPurePolyMulti(): boolean {
  const n = selectedOf(state, 'polygon').length;
  return n >= 2 && n === selectedCount(state, MULTI_KINDS);
}

/** Start dragging these objects together (`move-all`, one move step over their
 *  Movables): a part, a shape, a line, an arc, a leader, a cloud — or the whole
 *  selection. `head`: the one pressed; its own reference point goes on the grid,
 *  so it lands there the same way dragged alone or with the rest. */
function beginObjectMove(world: Vec2, items: ObjItem[], head?: string): void {
  if (!items.length) return;
  const session = beginSelectionMove({ step: 'open' }, items);
  startDrag('move-all');
  moveStartWorld = world;
  const ms = session.movables;
  // Everything being dragged, and what it carries, is left out of its own snap targets.
  planMove = { session, ref: (ms.find((m) => m.id === head) ?? ms[0]).origin(), exclude: new Set([...selectedObjectIds(), ...session.moving]) };
  renderProperties();
  invalidate();
}

/** A move drag on the plan (`move-all`, `move`): its session; for `move-all`,
 *  the point that goes on the grid and what its snap leaves out. */
let planMove: { session: SelectionMove; ref: Vec2; exclude: Set<string> } | null = null;
/** The live plan drag's move, let go: kept (Ctrl still held leaves its copy),
 *  or put back. */
function endPlanMove(how: 'keep' | 'cancel', e?: MouseEvent): void {
  const m = planMove;
  planMove = null;
  m?.session.end(how, { copy: !!e && (e.ctrlKey || e.metaKey) });
  renderProperties();
}

/** Start dragging the whole selection (`head`: the object pressed). */
function beginUnifiedMove(world: Vec2, head?: string): void {
  beginObjectMove(world, selectedItems(state, state.drawing, MULTI_KINDS), head);
}

export function setTool(tool: Tool): void {
  if (!placeOf(tool).in3d) toPlan();
  else if (tool === 'section-3d') to3d();
  cancelArmed(); // another tool: the armed pick is let go
  const prevTool = state.tool;
  const prevTab = TOOL_LIFE[prevTool].tab;
  state.tool = tool;
  setCanvasHover(null);
  // The Drawing tab is a temporary home for a tool's pre-click inputs (its row's
  // `tab`). Switching into such a tool opens it; switching out of one returns to
  // the normal tabs.
  if (TOOL_LIFE[tool].tab) state.propTab = 'drawing';
  else if (prevTab && state.propTab === 'drawing') state.propTab = 'properties';
  // Every tool's work in progress goes (its row's `reset`); what a tool keeps
  // while it is re-armed goes only when another is armed (`leave`).
  for (const t of TOOLS) {
    TOOL_LIFE[t].reset?.();
    if (t !== tool) TOOL_LIFE[t].leave?.();
  }
  state.snapIndicatorKind = null;
  state.hoverWorld = null;
  state.snapIndicator = null;
  cancelDrag(); // a drag under way goes back where it began
  state.benchAddHover = null;
  state.heaterCoverHover = null;
  state.drawSnapVerts = [];
  sectionAlignVerts = [];
  snapSetUi.dimAlignVerts = [];
  snapSetUi.moveAlignGuides = [];
  state.distEntry = null; updateDistEntry();
  // Leaving select mode (or switching tools) ends any vertex/PDF editing.
  leaveEditModes();
  // Drilled into a section view, the tools that work in its plane act on a room's
  // selected feature there (Move moves it, the rest say why not): it stays
  // selected, after too.
  const keepFeature = placeOf(tool).inPlanes && featureSelectedIn(toolPlane());
  // A support or a fixture (a backrest, a light) stays: the light and support
  // tools carry on from the one selected.
  if (!keepFeature) dropFeature(state, 'bench', 'door', 'glass', 'heaterWall', 'innerWall', 'opening');
  if (tool === 'section-3d' && prevTool !== 'section-3d') startSection3d();
  const section3dChanged = (tool === 'section-3d') !== (prevTool === 'section-3d');
  if (section3dChanged) syncView3dSection();
  for (const [t, id] of Object.entries(TOOL_BUTTONS)) if (id) $(id).classList.toggle('active', tool === t);
  const toolBtn = TOOL_BUTTONS[tool];
  if (tool !== 'select' && toolBtn) {
    lastTool = tool;
    // Echo the started tool in the command history, however it was started.
    if (tool !== prevTool) cmdLog(`${tr('Command')}: ${commandForButton(toolBtn)?.name ?? tool}`);
  }
  canvas.style.cursor = tool === 'select' ? 'default' : 'crosshair';
  updateEmptyState(); // now, not next frame: the tool's first click must reach the drawing
  // However the tool was armed, the panel never keeps the last one's Drawing tab
  // (or the 3D section's card): last, so a fallback to Select inside it wins.
  if (section3dChanged || TOOL_LIFE[tool].tab !== prevTab) renderProperties();
  invalidate();
}

/** A one-shot tool has done its job: back to select (Enter / Space on an idle
 *  command bar starts it again) — or, with Settings ▸ "Keep drawing tools active",
 *  re-armed with a fresh draft, AutoCAD's way. Every draw / annotate / modify tool
 *  ends through here, the success path and the degenerate one alike. The
 *  room-feature tools — bench, glass, opening, inner wall, heater wall — never
 *  call this: adding several tiers or panes in a row is their normal use. So do
 *  the Transform / Array panels, which are built for repeated Apply. */
export function endOneShot(): void {
  if (state.keepToolActive && state.tool !== 'select') armTool(state.tool);
  else setTool('select');
  renderProperties();
}

/** Arm a tool afresh, as its button does: its row's setup (`arm`), then setTool.
 *  A repeat, the command bar and the menus click the button, and Keep drawing
 *  tools active re-arms through here, so every way in starts it the same. */
function armTool(t: Tool): void {
  TOOL_LIFE[t].arm?.();
  setTool(t);
}

/** A tool's button: arms it afresh, or — armed already — goes back to Select. */
function toggleTool(t: Tool): void {
  if (state.tool === t) setTool('select');
  else armTool(t);
}

/** A click on tool `t`'s button (TOOL_BUTTONS; `wireToolButtons` wires every
 *  one): on a sheet the paper tool of its row's `sheet`, if it has one; else the
 *  tool toggled, and the panel rebuilt for it. */
function clickToolButton(t: Tool): void {
  if (activeSheet()) {
    const paper = TOOL_LIFE[t].sheet;
    if (paper) toggleSchTool(paper.tool, paper.closed);
    return;
  }
  toggleTool(t);
  renderProperties();
}

/** The toolbar buttons a closed-outline drawing editor keeps: its tools'
 *  (CLOSED_OUTLINE_TOOLS), Undo / Redo, Delete and the zooms. */
const CLOSED_OUTLINE_BUTTONS: ReadonlySet<string> = new Set([
  ...[...CLOSED_OUTLINE_TOOLS].map((t) => TOOL_BUTTONS[t]).filter((id): id is string => !!id),
  'btn-delete',
  'btn-redo',
  'btn-undo',
  'btn-zoom-fit',
  'btn-zoom-in',
  'btn-zoom-out',
]);

/** What the tools' preconditions read (toolNeeds.ts), as the selection stands. */
function toolContext(): ToolContext {
  const room = selectedInstance();
  const walled = !!room?.autoBenches;
  return {
    armed: state.tool,
    selected: selectedCount(state, MULTI_KINDS),
    // Drilled into a section view, a selected feature of a room (a bench too) moves there.
    sectionFeature: featureSelectedIn(toolPlane()),
    underlay: bgImages().length > 0,
    room: walled,
    polyRoom: !!rcpToolRoom(),
    coveCeiling: walled && !!room && !!coveKind(room),
    saunaBench: room?.category === 'saunabench',
    bandWall: !!selectedBandWall(),
    closedOnly: !!drawEditHost()?.closedOnly,
  };
}

/**
 * Enable or grey out the buttons that cannot always act. Every tool's button
 * reads its precondition (toolNeeds.ts) from one context, and the armed tool
 * whose precondition fails falls back to Select by the same rule. All buttons
 * stay visible — a button that would do nothing is greyed out, saying why.
 */
function updateToolButtons(): void {
  const ctx = toolContext();
  // A greyed-out button says why in its tooltip (setButtonEnabled).
  const setEnabled = (id: string, enabled: boolean, reason: string): void => {
    const el = document.getElementById(id) as HTMLButtonElement | null;
    if (el) setButtonEnabled(el, enabled, reason);
  };
  for (const t of TOOLS) {
    const id = TOOL_BUTTONS[t], why = toolReason(t, ctx);
    if (id) setEnabled(id, !why, why ?? '');
  }
  // Zoom to selection: the 3D view greys it by its own selection (syncView3dSelection).
  if (state.viewTab !== '3d') setEnabled('btn-zoom-selection', !activeSheet() && selectionPoints().length > 0, 'Select something first');
  const room = selectedInstance();
  const ex = explodable();
  setEnabled('btn-explode', ex.polys.length + ex.walls.length > 0, 'Select a polygon, line or wall first');
  setEnabled('btn-join', selectedOf(state, 'wall').length >= 2, 'Select two or more lines first');
  // The 1:X scale tool only works while editing the PDF (transform mode).
  setEnabled('btn-pdf-scale-factor', state.editPdf, 'Double-click an underlay to edit it first');
  // Edit room = a double-click on the selected room: lit while its corners are live.
  const editRoom = polyRoomEditInst();
  setEnabled('btn-edit-room', !!editRoom || (!!room && isPolyRoom(room)), 'Select a room first');
  $('btn-edit-room').classList.toggle('active', !!editRoom);
  // Edit polygon = a double-click on the selected polygon, line, wall or cloud.
  const editShape = editingVertexShape();
  setEnabled('btn-edit-polygon', !!editShape || !!selectedEditableShape(), 'Select a polygon, line or wall first');
  $('btn-edit-polygon').classList.toggle('active', !!editShape);
  // A dot on 3D section while the drawing has one, lit or not.
  const section3dBtn = $('btn-section-3d');
  section3dBtn.classList.toggle('has-dot', !!state.drawing.section3d);
  setButtonNote(section3dBtn, state.drawing.section3d ? 'A section cuts the 3D view' : null);
  setToolbarContext({ room: ctx.room }); // the tabbed toolbar brings Sauna forward
  // The Delete button only enables when something deletable is selected.
  setEnabled('btn-delete', SPACES[liveSpace()].deletable(), 'Select something first');
  // A closed-outline editor (a steam profile) shows only what acts there: the
  // toolbar is otherwise a wall of greyed buttons that says nothing.
  for (const b of document.querySelectorAll<HTMLElement>('#toolbar-dock button[id]')) {
    b.classList.toggle('tb-off', ctx.closedOnly && !CLOSED_OUTLINE_BUTTONS.has(b.id));
  }
  refreshSectionVisibility(); // keep all sections visible (buttons only disable)
  // The armed tool its precondition no longer lets run gives way to Select.
  if (toolReason(state.tool, ctx)) setTool('select');
}

/** Snap for the PDF-scale reference: the second point aligns orthogonally (H / V,
 *  or 45°) to the first, so a distance measured along a straight edge stays true.
 *  Uses the shared draft snapper (no grid rounding, so the measured pixel span is
 *  preserved); the first point is placed freely. Alt suspends it. */
function pdfScaleSnap(world: Vec2, shift: boolean): Vec2 {
  if (state.scalePts.length !== 1) return world;
  const a = state.scalePts[0];
  return snapDraftPoint(world, [a], { shift, allow45: true, lastSeg: [a, a] });
}
// Which underlay the scale tool acts on: captured when the tool is entered (from
// the one being edited), so it survives `setTool` clearing the edit selection.
let scaleTargetId: string | null = null;
// Polygon tool: the vertices placed so far for the in-progress polygon.
let polyDraft: Vec2[] = [];
// Flooring tool: the in-progress polygon vertices (shared by all four floor tools).
let floorDraft: Vec2[] = [];
// Circle/ellipse tool: the centre (after the 1st click), and the first semi-axis
// (set by a Shift-second-click, while awaiting the third click for the second axis).
let circleCenter: Vec2 | null = null;
let ellipseRx: number | null = null;
// Wall tool: the centre-line vertices placed so far for the in-progress wall.
let wallDraft: Vec2[] = [];
// Line tool: the polyline vertices placed so far.
let lineDraft: Vec2[] = [];
// Revision-cloud tool: the vertices placed so far for the in-progress cloud.
let revCloudDraft: Vec2[] = [];
// Rectangle tool: the first corner (null until the first click).
export let rectStart: Vec2 | null = null;
// Arc tool: the points placed so far, and the input mode.
let arcDraft: Vec2[] = [];
// Leader tool: the vertices of the in-progress leader (arrow tip first).
let leaderDraft: Vec2[] = [];
// Section tool: the two main points placed so far.
let sectionDraft: { a: Vec2 | null; b: Vec2 | null } = { a: null, b: null };

// The other sections' cut-line endpoints the live draft may align to, kept from
// the last snap so the purple alignment guides can be drawn to them.
let sectionAlignVerts: Vec2[] = [];

/** Snap a point while drawing a section line. The second point locks to
 *  horizontal / vertical / 45° from the first (plus proximity + ortho snaps to
 *  existing geometry); the first and the offset point use the standard geometry
 *  snaps. Both vertices also align to the horizontal / vertical projections of
 *  every OTHER section line's endpoints (drawn as purple guides).
 *  Hold Alt to suspend snapping. Mirrors the line/wall tools. */
function snapSectionPoint(world: Vec2, shift: boolean): Vec2 {
  const nv = shift ? [] : nearbyOrthoVerts(world);
  state.drawSnapVerts = nv;
  const sv = shift ? [] : sectionLineVertices(shownSections());
  sectionAlignVerts = sv;
  if (sectionDraft.a && !sectionDraft.b) {
    return snapDraftPoint(world, [sectionDraft.a, ...nv, ...sv], { shift, allow45: true, lastSeg: [sectionDraft.a, sectionDraft.a] });
  }
  return snapDraftPoint(world, [...nv, ...sv], { shift });
}

/** The purple cross-section alignment guide for this frame: the live section
 *  vertex (a draft point being placed, or an endpoint being dragged) and the
 *  other sections' endpoints it can line up with. */
function alignGuide(): { hover: Vec2; verts: Vec2[] } | null {
  if (sectionAlignVerts.length) {
    if (state.dragMode === 'section-endpoint' && drag.sectionEndpoint) {
      const s = (state.drawing.sections || []).find((x) => x.id === drag.sectionEndpoint!.id);
      if (s) return { hover: s[drag.sectionEndpoint.end], verts: sectionAlignVerts };
    }
    if (state.tool === 'section' && !sectionDraft.b && state.hoverWorld) return { hover: state.hoverWorld, verts: sectionAlignVerts };
  }
  // Dimension tool: aligning either endpoint (not the free offset point) to the
  // other dimensions' vertices and — for the second endpoint — the first one.
  if (state.tool === 'dimension' && snapSetUi.dimAlignVerts.length && state.hoverWorld && !(state.pendingA && state.pendingB)) {
    return { hover: state.hoverWorld, verts: snapSetUi.dimAlignVerts };
  }
  return null;
}

/** Build a live section from the two placed points and the cursor (side + offset).
 *  The displayed letter is derived from section order at draw time, so none is
 *  stored here. */
export function sectionPreview(cursor: Vec2): SectionLine | null {
  const a = sectionDraft.a; if (!a) return null;
  const b = sectionDraft.b ?? cursor;
  if (!sectionDraft.b) return { id: '_pending', a, b, side: 1, offset: 0 };
  const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy) || 1;
  const nx = -dy / len, ny = dx / len;
  const perp = (cursor.x - a.x) * nx + (cursor.y - a.y) * ny;
  return { id: '_pending', a, b, side: perp >= 0 ? 1 : -1, offset: Math.abs(perp) };
}
function addSection(sec: SectionLine): void {
  pushHistory();
  if (!state.drawing.sections) state.drawing.sections = [];
  const s: SectionLine = { ...sec, id: newId('sec') };
  state.drawing.sections.push(s);
  selectOne(state, 'section', s.id); // the new object alone is selected
  renderProperties();
  invalidate();
}
// Polygonal-room tool: the wall-outline vertices placed so far, and the product
// template being drawn (a sauna/steam room with polyRoom = true).
let polyRoomDraft: Vec2[] = [];
let polyRoomTemplate: ProductTemplate | null = null;
// Two-click rectangle-room tool: the armed template and the first-corner click.
let rectRoomTemplate: ProductTemplate | null = null;
export let rectRoomStart: Vec2 | null = null;
// Bench tool: the room's bench corner join under the cursor (pulsing green;
// click to swap which bench wins the corner). Null when not over a join.
// `corner` is a polygon vertex index (poly room) or 'left'/'right' (rect room);
// `tier` is the tier index (poly) or rank level (rect).
let hoverBenchJoint: RenderOptions['benchJoint'] = null;
// Bench-poly tool: polyline vertices for an in-progress heatedbench/saltwall.
let benchPolyDraft: Vec2[] = [];
let benchPolyTemplate: ProductTemplate | null = null;
// The last computed side-sign for the repeat tool, saved in repeatPreview so
// finishRepeat uses the same side the live preview showed (not a stale re-derivation).
let repeatSideSignLast = 1;

// ---- Repeatable-product placement (tile copies along a drawn polyline) ----
// The user draws an open polyline; copies of `repeatTemplate` are tiled along it
// (top edge on the line) on the side toward the cursor, spaced by the template's
// offset. `repeatDraft` holds the committed baseline vertices.
let repeatDraft: Vec2[] = [];
let repeatTemplate: ProductTemplate | null = null;

// ---- New lines' defaults, and the selection's transforms (rotate, flip, mirror, move, clone) ----

const WALL_DEFAULT_THICKNESS = 100;
export const LINE_DEFAULT_THICKNESS = 1; // mm stroke width for a new line / arc
export const LINE_DEFAULT_COLOR = '#ffffff';
export const LINE_DEFAULT_HEIGHT = 0; // mm; new lines are flat (no 3D extrusion) by default

/**
 * Snap a point for the line tool: round to the grid, then align to the
 * orthogonal projection of an existing vertex (this line's own draft plus other
 * lines' vertices), so new points line up horizontally / vertically with them.
 */
function lineSnap(world: Vec2, shift: boolean): Vec2 {
  const others: Vec2[] = [...lineDraft];
  if (drawingEditor.drawingEditId) others.push(...editorShapeSnapVerts());
  else for (const w of state.drawing.walls || []) if (w.style === 'line') others.push(...w.points);
  const nv = shift || drawingEditor.drawingEditId ? [] : nearbyOrthoVerts(world);
  state.drawSnapVerts = nv;
  return snapDraftPoint(world, [...others, ...nv], { shift, allow45: true, lastSeg: draftSeg(lineDraft) });
}

export function rotateSelected(deg: number): void {
  const inst = selectedInstance();
  if (!inst) return;
  pushHistory();
  inst.rotation = (inst.rotation + deg) % 360;
  renderProperties();
  invalidate();
}

/** Whether the current selection has anything that can be flipped. */
function hasFlippableSelection(): boolean {
  // On a sheet the sheet's own selection flips (the model selection is stale there),
  // when Flip changes it: a schematic's view or a viewport alone does not flip.
  const sh = activeSheet();
  if (sh) return !titleEditor.titleEdit && sheetObjectsFlip(sh, SHEET_OBJECTS, schemSelSet);
  return selectedCount(state, MULTI_KINDS) > 0 || !!state.selectedDimId;
}

/** Flip the sheet's selection about its own centre line (paper mm, Y down):
 *  boxes and images swap sides, keeping their size and their contents readable;
 *  lines, connection waypoints and box leaders reflect point by point. */
function flipSheetSelection(axis: 'h' | 'v'): void {
  const sh = activeSheet(); if (!sh || titleEditor.titleEdit || !schemSelSet.length) return;
  if (!sheetObjectsFlip(sh, SHEET_OBJECTS, schemSelSet)) return; // no step for nothing flipped
  pushHistory();
  mirrorSheetObjects(sh, SHEET_OBJECTS, schemSelSet, axis);
  renderProperties();
  invalidate();
}

/**
 * Mirror the whole selection about its combined bounding-box centre, horizontally
 * ('h', across a vertical line) or vertically ('v'). Free-form objects (polygons,
 * ellipses, walls, lines, texts, dimensions, polyline benches) mirror their actual
 * geometry; product instances mirror their placement + orientation, and a
 * polygonal room mirrors its outline (its walls/benches re-derive on the mirrored
 * polygon). A product's internal handedness (door side, bench along-offsets) is
 * left as-is.
 */
function flipSelection(axis: 'h' | 'v'): void {
  if (activeSheet()) { flipSheetSelection(axis); return; }
  const plane = toolPlane(), sec = sectionOf(plane);
  if (sec) {
    // In a section: across the vertical through the selection's middle (a plan
    // mirror square to the cut), or its heights about its middle height.
    const why = planeRefusal(plane);
    if (why) { flashToolStatus(why); return; }
    const ts = sectionSelectionBoxes(sec);
    if (!ts.length) return;
    const t0 = Math.min(...ts.map((t) => t.bounds.min.x)), t1 = Math.max(...ts.map((t) => t.bounds.max.x));
    const h0 = Math.min(...ts.map((t) => t.bounds.min.y)), h1 = Math.max(...ts.map((t) => t.bounds.max.y));
    if (axis === 'h') { const [a, b] = acrossCut(sec, (t0 + t1) / 2); mirrorSelection(a, b); return; }
    mirrorHeightsAbout((h0 + h1) / 2);
    return;
  }
  const c = selectionCenter();
  if (!c) return;
  // A flip is a mirror about the selection's own centre line: vertical for 'h'
  // (left ↔ right), horizontal for 'v'.
  mirrorSelection(c, axis === 'h' ? { x: c.x, y: c.y + 1 } : { x: c.x + 1, y: c.y });
}

/**
 * Mirror every selected object across the line through `a` and `b` (any
 * direction), in ONE move session: what a room carries mirrors with it, the
 * parts re-anchor in the room they now stand in. Free-form geometry is
 * reflected point by point (rings reversed so their winding survives); an
 * arc's angles reflect as 2θ − φ with start and end swapped; a text box keeps
 * its size and moves its centre; a product moves its placement and turns by
 * `mirroredRotationDeg` (its wall side keeps facing a wall), and a room mirrors
 * its own outline in local X. A product's internal handedness (door side, bench
 * along-offsets) is left as-is. `from`: where the step began, when the caller
 * changed the drawing first (Mirror's copy + mirror is one undo step).
 */
function mirrorSelection(a: Vec2, b: Vec2, from?: UndoMark): void {
  moveSelectionOnce((s) => s.mirror(a, b), selectedItems(state, state.drawing), from);
}

/** World-space centre of the current selection's combined bounding box (the
 *  default pivot for Rotate / Scale). Null when nothing transformable is selected. */
export function selectionCenter(): Vec2 | null {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  const acc = (p: Vec2): void => { if (p.x < minX) minX = p.x; if (p.y < minY) minY = p.y; if (p.x > maxX) maxX = p.x; if (p.y > maxY) maxY = p.y; };
  for (const m of selectedMovables()) m.verts().forEach(acc);
  for (const dim of selectedObjectsOf(state, state.drawing, 'dim')) { acc(dim.a); acc(dim.b); }
  if (!isFinite(minX)) return null;
  return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
}

/** Transform ▸ Scale on the plan: the selection ×k about `c`, in ONE move
 *  session — what a room carries scaled with it the same way (its place about
 *  `c`, its size unless size-locked), the parts re-anchored, one undo step. */
export function scaleSelection(c: Vec2, k: number): void {
  moveSelectionOnce((s) => s.transform(scaling(c, k)), selectedItems(state, state.drawing));
}

/** Map an explicit set of objects through `fn` (no history, no repaint): how a
 *  constraint solve moves and turns a rigid body. Each object through its
 *  adapter (drawingObjects.ts), then each re-derives (a room re-lays its benches
 *  once what stands in it has moved too). */
export function transformObjectsIn(items: readonly ObjItem[], fn: (p: Vec2) => Vec2, rotDeg: number, scale: number): void {
  transformItems(OBJECTS, items, { fn, rot: rotDeg, scale });
}

/** Clone every currently-selected object once — what Copy would copy (`cb`, the
 *  selection taken once for a whole array), a room with what stands in it —
 *  mapping it through `fn`, turned `rotDeg` and risen `rise` mm: the arrays and
 *  Transform ▸ Copy, through the ONE copy (`placeCopies`). The selection stays
 *  on the originals. */
export function cloneTargets(fn: (p: Vec2) => Vec2, rotDeg: number, rise = 0, cb = selectionClipboard()): void {
  if (cb) placeCopies(cb, { fn, rot: rotDeg, scale: 1 }, rise);
}

/** Top surface elevation (mm above the floor) of an instance, as the 3D view
 *  builds it (`partSpan`). Used to break selection ties: when several stacked
 *  objects share the click point, the physically highest one wins. */
function instanceTop(inst: ProductInstance): number {
  if (inst.autoBenches) return roomCrown(inst); // room shell, base 0
  return partSpanOf(inst)[1];
}


/** Hit-test instances. Among all whose footprint contains the point, returns the
 *  one whose top surface sits highest (height + height-from-ground); z-order
 *  breaks ties (a later-drawn object wins over an equal-height earlier one). */
export function hitTest(world: Vec2): string | null { return instancesAt(world, false)[0] ?? null; }
/** Every pickable instance under the point, the one a click takes first: the
 *  tallest (`instanceTop`), and of equal ones the later-drawn. `nonRoom` leaves
 *  rooms out and uses the room-aware containment (a part on a bench).
 *  `frozenToo`: the frozen ones as well — seen, so the pick stack puts them in
 *  front of what they cover. */
export function instancesAt(world: Vec2, nonRoom: boolean, frozenToo = false): string[] {
  const m = layers();
  const hits: { id: string; rank: number; top: number; i: number }[] = [];
  state.drawing.instances.forEach((inst, i) => {
    if ((nonRoom && inst.autoBenches) || isDisciplineHidden(inst.category)) return; // can't pick a hidden object
    if (inst.frozen && !frozenToo) return; // frozen objects are locked: not pickable (still snap)
    if (onHiddenLayer(m, inst.id)) return;
    if (!instanceContains(inst, world, nonRoom)) return;
    hits.push({ id: inst.id, rank: m.rank.get(inst.id) ?? -1, top: instanceTop(inst), i });
  });
  // What is drawn on top first (a part standing in a room paints over it).
  return hits.sort((a, b) => b.rank - a.rank || b.top - a.top || b.i - a.i).map((h) => h.id);
}

/** Hits of one kind, what is drawn on top first (the layer stacking); objects
 *  on hidden layers are left out. */
export function topFirst<T extends { id: string }>(hits: T[]): T[] {
  const m = layers();
  return hits
    .filter((o) => !onHiddenLayer(m, o.id))
    .map((o, i) => ({ o, i, k: m.rank.get(o.id) ?? -1 }))
    .sort((a, b) => b.k - a.k || b.i - a.i)
    .map((x) => x.o);
}

/** The objects of one kind a press at a world point lands on (planHits.ts, its
 *  rule), what is drawn on top first; objects on hidden layers are left out. */
export function objectsAt<K extends HitKind>(kind: K, world: Vec2): KindObjects[K][] {
  return topFirst(hitsOf(kind, objectsOf(state.drawing, kind) ?? [], world, hitCtx()));
}
/** What the hit rules ask of the plan now: its view, the rooms the ceiling
 *  plans show, what the section views draw (gathered once, on first ask). */
export function hitCtx(): HitCtx {
  let sv: ReturnType<typeof sectionSources> | undefined;
  return {
    px: 1 / vp.scale,
    view: vp,
    shownRoom: (id) => { const r = state.drawing.instances.find((i) => i.id === id); return r && isPolyRoom(r) && !objHidden(id) ? r : null; },
    sectionSources: () => (sv ??= sectionSources()),
  };
}
/** The top one of them, or null. */
export function objectAt<K extends HitKind>(kind: K, world: Vec2): KindObjects[K] | null {
  return objectsAt(kind, world)[0] ?? null;
}

/**
 * The non-room instance (a heater, cube or part) whose footprint contains the
 * world point and whose top surface sits highest. Used so an object sitting over
 * a room can always be picked above it — even when the room is drawn on top of it
 * in z-order (e.g. a heater grouped into a room placed after it) — and so that
 * among stacked parts the highest one is selected.
 */
export function hitTestNonRoom(world: Vec2): string | null { return instancesAt(world, true)[0] ?? null; }

/** The smaller of an instance's two on-screen footprint dimensions, in pixels. */
function instanceScreenMinExtent(inst: ProductInstance): number {
  const cs = instanceOutline(inst);
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const c of cs) {
    if (c.x < minX) minX = c.x; if (c.x > maxX) maxX = c.x;
    if (c.y < minY) minY = c.y; if (c.y > maxY) maxY = c.y;
  }
  return Math.min(maxX - minX, maxY - minY) * vp.scale;
}

/** Select an instance and begin a move drag from `world`. */
function selectInstanceForMove(id: string, world: Vec2): void {
  // Alone: a sub-selected door or pane of the room goes too, as a click away from it.
  selectOne(state, 'instance', id);
  const inst = selectedInstance()!;
  planMove = { session: beginSelectionMove({ step: 'open' }, [{ kind: 'instance', obj: inst }]), ref: { x: inst.x, y: inst.y }, exclude: new Set() };
  startDrag('move');
  moveStartWorld = world;
  moveInstanceStart = { x: inst.x, y: inst.y };
  drag.moveNodesStart = inst.nodes ? inst.nodes.map((n) => ({ x: n.x, y: n.y })) : [];
  renderProperties();
  invalidate();
}

/** What stands in the given rooms and is not itself among `insts`. */
function roomsCarry(insts: readonly ProductInstance[]): ProductInstance[] {
  const ids = new Set(insts.map((i) => i.id));
  const out: ProductInstance[] = [];
  // A part on a locked layer stays put even when the room it stands in moves.
  for (const r of insts) if (r.autoBenches) for (const c of roomContents(r)) if (!ids.has(c.id) && !objLocked(c.id)) { ids.add(c.id); out.push(c); }
  return out;
}

/** What the plan drag's room takes along: what stood in it when the drag began,
 *  captured then — a room slid over a part mid-drag does not pick it up. */
export function planMoveCarry(): readonly ProductInstance[] {
  return planMove?.session.carry ?? [];
}



/** Toggle whichever object sits under the cursor into the multi-selection WITHOUT
 *  clearing the other kinds — so Shift-click can build a mixed selection of parts,
 *  polygons, walls, texts, arcs, leaders and clouds. Returns false if nothing hit. */
/** The topmost object of the kinds a selection can mix (parts, rooms, shapes,
 *  walls, texts, arcs, leaders, clouds) under a point — the layer stacking's
 *  order, hidden layers left out; none when a locked one is in front of it. */
function topSelectableAt(world: Vec2): ObjRef | null {
  return frontWhere(objectStackAt(world, vp.worldToScreen(world)), (r) => (MULTI_KINDS as readonly ItemKind[]).includes(r.kind as ItemKind));
}

function toggleAnySelection(world: Vec2): boolean {
  const top = topSelectableAt(world), kind = top ? refItem(top.kind) : null;
  if (!top || !kind) return false;
  toggleObject(state, kind, top.id); // the other kinds stay; a feature, a title, an edge go
  state.selectedDimId = null;
  dropDrag();
  renderProperties();
  invalidate();
  return true;
}

/** Find a resize handle of the selected instance near the screen point. */
export function hitHandle(sp: Vec2): Handle | null {
  const inst = selectedInstance();
  if (!inst) return null;
  if (resizeLocked(inst) || isTilted(inst)) return null; // locked or tilted parts expose no resize handles
  for (const h of instanceHandles(inst)) {
    const s = vp.worldToScreen(h.world);
    if (Math.abs(s.x - sp.x) <= HANDLE_PX && Math.abs(s.y - sp.y) <= HANDLE_PX) return h;
  }
  return null;
}

/** A MID-EDGE resize handle over a door, glazed pane or opening of the selected
 *  room gives way to it — the same picks, and tolerance, the mousedown uses to
 *  select them. Corner and node handles never yield. */
function handleYieldsToFeature(h: Handle, world: Vec2): boolean {
  if (h.nodeIndex !== undefined || (h.sx !== 0 && h.sy !== 0)) return false;
  return selectedRoomFeatureAt(world);
}

/** Whether a press here is on a door, pane or opening of the selected room. */
export function selectedRoomFeatureAt(world: Vec2): boolean {
  if (doorHitTest(world)) return true;
  const room = selectedInstance();
  if (!room || instLocked(room)) return false;
  const local = toInstanceLocal(room, world);
  return ((room.glass || []).length > 0 && glassHitLocal(room, local, 4 / vp.scale) >= 0)
    || ((room.openings || []).length > 0 && openingHitLocal(room, local, 4 / vp.scale) >= 0);
}

/** True when the screen point is over the selected instance's rotation knob. */
function hitRotation(sp: Vec2): boolean {
  const inst = selectedInstance();
  if (!inst) return false;
  if (inst.nodes && inst.nodes.length >= 2) return false; // poly-bench has no rotation knob
  const k = rotationHandleScreen(vp, inst);
  return Math.hypot(k.x - sp.x, k.y - sp.y) <= HANDLE_PX;
}

/** Point the instance's back edge towards the world point: free, or in whole
 *  15° steps while Shift is held (`snapRotationDeg`). */
function rotateInstanceToward(inst: ProductInstance, world: Vec2, shift: boolean): void {
  const deg = (Math.atan2(world.y - inst.y, world.x - inst.x) * 180) / Math.PI - 90;
  inst.rotation = ((snapRotationDeg(deg, shift) % 360) + 360) % 360;
}

/** The currently selected free shape (polygon or wall), or null. */
function selectedShape(): Polygon | Wall | null {
  const poly = (state.drawing.polygons || []).find((p) => p.id === state.selectedPolyId);
  if (poly) return poly;
  return (state.drawing.walls || []).find((w) => w.id === state.selectedWallId) || null;
}

/** The world outline points used to bound a shape (the band for walls). */
function shapeOutlinePoints(shape: Polygon | Wall): Vec2[] {
  return 'thickness' in shape ? wallBand(shape.points, shape.thickness, shape.align) : shape.points;
}

/** True when the screen point is over the selected shape's rotation knob. */
function hitShapeRotation(sp: Vec2): boolean {
  const shape = selectedShape();
  if (!shape || shape.points.length < 2) return false;
  // Locked polygons can't be rotated (it would break the constraints); a circle
  // has no meaningful rotation, but an ellipse does.
  if (isPolygonShape(shape)) {
    const p = shape as Polygon;
    if (polyHasConstraints(p)) return false;
    if (p.ellipse) { const { rx, ry } = ellipseParams(p); if (Math.abs(rx - ry) < 1) return false; }
  }
  const k = shapeRotationKnob(vp, shapeOutlinePoints(shape)).knob;
  return Math.hypot(k.x - sp.x, k.y - sp.y) <= HANDLE_PX;
}

/** The lone selected polygon (the one that shows resize handles), or null. */
export function resizablePoly(): Polygon | null {
  if (state.selectedPolyIds.length > 1) return null;
  return (state.drawing.polygons || []).find((p) => p.id === state.selectedPolyId) || null;
}

/** A bounding-box resize handle of the selected polygon near the screen point. */
function hitShapeHandle(sp: Vec2): Handle | null {
  const poly = resizablePoly();
  if (!poly || poly.points.length < 2) return null;
  for (const h of shapeBBoxHandles(poly.points)) {
    const s = vp.worldToScreen(h.world);
    if (Math.abs(s.x - sp.x) <= HANDLE_PX && Math.abs(s.y - sp.y) <= HANDLE_PX) return h;
  }
  return null;
}

/**
 * Scale a polygon's stored points so the dragged bounding-box handle follows the
 * cursor while the opposite side stays pinned. `start`/`bbox` are snapshots from
 * the gesture start; edge handles scale one axis only, corners both.
 */
/** The point-map that resizes a bbox to put the grabbed `handle` under `world`,
 *  anchored to the opposite side (respecting Link-W&H). Shared by polygon and
 *  revision-cloud resize. */
function bboxScaler(handle: Handle, bbox: { minx: number; maxx: number; miny: number; maxy: number }, world: Vec2, bounds?: { loX: number; hiX: number; loY: number; hiY: number }): (p: Vec2) => Vec2 {
  const MIN = 1; // keep at least 1mm on the dragged axis so it can't collapse
  const ax = handle.sx > 0 ? bbox.minx : handle.sx < 0 ? bbox.maxx : (bbox.minx + bbox.maxx) / 2;
  const ay = handle.sy > 0 ? bbox.miny : handle.sy < 0 ? bbox.maxy : (bbox.miny + bbox.maxy) / 2;
  const hx = handle.sx > 0 ? bbox.maxx : handle.sx < 0 ? bbox.minx : ax;
  const hy = handle.sy > 0 ? bbox.maxy : handle.sy < 0 ? bbox.miny : ay;
  let fx = 1, fy = 1;
  if (handle.sx !== 0 && Math.abs(hx - ax) > 1e-6) {
    let target = world.x;
    if (Math.abs(target - ax) < MIN) target = ax + Math.sign(hx - ax) * MIN;
    fx = (target - ax) / (hx - ax);
  }
  if (handle.sy !== 0 && Math.abs(hy - ay) > 1e-6) {
    let target = world.y;
    if (Math.abs(target - ay) < MIN) target = ay + Math.sign(hy - ay) * MIN;
    fy = (target - ay) / (hy - ay);
  }
  // Link W&H: keep the aspect ratio — the passive axis follows the dragged one.
  if (linkWH()) {
    const f = handle.sx !== 0 && handle.sy !== 0 ? Math.max(fx, fy) : handle.sx !== 0 ? fx : fy;
    fx = f; fy = f;
  }
  // A length-locked edge freezes its axis, so a bbox drag can't stretch a locked
  // wall — the same rule `roomResizeConstraints` gives a room. When chained the
  // single factor is clamped to the tightest of both axes' bounds.
  if (bounds) {
    if (dimChained) {
      const lo = Math.max(bounds.loX, bounds.loY), hi = Math.min(bounds.hiX, bounds.hiY);
      const f = Math.min(Math.max(fx, lo), hi); fx = f; fy = f;
    } else {
      fx = Math.min(Math.max(fx, bounds.loX), bounds.hiX);
      fy = Math.min(Math.max(fy, bounds.loY), bounds.hiY);
    }
  }
  return (p: Vec2): Vec2 => ({ x: ax + (p.x - ax) * fx, y: ay + (p.y - ay) * fy });
}

function resizePolyTo(poly: Polygon, handle: Handle, start: Vec2[], holesStart: Vec2[][], bbox: { minx: number; maxx: number; miny: number; maxy: number }, world: Vec2): void {
  const scale = bboxScaler(handle, bbox, world, ringScaleBounds(start, poly.edgeLengths));
  poly.points = start.map(scale);
  setPolyHoles(poly, holesStart.map((h) => h.map(scale)));
}

/** The single selected revision cloud (its handles behave like a polygon's). */
function singleSelectedCloud(): RevisionCloud | null {
  if (state.selectedRevCloudIds.length > 1) return null;
  return (state.drawing.revisionClouds || []).find((c) => c.id === state.selectedRevCloudId) || null;
}

/** Bounding box of a point set (used for cloud resize). */
function pointsBBox(pts: Vec2[]): { minx: number; maxx: number; miny: number; maxy: number } {
  let minx = Infinity, maxx = -Infinity, miny = Infinity, maxy = -Infinity;
  for (const p of pts) { if (p.x < minx) minx = p.x; if (p.x > maxx) maxx = p.x; if (p.y < miny) miny = p.y; if (p.y > maxy) maxy = p.y; }
  return { minx, maxx, miny, maxy };
}

/** The bounding-box middle of a point set, on screen. */
function bboxCentreScreen(pts: Vec2[]): Vec2 {
  const b = pointsBBox(pts);
  return vp.worldToScreen({ x: (b.minx + b.maxx) / 2, y: (b.miny + b.maxy) / 2 });
}

/** Each kind's hit test (handleHits.ts): its payload while the pointer is on it,
 *  as its press takes it — a mid-edge resize handle over the selected room's own
 *  door, pane or opening gives way to it; a section view's handle is no hit
 *  while one is worked. */
const HANDLE_PROBES: HandleProbes<[Vec2, Vec2]> = {
  viewHandle: (sp) => {
    const view = !handleWork ? sectionHandleAt(sp) : null;
    return view ? { view } : null;
  },
  grip: (sp) => {
    const grip = state.tool === 'select' ? gripAt(sp) : null;
    return grip ? { grip } : null;
  },
  ceilingArrow: (sp) => {
    const room = ceilingArrowAt(sp) ? ceilingRoom() : null;
    return room ? { room } : null;
  },
  underlayKnob: (sp) => {
    const bg = state.editPdf ? editedBg() : null;
    return bg && pdfRotationAtScreen(sp) ? { bg } : null;
  },
  underlayResize: (sp) => {
    const bg = state.editPdf ? editedBg() : null, handle = bg ? pdfHandleAtScreen(sp) : null;
    return bg && handle ? { bg, handle, at: vp.worldToScreen(handle.world), centre: vp.worldToScreen({ x: bg.x, y: bg.y }) } : null;
  },
  benchHandle: (sp) => {
    const bench = (state.tool === 'select' || state.tool === 'bench') && featureOf(state, 'bench') ? hitBenchHandle(sp) : null;
    return bench ? { bench } : null;
  },
  doorJamb: (sp) => doorJambHit(sp),
  doorFlip: (sp) => {
    const sel = selectedDoor();
    if (!sel) return null;
    const onIcon = (local: Vec2 | null): boolean => {
      if (!local) return false;
      const s = vp.worldToScreen(instanceToWorld(sel.inst, local));
      return Math.hypot(s.x - sp.x, s.y - sp.y) <= HANDLE_PX + 2;
    };
    const rd = isPolyRoom(sel.inst) ? roomDoor(sel.inst, sel.door) : null;
    const icons = rd ? doorIcons(sel.inst, rd) : null;
    if (onIcon(icons?.swing ?? null)) return { flip: 'swing', ...sel };
    const hinge = rd ? icons?.flip ?? null : doorFlipIconLocal(sel.inst, sel.door);
    return onIcon(hinge) ? { flip: 'hinge', ...sel } : null;
  },
  instKnob: (sp) => {
    const inst = hitRotation(sp) ? selectedInstance() : null;
    return inst ? { inst } : null;
  },
  shapeKnob: (sp) => {
    const shape = hitShapeRotation(sp) ? selectedShape() : null;
    return shape ? { shape } : null;
  },
  instResize: (sp, world) => {
    const inst = selectedInstance(), handle = hitHandle(sp);
    if (!inst || !handle || handle.nodeIndex !== undefined || handleYieldsToFeature(handle, world)) return null;
    return { inst, handle, at: vp.worldToScreen(handle.world), centre: vp.worldToScreen(instanceToWorld(inst, { x: 0, y: 0 })) };
  },
  nodeHandle: (sp) => {
    const inst = selectedInstance(), handle = hitHandle(sp);
    return inst && handle && handle.nodeIndex !== undefined ? { inst, handle } : null;
  },
  heaterCover: (sp) => {
    const cover = heaterCoverHoverAt(sp); // re-derived, so a stale highlight can't act
    return cover ? { cover } : null;
  },
  polyEdge: (_sp, world) => {
    const poly = resizablePoly(), edge = poly ? polyEdgeAt(poly, world, EDGE_PICK_PX / vp.scale) : -1;
    return poly && edge >= 0 ? { poly, edge } : null;
  },
  shapeResize: (sp) => {
    const poly = resizablePoly(), handle = hitShapeHandle(sp);
    return poly && handle ? { poly, handle, at: vp.worldToScreen(handle.world), centre: bboxCentreScreen(poly.points) } : null;
  },
  cloudKnob: (sp) => {
    const cloud = singleSelectedCloud();
    if (!cloud || cloud.points.length < 2) return null;
    const k = shapeRotationKnob(vp, cloud.points).knob;
    return Math.hypot(k.x - sp.x, k.y - sp.y) <= HANDLE_PX ? { cloud } : null;
  },
  cloudResize: (sp) => {
    const cloud = singleSelectedCloud();
    const handle = cloud && cloud.points.length >= 2 ? shapeBBoxHandles(cloud.points).find((h) => {
      const s = vp.worldToScreen(h.world);
      return Math.abs(s.x - sp.x) <= HANDLE_PX && Math.abs(s.y - sp.y) <= HANDLE_PX;
    }) : undefined;
    return cloud && handle ? { cloud, handle, at: vp.worldToScreen(handle.world), centre: bboxCentreScreen(cloud.points) } : null;
  },
  tableResize: (sp) => {
    const t = scheduleTableHandleAt(sp);
    return t ? { table: t.kind, side: t.handle } : null;
  },
  textKnob: (sp) => {
    const text = textKnobAt(sp) ? selectedTextBox() : null;
    return text ? { text } : null;
  },
  textResize: (sp) => {
    const text = selectedTextBox(), handle = text ? textHandleAtScreen(sp) : null;
    return text && handle ? { text, handle, at: vp.worldToScreen(handle.world), centre: vp.worldToScreen(textBoxCentre(text)) } : null;
  },
  textLeader: (sp) => {
    const text = textLeaderHandleAt(sp) ? selectedTextBox() : null;
    return text ? { text } : null;
  },
  sectionEnd: (sp) => {
    const sec = selectedSection();
    const end = sec ? (['a', 'b'] as const).find((k) => {
      const es = vp.worldToScreen(sec[k]);
      return Math.hypot(sp.x - es.x, sp.y - es.y) <= GRIP_HIT_PX;
    }) : undefined;
    return sec && end ? { sec, end } : null;
  },
  sectionKnob: (sp) => {
    const sec = selectedSection();
    if (!sec) return null;
    const sv = sectionSources();
    const rot = sectionRotateHandle(vp, sec, sv.instances, sv.walls, sv.polygons, sv.pipes);
    return Math.hypot(sp.x - rot.x, sp.y - rot.y) <= 8 ? { sec } : null;
  },
  drainKnob: (sp) => {
    const floor = selectedFloor(), rk = floor?.drain ? floorDrainRotateHandle(floor) : null;
    return floor && rk && Math.hypot(sp.x - rk.x, sp.y - rk.y) <= 8 ? { floor } : null;
  },
};

/** The selection's handle under the pointer — the ONE answer the press, the
 *  Shift gate, the cursor and the hover share (handleHits.ts): the first kind,
 *  in the order the mouse-down tries them, whose hit test the pointer is on. */
export function handleAt(sp: Vec2, world: Vec2): HandleHit | null {
  return firstHandle(HANDLE_PROBES, sp, world);
}

/** The selected text box, or null. */
function selectedTextBox(): TextComment | null {
  return (state.drawing.texts || []).find((t) => t.id === state.selectedTextId) ?? null;
}

/** The selected section, or null. */
function selectedSection(): SectionLine | null {
  return (state.drawing.sections || []).find((s) => s.id === state.selectedSectionId) ?? null;
}

/**
 * The select tool's resting pointer: it names the action a press would start.
 * The selection's handle under it (`hit`, from `handleAt`) shows its kind's
 * cursor (`cursorOf`) — the press works that same handle, so the two cannot
 * disagree —, in the press's order against a connection's tag and a view's
 * title (taken after a section view's handle, a grip and the fall arrow, before
 * the rest); then move over a grabbable vertex or a wall dimension-chain joint.
 */
function selectCursor(sp: Vec2, hit: HandleHit | null): string {
  if (hit && TAKEN_FIRST.has(hit.kind)) return cursorOf(hit);
  if (pickableConnTagAt(sp) || viewTitleAt(sp)) return 'move';
  if (hit) return cursorOf(hit);
  if (state.vertexGrabHover || wallSpanJointAt(sp)) return 'move';
  return 'default';
}


/** Bounding-box centre of a point set (the cloud rotation pivot). */
function shapeCenterOf(pts: Vec2[]): Vec2 {
  const b = pointsBBox(pts);
  return { x: (b.minx + b.maxx) / 2, y: (b.miny + b.maxy) / 2 };
}

/** Hole rings of a shape (empty for walls / hole-less polygons). */
function shapeHoles(shape: EditableShape): Vec2[][] {
  return isPolygonShape(shape) ? ((shape as Polygon).holes || []) : [];
}

/** Deep-copy a set of rings (for gesture-start snapshots). */
function snapshotRings(rings: Vec2[][]): Vec2[][] {
  return rings.map((r) => r.map((p) => ({ x: p.x, y: p.y })));
}

/** Set a shape's holes (polygons only); clears the field when empty. */
function setPolyHoles(shape: Polygon | Wall, rings: Vec2[][]): void {
  if (!isPolygonShape(shape)) return;
  (shape as Polygon).holes = rings.length ? rings : undefined;
}

/** Apply a point map to a shape's hole rings, from a snapshot. */
function transformHoles(shape: Polygon | Wall, holesStart: Vec2[][], fn: (p: Vec2) => Vec2): void {
  if (!isPolygonShape(shape)) return;
  setPolyHoles(shape, holesStart.map((h) => h.map(fn)));
}


// ---- Circles & ellipses (stored as polygons marked `ellipse`) ----

const ELLIPSE_SEGMENTS = 64; // a multiple of 4 so the axis extremes are exact

/** Axis-aligned ellipse outline points fitted around (cx,cy) with semi-axes rx,ry. */
export function ellipsePoints(cx: number, cy: number, rx: number, ry: number, n = ELLIPSE_SEGMENTS, rot = 0): Vec2[] {
  const cos = Math.cos(rot), sin = Math.sin(rot);
  const pts: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2;
    const ex = Math.cos(a) * rx, ey = Math.sin(a) * ry;
    pts.push({ x: cx + ex * cos - ey * sin, y: cy + ex * sin + ey * cos });
  }
  return pts;
}

/**
 * Centre, semi-axes and rotation of an ellipse polygon, recovered from its point
 * ring (mm / radians). The ring is a canonical ellipse sampled at equal angles,
 * so its centroid is the centre, `points[0]` is the major-axis end (giving rx and
 * the rotation) and `points[n/4]` is the minor-axis end (giving ry).
 */
export function ellipseParams(poly: Polygon): { cx: number; cy: number; rx: number; ry: number; rot: number } {
  const pts = poly.points;
  const n = pts.length || 1;
  let cx = 0, cy = 0;
  for (const p of pts) { cx += p.x; cy += p.y; }
  cx /= n; cy /= n;
  const p0 = pts[0] ?? { x: cx, y: cy };
  const pq = pts[Math.round(n / 4) % n] ?? { x: cx, y: cy };
  return {
    cx, cy,
    rx: Math.hypot(p0.x - cx, p0.y - cy),
    ry: Math.hypot(pq.x - cx, pq.y - cy),
    rot: Math.atan2(p0.y - cy, p0.x - cx),
  };
}

/** Create a circle/ellipse polygon at (cx,cy) with the given semi-axes and select it. */
function createEllipse(cx: number, cy: number, rx: number, ry: number): void {
  if (rx < 1 || ry < 1) return;
  pushHistory();
  const poly: Polygon = {
    id: newId('poly'),
    points: ellipsePoints(Math.round(cx), Math.round(cy), Math.round(rx), Math.round(ry)),
    ellipse: true,
    fill: '#ffffff',
    fillOpacity: 1,
    stroke: '#222222',
    strokeOpacity: 1,
  };
  const deInst = drawEditInst();
  if (deInst) {
    poly.points = poly.points.map((p) => toInstanceLocal(deInst, p));
    poly.z = nextDrawZ(deInst);
    (deInst.drawPolygons ??= []).push(poly);
    state.drawEditSelectedPolyId = poly.id;
    state.drawEditSelectedWallId = null;
    state.drawEditVerticesId = null;
  } else {
    (state.drawing.polygons ??= []).push(poly);
    deselectAll(); // the new object alone is selected
    selectOne(state, 'polygon', poly.id);
  }
  endOneShot();
  renderProperties();
}

/** Properties for a selected circle/ellipse: radius, position, arrange, fill, outline. */
/** The shared 3D-extrusion parameters (height + height-from-ground) for a
 *  polygon/ellipse — the same pair a heater exposes. Height 0 keeps the shape
 *  flat (not extruded in the 3D preview). */
function appendShapeHeightRows(body: HTMLElement, poly: Polygon): void {
  body.appendChild(sectionTitle('3D'));
  body.appendChild(numberRow('Height (mm)', Math.round(poly.height ?? 0), (v) => {
    pushHistory(); poly.height = Math.max(0, Math.round(v)); renderProperties(); invalidate();
  }, { commit: true }));
  body.appendChild(heightFromGroundRow(polygonBase(poly), (v) => { poly.heightFromGround = v; }));
}

function renderEllipseProperties(body: HTMLElement, poly: Polygon): void {
  const { cx, cy, rx, ry, rot } = ellipseParams(poly);
  const circle = Math.abs(rx - ry) < 1;
  const setRadii = (nrx: number, nry: number): void => {
    if (nrx < 1 || nry < 1) return;
    pushHistory();
    poly.points = ellipsePoints(Math.round(cx), Math.round(cy), Math.round(nrx), Math.round(nry), ELLIPSE_SEGMENTS, rot);
    syncDimensions();
    renderProperties();
    invalidate();
  };
  body.appendChild(sectionTitle(circle ? 'Circle' : 'Ellipse'));
  body.appendChild(nameRow(poly.name, dictDefaults(polygonWord(poly), trIn), (v) => { poly.name = v.trim() || undefined; invalidate(); }));
  if (circle) {
    body.appendChild(numberRow('Radius (mm)', Math.round(rx), (v) => setRadii(v, v), { commit: true }));
  } else {
    // Link the two radii so scaling one axis scales the other proportionally.
    body.appendChild(dimChainToggle());
    body.appendChild(numberRow('Radius X (mm)', Math.round(rx), (v) => setRadii(v, dimChained && rx > 0 ? ry * (v / rx) : ry), { commit: true }));
    body.appendChild(numberRow('Radius Y (mm)', Math.round(ry), (v) => setRadii(dimChained && ry > 0 ? rx * (v / ry) : rx, v), { commit: true }));
  }
  const minX = cx - rx, minY = cy - ry;
  const translate = (dx: number, dy: number): void => {
    if (!dx && !dy) return;
    pushHistory();
    poly.points = poly.points.map((p) => ({ x: p.x + dx, y: p.y + dy }));
    syncDimensions();
    renderProperties();
    invalidate();
  };
  body.appendChild(sectionTitle('Position'));
  body.appendChild(numberRow('X (mm)', Math.round(minX), (v) => translate(v - minX, 0), { commit: true }));
  body.appendChild(numberRow('Y (mm)', Math.round(minY), (v) => translate(0, v - minY), { commit: true }));
  body.appendChild(sectionTitle('Fill'));
  body.appendChild(colorRow('Colour', poly.fill, (hex) => (poly.fill = hex)));
  body.appendChild(opacityRow('Opacity', poly.fillOpacity, (v) => (poly.fillOpacity = v)));
  body.appendChild(sectionTitle('Outline'));
  body.appendChild(colorRow('Colour', poly.stroke, (hex) => (poly.stroke = hex)));
  body.appendChild(opacityRow('Opacity', poly.strokeOpacity, (v) => (poly.strokeOpacity = v)));
  appendShapeHeightRows(body, poly);
  renderPolyConnections(body, poly);
  const hint = document.createElement('div');
  hint.className = 'prop-empty';
  hint.style.marginTop = '10px';
  hint.textContent = tr('Drag to move · drag a handle to resize.');
  body.appendChild(hint);
}

/**
 * The extra offset that snaps a dragged polygon (its vertices given in `moved`
 * world positions) onto the nearest vertex of another polygon, or null. `at` is
 * the coincident point, for the snap indicator.
 */
/** The three points an arc is snapped by: its two ends and its centre. */
export function arcEndPoints(arc: Pick<Arc, 'center' | 'radius' | 'startDeg' | 'endDeg'>): Vec2[] {
  const at = (deg: number): Vec2 => ({
    x: arc.center.x + arc.radius * Math.cos((deg * Math.PI) / 180),
    y: arc.center.y + arc.radius * Math.sin((deg * Math.PI) / 180),
  });
  return [at(arc.startDeg), at(arc.endDeg)];
}

function arcSnapPoints(arc: Pick<Arc, 'center' | 'radius' | 'startDeg' | 'endDeg'>): Vec2[] {
  return [arc.center, ...arcEndPoints(arc)];
}


/** A move step's inputs from the drag under way (moveStep.ts): the vertex
 *  grabbed at the press, else the dragged object's own reference point
 *  `origin`, goes on the grid; the cursor's travel since the press. */
function moveFrom(world: Vec2, origin: Vec2): Pick<MoveStepIn, 'ref' | 'grabbed' | 'travel' | 'cursor' | 'grid'> {
  const gv = drag.grabbedVertexStart;
  return { ref: gv ?? origin, grabbed: !!gv, travel: { x: world.x - moveStartWorld.x, y: world.y - moveStartWorld.y }, cursor: world, grid: gsnap };
}

/** The vertices a move drag could grab under the cursor, so the snap symbol knows
 *  where to sit. A 2+ selection offers every selected object's own corners; a
 *  single not-yet-selected object under the cursor offers its corners (its first
 *  click selects AND moves it). A single already-selected object is left out — its
 *  corners are resize / vertex-edit handles, so grabbing one must keep resizing. */
function dragGrabVerts(world: Vec2, headOf: () => ObjRef | null = () => objectStackAt(world, vp.worldToScreen(world)).head): Vec2[] {
  const selCount = selectedCount(state, MULTI_KINDS);
  if (selCount >= 2 && pointOverSelection(world)) return selectedMovables().flatMap((m) => m.verts());
  // The object the press takes: the head of the stack, nothing under it.
  const head = headOf();
  if (!head) return [];
  const d = state.drawing;
  switch (head.kind) {
    case 'instance': {
      if (selCount === 1 && selectedOf(state, 'instance').includes(head.id)) return [];
      const i = d.instances.find((x) => x.id === head.id);
      if (!i) return [];
      return OBJECTS.instance.points(i).map((p) => ({ x: p.x, y: p.y }));
    }
    case 'polygon': {
      const poly = (d.polygons || []).find((x) => x.id === head.id);
      return !poly || poly.ellipse || (selCount === 1 && state.selectedPolyId === poly.id) ? [] : poly.points;
    }
    case 'wall': {
      const wall = (d.walls || []).find((x) => x.id === head.id);
      return !wall || (selCount === 1 && state.selectedWallId === wall.id) ? [] : wallSnapVerts(wall);
    }
    case 'arc': {
      const arc = (d.arcs || []).find((x) => x.id === head.id);
      return !arc || (selCount === 1 && state.selectedArcId === arc.id) ? [] : arcSnapPoints(arc);
    }
    case 'leader': {
      const leader = (d.leaders || []).find((x) => x.id === head.id);
      return !leader || (selCount === 1 && state.selectedLeaderId === leader.id) ? [] : leader.points;
    }
    case 'cloud': {
      const cloud = (d.revisionClouds || []).find((x) => x.id === head.id);
      return !cloud || (selCount === 1 && state.selectedRevCloudId === cloud.id) ? [] : cloud.points;
    }
    default: return [];
  }
}

function moveVertexSnap(
  moved: Vec2[], except: ReadonlySet<string> | string | null, cursor: Vec2,
): { dx: number; dy: number; at: Vec2 } | null {
  if (!state.snapEnabled) return null;
  // `snapCandidates` is the drawing's ONE vertex list — products, rooms (outer and
  // inner corners, jambs, pane ends), user polygons, walls and lines, dimensions,
  // generated sections and the imported plan. Gathered ONCE per drag (it replays
  // every section's emitter, which is far too much work to redo on every
  // mousemove); nothing but the dragged object — which is excluded — moves while
  // it is held.
  // What is constrained to the dragged objects follows them, so its vertices are
  // no target either (they would hold the drag where it started).
  if (!state.moveSnapTargets) state.moveSnapTargets = snapCandidates(withConstrainedPartners(except)).map((c) => c.p);
  return moveSnapOffset(moved, cursor, state.moveSnapTargets, 12 / vp.scale);
}

/** Bounding-box centre (world) of a shape — the rotation pivot. */
function shapeCenter(shape: Polygon | Wall): Vec2 {
  const pts = shapeOutlinePoints(shape);
  let minx = Infinity, maxx = -Infinity, miny = Infinity, maxy = -Infinity;
  for (const p of pts) {
    if (p.x < minx) minx = p.x;
    if (p.x > maxx) maxx = p.x;
    if (p.y < miny) miny = p.y;
    if (p.y > maxy) maxy = p.y;
  }
  return { x: (minx + maxx) / 2, y: (miny + maxy) / 2 };
}

/** Rotate a point around a pivot by `rad` radians (CCW). */
export function rotateAround(p: Vec2, pivot: Vec2, rad: number): Vec2 {
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  const dx = p.x - pivot.x;
  const dy = p.y - pivot.y;
  return { x: pivot.x + dx * c - dy * s, y: pivot.y + dx * s + dy * c };
}

/** Shift held on the current mouse event: while drawing it constrains the new
 *  segment to whole 15° steps from the last point (PowerPoint's Shift); set from
 *  every model and paper mouse event, read by the shared snappers. */
let drawConstrain = false;
/** The angle lock the draft snappers apply: Ortho wins, then Shift's 15°, then
 *  Polar tracking. */
export function draftLockStep(): number {
  return state.orthoEnabled ? 90 : drawConstrain ? ROTATE_STEP_DEG : state.polarStep;
}

interface DraftSnapOpts {
  /** Hold Alt to suspend all snapping (free placement). */
  shift?: boolean;
  /** Skip Shift's 15° lock — a rectangle turns Shift into "square" instead. */
  noConstrain?: boolean;
  /** Objects left out of the object snap (ones following the cursor). */
  except?: ReadonlySet<string>;
  /** Offer a 45°-multiple ray from the last vertex. */
  allow45?: boolean;
  /** Last drawn segment: its end anchors the 45° ray and the 90° normal guide. */
  lastSeg?: readonly [Vec2, Vec2];
  /** Extra object-snap endpoints — an excluded object's own points that stay put. */
  extra?: Vec2[];
}

/**
 * The one snapping routine every model draw tool and vertex-drag uses: a
 * proximity snap to existing geometry (`snapPoint`), then the shared
 * `snapDraftGuides` alignment/45°/ortho guides at model scale. The sheet line
 * tool goes through the same guides in paper mm (`schLineSnap`).
 * Snapping is on by default; holding Alt suspends it entirely, and Shift locks
 * the new segment to 15° steps (`drawConstrain`).
 */
export function snapDraftPoint(world: Vec2, candidates: Vec2[], opts: DraftSnapOpts = {}): Vec2 {
  return snapDraftPointHit(world, candidates, opts).p;
}
/** `snapDraftPoint`, saying whether an object snap (OSNAP) took the point. */
function snapDraftPointHit(world: Vec2, candidates: Vec2[], opts: DraftSnapOpts = {}): PlanSnap {
  if (opts.shift) return { p: world, osnap: false }; // Alt suspends snapping
  const s = snapPointAnchored(world, opts.except ?? null, opts.extra);
  // An acquired OSNAP wins over the guides, same rule as the dimension tool.
  // Here the marker was drawn at the candidate and then the guides moved the
  // returned point off it — so the marker showed a vertex the click did not take.
  if (s.snapped && !(drawConstrain && !opts.noConstrain && opts.lastSeg)) return { p: s.point, osnap: true };
  return { p: snapDraftGuides(s.point, candidates, 12 / vp.scale, {
    allow45: opts.allow45,
    lastSeg: opts.lastSeg,
    lockStep: opts.noConstrain ? (state.orthoEnabled ? 90 : state.polarStep) : draftLockStep(),
  }), osnap: false };
}

/** Offset, Trim / Extend, Fillet / Chamfer and Constrain (`plan` in `TOOL_PLACE`)
 *  clicked in a section view or a ceiling plan: they work on the plan, and say so. */
function downPlanOnlyTool(e: MouseEvent, world: Vec2): boolean {
  if (e.button !== 0 || !placeOf(state.tool).planOnly || toolPlane(world).kind === 'plan') return false;
  flashToolStatus(tr('This tool works on the plan — not in a section view or a ceiling plan.'));
  return true;
}
/** Rotating in a section view tilts what is selected: only parts that tilt. */
const CANNOT_TILT = '{obj} cannot tilt — only custom parts, lights, infrared panels, fans, steam conveyors, showers, SpaManager boxes, control units and steam generators do.';

/** A tilt of the selection in a section view: about the level line through
 *  `pivot` square to the view (right-hand about `axis` = the cut × up, so a turn
 *  from along the cut toward up is positive), and where each part started — an
 *  infrared panel with the wall it stands against. */
type TiltRun = { axis: Point3; pivot: Point3; parts: Array<{ inst: ProductInstance; body: Body; start: BodyPlacement; wall: WallFace | null }> };

/** Why the selection cannot tilt: the first of it that stays upright. */
function tiltRefusal(): string | null {
  const ids = selectedMovables().map((m) => m.id);
  if (!ids.length) return null;
  for (const id of ids) {
    const i = state.drawing.instances.find((x) => x.id === id);
    if (!i || !canTilt(i)) return tr(CANNOT_TILT).replace('{obj}', constraintObjName(id));
  }
  return null;
}

/** Where a part's body stands now, as a placement. */
function placementOf(inst: ProductInstance): BodyPlacement {
  return { x: inst.x, y: inst.y, base: partSpanOf(inst)[0], rotation: inst.rotation, rotationX: inst.rotationX ?? 0, rotationY: inst.rotationY ?? 0 };
}

/** Begin tilting the selected parts in section `sec` about `pivot`. */
export function startTilt(sec: SectionLine, pivot: Point3): TiltRun {
  const parts = state.drawing.instances.filter((i) => selectedOf(state, 'instance').includes(i.id) && canTilt(i)).map((inst) => {
    const body = tiltBody(inst);
    return { inst, body, start: placementOf(inst), wall: traitsOf(inst.category).tiltsAgainstWall ? wallAgainst(body, hostWallFaces(inst)) : null };
  });
  return { axis: tiltAxis(sec), pivot, parts };
}

/** Tilt the run's parts `deg` from where they started (each placed afresh, so a
 *  preview never drifts), never into the floor; an infrared panel stays against
 *  its wall. */
export function applyTilt(run: TiltRun, deg: number): void {
  for (const p of run.parts) {
    placePart(p.inst, aboveFloor(turnBody(p.body, run.axis, deg, run.pivot), p.start.base));
    if (p.wall) keepAgainst(p.inst, p.wall);
  }
  for (const room of new Set(run.parts.map((p) => saunaOfHeater(p.inst)).filter((r): r is ProductInstance => !!r))) regenerateSauna(room);
}

/** The selection's middle in a section view: its boxes' middle there, as a
 *  point of the model (on the cut, its height). */
export function sectionSelectionMiddle(sec: SectionLine): Point3 | null {
  const ts = sectionSelectionBoxes(sec);
  if (!ts.length) return null;
  const t = (Math.min(...ts.map((x) => x.bounds.min.x)) + Math.max(...ts.map((x) => x.bounds.max.x))) / 2;
  const h = (Math.min(...ts.map((x) => x.bounds.min.y)) + Math.max(...ts.map((x) => x.bounds.max.y))) / 2;
  return { ...onCut(sec, t), z: h };
}

/** What a point taken in a plane snaps onto: the drawing's object snap leaving
 *  out `except` (what is moving), or — a drag — the points gathered when it
 *  began (`fixed`: a section's include the dragged objects' own, where they
 *  started). */
export type PlaneSnapOnto = { except: ReadonlySet<string> | null } | { fixed: Vec2[] };

/** Where a point a tool takes in `plane` lands — Move / Copy / Rotate's base and
 *  second points, the mirror line's, a polar array's centre, a drag in a view's
 *  plane. Alt: where the cursor is. The first point (no `from`) takes the object
 *  snap. One from `from` snaps as a draft segment from it — the OSNAP, the
 *  alignment guides, Shift a 15° step (`snapDraftPoint`) —; in a section view in
 *  the view's own frame, along it and up it however it is turned on the plan,
 *  an acquired OSNAP first. A drag (`fixed`) takes the nearest gathered point
 *  within 12 px, and Shift keeps it level with or plumb over `from`. */
export function planeSnap(world: Vec2, plane: WorkPlane, from: Vec2 | null, alt: boolean, onto: PlaneSnapOnto): Vec2 {
  if ('fixed' in onto) {
    const p = alt ? world : nearestVertexWithin(world, onto.fixed, 12 / vp.scale) ?? world;
    return drawConstrain && from ? levelOrPlumb(plane, from, p) : p;
  }
  if (!from || plane.kind !== 'section') {
    return snapDraftPoint(world, from ? [from] : [], { shift: alt, allow45: true, lastSeg: from ? [from, from] : undefined, except: onto.except ?? undefined });
  }
  if (alt) return world;
  const s = snapPointAnchored(world, onto.except);
  if (s.snapped) return s.point;
  const base = toPlaneFrame(plane, from);
  return fromPlaneFrame(plane, snapDraftGuides(toPlaneFrame(plane, s.point), [base], 12 / vp.scale, { allow45: true, lastSeg: [base, base], lockStep: draftLockStep() }));
}

/** A pick point for Move / Copy / Rotate (`planeSnap` from the base, in its
 *  plane), never onto the objects being moved — but Rotate's Horizontal levels a
 *  point of the selection itself, so it snaps onto it. */
function pickSnap(world: Vec2, alt: boolean): Vec2 {
  return planeSnap(world, pickPlane, pickBase, alt, { except: pickBase && !(state.tool === 'rotate' && rotateLevel) ? pickExcept : null });
}

/** The plane a tool works in (sections.md, *Edit tools in a view's plane*). The
 *  base point decides: clicked at `at`, the section view or the reflected
 *  ceiling plan drawn there — a point ON a view's box, its floor line where a
 *  part's foot snaps, within 4 px, is in the view —, else the plan. Asked on a
 *  click, never on hover: finding a view's box draws its section. With no click
 *  — a panel's Apply, an Arrange button, Select's press in a view —, the section
 *  view drilled into while it is, else the plan. */
export function toolPlane(at?: Vec2): WorkPlane {
  if (!at) {
    const sec = state.sectionDrill ? (state.drawing.sections || []).find((s) => s.id === state.sectionDrill) : undefined;
    return sec ? { kind: 'section', sec } : PLAN_PLANE;
  }
  const sv = sectionSources(), tol = 4 / vp.scale;
  for (const sec of state.drawing.sections || []) {
    if (objHidden(sec.id)) continue;
    const ring = sectionViewOutline(sec, sv.instances, sv.walls, sv.polygons, sv.pipes);
    if (pointInPolygon(at, ring) || ring.some((p, i) => distToSegment(at, p, ring[(i + 1) % ring.length]) <= tol)) return { kind: 'section', sec };
  }
  const v = objectAt('rcp', at);
  return v ? { kind: 'rcp', offset: { ...v.offset } } : PLAN_PLANE;
}

/** A drag of the selection inside a drilled-into section view (along its cut and
 *  up) or inside a ceiling plan (as on the plan): its move session (the copy a
 *  Ctrl press leaves behind taken at the press), where it was grabbed, what it
 *  snaps to (gathered once — a section's points include the dragged objects'
 *  own, where they started), and a room's features it moves instead. */
let planeDrag: {
  session: SelectionMove; plane: WorkPlane; grab: Vec2; targets: Vec2[];
  features: FeatureMove[] | null;
} | null = null;

function startPlaneDrag(e: MouseEvent, plane: WorkPlane, world: Vec2, features: FeatureMove[] | null = null): void {
  const except = new Set(selectedObjectIds());
  // A room's features are no whole objects: the session moves none, they move by their own rule.
  const session = beginSelectionMove({ step: 'open', copy: (e.ctrlKey || e.metaKey) && !features }, features ? [] : undefined);
  planeDrag = { session, plane, grab: world, features, targets: snapCandidates(withConstrainedPartners(except)).map((c) => c.p) };
  startDrag('plane-move');
}

function dragMovePlane(e: MouseEvent, _sp: Vec2, world: Vec2): boolean {
  const d = planeDrag;
  if (!d) return true;
  const m = planeMove(d.plane, d.grab, planeSnap(world, d.plane, d.grab, e.altKey, { fixed: d.targets }));
  if (d.session.moveTo({ x: m.dx, y: m.dy, z: m.dz })) {
    if (d.features) moveFeaturesBy(d.features, m.dx, m.dy, m.dz);
    invalidate();
  }
  return true;
}

/** The drag let go: a Ctrl drag leaves a copy where the objects started. */
function endPlaneDrag(e: MouseEvent): void {
  const d = planeDrag;
  planeDrag = null;
  d?.session.end('keep', { copy: e.ctrlKey || e.metaKey });
  renderProperties();
}
function cancelPlaneDrag(): void {
  const d = planeDrag;
  planeDrag = null;
  d?.session.end('cancel');
}

export function sectionDisplayName(sec: SectionLine): string {
  return `${tr('Section')} ${sectionLabel((state.drawing.sections || []).indexOf(sec))}`;
}

/** A room's feature, or a drawn wall's, selected and moved in a section view:
 *  where it started. */
type FeatureMove = { id: string; wall: boolean; part: SecPart; from: FeatureSpot };

/** The selected features (sub-selected parts of a room or a drawn wall), each
 *  where it stands — null when what is selected is whole objects. A backrest is
 *  left out (it moves with its bench). */
export function selectedFeatureMoves(): FeatureMove[] | null {
  const ts = sectionTargets().filter((t) => t.part);
  if (!ts.length) return null;
  const out: FeatureMove[] = [];
  for (const t of ts) {
    const part = t.part!;
    const w = part.kind === 'wallFeature' ? (state.drawing.walls || []).find((x) => x.id === t.id) : undefined;
    const f = w?.features?.find((x) => x.id === part.id);
    const inst = w ? undefined : state.drawing.instances.find((i) => i.id === t.id);
    const from = w && f ? wallFeatureSpot(w, f) : inst ? featureSpot(inst, part) : null;
    if (from && (from.along != null || from.up != null)) out.push({ id: t.id, wall: !!w, part, from });
  }
  return out;
}

/** Why the selected features do not move in a section view (none of them can). */
export function featureStuckWhy(): string {
  const ts = sectionTargets();
  for (const t of ts) {
    const inst = t.part ? state.drawing.instances.find((i) => i.id === t.id) : undefined;
    const stuck = inst && t.part ? featureSpot(inst, t.part)?.stuck : undefined;
    if (stuck === 'follows-ceiling') return tr('A partition that follows the ceiling has no height of its own to move — give it one first.');
    if (stuck === 'profile') return tr('A bench swept from a profile takes its seat from the profile.');
    if (t.part?.kind === 'door') return tr('A door in a partition moves with the partition.');
    if (t.part?.kind === 'fixture' && inst?.lights?.some((l) => l.id === t.part!.id)) return tr('A light moves with what it is fixed to.');
  }
  return tr('A backrest moves with its bench.');
}

/** A room's or a drawn wall's feature is what is selected, and `plane` is a
 *  section view: a tool there moves it alone, or says it only moves. */
export function featureSelectedIn(plane: WorkPlane): boolean {
  return plane.kind === 'section' && sectionTargets().some((t) => !!t.part);
}

/** Why a tool that takes whole objects (Copy, Mirror, Flip, the arrays) cannot
 *  act on the selection in `plane` — a feature selected in a section view only
 *  moves —, and with `tilt` one that tilts them there (Rotate, the panel's
 *  Rotate, a polar array, a slanted mirror line): a part that stays upright
 *  refuses the whole turn. Null when it can (always on the plan). */
export function planeRefusal(plane: WorkPlane, tilt = false): string | null {
  if (plane.kind !== 'section') return null;
  if (featureSelectedIn(plane)) return tr(FEATURE_ONLY_MOVES);
  return tilt ? tiltRefusal() : null;
}

/** Move the features (dx, dy) on the plan and dz up from where they started: each
 *  slides by the move's part along its wall and rises, as far as it can. */
export function moveFeaturesBy(moves: FeatureMove[], dx: number, dy: number, dz: number): void {
  for (const m of moves) {
    const d = m.from.dir ? dx * m.from.dir.x + dy * m.from.dir.y : 0;
    const to = { along: m.from.along != null ? m.from.along + d : undefined, up: m.from.up != null ? m.from.up + dz : undefined };
    if (m.wall) {
      const w = (state.drawing.walls || []).find((x) => x.id === m.id), f = w?.features?.find((x) => x.id === m.part.id);
      if (w && f) placeWallFeature(w, f, to);
      continue;
    }
    const inst = state.drawing.instances.find((i) => i.id === m.id);
    if (!inst) continue;
    followVertices(state.drawing, inst.id, () => placeFeature(inst, m.part, to));
    regenerateSauna(inst);
  }
}

/** Where an object stands, base to top (mm above the ground); null for a kind
 *  with no height. */
export function heightSpanOf(id: string): [number, number] | null {
  const i = state.drawing.instances.find((x) => x.id === id);
  if (i) return i.autoBenches ? [roomBase(i), roomBase(i) + roomCrown(i)] : partSpanOf(i);
  const p = (state.drawing.polygons || []).find((x) => x.id === id);
  if (p) return [polygonBase(p), polygonBase(p) + polygonHeight(p)];
  const w = (state.drawing.walls || []).find((x) => x.id === id);
  if (w) return [wallBase(w), wallBase(w) + wallHeight(w)];
  return null;
}

/** What the selected rooms carry that is not itself selected. */
export function selectionCarry(): ProductInstance[] {
  return roomsCarry(state.drawing.instances.filter((i) => selectedOf(state, 'instance').includes(i.id)));
}

/** Mirror the selection's heights about `h` (mm above the ground), in ONE move
 *  session: each object's base goes where its top was seen from the line; it
 *  stays upright, and what a room carries rises with it. `from`: as
 *  `mirrorSelection`'s. */
function mirrorHeightsAbout(h: number, from?: UndoMark): void {
  moveSelectionOnce((s) => {
    for (const m of s.movables) {
      const span = heightSpanOf(m.id);
      if (span) s.moveOne(m.id, { x: 0, y: 0, z: 2 * h - span[1] - span[0] });
    }
  }, undefined, from);
}

/** The selection in a drilled-into section view: each object's box there
 *  (along, up) — Align / Distribute's targets, Scale's and Mirror's extents.
 *  What the view does not show is left out; a selected room feature moves on
 *  its own, so it has none. */
export function sectionSelectionBoxes(sec: SectionLine): { id: string; bounds: Bounds }[] {
  if (selectedFeatureMoves()) return [];
  const sv = sectionSources(), movs = selectedMovables();
  const boxes = sectionSourceBoxes(sec, sv.instances, sv.walls, sv.polygons, movs.map((m) => srcOf(m.id)));
  return movs.flatMap((m, i) => {
    const b = boxes[i];
    return b ? [{ id: m.id, bounds: { min: { x: b.t0, y: b.h0 }, max: { x: b.t1, y: b.h1 } } }] : [];
  });
}

/** Scale the selection ×k in a section view, in ONE move session: each object's
 *  place along the cut about the selection's middle there, its size every way
 *  (`sectionScaleMap`, workPlane.ts; a size-locked product keeps its own). A
 *  room is scaled as on the plan — its heights kept — its base about the
 *  selection's foot (what stands on the floor stays on it); what stands in it,
 *  carried or selected with it, goes by the room's map as on the plan, heights
 *  kept, rising with its base. An object on its own scales its base about the
 *  foot and its heights ×k. Returns how many kept their size. */
export function scaleInSection(sec: SectionLine, k: number): number {
  const ts = sectionSelectionBoxes(sec);
  if (!ts.length) return 0;
  const along = (Math.min(...ts.map((t) => t.bounds.min.x)) + Math.max(...ts.map((t) => t.bounds.max.x))) / 2;
  const foot = Math.min(...ts.map((t) => t.bounds.min.y));
  const mid = (pts: Vec2[]): Vec2 => ({ x: pts.reduce((a, q) => a + q.x, 0) / pts.length, y: pts.reduce((a, q) => a + q.y, 0) / pts.length });
  const d = state.drawing;
  let kept = 0;
  moveSelectionOnce((s) => {
    // Each object's step, worked out before any is taken (the room a part stands in, as it stood).
    const rooms = new Map<string, { m: PointMap; dz: number }>();
    for (const mv of s.movables) {
      const r = d.instances.find((x) => x.id === mv.id);
      if (r?.autoBenches) rooms.set(r.id, { m: sectionScaleMap(sec, along, k, { x: r.x, y: r.y }), dz: (k - 1) * (roomBase(r) - foot) });
    }
    const steps: { id: string; m: PointMap; dz: number }[] = [];
    for (const mv of s.movables) {
      // What has a height: a part (a node object by its nodes' middle), a shape, a drawn wall.
      const i = d.instances.find((x) => x.id === mv.id);
      const pts = i ? i.nodes : [...(d.polygons || []), ...(d.walls || [])].find((x) => x.id === mv.id)?.points;
      const c = pts?.length ? mid(pts) : i ? { x: i.x, y: i.y } : null;
      if (!c) continue;
      if (i && resizeLocked(i)) kept++;
      const room = i && (rooms.get(i.id) ?? rooms.get(hostRoomOf(i)?.id ?? ''));
      steps.push(room ? { id: mv.id, ...room } : { id: mv.id, m: sectionScaleMap(sec, along, k, c, foot), dz: 0 });
    }
    for (const st of steps) {
      s.transform(st.m, st.id);
      if (st.dz) s.moveOne(st.id, { x: 0, y: 0, z: st.dz });
    }
  });
  return kept;
}

/** The base point is down: the selection starts following the cursor, in ONE
 *  move session (one undo step for the whole move, from the base pick's mark). */
function startPick(p: Vec2, plane: WorkPlane = PLAN_PLANE, features: FeatureMove[] | null = null): void {
  pickBase = p;
  pickBase3d = null;
  pickPlane = plane;
  pickFeatures = features;
  // Rotate in a section view tilts about the level line through the base point.
  pickTilt = null;
  if (state.tool === 'rotate' && plane.kind === 'section') {
    const at = planePoint(plane, p);
    pickTilt = { run: startTilt(plane.sec, { x: at.at.x, y: at.at.y, z: at.z ?? 0 }), deg: 0 };
  }
  pickExcept = new Set([...selectedObjectIds()]);
  for (const id of [...pickExcept]) {
    const g = state.drawing.instances.find((i) => i.id === id)?.groupId;
    if (g) for (const m of state.drawing.instances) if (m.groupId === g) pickExcept.add(m.id);
  }
  // Rotate turns what Transform turns (a dimension selected too); a room's
  // features move by their own rule, so the session moves no whole object.
  const items = features ? [] : state.tool === 'rotate' ? selectedItems(state, state.drawing) : undefined;
  pickMove = beginSelectionMove({ step: 'mark', copy: state.tool === 'copy' }, items);
}

/** Show the selection where the pick would put it: moved to `target`, or turned
 *  to the angle of `target` seen from the base (degrees CCW from east). */
function previewPick(target: Vec2): void {
  if (!pickBase) return;
  if (state.tool === 'rotate') {
    if (rotateLevel) return; // nothing turns until the point is clicked
    // In a section view the angle is the view's own (along the cut toward up).
    const a = toPlaneFrame(pickPlane, pickBase), b = toPlaneFrame(pickPlane, target);
    applyPickAngle((Math.atan2(b.y - a.y, b.x - a.x) * 180) / Math.PI);
    return;
  }
  // In a section: along its cut and up; in a ceiling plan or the plan: as drawn.
  movePickTo(planeMove(pickPlane, pickBase, target));
}

/** Show the selection moved `m` from where the pick began. */
function movePickTo(m: { dx: number; dy: number; dz: number }): void {
  if (!pickMove?.moveTo({ x: m.dx, y: m.dy, z: m.dz })) return;
  if (pickFeatures) moveFeaturesBy(pickFeatures, m.dx, m.dy, m.dz);
  renderProperties(); // the panel's X / Y follow, as in a drag
  invalidate();
}
function applyPickAngle(deg: number): void {
  const s = pickMove;
  if (!pickBase || !s) return;
  // In a section view the turn tilts the parts about a level line.
  const tilt = pickTilt;
  if (tilt) {
    if (deg !== tilt.deg) s.own(() => applyTilt(tilt.run, deg), true);
    tilt.deg = deg;
    return;
  }
  // A ceiling plan's pivot is its room's point under it.
  if (!s.turnTo(deg, planePoint(pickPlane, pickBase).at)) return;
  syncDimensions();
  renderProperties();
  invalidate();
}

/** Where Move / Copy's base point was clicked in the 3D view, its height too
 *  (null for a pick begun on the plan). */
let pickBase3d: Pt3 | null = null;
/** The 3D base point of the live pick, else null. */
export const pickBase3dNow = (): Pt3 | null => (pickBase ? pickBase3d : null);

/** What a pick begun in 3D moves — the selection and what its rooms carry —,
 *  else null: the 3D view's click sees through it (it follows the pointer). */
export function pickMoving3d(): Set<string> | null {
  if (!pickBase || !pickBase3d) return null;
  return new Set([...selectedObjectIds(), ...(pickMove?.moving ?? [])]);
}

/** Show Move / Copy's selection moved to `p` from its 3D base point — the 3D
 *  view's live preview, as the plan's pointer moves it. */
export function previewPick3d(p: Pt3): void {
  const b = pickBase3d;
  if (!pickBase || !b) return;
  movePickTo({ dx: Math.round(p.x - b.x), dy: Math.round(p.y - b.y), dz: Math.round(p.z - b.z) });
}

/** A Move / Copy click in the 3D view, on what it shows (`p`): the base point,
 *  then where it goes — the selection moved (Copy: copied) by the step between
 *  them, its height too, in whole mm. One undo step, as on the plan; a pick begun
 *  on the plan starts again here. */
export function pickClick3d(p: Pt3): void {
  if (pickBase && !pickBase3d) cancelPick();
  if (!pickBase) {
    if (selectedCount(state, MULTI_KINDS) === 0) { flashToolStatus(tr('Select the objects first')); return; }
    const fixedSel = state.tool !== 'copy' ? [...selectedObjectIds()].find((id) => isFixedObject(id)) : undefined;
    if (fixedSel) { flashToolStatus(tr('{obj} is fixed in place.').replace('{obj}', constraintObjName(fixedSel))); return; }
    startPick({ x: p.x, y: p.y });
    pickBase3d = p;
    invalidate();
    return;
  }
  const b = pickBase3d!;
  movePickTo({ dx: Math.round(p.x - b.x), dy: Math.round(p.y - b.y), dz: Math.round(p.z - b.z) });
  finishPick(null);
}

/** The pick's state let go (its move session handed back, to be ended). */
function dropPick(): SelectionMove | null {
  const s = pickMove;
  pickBase = null; // before endOneShot: setTool would cancel a live pick
  pickMove = null;
  pickFeatures = null;
  pickTilt = null;
  pickPlane = PLAN_PLANE;
  return s;
}

/** The second pick: leave the selection there (COPY leaves a copy where it
 *  stood), the move kept as one undo step from the base pick, and the command ended. */
export function finishPick(target: Vec2 | null, angleDeg?: number): void {
  if (!pickBase) return;
  // Horizontal: the turn that levels the pivot→point line, in the view's frame.
  if (state.tool === 'rotate' && rotateLevel && target && angleDeg == null) {
    const turn = levelTurnDeg(toPlaneFrame(pickPlane, pickBase), toPlaneFrame(pickPlane, target));
    if (turn == null) { flashToolStatus(tr('Click a point away from the pivot.')); return; }
    angleDeg = turn;
    target = null;
  }
  if (angleDeg != null) applyPickAngle(angleDeg);
  else if (target) previewPick(target);
  dropPick()?.end('keep');
  endOneShot();
}

/** Take a live pick back (Esc, or switching tools): the drawing goes back to its
 *  snapshot at the base pick, exactly — a preview turned or moved back by the
 *  angle or the distance it went drifts by rounding — and nothing reaches the
 *  history. */
function cancelPick(): void {
  if (!pickBase) return;
  dropPick()?.end('cancel');
  invalidate();
}

/** Shift makes a rectangle's second corner a square's: both sides take the
 *  longer of the two, each keeping its own direction from the first corner. */
function squareWithShift(start: Vec2 | null, p: Vec2, shift: boolean): Vec2 {
  if (!start || !shift) return p;
  const dx = p.x - start.x, dy = p.y - start.y;
  const side = Math.max(Math.abs(dx), Math.abs(dy));
  return { x: start.x + (dx < 0 ? -side : side), y: start.y + (dy < 0 ? -side : side) };
}

/** Max distance (mm) at which a nearby object's vertex is offered as an
 *  orthogonal draw-snap target. */
const NEARBY_SNAP_DIST = 1000;

/** Vertices of all objects within `maxDist` (mm) of `p` — instance corners,
 *  polygon / circle points and wall vertices — offered as extra orthogonal
 *  draw-snap targets so a new vertex can align to nearby geometry. */
function nearbyOrthoVerts(p: Vec2, maxDist = NEARBY_SNAP_DIST, except: string | null = null): Vec2[] {
  const out: Vec2[] = [];
  const consider = (v: Vec2): void => {
    if (Math.abs(v.x - p.x) <= maxDist && Math.abs(v.y - p.y) <= maxDist && distance(p, v) <= maxDist) out.push(v);
  };
  const out_ = snapExcludes(except);
  for (const inst of state.drawing.instances) if (!out_(inst.id)) for (const c of instanceOutline(inst)) consider(c);
  for (const poly of state.drawing.polygons || []) if (!out_(poly.id)) for (const v of poly.points) consider(v);
  for (const wall of state.drawing.walls || []) if (!out_(wall.id)) for (const v of wall.points) consider(v);
  for (const f of state.drawing.floorings || []) if (!out_(f.id)) for (const v of f.points) consider(v);
  return out;
}

/**
 * Snap for dragging an EXISTING vertex — the same engine every draw tool uses:
 * grid, orthogonal projection to the shape's own vertices AND to nearby objects
 * (or `nearby`, the sibling shapes in the drawing editor), with a 45° / normal
 * anchor from the neighbour. Records the blue alignment guide.
 * `self` (the dragged object) is left out of the object snap and the nearby
 * targets, and only its OTHER vertices (`ownVerts`) are offered back: everything
 * of it that moves with the cursor — the dragged vertex's last position, a thick
 * wall's band corners and the runs either side — would otherwise catch the
 * vertex, and the drag would hold still until the cursor left the aperture,
 * then jump.
 */
export function editVertexSnap(world: Vec2, ownVerts: Vec2[], self: string | null, prev: Vec2 | undefined, shift: boolean, nearby?: Vec2[]): Vec2 {
  return editVertexSnapHit(world, ownVerts, self, prev, shift, nearby).p;
}
/** `editVertexSnap`, saying whether an object snap (OSNAP) took the point. */
export function editVertexSnapHit(world: Vec2, ownVerts: Vec2[], self: string | null, prev: Vec2 | undefined, shift: boolean, nearby?: Vec2[]): PlanSnap {
  const nv = shift ? [] : (nearby ?? nearbyOrthoVerts(world, NEARBY_SNAP_DIST, self));
  const hit = snapDraftPointHit(gsnapPt(world), [...ownVerts, ...nv], {
    shift, allow45: true, lastSeg: prev ? [prev, prev] : undefined,
    except: self ? new Set([self]) : undefined, extra: self ? ownVerts : undefined,
  });
  state.editGuide = shift ? null : { at: hit.p, verts: [...ownVerts, ...nv] };
  return hit;
}

/** Poly-room draw snap (routes through the shared draft snapper), also aligning
 *  to nearby objects' vertices. */
function applyPolyRoomSnap(world: Vec2, shift: boolean): Vec2 {
  const extra = shift ? [] : nearbyOrthoVerts(world);
  state.drawSnapVerts = extra;
  return snapDraftPoint(world, [...polyRoomDraft, ...extra], { shift, allow45: true, lastSeg: draftSeg(polyRoomDraft) });
}

/** Whether a world point lies on the (possibly rotated) edited PDF underlay. */
function pointInPdf(world: Vec2): boolean {
  const bi = editedBg();
  if (!bi) return false;
  const lp = rotatePoint({ x: world.x - bi.x, y: world.y - bi.y }, -(bi.rotation || 0));
  return Math.abs(lp.x) <= bi.widthMm / 2 && Math.abs(lp.y) <= bi.heightMm / 2;
}

/** The resize handle of the edited PDF under a screen point, or null. */
function pdfHandleAtScreen(sp: Vec2): Handle | null {
  const bi = editedBg();
  if (!bi) return null;
  for (const h of pdfBoxHandles(bi)) {
    const s = vp.worldToScreen(h.world);
    if (Math.abs(s.x - sp.x) <= HANDLE_PX && Math.abs(s.y - sp.y) <= HANDLE_PX) return h;
  }
  return null;
}

/** Whether a screen point is over the edited PDF's rotation knob. */
function pdfRotationAtScreen(sp: Vec2): boolean {
  const bi = editedBg();
  if (!bi) return false;
  const k = pdfRotationKnobScreen(vp, bi);
  return Math.hypot(k.x - sp.x, k.y - sp.y) <= HANDLE_PX;
}

/** Resize the edited PDF by dragging a handle, keeping the opposite edge fixed in world space. */
function resizePdf(handle: Handle, mouseWorld: Vec2): void {
  const bi = editedBg();
  if (!bi) return;
  const { sx, sy } = handle;
  const rot = bi.rotation || 0;
  const center = { x: bi.x, y: bi.y };
  const anchorLocal = { x: sx === 0 ? 0 : (-sx * bi.widthMm) / 2, y: sy === 0 ? 0 : (-sy * bi.heightMm) / 2 };
  const anchorWorld = addVec(center, rotatePoint(anchorLocal, rot));
  const v = rotatePoint(subVec(mouseWorld, anchorWorld), -rot);
  const MIN = 1;
  const oldW = bi.widthMm, oldH = bi.heightMm;
  if (sx !== 0) bi.widthMm = Math.max(MIN, sx * v.x);
  if (sy !== 0) bi.heightMm = Math.max(MIN, sy * v.y);
  // Link W&H: the passive axis follows the dragged one (a corner uses the faster axis).
  if (linkWH() && oldW > 0 && oldH > 0) {
    if (sx !== 0 && sy === 0) bi.heightMm = Math.max(MIN, oldH * (bi.widthMm / oldW));
    else if (sy !== 0 && sx === 0) bi.widthMm = Math.max(MIN, oldW * (bi.heightMm / oldH));
    else if (sx !== 0 && sy !== 0) { const f = Math.max(bi.widthMm / oldW, bi.heightMm / oldH); bi.widthMm = Math.max(MIN, oldW * f); bi.heightMm = Math.max(MIN, oldH * f); }
  }
  const nc = addVec(anchorWorld, rotatePoint({ x: (sx * bi.widthMm) / 2, y: (sy * bi.heightMm) / 2 }, rot));
  bi.x = nc.x;
  bi.y = nc.y;
}

/** A modal cheat-sheet of keyboard shortcuts (toggled by `?` or Help ▸ menu). */
export function showShortcutsHelp(): void {
  const existing = document.getElementById('shortcuts-overlay');
  if (existing) { existing.remove(); return; } // toggle off
  const ov = document.createElement('div');
  ov.id = 'shortcuts-overlay';
  ov.className = 'shortcuts-overlay';
  ov.dataset.modal = '';
  ov.dataset.dismissable = '';
  const panel = document.createElement('div');
  panel.className = 'shortcuts-panel';
  const head = document.createElement('div');
  head.className = 'shortcuts-head';
  head.textContent = tr('Keyboard shortcuts');
  const close = document.createElement('button');
  close.className = 'shortcuts-close';
  close.dataset.role = 'cancel';
  close.textContent = '×';
  close.addEventListener('click', () => ov.remove());
  head.appendChild(close);
  panel.appendChild(head);
  // Searchable: in the UI language and in English, accent-insensitive, any order
  // (the command search's own `searchKey`).
  const search = document.createElement('input');
  search.type = 'search';
  search.className = 'shortcuts-search';
  search.placeholder = tr('Search shortcuts and gestures…');
  panel.appendChild(search);
  const table = document.createElement('table');
  table.className = 'shortcuts-table';
  const rows: Array<{ tr: HTMLTableRowElement; group: HTMLTableRowElement; key: string }> = [];
  const addGroup = (title: string): HTMLTableRowElement => {
    const gr = document.createElement('tr');
    const gh = document.createElement('th');
    gh.colSpan = 2;
    gh.className = 'shortcuts-group';
    gh.textContent = tr(title);
    gr.appendChild(gh);
    table.appendChild(gr);
    return gr;
  };
  const addRow = (group: HTMLTableRowElement, keys: string, label: string, en: string): void => {
    const row = document.createElement('tr');
    const kc = document.createElement('td');
    kc.className = 'shortcuts-key';
    kc.textContent = keys;
    const dc = document.createElement('td');
    dc.textContent = tr(label);
    row.append(kc, dc);
    table.appendChild(row);
    rows.push({ tr: row, group, key: searchKey(`${keys} ${tr(label)} ${en}`) });
  };
  // Generated from the one shortcut table, group by group, so this sheet can
  // never list a key the handler doesn't honour (or miss one it does).
  const groups: ShortcutGroup[] = ['File', 'Edit', 'Drawing', 'View', 'Help'];
  for (const g of groups) {
    const gr = addGroup(g);
    for (const sc of SHORTCUTS.filter((x) => x.group === g)) addRow(gr, sc.display ? tr(sc.display) : shortcutKeysLabel(sc, tr), sc.label, sc.label);
  }
  const mouse = addGroup('Mouse');
  for (const g of GESTURES) addRow(mouse, tr(g.how), g.label, `${g.how} ${g.label}`);
  const none = document.createElement('div');
  none.className = 'prop-empty hidden';
  none.textContent = tr('Nothing matches.');
  // `?` still closes the sheet it opened — until something is typed in the box.
  search.addEventListener('keydown', (e) => { if (matchesShortcut(e, 'help') && !search.value) { e.preventDefault(); ov.remove(); } });
  search.addEventListener('input', () => {
    const words = searchKey(search.value).split(' ').filter(Boolean);
    const live = new Set<HTMLTableRowElement>();
    for (const r of rows) {
      const on = words.every((w) => r.key.includes(w));
      r.tr.hidden = !on;
      if (on) live.add(r.group);
    }
    for (const r of rows) r.group.hidden = !live.has(r.group);
    none.classList.toggle('hidden', live.size > 0);
  });
  panel.append(table, none);
  ov.appendChild(panel);
  document.body.appendChild(ov);
  setTimeout(() => search.focus(), 0);
}

/** Finish editing a text box inline, keeping (or putting back) what was typed. */
export function closeTextEditor(commit = true): void {
  if (state.editingTextId) textEditorUi.close(commit);
}

// ---- In-drawing connection-schedule table -----------------------------------

export const cellEditor = $('cell-editor') as HTMLTextAreaElement;
/** The inline editor over a schedule table's cell (ui/inlineText.ts). */
export const cellEditorUi = inlineEditor(cellEditor);


/** Every dimension whose drawn line (at its offset, not the measured segment)
 *  passes near a world point, top of the stacking first. Hidden and locked
 *  ones are left out: nothing may pick them. */
function dimensionsAt(world: Vec2): string[] {
  return objectsAt('dim', world).filter((d) => !objUnpickable(d.id)).map((d) => d.id);
}

/** Hit-test dimension lines near a world point. Returns dimension id or null. */
function hitDimension(world: Vec2, _sp: Vec2): string | null {
  return dimensionsAt(world)[0] ?? null;
}

/** World-space route of a connection: from-centre → fixed waypoints → to-centre. */
export function connectionPath(c: Connection): Vec2[] | null {
  return connectionRoute(state.drawing, c);
}

/** A catalogue entry's connection points, copied for a placed part (absent when
 *  it declares none). */
export function copyPorts(ports?: ConnPort[]): ConnPort[] | undefined {
  return ports?.length ? ports.map((p) => ({ ...p })) : undefined;
}

/** The Connect tool's pick (connPick.ts) against the app: its object snap,
 *  Select's hit tests, what is hidden, the zoom. */
function connPickEnv(): ConnPickEnv {
  return {
    drawing: state.drawing,
    aperture: SNAP_PX / vp.scale,
    featureTol: 8 / vp.scale,
    outlineTol: 1.5 / vp.scale,
    partShown: (i) => !i.frozen && !isDisciplineHidden(i.category) && !objHidden(i.id),
    snap: (world) => snapPointAnchored(world, null),
    textAt: (world) => objectsAt('text', world)[0],
    partAt: (world) => { const id = instancesAt(world, true)[0]; return id ? state.drawing.instances.find((i) => i.id === id) : undefined; },
    roomAt: (world) => instancesAt(world, false).map((id) => state.drawing.instances.find((i) => i.id === id)).find((i) => i?.autoBenches && isPolyRoom(i)),
    polygonAt: (world) => objectsAt('polygon', world)[0],
    wallAt: (world) => objectsAt('wall', world)[0],
    floorAt: (world) => objectsAt('flooring', world)[0],
    anchors: (part) => instanceSnapPoints(part).filter((x) => x.anchor).map((x) => x.p),
    markCentre: (c) => { state.snapIndicator = c; state.snapIndicatorKind = 'center'; },
  };
}

/** What a Connect click (or an end dragged) at `world` attaches to
 *  (connPick.ts: a port, an anchored snap, an outline, the object under it, a
 *  free point; Alt free). */
export function connPickAt(world: Vec2, free = false): ConnPick {
  return connPickAtPure(connPickEnv(), world, free);
}

/** The object under `world` a connection end can glue to, and its dots. */
function connTargetAt(world: Vec2): ConnBody | null {
  return connTargetAtPure(connPickEnv(), world);
}

/** The dots the object under `world` offers a connection end. */
export function connPortsAt(world: Vec2): Vec2[] {
  return connTargetAt(world)?.dots ?? [];
}

/** Finish the connection being drawn at `to`. Refused when it would join a point
 *  to itself. The new connection is born in the last-used discipline and is left
 *  SELECTED, its panel open — no dialog. */
function finishConnection(to: ConnPick): void {
  const d = connectionUi.connDraft;
  if (!d) return;
  const bends = d.pts.slice(1);
  if (d.from.id === to.id && !bends.length && distance(d.from.p, to.p) < 1) return;
  pushHistory();
  // A connection drawn from (or to) a declared port starts in that port's
  // discipline — unless one was typed for this line, which is the user saying so.
  const conn = newConnection(connEditCtx(), { id: newConnectionId(), ...draftEnds(d.from, to), bends }, connSticky());
  state.drawing.connections.push(conn);
  connectionUi.connDraft = null;
  state.connHoverDots = [];
  endOneShot();
  deselectAll();
  selectOne(state, 'conn', conn.id);
  updateStatus();
  renderProperties();
  invalidate();
}

/** What the Connect tool shows while a connection is drawn: the one it would
 *  make now, to the pick under the cursor, resolved as every connection is
 *  (`connGeom`) — its plan route and its discipline. A pipe's type is copied
 *  into a scratch drawing, not the drawing. */
function connDraftPreview(d: NonNullable<typeof connectionUi.connDraft>): { pts: Vec2[]; type: ConnectionType } | null {
  const scratch: Drawing = { ...state.drawing, pipeTypes: [...(state.drawing.pipeTypes ?? [])] };
  const c = newConnection({ drawing: scratch, pipeTypes: pipeTypeChoices() }, { id: '', ...draftEnds(d.from, d.hover), bends: d.pts.slice(1) }, connSticky());
  const g = connGeom(scratch, c);
  return g ? { pts: g.plan.pts, type: g.discipline } : null;
}

/** Enter / right-click / Esc / a click on the last bend (polylineDraft.ts, once
 *  it has one): the last bend becomes a FREE end. */
function finishConnectionFree(): void {
  const p = connectionUi.connDraft && connectionUi.connDraft.pts.length > 1 ? connectionUi.connDraft.pts.pop() : undefined;
  if (p) finishConnection({ id: '', end: { p }, p });
}

/** Swap a connection's ends (its From ↔ To, glue and bends). */
export function reverseSelectedConnection(c: Connection): void {
  pushHistory();
  connEdit(c, { kind: 'reverse' });
  renderProperties();
  invalidate();
}

/** A fixed bend on segment `seg` of the route, at `p` — then dragged by its grip. */
export function addConnectionBend(c: Connection, seg: number, p: Vec2): void {
  pushHistory();
  connEdit(c, { kind: 'bendAdd', at: seg, p });
  invalidate();
}

// ---- Pipes: a connection made of a pipe (docs/claude/connections.md *Pipes*) ----


/** Selection priority when two candidates are (nearly) equidistant: higher wins,
 *  matching AutoCAD's osnap precedence (endpoint > intersection > midpoint > …). */
export const SNAP_PRIORITY: Record<SnapKind, number> = {
  endpoint: 8, node: 7, intersection: 6, midpoint: 5, center: 4, quadrant: 3, perpendicular: 2, nearest: 1,
};

/** Snap candidates contributed by one instance (each anchored so it tracks resize). */
/** The corners a dragged part snaps BY. A heater block offers its real outlines —
 *  body, clearance gap (per side) and outer edge with its guard cover — not
 *  `instanceCorners`, a SYMMETRIC box sized by the larger gap on each axis, whose
 *  edge on the smaller-gap side is an edge that isn't there. */
export function instanceProbeCorners(inst: ProductInstance): Vec2[] {
  if (!drawsHeaterBlock(inst)) return instanceCorners(inst);
  const out: Vec2[] = [];
  for (const p of heaterOutlines(inst).flat()) if (!out.some((q) => Math.abs(q.x - p.x) < 1e-6 && Math.abs(q.y - p.y) < 1e-6)) out.push(p);
  return out.map((p) => instanceToWorld(inst, p));
}

/** World-space vertices of the drawing-editor instance's own shapes (polygons +
 *  walls), so new lines/polygons can snap to the drawing being edited. */
export function editorShapeSnapVerts(): Vec2[] {
  const inst = drawEditInst();
  if (!inst) return [];
  const toW = (p: Vec2): Vec2 => instanceToWorld(inst, p);
  const out: Vec2[] = [];
  for (const p of inst.drawPolygons ?? []) {
    for (const v of p.points) out.push(toW(v));
    for (const h of p.holes ?? []) for (const v of h) out.push(toW(v));
  }
  for (const w of inst.drawWalls ?? []) for (const v of w.points) out.push(toW(v));
  return out;
}

/** Collect candidate snap points: instance corners/centres, plan and dimension endpoints. */
/** The snap lists' exclusion: what the caller leaves out, and whatever sits on a
 *  hidden layer (a locked one still snaps). */
/** `hidden`: more layers to leave out (a sheet's own, for its paper snap). */
export function snapExcludes(except: ReadonlySet<string> | string | null, hidden?: ReadonlySet<string>): (id: string) => boolean {
  const ex = excludes(except);
  const m = layers();
  return (id) => ex(id) || onHiddenLayer(m, id, hidden);
}

/** What a section view cuts and shows: every object on a shown layer. */
export function sectionSources(hidden?: ReadonlySet<string>): { instances: ProductInstance[]; walls: Wall[]; polygons: Polygon[]; pipes: SectionPipe[] } {
  const m = layers();
  const shown = <T extends { id: string }>(arr: T[] | undefined): T[] => (arr || []).filter((o) => !onHiddenLayer(m, o.id, hidden));
  const pipes = pipeTubes(state.drawing, (c) => !onHiddenLayer(m, c.id, hidden) && state.disciplineFilters[connDiscipline(c)] !== false);
  return { instances: shown(state.drawing.instances), walls: shown(state.drawing.walls), polygons: shown(state.drawing.polygons), pipes };
}

/** What each drawn kind offers `snapCandidates`: its corners, centres and
 *  quadrants — the vertex list every move, draw and dimension snaps to. Every
 *  ItemKind has an entry, so a new kind does not typecheck until it says what it
 *  snaps to (null: nothing, and why); SNAP_EDGES is its twin for the runs between
 *  them. The keys run in the order the list is built (a tie goes to the first). */
type SnapPointsOf = (pts: SnapCandidate[], isExcluded: (id: string) => boolean, hidden: ReadonlySet<string> | undefined, sheet: Sheet | undefined) => void;
export const SNAP_POINTS: Record<ItemKind, SnapPointsOf | null> = {
  instance: (pts, isExcluded, hidden, sheet) => {
    for (const inst of state.drawing.instances) {
      if (isExcluded(inst.id)) continue;
      for (const c of instanceSnapPoints(inst)) pts.push(c);
      // The ceiling lines the room shows: a ridge's or a crease's ends, a flat's corners.
      const cm = ceilingMarksNow(inst, sheet);
      if (cm) for (const p of ceilingSnapMarks(cm).points) pts.push({ p: instanceToWorld(inst, p), kind: 'endpoint', of: { kind: 'room', id: inst.id } });
    }
  },
  polygon: (pts, isExcluded, hidden, sheet) => {
    // User-drawn polygon vertices, anchored so a dimension snapped to one tracks
    // that vertex (and can resize the segment when its value is edited). A hole's
    // vertices are real vertices too — they are just not anchored, since an
    // anchor addresses the outer ring's index.
    for (const poly of state.drawing.polygons || []) {
      if (isExcluded(poly.id)) continue;
      const of = { kind: 'shape', id: poly.id } as const;
      for (const h of poly.holes || []) for (const v of h) pts.push({ p: v, of });
      // A circle/ellipse has NO vertices: `points` is a tessellation, so offering it
      // like a polygon's corners hands out 64 fake endpoints. They lie (the marker
      // says □ Endpoint on a point that is only on the curve by accident of the
      // tessellation, and the true nearest point of the arc is between two of them),
      // and since precedence beats distance they also buried the centre on any
      // circle small enough for its rim to sit inside the aperture. What a curve
      // really offers is its CENTRE and its four QUADRANTS, both derived through the
      // same fit `drawEllipsePolygon` draws it with, so the marker cannot sit off
      // the drawn shape. Anything along the rim between them is what `nearest` is
      // for — `collectSnapEdges` still contributes the ring as edges.
      if (poly.ellipse) {
        const fit = ellipseFromRing(poly.points);
        if (fit) {
          pts.push({ p: fit.center, kind: 'center', of });
          for (const q of ellipseQuadrants(fit)) pts.push({ p: q, kind: 'quadrant', of });
        }
        continue;
      }
      poly.points.forEach((v, vi) => pts.push({ p: v, anchor: { instId: '', nx: 0, ny: 0, shape: { id: poly.id, vertex: vi } }, of }));
    }
  },
  wall: (pts, isExcluded, hidden, sheet) => {
    // Outer edges of user-drawn walls: objects snap flush to a wall's outside
    // faces (the band outline). The line tool is an annotation, not a wall.
    for (const wall of state.drawing.walls || []) {
      if (isExcluded(wall.id) || wall.style === 'line') continue;
      const of = { kind: 'wall', id: wall.id } as const;
      for (const v of wallBand(wall.points, wall.thickness, wall.align)) pts.push({ p: v, of });
      for (const sp of wallFeatureSpans(wall)) for (const v of wallFeatureQuad(sp)) pts.push({ p: v, of }); // the jambs
    }
    // Wall/line centre-line vertices, anchored so a dimension snapped to one tracks
    // that wall (and can resize the segment when its value is edited).
    for (const wall of state.drawing.walls || []) {
      if (isExcluded(wall.id)) continue;
      wall.points.forEach((v, vi) => pts.push({ p: v, anchor: { instId: '', nx: 0, ny: 0, wall: { id: wall.id, vertex: vi } }, of: { kind: 'wall', id: wall.id } }));
    }
  },
  dim: (pts, isExcluded, hidden, sheet) => {
    for (const d of state.drawing.dimensions) {
      if (isExcluded(d.id)) continue;
      pts.push({ p: d.a }, { p: d.b });
    }
  },
  flooring: (pts, isExcluded, hidden, sheet) => {
    // Every flooring's polygon vertices, so drawing/moving/dimensioning snaps to floors.
    for (const f of state.drawing.floorings || []) {
      if (isExcluded(f.id)) continue;
      for (const v of f.points) pts.push({ p: v });
    }
  },
  text: (pts, isExcluded, hidden, sheet) => {
    // A text box is a drawn object too: its four corners, and its leader tip when
    // it has one (the point the arrow is aimed at is the thing worth measuring to).
    for (const t of state.drawing.texts || []) {
      if (isExcluded(t.id)) continue;
      for (const c of textBoxCorners(t)) pts.push({ p: c });
      if (t.leader) pts.push({ p: t.leader });
    }
  },
  // The schedule tables are deliberately absent: their drawn height comes from
  // `scheduleTableLayout`, which needs a text-measuring canvas context, and this
  // runs on every mousemove. `heightMm` on the table is the TEXT height, not the
  // box, so it cannot stand in for one.
  table: null,
  leader: (pts, isExcluded, hidden, sheet) => {
    // Annotation geometry is drawn objects too, so it snaps like the rest: a
    // leader's and a revision cloud's own vertices, and an arc's ends + centre.
    for (const l of state.drawing.leaders || []) {
      if (isExcluded(l.id)) continue;
      for (const v of l.points) pts.push({ p: v });
    }
  },
  cloud: (pts, isExcluded, hidden, sheet) => {
    for (const c of state.drawing.revisionClouds || []) {
      if (isExcluded(c.id)) continue;
      for (const v of c.points) pts.push({ p: v });
    }
  },
  arc: (pts, isExcluded, hidden, sheet) => {
    for (const a of state.drawing.arcs || []) {
      if (isExcluded(a.id)) continue;
      // The centre is a CENTRE snap: untagged it fell back to 'endpoint', so it drew
      // the □ glyph and answered to the Endpoint toggle instead of Centre.
      pts.push({ p: { x: a.center.x, y: a.center.y }, kind: 'center' });
      for (const v of arcEndPoints(a)) pts.push({ p: v });
    }
  },
  rcp: (pts, isExcluded) => {
    // A reflected ceiling plan's lines' ends and corners, so its walls can be dimensioned.
    for (const v of state.drawing.ceilingPlans || []) {
      if (isExcluded(v.id)) continue;
      for (const pc of rcpWorldPieces(v)) if (pc.kind === 'line') for (const p of pc.pts) pts.push({ p });
    }
  },
  section: (pts, isExcluded, hidden, sheet) => {
    // Vertices of each generated section elevation (bench tops, wall corners,
    // floor/ceiling lines), so the cursor and dimension tool snap onto them.
    for (const sec of state.drawing.sections || []) {
      if (isExcluded(sec.id)) continue;
      const sv = sectionSources(hidden);
      for (const v of sectionVertices(sec, sv.instances, sv.walls, sv.polygons, sv.pipes)) pts.push({ p: v });
    }
  },
  // A connection runs between objects that snap by their own points.
  conn: null,
};

// =====================================================================
// Pointer interaction
// =====================================================================
/** The drag under way's own data (*The drag*, docs/claude/tools.md): what the
 *  press that starts it sets, read by its move and its release, and dropped whole
 *  when it ends or is put back (dropDragData). The mode, and how far the pointer
 *  has travelled, are the core's (`state.dragMode`, `dragTravelled()`). */
function freshDragData() {
  return {
    // While dragging a part over another part, the part underneath (its bbox
    // flashes); releasing relates the two so they can be connected.
    dropTargetId: null as string | null,
    dragGuides: [] as DragGuide[],
    /** World position of the vertex a move drag grabbed (the snap symbol was showing
     *  on it at mousedown), or null when the drag started away from any vertex. When
     *  set, the move paths snap THIS vertex to the grid and to other objects instead
     *  of the object's own origin. Captured at mousedown, cleared at mouseup. */
    grabbedVertexStart: null as Vec2 | null,
    /** The connection whose schedule tag is being slid along its route. */
    connLabel: null as string | null,
    // Schedule-table drag: which table, the grabbed world point + the table's
    // anchor/width at grab time (move updates x/y; resize updates width, or x+width
    // from the left edge).
    table: null as { kind: SchedTableKind; grab: Vec2; x: number; y: number; width: number; handle: -1 | 1 } | null,
    // Rubber-band selection: drag start (left→right = window, right→left = crossing).
    marqueeStart: null as Vec2 | null,
    marqueeEnd: null as Vec2 | null,
    /** The marquee in progress ADDS to the selection (it started with Shift). */
    marqueeAdd: false,
    // Dimension drag: slide the line perpendicular to change its offset, keeping the
    // (anchored) endpoints fixed. `grab` is the world point grabbed; `origOffset` the
    // dimension's offset at grab time.
    dimOffset: null as { id: string; grab: Vec2; origOffset: number } | null,
    // Section-line drag: translate both endpoints so the whole line (and the section
    // drawing it generates) follows the cursor. `grab` is the grabbed world point.
    sectionMove: null as { id: string; grab: Vec2; a: Vec2; b: Vec2 } | null,
    // Section-drawing drag: move the generated drawing (sets viewOrigin) or rotate it
    // (sets viewAngle) independently of the cut line. `grab` is the grabbed world
    // point; `origin` the drawing origin at grab time; `angle0`/`pivot` for rotation.
    sectionView: null as { id: string; grab: Vec2; origin: Vec2; angle0: number; pivot: Vec2 } | null,
    /** A reflected ceiling plan being dragged: where it was grabbed, its offset then. */
    rcpView: null as { id: string; grab: Vec2; offset0: Vec2 } | null,
    /** A fibre of a starry sky being dragged: its room, its sky and its number. */
    skyFibre: null as { instId: string; lightId: string; i: number } | null,
    // Section-endpoint drag: reshape the cut by moving one end (a or b) of the line.
    sectionEndpoint: null as { id: string; end: 'a' | 'b' } | null,
    // Text-comment resize: the handle being dragged.
    textResizeHandle: null as Handle | null,
    // Vertex-edit: the polygon/wall vertex being dragged.
    // `closed`: a wall / line whose ends met when the drag began (its joint moves as one).
    vertex: null as { id: string; ring: number; index: number; closed?: boolean } | null,
    // PDF transform: the resize handle being dragged.
    pdfResizeHandle: null as Handle | null,
    resizeHandle: null as Handle | null,
    // Polygon bounding-box resize: the handle being dragged and the bbox at gesture
    // start (the drag scales `shapePointsStart` about the opposite side).
    shapeResizeHandle: null as Handle | null,
    shapeResizeBBox: null as { minx: number; maxx: number; miny: number; maxy: number } | null,
    doorResizeSide: null as 'left' | 'right' | null,
    // The wall dimension-chain joint being dragged: which room, which edge, and the
    // index of the figure on its left.
    wallSpan: null as { instId: string; edge: number; index: number } | null,
    // Glass tool: dragging along a wall to mark a glazed segment. `edge` is set for
    // polygonal rooms (the polygon edge index), with start/end measured along it.
    // `innerWallId` set = the pane is being glazed into that interior partition, and
    // start/end are distances along its inward length instead of along a room edge.
    glass: null as { instId: string; wall: WallSide; start: number; end: number; downLocal: Vec2; edge?: number; innerWallId?: string } | null,
    // Opening tool: dragging along a wall to mark an open (glass-less) segment.
    opening: null as { instId: string; wall: WallSide; start: number; end: number; downLocal: Vec2; edge?: number } | null,
    // Inner-wall tool: from a point on a wall, dragging inward (perpendicular) to set
    // the interior wall's length. `pos` is locked at the start.
    // `edge` is set for a partition hosted on a polygonal room's edge (then `wall`
    // is a placeholder and `pos` runs along that edge).
    innerWall: null as { instId: string; wall: WallSide; pos: number; length: number; downLocal: Vec2; edge?: number } | null,
    // Inner-wall tool: grabbing an existing inner wall to slide it along its host
    // wall (changing only `pos`, so its perpendicularity and wall snaps are kept).
    // `selectOnly` marks a drag started from vertex-edit mode: there a plain click
    // SELECTS the partition, where the inner-wall tool's click means delete.
    innerWallMove: null as { instId: string; index: number; wall: WallSide; grabAlong: number; origPos: number; edge?: number; selectOnly?: boolean } | null,
    // A selected bench's handle being dragged (Select or the Bench tool).
    benchEdit: null as { instId: string; benchId: string; kind: BenchHandleKind } | null,
    wallFeat: null as { wallId: string; kind: 'glass' | 'opening'; seg: number; s0: number; s1: number } | null,
    // A selected wall feature being slid along its segment: where it started and the
    // station the press grabbed.
    wallFeatMove: null as { wallId: string; id: string; start: number; end: number; grab: number } | null,
    /** A title being dragged: which, where it was grabbed (world), its offset then
     *  (a section's `t`, `h`; a ceiling plan's `x`, `y`). */
    viewTitle: null as (ViewTitleRef & { grab: Vec2; o0: [number, number] }) | null,
    // Polyline-bench node drag: the index being moved, and snapshots of all nodes.
    moveNodeIndex: -1,
    moveNodesStart: [] as Vec2[],
    drawEditResizeHandle: null as Handle | null,
    drawEditResizeBBox: null as { minx: number; maxx: number; miny: number; maxy: number } | null,
    drawEditDownLocal: null as Vec2 | null,
    polyRoomVertex: null as number | null,
    // Why a corner drag is currently being refused: the wall whose figures don't fit
    // any other length (highlighted red for as long as it blocks), or `edge: null` for
    // a reshape that would slide the whole ring instead of resizing it.
    reshapeBlock: null as { instId: string; edge: number | null } | null,
  };
}
const drag = freshDragData();
function dropDragData(): void {
  Object.assign(drag, freshDragData());
}
/** A ceiling grip's work records its room's section views again only once it
 *  settles: each step holds them on their last recording (section.ts
 *  `holdRoomSections`), and only two things release them (null), so the next
 *  frame records them anew — the pointer resting `SECTION_SETTLE_MS` (this
 *  timer) and the grip's work ending, however it ends (`afterGripWork`). */
const SECTION_SETTLE_MS = 150;
let sectionSettle = 0;
export function holdSections(room: string | null): void {
  clearTimeout(sectionSettle);
  holdRoomSections(room);
  if (room) sectionSettle = window.setTimeout(() => holdSections(null), SECTION_SETTLE_MS);
  else invalidate();
}
/** A press on a member of an already-selected group: released without a drag,
 *  it selects one level further in (PowerPoint's click-into-a-group). */
let groupDrill: { sp: Vec2; ids: string[] } | null = null;
/** An Alt press in Select, waiting for its release to cycle the selection. */
let cycleProbe: { sp: Vec2; world: Vec2; prev: ObjRef[] } | null = null;
/** The pointer is over the drawing — Tab cycles only then. */
export let pointerOnCanvas = false;
/** Where the pointer last was over the drawing (screen px) — Ctrl+V pastes there. */
let lastPointer: Vec2 = { x: 0, y: 0 };
/** Space is held: a left drag pans (`spacePanned` records that one did, so the
 *  release does not also repeat the last tool). */
export let spaceHeld = false;
let spacePanned = false;
/** A press with Space held starts a pan: the release then repeats no tool. */
export function noteSpacePan(): void { if (spaceHeld) spacePanned = true; }
// The moved text box's start corner.
let textMoveStart: Vec2 = { x: 0, y: 0 };
let pdfRotateStart = 0;
export let lastScreen: Vec2 = { x: 0, y: 0 };
let moveStartWorld: Vec2 = { x: 0, y: 0 };
let moveInstanceStart: Vec2 = { x: 0, y: 0 };
// Polygon/wall move & rotate: snapshot the points at gesture start so the drag
// is computed from a stable base. `shapeHolesStart` mirrors it for polygon holes.
let shapePointsStart: Vec2[] = [];
let shapeHolesStart: Vec2[][] = [];
let rotatePivot: Vec2 = { x: 0, y: 0 };
let rotateStartAngle = 0;
// A door, panes or openings are sub-selected inside the selected room (the one
// feature selected, selection.ts): an id that is not on the selected room simply
// resolves to nothing, so it never needs clearing from afar. Panes and openings
// are picked several at a time (Shift/Ctrl-click), the last picked the primary.
export function selDoorId(): string | null { return featureOf(state, 'door')?.id ?? null; }
export function selGlassId(): string | null { return featureOf(state, 'glass')?.id ?? null; }
export function selOpeningId(): string | null { return featureOf(state, 'opening')?.id ?? null; }
/** Sub-select a door of the selected room alone (none: let the door go). */
export function pickDoor(id: string | null | undefined): void {
  if (id) selectFeature(state, { kind: 'door', id }); else dropFeature(state, 'door');
}
/** Sub-select these panes, or openings, of the selected room — the last the
 *  primary; none lets them go. */
export function pickSegments(kind: 'glass' | 'opening', ids: readonly (string | null | undefined)[]): void {
  const list = [...new Set(ids.filter((x): x is string => !!x))];
  if (list.length) selectFeature(state, { kind, id: list[list.length - 1], ids: list }); else dropFeature(state, kind);
}
/** The (room, door) currently sub-selected, or null. */
export function selectedDoor(): { inst: ProductInstance; door: SaunaDoor } | null {
  const inst = selectedInstance();
  const door = inst ? doorById(inst, selDoorId()) : null;
  return inst && door ? { inst, door } : null;
}
// Selected-bench border/vertex editing: which handle of which bench is dragged.
export type BenchHandleKind =
  | 'corner-end-inner'
  | 'corner-end-outer'
  | 'corner-start-inner'
  | 'corner-start-outer'
  | 'end'
  | 'inner'
  | 'start';

/**
 * Snap coordinate `t` along the axis (origin + t·dir) to guide vertices: for each
 * vertex the axis is intersected with its vertical, horizontal and ±45° guide
 * lines, plus the vertex's perpendicular foot (aligning the dragged face with the
 * vertex). The nearest candidate within `thr` wins; otherwise `t` is unchanged.
 */
function benchSnapAlong(origin: Vec2, dir: Vec2, t: number, verts: Vec2[], thr: number): number {
  let best = t, bestD = thr;
  const tryT = (cand: number): void => {
    const d = Math.abs(cand - t);
    if (d < bestD) { bestD = d; best = cand; }
  };
  for (const v of verts) {
    const rx = v.x - origin.x, ry = v.y - origin.y;
    tryT(rx * dir.x + ry * dir.y); // perpendicular foot
    if (Math.abs(dir.x) > 1e-9) tryT(rx / dir.x); // vertical line x = v.x
    if (Math.abs(dir.y) > 1e-9) tryT(ry / dir.y); // horizontal line y = v.y
    const dd = dir.x - dir.y, ds = dir.x + dir.y;
    if (Math.abs(dd) > 1e-9) tryT((rx - ry) / dd); // 45° line (1,1) through v
    if (Math.abs(ds) > 1e-9) tryT((rx + ry) / ds); // 45° line (1,-1) through v
  }
  return best;
}


/** The selected bench's edit handles as {kind, world point}, or [] (a
 *  partition's bench has none). */
function selectedBenchHandles(): Array<{ kind: BenchHandleKind; p: Vec2 }> {
  const sel = featureOf(state, 'bench');
  const inst = sel ? state.drawing.instances.find((i) => i.id === sel.instId) : undefined;
  if (!sel || !inst || !isPolyRoom(inst)) return [];
  return polyBenchHandles(inst, sel.benchId).map((h) => ({ kind: h.kind, p: instanceToWorld(inst, h.p) }));
}

/** The selected bench's handle under the screen point, or null. */
function hitBenchHandle(sp: Vec2): BenchHandleKind | null {
  for (const h of selectedBenchHandles()) {
    const s = vp.worldToScreen(h.p);
    if (Math.abs(s.x - sp.x) <= HANDLE_PX && Math.abs(s.y - sp.y) <= HANDLE_PX) return h.kind;
  }
  return null;
}

/** Apply a bench handle drag to the model, snapping the dragged border(s) to the
 *  room's and other benches' vertices and their orthogonal / 45° guide lines.
 *  It writes through the bench's adapter; the room is re-derived at the release
 *  (`endBenchEditDrag`), so a bench dragged past a partition is split there. */
function applyBenchEditDrag(world: Vec2): void {
  const be = drag.benchEdit;
  if (!be) return;
  const inst = state.drawing.instances.find((i) => i.id === be.instId);
  const b = inst ? roomBenchOf(inst, be.benchId) : null;
  const fr = inst && b?.host.kind === 'wall' ? polyBenchFrame(inst, be.benchId) : null;
  if (!inst || !b || !fr) return;
  const local = toInstanceLocal(inst, world);
  const thr = 12 / vp.scale;
  const verts = polyBenchGuideVerts(inst, be.benchId);
  const k = be.kind;
  const editsT = k === 'start' || k === 'end' || k.startsWith('corner');
  const editsDepth = k === 'inner' || k === 'corner-start-inner' || k === 'corner-end-inner';
  const isStart = k === 'start' || k === 'corner-start-outer' || k === 'corner-start-inner';
  const patch: BenchPatch = {};
  if (editsT) {
    const rawT = (local.x - fr.origin.x) * fr.dir.x + (local.y - fr.origin.y) * fr.dir.y;
    const t = Math.max(0, Math.min(fr.len, benchSnapAlong(fr.origin, fr.dir, rawT, verts, thr)));
    const s0 = b.leg.start ?? 0, e0 = b.leg.end ?? fr.len;
    if (isStart) patch.start = Math.min(t, e0 - 100);
    else patch.end = Math.max(t, s0 + 100);
  }
  if (editsDepth) {
    const rawOff = (local.x - fr.origin.x) * fr.normal.x + (local.y - fr.origin.y) * fr.normal.y;
    const off = benchSnapAlong(fr.origin, fr.normal, rawOff, verts, thr);
    // The inner handle is drawn slid wall-ward by the tuck; add it back so the
    // depth reads from the true (un-tucked) inner edge. Offsets run from the
    // wall's outer edge line.
    patch.depth = off - wallThicknessOf(inst) - fr.oOff + fr.shift;
  }
  b.write(patch);
  invalidate();
}

/** A bench handle's release: the room re-derived from the bench as dragged
 *  (`regenerateSauna`: split at a partition it now crosses), inside the drag's
 *  one undo step. */
function endBenchEditDrag(): void {
  const room = drag.benchEdit ? state.drawing.instances.find((i) => i.id === drag.benchEdit!.instId) : undefined;
  if (room) regenerateSauna(room);
  commitGesture();
  renderProperties();
}

/**
 * The clickable add-bench area under the cursor (bench tool hover): the footprint
 * the new bench would occupy, as local-frame polygons of the hovered room. Null
 * when a click there wouldn't add a bench (existing bench body, full stack, no
 * space, front wall).
 */
export type BenchHover = { instId: string; polys: Vec2[][]; kind: 'add' | 'delete' | 'pick'; shift?: Vec2 };

// ---- Room fixtures: the cove (the Cove tool) ------------------------------------

/** A room's interior partitions. Each has its id (polyRoom.ts `mintFeatureIds`:
 *  a load, a paste and every regeneration mint the missing ones), so reading
 *  them never writes the drawing. */
export function innerWallsOf(room: ProductInstance): InnerWall[] {
  return room.innerWalls || [];
}

/** The room's glazed panes, each with its id (the twin of `innerWallsOf`). */
export function glassPanesOf(room: ProductInstance): GlassSegment[] {
  return room.glass || [];
}

/** The length (mm) of the run a pane is glazed into: its polygon edge, or the
 *  inward length of the partition when it is glazed into one. `start`/`end` are
 *  measured along that run, so every gap figure in the panel reads from it. */
function glassRunLength(inst: ProductInstance, seg: GlassSegment): number {
  if (seg.innerWallId != null) {
    const iw = innerWallsOf(inst).find((w) => w.id === seg.innerWallId);
    return iw ? Math.max(0, iw.length) : 0;
  }
  return polyEdgeLengths(inst)[seg.edge ?? 0] ?? 0;
}

/** Where a room-wall pane / opening sits along its WALL, or null for one in a
 *  partition (or on a legacy rectangular room). */
export function paneOnWall(inst: ProductInstance, seg: GlassSegment): { w: number; s: number; t: number; len: number } | null {
  if (seg.innerWallId != null || seg.edge == null || !isPolyRoom(inst)) return null;
  const at = segToWall(inst, seg.edge);
  if (!at) return null;
  return { w: at.wall, s: at.offset + Math.min(seg.start, seg.end), t: at.offset + Math.max(seg.start, seg.end), len: wallRuns(inst)[at.wall].len };
}

/** Put a room-wall pane / opening at `[s, t]` along its wall — a direct edit (the
 *  panel's Width / Distance fields): the wall is re-laid around it, its neighbours
 *  clipped where it now overlaps them. */
function setPaneOnWall(inst: ProductInstance, seg: GlassSegment, s: number, t: number): void {
  const at = paneOnWall(inst, seg);
  if (!at) return;
  const feats = wallFeatures(inst, at.w).map((f) => (f.seg === seg ? { ...f, s, t } : f));
  // The edited one wins any overlap: lay it out first.
  const kept = clipAround(feats, seg);
  const gone = new Set(feats.filter((f) => !kept.some((k) => k.seg === f.seg)).map((f) => f.seg));
  if (gone.size) {
    inst.glass = inst.glass?.filter((g) => !gone.has(g));
    inst.openings = inst.openings?.filter((o) => !gone.has(o));
  }
  followVertices(state.drawing, inst.id, () => layoutWall(inst, at.w, kept));
}

/** A wall's features with `winner`'s span cut out of every other one (a neighbour
 *  it now overlaps gives way; one it swallows is dropped). */
function clipAround(feats: ReturnType<typeof wallFeatures>, winner: GlassSegment): ReturnType<typeof wallFeatures> {
  const win = feats.find((f) => f.seg === winner);
  if (!win) return feats;
  const out = [win];
  for (const f of feats) {
    if (f === win) continue;
    if (f.t <= win.s || f.s >= win.t) { out.push(f); continue; }
    if (f.s < win.s && win.s - f.s >= GLASS_MIN) out.push({ ...f, t: win.s });
    else if (f.t > win.t && f.t - win.t >= GLASS_MIN) out.push({ ...f, s: win.t });
  }
  return out;
}

/** The selected glazed pane, resolved against the selected instance — a pane id
 *  left over from another room simply resolves to nothing. */
export function selectedGlassPane(): { inst: ProductInstance; seg: GlassSegment } | null {
  const id = selGlassId();
  if (!id) return null;
  const inst = selectedInstance();
  if (!inst) return null;
  const seg = glassPanesOf(inst).find((g) => g.id === id);
  return seg ? { inst, seg } : null;
}

/** The selected panes' ids (the primary last). */
function selGlassIds(): string[] {
  return featureOf(state, 'glass')?.ids ?? [];
}

/** Every selected glazed pane, resolved against the selected room — stale ids from
 *  another room resolve to nothing, exactly like `selectedGlassPane`. */
export function selectedGlassPanes(): { inst: ProductInstance; seg: GlassSegment }[] {
  const inst = selectedInstance();
  if (!inst) return [];
  const ids = new Set(selGlassIds());
  return glassPanesOf(inst).filter((g) => g.id && ids.has(g.id)).map((seg) => ({ inst, seg }));
}

/** Openings of a room, each with its id — the twin of `glassPanesOf`. */
export function openingsOf(room: ProductInstance): GlassSegment[] {
  return room.openings || [];
}
/** The selected openings' ids (the primary last) — the twin of `selGlassIds`. */
function selOpeningIds(): string[] {
  return featureOf(state, 'opening')?.ids ?? [];
}
export function selectedOpening(): { inst: ProductInstance; seg: GlassSegment } | null {
  const id = selOpeningId();
  if (!id) return null;
  const inst = selectedInstance();
  const seg = inst ? openingsOf(inst).find((o) => o.id === id) : undefined;
  return seg ? { inst: inst!, seg } : null;
}
export function selectedOpenings(): { inst: ProductInstance; seg: GlassSegment }[] {
  const inst = selectedInstance();
  if (!inst) return [];
  const ids = new Set(selOpeningIds());
  return openingsOf(inst).filter((o) => o.id && ids.has(o.id)).map((seg) => ({ inst, seg }));
}

export function removeInnerWall(room: ProductInstance, wallId: string): void {
  room.innerWalls = innerWallsOf(room).filter((w) => w.id !== wallId);
  if (!room.innerWalls.length) delete room.innerWalls;
  if (featureOf(state, 'innerWall')?.wallId === wallId) dropFeature(state, 'innerWall');
  // Doors and glazed panes hosted on the removed partition go with it.
  if (room.doors) room.doors = room.doors.filter((d) => d.innerWallId !== wallId);
  if (room.glass) {
    room.glass = room.glass.filter((g) => g.innerWallId !== wallId);
    if (!room.glass.length) delete room.glass;
  }
}

/** Inner-wall tool hover, read from the press's target: the partition a press
 *  takes hold of highlights red — a click without a drag removes it, the same
 *  red-before-deletion cue the bench and heater-wall tools use. (A bare wall gets
 *  no preview: the new partition is dragged out.) */
function innerWallHoverAt(aim: ToolAim): BenchHover | null {
  const world = aim.world;
  const room = roomAt(roomsToEdit(), world);
  const t = room ? innerWallTargetIn(room, toInstanceLocal(room, world), wallReach(aim)) : null;
  if (!room || !t || !('move' in t)) return null;
  const band = polyInnerWallBand(room, innerWallsOf(room)[t.move]);
  return band.length >= 4 ? { instId: room.id, polys: [band], kind: 'delete' } : null;
}

/** How a new wall bench starts (its depth, a steam room's profile). */
const newTier: NewTier = (room, edge) => newPolyBench(room, '', edge);

/** What the Bench tool's hover shows, read from the press's own target
 *  (roomTargets.ts `benchTargetIn`): the corner join a click swaps (outlined), or
 *  the bench a click adds (green) or removes (red). */
function benchToolHoverAt(aim: ToolAim): { joint: RenderOptions['benchJoint']; hover: BenchHover | null } {
  const world = aim.world;
  const nj = nodeBenchJointAt(world);
  if (nj) return { joint: { instId: nj.inst.id, corner: { node: nj.node }, tier: 0 }, hover: null };
  const room = roomAt(roomsToEdit(), world);
  if (!room) return { joint: null, hover: null };
  const t = benchTargetIn(room, toInstanceLocal(room, world), wallReach(aim), newTier);
  if (!t) return { joint: null, hover: null };
  const shown = (polys: Vec2[][], kind: BenchHover['kind']): { joint: null; hover: BenchHover | null } => ({ joint: null, hover: polys.length ? { instId: room.id, polys, kind } : null });
  switch (t.kind) {
    case 'joint': return { joint: { instId: room.id, corner: t.corner, tier: t.tier }, hover: null };
    case 'partitionJoint': return { joint: { instId: room.id, corner: { iw: t.iwIndex, face: t.face }, tier: t.tier }, hover: null };
    case 'partitionBench': {
      const ghost = JSON.parse(JSON.stringify(room)) as ProductInstance;
      const id = addPolyInnerWallBench(ghost, t.iwIndex, t.face);
      const band = id ? polyInnerBenchBands(ghost).find((b) => b.id === id) : null;
      return shown(band ? [band.poly] : [], 'add');
    }
    case 'removeBench': return shown([t.poly], 'delete');
    case 'nextTier':
    case 'firstBench': return shown(nextTierPolys(room, t.edge, newTier, t.span), 'add');
    default: return unreachable(t, 'bench target');
  }
}

/** Topmost sauna (auto-bench) whose footprint contains the world point, or null. */
function saunaAt(world: Vec2): ProductInstance | null {
  for (let i = state.drawing.instances.length - 1; i >= 0; i--) {
    const inst = state.drawing.instances[i];
    if (!inst.autoBenches) continue;
    const p = toInstanceLocal(inst, world);
    if (isPolyRoom(inst)) { if (pointInPolygon(p, inst.polygon!)) return inst; continue; }
    if (Math.abs(p.x) <= inst.width / 2 && Math.abs(p.y) <= inst.depth / 2) return inst;
  }
  return null;
}

/**
 * Record (or clear) a steam cube's snap to a steam room: if the cube sits inside
 * a steam room with an edge against a wall, store its offset from the nearest
 * inner corner so it can follow that wall/corner when the room is resized.
 */
function updateCubeSnap(cube: ProductInstance): void {
  if (cube.category !== 'steamcube') return;
  const room = state.drawing.instances.find(
    (i) => i.autoBenches && i.category === 'steamroom' && !isPolyRoom(i) &&
      Math.abs(toInstanceLocal(i, { x: cube.x, y: cube.y }).x) <= i.width / 2 &&
      Math.abs(toInstanceLocal(i, { x: cube.x, y: cube.y }).y) <= i.depth / 2
  );
  if (!room) { cube.cubeSnap = undefined; return; }
  const wall = wallThicknessOf(room);
  const innerHw = room.width / 2 - wall, innerHd = room.depth / 2 - wall;
  const c = toInstanceLocal(room, { x: cube.x, y: cube.y });
  const chw = cube.width / 2, chd = cube.depth / 2;
  const gaps = [Math.abs((c.x - chw) - -innerHw), Math.abs(innerHw - (c.x + chw)), Math.abs((c.y - chd) - -innerHd), Math.abs(innerHd - (c.y + chd))];
  const TOL = 80;
  if (Math.min(...gaps) > TOL) { cube.cubeSnap = undefined; return; } // floating, not snapped
  const sx = c.x >= 0 ? 1 : -1, sy = c.y >= 0 ? 1 : -1;
  cube.cubeSnap = { roomId: room.id, sx, sy, ox: c.x - sx * innerHw, oy: c.y - sy * innerHd };
}

/** The room's outline vertices in its own local frame. Poly rooms use their
 *  polygon; a (legacy) rectangular room uses its four corners. */
function roomLocalVerts(room: ProductInstance): Vec2[] {
  if (isPolyRoom(room)) return room.polygon!;
  const hw = room.width / 2, hd = room.depth / 2;
  return [{ x: -hw, y: -hd }, { x: hw, y: -hd }, { x: hw, y: hd }, { x: -hw, y: hd }];
}

/** Record (or clear) an interior item's anchor to the NEAREST vertex of the room
 *  it sits in, storing a constant room-local offset from that vertex so it follows
 *  the corner when the room is resized. Called when the item is placed or moved. */
function updateRoomAnchor(item: ProductInstance): void {
  if (!traitsOf(item.category).carvesBenches) return; // heaters / steam cubes
  const room = saunaOfHeater(item) ?? saunaAt({ x: item.x, y: item.y });
  if (!room) { item.roomAnchor = undefined; return; } // not inside a room
  const verts = roomLocalVerts(room);
  if (!verts.length) { item.roomAnchor = undefined; return; }
  const local = toInstanceLocal(room, { x: item.x, y: item.y });
  let best = 0, bestD = Infinity;
  verts.forEach((vv, i) => { const d = (vv.x - local.x) ** 2 + (vv.y - local.y) ** 2; if (d < bestD) { bestD = d; best = i; } });
  item.roomAnchor = { roomId: room.id, v: best, dx: local.x - verts[best].x, dy: local.y - verts[best].y };
}

/** Re-place every item anchored to `room` after its size/shape changes: keep each
 *  at its stored offset from its (now-moved) anchor vertex, then clamp inside. */
function repositionAnchoredItems(room: ProductInstance): void {
  const verts = roomLocalVerts(room);
  if (!verts.length) return;
  for (const item of state.drawing.instances) {
    const a = item.roomAnchor;
    if (!a || a.roomId !== room.id) continue;
    // A part on a locked layer stays put; its anchor is re-read from the resized
    // room, so it does not jump at the next resize once unlocked.
    if (objLocked(item.id)) { updateRoomAnchor(item); continue; }
    const vv = verts[a.v] ?? verts[0];
    const w = instanceToWorld(room, { x: vv.x + a.dx, y: vv.y + a.dy });
    item.x = w.x; item.y = w.y;
    keepInRoom(item, room); // safety: never let it end up outside the interior
  }
}

/** Re-place steam cubes snapped to a room after its size changes. */
function repositionSnappedCubes(room: ProductInstance): void {
  const wall = wallThicknessOf(room);
  const innerHw = room.width / 2 - wall, innerHd = room.depth / 2 - wall;
  for (const cube of state.drawing.instances) {
    if (cube.category !== 'steamcube' || cube.cubeSnap?.roomId !== room.id) continue;
    if (objLocked(cube.id)) { updateCubeSnap(cube); continue; }
    const { sx, sy, ox, oy } = cube.cubeSnap;
    const w = instanceToWorld(room, { x: sx * innerHw + ox, y: sy * innerHd + oy });
    cube.x = w.x;
    cube.y = w.y;
  }
}

export function polyRoomEditInst(): ProductInstance | null {
  if (!state.polyRoomVertexEditId) return null;
  return state.drawing.instances.find((i) => i.id === state.polyRoomVertexEditId) ?? null;
}

/** Refuse a corner drag, and say why once — the message is only re-flashed when
 *  the reason changes, since the drag re-tests on every frame. `edge` names the
 *  wall whose figures don't fit; null means the reshape would slide the whole ring. */
function blockReshape(inst: ProductInstance, edge: number | null): void {
  if (drag.reshapeBlock?.instId !== inst.id || drag.reshapeBlock.edge !== edge) {
    flashToolStatus(edge == null
      ? tr('Clamped — dragging further would carry every corner of the room instead of resizing it.')
      : tr('Wall {n} is at its locked size — its door, glass and pinned figures don’t fit another length.').replace('{n}', String(edge + 1)));
  }
  drag.reshapeBlock = { instId: inst.id, edge };
}

/** World outline of the wall band a corner drag is being refused by. */
function blockedWallRing(): Vec2[] | null {
  if (!drag.reshapeBlock || drag.reshapeBlock.edge == null) return null;
  const inst = state.drawing.instances.find((i) => i.id === drag.reshapeBlock!.instId);
  const e = inst ? polyEdges(wallView(inst))[drag.reshapeBlock.edge] : null; // a WALL (roomReshapeBreak)
  return inst && e ? [e.a, e.b, e.ib, e.ia].map((p) => instanceToWorld(inst, p)) : null;
}

/** Edit a room's corners and walls: what a double-click on it, or Edit room
 *  on the Sauna toolbar, does. */
function enterPolyRoomVertexEdit(room: ProductInstance): void {
  toPlan();
  selectOne(state, 'instance', room.id);
  enterEdit({ kind: 'room', id: room.id });
  renderProperties();
  invalidate();
}

function exitPolyRoomVertexEdit(): void {
  leaveEdit('room');
  drag.polyRoomVertex = null;
  drag.reshapeBlock = null;
  renderProperties();
  invalidate();
}


/** The room a part stands in: the room under its centre, or — for a room's own
 *  drain / door channel — that room wherever it sits, and for a part through a
 *  wall the room its room side stands in. DERIVED, never stored: bench carving,
 *  a room's move / turn / copy and the connection choices all read it, so what
 *  is in a room is simply what stands in it. */
export function hostRoomOf(inst: ProductInstance): ProductInstance | null {
  if (inst.autoBenches) return null;
  if (inst.roomFixture) return state.drawing.instances.find((r) => r.id === inst.roomFixture!.roomId) ?? null;
  const band = throughWallBand(inst);
  return saunaAt(band ? instanceToWorld(inst, { x: 0, y: (band.y0 + band.y1) / 2 }) : { x: inst.x, y: inst.y });
}

/** Everything standing in a room, its fixtures included. */
export function roomContents(room: ProductInstance): ProductInstance[] {
  return state.drawing.instances.filter((i) => i !== room && hostRoomOf(i) === room);
}

/** THE writer of a room's top — its Height, its ceiling's shape and figures,
 *  its own Height from ground, in its panel, the multi-selection's, the plan's
 *  ceiling grips or a handle in 3D or a section: whatever lay on the room's top
 *  before `write` (mounting.ts `restsOnTop`: a junction box, a fibre projector)
 *  lies on it after, and what hung under its ceiling or stood on its walls a set
 *  way under it (a control unit's sensor: `hangsUnder`, `standsBelowCeiling`)
 *  does after, in the same undo step. Derived at the moment of the change, so
 *  nothing is stored: a part moved off the top, or given a height of its own,
 *  simply no longer follows. */
export function editRoomTop<T>(room: ProductInstance, write: () => T): T {
  if (!room.autoBenches) return write();
  const contents = roomContents(room);
  const resting = contents.filter((p) => restsOnTop(room, p));
  // What hangs under its ceiling, and stands on its walls a set way under it.
  const hanging = contents.filter((p) => mountOf(p)?.underCeiling && hangsUnder(room, p));
  const under = contents.flatMap((p) => {
    const below = mountOf(p)?.wallMountBelowCeiling;
    return below != null && standsBelowCeiling(room, p, below) ? [{ p, below }] : [];
  });
  const out = write();
  for (const p of resting) layOnTop(room, p);
  for (const p of hanging) hangUnder(room, p);
  for (const { p, below } of under) seatBelowCeiling(room, p, below);
  return out;
}

// ---- Steam-room auto fixtures (centre drain + door drain-channel) ----


/** Build a bare parametric fixture instance (drain / drain channel) from its builtin template. */
function makeFixtureInstance(templateId: string, kind: 'centerDrain' | 'doorChannel', room: ProductInstance): ProductInstance {
  const t = state.templates.find((x) => x.id === templateId) ?? BUILTIN_PRODUCTS.find((x) => x.id === templateId)!;
  // Kept in step with its room, it starts bare: no rough-ins, no connection points.
  return {
    ...placeCopy(t, prepId), id: newId('inst'), x: room.x, y: room.y, rotation: room.rotation,
    roomFixture: { roomId: room.id, kind }, preparations: [], ports: undefined,
  };
}

/** Give a newly placed steam room its centre drain and a door drain-channel. */
function addSteamRoomFixtures(room: ProductInstance): void {
  if (room.category !== 'steamroom') return;
  if (!roomFixtureOf(state.drawing, room, 'centerDrain')) state.drawing.instances.push(makeFixtureInstance('builtin-drain', 'centerDrain', room));
  if (mainRoomDoor(room) && !roomFixtureOf(state.drawing, room, 'doorChannel')) state.drawing.instances.push(makeFixtureInstance('builtin-canalina', 'doorChannel', room));
  syncRoomFixtures(state.drawing, room);
}



/** World-space axis-aligned bounding box of an instance's (rotated) footprint. */
function instanceAABB(inst: ProductInstance): { x0: number; y0: number; x1: number; y1: number } {
  const cs = instanceOutline(inst);
  const xs = cs.map((c) => c.x), ys = cs.map((c) => c.y);
  return { x0: Math.min(...xs), y0: Math.min(...ys), x1: Math.max(...xs), y1: Math.max(...ys) };
}

/**
 * The topmost other (non-room) part whose footprint OVERLAPS `inst`'s footprint.
 * Overlap (not centre-in-target) so that dragging a part visually onto another
 * relates them even when it's grabbed off-centre — the dragged centre needn't sit
 * inside the target.
 */
function partUnder(inst: ProductInstance): string | null {
  const a = instanceAABB(inst);
  for (let i = state.drawing.instances.length - 1; i >= 0; i--) {
    const t = state.drawing.instances[i];
    if (t.id === inst.id || t.autoBenches || objUnpickable(t.id)) continue; // not itself, not a room, nothing hidden / locked
    if (inst.relId && t.relId === inst.relId) continue; // already related
    if (inst.groupId && t.groupId === inst.groupId) continue; // its own group
    const b = instanceAABB(t);
    if (a.x0 < b.x1 && a.x1 > b.x0 && a.y0 < b.y1 && a.y1 > b.y0) return t.id;
  }
  return null;
}

/** "Relate to <name>", beside the part a drag would relate to on release. */
function dropTargetLabel(): string | undefined {
  const t = drag.dropTargetId ? state.drawing.instances.find((i) => i.id === drag.dropTargetId) : null;
  return t ? tr('Relate to {name}').replace('{name}', numberedInstanceNames().get(t.id) ?? t.name) : undefined;
}

/** The parts related to what is selected (and not selected themselves). */
function relatedToSelection(): string[] {
  if (activeSheet()) return [];
  const sel = new Set(selectedOf(state, 'instance'));
  const out = new Set<string>();
  for (const id of sel) {
    const inst = state.drawing.instances.find((i) => i.id === id);
    if (inst) for (const r of relatedTo(state.drawing.instances, inst)) if (!sel.has(r.id)) out.add(r.id);
  }
  return [...out];
}

/** Take parts out of their relations (right-click ▸ Remove relation). */
export function unrelateParts(insts: ProductInstance[]): void {
  pushHistory();
  unrelate(state.drawing.instances, new Set(insts.map((i) => i.id)));
  renderProperties();
  invalidate();
}


/** Relate two parts (and whatever each is already related to). */
function relateParts(a: ProductInstance, b: ProductInstance): void {
  relate(state.drawing.instances, a, b, () => newId('rel'));
}

/** Topmost room bench under a world point: its room id and bench id, or null. */
function benchHitTest(world: Vec2): { instId: string; benchId: string } | null {
  for (const inst of roomsToEdit()) {
    if (!isPolyRoom(inst)) continue;
    const local = toInstanceLocal(inst, world);
    // A partition's bench first: it is painted over the walls' benches.
    const ib = partitionBenchAt(inst, local);
    if (ib) return { instId: inst.id, benchId: ib.id };
    const pick = polyBenchPick(inst, local);
    if (pick) return { instId: inst.id, benchId: pick.band.id };
  }
  return null;
}

/** Distance along a partition's inward length for a local point (clamped). */
function innerWallAlong(inst: ProductInstance, iwId: string, local: Vec2): number {
  const iw = innerWallsOf(inst).find((w) => w.id === iwId);
  if (!iw) return 0;
  const m = polyInnerWallMetrics(inst, iw.edge ?? -1, local);
  return Math.max(0, Math.min(Math.max(0, iw.length), m?.depth ?? 0));
}

/** Place a new door under the cursor: on an interior partition if one is hit,
 *  otherwise on the nearest outer wall. Pushes history and returns the room+door,
 *  or null when there is no room wall under the cursor. */
function addDoorAt(world: Vec2, reach: number): { inst: ProductInstance; door: SaunaDoor } | null {
  const d = doorToAdd(roomsToEdit(), world, reach);
  if (!d) return null;
  pushHistory();
  if (d.partition && !d.partition.id) { d.partition.id = newId('iw'); d.door.innerWallId = d.partition.id; }
  roomDoors(d.room).push(d.door);
  return { inst: d.room, door: d.door };
}


/** Live preview of the glass segment being dragged. */
function glassPreview(): { instId: string; seg: GlassSegment } | null {
  if (!drag.glass) return null;
  return { instId: drag.glass.instId, seg: { wall: drag.glass.wall, start: drag.glass.start, end: drag.glass.end, edge: drag.glass.edge, innerWallId: drag.glass.innerWallId } };
}

/** Live preview of the opening segment being dragged. */
function openingPreview(): { instId: string; seg: GlassSegment } | null {
  if (!drag.opening) return null;
  return { instId: drag.opening.instId, seg: { wall: drag.opening.wall, start: drag.opening.start, end: drag.opening.end, edge: drag.opening.edge } };
}

/** Live preview of the interior wall being dragged. */
function innerWallPreview(): { instId: string; iw: InnerWall } | null {
  if (!drag.innerWall || drag.innerWall.length < 1) return null;
  return { instId: drag.innerWall.instId, iw: { wall: drag.innerWall.wall, edge: drag.innerWall.edge, pos: drag.innerWall.pos, length: drag.innerWall.length } };
}

/** The full inward span a wall's interior wall can reach (to the opposite wall). */
function innerWallSpan(inst: ProductInstance, wall: WallSide): number {
  return wall === 'front' || wall === 'back' ? inst.depth : inst.width;
}

/** Topmost inner wall of a legacy rectangular room under a world point: room id
 *  + its index, or null. */
function innerWallHitTest(world: Vec2): { instId: string; index: number } | null {
  for (const inst of roomsToEdit()) {
    if (isPolyRoom(inst) || !inst.innerWalls?.length) continue;
    const idx = innerWallHitLocal(inst, toInstanceLocal(inst, world), 0);
    if (idx >= 0) return { instId: inst.id, index: idx };
  }
  return null;
}

/** Add a bench tier onto one face of a polygonal room's interior partition wall,
 *  returning the new bench id (or '' when the face is full). Mirrors the
 *  rectangular `addInnerWallBench`, but the run is the wall's inward length. */
function addPolyInnerWallBench(inst: ProductInstance, iwIndex: number, face: 'a' | 'b'): string {
  const iw = inst.innerWalls?.[iwIndex];
  if (!iw) return '';
  const existing = iw.benches?.[face] ?? [];
  if (existing.length >= MAX_BENCH_TIERS) return '';
  const def = inst.category === 'steamroom' ? 500 : 600;
  const depth = Math.max(MIN_BENCH_DEPTH, Math.min(def, polyBenchDepth(inst)));
  if (!iw.id) iw.id = newId('iw');
  if (!iw.benches) iw.benches = {};
  const id = newId('ib');
  const leg: ProductBenchLeg = { id, depth, start: 0, end: Math.max(0, iw.length) };
  const preset = inst.category === 'steamroom' ? defaultSteamPreset() : null;
  if (preset) applyBenchProfile(leg, preset);
  iw.benches[face] = [...existing, leg];
  return id;
}

export function screenPos(e: MouseEvent): Vec2 {
  // Screen space is CSS pixels (the render applies a devicePixelRatio transform).
  const rect = canvas.getBoundingClientRect();
  return { x: e.clientX - rect.left, y: e.clientY - rect.top };
}

canvas.addEventListener('mousedown', (e) => {
  cancelZoomAnim();
  pressFinishedDraft = false;
  if (constraintUi.constraintClashIds.size) { constraintUi.constraintClashIds = new Set(); invalidate(); } // a refusal's lit culprits last until the next press
  if (dragHeld && !moveHolds) dragHeld = null; // a move that ended off the canvas
  drawConstrain = e.shiftKey;
  // An armed pick takes the next click, before anything else does.
  if (!spaceHeld && downArmedPick(e, screenPos(e), !!activeSheet())) return;
  if (activeSheet()) { sheetMouseDown(e); return; }
  const sp = screenPos(e);
  lastScreen = sp;
  notePress(sp);
  const world = vp.screenToWorld(sp);
  // What is under way: a click drops an armed handle or grip there (a press while
  // a section handle is held does nothing).
  if (e.button === 0 && !spaceHeld && pressUnderWay({ space: 'plan', at: sp, alt: e.altKey })) return;
  // Alt+click in Select cycles to the next object under the cursor — decided on
  // release (see the mouseup below), so an Alt+DRAG still just moves unsnapped.
  cycleProbe = state.tool === 'select' && e.button === 0 && e.altKey ? { sp, world, prev: selectedRefs() } : null;
  // Space held: a left drag pans, whatever the tool and whatever is under it.
  if (downSpacePan(e)) return;

  // If the press lands on a vertex under the cursor (the snap symbol was showing
  // there), remember it: whichever move drag this mousedown starts will snap THAT
  // vertex to the grid and to other objects. Reset every press so a press away from
  // any vertex (or a non-move gesture) falls back to the ordinary origin snap.
  drag.grabbedVertexStart = state.tool === 'select' && e.button === 0
    ? nearestVertexWithin(world, dragGrabVerts(world), SNAP_PX / vp.scale)
    : null;

  // Offset, Trim, Fillet and Constrain work on the plan, not in a view.
  if (downPlanOnlyTool(e, world)) return;
  // Constrain: pick a piece, then another; the first holds, the second moves.
  if (state.tool === 'constrain' && e.button === 0) { constrainClick(e, world); return; }
  // The selection's handle under the press, asked ONCE (`handleAt`, handleHits.ts):
  // each kind's press below takes it at its place in HANDLE_ORDER — the order the
  // cursor and the hover read it in too.
  const handle = e.button === 0 ? handleAt(sp, world) : null;
  // The selected room's handles in a section view, drawn over everything, take
  // the press.
  if (pressSectionHandle(e, sp, handleOf(handle, 'viewHandle'))) { cycleProbe = null; return; }
  // A constraint badge: selects its constraint (ahead of whatever lies under it).
  if (state.tool === 'select' && e.button === 0 && !e.altKey) {
    const cid = badgeAt(constraintBadgeBoxes, sp);
    if (cid) { pickConstraint(cid); return; }
  }

  // Measure tool: collect two points, read out distance/angle (no object added).
  if (downMeasureTool(e, world)) return;

  // Shift/Ctrl-click a glazed pane of the ALREADY-SELECTED room toggles it into the
  // pane multi-selection (edit several panes together / make them equal). Checked
  // before the generic multi-select so a Shift-click on a pane picks the pane, not
  // the whole room.
  if (downModifierToggle(e, world)) return;
  // Shift/Ctrl-click another light of the selected light's room picks it with it.
  if (downLightToggle(e, world)) return;
  // A press on a selection grip (a leader's / line's vertex, an arc's end or
  // middle) drags that grip — before any selection logic, Shift included; a
  // release that did not move arms it (see GripWork).
  if (downGrip(e, sp, handleOf(handle, 'grip')?.grip ?? null)) return;
  // A ceiling's fall arrow, dragged onto a wall, turns it — ahead of the group
  // click and the Shift toggle, like a grip.
  if (downCeilingArrow(handleOf(handle, 'ceilingArrow'), sp)) return;
  // A press on a connection's schedule tag selects that connection and slides the
  // tag along its route (the tag is drawn over everything, so it takes the press).
  const tagId = e.button === 0 && state.tool === 'select' ? pickableConnTagAt(sp) : null;
  if (downConnTag(tagId)) return;
  // A view's title ("A–A", a ceiling plan's): a press selects it, a drag moves it.
  if (downViewTitle(e, sp, world)) return;
  // A light's tag: a press selects its light, a drag moves the tag.
  if (downLightTag(e, sp, world)) return;
  // Shift+click (select tool) toggles whatever object is under the cursor into the
  // multi-selection — any kind, mixed freely — rather than panning. A press on
  // one of the selection's handles that drags (a knob, a resize handle, a jamb, a
  // section's end…) is that handle's, Shift or not: Shift there means "15° steps"
  // / "keep proportions", so neither the group click nor the toggle takes it.
  const onHandle = handleTakesPress(handle);
  // A group's member: a Shift-click adds or removes the whole (outermost) group;
  // a plain click selects the whole group (and drags it), unless one of its
  // groups is what is selected — then a click without a drag drills one level
  // in (mouseup).
  groupDrill = null;
  if (downGroupClick(e, sp, world, onHandle)) return;
  if (downSectionShift(e, world, onHandle)) return;
  if (downShiftMarquee(e, world, onHandle)) return;
  // Middle- and right-drag pan (and Space-drag, above); Shift is a modifier.
  if (e.button === 1 || e.button === 2) {
    startDrag('pan');
    return;
  }

  // A left-press on any object already part of a 2+ multi-selection drags the
  // whole selection together, whatever mix of kinds it contains.
  if (downMoveMultiSelection(e, world)) return;

  // An underlay being edited: its knob turns it, its handles resize it.
  if (downUnderlayKnob(world, handleOf(handle, 'underlayKnob'))) return;
  if (downUnderlayResize(handleOf(handle, 'underlayResize'))) return;

  // What is edited in place takes a press on itself — a room's or a shape's
  // corner or body, the underlay, the imported plan, a part's drawing; a press
  // away from it may leave the mode and goes on down (EDIT_LIFE).
  if (downEditMode(sp, world)) return;

  // Dimension tool: click a start point, an end point (both snapped), then a
  // third point to set the offset at which the dimension line is drawn.
  if (downDimensionTool(world)) return;

  // Section tool: click two main points, then a third click to set the view
  // direction + the perpendicular offset where the section is drawn.
  if (downSectionTool(e, world)) return;

  // Reflected ceiling plan tool: one click places the selected room's view.
  if (downRcpTool(e, world)) return;

  // PDF-scale tool: pick two points on the PDF, then enter the real distance.
  if (downPdfScaleTool(e, world)) return;

  // Polygon tool: each click adds a vertex; click the first vertex (or
  // double-click / Enter) to finish.
  if (downPolygonTool(e, world)) return;

  // Flooring tools: draw the outline exactly like the polygon tool; the finished
  // shape becomes a Flooring of the active tool's kind (below).
  if (downFloorTool(e, world)) return;

  // Revision-cloud tool: each click adds a vertex; click the first vertex (or
  // double-click / Enter) to finish. Same snapping as the polygon tool.
  if (downRevisionCloudTool(e, world)) return;

  // Circle/ellipse tool: 1st click sets the centre, 2nd the radius (Shift on the
  // 2nd click starts an ellipse — a 3rd click sets its second radius). 1mm precision.
  if (downCircleTool(e, world)) return;

  // Polygonal-room tool: each click adds an outer wall vertex; click the first
  // vertex (or double-click / Enter / right-click) to close and build the room.
  if (downPolyRoomTool(e, world)) return;

  // Rectangle-room tool: two clicks (opposite corners) → a 4-vertex polygon room.
  if (downRectRoomTool(e, world)) return;

  // Wall tool: each click adds a vertex to the centre polyline.
  if (downWallTool(e, world)) return;

  // Repeat tool: each click adds a vertex to the baseline polyline. Copies are
  // tiled along it; right-click / double-click / Enter commits.
  if (downRepeatTool(e, world)) return;

  // Bench-poly tool: each click adds a vertex to the in-progress polyline, unless
  // it would sit closer than the bench depth to an existing vertex (rejected).
  if (downBenchPolyTool(e, world)) return;

  // Line tool: each click adds a vertex, snapped via the shared draft snapper
  // (H/V alignment + 45°). Right-click / Enter finishes the polyline.
  if (downLineTool(e, world)) return;

  // ZOOM: two corners of a window, which then fills the view.
  if (downZoomWindowTool(world)) return;

  // Move / Copy / Rotate: the first click is the base point, the second the
  // destination (or, for Rotate, the angle).
  if (downPickTool(e, world)) return;

  // Mirror tool: two clicks pick the mirror line; the selection is mirrored across
  // it (as a copy unless "Erase source" was chosen) and the tool is done.
  if (downMirrorTool(e, world)) return;

  // Offset tool: first click picks the source line/polyline/polygon; the second
  // drops a parallel copy on the cursor's side, and the tool is done.
  if (downOffsetTool(world)) return;

  // Trim/Extend and Fillet/Chamfer both pick two objects, then act on them.
  if (state.tool === 'trim' || state.tool === 'fillet') {
    handleModifyClick(state.tool, world);
    return;
  }

  // Rectangle tool: click one corner, then the opposite corner.
  if (downRectangleTool(e, world)) return;

  // Arc tool: three clicks. 3-point = start, through, end; SCE = centre, start, end.
  if (downArcTool(world)) return;

  // Leader tool: click the arrow tip, then bend points; a click near the last
  // point (or Enter / right-click) finishes and opens the label for editing.
  if (downLeaderTool(e, world)) return;


  // Text tool: click two corners for the box, then an optional third point for
  // a leader arrow. The third click commits and opens the inline editor.
  if (downTextTool(world)) return;

  // Print-window tool: click two corners to define the region to print.
  if (downPrintWindowTool(world)) return;


  // The room tools' aim: the plan's point, its zoom's px, Alt.
  const aim = planAim(world, vp.scale, sp, e.altKey);
  // Door tool: click a wall (outer or inner partition) to add a door there.
  if (downDoorTool(aim)) return;

  // Glass tool: drag along a sauna wall to mark a glazed segment.
  if (downGlassTool(aim)) return;

  // Opening tool: drag along a wall to mark an open (glass-less) segment.
  if (downOpeningTool(aim)) return;

  // Inner-wall tool: grabbing an existing inner wall slides it along its host
  // wall (a no-move click removes it); on a bare wall it draws a new one.
  if (downInnerWallTool(aim)) return;

  // Border/vertex handles of the selected bench: grabbing one starts a stretch /
  // vertex-drag edit (works with both the select and bench tools).
  if (downSelectedBenchHandles(handleOf(handle, 'benchHandle'))) return;

  // Bench support tool: a press on a placed support removes it (it lit red); on a
  // slatted bench it places one across every tier there (green) — or, dragged
  // along the bench, a row, placed on release.
  if (downBenchSupportTool(e, sp, aim)) return;

  // Bench end tool: a click on a bench's free end closes it (green), on a closed
  // one opens it (red).
  if (downBenchEndTool(e, aim)) return;
  if (downBenchRoundTool(e, aim)) return;

  // Hidden heater wall tool: click a bare room wall to add a false wall set back
  // by an air gap; click an existing one to remove it. Mirrors the bench tool's
  // hover-the-target-then-click flow.
  if (downHeaterWallTool(e, aim)) return;

  // Backrest tool: click the face of a wall, a heater wall or a partition to add
  // a backrest over its high bench; click one to remove it.
  if (downBackrestTool(e, aim)) return;
  if (downSculptBenchTool(e, aim)) return;

  // Floor grille tool: click a sauna's floor to lay a grille over it; click one
  // that has one to take it away.
  if (downFloorGrilleTool(e, aim)) return;

  // Strip tool: click a bench or a backrest to run a strip light along it (in the
  // position typed, or the one the cursor is nearest); click one to remove it.
  if (downStripTool(e, aim)) return;

  // A fibre kit just laid (a starry sky, a fibre point): the click places its
  // projector, above the ceiling or outside the room.
  // With the Starry sky tool armed, its sky's margin grips take the press.
  if (downSkyMarginGrip(e, sp)) return;
  if (downKitStep(e, aim)) return;

  // Spot tool: click a room's ceiling to put a spot (or a fibre end) there; click
  // one to remove it.
  if (downSpotTool(e, sp, aim)) return;

  // Starry sky tool: click a room's ceiling to scatter a kit's fibres over it (or
  // draw its zone); click a sky to remove it.
  if (downSkyTool(e, aim)) return;

  // Channel tool: draw a channel click by click, or lay one along a wall, down the
  // middle or in runs; click one to remove it.
  if (downChannelTool(e, world)) return;

  // Cove tool: click a wall to run the room's cove along it; click one of its
  // walls again to take it out.
  if (downCoveTool(e, aim)) return;

  // Bench tool: click a bare edge to add a full-edge bench (mitred against its
  // neighbours); click the empty strip just inward of a bench to stack the next
  // tier there; click a bench to delete it; click a joint to step it on.
  if (downBenchTool(aim)) return;

  // Connect: click the start, click any bends, click the end — each end glued to
  // what is under it (a snapped point, an object's centre) or a free point. A
  // drag from one object to another does it in one go (see mouseup).
  if (downConnectionTool(e, world)) return;

  // Select: a press starts with no connection selected — the connection pick
  // below takes one back (a stale id would keep its line highlighted under
  // whatever else the press selects).
  if (state.tool === 'select' && e.button === 0) { state.selectedConnId = null; state.selectedRcpId = null; }

  // Door jamb handle: drag to resize the door width (selected sauna).
  if (downDoorJamb(handleOf(handle, 'doorJamb'))) return;

  // Flip icons on the selected door: hinge left/right, plus (on a partition, where
  // both sides are interior) the push/pull side the leaf swings to.
  if (downSelectedDoor(handleOf(handle, 'doorFlip'))) return;

  // Rotation knob of the currently selected instance.
  if (downRotateKnob(handleOf(handle, 'instKnob'))) return;

  // Rotation knob of the currently selected polygon/wall.
  if (downShapeRotateKnob(world, handleOf(handle, 'shapeKnob'))) return;

  // Resize handle of the currently selected instance takes priority — except a
  // MID-EDGE handle sitting over one of the room's own doors, panes or openings
  // (no hit there, `handleYieldsToFeature`). They are centred on their wall by
  // default, so the mid-edge handle lands squarely on them and would make them
  // unpickable the moment their room is selected. Corner handles still win, and
  // the other walls' mid-edge handles are untouched.
  if (downResizeHandle(handleOf(handle, 'instResize'))) return;
  if (downNodeHandle(handleOf(handle, 'nodeHandle'))) return;

  // Guard-cover side of the selected heater under the cursor: build it (blue gap
  // edge) or clear it (red cover band). After the resize handles, so a corner grab
  // still resizes the heater rather than reaching the cover band underneath it.
  if (downHeaterCover(handleOf(handle, 'heaterCover'))) return;

  // Mid-edge click on the already-selected polygon selects that edge (corners and
  // the rest of the bbox handles still resize; edge mid-points defer to this).
  if (downPolyEdge(handleOf(handle, 'polyEdge'))) return;

  // Bounding-box resize handle of the currently selected polygon.
  if (downShapeHandle(handleOf(handle, 'shapeResize'))) return;

  // A selected revision cloud gets the same handles as a polygon: its rotation
  // knob and bounding-box resize handles (grabbed here, applied in mousemove).
  if (downCloudKnob(world, handleOf(handle, 'cloudKnob'))) return;
  if (downCloudHandle(handleOf(handle, 'cloudResize'))) return;

  // The selected schedule table's width handles.
  if (downTableHandle(world, handleOf(handle, 'tableResize'))) return;

  // The selected text box's rotate knob, its resize handles and its leader's tip
  // (the tip drags where the arrow points).
  if (downTextKnob(handleOf(handle, 'textKnob'))) return;
  if (downTextHandle(handleOf(handle, 'textResize'))) return;
  if (downTextLeaderHandle(handleOf(handle, 'textLeader'))) return;

  // The selected section's cut-line ends reshape the cut; its rotate handle spins
  // the generated drawing about its baseline start (sets viewAngle).
  if (downSectionEnd(handleOf(handle, 'sectionEnd'))) return;
  if (downSectionKnob(world, handleOf(handle, 'sectionKnob'))) return;

  // The rotate knob of the selected floor's drain.
  if (downDrainKnob(handleOf(handle, 'drainKnob'))) return;

  // A click on the selected shape's temporary face dimension keeps the selection
  // (so it can be double-clicked to edit) and otherwise does nothing — the same
  // for a drawn polygon, a poly-room or a rectangular-footprint object.
  // Only a click on a temporary dimension's FIGURE counts — the dimension line
  // itself runs the length of the edge, and holding the selection for it would
  // make the whole band around an object unclickable. (A corner's R leader has
  // its own hit test, checked here too so its label keeps the selection.)
  const dimProbe = dimLabelProbeAt(planHits, sp);
  const onDim = !!dimProbe || !!cornerRadiusDimAt(sp);
  const fdSel = onDim ? faceDimTarget() : null;
  if (fdSel && faceDimEdgeAt(fdSel, sp, dimProbe ?? world).edge >= 0) { dropDrag(); return; }
  // …and so does a press on a selected support's figure.
  if (supportFiguresShown[supportFigureAt(planHits, sp)]?.edit) { dropDrag(); return; }
  // A press on a selected spot's figure opens it for a typed distance.
  if (downSpotFigure(sp)) return;

  // From here a press takes what is DRAWN ON TOP under it — the head of the
  // layer stacking (`objectStackAt`), never a fixed order of kinds — so each
  // branch below fires only for its own kind at the head. A room's features
  // (doors, panes, heater / inner walls) are reached only when nothing painted
  // above the room is under the press. Hidden layers' objects are never taken,
  // and a locked one on top takes the press: nothing (the head is none).
  const stackHere = objectStackAt(world, sp), pickStack = stackHere.seen;
  state.selectedWallFeatureId = null; // re-picked below when the press lands on one
  const pickHead = stackHere.head;
  const headOf = (kind: ObjRef['kind']): string | null => (pickHead && pickHead.kind === kind ? pickHead.id : null);

  // In-drawing connection-schedule table (an annotation on top): the body drags
  // to move it (its width handles were tried above). Clicking anything else
  // below clears the table selection.
  const tblBody = scheduleTableBodyAt(sp);
  if (downTableBody(world, pickStack, tblBody)) return;
  if (state.selectedScheduleTable) { state.selectedScheduleTable = null; renderProperties(); invalidate(); }

  // Steam-room feature cube (the internal `cubePos`, not a placed steamcube
  // product): drag to move it within the room; benches adapt.
  const featId = featureHitTest(world);
  if (downRoomFeaturePart(pickStack, featId)) return;

  // A bench support of the SELECTED room or sauna bench: a click selects it, a
  // drag slides it along its bench — the room rule, as for a pane: select the
  // room first, so a click on a bench still reaches the bench.
  const supHost = selectedInstance();
  const supHit = supHost && !supHost.frozen && !e.shiftKey && !e.ctrlKey && !e.metaKey && (supHost.benchSupports?.length ?? 0) > 0
    && roomReachable(pickStack, supHost.id) ? supportTargetAt(world, supHost, 1 / vp.scale) : null;
  if (downSupportMark(sp, supHost, supHit)) return;

  // Hidden heater wall: click selects it so its own properties (air gap, height,
  // height from ground, thickness, colour) can be edited.
  const hwHit = heaterWallAt(roomsToEdit(), world);
  if (downHeaterWall(pickStack, hwHit)) return;

  // Interior partition: click selects it so its own properties (thickness,
  // colour, height, height from ground) can be edited, and Delete removes it.
  const iwHit = partitionAt(roomsToEdit(), world);
  if (downInnerWall(pickStack, iwHit)) return;

  // Joint of a wall's dimension chain on the selected room: drag it to resize the
  // pane (or door) and the solid run either side of it at once. Checked before the
  // pane pick below, since a joint IS a pane's own edge and is the narrower target.
  const jointHit = wallSpanJointAt(sp);
  if (downWallSpanJoint(pickStack, jointHit)) return;

  // Glazed pane of the ALREADY-SELECTED room: click sub-selects it, so its own
  // colour and size can be edited. Scoped to the selected room on purpose — a
  // pane sits on a wall, and picking one out of an unselected room would mean a
  // click on that wall could never select the room itself.
  const selRoom = selectedInstance();
  const selRoomLive = !!selRoom && !selRoom.frozen && roomReachable(pickStack, selRoom.id);
  if (downRoomPane(world, selRoom, selRoomLive)) return;
  // An opening of the selected room: click sub-selects it, the twin of the pane pick.
  if (downRoomOpening(world, selRoom, selRoomLive)) return;

  // Room door: click selects the door (its own properties); drag repositions it
  // along its wall, a click-without-drag flips the hinge.
  const doorHit = doorHitTest(world);
  if (downRoomDoor(pickStack, doorHit)) return;

  // A door, pane or opening of the SELECTED drawn wall: a click sub-selects it and
  // a drag slides it along its segment — the room rule: select the wall first, so
  // a click on a wall's doorway still reaches the wall.
  const selBand = selectedBandWall();
  const wfHit = selBand && !e.shiftKey && !e.ctrlKey && !e.metaKey ? wallFeatureAt(selBand, world, 4 / vp.scale) : null;
  if (downWallFeature(world, selBand, wfHit)) return;

  // Text comment (annotations sit on top): click to select + drag to move (the
  // selected box's knob and handles were tried above).
  const headText = headOf('text');
  const tc = headText ? (state.drawing.texts || []).find((t) => t.id === headText) ?? null : null;
  if (downTextBox(world, tc)) return;

  // A part or heater standing in a room paints over it (it nests above its
  // room), so it heads the stack over the bench and the room underneath. The
  // room itself yields to its bench (a room is moved only from its walls).
  const headInst = headOf('instance');
  const hit = headInst ?? (pickHead && ['bench', 'fixture', 'heaterWall', 'innerWall', 'support'].includes(pickHead.kind) ? pickHead.instId ?? null : null);
  const nonRoomHit = headInst && !state.drawing.instances.find((i) => i.id === headInst)?.autoBenches ? headInst : null;
  // Clicking an instance that's already part of a multi-selection (no shift)
  // drags the whole group together.
  const multiCandidate = nonRoomHit ?? hit;
  if (multiCandidate && state.selectedIds.length > 1 && state.selectedIds.includes(multiCandidate)) {
    beginUnifiedMove(world, multiCandidate);
    return;
  }
  if (nonRoomHit) {
    selectInstanceForMove(nonRoomHit, world);
    return;
  }

  // When the room under the cursor is the already-selected instance and it is
  // small on screen (zoomed out), a press anywhere over its body moves it —
  // otherwise the only grab region is the thin wall strip and a bench band would
  // steal the click. Zoomed in it is large, so this is skipped and benches stay
  // individually selectable. Handles / doors / on-top parts were tested above and
  // keep priority.
  if (downSelectedRoomBody(world, hit)) return;

  // A fibre of the selected starry sky drags on its own.
  if (downSkyFibre(world, pickHead)) return;

  // A spot or a fibre end: a press selects it, a drag moves it.
  if (downSpot(e, sp, world, pickHead)) return;
  // A channel: a press selects it, a drag moves it.
  if (downChannel(e, sp, world, pickHead)) return;

  // A backrest selects individually, as the bench under it does.
  if (downFixture(pickHead)) return;

  // A bench inside a room selects individually (room moves only from its walls).
  const benchHit = pickHead?.kind === 'bench' ? { instId: pickHead.instId!, benchId: pickHead.id } : null;
  if (downBench(benchHit)) return;

  // The room (or any other instance) under the cursor.
  if (headInst) {
    selectInstanceForMove(headInst, world);
    return;
  }

  const dimHit = headOf('dim');
  if (downDimension(world, dimHit)) return;

  // A connection line (5 px): click selects it — its panel, Delete, its grips.
  const connHit = headOf('conn');
  if (downConnection(connHit)) return;

  // Click inside a section drawing's box to select it and drag the drawing
  // (independently of the cut line; sets viewOrigin). A selected section's ends
  // and rotate handle were tried above.
  const headSec = headOf('section');
  const secAtHead = headSec ? (state.drawing.sections || []).find((s) => s.id === headSec) ?? null : null;
  const ctx = hitCtx();
  const viewHit = secAtHead && inSectionView(secAtHead, world, ctx) ? secAtHead : null;
  if (downSectionView(e, sp, world, viewHit)) return;
  // Click near a section line to select it and drag it (the section drawing
  // regenerates live as the line moves); Delete removes it.
  const secHit = secAtHead && onSectionLine(secAtHead, world, ctx) ? secAtHead : null;
  if (downSectionLine(world, secHit)) return;
  state.selectedSectionId = null; // clicking elsewhere deselects the section
  state.sectionDrill = null;

  // A reflected ceiling plan: a press inside it selects it and drags it.
  // A spot in a reflected ceiling plan drags there; anywhere else the view moves.
  if (downRcpSpot(e, sp, world, headOf('rcp'))) return;
  if (downRcpView(world, headOf('rcp'), e)) return;
  state.selectedRcpId = null;

  // Flooring: when a floor is selected, its drain grabs first (moved, confined to
  // the polygon; its rotate knob was tried above). Then a click on any floor's
  // area selects that floor and drags the whole floor.
  if (downFloorDrain(world, selectedFloor())) return;
  const headFloor = headOf('flooring');
  const floorHit = headFloor ? (state.drawing.floorings || []).find((f) => f.id === headFloor) ?? null : null;
  if (downFloor(world, floorHit)) return;
  state.selectedFloorId = null; state.selectedFloorDrain = false; state.floorDrainDims = [];

  // Click a polygon's fill to select it; dragging then moves it.
  const headPoly = headOf('polygon');
  const poly = headPoly ? (state.drawing.polygons || []).find((q) => q.id === headPoly) ?? null : null;
  if (downPolygon(world, poly)) return;

  // Click a wall band to select it; dragging then moves it.
  const headWall = headOf('wall');
  const wall = headWall ? (state.drawing.walls || []).find((w) => w.id === headWall) ?? null : null;
  if (downWall(world, wall)) return;

  // Click near a leader to select it; dragging then moves it.
  const headLeader = headOf('leader');
  const leader = headLeader ? (state.drawing.leaders || []).find((l) => l.id === headLeader) ?? null : null;
  if (downLeader(world, leader)) return;

  // Click near a free-standing arc to select it; dragging then moves it.
  const headArc = headOf('arc');
  const arc = headArc ? (state.drawing.arcs || []).find((a) => a.id === headArc) ?? null : null;
  if (downArc(world, arc)) return;

  // Click near a revision cloud's outline to select it; dragging then moves it.
  const headCloud = headOf('cloud');
  const cloud = headCloud ? (state.drawing.revisionClouds || []).find((c) => c.id === headCloud) ?? null : null;
  if (downCloud(world, cloud)) return;

  // Empty space with the left button: start a rubber-band marquee selection.
  // (A plain click just deselects — a title or a constraint too; panning is
  // middle / right / Space-drag.)
  clearSelection(state);
  startMarquee(world, false);
});

// ---- the canvas mouse-down, one function per press it can be, in the order the handler tries them ----

function downSpacePan(e: MouseEvent): boolean {
  if (e.button === 0 && spaceHeld) {
    spacePanned = true;
    startDrag('pan');
    canvas.style.cursor = 'grabbing';
    return true;
  }
  return false;
}

function downMeasureTool(e: MouseEvent, world: Vec2): boolean {
  if (state.tool === 'measure' && e.button === 0) {
    const p = snapPoint(world, null);
    if (measurePts.length >= 2) measurePts = [];
    measurePts.push(p);
    if (measurePts.length === 2) { reportMeasure(); endOneShot(); }
    invalidate();
    return true;
  }
  return false;
}

function downModifierToggle(e: MouseEvent, world: Vec2): boolean {
  if (e.button === 0 && (e.shiftKey || e.ctrlKey || e.metaKey) && state.tool === 'select') {
    const selRoom = selectedInstance();
    if (selRoom && !instLocked(selRoom) && (selRoom.glass || []).length) {
      const gi = glassHitLocal(selRoom, toInstanceLocal(selRoom, world), 4 / vp.scale);
      if (gi >= 0) {
        const id = glassPanesOf(selRoom)[gi].id ?? null;
        if (id) {
          const set = new Set(selGlassIds());
          if (set.has(id)) set.delete(id); else set.add(id);
          pickSegments('glass', [...set]);
          dropDrag();
          renderProperties();
          invalidate();
          return true;
        }
      }
    }
    // Same for an opening of the selected room (the twin of the pane toggle above).
    if (selRoom && !instLocked(selRoom) && (selRoom.openings || []).length) {
      const oi = openingHitLocal(selRoom, toInstanceLocal(selRoom, world), 4 / vp.scale);
      if (oi >= 0) {
        const id = openingsOf(selRoom)[oi].id ?? null;
        if (id) {
          const set = new Set(selOpeningIds());
          if (set.has(id)) set.delete(id); else set.add(id);
          pickSegments('opening', [...set]);
          dropDrag();
          renderProperties();
          invalidate();
          return true;
        }
      }
    }
  }
  return false;
}

export function downGrip(e: MouseEvent, sp: Vec2, grip: (SelGrip & { index: number; }) | null): boolean {
  if (grip) {
    const start = undoSteps.begin();
    ceilingUi.gripFlash = null;
    state.toolStatusFlash = null; // a refusal left from an earlier work gives way to this one's prompt
    cycleProbe = null;
    gripUi.gripWork = {
      grip, index: grip.index, base: { x: grip.at.x, y: grip.at.y }, press: sp, pointer: sp, alt: e.altKey,
      moved: false, armed: false, start, owner: gripOwner(),
    };
    startDrag('grip');
    updateStatus();
    invalidate();
    return true;
  }
  return false;
}

function downCeilingArrow(hit: HitOf<'ceilingArrow'> | null, sp: Vec2): boolean {
  if (hit) {
    beginGesture();
    ceilingUi.ceilingArrowDrag = { instId: hit.room.id, sp, moved: false };
    startDrag('ceiling-arrow');
    canvas.style.cursor = 'grabbing';
    return true;
  }
  return false;
}

/** A press on a view's title: select it alone and drag it (one gesture). */
function downViewTitle(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (state.tool !== 'select' || e.button !== 0 || e.shiftKey || e.ctrlKey || e.metaKey) return false;
  const t = viewTitleAt(sp), v = t ? viewOfTitle(t) : undefined;
  if (!t || !v) return false;
  deselectAll();
  selectViewTitle(state, t);
  beginGesture();
  const o = v.titleOffset;
  drag.viewTitle = { ...t, grab: world, o0: !o ? [0, 0] : 't' in o ? [o.t, o.h] : [o.x, o.y] };
  startDrag('view-title');
  renderProperties();
  invalidate();
  return true;
}

/** A dragged title follows the cursor — a section's in its view's frame, so it
 *  turns with the view. */
function dragMoveViewTitle(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const d = drag.viewTitle, v = d ? viewOfTitle(d) : undefined;
  if (!d || !v) return false;
  const dx = world.x - d.grab.x, dy = world.y - d.grab.y;
  if (d.kind === 'section') {
    const { T, N } = sectionView(v as SectionLine);
    (v as SectionLine).titleOffset = { t: Math.round(d.o0[0] + dx * T.x + dy * T.y), h: Math.round(d.o0[1] + dx * N.x + dy * N.y) };
  } else (v as CeilingPlanView).titleOffset = { x: Math.round(d.o0[0] + dx), y: Math.round(d.o0[1] + dy) };
  invalidate();
  return false;
}

function downConnTag(tagId: string | null): boolean {
  if (tagId) {
    deselectAll();
    selectOne(state, 'conn', tagId);
    beginGesture();
    drag.connLabel = tagId;
    startDrag('conn-label');
    renderProperties();
    invalidate();
    return true;
  }
  return false;
}

function downGroupClick(e: MouseEvent, sp: Vec2, world: Vec2, onHandle: boolean): boolean {
  if (e.button === 0 && state.tool === 'select' && !onHandle && editAllows(state.editing, 'pick')) {
    // Shift toggles what `toggleAnySelection` would take (the top SELECTABLE
    // object), so a dimension over a group's member still toggles the group.
    const head = e.shiftKey ? topSelectableAt(world) : objectStackAt(world, sp).head;
    if (!head) return false;
    if (e.shiftKey) {
      const obj = clickTarget(state.drawing, head.id, selectedObjectIds()) ? groupables(state.drawing).find((o) => o.id === head.id) : undefined;
      const outer = obj ? outermostGroup(state.drawing, obj) : undefined;
      if (outer) { mergeGroupSel(outer, groupFullySelected(outer)); return true; }
      return false;
    }
    const take = clickTake(state.drawing, head, selectedObjectIds());
    if ('ids' in take) { applyClickTake(take); beginUnifiedMove(world, head.id); return true; }
    if ('drill' in take) groupDrill = { sp, ids: take.drill };
  }
  return false;
}

/** Drilled into a section view, Shift+click there adds the object under it to
 *  the selection or takes it out, as on the plan; a room's feature is picked on
 *  its own, so a selected one is not added to. */
function downSectionShift(e: MouseEvent, world: Vec2, onHandle: boolean): boolean {
  if (e.button !== 0 || !e.shiftKey || state.tool !== 'select' || onHandle || !editAllows(state.editing, 'shiftPick')) return false;
  const sec = sectionOf(toolPlane());
  if (!sec || !inSectionView(sec, world, hitCtx())) return false;
  const top = sectionPicksAt(sec, world).head;
  if (!top?.ids || sectionTargets().some((t) => t.part)) return true;
  const had = selectedObjectIds(), on = top.ids.every((x) => had.has(x));
  for (const x of top.ids) { if (on) had.delete(x); else had.add(x); }
  if (!had.size) return true;
  deselectAll();
  setObjectSelection(selectionOfIds(state.drawing, [...had]));
  state.sectionDrill = sec.id;
  renderProperties();
  invalidate();
  return true;
}

function downShiftMarquee(e: MouseEvent, world: Vec2, onHandle: boolean): boolean {
  if (e.button === 0 && e.shiftKey && state.tool === 'select' && !onHandle && editAllows(state.editing, 'shiftPick')) {
    if (toggleAnySelection(world)) return true;
    // Shift on empty paper: a marquee that ADDS to the selection (PowerPoint).
    startMarquee(world, true);
    return true;
  }
  return false;
}

function downMoveMultiSelection(e: MouseEvent, world: Vec2): boolean {
  if (e.button === 0 && state.tool === 'select' && editAllows(state.editing, 'multiMove')
      && selectedCount(state, MULTI_KINDS) >= 2 && pointOverSelection(world)) {
    beginUnifiedMove(world, objectStackAt(world, vp.worldToScreen(world)).head?.id);
    return true;
  }
  return false;
}

function downRoomVertexEdit(sp: Vec2, world: Vec2): boolean {
  if (state.polyRoomVertexEditId && state.tool === 'select') {
    const prInst = polyRoomEditInst();
    if (prInst && prInst.polygon) {
      const worldPts = prInst.polygon.map((lp) => instanceToWorld(prInst, lp));
      const idx = vertexAtScreen(worldPts, sp);
      // A jamb is not a corner: it only slides along its wall, resizing the pane
      // and the run either side — exactly the chain-joint drag.
      const jamb = idx >= 0 && isJoint(prInst, idx) ? jambJoint(prInst, idx) : null;
      if (jamb) {
        beginGesture();
        drag.wallSpan = { instId: prInst.id, edge: jamb.wall, index: jamb.index };
        startDrag('wall-span');
        invalidate();
        return true;
      }
      if (idx >= 0) {
        beginGesture();
        drag.polyRoomVertex = idx;
        // Grabbing a corner also selects it (angle / coordinate locks); a plain
        // click with no drag leaves it selected so its panel stays open.
        pickRoomCorner(idx);
        startDrag('poly-room-vertex');
        renderProperties();
        invalidate(); // repaint so the grabbed corner reads blue immediately
        return true;
      }
      // An interior partition is selectable here too, and dragging it slides it
      // along its host wall (the same gesture the inner-wall tool uses), so its
      // position can be edited without leaving vertex-edit mode. Tested before
      // the wall edges: a partition starts at the wall's INNER face, so the two
      // targets don't overlap, and the partition is the more specific pick.
      const prLocal = toInstanceLocal(prInst, world);
      const pih = polyInnerWallHit(prInst, prLocal);
      if (pih >= 0) {
        const iw = prInst.innerWalls![pih];
        beginGesture();
        pickRoomCorner(null);
        selectFeature(state, { kind: 'innerWall', instId: prInst.id, wallId: innerWallsOf(prInst)[pih].id! });
        const m = polyInnerWallMetrics(prInst, iw.edge!, prLocal);
        drag.innerWallMove = { instId: prInst.id, index: pih, wall: iw.wall, grabAlong: m?.along ?? iw.pos, origPos: iw.pos, edge: iw.edge, selectOnly: true };
        startDrag('inner-wall-move');
        renderProperties();
        invalidate();
        return true;
      }
      // Mid-edge click selects that wall edge — or completes a queued relation
      // (pick a relation on one segment, then click the segment it applies to).
      const eIdx = polyEdgeAt({ id: prInst.id, points: worldPts } as Polygon, world, EDGE_PICK_PX / vp.scale);
      if (eIdx >= 0) {
        tryApplyPendingRelation(prInst.id, eIdx);
        pickRoomWall(eIdx);
        renderProperties();
        invalidate();
        return true;
      }
    }
    pickRoomCorner(null);
    // Clicking inside the room body moves the whole room but KEEPS vertex-edit
    // active (same rule as the polygon/wall channel above); only a click outside
    // the body exits the mode. Either way we fall through to the normal move path.
    const inBody = prInst && prInst.polygon && roomAt(roomsToEdit(), world)?.id === prInst.id;
    if (!inBody) exitPolyRoomVertexEdit();
    // fall through to normal interaction (door handles, select, move, etc.)
  }
  return false;
}

function downShapeVertexEdit(sp: Vec2, world: Vec2): boolean {
  if (state.editVerticesId) {
    const shape = editingVertexShape();
    if (shape) {
      const hit = vertexAtScreenRings(shape, sp);
      if (hit) {
        beginGesture();
        drag.vertex = { id: shape.id, ring: hit.ring, index: hit.index, closed: !isPolygonShape(shape) && hit.ring === 0 && polylineIsClosed(shape.points) };
        startDrag('vertex');
        return true;
      }
      if (pointInShape(shape, world)) {
        const item: ObjItem = 'revisionId' in shape ? { kind: 'cloud', obj: shape } : isPolygonShape(shape) ? { kind: 'polygon', obj: shape as Polygon } : { kind: 'wall', obj: shape as Wall };
        beginObjectMove(world, [item], shape.id);
        return true;
      }
    }
    leaveEdit('shape'); // clicked elsewhere: leave vertex-edit mode
  }
  return false;
}

function downUnderlayKnob(world: Vec2, hit: HitOf<'underlayKnob'> | null): boolean {
  if (!hit) return false;
  const bg = hit.bg;
  beginGesture();
  startDrag('rotate-pdf');
  rotatePivot = { x: bg.x, y: bg.y };
  rotateStartAngle = Math.atan2(world.y - rotatePivot.y, world.x - rotatePivot.x);
  pdfRotateStart = bg.rotation || 0;
  return true;
}

function downUnderlayResize(hit: HitOf<'underlayResize'> | null): boolean {
  if (!hit) return false;
  beginGesture();
  drag.pdfResizeHandle = hit.handle;
  startDrag('resize-pdf');
  return true;
}

/** The underlay being edited: its body moves it, a press elsewhere leaves the
 *  mode (its knob and handles were tried before). */
function downUnderlayEdit(world: Vec2): boolean {
  if (state.editPdf && editedBg()) {
    const bg = editedBg()!;
    if (pointInPdf(world)) {
      beginGesture();
      startDrag('move-pdf');
      moveStartWorld = world;
      moveInstanceStart = { x: bg.x, y: bg.y };
      return true;
    }
    leaveEdit('underlay'); // clicked outside: leave PDF transform mode
  }
  return false;
}

function downImportedPlanEdit(world: Vec2): boolean {
  if (state.editBg) {
    if (pointInBgGeom(world)) {
      beginGesture();
      startDrag('move-bg');
      moveStartWorld = world;
      moveInstanceStart = { x: state.drawing.backgroundOffset?.x ?? 0, y: state.drawing.backgroundOffset?.y ?? 0 };
      return true;
    }
    leaveEdit('plan'); // clicked outside: deselect the imported drawing
  }
  return false;
}

// ---- Edit in place: one row per mode ----

/** What a mode edited in place does with a press, a double-click, a right-click,
 *  Esc, Delete, the panel and the prompt. Which of the plan's OWN Select gestures still
 *  run beside it is its row in EDIT_RULES (editMode.ts). Every mode has a row, so
 *  a new mode does not compile until it says what each does. */
interface EditLife {
  /** A press, at its place in the mouse-down order: true when the mode took it.
   *  A press away from what it edits may leave the mode, and goes on down. */
  down: (sp: Vec2, world: Vec2) => boolean;
  /** A double-click with Select, which it takes; null: the plan's. */
  dblclick: ((sp: Vec2, world: Vec2) => void) | null;
  /** A right-click, which it takes; null: the plan's object menu. */
  context: ((sp: Vec2) => void) | null;
  /** Esc — and Enter, where EDIT_RULES says Enter finishes it too. */
  esc: () => void;
  /** Enter, in a mode Enter does not simply leave (EDIT_RULES): true when it
   *  took the key; null: Enter is not the mode's. */
  enter: (() => boolean) | null;
  /** Delete, ahead of the selection's: true when it took the key; null: the
   *  selection's Delete. */
  del: (() => boolean) | null;
  /** Its panel, ahead of the selection's own: true when it drew one. */
  panel: (body: HTMLElement) => boolean;
  /** Its prompt in the command bar (English: `updateStatus` translates it). */
  hint: () => string;
}

/** Leave the plan's modes (not a part's drawing editor) and show it. */
function leaveEditShown(kind: EditKind): void {
  leaveEdit(kind);
  renderProperties();
  invalidate();
}

export const EDIT_LIFE: Record<EditKind, EditLife> = {
  part: {
    down: downDrawingEditor,
    dblclick: dblclickDrawingEditor,
    context: contextDrawingEditor,
    // Esc lets go of the shape whose vertices are shown, then cancels (asked
    // first when something changed); Enter is Done (keyDrawingEditorEnter).
    esc: () => {
      if (topDialog()) return; // a dialog's Esc is its own
      // A drawing tool's draft, then the tool itself, go first (the plan's Esc):
      // only Esc with Select cancels the editor.
      if (state.tool !== 'select') { escapeTool(); return; }
      if (!state.drawEditVerticesId) { void requestCancelDrawEdit(); return; }
      state.drawEditVerticesId = null;
      renderProperties();
      invalidate();
    },
    // Enter with Select and nothing under way is Done.
    enter: () => {
      if (state.tool !== 'select' || topDialog()) return false; // a dialog's Enter is its own
      exitDrawingEdit();
      return true;
    },
    // The drawing editor is an Edit space of its own: Delete there is SPACES.part's.
    del: null,
    panel: renderDrawingEditorPanel,
    hint: () => state.drawEditVerticesId ? 'drawing editor — drag handles to move · double-click an edge to add a vertex · right-click a vertex to remove · Esc to exit vertex edit'
      : drawEditHost()?.closedOnly ? 'Polygon or Rectangle: draw · Enter: Done · Esc: Cancel'
      : 'drawing editor — click a shape to select · double-click a shape to edit vertices · use polygon / wall / line / circle tools to add shapes · double-click blank area or Esc to exit',
  },
  plan: {
    down: (_sp, world) => downImportedPlanEdit(world),
    dblclick: null,
    context: null,
    esc: () => leaveEditShown('plan'),
    enter: null,
    del: deleteImportedPlan,
    panel: renderImportedPlanPanel,
    hint: () => 'Drag to move · Enter or Esc to finish.',
  },
  room: {
    down: downRoomVertexEdit,
    dblclick: dblclickRoomVertexEdit,
    context: contextRoomVertexEdit,
    esc: () => leaveEditShown('room'),
    enter: null,
    del: deleteRoomCorner,
    panel: (body) => renderRoomEdgePanel(body) || renderRoomVertexPanel(body),
    hint: () => 'room vertex edit — drag a corner to reshape · drag inside to move the room · click a wall to lock it horizontal/vertical · double-click an edge to add a corner · right-click a corner to delete · Esc or Enter to finish',
  },
  shape: {
    down: downShapeVertexEdit,
    dblclick: null,
    context: contextShapeVertexEdit,
    esc: () => leaveEditShown('shape'),
    enter: null,
    del: null,
    panel: () => false,
    hint: () => 'revisionId' in (editingVertexShape() ?? {})
      ? 'Editing shape — drag a corner to reshape · double-click an edge to add a corner · right-click a corner to remove · drag inside to move · Enter/Esc to finish.'
      : 'Editing vertices: drag handles to move · double-click an edge to add · right-click a vertex to remove · Esc to finish.',
  },
  underlay: {
    down: (_sp, world) => downUnderlayEdit(world),
    dblclick: null,
    context: null,
    esc: () => leaveEditShown('underlay'),
    enter: null,
    del: deleteEditedUnderlay,
    panel: renderUnderlayEditPanel,
    hint: () => 'Drag to move · drag a handle to resize / rotate · Enter or Esc to finish.',
  },
};

/** The Delete of what is edited in place, ahead of the selection's (EDIT_LIFE):
 *  true when it took the key. */
export function editModeDelete(): boolean {
  const del = state.editing ? EDIT_LIFE[state.editing.kind].del : null;
  return !!del && del();
}

/** Whether what is edited in place has a Delete of its own (the button lights). */
export function editModeDeletes(): boolean {
  return !!state.editing && !!EDIT_LIFE[state.editing.kind].del;
}

/** A room's vertex edit: Delete removes the corner picked, as its right-click
 *  does — never the room being edited. No corner picked: the selection's Delete. */
function deleteRoomCorner(): boolean {
  const inst = polyRoomEditInst(), v = state.polyRoomSelectedVertex;
  if (!inst || v == null) return false;
  removeRoomCorner(inst, v);
  pickRoomCorner(null);
  renderProperties();
  invalidate();
  return true;
}

/** What is edited in place takes a press on itself (EDIT_LIFE). */
function downEditMode(sp: Vec2, world: Vec2): boolean {
  const m = state.editing;
  return !!m && EDIT_LIFE[m.kind].down(sp, world);
}

/** A room's vertex edit: a double-click on a wall adds a corner there; away from
 *  every wall it leaves the mode. */
function dblclickRoomVertexEdit(_sp: Vec2, world: Vec2): void {
  const prInst = polyRoomEditInst();
  const seg = prInst?.polygon ? nearestSegment(prInst.polygon.map((lp) => instanceToWorld(prInst, lp)), world, true, 10 / vp.scale) : null;
  if (prInst && seg) { insertRoomCorner(prInst, seg.index, toInstanceLocal(prInst, seg.point)); return; }
  exitPolyRoomVertexEdit();
}

/** A room's vertex edit: a right-click on a corner removes it (a room keeps 3). */
function contextRoomVertexEdit(sp: Vec2): void {
  const prInst = polyRoomEditInst();
  const idx = prInst?.polygon ? vertexAtScreen(prInst.polygon.map((lp) => instanceToWorld(prInst, lp)), sp) : -1;
  if (prInst && idx >= 0) removeRoomCorner(prInst, idx);
}

/** A shape's vertex edit: a right-click on a vertex removes it (the shape stays valid). */
function contextShapeVertexEdit(sp: Vec2): void {
  const shape = editingVertexShape();
  const hit = shape ? vertexAtScreenRings(shape, sp) : null;
  if (!shape || !hit || !canRemoveVertex(shape, hit.ring)) return;
  pushHistory();
  removeVertexFromShape(shape, hit.ring, hit.index);
  invalidate();
}

/** The drawing editor: a double-click on the edge of the shape whose vertices
 *  are shown adds one; on a polygon or line it shows that shape's vertices; on
 *  blank paper it leaves the editor. */
function dblclickDrawingEditor(_sp: Vec2, world: Vec2): void {
  const deInst = drawEditInst();
  if (deInst) {
    const deShapeInfo = editingDrawEditShape();
    if (deShapeInfo) {
      const wShape = deShapeInfo.isWall
        ? localWallToWorld(deInst, deShapeInfo.shape as Wall)
        : localPolyToWorld(deInst, deShapeInfo.shape as Polygon);
      const seg = nearestSegmentRings(wShape, world, 10 / vp.scale);
      if (seg) {
        pushHistory();
        insertVertexInShape(deShapeInfo.shape, seg.ring, seg.index, toInstanceLocal(deInst, seg.point));
        invalidate();
        return;
      }
    }
    const lp = drawEditPolyAt(world);
    const lw = lp ? null : drawEditWallAt(world);
    if (lp && !lp.ellipse) {
      state.drawEditVerticesId = lp.id;
      state.drawEditSelectedPolyId = lp.id;
      state.drawEditSelectedWallId = null;
      drawingEditor.drawEditSelectedEdge = null;
      renderProperties();
      invalidate();
      return;
    }
    if (lw) {
      state.drawEditVerticesId = lw.id;
      state.drawEditSelectedWallId = lw.id;
      state.drawEditSelectedPolyId = null;
      renderProperties();
      invalidate();
      return;
    }
  }
  exitDrawingEdit();
}

/** The drawing editor: a right-click on a vertex of the shape whose vertices are
 *  shown removes it; anywhere else it does nothing (the plan behind has no menu here). */
function contextDrawingEditor(sp: Vec2): void {
  const deInst = drawEditInst();
  const deShapeInfo = deInst && state.drawEditVerticesId ? editingDrawEditShape() : null;
  if (!deInst || !deShapeInfo) return;
  const wShape = deShapeInfo.isWall
    ? localWallToWorld(deInst, deShapeInfo.shape as Wall)
    : localPolyToWorld(deInst, deShapeInfo.shape as Polygon);
  const hit = vertexAtScreenRings(wShape, sp);
  if (!hit || !canRemoveVertex(deShapeInfo.shape, hit.ring)) return;
  pushHistory();
  removeVertexFromShape(deShapeInfo.shape, hit.ring, hit.index);
  invalidate();
}

function downDimensionTool(world: Vec2): boolean {
  if (state.tool === 'dimension') {
    if (!state.pendingA) {
      const s = snapDimPoint(world, null); // align to other dimensions' vertices
      state.pendingA = s.point;
      pendingARef = s.anchor;
    } else if (!state.pendingB) {
      const s = snapDimPoint(world, state.pendingA); // ortho/polar from the first pick
      state.pendingB = s.point;
      pendingBRef = s.anchor;
    } else {
      // The third click places the line AND says what it measures: above/below the
      // span is a horizontal dimension, out to the side a vertical one, straight
      // out from a slanted span an aligned one (AutoCAD's DIM).
      const kind = inferDimKind(state.pendingA, state.pendingB, world);
      const offset = dimOffset(state.pendingA, state.pendingB, world, kind);
      addDimension(state.pendingA, state.pendingB, offset, pendingARef, pendingBRef, kind);
      state.pendingA = null;
      state.pendingB = null;
      pendingARef = undefined;
      pendingBRef = undefined;
      endOneShot(); // the new dimension stays selected
    }
    invalidate();
    return true;
  }
  return false;
}

function downRcpTool(e: MouseEvent, world: Vec2): boolean {
  const kind = rcpToolKind();
  if (kind === null) return false;
  const room = rcpToolRoom(), at = rcpPlacement(world, e.altKey);
  if (room && at) {
    pushHistory();
    const v: CeilingPlanView = { id: newId('rcp'), roomId: room.id, offset: at.offset, ...(kind === 'above' ? { kind } : {}) };
    (state.drawing.ceilingPlans ??= []).push(v);
    endOneShot();
    deselectAll();
    selectOne(state, 'rcp', v.id); // the new view stays selected
    renderProperties();
  }
  invalidate();
  return true;
}

function downSectionTool(e: MouseEvent, world: Vec2): boolean {
  if (state.tool === 'section') {
    sectionClickAt(snapSectionPoint(world, e.altKey));
    return true;
  }
  return false;
}

/** A Section tool click at plan point `p` (the plan's snapped; the 3D view's on
 *  what it shows): the cut's start, its end, then the side it looks toward. */
export function sectionClickAt(p: Vec2): void {
  if (!sectionDraft.a) sectionDraft.a = p;
  else if (!sectionDraft.b) sectionDraft.b = p;
  else {
    const prev = sectionPreview(p);
    sectionDraft = { a: null, b: null };
    if (prev) { addSection(prev); endOneShot(); } // the new section stays selected
  }
  invalidate();
}

/** The Section tool's draft: the cut's start and end clicked so far. */
export const sectionDraftNow = (): { a: Vec2 | null; b: Vec2 | null } => sectionDraft;

function downPdfScaleTool(e: MouseEvent, world: Vec2): boolean {
  if (state.tool === 'pdf-scale') {
    if (!scaleTargetBg()) {
      void showAlert(tr('Open a PDF first, then use this tool to set its scale.'));
      return true;
    }
    if (state.scalePts.length >= 2) state.scalePts = [];
    state.scalePts.push(pdfScaleSnap(world, e.altKey)); // 2nd point snaps orthogonally to the 1st
    if (state.scalePts.length === 2) openScaleDialog();
    invalidate();
    return true;
  }
  return false;
}

function downPolygonTool(e: MouseEvent, world: Vec2): boolean {
  if (state.tool === 'polygon') {
    if (state.distEntry != null) { state.distEntry = null; updateDistEntry(); }
    const polyVerts = drawingEditor.drawingEditId ? [...polyDraft, ...editorShapeSnapVerts()] : [...polyDraft, ...(e.altKey ? [] : nearbyOrthoVerts(world))];
    const w = snapDraftPoint(world, polyVerts, { shift: e.altKey, allow45: true, lastSeg: draftSeg(polyDraft) });
    clickPolyline(w);
    invalidate();
    return true;
  }
  return false;
}

function downFloorTool(e: MouseEvent, world: Vec2): boolean {
  if (floorToolKind(state.tool)) {
    if (state.distEntry != null) { state.distEntry = null; updateDistEntry(); }
    const verts = [...floorDraft, ...(e.altKey ? [] : nearbyOrthoVerts(world))];
    const w = snapDraftPoint(world, verts, { shift: e.altKey, allow45: true, lastSeg: draftSeg(floorDraft) });
    clickPolyline(w);
    invalidate();
    return true;
  }
  return false;
}

function downRevisionCloudTool(e: MouseEvent, world: Vec2): boolean {
  if (state.tool === 'revision-cloud') {
    if (state.distEntry != null) { state.distEntry = null; updateDistEntry(); }
    const cloudVerts = [...revCloudDraft, ...(e.altKey ? [] : nearbyOrthoVerts(world))];
    const w = snapDraftPoint(world, cloudVerts, { shift: e.altKey, allow45: true, lastSeg: draftSeg(revCloudDraft) });
    clickPolyline(w);
    invalidate();
    return true;
  }
  return false;
}

function downCircleTool(e: MouseEvent, world: Vec2): boolean {
  if (state.tool === 'circle') {
    const w = circleSnap(world, e.altKey); // Shift means "ellipse" here; Alt suspends snapping
    if (!circleCenter) {
      circleCenter = w;
    } else if (ellipseRx == null) {
      const r = Math.round(distance(circleCenter, w));
      if (e.shiftKey) {
        if (r >= 1) ellipseRx = r; // await the third click for the second radius
      } else {
        createEllipse(circleCenter.x, circleCenter.y, r, r);
        circleCenter = null;
        endOneShot();
      }
    } else {
      createEllipse(circleCenter.x, circleCenter.y, ellipseRx, Math.round(distance(circleCenter, w)));
      circleCenter = null;
      ellipseRx = null;
      endOneShot();
    }
    invalidate();
    return true;
  }
  return false;
}

function downPolyRoomTool(e: MouseEvent, world: Vec2): boolean {
  if (state.tool === 'poly-room') {
    if (state.distEntry != null) { state.distEntry = null; updateDistEntry(); }
    clickPolyline(applyPolyRoomSnap(world, e.altKey));
    invalidate();
    return true;
  }
  return false;
}

function downRectRoomTool(e: MouseEvent, world: Vec2): boolean {
  if (state.tool === 'rect-room') {
    if (state.distEntry != null) { state.distEntry = null; updateDistEntry(); }
    const p = squareWithShift(rectRoomStart, snapDraftPoint(world, rectRoomStart ? [rectRoomStart] : [], { shift: e.altKey, noConstrain: true, lastSeg: rectRoomStart ? [rectRoomStart, rectRoomStart] : undefined }), e.shiftKey);
    if (!rectRoomStart) rectRoomStart = p;
    else finishRectRoom(rectRoomStart, p);
    invalidate();
    return true;
  }
  return false;
}

function downWallTool(e: MouseEvent, world: Vec2): boolean {
  if (state.tool === 'wall') {
    if (state.distEntry != null) { state.distEntry = null; updateDistEntry(); }
    const p = snapDraftPoint(world, [...wallDraft, ...(e.altKey ? [] : nearbyOrthoVerts(world))], { shift: e.altKey, allow45: true, lastSeg: draftSeg(wallDraft) });
    clickPolyline(p); // on the start point: closed into a ring
    invalidate();
    return true;
  }
  return false;
}

function downRepeatTool(e: MouseEvent, world: Vec2): boolean {
  if (state.tool === 'repeat') {
    if (state.distEntry != null) { state.distEntry = null; updateDistEntry(); }
    clickPolyline(repeatSnap(world, e.altKey));
    invalidate();
    return true;
  }
  return false;
}

function downBenchPolyTool(e: MouseEvent, world: Vec2): boolean {
  if (state.tool === 'bench-poly') {
    clickPolyline(benchPolySnap(world, e.altKey));
    invalidate();
    return true;
  }
  return false;
}

function downLineTool(e: MouseEvent, world: Vec2): boolean {
  if (state.tool === 'line') {
    if (state.distEntry != null) { state.distEntry = null; updateDistEntry(); }
    const p = lineSnap(world, e.altKey);
    clickPolyline(p); // on the start point: closed, as a wall is
    invalidate();
    return true;
  }
  return false;
}

function downZoomWindowTool(world: Vec2): boolean {
  if (state.tool === 'zoom') {
    if (!zoomFrom) { zoomFrom = world; invalidate(); return true; }
    zoomWindow(zoomFrom, world);
    endOneShot();
    return true;
  }
  return false;
}

function downPickTool(e: MouseEvent, world: Vec2): boolean {
  if (placeOf(state.tool).picks) {
    const plane = pickBase ? pickPlane : toolPlane(world);
    // In a section view Copy and Rotate take whole objects, and Rotate tilts them:
    // every selected part must be one that tilts.
    const why = pickBase || state.tool === 'move' ? null : planeRefusal(plane, state.tool === 'rotate');
    if (why) { flashToolStatus(why); return true; }
    // In a section view a selected feature of a room or a drawn wall moves on its own.
    const feats = !pickBase && plane.kind === 'section' ? selectedFeatureMoves() : null;
    if (feats && !feats.length) { flashToolStatus(featureStuckWhy()); return true; }
    if (!pickBase && !feats && selectedCount(state, MULTI_KINDS) === 0) { flashToolStatus(tr('Select the objects first')); return true; }
    // Move / Rotate would take a fixed object with it (Copy leaves it be).
    const fixedSel = state.tool !== 'copy' && !feats ? [...selectedObjectIds()].find((id) => isFixedObject(id)) : undefined;
    if (!pickBase && fixedSel) { flashToolStatus(tr('{obj} is fixed in place.').replace('{obj}', constraintObjName(fixedSel))); return true; }
    if (state.distEntry != null) { state.distEntry = null; updateDistEntry(); }
    const p = pickSnap(world, e.altKey);
    if (!pickBase) startPick(p, plane, feats);
    else finishPick(p);
    invalidate();
    return true;
  }
  return false;
}

function downMirrorTool(e: MouseEvent, world: Vec2): boolean {
  if (state.tool === 'mirror') {
    const plane = mirrorFrom ? mirrorPlane : toolPlane(world);
    mirrorClickAt(planeSnap(world, plane, mirrorFrom, e.altKey, { except: null }), plane);
    return true;
  }
  return false;
}

/** The Mirror tool's first point, or null. */
export const mirrorFromNow = (): Vec2 | null => mirrorFrom;

/** A Mirror tool click at `p` in `plane` (the plan's snapped; the 3D view's on
 *  what it shows, in the plan's plane — the mirror is the plumb plane through the
 *  line): the line's first point, then its second, which mirrors. A first point
 *  set in another plane starts again. */
export function mirrorClickAt(p: Vec2, plane: WorkPlane): void {
  if (mirrorFrom && mirrorPlane.kind !== plane.kind) mirrorFrom = null;
  if (!mirrorFrom) mirrorPlane = plane;
  const why = mirrorFrom ? null : planeRefusal(mirrorPlane);
  if (why) { flashToolStatus(why); return; }
  if (selectedCount(state, MULTI_KINDS) === 0) { flashToolStatus(tr('Select the objects to mirror first')); return; }
  if (!mirrorFrom) { mirrorFrom = p; invalidate(); return; }
  if (distance(mirrorFrom, p) < 1e-6) return;
  const a = mirrorFrom, pl = mirrorPlane;
  // In a section: a plumb line mirrors across the vertical plane square to the
  // cut there, a level one mirrors heights; a slanted one would tilt. In a
  // ceiling plan, the line is its room's line under it.
  // Each is ONE move session of the selection, its step begun before the copies.
  let apply: (from: UndoMark) => void = (from) => { mirrorSelection(planePoint(pl, a).at, planePoint(pl, p).at, from); };
  if (pl.kind === 'section') {
    const m = viewMirror(pl.sec, toPlaneFrame(pl, a), toPlaneFrame(pl, p));
    if (m.kind === 'plumb') apply = (from) => { mirrorSelection(m.across[0], m.across[1], from); };
    else if (m.kind === 'level') apply = (from) => { mirrorHeightsAbout(m.h, from); };
    else {
      // A slanted line tilts: the mirror plane holds it and the view's direction.
      const why = planeRefusal(pl, true);
      if (why) { flashToolStatus(why); mirrorFrom = null; invalidate(); return; }
      apply = (from) => moveSelectionOnce((s) => s.own(() => {
        for (const i of state.drawing.instances.filter((x) => selectedOf(state, 'instance').includes(x.id) && canTilt(x))) {
          const body = tiltBody(i);
          placePart(i, aboveFloor(mirrorBody(body, m.at, m.normal), body.base));
        }
      }, true), undefined, from);
    }
  }
  const from = undoSteps.mark();
  if (!mirrorErase) { const cb = selectionClipboard(); if (cb) insertClipboard(cb, { x: 0, y: 0 }); } // the copies are what get mirrored
  apply(from);
  endOneShot();
}

function downOffsetTool(world: Vec2): boolean {
  if (state.tool === 'offset') {
    if (!modifyTools.modifyState?.first) {
      const hit = pickModifyObject(world);
      if (hit) { modifyTools.modifyState = { first: { ...hit, at: world }, preview: null }; invalidate(); }
    } else if (commitOffset(snapPoint(world, null))) {
      endOneShot();
    }
    return true;
  }
  return false;
}

function downRectangleTool(e: MouseEvent, world: Vec2): boolean {
  if (state.tool === 'rectangle') {
    if (state.distEntry != null) { state.distEntry = null; updateDistEntry(); }
    const p = squareWithShift(rectStart, snapDraftPoint(world, rectStart ? [rectStart] : [], { shift: e.altKey, noConstrain: true, lastSeg: rectStart ? [rectStart, rectStart] : undefined }), e.shiftKey);
    if (!rectStart) rectStart = p;
    else finishRect(rectStart, p);
    invalidate();
    return true;
  }
  return false;
}

function downArcTool(world: Vec2): boolean {
  if (state.tool === 'arc') {
    arcDraft.push(snapPoint(world, null));
    if (arcDraft.length >= 3) finishArc();
    invalidate();
    return true;
  }
  return false;
}

function downLeaderTool(e: MouseEvent, world: Vec2): boolean {
  if (state.tool === 'leader') {
    if (state.distEntry != null) { state.distEntry = null; updateDistEntry(); }
    const p = snapDraftPoint(world, leaderDraft, { shift: e.altKey, allow45: true, lastSeg: draftSeg(leaderDraft) });
    clickPolyline(p); // on the last point: finished, its label next
    invalidate();
    return true;
  }
  return false;
}

function downTextTool(world: Vec2): boolean {
  if (state.tool === 'text') {
    const w = snapPoint(world, null);
    if (!textBoxes.textDraft.a) textBoxes.textDraft.a = w;
    else if (!textBoxes.textDraft.b) textBoxes.textDraft.b = w;
    else {
      textBoxes.textDraft.leader = w;
      finishTextComment();
    }
    invalidate();
    return true;
  }
  return false;
}

function downPrintWindowTool(world: Vec2): boolean {
  if (state.tool === 'print-window') {
    if (!state.printA) {
      state.printA = world;
    } else {
      const a = state.printA;
      state.printA = null;
      setTool('select');
      openPrintPreview(boundsOfPoints(a, world));
    }
    invalidate();
    return true;
  }
  return false;
}

export function downDoorTool(aim: ToolAim): boolean {
  const world = aim.world;
  if (state.tool === 'door') {
    // A drawn wall too: tried first while one is selected, else where no room wall
    // took the click.
    const wallFirst = !!selectedBandWall();
    let onWall = wallFirst ? addWallDoorAt(world) : null;
    const placed = onWall || (wallFirst && bandWallStationAt(world)) ? null : addDoorAt(world, paneReach(aim)); // pushes history only when it actually places
    if (!placed && !onWall && !wallFirst) onWall = addWallDoorAt(world);
    if (onWall) {
      pickWallFeature(onWall.wall, onWall.f);
      setTool('select'); // one door per click, as on a room
      renderProperties();
      return true;
    }
    if (placed) {
      selectOne(state, 'instance', placed.inst.id);
      pickDoor(placed.door.id);
      regenerateSauna(placed.inst);
      setTool('select'); // one door per click; back to select to edit it
      renderProperties();
      invalidate();
    }
    return true;
  }
  return false;
}

export function downGlassTool(aim: ToolAim): boolean {
  const world = aim.world;
  if (state.tool === 'glass') {
    if (selectedBandWall() && startWallFeatureDrag(world, 'glass')) return true;
    // An interior partition directly under the cursor wins — the same rule the door
    // tool uses, so glass is painted onto whichever wall you are actually pointing at.
    const t = paneTargetAt(roomsToEdit(), world, paneReach(aim), true);
    if (t) {
      beginGesture();
      selectRoomKeepingFeature(state, t.room.id);
      drag.glass = t.partition
        ? { instId: t.room.id, wall: 'front', start: t.along, end: t.along, downLocal: t.local, innerWallId: t.partition.id }
        : { instId: t.room.id, wall: t.wall, start: t.pos, end: t.pos, downLocal: t.local, edge: t.edge };
      startDrag('glass');
      renderProperties();
      invalidate();
    } else startWallFeatureDrag(world, 'glass');
    return true;
  }
  return false;
}

export function downOpeningTool(aim: ToolAim): boolean {
  const world = aim.world;
  if (state.tool === 'opening') {
    if (selectedBandWall() && startWallFeatureDrag(world, 'opening')) return true;
    const t = paneTargetAt(roomsToEdit(), world, paneReach(aim), false);
    if (t && !t.partition) {
      beginGesture();
      selectRoomKeepingFeature(state, t.room.id);
      drag.opening = { instId: t.room.id, wall: t.wall, start: t.pos, end: t.pos, downLocal: t.local, edge: t.edge };
      startDrag('opening');
      renderProperties();
      invalidate();
    } else startWallFeatureDrag(world, 'opening');
    return true;
  }
  return false;
}

export function downInnerWallTool(aim: ToolAim): boolean {
  const world = aim.world;
  if (state.tool === 'inner-wall') {
    // Polygonal rooms: partitions are hosted on a polygon edge (edge + along).
    const room = roomAt(roomsToEdit(), world);
    if (room) {
      const local = toInstanceLocal(room, world);
      const t = innerWallTargetIn(room, local, wallReach(aim));
      if (!t) return true;
      beginGesture();
      selectRoomKeepingFeature(state, room.id);
      if ('move' in t) {
        const iw = innerWallsOf(room)[t.move];
        const m = polyInnerWallMetrics(room, iw.edge!, local);
        drag.innerWallMove = { instId: room.id, index: t.move, wall: iw.wall, grabAlong: m?.along ?? iw.pos, origPos: iw.pos, edge: iw.edge };
        startDrag('inner-wall-move');
      } else {
        drag.innerWall = { instId: room.id, wall: 'front', pos: t.pos, length: 0, downLocal: local, edge: t.edge };
        startDrag('inner-wall');
      }
      renderProperties();
      invalidate();
      return true;
    }
    const ih = innerWallHitTest(world);
    if (ih) {
      const inst = state.drawing.instances.find((i) => i.id === ih.instId)!;
      const iw = inst.innerWalls![ih.index];
      beginGesture();
      selectRoomKeepingFeature(state, ih.instId);
      const local = toInstanceLocal(inst, world);
      const along = iw.wall === 'front' || iw.wall === 'back' ? local.x : local.y;
      drag.innerWallMove = { instId: ih.instId, index: ih.index, wall: iw.wall, grabAlong: along, origPos: iw.pos };
      startDrag('inner-wall-move');
      renderProperties();
      invalidate();
      return true;
    }
    const hit = nearestWallAt(roomsToEdit(), world, paneReach(aim));
    if (hit && !isPolyRoom(hit.room)) {
      beginGesture();
      const inst = hit.room;
      const wt = wallThicknessOf(inst);
      const horiz = hit.wall === 'front' || hit.wall === 'back';
      const half = (horiz ? inst.width : inst.depth) / 2;
      // Keep the wall a touch in from the corners so it sits inside the room.
      const pos = Math.max(-half + wt, Math.min(half - wt, hit.pos));
      selectRoomKeepingFeature(state, inst.id);
      drag.innerWall = { instId: inst.id, wall: hit.wall, pos, length: 0, downLocal: hit.local };
      startDrag('inner-wall');
      renderProperties();
      invalidate();
    }
    return true;
  }
  return false;
}

function downSelectedBenchHandles(hit: HitOf<'benchHandle'> | null): boolean {
  const bench = featureOf(state, 'bench');
  if (!hit || !bench) return false;
  beginGesture();
  drag.benchEdit = { instId: bench.instId, benchId: bench.benchId, kind: hit.bench };
  startDrag('bench-edit');
  invalidate();
  return true;
}

export function downHeaterWallTool(e: MouseEvent, aim: ToolAim): boolean {
  const world = aim.world;
  if (state.tool === 'heater-wall' && e.button === 0) {
    const t = heaterWallTargetAt(roomsToEdit(), world, wallReach(aim));
    if (t && 'remove' in t) {
      pushHistory();
      selectRoomKeepingFeature(state, t.room.id);
      removeHeaterWall(t.room, t.remove.id);
      regenerateSauna(t.room);
      renderProperties();
      invalidate();
    } else if (t) {
      editRoom(t.room, () => {
        const hw = addHeaterWall(t.room, t.add);
        selectRoomKeepingFeature(state, t.room.id);
        selectFeature(state, { kind: 'heaterWall', instId: t.room.id, wallId: hw.id });
      });
    }
    return true;
  }
  return false;
}

/** Whether the bench joint under the Bench tool's cursor can mitre — its
 *  click then steps through four states, not two. */
function hoverBenchJointCanMitre(): boolean {
  const j = hoverBenchJoint;
  const inst = j ? state.drawing.instances.find((x) => x.id === j.instId) : undefined;
  if (!j || !inst) return false;
  if (typeof j.corner === 'number') return polyBenchCanMitre(inst, j.corner, j.tier);
  if (typeof j.corner === 'object' && 'iw' in j.corner) return partitionBenchCanMitre(inst, j.corner.iw, j.corner.face, j.tier);
  return false;
}

/** Say which way a bench joint now meets, after a click stepped it on. */
function flashBenchJoin(s: BenchJoinState): void {
  const say: Record<BenchJoinState, string> = {
    default: tr('Join: the default bench runs through'),
    swapped: tr('Join: the other bench runs through'),
    mitre: tr('Join: mitred, the default bench’s frame runs through'),
    mitreSwapped: tr('Join: mitred, the other bench’s frame runs through'),
  };
  flashToolStatus(say[s]);
}

export function downBenchTool(aim: ToolAim): boolean {
  const world = aim.world;
  if (state.tool !== 'bench') return false;
  if (downNodeBenchJoint(world)) return true;
  const room = roomAt(roomsToEdit(), world);
  if (!room) return true;
  const t = benchTargetIn(room, toInstanceLocal(room, world), wallReach(aim), newTier);
  beginGesture();
  selectRoomKeepingFeature(state, room.id);
  if (t) applyBenchTarget(room, t);
  commitGesture();
  renderProperties();
  invalidate();
  return true;
}

/** Do what the Bench tool's target says (the hover showed it). The room's other
 *  feature stays picked (`selectRoomKeepingFeature`); a bench taken away, or one
 *  added, lets go of a picked partition or heater wall. */
function applyBenchTarget(room: ProductInstance, t: BenchTarget): void {
  const select = (benchId: string): void => selectFeature(state, { kind: 'bench', instId: room.id, benchId });
  switch (t.kind) {
    case 'joint':
      togglePolyBenchJoin(room, t.corner, t.tier);
      flashBenchJoin(polyBenchJoinState(room, t.corner, t.tier));
      return;
    case 'partitionJoint':
      togglePartitionBenchJoin(room, t.iwIndex, t.face, t.tier);
      flashBenchJoin(partitionBenchJoinState(room, t.iwIndex, t.face, t.tier));
      return;
    case 'partitionBench': {
      const id = addPolyInnerWallBench(room, t.iwIndex, t.face);
      if (id) select(id);
      else dropFeature(state, 'heaterWall', 'innerWall');
      return;
    }
    case 'removeBench':
      removeRoomBench(room, t.id);
      return;
    case 'nextTier':
    case 'firstBench': {
      // A tier is clipped to the clicked side of any partition on its edge, so it
      // meets the partition instead of spanning across it (stored benches are
      // drawn as they are, never reconciled).
      const id = newId('pb');
      (room.polyBenches ??= []).push(newPolyBench(room, id, t.edge, t.span));
      select(id);
      return;
    }
    default: unreachable(t, 'bench target');
  }
}

function downConnectionTool(e: MouseEvent, world: Vec2): boolean {
  if (state.tool === 'connection') {
    if (e.button !== 0) return true;
    const pick = connPickAt(world, e.altKey);
    if (!connectionUi.connDraft) connectionUi.connDraft = { from: pick, pts: [pick.p], hover: pick, press: world };
    else if (pick.id) finishConnection(pick);
    else clickPolyline(connDraftSnap(pick.p, e.altKey));
    updateStatus();
    invalidate();
    return true;
  }
  return false;
}

function downDrawingEditor(sp: Vec2, world: Vec2): boolean {
  if (drawingEditor.drawingEditId && state.tool === 'select') {
    const deInst = drawEditInst();
    if (deInst) {
      if (state.drawEditVerticesId) {
        const deShapeInfo = editingDrawEditShape();
        if (deShapeInfo) {
          const wShape = deShapeInfo.isWall
            ? localWallToWorld(deInst, deShapeInfo.shape as Wall)
            : localPolyToWorld(deInst, deShapeInfo.shape as Polygon);
          const hit = vertexAtScreenRings(wShape, sp);
          if (hit) {
            beginGesture();
            drawingEditor.drawEditVertexDrag = { id: state.drawEditVerticesId, ring: hit.ring, index: hit.index };
            startDrag('draw-edit-vertex');
            return true;
          }
        }
        state.drawEditVerticesId = null;
        renderProperties();
        invalidate();
      }
      // Bounding-box resize handle of the selected shape takes priority.
      const rz = hitDrawEditHandle(sp);
      if (rz) {
        const sel = selectedDrawEditShape()!;
        beginGesture();
        drag.drawEditResizeHandle = rz;
        drawEditDragStart = sel.shape.points.map((p) => ({ x: p.x, y: p.y }));
        drawEditHolesStart = snapshotRings(shapeHoles(sel.shape));
        let minx = Infinity, maxx = -Infinity, miny = Infinity, maxy = -Infinity;
        for (const p of sel.shape.points) { minx = Math.min(minx, p.x); maxx = Math.max(maxx, p.x); miny = Math.min(miny, p.y); maxy = Math.max(maxy, p.y); }
        drag.drawEditResizeBBox = { minx, maxx, miny, maxy };
        startDrag('draw-edit-resize');
        return true;
      }
      // Mid-edge click on the selected polygon selects that edge (Make H/V).
      if (state.drawEditSelectedPolyId) {
        const selP = deInst.drawPolygons?.find((p) => p.id === state.drawEditSelectedPolyId);
        if (selP && !selP.ellipse) {
          const eIdx = polyEdgeAt(localPolyToWorld(deInst, selP), world, EDGE_PICK_PX / vp.scale);
          if (eIdx >= 0) {
            drawingEditor.drawEditSelectedEdge = { id: selP.id, edge: eIdx };
            renderProperties();
            invalidate();
            return true;
          }
        }
      }
      // Click a shape → select it and start a move drag (a click with no motion
      // just selects; dragging moves the whole shape).
      const lp = drawEditPolyAt(world);
      const lw = lp ? null : drawEditWallAt(world);
      if (lp || lw) {
        beginGesture();
        drawingEditor.drawEditSelectedEdge = null;
        if (lp) { state.drawEditSelectedPolyId = lp.id; state.drawEditSelectedWallId = null; }
        else { state.drawEditSelectedWallId = lw!.id; state.drawEditSelectedPolyId = null; }
        const shape = (lp ?? lw)!;
        drawEditDragStart = shape.points.map((p) => ({ x: p.x, y: p.y }));
        drawEditHolesStart = snapshotRings(shapeHoles(shape));
        drag.drawEditDownLocal = toInstanceLocal(deInst, world);
        startDrag('draw-edit-move');
        renderProperties();
        invalidate();
        return true;
      }
    }
    state.drawEditSelectedPolyId = null;
    state.drawEditSelectedWallId = null;
    drawingEditor.drawEditSelectedEdge = null;
    renderProperties();
    invalidate();
    return true;
  }
  return false;
}

function downDoorJamb(hit: HitOf<'doorJamb'> | null): boolean {
  if (!hit) return false;
  beginGesture();
  drag.doorResizeSide = hit.side;
  startDrag('door-resize');
  return true;
}

function downSelectedDoor(hit: HitOf<'doorFlip'> | null): boolean {
  if (!hit) return false;
  const { inst, door } = hit;
  pushHistory();
  if (hit.flip === 'swing') door.swingBack = !door.swingBack;
  else flipDoor(door);
  regenerateSauna(inst);
  renderProperties();
  invalidate();
  return true;
}

function downRotateKnob(hit: HitOf<'instKnob'> | null): boolean {
  if (!hit) return false;
  beginGesture();
  startDrag('rotate');
  return true;
}

function downShapeRotateKnob(world: Vec2, hit: HitOf<'shapeKnob'> | null): boolean {
  if (!hit) return false;
  const shape = hit.shape;
  beginGesture();
  startDrag('rotate-shape');
  shapePointsStart = shape.points.map((p) => ({ x: p.x, y: p.y }));
  shapeHolesStart = snapshotRings(shapeHoles(shape));
  rotatePivot = shapeCenter(shape);
  rotateStartAngle = Math.atan2(world.y - rotatePivot.y, world.x - rotatePivot.x);
  return true;
}

function downResizeHandle(hit: HitOf<'instResize'> | null): boolean {
  if (!hit) return false;
  beginGesture();
  drag.resizeHandle = hit.handle;
  startDrag('resize');
  holdDragLayers([hit.inst.id]); // a resize can carry a part's centre into a room too
  return true;
}

/** A node of a node object (a polyline bench, a salt wall): drag this node. */
function downNodeHandle(hit: HitOf<'nodeHandle'> | null): boolean {
  if (!hit) return false;
  const { inst, handle } = hit;
  beginGesture();
  drag.moveNodesStart = inst.nodes ? inst.nodes.map((n) => ({ x: n.x, y: n.y })) : [];
  drag.moveNodeIndex = handle.nodeIndex!;
  startDrag('move-node');
  holdDragLayers([inst.id]);
  return true;
}

function downHeaterCover(hit: HitOf<'heaterCover'> | null): boolean {
  if (!hit) return false;
  applyHeaterCoverSide(hit.cover.instId, hit.cover.side, hit.cover.kind);
  return true;
}

function downPolyEdge(hit: HitOf<'polyEdge'> | null): boolean {
  if (!hit) return false;
  tryApplyPendingRelation(hit.poly.id, hit.edge);
  selectPolyEdge(state, hit.poly.id, hit.edge);
  state.propTab = 'properties';
  dropDrag();
  renderProperties();
  invalidate();
  return true;
}

function downShapeHandle(hit: HitOf<'shapeResize'> | null): boolean {
  if (!hit) return false;
  const poly = hit.poly;
  beginGesture();
  state.selectedPolyEdge = null;
  drag.shapeResizeHandle = hit.handle;
  shapePointsStart = poly.points.map((p) => ({ x: p.x, y: p.y }));
  shapeHolesStart = snapshotRings(poly.holes || []);
  drag.shapeResizeBBox = pointsBBox(poly.points);
  startDrag('resize-shape');
  return true;
}

function downCloudKnob(world: Vec2, hit: HitOf<'cloudKnob'> | null): boolean {
  if (!hit) return false;
  const pts = hit.cloud.points;
  beginGesture();
  startDrag('rotate-cloud');
  shapePointsStart = pts.map((p) => ({ x: p.x, y: p.y }));
  rotatePivot = shapeCenterOf(pts);
  rotateStartAngle = Math.atan2(world.y - rotatePivot.y, world.x - rotatePivot.x);
  return true;
}

function downCloudHandle(hit: HitOf<'cloudResize'> | null): boolean {
  if (!hit) return false;
  const pts = hit.cloud.points;
  beginGesture();
  drag.shapeResizeHandle = hit.handle;
  shapePointsStart = pts.map((p) => ({ x: p.x, y: p.y }));
  drag.shapeResizeBBox = pointsBBox(pts);
  startDrag('resize-cloud');
  return true;
}

function downTableHandle(world: Vec2, hit: HitOf<'tableResize'> | null): boolean {
  const t = hit ? tableFor(hit.table) : undefined;
  if (!hit || !t) return false;
  beginGesture();
  drag.table = { kind: hit.table, grab: world, x: t.x, y: t.y, width: t.width, handle: hit.side };
  startDrag('resize-table');
  return true;
}

function downTableBody(world: Vec2, pickStack: ObjRef[], tblBody: SchedTableKind | null): boolean {
  if (tblBody && tableReachable(tblBody, pickStack)) {
    const t = tableFor(tblBody)!;
    beginGesture();
    selectOne(state, 'table', tblBody);
    drag.table = { kind: tblBody, grab: world, x: t.x, y: t.y, width: t.width, handle: 1 };
    startDrag('move-table');
    renderProperties();
    invalidate();
    return true;
  }
  return false;
}

function downRoomFeaturePart(pickStack: ObjRef[], featId: string | null): boolean {
  if (featId && roomReachable(pickStack, featId)) {
    beginGesture();
    selectOne(state, 'instance', featId);
    startDrag('cube-move');
    renderProperties();
    invalidate();
    return true;
  }
  return false;
}

function downHeaterWall(pickStack: ObjRef[], hwHit: { room: ProductInstance; wall: HeaterWall; } | null): boolean {
  if (hwHit && roomReachable(pickStack, hwHit.room.id)) {
    selectRef({ kind: 'heaterWall', id: hwHit.wall.id, instId: hwHit.room.id });
    return true;
  }
  return false;
}

function downInnerWall(pickStack: ObjRef[], iwHit: { room: ProductInstance; wall: InnerWall; } | null): boolean {
  if (iwHit && iwHit.wall.id && roomReachable(pickStack, iwHit.room.id)) {
    selectRef({ kind: 'innerWall', id: iwHit.wall.id, instId: iwHit.room.id });
    return true;
  }
  return false;
}

function downWallSpanJoint(pickStack: ObjRef[], jointHit: { inst: ProductInstance; edge: number; index: number; } | null): boolean {
  if (jointHit && roomReachable(pickStack, jointHit.inst.id)) {
    beginGesture();
    drag.wallSpan = { instId: jointHit.inst.id, edge: jointHit.edge, index: jointHit.index };
    startDrag('wall-span');
    return true;
  }
  return false;
}

function downRoomPane(world: Vec2, selRoom: ProductInstance | null, selRoomLive: boolean): boolean {
  if (selRoom && selRoomLive && (selRoom.glass || []).length) {
    const gi = glassHitLocal(selRoom, toInstanceLocal(selRoom, world), 4 / vp.scale);
    if (gi >= 0) {
      pickSegments('glass', [glassPanesOf(selRoom)[gi].id]); // a plain click is a single selection
      state.selectedDimId = null;
      dropDrag();
      renderProperties();
      invalidate();
      return true;
    }
  }
  return false;
}

function downRoomOpening(world: Vec2, selRoom: ProductInstance | null, selRoomLive: boolean): boolean {
  if (selRoom && selRoomLive && (selRoom.openings || []).length) {
    const oi = openingHitLocal(selRoom, toInstanceLocal(selRoom, world), 4 / vp.scale);
    if (oi >= 0) {
      pickSegments('opening', [openingsOf(selRoom)[oi].id]);
      state.selectedDimId = null;
      dropDrag();
      renderProperties();
      invalidate();
      return true;
    }
  }
  return false;
}

function downRoomDoor(pickStack: ObjRef[], doorHit: { instId: string; doorId: string; } | null): boolean {
  if (doorHit && roomReachable(pickStack, doorHit.instId)) {
    beginGesture();
    selectOne(state, 'instance', doorHit.instId);
    pickDoor(doorHit.doorId);
    startDrag('door');
    renderProperties();
    invalidate();
    return true;
  }
  return false;
}

function downWallFeature(world: Vec2, selBand: Wall | null, wfHit: WallFeatureSpan | null): boolean {
  if (selBand && wfHit) {
    beginGesture();
    pickWallFeature(selBand, wfHit.f);
    drag.wallFeatMove = { wallId: selBand.id, id: wfHit.f.id, start: wfHit.s, end: wfHit.e, grab: wallSegStation(selBand, wfHit.seg, world) ?? wfHit.s };
    startDrag('wall-feature-move');
    return true;
  }
  return false;
}

function downTextKnob(hit: HitOf<'textKnob'> | null): boolean {
  if (!hit) return false;
  beginGesture();
  startDrag('rotate-text');
  return true;
}

function downTextHandle(hit: HitOf<'textResize'> | null): boolean {
  if (!hit) return false;
  beginGesture();
  drag.textResizeHandle = hit.handle;
  startDrag('resize-text');
  return true;
}

function downTextLeaderHandle(hit: HitOf<'textLeader'> | null): boolean {
  if (!hit) return false;
  beginGesture();
  startDrag('move-text-leader');
  return true;
}

function downTextBox(world: Vec2, tc: TextComment | null): boolean {
  if (tc) {
    selectOne(state, 'text', tc.id);
    planMove = { session: beginSelectionMove({ step: 'open' }, [{ kind: 'text', obj: tc }]), ref: { x: tc.x, y: tc.y }, exclude: new Set() };
    startDrag('move-text');
    moveStartWorld = world;
    textMoveStart = { x: tc.x, y: tc.y };
    renderProperties();
    invalidate();
    return true;
  }
  return false;
}

function downSelectedRoomBody(world: Vec2, hit: string | null): boolean {
  if (hit && hit === state.selectedId && state.selectedIds.length <= 1) {
    const selInst = state.drawing.instances.find((i) => i.id === hit);
    if (selInst && instanceScreenMinExtent(selInst) < GRAB_MOVE_PX) {
      selectInstanceForMove(hit, world);
      return true;
    }
  }
  return false;
}

function downBench(benchHit: { instId: string; benchId: string; } | null): boolean {
  if (benchHit) {
    clearSelection(state);
    selectFeature(state, { kind: 'bench', ...benchHit });
    dropDrag();
    renderProperties();
    invalidate();
    return true;
  }
  return false;
}

function downDimension(world: Vec2, dimHit: string | null): boolean {
  if (dimHit) {
    selectOne(state, 'dim', dimHit);
    // Drag the line to slide it perpendicular (change its offset); the anchored
    // endpoints stay put.
    const dim = state.drawing.dimensions.find((x) => x.id === dimHit);
    if (dim) {
      beginGesture();
      drag.dimOffset = { id: dimHit, grab: world, origOffset: dim.offset };
      startDrag('dim-offset');
    }
    renderProperties();
    invalidate();
    return true;
  }
  return false;
}

function downConnection(connHit: string | null): boolean {
  if (connHit) {
    deselectAll();
    selectOne(state, 'conn', connHit);
    renderProperties();
    invalidate();
    return true;
  }
  return false;
}

/** A square handle at an end of the selected section's line: it reshapes the cut. */
function downSectionEnd(hit: HitOf<'sectionEnd'> | null): boolean {
  if (!hit) return false;
  beginGesture();
  drag.sectionEndpoint = { id: hit.sec.id, end: hit.end };
  startDrag('section-endpoint');
  invalidate();
  return true;
}

/** The selected section's rotate handle: it spins the generated drawing. */
function downSectionKnob(world: Vec2, hit: HitOf<'sectionKnob'> | null): boolean {
  if (!hit) return false;
  const sec = hit.sec, v = sectionView(sec);
  beginGesture();
  drag.sectionView = { id: sec.id, grab: world, origin: { ...v.O }, angle0: sec.viewAngle ?? Math.atan2(v.T.y, v.T.x), pivot: { ...v.O } };
  startDrag('section-view-rotate');
  invalidate();
  return true;
}

/** A press inside a reflected ceiling plan: select it and drag it (one gesture). */
function downRcpView(world: Vec2, id: string | null | undefined, e?: MouseEvent): boolean {
  const v = id ? (state.drawing.ceilingPlans || []).find((x) => x.id === id) : undefined;
  if (!v) return false;
  // A selected part seen in it (its room's plan, shifted) drags as on the plan.
  const at = { x: world.x - v.offset.x, y: world.y - v.offset.y };
  const hit = e && e.button === 0 && state.tool === 'select'
    ? state.drawing.instances.find((i) => selectedOf(state, 'instance').includes(i.id) && !i.autoBenches && pointInPolygon(at, instanceCorners(i)))
    : undefined;
  if (hit && e) {
    const fixed = [...selectedObjectIds()].find((x) => isFixedObject(x));
    if (fixed) { constraintFlash(tr('{obj} is fixed in place.').replace('{obj}', constraintObjName(fixed))); return true; }
    startPlaneDrag(e, { kind: 'rcp', offset: { ...v.offset } }, world);
    return true;
  }
  deselectAll();
  selectOne(state, 'rcp', v.id);
  beginGesture();
  drag.rcpView = { id: v.id, grab: world, offset0: { ...v.offset } };
  startDrag('rcp-move');
  renderProperties();
  invalidate();
  return true;
}

/** Which view the armed tool places: 'reflected' (Ceiling plan), 'above' (Above
 *  ceiling), or null while neither is armed. */
function rcpToolKind(): 'reflected' | 'above' | null {
  return state.tool === 'rcp' ? 'reflected' : state.tool === 'ceiling-top' ? 'above' : null;
}

/** The room the reflected ceiling plan tool shows: the selected walled room. */
function rcpToolRoom(): ProductInstance | null {
  const r = selectedInstance();
  return r && r.autoBenches && isPolyRoom(r) ? r : null;
}
/** Alt held at the last move: the tool's view goes where the cursor is. */
let rcpFree = false;
/** Where the tool would place its view with the cursor at `world`: the room's
 *  middle there, held in projection with the room. */
function rcpPlacement(world: Vec2, free: boolean): { offset: Vec2; guide: [Vec2, Vec2] | null } | null {
  const room = rcpToolRoom();
  return room ? rcpSnapOffset({ x: world.x - room.x, y: world.y - room.y }, room, free) : null;
}
/** The tool's view following the cursor. */
function rcpPreview(): CeilingPlanView | null {
  const kind = rcpToolKind(), room = kind ? rcpToolRoom() : null;
  const at = room && state.hoverWorld ? rcpPlacement(state.hoverWorld, rcpFree) : null;
  return room && at ? { id: 'rcp-preview', roomId: room.id, offset: at.offset, ...(kind === 'above' ? { kind } : {}) } : null;
}

/** A view's offset held in projection with its room: within 12 px of lining up
 *  with it — straight above or below (x the room's), straight beside it (y the
 *  room's) — it lines up, the room's orthogonal projection, and the guide is the
 *  line it is held on; else on the grid. Alt: free. */
function rcpSnapOffset(want: Vec2, room: ProductInstance, free: boolean): { offset: Vec2; guide: [Vec2, Vec2] | null } {
  if (free) return { offset: want, guide: null };
  const tol = 12 / vp.scale, ax = Math.abs(want.x), ay = Math.abs(want.y);
  let offset = { x: gsnap(want.x), y: gsnap(want.y) };
  if (ax <= tol && (ay > tol || ax <= ay)) offset = { x: 0, y: offset.y };
  else if (ay <= tol) offset = { x: offset.x, y: 0 };
  else return { offset, guide: null };
  const c = { x: room.x, y: room.y };
  return { offset, guide: [c, { x: c.x + offset.x, y: c.y + offset.y }] };
}

function downSectionView(e: MouseEvent, sp: Vec2, world: Vec2, viewHit: SectionLine | null): boolean {
  if (viewHit && downSectionObject(e, sp, world, viewHit)) return true;
  if (viewHit) {
    // On a selected (or drilled-into) view the press is a drill click if it ends
    // where it began; a drag moves the view either way, the selection kept.
    sectionClick = sectionDrills(viewHit.id) ? { id: viewHit.id, sp, world, alt: e.altKey } : null;
    if (state.sectionDrill !== viewHit.id) { selectOne(state, 'section', viewHit.id); state.sectionDrill = null; }
    beginGesture();
    drag.sectionView = { id: viewHit.id, grab: world, origin: { ...sectionView(viewHit).O }, angle0: 0, pivot: { x: 0, y: 0 } };
    startDrag('section-view-move');
    renderProperties();
    invalidate();
    return true;
  }
  return false;
}

/** Drilled into a view, a press on what is selected there drags it in the view's
 *  plane — a selected feature of a room or a drawn wall along its wall and up —;
 *  a click (released where pressed) still picks, as any drill click. */
function downSectionObject(e: MouseEvent, sp: Vec2, world: Vec2, sec: SectionLine): boolean {
  if (state.sectionDrill !== sec.id || e.button !== 0 || state.tool !== 'select') return false;
  const top = sectionPicksAt(sec, world).head;
  if (!top || !sectionTargets().some((t) => srcKey(t) === srcKey(top.target))) return false;
  const feats = top.target.part ? selectedFeatureMoves() : null;
  if (feats && !feats.length) return false;
  const fixed = feats ? undefined : [...selectedObjectIds()].find((id) => isFixedObject(id));
  if (fixed) { constraintFlash(tr('{obj} is fixed in place.').replace('{obj}', constraintObjName(fixed))); return true; }
  sectionClick = { id: sec.id, sp, world, alt: e.altKey };
  startPlaneDrag(e, { kind: 'section', sec }, world, feats);
  return true;
}

function downSectionLine(world: Vec2, secHit: SectionLine | null): boolean {
  if (secHit) {
    selectOne(state, 'section', secHit.id);
    state.sectionDrill = null;
    beginGesture();
    drag.sectionMove = { id: secHit.id, grab: world, a: { ...secHit.a }, b: { ...secHit.b } };
    startDrag('section-move');
    renderProperties();
    invalidate();
    return true;
  }
  return false;
}

/** The rotate knob of the selected floor's drain. */
function downDrainKnob(hit: HitOf<'drainKnob'> | null): boolean {
  if (!hit) return false;
  beginGesture();
  state.selectedFloorDrain = true;
  startDrag('rotate-floor-drain');
  updateFloorDrainDims(hit.floor);
  invalidate();
  return true;
}

function downFloorDrain(world: Vec2, selFloor: Flooring | null): boolean {
  if (selFloor && selFloor.drain) {
    if (floorDrainHit(selFloor, world)) {
      beginGesture();
      state.selectedFloorDrain = true;
      startDrag('move-floor-drain');
      moveStartWorld = world;
      moveInstanceStart = { x: selFloor.drain.x, y: selFloor.drain.y };
      updateFloorDrainDims(selFloor);
      renderProperties();
      invalidate();
      return true;
    }
  }
  return false;
}

function downFloor(world: Vec2, floorHit: Flooring | null): boolean {
  if (floorHit) {
    deselectAll();
    selectOne(state, 'flooring', floorHit.id);
    planMove = { session: beginSelectionMove({ step: 'open' }, [{ kind: 'flooring', obj: floorHit }]), ref: { ...(floorHit.points[0] ?? world) }, exclude: new Set() };
    startDrag('move-floor');
    moveStartWorld = world;
    renderProperties();
    invalidate();
    return true;
  }
  return false;
}

function downPolygon(world: Vec2, poly: Polygon | null): boolean {
  if (poly) {
    // Clicking a polygon already in a multi-selection drags the whole group.
    if (state.selectedPolyIds.length > 1 && state.selectedPolyIds.includes(poly.id)) {
      beginUnifiedMove(world, poly.id);
      return true;
    }
    selectOne(state, 'polygon', poly.id); // a body/handle click drops any edge selection
    beginObjectMove(world, [{ kind: 'polygon', obj: poly }], poly.id);
    return true;
  }
  return false;
}

function downWall(world: Vec2, wall: Wall | null): boolean {
  if (wall) {
    selectOne(state, 'wall', wall.id);
    beginObjectMove(world, [{ kind: 'wall', obj: wall }], wall.id);
    return true;
  }
  return false;
}

function downLeader(world: Vec2, leader: Leader | null): boolean {
  if (leader) {
    leaveEditModes();
    selectOne(state, 'leader', leader.id);
    beginObjectMove(world, [{ kind: 'leader', obj: leader }], leader.id);
    return true;
  }
  return false;
}

function downArc(world: Vec2, arc: Arc | null): boolean {
  if (arc) {
    leaveEditModes();
    selectOne(state, 'arc', arc.id);
    beginObjectMove(world, [{ kind: 'arc', obj: arc }], arc.id);
    return true;
  }
  return false;
}

function downCloud(world: Vec2, cloud: RevisionCloud | null): boolean {
  if (cloud) {
    selectOne(state, 'cloud', cloud.id);
    beginObjectMove(world, [{ kind: 'cloud', obj: cloud }], cloud.id);
    return true;
  }
  return false;
}

/** Start a rubber-band marquee at `world`. `add` (Shift) keeps the selection and
 *  adds what the band takes to it; otherwise the band replaces it. */
function startMarquee(world: Vec2, add: boolean): void {
  drag.marqueeStart = world;
  drag.marqueeEnd = world;
  drag.marqueeAdd = add;
  startDrag('marquee');
  renderProperties();
  invalidate();
}

window.addEventListener('mousemove', (e) => {
  drawConstrain = e.shiftKey;
  if (activeSheet()) { sheetMouseMove(e); return; }
  // Over the 3D view the plan is hidden: its hovers would follow the pointer in
  // plan coordinates — Move's pick fought the 3D one's (ui/tools3d.ts follows it there).
  if (state.viewTab === '3d') return;
  const sp = screenPos(e);
  // A FIXED object won't move: a move drag that would take one ends before it
  // starts (the reconcile pass would only put it back), and the bar says why.
  // A Ctrl-drag copies, which leaves it where it is.
  if (moveRefuseFixedDrag(e)) return;
  // The first frame of a Ctrl move drag: capture what is about to move.
  moveCaptureCtrlCopy(e);
  updateCoordReadout(vp.screenToWorld(sp)); // live X / Y in the status bar
  // What is under way follows the pointer (a section handle's work, applied
  // once a frame; an armed grip); a pan still pans.
  if (moveUnderWay(e, sp)) return;

  // Constrain: the piece a click would take lights up.
  hoverConstrainTool(sp);
  if (state.dragMode === 'none') {
    const hb = state.tool === 'select' ? badgeAt(constraintBadgeBoxes, sp) : null;
    if (hb !== constraintUi.hoverConstraintId) { constraintUi.hoverConstraintId = hb; repaint(); }
  }

  // Measure tool: track the cursor (snapped) so the rubber-band line follows.
  if (state.tool === 'measure' && state.dragMode === 'none') {
    state.hoverWorld = snapPoint(vp.screenToWorld(sp), null);
    repaint();
  }

  // Connect: the object under the cursor shows its connection points, and the
  // rubber band follows what a click would pick.
  hoverConnectionTool(e, sp);

  // An armed pick follows the pointer (a connection bend picked up, a polar
  // array's centre); one that took the move ends it here.
  if (moveArmedPick(e, sp)) return;

  hoverDimensionTool(sp);

  if (state.tool === 'pdf-scale' && state.dragMode === 'none') {
    state.hoverWorld = pdfScaleSnap(vp.screenToWorld(sp), e.altKey);
    repaint();
  }

  hoverPolylineTool(e, sp);

  hoverFloorTool(e, sp);

  hoverLineTool(e, sp);

  hoverRectangleTool(e, sp);

  hoverRectRoomTool(e, sp);

  if (state.tool === 'arc' && state.dragMode === 'none') {
    state.hoverWorld = snapPoint(vp.screenToWorld(sp), null);
    repaint();
  }

  if (state.tool === 'leader' && state.dragMode === 'none') {
    state.hoverWorld = snapDraftPoint(vp.screenToWorld(sp), leaderDraft, { shift: e.altKey, allow45: true, lastSeg: draftSeg(leaderDraft) });
    repaint();
  }

  if (state.tool === 'zoom' && state.dragMode === 'none') {
    state.hoverWorld = vp.screenToWorld(sp);
    if (zoomFrom) repaint();
  }

  hoverPickTool(e, sp);


  if (state.tool === 'mirror' && state.dragMode === 'none') {
    state.hoverWorld = planeSnap(vp.screenToWorld(sp), mirrorPlane, mirrorFrom, e.altKey, { except: null });
    repaint();
  }

  // Offset tool: once a source is picked, preview the parallel copy on the
  // cursor's side.
  hoverOffsetTool(sp);

  // Reflected ceiling plan tool: the view follows the cursor, held in line with its room.
  hoverRcpTool(e, sp);

  // Section tool: live-snap the moving endpoint (H/V/45 from the first point,
  // plus geometry snaps) so the preview and the committed line match.
  if (state.tool === 'section' && state.dragMode === 'none') {
    state.hoverWorld = snapSectionPoint(vp.screenToWorld(sp), e.altKey);
    repaint();
  }

  if (state.tool === 'poly-room' && state.dragMode === 'none') {
    state.hoverWorld = applyPolyRoomSnap(vp.screenToWorld(sp), e.altKey);
    repaint();
  }

  // Bench-poly (salt wall / heated bench): live preview of the segment to the
  // cursor, snapped to other corners (H/V) and 45° (Alt suspends snapping).
  if (state.tool === 'bench-poly' && state.dragMode === 'none') {
    state.hoverWorld = benchPolySnap(vp.screenToWorld(sp), e.altKey);
    repaint();
  }

  // Bench tool over a room: highlight the corner join under the cursor (click
  // swaps which bench wins). Cleared when the cursor leaves a join.
  hoverBenchTool(e, sp);

  if (state.tool === 'repeat' && state.dragMode === 'none') {
    state.hoverWorld = repeatSnap(vp.screenToWorld(sp), e.altKey);
    repaint();
  }

  if (state.tool === 'circle' && state.dragMode === 'none') {
    state.hoverWorld = circleSnap(vp.screenToWorld(sp), e.altKey);
    repaint();
  }

  if (state.tool === 'print-window' && state.printA && state.dragMode === 'none') {
    state.hoverWorld = vp.screenToWorld(sp);
    repaint();
  }

  // Text tool: preview the box (and leader) following the cursor.
  if (state.tool === 'text' && textBoxes.textDraft.a && state.dragMode === 'none') {
    state.hoverWorld = snapPoint(vp.screenToWorld(sp), null);
    repaint();
  }

  if (state.dragMode === 'none') {
    lastScreen = sp;
    return;
  }
  const world = vp.screenToWorld(sp);
  stepDrag(sp);
  if (DRAG_LIFE[state.dragMode].move?.(e, sp, world, planAim(world, vp.scale, sp, e.altKey))) return;
  lastScreen = sp;
});

// ---- the window mouse-move before a drag: the tools' hover feedback, in the order the handler runs them ----

function moveRefuseFixedDrag(e: MouseEvent): boolean {
  if (COPY_DRAG_MODES.has(state.dragMode) && e.buttons === 1 && !(e.ctrlKey || e.metaKey) && state.tool === 'select') {
    const fixed = [...selectedObjectIds(), ...planMoveCarry().map((c) => c.id)].find((id) => isFixedObject(id));
    if (fixed) {
      endPlanMove('cancel');
      dropDrag();
      constraintFlash(tr('{obj} is fixed in place.').replace('{obj}', constraintObjName(fixed)));
      return true;
    }
  }
  return false;
}

function moveCaptureCtrlCopy(e: MouseEvent): void {
  if (!(e.ctrlKey || e.metaKey) || e.buttons !== 1 || !COPY_DRAG_MODES.has(state.dragMode) || state.tool !== 'select') return;
  // Its move session takes the copy (and leaves it).
  planMove?.session.takeCopy();
}

function moveUnderWay(e: MouseEvent, sp: Vec2): boolean {
  return moveUnderWayAt({ space: 'plan', at: sp, alt: e.altKey }) && state.dragMode !== 'pan';
}

function hoverConstrainTool(sp: Vec2): void {
  if (state.tool === 'constrain' && state.dragMode === 'none') {
    if (constraintUi.constrainCycle && Math.hypot(sp.x - constraintUi.constrainCycle.sp.x, sp.y - constraintUi.constrainCycle.sp.y) > 4) constraintUi.constrainCycle = null;
    const hp = constraintPieceAt(vp.screenToWorld(sp), sp);
    if (hp?.ref !== constraintUi.constrainHover?.ref) { constraintUi.constrainHover = hp; setConstraintSnapMarker(hp); repaint(); }
  }
}

function hoverConnectionTool(e: MouseEvent, sp: Vec2): void {
  if (state.tool === 'connection' && state.dragMode === 'none') {
    const w = vp.screenToWorld(sp);
    const pick = connPickAt(w, e.altKey);
    state.connHoverDots = connPortsAt(w);
    if (connectionUi.connDraft) connectionUi.connDraft.hover = pick.id ? pick : { ...pick, p: connDraftSnap(pick.p, e.altKey) };
    repaint();
  }
}

function hoverDimensionTool(sp: Vec2): void {
  if (state.tool === 'dimension' && state.dragMode === 'none') {
    const w = vp.screenToWorld(sp);
    // Snap while picking the two endpoints; the offset point is free.
    if (state.pendingA && state.pendingB) {
      state.hoverWorld = w;
      state.snapIndicator = null;
      snapSetUi.dimAlignVerts = [];
    } else {
      // Both endpoints align to the other dimensions' vertices; the second also to
      // the first pick (ortho/polar too, when on). The free offset point does not.
      state.hoverWorld = snapDimPoint(w, state.pendingA).point;
    }
    repaint();
  }
}

function hoverPolylineTool(e: MouseEvent, sp: Vec2): void {
  if ((state.tool === 'polygon' || state.tool === 'wall' || state.tool === 'revision-cloud') && state.dragMode === 'none') {
    const wpt = vp.screenToWorld(sp);
    const nv = !drawingEditor.drawingEditId && !e.altKey ? nearbyOrthoVerts(wpt) : [];
    state.drawSnapVerts = nv;
    state.hoverWorld = state.tool === 'polygon'
      ? snapDraftPoint(wpt, drawingEditor.drawingEditId ? [...polyDraft, ...editorShapeSnapVerts()] : [...polyDraft, ...nv], { shift: e.altKey, allow45: true, lastSeg: draftSeg(polyDraft) })
      : state.tool === 'wall'
        ? snapDraftPoint(wpt, [...wallDraft, ...nv], { shift: e.altKey, allow45: true, lastSeg: draftSeg(wallDraft) })
        : snapDraftPoint(wpt, [...revCloudDraft, ...nv], { shift: e.altKey, allow45: true, lastSeg: draftSeg(revCloudDraft) });
    updateDistEntry();
    repaint();
  }
}

function hoverFloorTool(e: MouseEvent, sp: Vec2): void {
  if (floorToolKind(state.tool) && state.dragMode === 'none') {
    const wpt = vp.screenToWorld(sp);
    const nv = e.altKey ? [] : nearbyOrthoVerts(wpt);
    state.drawSnapVerts = nv;
    state.hoverWorld = snapDraftPoint(wpt, [...floorDraft, ...nv], { shift: e.altKey, allow45: true, lastSeg: draftSeg(floorDraft) });
    updateDistEntry();
    repaint();
  }
}

function hoverLineTool(e: MouseEvent, sp: Vec2): void {
  if (state.tool === 'line' && state.dragMode === 'none') {
    state.hoverWorld = lineSnap(vp.screenToWorld(sp), e.altKey);
    updateDistEntry();
    repaint();
  }
}

function hoverRectangleTool(e: MouseEvent, sp: Vec2): void {
  if (state.tool === 'rectangle' && state.dragMode === 'none') {
    const w = vp.screenToWorld(sp);
    state.hoverWorld = squareWithShift(rectStart, snapDraftPoint(w, rectStart ? [rectStart] : [], { shift: e.altKey, noConstrain: true, lastSeg: rectStart ? [rectStart, rectStart] : undefined }), e.shiftKey);
    updateDistEntry();
    repaint();
  }
}

function hoverRectRoomTool(e: MouseEvent, sp: Vec2): void {
  if (state.tool === 'rect-room' && state.dragMode === 'none') {
    const w = vp.screenToWorld(sp);
    state.hoverWorld = squareWithShift(rectRoomStart, snapDraftPoint(w, rectRoomStart ? [rectRoomStart] : [], { shift: e.altKey, noConstrain: true, lastSeg: rectRoomStart ? [rectRoomStart, rectRoomStart] : undefined }), e.shiftKey);
    repaint();
  }
}

function hoverPickTool(e: MouseEvent, sp: Vec2): void {
  if (placeOf(state.tool).picks && state.dragMode === 'none') {
    state.hoverWorld = pickSnap(vp.screenToWorld(sp), e.altKey);
    if (pickBase) { const to = nextPoint(); if (to) previewPick(to); }
    updateDistEntry();
    repaint();
  }
}

function hoverRcpTool(e: MouseEvent, sp: Vec2): void {
  if (rcpToolKind() === null || state.dragMode !== 'none') return;
  state.hoverWorld = vp.screenToWorld(sp);
  rcpFree = e.altKey;
  state.rcpGuide = rcpPlacement(state.hoverWorld, rcpFree)?.guide ?? null;
  repaint();
}

function hoverOffsetTool(sp: Vec2): void {
  if (state.tool === 'offset' && state.dragMode === 'none') {
    if (modifyTools.modifyState?.first) {
      modifyTools.modifyState.preview = offsetPreview(snapPoint(vp.screenToWorld(sp), null));
    }
    repaint();
  }
}

/** The Bench tool's corner join under the pointer (pulsing green): the click
 *  swaps it. */
function setHoverBenchJoint(jt: RenderOptions['benchJoint']): void {
  const key = (j: RenderOptions['benchJoint']): string => (j ? JSON.stringify(j) : '');
  if (key(jt) === key(hoverBenchJoint)) return;
  hoverBenchJoint = jt;
  repaint();
}

/** The Bench tool's area at `aim` (its row's, ui/tools3d.ts): the bench a click
 *  adds or removes, or — on a corner join, which a click steps on — none, the
 *  join lit instead. */
export function benchAreaAt(aim: ToolAim): BenchHover | null {
  const b = benchToolHoverAt(aim);
  setHoverBenchJoint(b.joint ?? null);
  return b.hover;
}

/** The Door tool's area: the door a click adds — not while a drawn wall is
 *  selected: it takes the click first. */
export function doorAreaAt(aim: ToolAim): BenchHover | null {
  const d = selectedBandWall() ? null : doorToAdd(roomsToEdit(), aim.world, paneReach(aim));
  return d ? { instId: d.room.id, polys: doorToAddPolys(d), kind: 'add' } : null;
}

/** The Inner-wall tool's area: the partition a press takes hold of. */
export function innerWallAreaAt(aim: ToolAim): BenchHover | null {
  return innerWallHoverAt(aim);
}

/** What a click of the tool armed would do where the plan's pointer is: the
 *  room tool's area (its row's `area`, the one the 3D view shows too), the
 *  Channel tool's (the plan's alone), an armed pick's (Pick on plan: the wall,
 *  blue). */
function planToolAreaAt(e: MouseEvent, sp: Vec2): BenchHover | null {
  const world = vp.screenToWorld(sp), aim = planAim(world, vp.scale, sp, e.altKey);
  const area = tool3dRowOf(state.tool)?.area;
  if (area) return area(e, aim);
  if (state.tool === 'light-channel') return channelHoverAt(e, world);
  return armed()?.lit?.(world) ?? null;
}

function hoverBenchTool(e: MouseEvent, sp: Vec2): void {
  {
    // Bright-green highlight of the clickable add-bench area under the cursor
    // (suppressed while a corner join is highlighted — that click swaps instead).
    let addHover: BenchHover | null = null;
    if (state.dragMode === 'none') addHover = planToolAreaAt(e, sp);
    else {
      setHoverBenchJoint(null);
      if (state.dragMode === 'support-row') addHover = state.benchAddHover;
      else if (state.dragMode === 'ceiling-arrow') {
        // The wall the arrow would be dropped on — none for a click, or back on the arrow.
        const room = ceilingPickRoom();
        addHover = room ? ceilingWallHover(room, ceilingArrowDropAt(sp)) : null;
      }
    }
    const hKey = (h: typeof addHover): string => (h ? `${h.instId}:${h.kind}:${JSON.stringify(h.polys)}` : '');
    if (hKey(addHover) !== hKey(state.benchAddHover)) {
      state.benchAddHover = addHover;
      repaint();
    }
    // Guard-cover hover on the selected heater: blue over a bare gap edge (click
    // builds a cover there), red over an existing cover side (click clears it).
    const hc = state.dragMode === 'none' ? heaterCoverHoverAt(sp) : null;
    const cKey = (h: typeof hc): string => (h ? `${h.instId}:${h.side}:${h.kind}` : '');
    if (cKey(hc) !== cKey(state.heaterCoverHover)) {
      state.heaterCoverHover = hc;
      repaint();
    }
    // Snap symbol on the vertex under the cursor: grabbing it makes THAT vertex the
    // point that snaps to the grid and to other objects (drawn as an endpoint mark).
    // Computed BEFORE the cursor below, which reads state.vertexGrabHover.
    const wv = vp.screenToWorld(sp);
    // ONE stack per move, built only when something needs its head (and only
    // over the drawing): the grab marker and the hover outline share it.
    let stackHead: ObjRef | null | undefined;
    const headAt = (): ObjRef | null => (stackHead === undefined ? (stackHead = objectStackAt(wv, sp).head) : stackHead);
    const vGrab = state.tool === 'select' && state.dragMode === 'none' && e.target === canvas
      ? nearestVertexWithin(wv, dragGrabVerts(wv, headAt), SNAP_PX / vp.scale)
      : null;
    const cur = state.vertexGrabHover;
    if ((!!vGrab !== !!cur) || (vGrab && cur && (vGrab.x !== cur.x || vGrab.y !== cur.y))) {
      state.vertexGrabHover = vGrab;
      repaint();
    }
    // The selection's handle under the pointer, asked once for the hover and the
    // cursor — the press asks the same query (`handleAt`).
    const hit = state.tool === 'select' && state.dragMode === 'none' ? handleAt(sp, wv) : null;
    // The pointer names the action a press would start. The select tool resolves it
    // through selectCursor (resize / grab / move); other tools keep the wall-joint
    // cue only (their crosshair is set by setTool).
    // Hover highlight: what a Select click here would pick. Only over the canvas
    // itself, with no button down, and where the mode edited in place keeps the
    // plan's hover (EDIT_RULES: a press means something else in the others).
    const hv = state.tool === 'select' && state.dragMode === 'none' && e.buttons === 0 && e.target === canvas && !spaceHeld
      && editAllows(state.editing, 'hover') && !constraintUi.hoverConstraintId && !armedTakesClick()
      ? hoverTargetAt(wv, sp, hit, headAt) : null;
    if ((hv?.key ?? '') !== (canvasHover?.key ?? '')) repaint();
    setCanvasHover(hv);
    if (state.dragMode === 'none') {
      canvas.style.cursor = spaceHeld ? 'grab'
        : armedCursor() ? armedCursor()
        : state.tool === 'select' && constraintUi.hoverConstraintId ? 'pointer'
        : state.tool === 'select'
        ? selectCursor(sp, hit)
        : (wallSpanJointAt(sp) ? 'move' : 'default');
    }
  }
}

// ---- the window mouse-move while a drag is live: one function per drag mode, dispatched through DRAG_LIFE ----

/** A drag mode's life after the press that starts it (*The drag*,
 *  docs/claude/tools.md). */
interface DragRow {
  /** A pointer move: true when it handled the move (the handler stops there);
   *  null: nothing here (handled before the chain, or not at all). */
  move: ((e: MouseEvent, sp: Vec2, world: Vec2, aim: ToolAim) => boolean) | null;
  /** The release: what the drag leaves behind. 'own' when it ended itself (an
   *  armed grip or handle keeps its gesture open, a polygon pick takes the
   *  marquee's selection) and nothing else of the release runs. */
  end?: (e: MouseEvent, aim: ToolAim) => 'own' | void;
  /** Put back by Esc, undo or redo, another tool or view. Absent: the drawing goes
   *  back to where the press began (`cancelGesture`). */
  cancel?: () => void;
  /** The held phase of a work a click may also arm (a grip's, a section
   *  handle's): put back, that work is — its row in the gesture under way
   *  (underWay.ts), never a cancel naming it. */
  work?: WorkKind;
  /** The objects it changes as it goes, which the 3D view builds again live (with
   *  what follows them, sceneChanges.ts) while it is dragged there (ui/tools3d.ts).
   *  Absent: it changes nothing until its release — a 3D drag's ghost shows what
   *  it would add. */
  live3d?: () => readonly string[];
}

/** Each drag's life, by its mode: what a move, its release and a cancel do. Every
 *  mode has a row, so a new DragMode does not typecheck until it says. */
const DRAG_LIFE: Record<DragMode, DragRow> = {
  'bench-edit': { move: dragMoveBenchEdit, end: endBenchEditDrag },
  'ceiling-arrow': { move: null, end: endCeilingArrowDrag, cancel: cancelCeilingArrowDrag },
  'channel-move': { move: dragMoveChannel, end: endChannelDrag, cancel: () => { cancelGesture(); endChannelDrag(); } },
  'conn-label': { move: dragMoveConnLabel },
  'cube-move': { move: dragMoveCubeMove },
  'dim-offset': { move: dragMoveDimOffset, end: renderProperties },
  door: { move: dragMoveDoor },
  'door-resize': { move: dragMoveDoorResize },
  'draw-edit-move': { move: dragMoveDrawEditMove },
  'draw-edit-resize': { move: dragMoveDrawEditResize },
  'draw-edit-vertex': { move: dragMoveDrawEditVertex },
  glass: { move: dragMoveGlass, end: endGlassDrag },
  grip: { move: dragMoveGrip, end: endGripDrag, work: 'grip' },
  handle: { move: null, end: endSectionHandleDrag, work: 'handle' },
  'inner-wall': { move: dragMoveInnerWall, end: endInnerWallDrag },
  'inner-wall-move': { move: dragMoveInnerWallMove, end: endInnerWallMoveDrag, live3d: () => (drag.innerWallMove ? [drag.innerWallMove.instId] : []) },
  'light-tag': { move: dragMoveLightTag, end: () => { lightTools.lightTagDrag = null; renderProperties(); }, cancel: () => { cancelGesture(); lightTools.lightTagDrag = null; } },
  marquee: { move: dragMoveMarquee, end: endMarquee, cancel: noDrawingChange },
  move: { move: dragMoveMove, end: (e) => { relateDropTarget(e); endPlanMove('keep', e); }, cancel: () => { endPlanMove('cancel'); } },
  'move-all': { move: dragMoveMoveAll, end: (e) => { endPlanMove('keep', e); }, cancel: () => { endPlanMove('cancel'); } },
  'move-bg': { move: dragMoveMoveBg },
  'move-floor': { move: dragMoveMoveFloor, end: (e) => { endPlanMove('keep', e); }, cancel: () => { endPlanMove('cancel'); } },
  'move-floor-drain': { move: dragMoveMoveFloorDrain },
  'move-node': { move: dragMoveMoveNode, end: renderProperties },
  'move-pdf': { move: dragMoveMovePdf },
  'move-table': { move: dragMoveMoveTable, end: renderProperties },
  'move-text': { move: dragMoveMoveText, end: (e) => { endPlanMove('keep', e); }, cancel: () => { endPlanMove('cancel'); } },
  'move-text-leader': { move: dragMoveMoveTextLeader },
  none: { move: null },
  opening: { move: dragMoveOpening, end: endOpeningDrag },
  pan: { move: dragMovePan, cancel: noDrawingChange },
  'plane-move': { move: dragMovePlane, end: endPlaneDrag, cancel: cancelPlaneDrag },
  'poly-room-vertex': { move: dragMovePolyRoomVertex },
  'rcp-move': { move: dragMoveRcp },
  resize: { move: dragMoveResize },
  'resize-cloud': { move: dragMoveResizeCloud },
  'resize-pdf': { move: dragMoveResizePdf },
  'resize-shape': { move: dragMoveResizeShape },
  'resize-table': { move: dragMoveResizeTable, end: renderProperties },
  'resize-text': { move: dragMoveResizeText },
  rotate: { move: dragMoveRotate },
  'rotate-cloud': { move: dragMoveRotateCloud },
  'rotate-floor-drain': { move: dragMoveRotateFloorDrain },
  'rotate-pdf': { move: dragMoveRotatePdf },
  'rotate-shape': { move: dragMoveRotateShape },
  'rotate-text': { move: dragMoveRotateText, end: renderProperties },
  'sculpt-trestle': { move: dragMoveSculptTrestle, end: endSculptTrestleDrag, cancel: cancelSculptTrestleDrag, live3d: () => (sculptTrestleTool.drag ? [sculptTrestleTool.drag.instId] : []) },
  'section-endpoint': { move: dragMoveSectionEndpoint, end: endSectionDrag, cancel: () => { cancelGesture(); endSectionDrag(); } },
  'section-move': { move: dragMoveSectionMove, end: endSectionDrag, cancel: () => { cancelGesture(); endSectionDrag(); } },
  'section-view-move': { move: dragMoveSectionViewMove, end: endSectionDrag, cancel: () => { cancelGesture(); endSectionDrag(); } },
  'section-view-rotate': { move: dragMoveSectionViewRotate, end: endSectionDrag, cancel: () => { cancelGesture(); endSectionDrag(); } },
  'sky-fibre': { move: dragMoveSkyFibre, end: renderProperties },
  'spot-move': { move: dragMoveSpot, end: endSpotDrag, cancel: () => { cancelGesture(); dropSpotDrag(); }, live3d: () => (spotDrag ? [spotDrag.instId] : []) },
  'support-move': { move: dragMoveSupportMove, end: endSupportMoveDrag, cancel: () => { cancelSupportMove(); }, live3d: () => (benchSupportTool.supportMoveDrag ? [benchSupportTool.supportMoveDrag.instId] : []) },
  'support-row': { move: dragMoveSupportRow, end: endSupportRowDrag, cancel: () => { benchSupportTool.supportRowDrag = null; } },
  vertex: { move: dragMoveVertex },
  'view-title': { move: dragMoveViewTitle, end: renderProperties },
  'wall-feature-add': { move: dragMoveWallFeatureAdd, end: endWallFeatureAddDrag },
  'wall-feature-move': { move: dragMoveWallFeatureMove, end: renderProperties },
  'wall-span': { move: dragMoveWallSpan, end: renderProperties },
};

function dragMovePan(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  vp.pan(sp.x - lastScreen.x, sp.y - lastScreen.y);
  repaint();
  return false;
}

function dragMoveMarquee(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  drag.marqueeEnd = world;
  repaint();
  return false;
}

function dragMovePolyRoomVertex(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drag.polyRoomVertex !== null) {
    const inst = polyRoomEditInst();
    if (inst && inst.polygon) {
      // Align the dragged corner to the horizontal/vertical projection of the
      // room's other corners (same as editing a polygon's vertices). Done in
      // world space so the alignment follows the screen axes.
      const n = inst.polygon.length;
      const others = inst.polygon.filter((_, i) => i !== drag.polyRoomVertex).map((v) => instanceToWorld(inst, v));
      const prev = instanceToWorld(inst, inst.polygon[(drag.polyRoomVertex - 1 + n) % n]);
      const snapped = editVertexSnap(world, others, inst.id, prev, e.altKey);
      const localPt = toInstanceLocal(inst, snapped);
      // With locked walls, solve the whole ring toward the cursor so the room
      // reshapes as a unit keeping every locked wall axis-aligned; otherwise move
      // just the dragged corner.
      // Edge lengths before the reshape, so features anchored along a wall can be
      // kept in proportion to its new length.
      const lensBefore = polyEdgeLengths(inst);
      const roomCons = roomRingCons(inst);
      const solved = ringHasLocks(roomCons) ? gcsDragVertex(inst.polygon, roomCons, drag.polyRoomVertex, localPt) : null;
      // Solved → the whole ring reshaped keeping its constraints. Unconstrained →
      // move the corner freely. Constrained but the solve failed → keep the corner
      // where it is, so we never show geometry that violates the constraints.
      const next = solved ?? (ringHasLocks(roomCons)
        ? null
        : inst.polygon.map((p, i) => (i === drag.polyRoomVertex ? localPt : p)));
      // The figures ON a wall constrain the reshape too: a corner may not drag a
      // wall shorter than its door, panes and pinned runs fit in, nor change one
      // whose every figure is held. The frame is refused rather than clamped, so
      // the room never shows a broken chain and releasing keeps the last valid shape.
      // A reshape that moves EVERY corner has stopped resizing the room and started
      // carrying it, so it is clamped at the last shape that still held an anchor.
      const broke = next ? roomReshapeBreak(inst, next) : null;
      const slides = !!next && broke == null && !reshapeKeepsAnchor(inst.polygon, next);
      if (next && broke == null && !slides) {
        inst.polygon = next;
        drag.reshapeBlock = null;
        recenterPolyRoom(inst); // keep the bounding box centred on the reshaped room
        followVertices(state.drawing, inst.id, () => rescaleEdgeFeatures(inst, lensBefore)); // glass/openings/partitions follow the wall
        regenerateSauna(inst);
      } else if (next && (broke != null || slides)) {
        blockReshape(inst, broke);
      }
      invalidate();
    }
  }
  return false;
}

function dragMoveConnLabel(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const c = drag.connLabel ? state.drawing.connections.find((x) => x.id === drag.connLabel) : undefined;
  const g = c ? connGeomOf(c) : null;
  // Kept on the line: the cursor's nearest point along the route, stored as a
  // fraction of its length (just off the ends, clear of the objects there).
  if (c && g) { connEdit(c, tagDragEdit(g, world)); invalidate(); }
  return false;
}

function dragMoveGrip(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (gripUi.gripWork) { gripUi.gripWork.pointer = sp; gripUi.gripWork.alt = e.altKey; stepGripWork(); }
  return false;
}

function dragMoveVertex(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const shape = editingVertexShape();
  if (shape && drag.vertex) {
    // Snap to nearby objects, then align to the horizontal/vertical projection
    // of the shape's other vertices (so edges stay square with its corners).
    const ring = shapeRings(shape)[drag.vertex.ring];
    // A closed wall's joint is two points: its twin moves with it, so it is no target.
    const twin = drag.vertex.closed ? (drag.vertex.index === 0 ? ring.length - 1 : drag.vertex.index === ring.length - 1 ? 0 : -1) : -1;
    const others = otherRingVertices(shape, drag.vertex.ring, drag.vertex.index).filter((v) => twin < 0 || v !== ring[twin]);
    if (ring) {
      // 45° anchor = an adjacent vertex. A closed polygon ring wraps; an open
      // wall/line must not wrap (vertex 0's neighbour is vertex 1, not the last).
      const idx = drag.vertex.index, closed = isPolygonShape(shape);
      const prev = closed ? ring[(idx - 1 + ring.length) % ring.length] : (ring[idx - 1] ?? ring[idx + 1]);
      let p = editVertexSnap(world, others, shape.id, prev, e.altKey);
      // A wall snaps by its faces too. Measured where the cursor puts the node,
      // before any snap: when one of its face corners is nearer a vertex than the
      // node is, that corner lands on it (both would be endpoint snaps, so the
      // nearer one is the one being aimed).
      if ('thickness' in shape && !e.altKey && state.snapEnabled && drag.vertex.ring === 0) {
        if (!state.moveSnapTargets) state.moveSnapTargets = snapCandidates(withConstrainedPartners(shape.id)).map((c) => c.p);
        const raw = gsnapPt(world);
        const nodeDist = Math.min(Infinity, ...state.moveSnapTargets.map((t) => Math.hypot(t.x - raw.x, t.y - raw.y)));
        const fs = wallFaceSnap(shape as Wall, drag.vertex.index, raw, state.moveSnapTargets, 12 / vp.scale, nodeDist);
        if (fs) { p = fs.node; state.snapIndicator = fs.at; state.snapIndicatorKind = 'endpoint'; }
      }
      // On a polygon's locked outer ring, solve the whole ring toward the
      // cursor so every locked edge stays axis-aligned; otherwise just keep the
      // dragged vertex on its own incident locked axes. A wall / line holds
      // only its pinned segment lengths (below).
      const isPolyRing0 = isPolygonShape(shape) && drag.vertex.ring === 0;
      const polyCons = isPolyRing0 ? polyRingCons(shape as Polygon) : {};
      const solvedRing = isPolyRing0 && ringHasLocks(polyCons) ? gcsDragVertex(ring, polyCons, drag.vertex.index, p) : null;
      if (solvedRing) { setShapeRing(shape, drag.vertex.ring, solvedRing); }
      else if (!ringHasLocks(polyCons)) {
        // Unconstrained ring (or a wall/line): move the vertex freely. A
        // constrained ring whose solve failed keeps its last valid position
        // instead — never show geometry that violates its constraints.
        // A wall / line end dropped on its other end closes it; a closed one's
        // joint moves as one.
        // A wall / line's pinned segments keep their length: the vertex slides
        // round the far end of each (and stays put where two cannot meet).
        if (isPolygonShape(shape)) ring[drag.vertex.index] = p;
        else dragPinnedVertex({ points: ring, segLengths: (shape as Wall).segLengths }, drag.vertex.index, p, !!drag.vertex.closed, 12 / vp.scale);
        setShapeRing(shape, drag.vertex.ring, ring);
      }
    }
    invalidate();
  }
  return false;
}

function dragMoveMoveTable(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const t = drag.table ? tableFor(drag.table.kind) : undefined;
  if (t && drag.table) {
    t.x = drag.table.x + (world.x - drag.table.grab.x);
    t.y = drag.table.y + (world.y - drag.table.grab.y);
    if (state.editingCell) closeCellEditor(false);
    invalidate();
  }
  return false;
}

function dragMoveResizeTable(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const t = drag.table ? tableFor(drag.table.kind) : undefined;
  if (t && drag.table) {
    const dxWorld = world.x - drag.table.grab.x;
    const minW = 220 / vp.scale; // matches the render minimum
    if (drag.table.handle === 1) {
      t.width = Math.max(minW, drag.table.width + dxWorld);
    } else {
      // Left edge: move the anchor, keep the right edge fixed.
      const right = drag.table.x + drag.table.width;
      t.x = Math.min(right - minW, drag.table.x + dxWorld);
      t.width = right - t.x;
    }
    if (state.editingCell) closeCellEditor(false);
    invalidate();
  }
  return false;
}

function dragMoveMoveText(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const t = (state.drawing.texts || []).find((x) => x.id === state.selectedTextId), session = planMove?.session;
  if (t && session) {
    // Its anchor on the grid, then its edges aligned to other text boxes'
    // projections (Alt suspends) — level boxes only: a turned box has no
    // horizontal / vertical edges to align.
    const aligns = !e.altKey && !t.rotation;
    if (!aligns) state.alignGuides = [];
    const { d } = moveStep({
      ...moveFrom(world, textMoveStart), grabbed: false, ref: textMoveStart, probes: () => [], vertexSnap: null,
      align: aligns ? (o) => {
        const nx = textMoveStart.x + o.x, ny = textMoveStart.y + o.y;
        const a = alignMoveSnap({ x0: nx, x1: nx + t.w, y0: ny - t.h, y1: ny }, otherTextRects(t.id), ALIGN_TOL_PX / vp.scale);
        return { x: a.dx, y: a.dy };
      } : null,
    });
    // The box alone: its leader's arrow stays on what it points at.
    session.own(() => { t.x = textMoveStart.x + d.x; t.y = textMoveStart.y + d.y; });
    invalidate();
  }
  return false;
}
function dragMoveRotateText(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  // The knob sits off the box's top edge, square to it: the box's rotation is the
  // cursor's bearing from the centre, less 90°. Free, 15° steps with Shift.
  const t = (state.drawing.texts || []).find((x) => x.id === state.selectedTextId);
  if (t) {
    const c = textBoxCentre(t);
    const bearing = (Math.atan2(world.y - c.y, world.x - c.x) * 180) / Math.PI;
    rotateTextBoxTo(t, snapRotationDeg(bearing - 90, e.shiftKey));
    invalidate();
  }
  return false;
}

function dragMoveResizeText(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const t = (state.drawing.texts || []).find((x) => x.id === state.selectedTextId);
  if (t && drag.textResizeHandle) {
    const h = resizeHandleSnap(world, t.id, e.altKey);
    resizeText(t, drag.textResizeHandle, h.snapped ? h.p : world, e.altKey || h.snapped);
    renderProperties();
    invalidate();
  }
  return false;
}

function dragMoveMoveTextLeader(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const t = (state.drawing.texts || []).find((x) => x.id === state.selectedTextId);
  if (t?.leader) {
    t.leader = snapPoint(world, null); // OSNAP to vertices, as when the arrow was placed
    invalidate();
  }
  return false;
}

function dragMoveMovePdf(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const bi = editedBg();
  if (bi) {
    const raw = { x: gsnap(moveInstanceStart.x + (world.x - moveStartWorld.x)), y: gsnap(moveInstanceStart.y + (world.y - moveStartWorld.y)) };
    // Align a corner of the underlay to another object's vertex (H/V), like every
    // other draw/move tool; the grid step is applied first, then this offset.
    const c = alignMovedUnderlay(raw, (centre) => pdfBoxCorners({ ...bi, x: centre.x, y: centre.y }), e.altKey);
    bi.x = c.x; bi.y = c.y;
    invalidate();
  }
  return false;
}

function dragMoveMoveBg(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const raw = { x: gsnap(moveInstanceStart.x + (world.x - moveStartWorld.x)), y: gsnap(moveInstanceStart.y + (world.y - moveStartWorld.y)) };
  // The DXF plan's corners are its bounds shifted by the candidate offset.
  const b = state.drawing.background!.bounds;
  const cornersAt = (o: Vec2): Vec2[] => [
    { x: b.min.x + o.x, y: b.min.y + o.y }, { x: b.max.x + o.x, y: b.min.y + o.y },
    { x: b.max.x + o.x, y: b.max.y + o.y }, { x: b.min.x + o.x, y: b.max.y + o.y },
  ];
  state.drawing.backgroundOffset = alignMovedUnderlay(raw, cornersAt, e.altKey);
  invalidate();
  return false;
}

function dragMoveMoveFloor(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const u = planMove;
  if (u && selectedFloor()) {
    // Its first vertex on the grid (a floor snaps to nothing else); its drain with it.
    const { d } = moveStep({ ...moveFrom(world, u.ref), grabbed: false, ref: u.ref, probes: () => [], vertexSnap: null });
    u.session.moveTo(d);
    invalidate();
  }
  return false;
}
function dragMoveMoveFloorDrain(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const floor = selectedFloor();
  if (floor && floor.drain) {
    // Grid-snap the cursor, then confine the drain's centre inside the floor.
    const c = confineToPolygon(gsnapPt(world), floor.points);
    floor.drain.x = c.x; floor.drain.y = c.y;
    updateFloorDrainDims(floor);
    invalidate();
  }
  return false;
}

function dragMoveRotateFloorDrain(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const floor = selectedFloor();
  if (floor && floor.drain) {
    const deg = (Math.atan2(world.y - floor.drain.y, world.x - floor.drain.x) * 180) / Math.PI - 90;
    floor.drain.rotation = ((snapRotationDeg(deg, e.shiftKey) % 360) + 360) % 360;
    invalidate();
  }
  return false;
}

function dragMoveResizePdf(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drag.pdfResizeHandle) {
    resizePdf(drag.pdfResizeHandle, resizeHandleSnap(world, editedBg()?.id ?? null, e.altKey).p);
    invalidate();
  }
  return false;
}

function dragMoveRotatePdf(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const bi = editedBg();
  if (bi) {
    const ang = Math.atan2(world.y - rotatePivot.y, world.x - rotatePivot.x);
    const deg = snapRotationDeg(pdfRotateStart + ((ang - rotateStartAngle) * 180) / Math.PI, e.shiftKey);
    bi.rotation = ((deg % 360) + 360) % 360;
    invalidate();
  }
  return false;
}

function dragMoveDimOffset(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drag.dimOffset) {
    const d = state.drawing.dimensions.find((x) => x.id === drag.dimOffset!.id);
    if (d) {
      const len = distance(d.a, d.b);
      if (len > 1e-6) {
        // Perpendicular (normal) of the measured segment, matching dimensionGeometry.
        const nx = -(d.b.y - d.a.y) / len, ny = (d.b.x - d.a.x) / len;
        d.offset = drag.dimOffset.origOffset + (world.x - drag.dimOffset.grab.x) * nx + (world.y - drag.dimOffset.grab.y) * ny;
        invalidate();
      }
    }
  }
  return false;
}

/** A drag of a FIXED section's line, ends or drawing moves nothing (and says so). */
function sectionDragHeld(id: string): boolean {
  if (!isFixedObject(id)) return false;
  constraintFlash(tr('{obj} is fixed in place.').replace('{obj}', constraintObjName(id)));
  return true;
}

function dragMoveSectionMove(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drag.sectionMove) {
    const s = (state.drawing.sections || []).find((x) => x.id === drag.sectionMove!.id);
    if (s && !sectionDragHeld(s.id)) {
      const dx = gsnap(drag.sectionMove.a.x + (world.x - drag.sectionMove.grab.x)) - drag.sectionMove.a.x;
      const dy = gsnap(drag.sectionMove.a.y + (world.y - drag.sectionMove.grab.y)) - drag.sectionMove.a.y;
      s.a = { x: drag.sectionMove.a.x + dx, y: drag.sectionMove.a.y + dy };
      s.b = { x: drag.sectionMove.b.x + dx, y: drag.sectionMove.b.y + dy };
      invalidate();
    }
  }
  return false;
}

function dragMoveRcp(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const v = drag.rcpView ? (state.drawing.ceilingPlans || []).find((x) => x.id === drag.rcpView!.id) : undefined;
  const room = v ? rcpRoom(v) : null;
  if (v && room) {
    const want = { x: drag.rcpView!.offset0.x + (world.x - drag.rcpView!.grab.x), y: drag.rcpView!.offset0.y + (world.y - drag.rcpView!.grab.y) };
    const r = rcpSnapOffset(want, room, e.altKey);
    v.offset = r.offset;
    state.rcpGuide = r.guide;
    invalidate();
  }
  return false;
}

/** A press on a fibre of the selected starry sky drags that fibre (one gesture). */
function downSkyFibre(world: Vec2, head: ObjRef | null): boolean {
  if (head?.kind !== 'fixture' || !head.instId || featureOf(state, 'fixture')?.id !== head.id || state.selectedId !== head.instId) return false;
  const room = state.drawing.instances.find((i) => i.id === head.instId);
  const sky = room ? skiesOf(room).find((s) => s.id === head.id) : undefined;
  const hit = room && sky ? skyFibreAt(room, toInstanceLocal(room, world), 5 / vp.scale) : null;
  if (!room || !sky || hit?.sky !== sky) return false;
  beginGesture();
  drag.skyFibre = { instId: room.id, lightId: sky.id, i: hit.fibre.i };
  startDrag('sky-fibre');
  return true;
}

/** A dragged fibre stays where it is left (`moved`), under the room's ceiling. */
function dragMoveSkyFibre(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const d = drag.skyFibre;
  const room = d ? state.drawing.instances.find((i) => i.id === d.instId) : undefined;
  const sky = room && d ? skiesOf(room).find((s) => s.id === d.lightId) : undefined;
  if (!room || !sky || !d) return false;
  const p = toInstanceLocal(room, world);
  if (!pointInPolygon(p, polyInnerCarved(room))) return false;
  (sky.moved ??= {})[d.i] = { x: Math.round(p.x), y: Math.round(p.y) };
  invalidate();
  return false;
}

function dragMoveSectionViewMove(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drag.sectionView) {
    const s = (state.drawing.sections || []).find((x) => x.id === drag.sectionView!.id);
    if (s) {
      if (Math.hypot(world.x - drag.sectionView.grab.x, world.y - drag.sectionView.grab.y) * vp.scale > 4 && sectionDragHeld(s.id)) return false;
      if (isFixedObject(s.id)) return false;
      s.viewOrigin = {
        x: gsnap(drag.sectionView.origin.x + (world.x - drag.sectionView.grab.x)),
        y: gsnap(drag.sectionView.origin.y + (world.y - drag.sectionView.grab.y)),
      };
      invalidate();
    }
  }
  return false;
}

function dragMoveSectionViewRotate(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drag.sectionView) {
    const s = (state.drawing.sections || []).find((x) => x.id === drag.sectionView!.id);
    if (s) {
      if (sectionDragHeld(s.id)) return false;
      const a0 = Math.atan2(drag.sectionView.grab.y - drag.sectionView.pivot.y, drag.sectionView.grab.x - drag.sectionView.pivot.x);
      const a1 = Math.atan2(world.y - drag.sectionView.pivot.y, world.x - drag.sectionView.pivot.x);
      s.viewAngle = snapRotationRad(drag.sectionView.angle0 + (a1 - a0), e.shiftKey);
      invalidate();
    }
  }
  return false;
}

function dragMoveSectionEndpoint(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drag.sectionEndpoint) {
    const s = (state.drawing.sections || []).find((x) => x.id === drag.sectionEndpoint!.id);
    if (s && !sectionDragHeld(s.id)) {
      const fixed = drag.sectionEndpoint.end === 'a' ? s.b : s.a;
      // Snap the dragged end to the horizontal / vertical / 45° projections of the
      // fixed end (and nearby geometry), like drawing a section's second point,
      // plus the other sections' endpoints — this one's own are excluded, since
      // the end being dragged is itself one of them.
      const nv = e.altKey ? [] : nearbyOrthoVerts(world);
      state.drawSnapVerts = nv;
      const sv = e.altKey ? [] : sectionLineVertices(shownSections(), s.id);
      sectionAlignVerts = sv;
      s[drag.sectionEndpoint.end] = snapDraftPoint(world, [fixed, ...nv, ...sv], { shift: e.altKey, allow45: true, lastSeg: [fixed, fixed] });
      invalidate();
    }
  }
  return false;
}

function dragMoveWallSpan(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drag.wallSpan) {
    dragWallSpanJoint(drag.wallSpan, world);
  }
  return false;
}

function dragMoveResizeShape(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const poly = resizablePoly();
  if (poly && drag.shapeResizeHandle && drag.shapeResizeBBox) {
    resizePolyTo(poly, drag.shapeResizeHandle, shapePointsStart, shapeHolesStart, drag.shapeResizeBBox, resizeHandleSnap(world, poly.id, e.altKey).p);
    renderProperties();
    invalidate();
  }
  return false;
}

function dragMoveRotateShape(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const shape = selectedShape();
  if (shape) {
    const ang = Math.atan2(world.y - rotatePivot.y, world.x - rotatePivot.x);
    const delta = snapRotationRad(ang - rotateStartAngle, e.shiftKey);
    shape.points = shapePointsStart.map((p) => rotateAround(p, rotatePivot, delta));
    transformHoles(shape, shapeHolesStart, (p) => rotateAround(p, rotatePivot, delta));
    invalidate();
  }
  return false;
}

function dragMoveResizeCloud(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const c = singleSelectedCloud();
  if (c && drag.shapeResizeHandle && drag.shapeResizeBBox) {
    c.points = shapePointsStart.map(bboxScaler(drag.shapeResizeHandle, drag.shapeResizeBBox, resizeHandleSnap(world, c.id, e.altKey).p));
    invalidate();
  }
  return false;
}

function dragMoveRotateCloud(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const c = singleSelectedCloud();
  if (c) {
    const ang = Math.atan2(world.y - rotatePivot.y, world.x - rotatePivot.x);
    const delta = snapRotationRad(ang - rotateStartAngle, e.shiftKey);
    c.points = shapePointsStart.map((p) => rotateAround(p, rotatePivot, delta));
    invalidate();
  }
  return false;
}

function dragMoveMoveAll(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const u = planMove;
  if (!u) return false;
  state.centroidGuides = [];
  // The objects sit `applied` from where they started (the last frame's step);
  // a candidate offset is measured from the start.
  const a = u.session.applied, ms = u.session.movables;
  const at = (d: Vec2, pts: Vec2[]): Vec2[] => pts.map((p) => ({ x: p.x + d.x - a.x, y: p.y + d.y - a.y }));
  const out = moveStep({
    ...moveFrom(world, u.ref),
    probes: (d) => at(d, ms.flatMap((m) => m.snaps())),
    vertexSnap: (moved, probe) => moveVertexSnap(moved, u.exclude, probe),
    // Their centre on another object's axis.
    align: state.snapEnabled ? (d) => centroidSnapForPoint(centroidOfPts(at(d, ms.flatMap((m) => m.verts()))), u.exclude, SNAP_PX / vp.scale).off : null,
  });
  state.snapIndicator = out.at;
  u.session.moveTo(out.d);
  invalidate();
  return false;
}
function dragMoveMove(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  // A part moves by the one move step (moveStep.ts): a grabbed vertex lands on
  // another object's; else its own snap (corners and centres on each axis,
  // centre on centre) aligns it.
  const grabbed = !!drag.grabbedVertexStart;
  const inst = selectedInstance(), session = planMove?.session;
  if (!inst || !session) return false;
  if (inst.nodes && drag.moveNodesStart.length === inst.nodes.length) {
    // Polyline-bench: all its nodes together, probed at their moved places.
    const nodesAt = (d: Vec2): Vec2[] => drag.moveNodesStart.map((n) => ({ x: n.x + d.x, y: n.y + d.y }));
    state.snapIndicator = null;
    const out = moveStep({
      ...moveFrom(world, moveInstanceStart),
      probes: nodesAt,
      vertexSnap: grabbed ? (moved, probe) => moveVertexSnap(moved, inst.id, probe) : null,
      align: (d) => { const ps = nodesAt(d); return snapMoveOffset(ps, centroidOfPts(ps), movingWith(inst), centroidSkip(inst)); },
    });
    if (out.at) state.snapIndicator = out.at;
    session.own(() => {
      const ns = nodesAt(out.d);
      for (let i = 0; i < ns.length; i++) inst.nodes![i] = ns[i];
      inst.x = moveInstanceStart.x + out.d.x;
      inst.y = moveInstanceStart.y + out.d.y;
    });
    drag.dragGuides = computeDragGuides(inst);
    renderProperties();
    invalidate();
    return true;
  }
  const placeAt = (d: Vec2): void => { inst.x = moveInstanceStart.x + d.x; inst.y = moveInstanceStart.y + d.y; };
  state.snapIndicator = null;
  const out = moveStep({
    ...moveFrom(world, moveInstanceStart),
    probes: (d) => { placeAt(d); return isPolyRoom(inst) ? inst.polygon!.map((p) => instanceToWorld(inst, p)) : instanceProbeCorners(inst); },
    vertexSnap: grabbed ? (moved, probe) => moveVertexSnap(moved, inst.id, probe) : null,
    align: (d) => { placeAt(d); return snapInstanceOffset(inst); },
  });
  if (out.at) state.snapIndicator = out.at;
  if (inst.autoBenches) {
    // A room: back where the session has it (the probes tried it elsewhere),
    // then moved by it, with what stood in it when the drag began.
    placeAt(session.applied);
    session.moveTo(out.d);
    drag.dropTargetId = null;
  } else {
    session.own(() => { placeAt(out.d); seatDragged(inst); });
    // Preview a drop target while dragging a part over another part: on release
    // the two are related. Alt drops it there without relating.
    drag.dropTargetId = e.altKey ? null : partUnder(inst);
  }
  drag.dragGuides = computeDragGuides(inst); // distance to nearby walls/objects
  renderProperties();
  invalidate();
  return false;
}
/** A part being dragged, where the step put it: seated on a wall it is dragged
 *  through, into or against, else flush on its room's nearest surface. */
function seatDragged(inst: ProductInstance): void {
  if (throughWallBand(inst)) {
    seatThroughWall(inst); // through a wall: its room side flush on it, facing in
  } else if (inWallBand(inst) && seatInWall(inst)) {
    // Set into a wall: its front flush out of the face it was dragged to.
  } else if (inst.backOnWall && seatBackOnWall(inst)) {
    // Its back on the wall it was dragged to, its front facing away.
  } else {
    // Any non-room object dragged in a room snaps FLUSH against the nearest
    // interior surface — outer wall, interior partition, hidden heater wall or
    // bench — rotating parallel to it so angled walls work too. Objects are
    // still not confined: out of range, the drag is left alone.
    const room = (traitsOf(inst.category).carvesBenches ? saunaOfHeater(inst) : null)
      ?? saunaAt({ x: inst.x, y: inst.y });
    if (room && traitsOf(inst.category).placedAsHeater) snapHeaterRotation(inst, room);
    if (room && state.snapEnabled) {
      // A heater's air gap sits flush as well as its body: either may meet the wall.
      // A tilted part (an upright junction box) by the box its tilted body fills.
      const he = isTilted(inst) ? instanceHalfExtents(inst) : { hw: inst.width / 2, hd: inst.depth / 2 };
      const body = { x0: -he.hw, y0: -he.hd, x1: he.hw, y1: he.hd };
      const boxes = drawsHeaterBlock(inst) ? [body, heaterGapBox(heaterGuard(inst).gap, inst.width, inst.depth)] : [body];
      const fs = roomFaceSnap(inst, room, (p) => instanceToWorld(room, p), SNAP_PX / vp.scale, boxes);
      if (fs) {
        inst.rotation = fs.rotation;
        inst.x += fs.dx;
        inst.y += fs.dy;
      }
    }
    if (room && traitsOf(inst.category).carvesBenches) regenerateSauna(room);
  }
}

function dragMoveMoveNode(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  state.centroidGuides = [];
  const inst = selectedInstance();
  if (inst && inst.nodes && drag.moveNodeIndex >= 0 && drag.moveNodeIndex < inst.nodes.length) {
    // Align the dragged node to the other nodes (H/V) and to 45° from its
    // neighbour, like every other vertex-drag. constrainDraggedVertex is n/a
    // here: node objects (heated benches / salt walls) carry no locked-edge
    // constraints — editVertexSnap's ortho/45° alignment is the equivalent.
    const others = inst.nodes.filter((_, i) => i !== drag.moveNodeIndex);
    const prev = inst.nodes[drag.moveNodeIndex - 1] ?? inst.nodes[drag.moveNodeIndex + 1];
    const snapped = editVertexSnap(world, others, inst.id, prev, e.altKey);
    inst.nodes[drag.moveNodeIndex] = { x: snapped.x, y: snapped.y };
    settleNodeRun(inst);
    invalidate();
  }
  return false;
}

function dragMoveResize(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const inst = selectedInstance();
  if (inst && drag.resizeHandle) {
    const h = resizeHandleSnap(world, inst.id, e.altKey);
    applyResize(inst, drag.resizeHandle, h.p, h.snapped);
    renderProperties();
    invalidate();
  }
  return false;
}

function dragMoveRotate(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const inst = selectedInstance();
  if (inst) {
    // Keep its heaters, a steam room's auto fixtures and the parts through its
    // walls at the same local position as the room spins.
    const bound =
      inst.autoBenches
        ? roomContents(inst)
            .filter((h) => (traitsOf(h.category).placedAsHeater || !!h.roomFixture || h.throughWall != null || h.inWall != null) && !objLocked(h.id))
            .map((h) => ({ h, local: toInstanceLocal(inst, { x: h.x, y: h.y }) }))
        : [];
    const prevRot = inst.rotation;
    rotateInstanceToward(inst, world, e.shiftKey); // Shift = whole 15° steps
    const dRot = inst.rotation - prevRot;
    for (const { h, local } of bound) {
      const w = instanceToWorld(inst, local);
      h.x = w.x;
      h.y = w.y;
      // Spin the heater by the same delta, preserving any rotation the user
      // gave it relative to the sauna.
      h.rotation = (((h.rotation + dRot) % 360) + 360) % 360;
    }
    renderProperties();
    invalidate();
  }
  return false;
}

function dragMoveDoor(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  const sel = selectedDoor();
  if (sel) {
    const { inst, door } = sel;
    if (isPolyRoom(inst)) {
      // Move the door to the cursor's position along its run, PUSHING the features
      // on the side it moves toward (they compress proportionally) and stretching
      // the other side — locked runs/panes keep their length and equal groups stay
      // equal, and the door clamps only where a side can no longer absorb the
      // change. dragDoorAlong owns all of that; the plain clamp is the fallback for
      // a partition door (it returns false).
      const rd = roomDoor(inst, door);
      const raw = rd ? doorAlong(rd, toInstanceLocal(inst, world)) : 0;
      if (rd && !dragDoorAlong(inst, door, raw)) {
        // A partition door keeps the same DOOR_SIDE_MIN of wall on each side.
        const len = rd.run.len, hwd = door.width / 2;
        const side = 2 * DOOR_SIDE_MIN + door.width <= len ? DOOR_SIDE_MIN : 0;
        door.offset = Math.max(hwd + side, Math.min(len - hwd - side, raw)) - len / 2;
      }
    } else {
      door.offset = clampDoorOffset(inst, door, toInstanceLocal(inst, world).x);
    }
    regenerateSauna(inst); // door position can trim a bench for passage
    renderProperties();
    invalidate();
  }
  return false;
}

function dragMoveWallFeatureAdd(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drag.wallFeat) {
    // The pane / opening being dragged out along a drawn wall's segment.
    const d = drag.wallFeat;
    const wall = (state.drawing.walls || []).find((w) => w.id === d.wallId);
    const fr = wall && wallSegFrame(wall, d.seg);
    const t = wall && wallSegStation(wall, d.seg, world);
    if (fr && t != null) { d.s1 = Math.max(fr.lo, Math.min(fr.hi, t)); invalidate(); }
  }
  return false;
}

function dragMoveWallFeatureMove(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drag.wallFeatMove) {
    // A drawn wall's door / pane / opening slid along its segment, stopping at the
    // straight run's ends and at its neighbours.
    const m = drag.wallFeatMove;
    const wall = (state.drawing.walls || []).find((w) => w.id === m.wallId);
    const f = wall?.features?.find((x) => x.id === m.id);
    const fr = wall && f && wallSegFrame(wall, f.seg);
    const t = wall && f ? wallSegStation(wall, f.seg, world) : null;
    if (wall && f && fr && t != null) {
      let lo = fr.lo, hi = fr.hi;
      for (const o of wall.features!) {
        if (o === f || o.seg !== f.seg) continue;
        if (Math.max(o.start, o.end) <= m.start + 1e-6) lo = Math.max(lo, Math.max(o.start, o.end));
        else if (Math.min(o.start, o.end) >= m.end - 1e-6) hi = Math.min(hi, Math.min(o.start, o.end));
      }
      const w = m.end - m.start;
      const st = Math.max(lo, Math.min(hi - w, m.start + t - m.grab));
      f.start = st; f.end = st + w;
      invalidate();
    }
  }
  return false;
}

function dragMoveGlass(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drag.glass) {
    const inst = state.drawing.instances.find((i) => i.id === drag.glass!.instId);
    if (inst) {
      const local = toInstanceLocal(inst, world);
      drag.glass.end = drag.glass.innerWallId != null
        ? innerWallAlong(inst, drag.glass.innerWallId, local)
        : drag.glass.edge != null
          ? edgeAlong(wallView(inst), drag.glass.edge, local)
          : clampToWall(inst, drag.glass.wall, drag.glass.wall === 'left' || drag.glass.wall === 'right' ? local.y : local.x);
      invalidate();
    }
  }
  return false;
}

function dragMoveOpening(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drag.opening) {
    const inst = state.drawing.instances.find((i) => i.id === drag.opening!.instId);
    if (inst) {
      const local = toInstanceLocal(inst, world);
      drag.opening.end = drag.opening.edge != null
        ? edgeAlong(wallView(inst), drag.opening.edge, local)
        : clampToWall(inst, drag.opening.wall, drag.opening.wall === 'left' || drag.opening.wall === 'right' ? local.y : local.x);
      invalidate();
    }
  }
  return false;
}

function dragMoveInnerWall(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drag.innerWall) {
    const inst = state.drawing.instances.find((i) => i.id === drag.innerWall!.instId);
    if (inst && drag.innerWall.edge != null) {
      // Polygonal room: length is the inward depth past the host edge's inner face.
      const local = toInstanceLocal(inst, world);
      const m = polyInnerWallMetrics(inst, drag.innerWall.edge, local);
      if (m) {
        const span = polyInnerWallSpan(inst, drag.innerWall.edge, drag.innerWall.pos);
        let len = m.depth;
        if (span - len < 30) len = span; // snap across the room to the far wall
        drag.innerWall.length = Math.max(0, Math.min(span, len));
      }
      invalidate();
    } else if (inst) {
      const local = toInstanceLocal(inst, world);
      const hw = inst.width / 2, hd = inst.depth / 2;
      // Perpendicular distance inward from the starting wall to the cursor.
      let len: number;
      if (drag.innerWall.wall === 'front') len = local.y + hd;
      else if (drag.innerWall.wall === 'back') len = hd - local.y;
      else if (drag.innerWall.wall === 'left') len = local.x + hw;
      else len = hw - local.x;
      const span = innerWallSpan(inst, drag.innerWall.wall);
      // Snap to the full depth (merge with the opposite wall) when close.
      if (span - len < 30) len = span;
      drag.innerWall.length = Math.max(0, Math.min(span, len));
      invalidate();
    }
  }
  return false;
}

function dragMoveInnerWallMove(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drag.innerWallMove) {
    const inst = state.drawing.instances.find((i) => i.id === drag.innerWallMove!.instId);
    const iw = inst?.innerWalls?.[drag.innerWallMove.index];
    if (inst && iw && drag.innerWallMove.edge != null) {
      // Polygonal room: slide along the host edge (pos), keeping length untouched.
      const local = toInstanceLocal(inst, world);
      const m = polyInnerWallMetrics(inst, drag.innerWallMove.edge, local);
      const e = polyEdges(wallView(inst))[drag.innerWallMove.edge];
      if (m && e) {
        const wt = wallThicknessOf(inst);
        const faces = partitionFaces(inst);
        iw.pos = Math.max(wt, Math.min(e.len - wt, drag.innerWallMove.origPos + (m.along - drag.innerWallMove.grabAlong)));
        benchesFollowPartitions(inst, faces); // the benches it split go with it
      }
      invalidate();
    } else if (inst && iw) {
      const local = toInstanceLocal(inst, world);
      // Slide only along the host wall: pos changes, length (and thus its base and
      // any opposite-wall merge) is untouched, so existing snaps are preserved.
      const along = iw.wall === 'front' || iw.wall === 'back' ? local.x : local.y;
      const wt = wallThicknessOf(inst);
      const half = (iw.wall === 'front' || iw.wall === 'back' ? inst.width : inst.depth) / 2;
      iw.pos = Math.max(-half + wt, Math.min(half - wt, drag.innerWallMove.origPos + (along - drag.innerWallMove.grabAlong)));
      invalidate();
    }
  }
  return false;
}

function dragMoveBenchEdit(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drag.benchEdit) {
    applyBenchEditDrag(world);
  }
  return false;
}

function dragMoveDoorResize(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drag.doorResizeSide) {
    const sel = selectedDoor();
    if (sel) {
      const { inst, door } = sel;
      const rd = isPolyRoom(inst) ? roomDoor(inst, door) : null;
      if (rd) resizeDoorTo(rd, drag.doorResizeSide, doorAlong(rd, toInstanceLocal(inst, world)));
      else if (!isPolyRoom(inst)) resizeDoorToJamb(inst, door, drag.doorResizeSide, toInstanceLocal(inst, world).x);
      regenerateSauna(inst);
      renderProperties();
      invalidate();
    }
  }
  return false;
}

function dragMoveCubeMove(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  // Only the steam-room's internal feature cube (`cubePos`) is dragged this way;
  // sauna heaters and placed steamcube products are separate instances moved
  // with the normal 'move' drag.
  const inst = selectedInstance();
  if (inst && inst.category === 'steamroom') {
    // Grid-snap the cube's world position (same base as every other object)
    // before converting into the room's local frame.
    inst.cubePos = toInstanceLocal(inst, gsnapPt(world));
    regenerateSauna(inst);
    renderProperties();
    invalidate();
  }
  return false;
}

function dragMoveDrawEditVertex(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drawingEditor.drawEditVertexDrag) {
    const deInst = drawEditInst();
    if (deInst) {
      const { id, ring, index } = drawingEditor.drawEditVertexDrag;
      const lp = deInst.drawPolygons?.find((p) => p.id === id);
      const lw = lp ? undefined : deInst.drawWalls?.find((w) => w.id === id);
      // Snap in world space against the shape's other vertices (H/V + 45° from the
      // neighbour), then convert back to the instance's local frame.
      const localRing = lp ? shapeRings(lp)[ring] : lw?.points;
      const others: Vec2[] = [];
      let prev: Vec2 | undefined;
      if (localRing) {
        for (let i = 0; i < localRing.length; i++) if (i !== index) others.push(instanceToWorld(deInst, localRing[i]));
        // Polygons wrap; open walls don't (vertex 0's neighbour is vertex 1).
        const nb = lp ? localRing[(index - 1 + localRing.length) % localRing.length] : (localRing[index - 1] ?? localRing[index + 1]);
        if (nb) prev = instanceToWorld(deInst, nb);
      }
      // Same snap as everywhere else, but the "nearby" targets are the part's
      // sibling shapes (the drawing editor's world), not the main-drawing objects.
      let localPt = toInstanceLocal(deInst, editVertexSnap(world, others, null, prev, e.altKey, editorShapeSnapVerts()));
      // Keep any locked incident edge horizontal/vertical (in the part's local frame).
      if (lp && ring === 0) localPt = constrainDraggedVertex(lp, index, localPt);
      if (lp) {
        const rings = shapeRings(lp);
        if (rings[ring]) rings[ring][index] = localPt;
        if (ring === 0) lp.points = rings[0];
        else if (lp.holes) lp.holes[ring - 1] = rings[ring];
        invalidate();
      } else if (lw && lw.points[index]) {
        lw.points[index] = localPt;
        invalidate();
      }
    }
  }
  return false;
}

function dragMoveDrawEditMove(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drag.drawEditDownLocal) {
    const sel = selectedDrawEditShape();
    const inst = drawEditInst();
    if (sel && inst) {
      const now = toInstanceLocal(inst, gsnapPt(world));
      const dx = now.x - drag.drawEditDownLocal.x, dy = now.y - drag.drawEditDownLocal.y;
      sel.shape.points = drawEditDragStart.map((p) => ({ x: p.x + dx, y: p.y + dy }));
      if (!sel.isWall) setPolyHoles(sel.shape, drawEditHolesStart.map((h) => h.map((p) => ({ x: p.x + dx, y: p.y + dy }))));
      invalidate();
    }
  }
  return false;
}

function dragMoveDrawEditResize(e: MouseEvent, sp: Vec2, world: Vec2): boolean {
  if (drag.drawEditResizeHandle && drag.drawEditResizeBBox) {
    const sel = selectedDrawEditShape();
    const inst = drawEditInst();
    if (sel && inst) {
      // Scale in the instance's local frame (points, bbox and mouse all local).
      resizePolyTo(sel.shape as Polygon, drag.drawEditResizeHandle, drawEditDragStart, drawEditHolesStart, drag.drawEditResizeBBox, toInstanceLocal(inst, gsnapPt(world)));
      invalidate();
    }
  }
  return false;
}

/**
 * Select instances and polygons with a marquee. Dragged left→right is a "window"
 * (objects entirely inside); right→left is "crossing" (≥1 vertex inside).
 */
function applyMarqueeSelection(a: Vec2, b: Vec2, add = false): void {
  const minx = Math.min(a.x, b.x), maxx = Math.max(a.x, b.x);
  const miny = Math.min(a.y, b.y), maxy = Math.max(a.y, b.y);
  // Too small to be intentional → treat as a plain click (the selection already
  // cleared, or — adding — left as it was).
  if (maxx - minx < 4 / vp.scale && maxy - miny < 4 / vp.scale) { renderProperties(); invalidate(); return; }
  // Hidden and locked layers' objects are out of the marquee's reach.
  const hits = (verts: Vec2[], id: string): boolean => !objUnpickable(id) && marqueeTakes(a, b, verts);
  const textCorners = (t: TextComment): Vec2[] => textBoxCorners(t);
  // Each part by its outline as drawn: a room's polygon, a node object's mitred run.
  const took = <T extends { id: string }>(arr: T[] | undefined, verts: (o: T) => Vec2[]): string[] => (arr || []).filter((o) => hits(verts(o), o.id)).map((o) => o.id);
  const got: ObjectSel = {
    instance: took(state.drawing.instances, instanceOutline),
    polygon: took(state.drawing.polygons, (p) => p.points),
    wall: took(state.drawing.walls, (w) => w.points),
    text: took(state.drawing.texts, textCorners),
    arc: took(state.drawing.arcs, arcSamplePts),
    leader: took(state.drawing.leaders, (l) => l.points),
    cloud: took(state.drawing.revisionClouds, (c) => c.points),
  };
  setObjectSelection(add ? mergeSelections(selectedObjects(state, MULTI_KINDS), got) : got);
}

/** Replace the selection with these objects of every multi-selectable kind (the
 *  marquee and Select All both land here). */
export function setObjectSelection(sel: ObjectSel): void {
  selectObjects(state, sel);
  renderProperties();
  invalidate();
}

// ---- Edit commands: Select All, Cut, Duplicate, Group / Ungroup ----
// Each acts on the space that is live (`liveSpace`): the title-block editor, a
// sheet, or the model — each a row of SPACES, with its own selection and clipboard.

/** Ctrl+A: select every object in the live space. */
export function selectAll(): void { SPACES[liveSpace()].selectAll(); }

/** The spaces the Edit commands act in: each clipboard's, and a part's own
 *  drawing in the drawing editor (its own selection; no clipboard). */
type EditSpaceKey = ClipSpace | 'part';
/** The space the Edit commands act in now. */
export function liveSpace(): EditSpaceKey {
  return titleEditor.titleEdit ? 'title' : drawingEditor.drawingEditId ? 'part' : activeSheet() ? 'sheet' : 'model';
}

/** What the Edit commands do in each space: the model, a sheet's paper, the title
 *  block's master view, a part's own drawing. Each keeps its own selection and clipboard; choosing the
 *  space is `liveSpace()`, once — `titleEdit ? … : activeSheet() ? … : …` was
 *  written at ten sites. */
interface EditSpace {
  /** Something is selected here. */
  has(): boolean;
  /** Its clipboard holds something (the model may adopt another window's copy). */
  canPaste(): boolean;
  /** Something selected here can be deleted (the Delete button, menu and key). */
  deletable(): boolean;
  copy(): void;
  /** `at`: where the paste lands (world in the model, paper mm on a sheet). */
  paste(at?: Vec2): void;
  cut(): void;
  del(): void;
  /** A copy beside the selection, selected, the clipboard untouched. */
  duplicate(): void;
  selectAll(): void;
}
export const SPACES: Record<EditSpaceKey, EditSpace> = {
  // The drawing editor: its one selected local shape. Select All and the
  // clipboard commands take nothing here — before, they reached the model behind
  // the editor (Ctrl+A then Delete deleted the edited part with the rest).
  part: {
    has: () => !!selectedEditorShapeId(),
    canPaste: () => false,
    deletable: () => !!selectedEditorShapeId(),
    copy: () => {},
    paste: () => {},
    cut: () => {},
    del: () => deleteEditorShape(),
    duplicate: () => {},
    selectAll: () => flashStatus(tr('The drawing editor selects one shape at a time')),
  },
  title: {
    has: () => titleEditor.selectedTitleObjIds.length > 0,
    canPaste: () => !!titleEditor.titleObjClipboard?.length,
    deletable: () => titleEditor.selectedTitleObjIds.length > 0,
    copy: () => copyTitleObjSelection(),
    paste: () => pasteTitleObjClipboard(),
    cut: () => { if (titleEditor.selectedTitleObjIds.length) { copyTitleObjSelection(); deleteSelectedTitleObj(); } },
    del: () => { if (titleEditor.selectedTitleObjIds.length) deleteSelectedTitleObj(); },
    duplicate: () => {
      const sh = activeSheet();
      const cart = sh ? cartiglioForSheet(sh) : null;
      const sel = cart ? selectedTitleObjs(cart) : [];
      if (!cart || !sel.length) return;
      const saved = saveClipboards();
      titleEditor.titleObjClipboard = sel.map((o) => structuredClone(o));
      titleEditor.titleObjClipboardSourceCartId = cart.id;
      titleEditor.titleObjPasteCount = 0;
      pasteTitleObjClipboard();
      restoreClipboards(saved);
    },
    selectAll: () => {
      const sh = activeSheet();
      const cart = sh ? cartiglioForSheet(sh) : null;
      titleEditor.selectedTitleObjIds = (cart?.objects ?? []).map((o) => o.id);
      titleEditor.selectedTitleObjId = titleEditor.selectedTitleObjIds[titleEditor.selectedTitleObjIds.length - 1] ?? null;
      renderProperties(); invalidate();
    },
  },
  sheet: {
    has: () => schemSelSet.length > 0,
    canPaste: () => !!clipboardUi.schObjClipboard?.length,
    deletable: () => sheetSelectionDeletable(),
    copy: () => copySchematicSelection(),
    paste: (at) => pasteSchematicClipboard(at),
    // A pick inside a view is not what Copy took (the view): Cut removes the view.
    cut: () => { const sh = activeSheet(); if (sh && schemSelSet.length) { copySchematicSelection(); deleteSchematicSelected(sh, false); } },
    del: () => deleteSheetSelection(),
    duplicate: () => {
      if (!schemSelSet.length) return;
      const saved = saveClipboards();
      copySchematicSelection();
      pasteSchematicClipboard();
      restoreClipboards(saved);
    },
    selectAll: () => {
      const sh = activeSheet();
      if (!sh) return;
      setSchemSelSet(allSheetObjects(sh, SHEET_OBJECTS));
      renderProperties(); invalidate();
    },
  },
  model: {
    has: () => selectionCopies(state, OBJECTS),
    canPaste: () => true,
    deletable: () => modelSelectionDeletable(),
    copy: () => { copySelection(); },
    paste: (at) => { void pasteObjectsFromSystem(at); },
    cut: () => {
      if (roomFeatureSelected()) { flashStatus(tr('Select the whole room to cut it — Delete removes just the selected feature')); return; }
      if (copySelection()) deleteSelected();
    },
    del: () => deleteSelected(),
    duplicate: () => {
      const cb = roomFeatureSelected() ? null : selectionClipboard();
      if (!cb) { flashNotCopied(); return; }
      const saved = saveClipboards();
      clipboardUi.clipboard = cb;
      clipboardUi.pasteCount = 0;
      pasteClipboard();
      restoreClipboards(saved);
    },
    selectAll: () => {
      if (state.tool !== 'select') setTool('select');
      deselectAll();
      // Everything on a layer that is shown and unlocked.
      const all = MULTI_KINDS.flatMap((k) => (objectsOf(state.drawing, k) ?? []).map((o) => (o as { id: string }).id));
      setObjectSelection(selectionOfIds(state.drawing, all.filter((id) => !objUnpickable(id))));
    },
  },
};

/** A room's feature selected (a bench, a partition, a heater wall, a door, a pane,
 *  an opening, a support, a backrest, a light): Copy would take the whole room
 *  while Delete takes only the feature, so Cut must not pair them, nor Duplicate
 *  copy the room for it. */
function roomFeatureSelected(): boolean {
  return !!liveFeatureNow();
}

/** Ctrl+X: copy, then delete what was copied. */
export function cutSelection(): void { SPACES[liveSpace()].cut();
}

/** Ctrl+D: a copy of the selection beside it, selected — without touching the
 *  clipboard (PowerPoint's Duplicate), so a later Paste still pastes what was copied. */
export function duplicateSelection(): void { SPACES[liveSpace()].duplicate();
}

// ---- Groups: any drawing objects, PowerPoint's rules (groups.ts) ----
// ONE kind of group: Ctrl+G's, a placed multi-part product (linked to its
// catalogue product) and a repeat row alike. A plain click on any member selects
// the outermost group and starts moving it; with a group selected, a click
// WITHOUT a drag drills one level in — a nested group, then the member itself
// (`groupDrill`, decided on mouseup). Shift-click adds or removes the whole
// outermost group. Groups nest: Ctrl+G around whole groups nests them.

/** Every member of a group (nested groups included), as a selection. */
export function groupMembersSel(gid: string): ObjectSel {
  return selectionOfIds(state.drawing, groupMembers(state.drawing, gid).map((m) => m.id));
}

/** Every selected object's id, across the multi kinds. */
export function selectedObjectIds(): Set<string> {
  return new Set(MULTI_KINDS.flatMap((k) => selectedOf(state, k)));
}

/** Whether every member of the group is selected. */
function groupFullySelected(gid: string): boolean {
  const cur = selectedObjectIds();
  return groupMembers(state.drawing, gid).every((m) => cur.has(m.id));
}

/** Add the group to the selection, or (`remove`) take it out. */
function mergeGroupSel(gid: string, remove: boolean): void {
  setObjectSelection(mergeSelections(selectedObjects(state, MULTI_KINDS), groupMembersSel(gid), remove));
}

/** The one group the selection is exactly (all its members, nothing else). */
export function selectedWholeGroup(): DrawingGroup | undefined {
  const sel = selectedObjectIds();
  return (state.drawing.groups || []).find((g) => {
    const m = groupMembers(state.drawing, g.id);
    return m.length === sel.size && m.every((o) => sel.has(o.id));
  });
}

/** Re-lay one placed product from its catalogue product (right-click ▸ Update
 *  from catalogue): the catalogue-wide update, for this copy only. */
export function updateProductFromCatalogue(gid: string, t: ProductTemplate): void {
  pushHistory();
  reconcileGroupFromTemplate(t, gid, productParts(gid), true);
  for (const p of productParts(gid)) postUpdateFixups(p);
  syncDimensions();
  renderProperties();
  invalidate();
}

/** Whether the selection holds at least one whole group (so Ungroup can act). */
export function selectionHoldsGroup(): boolean {
  const sel = selectedObjectIds();
  return (state.drawing.groups || []).some((g) => { const m = groupMembers(state.drawing, g.id); return m.length > 0 && m.every((o) => sel.has(o.id)); });
}

/** Ctrl+G: the selection becomes one group — any mix of kinds, rooms included.
 *  Whole groups in it nest in the new group. */
export function groupSelection(): void {
  if (activeSheet()) return;
  const sel = selectedObjectIds();
  const probe = structuredClone(state.drawing);
  const res = groupSelected(probe, sel, 'probe', '');
  if (res === 'too-few') { flashStatus(tr('Select two or more objects to group')); return; }
  if (res === 'mixed') { flashStatus(tr('Only members of the same group can be grouped together')); return; }
  pushHistory();
  groupSelected(state.drawing, sel, newId('grp'));
  renderProperties(); invalidate();
}

/** Ctrl+Shift+G: every whole group in the selection loses one level. A placed
 *  product ungrouped is unlinked from its catalogue product (AutoCAD's Explode),
 *  so it asks first. */
export async function ungroupSelection(): Promise<void> {
  if (activeSheet() || !selectionHoldsGroup()) return;
  const sel = selectedObjectIds();
  const linked = ungroupSelected(structuredClone(state.drawing), sel).some((g) => g.source);
  if (linked && !(await showConfirm(tr('Ungrouping a product unlinks it from the catalogue: a catalogue update will no longer reach its parts.'),
    { title: 'Ungroup the product?', ok: 'Ungroup' }))) return;
  pushHistory();
  ungroupSelected(state.drawing, sel);
  renderProperties(); invalidate();
}

/** The canvas mouse-up, one dispatcher: the release ends what the press began
 *  (on a sheet the sheet's, else the drag's end), then decides the clicks a
 *  press left pending — after the drag's end, so the press's own (no-movement)
 *  gesture has already closed. Each up…() clears its own probe on every
 *  release, a sheet's too. A new case is its own up…() at its place here. */
window.addEventListener('mouseup', (e) => {
  if (!upSheet()) endDrag(e);
  if (upSectionClick(e)) return;
  upGroupDrill(e);
  upAltCycle(e);
});

/** On a sheet the release is the sheet's (sheetMouseUp), not a drag's end. */
function upSheet(): boolean {
  if (!activeSheet()) return false;
  sheetMouseUp();
  return true;
}

/** A drag is being ended or put back: what that does may arm another tool
 *  (`endOneShot`) or switch the view (a restored drawing lost its sheet), which
 *  must not put the drag back again. */
let dragClosing = false;

/** The pointer let go: a Ctrl drag's copy is dropped, the drag ends by its row
 *  (DRAG_LIFE `end`), a Connect drag may connect, and what every drag leaves
 *  behind goes; the gesture the press opened is recorded. */
export function endDrag(e: MouseEvent, aim?: ToolAim): void {
  const mode = state.dragMode;
  dragClosing = true;
  try {
    const sp = screenPos(e);
    if (DRAG_LIFE[mode].end?.(e, aim ?? planAim(vp.screenToWorld(sp), vp.scale, sp, e.altKey)) === 'own') { dropDrag(); dropDragData(); return; }
    upConnectDrag();
    clearDragLeftovers();
    // A gesture still open belongs to the work under way a click armed (a pan
    // while one is armed leaves it open).
    if (!underWayNow()) commitGesture(); // record the gesture in undo history if it changed anything
    invalidate();
  } finally {
    dragClosing = false;
  }
}

// ---- A room tool's drag in the 3D view (ui/tools3d.ts) ----

/** The pointer moving in a drag a 3D press started, aimed at `aim` (where it
 *  meets the line or the level the drag follows, the 3D view's px there): its
 *  row's move, as the plan's. */
export function dragMove3d(e: MouseEvent, aim: ToolAim): void {
  const sp = screenPos(e);
  stepDrag(sp);
  DRAG_LIFE[state.dragMode].move?.(e, sp, aim.world, aim);
}

/** What the drag under way has changed so far, which the 3D view builds again
 *  live: its row's `live3d`. */
export function dragLive3d(): readonly string[] {
  return DRAG_LIFE[state.dragMode].live3d?.() ?? [];
}

/** What a room tool's drag in 3D runs along (world): a pane's or an opening's
 *  wall or partition, a new partition's own line, a partition slid along its
 *  wall — the pointer meets the plumb plane through `line` (a wall's inner
 *  face; a partition's centre line, inward) — and what it would add there: its
 *  footprint `slab` (from the drag's start to where it is now, through the
 *  wall's or the partition's thickness), `z0` to `z1` high, and what it is.
 *  `slab` is empty while nothing would be added (a partition slid, which moves
 *  itself). Null for a drag that follows the level pressed (a bench support). */
export function dragRun3d(): { line: [Vec2, Vec2]; slab: Vec2[]; z0: number; z1: number; kind: 'glass' | 'opening' | 'partition' } | null {
  const g = drag.glass ?? drag.opening, iw = drag.innerWall, mv = drag.innerWallMove;
  const id = g?.instId ?? iw?.instId ?? mv?.instId;
  const inst = id ? state.drawing.instances.find((i) => i.id === id) : undefined;
  if (!inst) return null;
  const view = wallView(inst), wt = wallThicknessOf(view), edges = polyEdges(view);
  // A new pane is glazed from the floor, a new opening from the default sill,
  // both up to the room's glass height, as they are built.
  const height = inst.height ?? 2300, paneTop = openingArchitrave(inst, {} as GlassSegment), sill = openingSill({});
  const W = (p: Vec2): Vec2 => instanceToWorld(inst, p);
  // A point `along` an edge (from its outer corner, as `edgeAlong` measures) and
  // `inward` from its outer face; a partition's run starts on the inner face.
  const at = (e: (typeof edges)[number], along: number, inward: number): Vec2 => ({ x: e.a.x + e.dir.x * along + e.normal.x * inward, y: e.a.y + e.dir.y * along + e.normal.y * inward });
  const onWall = (edge: number, s: number, t: number, kind: 'glass' | 'opening'): ReturnType<typeof dragRun3d> => {
    const e = edges[edge];
    if (!e) return null;
    const a = Math.min(s, t), b = Math.max(s, t), m = 3; // a hair proud of both faces
    return { line: [W(at(e, 0, wt)), W(at(e, e.len, wt))], slab: [at(e, a, -m), at(e, b, -m), at(e, b, wt + m), at(e, a, wt + m)].map(W), z0: kind === 'opening' ? sill : 0, z1: paneTop, kind };
  };
  const onPartition = (edge: number, pos: number, s: number, t: number, half: number, z1: number, kind: 'glass' | 'partition'): ReturnType<typeof dragRun3d> => {
    const e = edges[edge];
    if (!e) return null;
    const a = Math.min(s, t), b = Math.max(s, t), h = half + 3;
    return { line: [W(at(e, pos, wt)), W(at(e, pos, wt + 1000))], slab: [at(e, pos - h, wt + a), at(e, pos + h, wt + a), at(e, pos + h, wt + b), at(e, pos - h, wt + b)].map(W), z0: 0, z1, kind };
  };
  const paneOn = drag.glass?.innerWallId;
  if (g && paneOn != null) {
    const p = innerWallsOf(inst).find((x) => x.id === paneOn);
    return p && p.edge != null ? onPartition(p.edge, p.pos, g.start, g.end, innerWallThickness(inst, p) / 2, paneTop, 'glass') : null;
  }
  if (g?.edge != null) return onWall(g.edge, g.start, g.end, drag.glass ? 'glass' : 'opening');
  if (iw?.edge != null) return onPartition(iw.edge, iw.pos, 0, iw.length, wt / 2, height, 'partition');
  const slid = mv?.edge != null ? inst.innerWalls?.[mv.index] : undefined;
  const e = mv?.edge != null ? edges[mv.edge] : undefined;
  if (slid && e) return { line: [W(at(e, 0, wt)), W(at(e, e.len, wt))], slab: [], z0: 0, z1: height, kind: 'partition' };
  return null;
}

/** Put the drag under way back: Esc, undo or redo, another tool, another view. Its
 *  row's `cancel`, else the drawing goes back to where the press began
 *  (`cancelGesture`). True when there was a drag to put back. */
export function cancelDrag(): boolean {
  const mode = state.dragMode;
  if (mode === 'none' || dragClosing) return false;
  dragClosing = true;
  try {
    const row = DRAG_LIFE[mode];
    if (row.work) underWayRow(row.work).cancel();
    else if (row.cancel) row.cancel();
    else cancelGesture();
    clearDragLeftovers();
  } finally {
    dragClosing = false;
  }
  updateStatus();
  renderProperties();
  invalidate();
  return true;
}

/** The button-held drag in the gesture under way (underWay.ts): put back by its
 *  DRAG_LIFE row (`cancelDrag`); its moves and its release are its row's (they
 *  take the plan's event), so no key ends it. */
export const DRAG_UNDER_WAY: UnderWayRow = {
  live: () => state.dragMode !== 'none',
  armed: () => false,
  space: () => (state.viewTab === '3d' ? '3d' : 'plan'),
  move: null,
  press: () => false,
  finish: null,
  cancel: cancelDrag,
  drop: () => {},
  check: () => {},
  typed: null,
  hint: () => null,
};

/** What every drag leaves behind, however it ends: its guides, the layers held
 *  for it, and the drag itself with its data. */
function clearDragLeftovers(): void {
  state.editGuide = null; // vertex-drag alignment guide clears when the drag ends
  state.centroidGuides = [];
  if (dragHeld && !moveHolds) releaseDragLayers(); // a move session lets its own go
  drawingEditor.drawEditVertexDrag = null;
  state.rcpGuide = null;
  snapSetUi.moveAlignGuides = []; // clear the purple underlay-move alignment guides
  state.floorDrainDims = []; // clear the temporary drain dimensions
  state.alignGuides = []; // clear the purple text-box alignment guides
  state.moveSnapTargets = null; // re-gathered on the next shape drag
  state.snapIndicator = null;
  dropDrag();
  dropDragData();
}

/** Rubber-band marquee: pick objects inside (window) or touching (crossing) it.
 *  A product part awaiting a polygon pick takes this selection. */
function endMarquee(): 'own' | void {
  if (drag.marqueeStart && drag.marqueeEnd) applyMarqueeSelection(drag.marqueeStart, drag.marqueeEnd, drag.marqueeAdd);
  drag.marqueeStart = null;
  drag.marqueeAdd = false;
  drag.marqueeEnd = null;
  if (armedIs('part-polygons')) { dropDrag(); finishPolygonPick(); return 'own'; }
}

/** A section line, its end or its drawing let go: its snap guides go, its fields
 *  show where it is. */
function endSectionDrag(): void {
  state.drawSnapVerts = [];
  sectionAlignVerts = [];
  renderProperties();
}

/** The cancel of a drag that changes no drawing: nothing to put back (and an
 *  armed grip's or handle's open gesture is left alone). */
function noDrawingChange(): void {}

/** The ceiling's fall arrow dropped on a wall: the ceiling falls toward it. */
function endCeilingArrowDrag(e: MouseEvent): void {
  const room = ceilingPickRoom();
  const w = room ? ceilingArrowDropAt(screenPos(e)) : null;
  if (room && w != null) editRoomTop(room, () => setDirectionWall(room, w));
  ceilingUi.ceilingArrowDrag = null;
  if (state.benchAddHover?.kind === 'pick') state.benchAddHover = null;
  renderProperties();
}

/** Glass tool: a drag adds a glazed segment; a click removes one under the cursor. */
function endGlassDrag(): void {
  if (!drag.glass) return;
  const inst = state.drawing.instances.find((i) => i.id === drag.glass!.instId);
  if (inst) {
    const a = Math.min(drag.glass.start, drag.glass.end);
    const b = Math.max(drag.glass.start, drag.glass.end);
    if (dragTravelled() >= 5 && b - a >= GLASS_MIN) {
      if (drag.glass.edge != null && drag.glass.innerWallId == null && isPolyRoom(inst)) {
        // A room wall: the pane gets a whole segment of its own (split at its two
        // ends), merging with a pane it touches and cut around the doors.
        const edge = drag.glass.edge;
        followVertices(state.drawing, inst.id, () => addWallFeature(inst, 'glass', edge, a, b, GLASS_MIN).remap);
      } else {
        if (!inst.glass) inst.glass = [];
        inst.glass.push({ wall: drag.glass.wall, start: a, end: b, edge: drag.glass.edge, innerWallId: drag.glass.innerWallId });
        // Merge overlapping panes so glazing the same span twice counts once.
        inst.glass = mergeGlassSegments(inst.glass);
        clampGlassToDoor(inst); // glass must not overlap the door opening
      }
    } else {
      const i = glassHitLocal(inst, drag.glass.downLocal, 60);
      if (i >= 0 && inst.glass) inst.glass.splice(i, 1);
    }
    regenerateSauna(inst); // heals the jambs a removed pane leaves behind
    renderProperties();
  }
}

/** Opening tool: a drag adds an open segment; a click removes one under the
 * cursor. */
function endOpeningDrag(): void {
  if (!drag.opening) return;
  const inst = state.drawing.instances.find((i) => i.id === drag.opening!.instId);
  if (inst) {
    const a = Math.min(drag.opening.start, drag.opening.end);
    const b = Math.max(drag.opening.start, drag.opening.end);
    if (dragTravelled() >= 5 && b - a >= 50) {
      if (drag.opening.edge != null && isPolyRoom(inst)) {
        const edge = drag.opening.edge;
        followVertices(state.drawing, inst.id, () => addWallFeature(inst, 'opening', edge, a, b, 50).remap);
      } else {
        if (!inst.openings) inst.openings = [];
        inst.openings.push({ wall: drag.opening.wall, start: a, end: b, edge: drag.opening.edge });
        inst.openings = mergeGlassSegments(inst.openings); // merge touching openings on a wall
      }
    } else {
      const i = openingHitLocal(inst, drag.opening.downLocal, 60);
      if (i >= 0 && inst.openings) inst.openings.splice(i, 1);
    }
    regenerateSauna(inst); // heals the jambs a removed opening leaves behind
    renderProperties();
  }
}

/** Inner-wall tool: a drag adds an interior wall; a click removes one under the
 * cursor. */
function endInnerWallDrag(): void {
  if (!drag.innerWall) return;
  const inst = state.drawing.instances.find((i) => i.id === drag.innerWall!.instId);
  if (inst && drag.innerWall.edge != null) {
    if (dragTravelled() >= 5 && drag.innerWall.length >= 50) {
      if (!inst.innerWalls) inst.innerWalls = [];
      // Born with its id: the interior dimension split (innerWallDimSplits) and a
      // selection address it by id, and nothing else is guaranteed to mint one.
      inst.innerWalls.push({ id: newId('iw'), wall: drag.innerWall.wall, edge: drag.innerWall.edge, pos: drag.innerWall.pos, length: drag.innerWall.length });
    } else {
      const i = polyInnerWallHit(inst, drag.innerWall.downLocal);
      if (i >= 0 && inst.innerWalls) inst.innerWalls.splice(i, 1);
    }
    renderProperties();
  } else if (inst) {
    if (dragTravelled() >= 5 && drag.innerWall.length >= 50) {
      if (!inst.innerWalls) inst.innerWalls = [];
      inst.innerWalls.push({ id: newId('iw'), wall: drag.innerWall.wall, pos: drag.innerWall.pos, length: drag.innerWall.length });
    } else {
      const i = innerWallHitLocal(inst, drag.innerWall.downLocal, 0);
      if (i >= 0 && inst.innerWalls) inst.innerWalls.splice(i, 1);
    }
    renderProperties();
  }
}

/** Inner-wall tool: an inner wall was slid into place during the drag; a click
 * (no real move) on it removes it. */
function endInnerWallMoveDrag(): void {
  if (!drag.innerWallMove) return;
  const inst = state.drawing.instances.find((i) => i.id === drag.innerWallMove!.instId);
  if (inst && dragTravelled() < 5 && !drag.innerWallMove.selectOnly) inst.innerWalls?.splice(drag.innerWallMove.index, 1);
  renderProperties();
}

/** Bench support tool: a click places one support, a drag a row along the bench. */
function endSupportRowDrag(e: MouseEvent, aim: ToolAim): void {
  if (!benchSupportTool.supportRowDrag) return;
  const d = benchSupportTool.supportRowDrag;
  const sp = screenPos(e);
  const inst = state.drawing.instances.find((i) => i.id === d.instId);
  if (inst) {
    // The run's supports spread evenly along it, the new one(s) with them; with
    // Alt placed where pressed — not twice where one already stands (supportsForRow).
    const res = supportsAfterPress(inst, d.runIndex, d.run, d.s0, Math.hypot(sp.x - d.press.x, sp.y - d.press.y) < 4 ? d.s0 : d.s1, e.altKey, aim.mmPerPx);
    if (res.added.length) inst.benchSupports = res.list;
    if (res.added.length > 1) flashToolStatus(tr('Placed {n} bench supports.').replace('{n}', String(res.added.length)));
    else if (!res.added.length) flashToolStatus(tr('A support already stands there.'));
  }
  benchSupportTool.supportRowDrag = null;
  state.benchAddHover = supportHoverAt(aim);
  updateStatus();
  renderProperties();
}

/** A support slid into place; under the Bench support tool a click removes it. */
function endSupportMoveDrag(_e: MouseEvent, aim: ToolAim): void {
  if (!benchSupportTool.supportMoveDrag) return;
  // Under the Bench support tool a click on a support (no drag) removes it.
  const d = benchSupportTool.supportMoveDrag;
  const inst = state.drawing.instances.find((i) => i.id === d.instId);
  if (inst && d.removeOnClick && !d.moved) removeSupport(inst, d.id, d.runIndex);
  benchSupportTool.supportMoveDrag = null;
  if (state.tool === 'bench-support') state.benchAddHover = supportHoverAt(aim);
  renderProperties();
}

/** Connect by drag: released over an object away from the press → connect them.
 * Released anywhere else, the draft simply carries on as clicks. */
function upConnectDrag(): void {
  if (state.tool !== 'connection' || !connectionUi.connDraft?.press) return;
  const moved = distance(connectionUi.connDraft.press, connectionUi.connDraft.hover.p) * vp.scale > 6;
  if (moved && connectionUi.connDraft.hover.id) finishConnection(connectionUi.connDraft.hover);
  if (connectionUi.connDraft) connectionUi.connDraft.press = null;
}

/** Dropping a part onto another part relates them (not with Alt held). What a
 * part stands in needs no drop: it is the room under it — hostRoomOf. */
function relateDropTarget(e: MouseEvent): void {
  if (!drag.dropTargetId || e.altKey) return;
  const dragged = selectedInstance();
  const target = state.drawing.instances.find((i) => i.id === drag.dropTargetId);
  if (dragged && target && dragged.id !== target.id && !target.autoBenches) {
    relateParts(dragged, target);
  }
}

/** After a kept move of the parts `parts` (`from`: where each stood; what a
 *  room carried along is not among them and keeps its place, re-anchored only
 *  when it is among `anchors` — after a mirror or a scale): a heater / steam
 *  cube re-records (or clears) its anchor to the room under it, so it follows
 *  the nearest vertex when that room is resized, and the room it left and the
 *  room it entered both re-carve their benches; with `mount` (a step in plan) a
 *  part mounted by where it is dropped (a junction box) is mounted again where
 *  it went. The move session's end (moveSession.ts). */
function remountParts(parts: readonly string[], from: ReadonlyMap<string, Vec2>, mount: boolean, anchors: readonly string[]): void {
  let carved = false;
  for (const id of [...parts, ...anchors]) {
    const item = state.drawing.instances.find((i) => i.id === id);
    if (item && traitsOf(item.category).carvesBenches) {
      updateRoomAnchor(item);
      if (item.category === 'steamcube') updateCubeSnap(item); // legacy snap kept in sync
      carved = true;
    }
  }
  if (carved) for (const room of state.drawing.instances) if (room.autoBenches) regenerateSauna(room);
  if (!mount) return;
  const movedSet = new Set(parts);
  for (const id of parts) {
    const item = state.drawing.instances.find((i) => i.id === id), f = from.get(id);
    if (!item || (f && f.x === item.x && f.y === item.y)) continue;
    // Its back on a wall, the rest of its product moved with it goes along.
    const mates = item.backOnWall && item.groupId ? state.drawing.instances.filter((o) => o !== item && o.groupId === item.groupId && movedSet.has(o.id)) : [];
    if (mates.length) seatGroupOnWall(item, mates);
    else remountByDrag(item);
  }
}

/** Pane / opening tool on a drawn wall: a drag adds one, a click on one of its
 * kind removes it — the room tools' gesture. */
function endWallFeatureAddDrag(): void {
  if (!drag.wallFeat) return;
  const d = drag.wallFeat;
  const wall = (state.drawing.walls || []).find((w) => w.id === d.wallId);
  if (wall) {
    if (Math.abs(d.s1 - d.s0) * vp.scale >= 5) {
      const f = addDrawnWallFeature(wall, d.kind, d.seg, d.s0, d.s1, newId('wf'));
      if (f) pickWallFeature(wall, f);
      else flashToolStatus(tr('No room for it on this stretch of wall'));
    } else {
      const hit = (wall.features ?? []).find((f) => f.kind === d.kind && f.seg === d.seg
        && d.s0 >= Math.min(f.start, f.end) && d.s0 <= Math.max(f.start, f.end));
      if (hit) {
        wall.features = wall.features!.filter((f) => f !== hit);
        if (!wall.features.length) delete wall.features;
      }
    }
    renderProperties();
  }
}

/** Resize an instance by dragging a handle, keeping the opposite edge/corner
 *  fixed in world space at the size it really gets (resize.ts `resizeByHandle`). */
function applyResize(inst: ProductInstance, handle: Handle, mouseWorld: Vec2, onVertex = false): void {
  const r = resizeByHandle(inst, handle, mouseWorld, { linkWH: linkWH(), onVertex });
  // The centre first, so resizeInstance re-places snapped cubes against the room's final position.
  inst.x = r.center.x;
  inst.y = r.center.y;
  resizeInstance(inst, r.width, r.depth);
}

function addDimension(a: Vec2, b: Vec2, offset: number, aRef?: DimAnchor, bRef?: DimAnchor, kind: DimKind = 'aligned'): void {
  if (distance(a, b) < 1e-6) return;
  pushHistory();
  // 'aligned' is the absent default, so the common case adds no field.
  const dim: DimensionLine = { id: newId('dim'), a, b, offset, aRef, bRef, ...(kind === 'aligned' ? {} : { kind }) };
  state.drawing.dimensions.push(dim);
  selectOne(state, 'dim', dim.id);
  renderProperties();
}

/**
 * Refresh every anchored dimension's endpoints from its instance, so dimensions
 * follow the objects they're snapped to through moves and resizes. An anchor
 * whose instance was deleted is dropped (the endpoint keeps its last position).
 */
export function syncDimensions(): void {
  for (const d of state.drawing.dimensions) {
    if (d.aRef) {
      const p = anchorAt(state.drawing, d.aRef);
      if (p) d.a = p; else d.aRef = undefined;
    }
    if (d.bRef) {
      const p = anchorAt(state.drawing, d.bRef);
      if (p) d.b = p; else d.bRef = undefined;
    }
  }
}

/** Commit the in-progress polygon (needs at least 3 vertices) and select it. */
function finishPolygon(): void {
  if (polyDraft.length >= 3) {
    pushHistory();
    const poly: Polygon = {
      id: newId('poly'),
      points: polyDraft.map((p) => ({ x: p.x, y: p.y })),
      fill: '#ffffff',
      fillOpacity: 1,
      stroke: '#222222',
      strokeOpacity: 1,
    };
    const inst = drawEditInst();
    if (inst) {
      // Drawing editor mode: store in local frame.
      poly.points = poly.points.map((p) => toInstanceLocal(inst, p));
      poly.z = nextDrawZ(inst);
      (inst.drawPolygons ??= []).push(poly);
      state.drawEditSelectedPolyId = poly.id;
      state.drawEditSelectedWallId = null;
      state.drawEditVerticesId = null;
    } else {
      // Lock every already-horizontal/vertical segment so a squared shape comes
      // out constrained (the constraint solver then keeps it square on edits).
      poly.edgeConstraints = autoAxisLocks(poly.points);
      if (!state.drawing.polygons) state.drawing.polygons = [];
      state.drawing.polygons.push(poly);
      deselectAll(); // the new object alone is selected
      selectOne(state, 'polygon', poly.id);
    }
    endOneShot();
    renderProperties();
  }
  polyDraft = [];
  invalidate();
}

/** The template ids of every drain-like product (piletta / canalina), from the
 *  catalog, with the builtin piletta+canalina as the offline fallback. */
function drainTemplates(): ProductTemplate[] {
  const found = state.templates.filter((t) => t.category === 'drain' || t.category === 'canalina');
  const list = found.length ? found : BUILTIN_PRODUCTS.filter((t) => t.id === 'builtin-drain' || t.id === 'builtin-canalina');
  // Lead with the point drains (piletta) so a new sloping floor defaults to one.
  return [...list].sort((a, b) => (a.category === b.category ? 0 : a.category === 'drain' ? -1 : 1));
}

/** Resolve a drain product id to its template (catalog then builtin fallback). */
/** A drain's product names, kept on its floor for the schedule (offline-safe). */
function drainNames(t: ProductTemplate): Pick<FloorDrain, 'name' | 'nameI18n'> {
  return { name: t.name, nameI18n: t.nameI18n ? { ...t.nameI18n } : undefined };
}
function drainTemplate(productId: string): ProductTemplate | null {
  return state.templates.find((t) => t.id === productId) ?? BUILTIN_PRODUCTS.find((t) => t.id === productId) ?? drainTemplates()[0] ?? null;
}

/** A synthetic ProductInstance for a floor drain, so the drain drawers/hit-tests
 *  (built for instances) can be reused. */
export function drainInstance(d: FloorDrain): ProductInstance | null {
  const t = drainTemplate(d.productId);
  if (!t) return null;
  return { id: `_drain-${d.productId}`, templateId: t.id, name: t.name, category: t.category, x: d.x, y: d.y, rotation: d.rotation || 0, width: t.width, depth: t.depth, color: t.color } as ProductInstance;
}

/** Commit the in-progress flooring polygon as a Flooring of the active tool's
 *  kind; a sloping floor gets a default drain at the centroid. */
function finishFloor(): void {
  const kind = floorToolKind(state.tool);
  if (kind && floorDraft.length >= 3) {
    pushHistory();
    const points = floorDraft.map((p) => ({ x: p.x, y: p.y }));
    const floor: Flooring = { id: newId('floor'), kind, points };
    if (floorIsSloping(kind)) {
      const t = drainTemplates()[0];
      const c = floorCentroid(points);
      floor.drain = { productId: t?.id ?? 'builtin-drain', ...(t ? drainNames(t) : {}), x: c.x, y: c.y, rotation: 0 };
    }
    (state.drawing.floorings ??= []).push(floor);
    deselectAll();
    selectOne(state, 'flooring', floor.id);
    endOneShot();
    renderProperties();
  }
  floorDraft = [];
  invalidate();
}

/** The selected flooring, or null. */
export function selectedFloor(): Flooring | null {
  return state.selectedFloorId ? (state.drawing.floorings ?? []).find((f) => f.id === state.selectedFloorId) ?? null : null;
}

/** Whether a world point lies on a floor's drain footprint (box / circle). */
function floorDrainHit(floor: Flooring, world: Vec2): boolean {
  if (!floor.drain) return false;
  const inst = drainInstance(floor.drain);
  if (!inst) return false;
  const lp = toInstanceLocal(inst, world);
  return Math.abs(lp.x) <= inst.width / 2 + 6 / vp.scale && Math.abs(lp.y) <= inst.depth / 2 + 6 / vp.scale;
}

/** Screen position of a floor drain's rotation knob (off its +Y edge). */
function floorDrainRotateHandle(floor: Flooring): Vec2 | null {
  if (!floor.drain) return null;
  const inst = drainInstance(floor.drain);
  if (!inst) return null;
  const off = inst.depth / 2 + 24 / vp.scale;
  return vp.worldToScreen(instanceToWorld(inst, { x: 0, y: off }));
}

/** Recompute the temporary drain dimensions (to every floor edge). */
function updateFloorDrainDims(floor: Flooring): void {
  state.floorDrainDims = floor.drain ? edgeDistances({ x: floor.drain.x, y: floor.drain.y }, floor.points) : [];
}

/** Commit a rectangle (from two opposite corners) as a closed, axis-locked
 *  polygon — mirrors finishPolygon's placement (main drawing or drawing editor). */
export function finishRect(a: Vec2, b: Vec2): void {
  rectStart = null;
  const x0 = Math.min(a.x, b.x), x1 = Math.max(a.x, b.x), y0 = Math.min(a.y, b.y), y1 = Math.max(a.y, b.y);
  if (x1 - x0 < 1 || y1 - y0 < 1) { endOneShot(); invalidate(); return; }
  pushHistory();
  const poly: Polygon = {
    id: newId('poly'),
    points: [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }], // CCW
    fill: '#ffffff', fillOpacity: 1, stroke: '#222222', strokeOpacity: 1,
  };
  const inst = drawEditInst();
  if (inst) {
    poly.points = poly.points.map((p) => toInstanceLocal(inst, p));
    poly.z = nextDrawZ(inst);
    (inst.drawPolygons ??= []).push(poly);
    state.drawEditSelectedPolyId = poly.id;
    state.drawEditSelectedWallId = null;
    state.drawEditVerticesId = null;
  } else {
    poly.edgeConstraints = autoAxisLocks(poly.points);
    (state.drawing.polygons ??= []).push(poly);
    deselectAll(); // the new object alone is selected
    selectOne(state, 'polygon', poly.id);
  }
  endOneShot();
  renderProperties();
  invalidate();
}

/** Resolve the arc draft (3 points) to centre/radius/angles. 3-point mode fits a
 *  circle through all three; SCE mode takes p0 as centre, p1 the start, p2 the
 *  end direction. Returns null for a degenerate (collinear / zero-radius) input. */
function arcFromDraft(pts: Vec2[]): { center: Vec2; radius: number; startDeg: number; endDeg: number } | null {
  const deg = (p: Vec2, c: Vec2): number => (Math.atan2(p.y - c.y, p.x - c.x) * 180) / Math.PI;
  if (drawingTab.arcMode === 'sce') {
    const [c, s, e] = pts;
    const radius = distance(c, s);
    if (radius < 1) return null;
    return { center: c, radius, startDeg: deg(s, c), endDeg: deg(e, c) }; // CCW start→end
  }
  return arcThroughPoints(pts[0], pts[1], pts[2]);
}

/** Live preview data for the arc tool: the placed dots plus, once three points
 *  are available (2 placed + hover, or 3 placed), the fitted arc. */
function arcPreview(): { dots: Vec2[]; fit: { center: Vec2; radius: number; startDeg: number; endDeg: number } | null } | null {
  if (state.tool !== 'arc' || !arcDraft.length) return null;
  const dots = state.hoverWorld ? [...arcDraft, state.hoverWorld] : [...arcDraft];
  return { dots, fit: dots.length >= 3 ? arcFromDraft(dots.slice(0, 3)) : null };
}

function finishArc(): void {
  const a = arcFromDraft(arcDraft);
  arcDraft = [];
  if (!a) { endOneShot(); invalidate(); return; }
  pushHistory();
  const arc: Arc = { id: newId('arc'), ...a, stroke: LINE_DEFAULT_COLOR, strokeWidth: LINE_DEFAULT_THICKNESS };
  (state.drawing.arcs ??= []).push(arc);
  endOneShot();
  deselectAll(); // the new object alone is selected
  selectOne(state, 'arc', arc.id);
  renderProperties();
  invalidate();
}

/** Commit the in-progress leader (needs an arrow tip + at least one more point)
 *  and select it so its label can be typed in the properties panel. */
function finishLeader(): void {
  if (leaderDraft.length < 2) { leaderDraft = []; endOneShot(); invalidate(); return; }
  pushHistory();
  const leader: Leader = { id: newId('leader'), points: leaderDraft.map((p) => ({ x: p.x, y: p.y })), text: '', color: ANNOTATION_COLOR };
  (state.drawing.leaders ??= []).push(leader);
  leaderDraft = [];
  endOneShot();
  deselectAll(); // the new object alone is selected
  selectOne(state, 'leader', leader.id);
  renderProperties();
  invalidate();
  // Focus the label field so the user can type straight away.
  const ta = document.querySelector('#properties-body textarea') as HTMLTextAreaElement | null;
  ta?.focus();
}

/** The revision new clouds are tagged with: `drawing.currentRevisionId` if it
 *  still points at a real revision, else the most recent one. Creates a first
 *  revision ("A", today) if none exist yet, so a cloud always has something
 *  to tag. */
function activeRevisionId(): string {
  const revs = state.drawing.revisions;
  if (revs && revs.length) {
    if (state.drawing.currentRevisionId && revs.some((r) => r.id === state.drawing.currentRevisionId)) return state.drawing.currentRevisionId;
    return revs[revs.length - 1].id;
  }
  const rev: Revision = { id: newId('rev'), tag: 'A', date: new Date().toISOString().slice(0, 10), description: '' };
  state.drawing.revisions = [rev];
  state.drawing.currentRevisionId = rev.id;
  return rev.id;
}

function finishRevisionCloud(): void {
  if (revCloudDraft.length >= 3) {
    pushHistory();
    const cloud: RevisionCloud = {
      id: newId('revcloud'),
      points: revCloudDraft.map((p) => ({ x: p.x, y: p.y })),
      revisionId: activeRevisionId(),
    };
    if (!state.drawing.revisionClouds) state.drawing.revisionClouds = [];
    state.drawing.revisionClouds.push(cloud);
    deselectAll(); // the new object alone is selected
    selectOne(state, 'cloud', cloud.id);
    endOneShot();
    renderProperties();
  }
  revCloudDraft = [];
  invalidate();
}

/** Arm the polygonal-room tool for a polyRoom product (drawn by clicking vertices). */
function startPolyRoom(t: ProductTemplate): void {
  polyRoomTemplate = t;
  polyRoomDraft = [];
  setTool('poly-room');
  polyRoomTemplate = t; // setTool clears it; restore for the active draw
  clearSelection(state);
  renderProperties();
  invalidate();
}

/**
 * Build a walled sauna/steam room from an outer-wall polygon (world points). The
 * vertices become the OUTER perimeter (renderer offsets walls inward); the door
 * is auto-placed on the first edge. Shared by the free-form poly-room tool and
 * the two-click rectangle-room tool. Selects the new room.
 */
function commitRoomPolygon(t: ProductTemplate, worldPts: Vec2[]): void {
  if (worldPts.length < 3) return;
  const xs = worldPts.map((p) => p.x), ys = worldPts.map((p) => p.y);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
  // Local frame centred on the bbox; drawn vertex order kept (renderer handles
  // either winding) so the "last-added vertex" dimension rule stays meaningful.
  const local = worldPts.map((p) => ({ x: p.x - cx, y: p.y - cy }));
  const width = Math.max(1, maxX - minX), depth = Math.max(1, maxY - minY);
  const edge0Len = Math.hypot(local[1].x - local[0].x, local[1].y - local[0].y);
  const inst: ProductInstance = {
    ...placeCopy(t, prepId),
    id: newId('inst'),
    x: cx,
    y: cy,
    rotation: 0,
    width,
    depth,
    autoBenches: true,
    polygon: local,
    // Lock already-axis-aligned walls so a squared room stays square on edits.
    polygonConstraints: autoAxisLocks(local),
    doors: [defaultDoor(edge0Len, undefined, t.category)],
    glassHeight: 2000,
    height: t.height ?? defaultSaunaHeight(depth),
    benchModel: t.category === 'steamroom' ? 'A' : undefined,
  };
  regenerateSauna(inst);
  state.drawing.instances.push(inst);
  if (inst.category === 'steamroom') addSteamRoomFixtures(inst); // centre drain + door channel
  selectOne(state, 'instance', inst.id);
  leaveEditModes();
}


/** Commit the in-progress free-form polygonal room (>= 3 vertices). */
function finishPolyRoom(): void {
  const t = polyRoomTemplate;
  if (t && polyRoomDraft.length >= 3) {
    pushHistory();
    commitRoomPolygon(t, polyRoomDraft.map((p) => ({ x: p.x, y: p.y })));
  }
  polyRoomDraft = [];
  polyRoomTemplate = null;
  setTool('select');
  renderProperties();
  invalidate();
}

/** Arm the two-click rectangle-room tool for template `t`. */
function startRectRoom(t: ProductTemplate): void {
  rectRoomTemplate = t;
  rectRoomStart = null;
  setTool('rect-room');
  rectRoomTemplate = t; // setTool clears the draft; restore for the active draw
  clearSelection(state);
  renderProperties();
  invalidate();
}

/** Commit a rectangular room from two opposite corners (world). Edge 0 (the door
 *  edge) is the bottom side, matching the drawn rectangle's lower edge. */
export function finishRectRoom(a: Vec2, b: Vec2): void {
  const t = rectRoomTemplate;
  if (t && Math.abs(a.x - b.x) > 1 && Math.abs(a.y - b.y) > 1) {
    const x0 = Math.min(a.x, b.x), x1 = Math.max(a.x, b.x);
    const y0 = Math.min(a.y, b.y), y1 = Math.max(a.y, b.y);
    pushHistory();
    // CCW (Y-up); first edge is the bottom (y0) side so the door lands there.
    commitRoomPolygon(t, [{ x: x0, y: y0 }, { x: x1, y: y0 }, { x: x1, y: y1 }, { x: x0, y: y1 }]);
  }
  rectRoomStart = null;
  rectRoomTemplate = null;
  setTool('select');
  renderProperties();
  invalidate();
}

/** Build the live preview for the repeat tool: the baseline (committed vertices
 *  plus the cursor) and the tiled copies. */
function repeatPreview(): RepeatPreview | null {
  if (state.tool !== 'repeat' || !repeatTemplate) return null;
  const t = repeatTemplate;
  const hover = nextPoint();
  const pts = hover ? [...repeatDraft, hover] : [...repeatDraft];
  if (pts.length === 0) return null;
  const sideSign = repeatSideSign(repeatDraft, hover);
  repeatSideSignLast = sideSign;
  const copies = pts.length >= 2 ? repeatCopies(pts, t.width, t.depth, repeatPitch(t), sideSign) : [];
  return { pts, copies, width: t.width, depth: t.depth, color: t.color, geometry: t.geometry ?? null };
}

/** Arm the repeatable-product tool: the user draws a polyline along which copies
 *  of `t` are tiled. */
function startRepeat(t: ProductTemplate): void {
  repeatTemplate = t;
  repeatDraft = [];
  setTool('repeat');
  repeatTemplate = t; // setTool clears it; restore for the active draw
  clearSelection(state);
  renderProperties();
  invalidate();
}

/** Snap a repeat-tool vertex: standard point snap, then orthogonal lock + vertex
 *  alignment when Shift is held (like the line tool). */
function repeatSnap(world: Vec2, shift: boolean): Vec2 {
  const nv = shift ? [] : nearbyOrthoVerts(world);
  state.drawSnapVerts = nv;
  return snapDraftPoint(world, [...repeatDraft, ...nv], { shift, allow45: true, lastSeg: draftSeg(repeatDraft) });
}

/** Circle/ellipse draw snap: proximity + orthogonal alignment to nearby objects'
 *  vertices (and, once the centre is placed, the centre + a 45° ray from it). */
function circleSnap(world: Vec2, shift: boolean): Vec2 {
  const nv = shift ? [] : nearbyOrthoVerts(world);
  state.drawSnapVerts = nv;
  const anchor = circleCenter ? [circleCenter] : [];
  const p = snapDraftPoint(world, [...anchor, ...nv], { shift, allow45: !!circleCenter, lastSeg: circleCenter ? [circleCenter, circleCenter] : undefined });
  return { x: Math.round(p.x), y: Math.round(p.y) }; // 1mm precision
}

/** Start drawing a polyline-bench (heatedbench or saltwall). */
function startBenchPoly(t: ProductTemplate, world: Vec2): void {
  benchPolyTemplate = t;
  benchPolyDraft = [{ x: world.x, y: world.y }];
  setTool('bench-poly');
  benchPolyTemplate = t; // setTool clears it; restore
  clearSelection(state);
  renderProperties();
  invalidate();
}

/** Commit the in-progress bench-poly polyline as a placed instance. */
function finishBenchPoly(): void {
  const t = benchPolyTemplate;
  if (!t || benchPolyDraft.length < 2) return;
  const nodes = benchPolyDraft.map((p) => ({ x: p.x, y: p.y }));
  // Total path length becomes the stored width (used for label / schedule).
  let totalLen = 0;
  for (let i = 0; i < nodes.length - 1; i++) totalLen += Math.hypot(nodes[i + 1].x - nodes[i].x, nodes[i + 1].y - nodes[i].y);
  // Centroid of the nodes as the instance position (for connections, labels, etc.).
  const cx = nodes.reduce((s, n) => s + n.x, 0) / nodes.length;
  const cy = nodes.reduce((s, n) => s + n.y, 0) / nodes.length;
  pushHistory();
  // A sauna bench's depth is its slats' (placeCopy), not the template's nominal one.
  const inst: ProductInstance = { ...placeCopy(t, prepId), id: newId('inst'), x: cx, y: cy, rotation: 0, width: Math.round(totalLen), nodes };
  state.drawing.instances.push(inst);
  selectOne(state, 'instance', inst.id);
  leaveEditModes();
  setTool('select');
  renderProperties();
  invalidate();
}

/** Snap a bench-poly vertex (routes through the shared draft snapper). */
function benchPolySnap(world: Vec2, shift: boolean): Vec2 {
  const nv = shift ? [] : nearbyOrthoVerts(world);
  state.drawSnapVerts = nv;
  return snapDraftPoint(world, [...benchPolyDraft, ...nv], { shift, allow45: true, lastSeg: draftSeg(benchPolyDraft) });
}

/** A new bench-poly vertex must sit at least the bench depth away from every
 *  existing vertex, so no segment is shorter than the object is deep. */
function benchPolyVertexTooClose(p: Vec2): boolean {
  const depth = benchPolyTemplate?.depth ?? 0;
  if (depth <= 0) return false;
  return benchPolyDraft.some((v) => Math.hypot(p.x - v.x, p.y - v.y) < depth);
}



// ---- Repeatable placement: the copies along the drawn line, and placing them ----

/** The repeat pitch (centre-to-centre along the line) for template `t`. */
function repeatPitch(t: ProductTemplate): number {
  const offset = typeof t.offset === 'number' ? t.offset : 0;
  return Math.max(1, t.width + offset);
}

/**
 * Which side of the baseline the product bodies extend toward: +1 = left of the
 * first segment's travel direction, -1 = right. Determined by the cursor's
 * perpendicular position relative to the first committed segment; defaults to +1
 * while only the first segment is being drawn.
 */
function repeatSideSign(verts: Vec2[], hover: Vec2 | null): number {
  if (verts.length < 2 || !hover) return 1;
  const a = verts[0], b = verts[1];
  const dx = b.x - a.x, dy = b.y - a.y;
  // Left normal of (a->b) is (-dy, dx); the sign of (hover-a)·leftNormal picks the side.
  const cross = (hover.x - a.x) * -dy + (hover.y - a.y) * dx;
  return cross >= 0 ? 1 : -1;
}

interface RepeatCopy { x: number; y: number; rotation: number; }

/**
 * Tile copies of a `width`-wide product along `path` (world polyline), spaced by
 * `pitch`. Each copy's top edge lies on the path starting at a multiple of the
 * pitch; copies that would straddle a corner are skipped. Returns the centre and
 * rotation (deg) of each copy, with the body extending to the `sideSign` side.
 */
function repeatCopies(path: Vec2[], width: number, depth: number, pitch: number, sideSign: number): RepeatCopy[] {
  if (path.length < 2 || width <= 0) return [];
  // Cumulative arc length and per-segment data.
  const segs: { a: Vec2; len: number; dx: number; dy: number; start: number }[] = [];
  let total = 0;
  for (let i = 0; i + 1 < path.length; i++) {
    const a = path[i], b = path[i + 1];
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len < 1e-6) continue;
    segs.push({ a, len, dx: (b.x - a.x) / len, dy: (b.y - a.y) / len, start: total });
    total += len;
  }
  if (!segs.length) return [];
  const locate = (arc: number): { p: Vec2; dx: number; dy: number; seg: number } | null => {
    for (let i = 0; i < segs.length; i++) {
      const s = segs[i];
      if (arc >= s.start - 1e-6 && arc <= s.start + s.len + 1e-6) {
        const d = arc - s.start;
        return { p: { x: s.a.x + s.dx * d, y: s.a.y + s.dy * d }, dx: s.dx, dy: s.dy, seg: i };
      }
    }
    return null;
  };
  const copies: RepeatCopy[] = [];
  for (let k = 0; ; k++) {
    const a0 = k * pitch;
    if (a0 + width > total + 1e-6) break;
    const l0 = locate(a0), l1 = locate(a0 + width);
    if (!l0 || !l1 || l0.seg !== l1.seg) continue; // straddles a corner — skip
    const dx = l0.dx, dy = l0.dy;
    // Left normal of travel; flip for the chosen side.
    const nx = -dy * sideSign, ny = dx * sideSign;
    // Centre = top-edge start + half-width along the line + half-depth into the body.
    const cxw = l0.p.x + dx * (width / 2) + nx * (depth / 2);
    const cyw = l0.p.y + dy * (width / 2) + ny * (depth / 2);
    const baseDeg = Math.atan2(dy, dx) * 180 / Math.PI;
    copies.push({ x: cxw, y: cyw, rotation: sideSign >= 0 ? baseDeg : baseDeg + 180 });
  }
  return copies;
}

/** Commit the in-progress repeat polyline: turn each tiled copy into a grouped
 *  product instance. */
function finishRepeat(): void {
  const t = repeatTemplate;
  if (t && repeatDraft.length >= 2) {
    const sideSign = repeatSideSignLast;
    const copies = repeatCopies(repeatDraft, t.width, t.depth, repeatPitch(t), sideSign);
    if (copies.length) {
      pushHistory();
      // The row is one group (a click takes the whole row); a row of one is none.
      const groupId = newId('grp');
      if (copies.length > 1) (state.drawing.groups ??= []).push({ id: groupId });
      let firstId: string | null = null;
      for (const c of copies) {
        const inst: ProductInstance = {
          ...placeCopy(t, prepId),
          id: newId('inst'),
          ...(copies.length > 1 ? { groupId } : {}),
          x: c.x,
          y: c.y,
          rotation: c.rotation,
        };
        if (!firstId) firstId = inst.id;
        state.drawing.instances.push(inst);
      }
      if (firstId) selectOne(state, 'instance', firstId); else clearSelection(state);
      leaveEditModes();
    }
  }
  repeatDraft = [];
  repeatTemplate = null;
  setTool('select');
  renderProperties();
  invalidate();
}

// ---- Polygon edge selection & edge operations ----

/**
 * The face-dimension edge under a world point, or -1. The overlap-avoiding
 * renderer can push a label out to several offset steps (or to the other side),
 * so accept the cursor anywhere in the band just outside an edge rather than only
 * at the nominal 18px offset, and pick the closest edge.
 */
/** Which edge of `fdt` the figure under screen point `sp` measures: the edge its
 *  label names (`DIM_TAG_FACE_EDGE` — a wall's faces lie closer together than a
 *  probe sits off its segment), else the edge its probe resolves to. */
export function faceDimEdgeAt(fdt: FaceDimTarget, sp: Vec2, probe: Vec2): { edge: number; inside: boolean } {
  const tag = dimLabelTagAt(planHits, sp);
  if (tag?.startsWith(DIM_TAG_FACE_EDGE)) {
    const edge = Number(tag.slice(DIM_TAG_FACE_EDGE.length));
    if (Number.isInteger(edge) && edge >= 0 && edge < fdt.ring.length) return { edge, inside: false };
  }
  return faceDimAtPoints(fdt.ring, probe, fdt.ellipse, fdt.bothSides, fdt.closed ?? true);
}

function faceDimAtPoints(pts: Vec2[], world: Vec2, ellipse = false, bothSides = false, closed = true): { edge: number; inside: boolean } {
  const n = pts.length;
  if (ellipse || n < (closed ? 3 : 2)) return { edge: -1, inside: false };
  let cx = 0, cy = 0;
  for (const p of pts) { cx += p.x; cy += p.y; }
  cx /= n; cy /= n;
  const near = 4 / vp.scale;              // ignore points right on the edge
  // Out to the farthest lane drawDimSegments will step a label to (12 lanes of
  // 20 px past a 44 px first gap, + the label's own offset). The band only picks
  // WHICH edge a point belongs to — the caller has already required a hit on the
  // figure itself (`dimLabelAt`), so it no longer has to be tight.
  let best = -1, bestPerp = Infinity, bestInside = false;
  const edges = closed ? n : n - 1;       // an open polyline has no wrap edge
  for (let i = 0; i < edges; i++) {
    const a = pts[i], b = pts[(i + 1) % n];
    const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy);
    if (len < 1e-6) continue;
    const ex = dx / len, ey = dy / len;   // edge direction
    let nx = -ey, ny = ex;                 // outward normal
    if ((a.x - cx) * nx + (a.y - cy) * ny < 0) { nx = -nx; ny = -ny; }
    const along = (world.x - a.x) * ex + (world.y - a.y) * ey;     // mm along the edge
    const t = along / len;                                          // 0..1 along edge
    if (t < -0.1 || t > 1.1) continue;
    const perp = (world.x - a.x) * nx + (world.y - a.y) * ny;      // +ve = outside the edge
    // Outward always; also just-inside when the shape draws interior dimensions.
    if (perp < 0 && !bothSides) continue;
    // Rank by the true distance to the SEGMENT, not the infinite line: a point off
    // the end (t<0 / t>1) is near a CORNER, and the along-overshoot has to count or
    // its bare perpendicular can beat the wall it actually sits outside of — which
    // sent a corner figure's edit to the neighbouring wall's interior dimension.
    const over = t < 0 ? -along : t > 1 ? along - len : 0;
    const d = Math.hypot(perp, over);
    if (Math.abs(perp) < near || d > 300 / vp.scale) continue;
    if (d < bestPerp) { bestPerp = d; best = i; bestInside = perp < 0; }
  }
  return { edge: best, inside: bestInside };
}

/** Index of the polygon edge under a world point (mid-edge only, ends excluded), or -1. */
function polyEdgeAt(poly: Polygon, world: Vec2, tol: number): number {
  if (poly.ellipse) return -1; // ellipses have no editable edges
  const pts = poly.points, n = pts.length;
  let best = -1, bestD = tol;
  for (let i = 0; i < n; i++) {
    const a = pts[i], b = pts[(i + 1) % n];
    const dx = b.x - a.x, dy = b.y - a.y;
    const l2 = dx * dx + dy * dy;
    if (l2 < 1e-9) continue;
    const t = ((world.x - a.x) * dx + (world.y - a.y) * dy) / l2;
    // Reserve a band near each vertex for the corner handles — a fixed world
    // distance (the pick tolerance), capped at 30% so short edges stay clickable.
    // Distance-based means a long edge is selectable along nearly its whole span,
    // not just a fixed middle fraction.
    const reserve = Math.min(0.3, tol / Math.sqrt(l2));
    if (t < reserve || t > 1 - reserve) continue;
    const d = Math.hypot(world.x - (a.x + t * dx), world.y - (a.y + t * dy));
    if (d < bestD) { bestD = d; best = i; }
  }
  return best;
}

// ---- Edge orientation locks (horizontal / vertical constraints) ----

/** The lock on a polygon's edge `e` (vertex e → e+1), or null. */
export function edgeConstraintOf(poly: Polygon, e: number): 'h' | 'v' | null {
  return poly.edgeConstraints?.[e] ?? null;
}

/** True if the polygon has any locked edge. */
function polyHasConstraints(poly: Polygon): boolean {
  return !!poly.edgeConstraints?.some(Boolean);
}

/** Set/clear edge `e`'s lock, keeping the array sized to the vertices. */
export function setEdgeConstraint(poly: Polygon, e: number, c: 'h' | 'v' | null): void {
  const n = poly.points.length;
  const arr = (poly.edgeConstraints ?? []).slice(0, n);
  while (arr.length < n) arr.push(null);
  arr[e] = c;
  poly.edgeConstraints = arr.some(Boolean) ? arr : undefined;
}

/** Every locked edge of `poly` is exactly horizontal/vertical in `pts`. */
export function constraintsSatisfied(poly: Polygon, pts: Vec2[]): boolean {
  const cons = poly.edgeConstraints;
  if (!cons) return true;
  const n = pts.length;
  for (let e = 0; e < n && e < cons.length; e++) {
    const c = cons[e];
    if (!c) continue;
    const a = pts[e], b = pts[(e + 1) % n];
    if (c === 'h' && Math.abs(a.y - b.y) > 0.5) return false;
    if (c === 'v' && Math.abs(a.x - b.x) > 0.5) return false;
  }
  return true;
}

/** Lock a dragged outer-ring vertex onto its incident locked edges' axes. */
function constrainDraggedVertex(poly: Polygon, idx: number, pos: Vec2): Vec2 {
  const cons = poly.edgeConstraints;
  if (!cons) return pos;
  const n = poly.points.length;
  let x = pos.x, y = pos.y;
  const prevE = (idx - 1 + n) % n, curE = idx;
  const prev = poly.points[(idx - 1 + n) % n], next = poly.points[(idx + 1) % n];
  if (cons[prevE] === 'h') y = prev.y; else if (cons[prevE] === 'v') x = prev.x;
  if (cons[curE] === 'h') y = next.y; else if (cons[curE] === 'v') x = next.x;
  return { x, y };
}

/** Commit the in-progress wall (needs at least 2 vertices) and select it. */
function finishWall(): void {
  if (wallDraft.length >= 2) {
    pushHistory();
    const wall: Wall = {
      id: newId('wall'),
      points: wallDraft.map((p) => ({ x: p.x, y: p.y })),
      thickness: WALL_DEFAULT_THICKNESS,
      align: 'center',
      fill: '#e9e9e9',
      fillOpacity: 1,
    };
    const deInst = drawEditInst();
    if (deInst) {
      wall.points = wall.points.map((p) => toInstanceLocal(deInst, p));
      wall.z = nextDrawZ(deInst);
      (deInst.drawWalls ??= []).push(wall);
      state.drawEditSelectedWallId = wall.id;
      state.drawEditSelectedPolyId = null;
      state.drawEditVerticesId = null;
    } else {
      if (!state.drawing.walls) state.drawing.walls = [];
      state.drawing.walls.push(wall);
      deselectAll(); // the new object alone is selected
      selectOne(state, 'wall', wall.id);
    }
    endOneShot();
    renderProperties();
  }
  wallDraft = [];
  invalidate();
}

/** Finish the line being drawn: store it as a `style: 'line'` wall and select it. */
function finishLine(): void {
  if (lineDraft.length >= 2) {
    pushHistory();
    const line: Wall = {
      id: newId('line'),
      points: lineDraft.map((p) => ({ x: p.x, y: p.y })),
      style: 'line',
      thickness: LINE_DEFAULT_THICKNESS,
      align: 'center',
      fill: LINE_DEFAULT_COLOR,
      fillOpacity: 1,
      height: LINE_DEFAULT_HEIGHT,
    };
    const deInst = drawEditInst();
    if (deInst) {
      line.points = line.points.map((p) => toInstanceLocal(deInst, p));
      line.z = nextDrawZ(deInst);
      (deInst.drawWalls ??= []).push(line);
      state.drawEditSelectedWallId = line.id;
      state.drawEditSelectedPolyId = null;
      state.drawEditVerticesId = null;
    } else {
      if (!state.drawing.walls) state.drawing.walls = [];
      state.drawing.walls.push(line);
      deselectAll(); // the new object alone is selected
      selectOne(state, 'wall', line.id);
    }
    endOneShot();
    renderProperties();
  }
  lineDraft = [];
  invalidate();
}


/** Build the live dimension preview while the dimension tool is in use. */
function dimensionPreview(): { a: Vec2; b: Vec2; offset: number; kind?: DimKind } | null {
  if (state.tool !== 'dimension' || !state.pendingA || !state.hoverWorld) return null;
  if (!state.pendingB) return { a: state.pendingA, b: state.hoverWorld, offset: 0 };
  // The preview carries the inferred kind so the line snaps between horizontal,
  // vertical and aligned under the cursor — the pick is not a guess you discover
  // after clicking.
  const kind = inferDimKind(state.pendingA, state.pendingB, state.hoverWorld);
  return { a: state.pendingA, b: state.pendingB, offset: dimOffset(state.pendingA, state.pendingB, state.hoverWorld, kind), kind };
}

canvas.addEventListener('contextmenu', (e) => {
  e.preventDefault();
  // An armed pick is let go first, as Esc does.
  if (cancelArmed()) return;
  // A handle or a grip being worked: a right-click drops it where it is, as Enter.
  if (finishUnderWay()) return;
  // The title-block editor owns the canvas: falling through to the model handling
  // below acted on model objects hidden behind the page.
  if (titleEditor.titleEdit) { showTitleObjMenu(e); return; }
  // Schematic sheet: right-click finishes the work in progress, as Enter does,
  // else lets go of the step in progress.
  const shCtx = activeSheet();
  if (shCtx && !titleEditor.titleEdit) {
    if (finishPaperWork(shCtx)) return;
    if (PAPER_LIFE[schTool].drop?.()) { state.distEntry = null; updateDistEntry(); updateStatus(); invalidate(); return; }
    // Right-click a connection's routing point → remove it (the line re-routes).
    const wp = schWaypointAt(shCtx, paperView(), screenPos(e));
    if (wp) {
      const cn = schContent(shCtx).conns.find((c) => c.id === wp.connId);
      if (cn?.waypoints) {
        pushHistory();
        cn.waypoints.splice(wp.index, 1);
        if (cn.waypoints.length === 0) cn.waypoints = undefined;
        invalidate();
      }
      return;
    }
    // Right-click an object (or empty paper) → the shared menu.
    showSheetObjectMenu(e, shCtx);
    return;
  }
  // Constrain: a right-click drops the first pick (or the typed relation).
  if (state.tool === 'constrain' && (constrainFirst || constraintUi.constrainType)) { resetConstrainTool(); updateStatus(); invalidate(); return; }
  // Right-click finishes the work in progress, as Enter does.
  if (finishWork()) return;
  // Right-click a pinned wall figure → release it, the way a locked segment length
  // is released from its panel. Without this a typed figure could never be unpinned.
  if (showWallDimMenu(e)) return;
  // Right-click a text box → menu to enlarge / reduce its font.
  const txt = objectAt('text', vp.screenToWorld(screenPos(e)));
  if (txt && !objUnpickable(txt.id)) {
    if (!selectedOf(state, 'text').includes(txt.id)) { deselectAll(); selectOne(state, 'text', txt.id); renderProperties(); invalidate(); }
    showObjectContextMenu(e);
    return;
  }
  if (removePipeBendInView(screenPos(e))) return;
  // Right-click a connection's mid-route vertex to delete it; the line readjusts.
  const wp = hitConnectionWaypoint(vp.screenToWorld(screenPos(e)));
  if (wp) {
    pushHistory();
    connEdit(wp.conn, { kind: 'bendRemove', i: wp.index });
    invalidate();
    return;
  }
  // Right-click a node of the selected polyline salt-wall / heated-bench to delete
  // it (keeping at least 2 nodes).
  const ndSel = selectedInstance();
  const ndIdx = ndSel?.nodes && canRemoveVertex(nodeShim(ndSel), 0) ? vertexAtScreen(ndSel.nodes, screenPos(e)) : -1;
  if (ndSel && ndIdx >= 0) { removeNode(ndSel, ndIdx); return; }

  // What is edited in place takes the right-click when its row says so
  // (EDIT_LIFE): a corner or vertex under it goes.
  const editCtx = state.editing ? EDIT_LIFE[state.editing.kind].context : null;
  if (editCtx) { editCtx(screenPos(e)); return; }

  // Plain select mode, nothing special under the cursor: show the object menu.
  if (state.tool === 'select') showObjectContextMenu(e);
});

/** A press in a section view (`sectionClick`), released where it was pressed:
 *  true when it took the release, and the group drill and the Alt+click cycle
 *  with it. */
function upSectionClick(e: MouseEvent): boolean {
  const sc = sectionClick;
  sectionClick = null;
  if (sc && !activeSheet()) {
    const up = screenPos(e);
    // A click in a section view is that view's (its Alt+click cycles in it).
    if (Math.hypot(up.x - sc.sp.x, up.y - sc.sp.y) < 3) { drillSectionAt(sc.id, sc.world, sc.alt); groupDrill = null; cycleProbe = null; return true; }
  }
  return false;
}

/** A plain click on a member of the selected group (`groupDrill`), released
 *  where it was pressed, drills one level in. */
function upGroupDrill(e: MouseEvent): void {
  const gd = groupDrill;
  groupDrill = null;
  if (gd && !activeSheet()) {
    const up = screenPos(e);
    if (Math.hypot(up.x - gd.sp.x, up.y - gd.sp.y) < 3) { deselectAll(); setObjectSelection(selectionOfIds(state.drawing, gd.ids)); }
  }
}

/** Alt+click, released where it was pressed, steps from what was selected before
 *  the press to the next object under it. */
function upAltCycle(e: MouseEvent): void {
  const cp = cycleProbe;
  cycleProbe = null;
  if (!cp || activeSheet() || !cp.prev.length) return;
  const up = screenPos(e);
  if (Math.hypot(up.x - cp.sp.x, up.y - cp.sp.y) >= 3) return;
  const had = new Set(cp.prev.map(refKey));
  if (objectStackAt(cp.world, cp.sp).tab.some((r) => had.has(refKey(r)))) cycleSelectionAt(cp.world, cp.sp, 1, cp.prev);
}
// A handle or a grip a click armed is dropped where it is by a press anywhere
// else — a panel, the toolbar, a menu —, its step recorded before that press acts.
window.addEventListener('pointerdown', (e) => {
  const t = e.target as Node | null;
  finishArmedOffUnderWay((space) => (space === '3d' ? $('view3d-overlay') : canvas).contains(t));
}, true);
canvas.addEventListener('mouseenter', () => { pointerOnCanvas = true; });
canvas.addEventListener('mousemove', (e) => { pointerOnCanvas = true; lastPointer = screenPos(e); });
canvas.addEventListener('mouseleave', () => { pointerOnCanvas = false; });

// Registered after the main mousedown handler, so it runs once the press has
// been acted on: the prompt (and its [Close/Undo] options) reflects the new point
// straight away instead of on the next mouse move. A press acts on what was
// outlined, so the hover outline goes too (the next move brings back whatever is
// under the cursor then).
canvas.addEventListener('mousedown', () => { setCanvasHover(null); updateStatus(); });

// Double-click: finish a drawing in progress, otherwise enter/extend an edit mode.
canvas.addEventListener('dblclick', (e) => {
  if (activeSheet()) { sheetDblClick(e); return; }
  if (pressFinishedDraft) return;
  // A constraint's figure (a driving dimension) or a valued badge: edit its value.
  if (state.tool === 'select') {
    const cid = badgeAt(constraintBadgeBoxes, screenPos(e));
    if (cid) { editConstraintValueAt(cid, screenPos(e)); return; }
  }
  // A double-click finishes the work in progress, as Enter does.
  if (finishWork()) return;
  // Double-clicking a dimension edits its value — also while the dimension tool
  // is active, not only with the select tool.
  if (state.tool === 'dimension') {
    const w = vp.screenToWorld(screenPos(e));
    const did = hitDimension(w, screenPos(e));
    if (did) {
      selectOne(state, 'dim', did);
      renderProperties();
      invalidate();
      openDimEditor(did, screenPos(e));
    }
    return;
  }
  if (state.tool !== 'select') return;
  const world = vp.screenToWorld(screenPos(e));

  // Schedule-table cell: double-click a part name or description to edit it.
  const cellHit = scheduleCellHitAt(screenPos(e));
  if (cellHit && tableReachable(cellHit.kind, objectStackAt(world, screenPos(e)).seen)) {
    selectOne(state, 'table', cellHit.kind);
    renderProperties();
    openCellEditor(cellHit.kind, cellHit.rowId, cellHit.col, cellHit.edit);
    return;
  }

  // What is edited in place takes the double-click when its row says so
  // (EDIT_LIFE): a room or a part's drawing adds a corner on an edge, or leaves.
  const editDbl = state.editing ? EDIT_LIFE[state.editing.kind].dblclick : null;
  if (editDbl) { editDbl(screenPos(e), world); return; }

  // Double-click a temporary face dimension to edit that edge's length — checked
  // before entering a room's vertex-edit so a click on an interior dimension (just
  // inside a wall) edits it, while a click deeper in the room still enters edit.
  // A rounded corner's R dimension retypes the radius — same double-click gesture
  // as every other temporary dimension. Checked first: its leader runs outside
  // the room, where it can overlap a wall's face dimension.
  // A selected bench support's figure: typing a distance moves the support.
  const supFig = supportFiguresShown[supportFigureAt(planHits, screenPos(e))]?.edit;
  if (supFig) {
    const fig = supportFiguresShown[supportFigureAt(planHits, screenPos(e))];
    openFigureEditor({ kind: 'support', edit: supFig }, String(Math.round(fig.length)), screenPos(e));
    return;
  }
  const crHit = cornerRadiusDimAt(screenPos(e));
  if (crHit) {
    openFigureEditor({ kind: 'corner', instId: crHit.inst.id, vertex: crHit.vertex }, String(Math.round(effectiveCornerRadius(crHit.inst, crHit.vertex))), screenPos(e));
    return;
  }
  // A temporary dimension is edited by double-clicking its FIGURE (plus a 3 px
  // margin), never the dimension line running the length of the edge — that line
  // covers the whole object, and a click anywhere along it used to open an editor.
  // Resolve the figure from the clicked label's own probe point (on its intended
  // side), never the raw cursor — a label that spilled to the wrong side of the
  // wall would otherwise send the edit to the figure behind it.
  const dimProbe = dimLabelProbeAt(planHits, screenPos(e));
  const fdt = dimProbe ? faceDimTarget() : null;
  if (fdt) {
    const fe = faceDimEdgeAt(fdt, screenPos(e), dimProbe!);
    if (fe.edge >= 0) {
      // The outer dimension of any wall hosting a door or glazed pane is split
      // solid | feature | solid… — edit that figure instead of the whole wall.
      // The interior clear span (inside) still edits the wall length.
      // …unless the label clicked was the wall's OVERALL figure, which measures
      // the whole wall and so edits its length — its probe sits on the same edge
      // as the chain's, so only the tag can tell the two apart.
      const inst = selectedInstance();
      if (inst && isPolyRoom(inst) && !fe.inside && dimLabelTagAt(planHits, screenPos(e)) !== DIM_TAG_WALL_OVERALL) {
        const hit = wallDimKindAt(inst, fe.edge, dimProbe!);
        if (hit) { openWallDimEditor(inst, fe.edge, hit, screenPos(e)); return; }
      }
      // An interior dimension on a wall that hosts partitions is a clear span, not
      // the wall length — typing it moves the partition rather than the room wall.
      if (inst && isPolyRoom(inst) && fe.inside) {
        const hit = innerWallDimKind(inst, fe.edge, dimProbe!);
        if (hit) {
          openFigureEditor({ kind: 'innerWall', instId: inst.id, wallId: hit.wallId, part: hit.kind }, String(Math.round(hit.value)), screenPos(e));
          return;
        }
      }
      openFaceDimEditor(fdt, fe.edge, screenPos(e), fe.inside); return;
    }
  }

  // Below, what a double-click acts on is what is DRAWN ON TOP under it — the
  // head of the layer stacking, the same object a single click selects.
  const dHead = objectStackAt(world, screenPos(e)).head;
  const dOf = (kind: ObjRef['kind']): string | null => (dHead && dHead.kind === kind ? dHead.id : null);
  const dRoomId = dHead ? (dHead.instId ?? (dHead.kind === 'instance' ? dHead.id : null)) : null;
  const dRoom = dRoomId ? state.drawing.instances.find((i) => i.id === dRoomId) ?? null : null;

  // Double-click a polygon room (or one of its features) to enter vertex-edit mode.
  if (dRoom && dRoom.autoBenches && isPolyRoom(dRoom)) { enterPolyRoomVertexEdit(dRoom); return; }

  // Double-click a text comment to edit its text inline.
  const dText = dOf('text');
  const tc = dText ? (state.drawing.texts || []).find((t) => t.id === dText) ?? null : null;
  if (tc) {
    openTextEditor(tc);
    renderProperties();
    return;
  }

  // Double-click a dimension line to type a new value; the dimension and the
  // object it's snapped to resize to match.
  const dimId = dOf('dim');
  if (dimId) {
    selectOne(state, 'dim', dimId);
    renderProperties();
    invalidate();
    openDimEditor(dimId, screenPos(e));
    return;
  }

  if (addPipeBendInView(world)) return;

  // Double-click an existing mid-route vertex to pick it up; it follows the
  // cursor until the next click drops it at the new position.
  const wpHit = hitConnectionWaypoint(world);
  if (wpHit && !objUnpickable(wpHit.conn.id)) {
    beginGesture();
    const w = wpHit.conn.waypoints![wpHit.index];
    pickUpBend(wpHit.conn, wpHit.index, false, { x: w.x, y: w.y });
    return;
  }

  // Double-click a connection line to add a fixed mid-route vertex; it then
  // follows the cursor until the next click drops it in place.
  const ch = dOf('conn') ? hitConnection(world) : null;
  if (ch && ch.conn.id === dOf('conn')) {
    beginGesture();
    connEdit(ch.conn, { kind: 'bendAdd', at: ch.seg, p: world });
    pickUpBend(ch.conn, ch.seg, true, { x: world.x, y: world.y });
    return;
  }

  // Already editing vertices: double-click a segment to insert a vertex.
  const editing = editingVertexShape();
  if (editing) {
    const seg = nearestSegmentRings(editing, world, 10 / vp.scale);
    if (seg) {
      pushHistory();
      insertVertexInShape(editing, seg.ring, seg.index, seg.point);
      invalidate();
      return;
    }
  }

  // Enter vertex-edit mode for the revision cloud, polygon or wall on top
  // (ellipses have no editable vertices, so a double-click on one just leaves it
  // selected).
  const dCloud = dOf('cloud');
  const cloudEdit = dCloud ? (state.drawing.revisionClouds || []).find((c) => c.id === dCloud) ?? null : null;
  if (cloudEdit) { enterShapeVertexEdit(cloudEdit); return; }
  const dPoly = dOf('polygon'), dWall = dOf('wall');
  const poly = dPoly ? (state.drawing.polygons || []).find((q) => q.id === dPoly) ?? null : null;
  const wall = dWall ? (state.drawing.walls || []).find((w) => w.id === dWall) ?? null : null;
  if (poly && poly.ellipse) {
    selectOne(state, 'polygon', poly.id);
    renderProperties();
    invalidate();
    return;
  }
  if (poly || wall) { enterShapeVertexEdit((poly || wall)!); return; }

  // Double-click a segment of the selected polyline salt-wall / heated-bench to
  // insert a node (takes priority over entering the drawing editor for it).
  const ndInst = selectedInstance();
  if (ndInst?.nodes && ndInst.nodes.length >= 2) {
    const seg = nearestSegment(ndInst.nodes, world, false, 10 / vp.scale);
    if (seg) { insertNode(ndInst, seg.index, seg.point); return; }
  }

  // Double-click a placed instance to enter the drawing editor for it.
  const deEnterHit = dOf('instance');
  if (deEnterHit) {
    enterDrawingEdit(deEnterHit);
    return;
  }

  // Otherwise, double-clicking an underlay (with nothing on top) enters its
  // transform mode (move / resize / rotate) for that image. Double-clicking away
  // exits it. With several underlays, the topmost one under the cursor is picked.
  // A hidden or locked Imported layer takes no edit.
  const importedLive = !importedHidden() && !importedLocked();
  const bgHit = importedLive ? bgImageAt(world) : null;
  if (!dHead && bgHit) {
    enterEdit({ kind: 'underlay', id: bgHit.id ?? null });
    clearSelection(state);
    renderProperties();
    invalidate();
    return;
  }

  // No image under the cursor: double-clicking the imported DXF selects it so it
  // can be dragged. Double-clicking empty space leaves the mode.
  if (!dHead && importedLive && pointInBgGeom(world)) {
    selectBackgroundGeom();
  }
});

/** The point to zoom the wheel around. Over the content the cursor is used
 *  as-is (zoom-to-cursor). In the empty space above/below the drawing the pivot
 *  stays horizontally aligned with the cursor but is anchored to the content's
 *  vertical band (its nearest edge), so scrolling there tracks the cursor's x
 *  without flinging the drawing vertically — with a far pivot each notch would
 *  otherwise translate the content by `pivot-distance × scale × ~0.1` px, which
 *  reads as runaway zoom. With no content yet, the raw cursor is used. */
canvas.addEventListener('wheel', (e) => {
  cancelZoomAnim();
  if (activeSheet()) { sheetWheel(e); return; }
  e.preventDefault();
  // Zoom about the cursor: zoomAt keeps the world point under `sp` fixed, so the
  // point under the pointer stays put whatever the pointer is over — empty canvas
  // above/below the drawing included. (A previous "anchor to the content plane"
  // clamp made the wheel snap its pivot to the content's edge whenever the cursor
  // sat outside the drawing's vertical band, which read as zoom-not-at-cursor.)
  const sp = screenPos(e);
  // A trackpad pinch arrives as a wheel event with ctrlKey set; use a smooth,
  // delta-proportional factor for those (and a fixed step for a notched mouse
  // wheel) so pinch-zoom on touchpads feels continuous.
  const factor = e.ctrlKey
    ? Math.exp(-e.deltaY * 0.01)
    : e.deltaY < 0
      ? 1.12
      : 1 / 1.12;
  vp.zoomAt(sp, factor);
  repaint();
}, { passive: false });

// ---- Touchscreen pinch-to-zoom (and two-finger pan) ----
let pinch: { dist: number; mid: Vec2 } | null = null;
const touchScreenPos = (t: Touch): Vec2 => {
  const rect = canvas.getBoundingClientRect();
  return { x: t.clientX - rect.left, y: t.clientY - rect.top };
};
const touchMetrics = (a: Touch, b: Touch): { dist: number; mid: Vec2 } => {
  const pa = touchScreenPos(a);
  const pb = touchScreenPos(b);
  const dx = pa.x - pb.x;
  const dy = pa.y - pb.y;
  return { dist: Math.hypot(dx, dy) || 1, mid: { x: (pa.x + pb.x) / 2, y: (pa.y + pb.y) / 2 } };
};
canvas.addEventListener('touchstart', (e) => {
  if (e.touches.length === 2) {
    pinch = touchMetrics(e.touches[0], e.touches[1]);
    finishUnderWay(); // a pinch ends a grip's or a handle's work where it is
    endPlanMove('keep'); // abandon any drag the first finger started, where it is
    dropDrag();
    state.moveSnapTargets = null; // …and its cached snap targets, so the next drag re-gathers them
    e.preventDefault();
  }
}, { passive: false });
canvas.addEventListener('touchmove', (e) => {
  if (pinch && e.touches.length === 2) {
    e.preventDefault();
    const m = touchMetrics(e.touches[0], e.touches[1]);
    // Zoom around the pinch midpoint, then pan so the midpoint tracks the fingers.
    const sh = activeSheet();
    if (sh) {
      // On a sheet, drive the paper camera (paperCam) instead of the model
      // viewport, so pinch-to-zoom actually moves the page.
      paperZoomAt(sh, m.mid, m.dist / pinch.dist);
      sheetSpace.paperCam = { ...sheetSpace.paperCam, pan: { x: sheetSpace.paperCam.pan.x + (m.mid.x - pinch.mid.x), y: sheetSpace.paperCam.pan.y + (m.mid.y - pinch.mid.y) } };
      redrawBackdrop();
    } else {
      vp.zoomAt(m.mid, m.dist / pinch.dist);
      vp.pan(m.mid.x - pinch.mid.x, m.mid.y - pinch.mid.y);
    }
    pinch = m;
    invalidate();
  }
}, { passive: false });
const endPinch = (e: TouchEvent): void => {
  if (pinch && e.touches.length < 2) pinch = null;
};
canvas.addEventListener('touchend', endPinch);
canvas.addEventListener('touchcancel', endPinch);

// ---- Drag & drop from products panel ----
const dropOverlay = $('drop-overlay');
canvas.addEventListener('dragover', (e) => {
  const t = e.dataTransfer?.types;
  if (t?.includes('text/stenal-product') || t?.includes('text/stenal-titleobj')) {
    e.preventDefault();
    e.dataTransfer!.dropEffect = 'copy';
    dropOverlay.classList.add('show');
  }
});
canvas.addEventListener('dragleave', () => dropOverlay.classList.remove('show'));
canvas.addEventListener('drop', (e) => {
  e.preventDefault();
  dropOverlay.classList.remove('show');
  const objKind = e.dataTransfer?.getData('text/stenal-titleobj');
  if (objKind) { dropTitleObject(objKind, screenPos(e)); return; }
  const id = e.dataTransfer?.getData('text/stenal-product');
  if (!id) return;
  const t = state.templates.find((x) => x.id === id);
  if (!t) return;
  const sh = activeSheet();
  if (titleEditor.titleEdit) return;
  if (sh) {
    // `vp` is the hidden model view, so placing through it put the product
    // somewhere nobody could see. Through a viewport it lands under the cursor.
    const pos = screenPos(e);
    const vpt = viewportAt(paperView(), sh, pos);
    if (!vpt || placementStartsTool(t)) { flashStatus(tr('Drop the product inside a viewport, or place it in the Model tab')); return; }
    placeInstance(t, vpModelViewport(paperView(), vpt).screenToWorld(pos));
    clearSelection(state);
    renderProperties(); invalidate();
    return;
  }
  placeInstance(t, vp.screenToWorld(screenPos(e)));
});

/** Click on a catalogue card: a room or a run arms its drawing tool straight away
 *  (as a drop does — its first point is the next click); anything else waits for
 *  one click on the drawing, or inside a viewport on a sheet. */
function armPlacement(t: ProductTemplate): void {
  if (titleEditor.titleEdit) return;
  // In the 3D view a part is placed there, where the next click is (a room's or a
  // run's drawing still starts on the plan).
  if (state.viewTab === '3d' && !placementStartsTool(t)) {
    setTool('select');
    armPlacement3d(t, () => `${toolName(t, 'Part')} — ${tr('click in the 3D view to place it (Esc to cancel)')}`);
    return;
  }
  toPlan(); // it is placed on the plan
  const armsTool = placementStartsTool(t) && !traitsOf(t.category).run;
  if (armsTool && activeSheet()) { flashStatus(tr('Drop the product inside a viewport, or place it in the Model tab')); return; }
  setTool('select');
  if (armsTool) { placeInstance(t, vp.screenToWorld({ x: viewW() / 2, y: viewH() / 2 })); return; }
  // An armed pick: the click-to-place twin of dragging it there.
  armPick({
    kind: 'place-product',
    cursor: 'copy',
    onSheets: true,
    prompt: () => `${toolName(t, 'Part')} — ${tr('click on the drawing to place it (Esc to cancel)')}`,
    click: (_e, sp) => placePending(t, sp),
    cancel: () => {},
  });
  updateEmptyState(); // now: the placing click must reach the drawing
}
/** The click that places an armed product (model, or through a sheet viewport);
 *  'keep': a click on a sheet outside every viewport, which places nothing. */
function placePending(t: ProductTemplate, pos: Vec2): 'keep' | void {
  const sh = activeSheet();
  if (sh) {
    const vpt = viewportAt(paperView(), sh, pos);
    if (!vpt) { flashStatus(tr('Drop the product inside a viewport, or place it in the Model tab')); return 'keep'; }
    placeInstance(t, vpModelViewport(paperView(), vpt).screenToWorld(pos));
    clearSelection(state);
    renderProperties(); invalidate();
    return;
  }
  placeInstance(t, snapPoint(vp.screenToWorld(pos), null));
}

/** Products whose drop arms a drawing tool (rooms, repeatable runs, node benches)
 *  rather than placing an object at the drop point. */
export function placementStartsTool(t: ProductTemplate): boolean {
  return !!t.polyRoom || !!t.repeatable
    || (!!t.autoBenches && traitsOf(t.category).room)
    || traitsOf(t.category).run;
}

// ---- Drag & drop of DXF/PDF files from the file explorer (== Import button) ----
// Handled at the window level so a drop anywhere over the app imports the file,
// and so the browser's default "open the file" navigation is always suppressed.
window.addEventListener('dragover', (e) => {
  if (!e.dataTransfer?.types.includes('Files')) return;
  e.preventDefault();
  e.dataTransfer.dropEffect = 'copy';
  dropOverlay.textContent = tr('Drop to import DXF / DWG / PDF / image');
  dropOverlay.classList.add('show');
});
window.addEventListener('dragleave', (e) => {
  // Only hide when the cursor actually leaves the window (relatedTarget is null),
  // not when it crosses between child elements.
  if (!e.relatedTarget) dropOverlay.classList.remove('show');
});
window.addEventListener('drop', async (e) => {
  if (!e.dataTransfer?.types.includes('Files')) return;
  e.preventDefault();
  dropOverlay.classList.remove('show');
  dropOverlay.textContent = tr('Drop to place product');
  const cr = canvas.getBoundingClientRect();
  sheetSpace.schDropScreen = { x: e.clientX - cr.left, y: e.clientY - cr.top };
  const file = e.dataTransfer.files[0];
  if (file) await importDroppedFile(file);
});

// Single entry point for Ctrl+V / Edit ▸ Paste. A clipboard image (screenshot,
// "copy image", …) becomes a background underlay — the same as importing an image
// file; anything else pastes the in-app clipboard (title-block objects while
// editing a title block, otherwise drawing objects). Handling both here — instead
// of splitting image paste from the keydown object-paste — guarantees a clipboard
// image and an object paste can never both fire from one keystroke. Pastes into a
// text field are left to the browser so normal text paste still works.
window.addEventListener('paste', (e) => {
  const t = e.target as HTMLElement | null;
  if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
  // A modal dialog owns the screen — don't paste an underlay (or objects) behind it.
  if (document.querySelector('.modal-backdrop:not(.hidden)')) return;
  // A clipboard image takes precedence, but only in the model view (underlays
  // live in model space) and not while editing a title block.
  if (!titleEditor.titleEdit && state.viewTab === 'model') {
    for (const it of Array.from(e.clipboardData?.items ?? [])) {
      if (it.kind === 'file' && it.type.startsWith('image/')) {
        const file = it.getAsFile();
        if (file) { e.preventDefault(); void pasteImageFile(file); return; }
      }
    }
  }
  // Otherwise the object clipboard (no-ops when empty). Read the system clipboard
  // synchronously off the paste event first (no permission needed), so a copy
  // made in ANOTHER window is adopted before pasting.
  // With the pointer on the drawing, the paste lands centred on it (AutoCAD's
  // insertion point, PowerPoint's paste-where-you-are); otherwise stepped.
  e.preventDefault();
  // Paper takes its own clipboard; the model adopts the event's system copy first.
  const space = liveSpace();
  if (space !== 'model') { SPACES[space].paste(pointerOnCanvas ? screenToPaper(paperView(), lastPointer) : undefined); return; }
  adoptSystemClipboard(e.clipboardData?.getData('text/plain') ?? '');
  pasteClipboard(pointerOnCanvas ? vp.screenToWorld(lastPointer) : undefined);
});

// COPY companion to the paste listener: place the in-app selection on the system
// clipboard as our tagged JSON, so another StenalCAD window can paste it. Doing
// it here (and preventing the default) also stops an empty-selection canvas
// "copy" from clobbering the clipboard. Copies from a text field, a modal, or the
// title-block editor are left alone. The keydown/menu Copy has already built the
// in-app clipboard (and best-effort writeText'd it); this makes the keyboard path
// reliable and permission-free.
const putClipboardOnSystem = (e: ClipboardEvent): void => {
  const t = e.target as HTMLElement | null;
  if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
  if (titleEditor.titleEdit) return;
  if (activeSheet()) return; // a sheet's copy is the in-app schematic clipboard, not the system one
  if (document.querySelector('.modal-backdrop:not(.hidden)')) return;
  if (!clipboardUi.clipboard || !clipboardHasContent(clipboardUi.clipboard)) return;
  e.clipboardData?.setData('text/plain', serializeClipboard(clipboardUi.clipboard));
  e.preventDefault();
};
window.addEventListener('copy', putClipboardOnSystem);
window.addEventListener('cut', putClipboardOnSystem); // Ctrl+X: cutSelection has already filled `clipboard`

// =====================================================================
// Properties panel
// =====================================================================
/** Parse a colour given as #rrggbb, #rgb or rgb(r,g,b) into #rrggbb, or null. */
export function normalizeColor(v: string): string | null {
  const s = v.trim();
  let m = /^#?([0-9a-f]{6})$/i.exec(s);
  if (m) return '#' + m[1].toLowerCase();
  m = /^#?([0-9a-f]{3})$/i.exec(s);
  if (m) return '#' + m[1].split('').map((c) => c + c).join('').toLowerCase();
  m = /^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i.exec(s);
  if (m) {
    const h = (n: number) => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0');
    return '#' + h(+m[1]) + h(+m[2]) + h(+m[3]);
  }
  return null;
}

/** A placed instance's standard footprint from its product template: the
 *  single-body size, or the matching part's size for a multi-part product. */
/** Whether a placed part is resize-locked: its own override if set, else the
 *  product catalog's `lockResize` (inherited when placed). Rooms are always
 *  freely sized (they regenerate their layout), so they never lock. */
export function resizeLocked(inst: ProductInstance): boolean {
  if (inst.autoBenches) return false;
  if (inst.lockResize != null) return inst.lockResize;
  const t = state.templates.find((x) => x.id === inst.templateId);
  return t?.lockResize ?? false;
}

function standardSizeOf(inst: ProductInstance): { width: number; depth: number } | null {
  const t = state.templates.find((x) => x.id === inst.templateId);
  if (!t) return null;
  if (t.parts && t.parts.length) {
    const part = t.parts.find((p) => p.name === inst.name) ?? t.parts[0];
    return { width: part.width, depth: part.depth };
  }
  return { width: t.width, depth: t.depth };
}

/** ISO 216 A-series paper sizes (mm, portrait), for naming a PDF's page. */
const PAPER_SIZES: Array<{ name: string; w: number; h: number }> = [
  { name: 'A0', w: 841, h: 1189 }, { name: 'A1', w: 594, h: 841 },
  { name: 'A2', w: 420, h: 594 }, { name: 'A3', w: 297, h: 420 },
  { name: 'A4', w: 210, h: 297 }, { name: 'A5', w: 148, h: 210 },
];
/** "A4 (210 × 297 mm)" when the page matches a standard size (either orientation), else just the dimensions. */
function paperLabel(w: number, h: number): string {
  const text = `${Math.round(w)} × ${Math.round(h)} mm`;
  const near = (a: number, b: number): boolean => Math.abs(a - b) <= 2;
  for (const p of PAPER_SIZES) {
    if ((near(w, p.w) && near(h, p.h)) || (near(w, p.h) && near(h, p.w))) return `${p.name} (${text})`;
  }
  return text;
}
/** Drawing scale as "1:N" — the real extent divided by the paper extent. */
function formatPdfScale(bi: BackgroundImage): string {
  if (!bi.paperWidthMm) return '—';
  const ratio = bi.widthMm / bi.paperWidthMm;
  if (!isFinite(ratio) || ratio <= 0) return '—';
  const r = ratio >= 10 ? Math.round(ratio) : Math.round(ratio * 10) / 10;
  return `1:${r}`;
}

/** Set true when a rebuild was skipped because a field was being edited, so the
 *  panel can be brought up to date once the field is left. */
let propRenderPending = false;
function buildPropertiesPanel(): void {
  changes.touch();
  // Never rebuild the panel while the user is typing in one of its fields — the
  // `innerHTML = ''` below would blow away the in-progress value and caret (some
  // background path, e.g. a snap update or a group edit, can call this mid-edit).
  // Defer it; the field's focusout re-runs the render once editing is done.
  const ae = document.activeElement as HTMLElement | null;
  const typing = ae?.tagName === 'TEXTAREA' || (ae?.tagName === 'INPUT' && !['checkbox', 'radio'].includes((ae as HTMLInputElement).type));
  if (ae && typing && $('properties-body').contains(ae)) {
    propRenderPending = true;
    return;
  }
  renderPropertiesBody();
  const body = $('properties-body');
  const tab = body.querySelector<HTMLElement>('.prop-tabs .prop-tab.active')?.dataset.tab;
  if ((tab == null || tab === 'properties') && !drawingEditor.drawingEditId && !activeSheet() && !titleEditor.titleEdit && selectedCount(state, MULTI_KINDS) === 1) appendObjectConstraintsCard(body);
  if ((tab == null || tab === 'properties') && !drawingEditor.drawingEditId && !activeSheet() && !titleEditor.titleEdit) appendLayerCard(body);
  if ((tab == null || tab === 'properties') && !body.querySelector('.prop-delete') && !drawingEditor.drawingEditId && !activeSheet() && !titleEditor.titleEdit && modelSelectionDeletable()) {
    body.appendChild(deleteRow());
  }
  groupPropCards(body);
}

/** The selection's Layer: which layer it sits on, and moving it to another (a
 *  group goes whole, a room with what stands in it). Shown for every selection
 *  that sits on a layer; `(mixed)` when its objects sit on different ones. */
function appendLayerCard(body: HTMLElement): void {
  const ids = selectionLayerIds();
  if (!ids.length) return;
  const m = layers();
  const on = new Set(ids.map((id) => m.layerOf.get(id)));
  const options: Array<[string, string]> = [...m.layers].reverse().filter((l) => l.home !== 'imported').map((l) => [l.id, layerLabel(l)]);
  const mixed = on.size > 1;
  if (mixed) options.unshift(['', tr('(mixed)')]);
  body.appendChild(sectionTitle('Layer'));
  body.appendChild(selectRow('Layer', options, mixed ? '' : [...on][0] ?? '', (v) => {
    if (!v) return;
    pushHistory();
    moveToLayer(state.drawing, LAYER_ENV, selectionUnitIds(), v);
    renderProperties();
    invalidate();
  }));
}

/** The dedicated panel for a sub-selected glazed pane, shown INSTEAD of the room's
 *  panel: its own size, height, height-from-ground, colour and delete. A "back to
 *  room" button drops the sub-selection and returns to the room panel. */
/** Remove the selected flooring. */
export function deleteSelectedFloor(): void {
  const f = selectedFloor();
  if (!f) return;
  pushHistory();
  removeObjectsById(new Set([f.id]));
  state.selectedFloorId = null; state.selectedFloorDrain = false; state.floorDrainDims = [];
  renderProperties(); invalidate();
}

/** The selected flooring's panel: type, area, and (sloping only) the drain type
 *  chooser plus a movability hint. */
function renderFloorPanel(body: HTMLElement, floor: Flooring): void {
  body.appendChild(sectionTitle(floorLabel(floor.kind)));
  body.appendChild(infoRow('Area', `${(floorPolyArea(floor.points) / 1e6).toFixed(2)} m²`));
  if (floorIsSloping(floor.kind)) {
    const row = document.createElement('div'); row.className = 'prop-row';
    const lab = document.createElement('label'); lab.textContent = tr('Drain');
    const sel = document.createElement('select');
    for (const t of drainTemplates()) {
      const opt = document.createElement('option');
      opt.value = t.id; opt.textContent = productName(t, getUiLang());
      sel.appendChild(opt);
    }
    if (floor.drain) sel.value = floor.drain.productId;
    sel.addEventListener('change', () => {
      pushHistory();
      const t = drainTemplate(sel.value);
      if (!floor.drain) { const c = floorCentroid(floor.points); floor.drain = { productId: sel.value, x: c.x, y: c.y, rotation: 0 }; }
      else floor.drain.productId = sel.value;
      if (t) Object.assign(floor.drain, drainNames(t));
      const cc = confineToPolygon({ x: floor.drain.x, y: floor.drain.y }, floor.points);
      floor.drain.x = cc.x; floor.drain.y = cc.y;
      renderProperties(); invalidate();
    });
    row.append(lab, sel); body.appendChild(row);
    const hint = document.createElement('div');
    hint.className = 'field-hint';
    hint.textContent = tr('Drag the drain to move it, its knob to rotate. It stays inside the floor.');
    body.appendChild(hint);
  }
  body.appendChild(deleteRow(() => deleteSelectedFloor()));
}

function renderGlassPanePanel(body: HTMLElement, inst: ProductInstance, selG: GlassSegment): void {
  body.appendChild(sectionTitle('Glass pane'));
  body.appendChild(buttonRow('← Back to room', () => {
    dropFeature(state, 'glass');
    renderProperties();
    invalidate();
  }));
  const onWall = paneOnWall(inst, selG);
  const where = selG.innerWallId != null ? tr('inner wall') : `${tr('wall')} ${(onWall?.w ?? selG.edge ?? 0) + 1}`;
  body.appendChild(infoRow('On', where));
  // Resizing a pane holds the end it is nearest, so typing a width does not slide
  // it down the wall; the two gap rows move it without resizing it.
  appendSpanRows(body, inst, selG);
  // A glazing's pane is the glazing's (its height, its look): its card instead.
  const gz = glazingOfPane(inst, selG.id);
  if (gz) appendGlazingCard(body, inst, gz);
  else {
    // The resettable display overrides — its own height, height-from-ground and
    // colour — grouped together, each falling back to the room's glass when unset
    // (heights.ts's glassPaneTop / glassPaneBase and GLASS_STROKE are the one
    // resolvers plan, section and 3D read). Below the base the wall is solid.
    body.appendChild(sectionTitle('Display'));
    body.appendChild(numberRow('Height (mm)', Math.round(glassPaneTop(inst, selG)), (v) => {
      pushHistory(); selG.height = Math.max(1, v); renderProperties(); invalidate();
    }, { commit: true }));
    if (selG.id && ceilingCuts(inst).has(selG.id)) body.appendChild(noteRow('Cut by the ceiling'));
    body.appendChild(heightFromGroundRow(glassPaneBase(selG), (v) => { selG.heightFromGround = v || undefined; }, 0));
    body.appendChild(colorRow('Colour', selG.color || GLASS_STROKE, (hex) => {
      selG.color = hex;
      renderProperties();
      invalidate();
    }));
    if (selG.color || selG.height != null || selG.heightFromGround) {
      body.appendChild(buttonRow('Reset to defaults', () => {
        pushHistory();
        delete selG.color;
        delete selG.height;
        delete selG.heightFromGround;
        renderProperties();
        invalidate();
      }));
    }
  }
  body.appendChild(deleteRow(() => {
    pushHistory();
    inst.glass = glassPanesOf(inst).filter((g) => g !== selG);
    if (!inst.glass.length) delete inst.glass;
    dropFeature(state, 'glass');
    regenerateSauna(inst); renderProperties(); invalidate();
  }));
}

const WALL_FEATURE_LABEL: Record<WallFeature['kind'], string> = { door: 'Door', glass: 'Glass pane', opening: 'Opening' };

/** Take a door / pane / opening out of its drawn wall. */
export function removeWallFeature(wall: Wall, f: WallFeature): void {
  wall.features = (wall.features ?? []).filter((x) => x !== f);
  if (!wall.features.length) delete wall.features;
  state.selectedWallFeatureId = null;
  renderProperties();
  invalidate();
}

/** A drawn wall's door, pane or opening: where it sits along its segment (measured
 *  on the drawn line from the segment's two ends), how tall it is, and what only
 *  its kind has — a door's hinge and swing, a pane's base and colour, an
 *  opening's sill. Replaces the wall's panel, like a room's pane. */
function renderWallFeaturePanel(body: HTMLElement, wall: Wall, f: WallFeature): void {
  body.appendChild(sectionTitle(WALL_FEATURE_LABEL[f.kind]));
  body.appendChild(buttonRow('← Back to wall', () => { state.selectedWallFeatureId = null; renderProperties(); invalidate(); }));
  const fr = wallSegFrame(wall, f.seg);
  const sp = wallFeatureSpans(wall).find((x) => x.f === f);
  if (!fr || !sp) { body.appendChild(deleteRow(() => { pushHistory(); removeWallFeature(wall, f); })); return; }
  // It slides between the straight run's ends and its neighbours on the segment.
  let lo = fr.lo, hi = fr.hi;
  for (const o of wall.features ?? []) {
    if (o === f || o.seg !== f.seg) continue;
    if (Math.max(o.start, o.end) <= sp.s + 1e-6) lo = Math.max(lo, Math.max(o.start, o.end));
    else if (Math.min(o.start, o.end) >= sp.e - 1e-6) hi = Math.min(hi, Math.min(o.start, o.end));
  }
  const min = f.kind === 'door' ? WALL_DOOR_MIN : WALL_FEATURE_MIN;
  const a = sp.s, b = sp.e;
  const setSpan = (s0: number, e0: number): void => {
    const w = Math.max(min, Math.min(hi - lo, e0 - s0));
    const st = Math.max(lo, Math.min(hi - w, s0));
    f.start = st; f.end = st + w;
    renderProperties(); invalidate();
  };
  body.appendChild(sectionTitle('Size'));
  body.appendChild(numberRow('Width (mm)', Math.round(b - a), (v) => { pushHistory(); setSpan(a, a + v); }, { commit: true }));
  body.appendChild(numberRow('Distance from start (mm)', Math.round(a), (v) => { pushHistory(); setSpan(v, v + (b - a)); }, { commit: true }));
  body.appendChild(numberRow('Distance from end (mm)', Math.round(fr.len - b), (v) => { pushHistory(); setSpan(fr.len - v - (b - a), fr.len - v); }, { commit: true }));
  const setTop = (v: number): void => { pushHistory(); f.height = Math.max(1, v); renderProperties(); invalidate(); };
  const setBase = (v: number): void => { pushHistory(); f.heightFromGround = Math.max(0, v); renderProperties(); invalidate(); };
  if (f.kind === 'door') {
    body.appendChild(numberRow('Height (mm)', Math.round(wallFeatureTop(wall, f)), setTop, { commit: true }));
    body.appendChild(buttonRow('Flip hinge (left ⇄ right)', () => { pushHistory(); f.hingeEnd = !f.hingeEnd || undefined; renderProperties(); invalidate(); }));
    body.appendChild(buttonRow('Flip opening (push ⇅ pull)', () => { pushHistory(); f.swingRight = !f.swingRight || undefined; renderProperties(); invalidate(); }));
  } else if (f.kind === 'glass') {
    body.appendChild(numberRow('Height (mm)', Math.round(wallFeatureTop(wall, f)), setTop, { commit: true }));
    body.appendChild(heightFromGroundRow(wallFeatureBase(wall, f), setBase, 0));
    body.appendChild(colorRow('Colour', f.color || GLASS_STROKE, (hex) => { f.color = hex; renderProperties(); invalidate(); }));
  } else {
    body.appendChild(heightFromGroundRow(wallFeatureBase(wall, f), setBase, 0));
    body.appendChild(numberRow('Architrave height (mm)', Math.round(wallFeatureTop(wall, f)), setTop, { commit: true }));
  }
  if (f.height != null || f.heightFromGround != null || f.color) {
    body.appendChild(buttonRow('Reset to defaults', () => {
      pushHistory(); delete f.height; delete f.heightFromGround; delete f.color; renderProperties(); invalidate();
    }));
  }
  body.appendChild(deleteRow(() => { pushHistory(); removeWallFeature(wall, f); }));
}

/** The panel for a MULTI-pane selection (Shift/Ctrl-click): Width / Height / Height
 *  from ground / Colour edit every selected pane at once, plus an equal-length
 *  toggle (same outer wall only). Replaces the room panel, like the single-pane one. */
function renderGlassMultiPanel(body: HTMLElement, panes: { inst: ProductInstance; seg: GlassSegment }[]): void {
  const inst = panes[0].inst;
  const segs = panes.map((p) => p.seg);
  body.appendChild(sectionTitle('Selected panes'));
  body.appendChild(infoRow('Panes', String(segs.length)));
  body.appendChild(buttonRow('← Back to room', () => {
    dropFeature(state, 'glass');
    renderProperties(); invalidate();
  }));
  // Show the common value where all panes agree, else the first pane's — editing
  // applies the typed value to every selected pane.
  const shown = (vals: number[]): number => vals[0];

  body.appendChild(numberRow('Width (mm)', shown(segs.map((s) => Math.round(Math.abs(s.end - s.start)))), (v) => {
    pushHistory();
    for (const seg of segs) {
      const onWall = paneOnWall(inst, seg);
      const runLen = onWall ? onWall.len : glassRunLength(inst, seg);
      const w = Math.max(GLASS_MIN, v);
      const lo = Math.max(0, Math.min(onWall ? onWall.s : Math.min(seg.start, seg.end), Math.max(0, runLen - w)));
      const hi = runLen > 0 ? Math.min(runLen, lo + w) : lo + w;
      if (onWall) setPaneOnWall(inst, seg, lo, hi);
      else { seg.start = lo; seg.end = hi; }
    }
    regenerateSauna(inst); renderProperties(); invalidate();
  }, { commit: true }));
  // The resettable display overrides, grouped together as in the single-pane panel.
  body.appendChild(sectionTitle('Display'));
  body.appendChild(numberRow('Height (mm)', shown(segs.map((s) => Math.round(glassPaneTop(inst, s)))), (v) => {
    pushHistory(); for (const s of segs) s.height = Math.max(1, v); renderProperties(); invalidate();
  }, { commit: true }));
  body.appendChild(heightFromGroundRow(shown(segs.map((s) => Math.round(glassPaneBase(s)))), (v) => { for (const s of segs) s.heightFromGround = v || undefined; }, 0));
  body.appendChild(colorRow('Colour', segs[0].color || GLASS_STROKE, (hex) => {
    for (const s of segs) s.color = hex; renderProperties(); invalidate();
  }));
  if (segs.some((s) => s.color || s.height != null || s.heightFromGround)) {
    body.appendChild(buttonRow('Reset to defaults', () => {
      pushHistory();
      for (const s of segs) { delete s.color; delete s.height; delete s.heightFromGround; }
      renderProperties(); invalidate();
    }));
  }

  body.appendChild(equalToggleRow(inst, segs));

  body.appendChild(deleteRow(() => {
    pushHistory();
    inst.glass = glassPanesOf(inst).filter((g) => !segs.includes(g));
    if (!inst.glass.length) delete inst.glass;
    dropFeature(state, 'glass');
    regenerateSauna(inst); renderProperties(); invalidate();
  }));
}

/** The equal-length toggle row, shared by the pane and opening multi-panels: enabled
 *  for features on the room's outer walls — the same wall or DIFFERENT ones — it
 *  mints or clears a shared `equalGroup` and equalises the group at once (to the
 *  members' mean), every wall keeping its own length while its solids and door
 *  absorb the change. Panes in a partition are not in any wall chain. */
function equalToggleRow(inst: ProductInstance, segs: GlassSegment[]): HTMLElement {
  const sameWall = segs.every((s) => s.innerWallId == null && s.edge != null);
  const isEqual = !!segs[0].equalGroup && segs.every((s) => s.equalGroup === segs[0].equalGroup);
  const row = document.createElement('div');
  row.className = 'prop-row' + (sameWall ? '' : ' disabled');
  const label = document.createElement('label');
  label.textContent = tr('Equal length');
  const btn = document.createElement('button');
  btn.className = 'chain-toggle' + (isEqual ? ' active' : '');
  btn.textContent = isEqual ? '=' : '≠';
  btn.disabled = !sameWall;
  btn.title = !sameWall ? tr('Select panes on the room’s walls to make them equal')
    : isEqual ? tr('The panes are constrained to equal length. Click to release.')
    : tr('Make the selected panes equal length (each wall keeps its overall length).');
  if (sameWall) btn.addEventListener('click', () => {
    pushHistory();
    if (isEqual) { for (const s of segs) delete s.equalGroup; }
    else {
      const gid = newId('eq');
      for (const s of segs) { s.equalGroup = gid; delete s.lockedWidth; }
      followVertices(state.drawing, inst.id, () => equaliseGroup(inst, gid)); // equalise now, every wall keeps its length
    }
    regenerateSauna(inst); renderProperties(); invalidate();
  });
  row.append(label, btn);
  return row;
}

/** The three Width / Distance-from-left / Distance-from-right rows shared by the pane
 *  and opening panels — the figure driving the wall chain. `set` resizes ONE feature
 *  holding its near end; the gap rows move it without resizing. */
function appendSpanRows(body: HTMLElement, inst: ProductInstance, seg: GlassSegment): void {
  // A room-wall pane is measured along its whole WALL (it owns one segment of it);
  // a partition pane along the partition, as before.
  const onWall = paneOnWall(inst, seg);
  const runLen = onWall ? onWall.len : glassRunLength(inst, seg);
  const a = onWall ? onWall.s : Math.min(seg.start, seg.end), b = onWall ? onWall.t : Math.max(seg.start, seg.end);
  const setSpan = (s: number, e: number): void => {
    const w = Math.max(GLASS_MIN, e - s);
    const lo = Math.max(0, Math.min(s, Math.max(0, runLen - w)));
    const hi = runLen > 0 ? Math.min(runLen, lo + w) : lo + w;
    if (onWall) { setPaneOnWall(inst, seg, lo, hi); return; }
    seg.start = lo;
    seg.end = hi;
  };
  body.appendChild(numberRow('Width (mm)', Math.round(b - a), (v) => {
    pushHistory(); setSpan(a, a + v); regenerateSauna(inst); renderProperties(); invalidate();
  }, { commit: true }));
  body.appendChild(numberRow('Distance from left (mm)', Math.round(a), (v) => {
    pushHistory(); setSpan(v, v + (b - a)); regenerateSauna(inst); renderProperties(); invalidate();
  }, { commit: true }));
  body.appendChild(numberRow('Distance from right (mm)', Math.round(Math.max(0, runLen - b)), (v) => {
    pushHistory(); setSpan(runLen - v - (b - a), runLen - v); regenerateSauna(inst); renderProperties(); invalidate();
  }, { commit: true }));
}

/** A sub-selected opening's own panel — the twin of `renderGlassPanePanel`, but an
 *  opening is a hole so it edits sill / architrave heights, not glass colour/height. */
function renderOpeningPanel(body: HTMLElement, inst: ProductInstance, selO: GlassSegment): void {
  body.appendChild(sectionTitle('Opening'));
  body.appendChild(buttonRow('← Back to room', () => {
    dropFeature(state, 'opening');
    renderProperties(); invalidate();
  }));
  body.appendChild(infoRow('On', selO.innerWallId != null ? tr('inner wall') : `${tr('wall')} ${(paneOnWall(inst, selO)?.w ?? selO.edge ?? 0) + 1}`));
  appendSpanRows(body, inst, selO);
  // Sill = solid wall below the opening; architrave = its top (solid wall above).
  body.appendChild(heightFromGroundRow(openingSill(selO), (v) => { selO.heightFromGround = v; }, 0));
  body.appendChild(numberRow('Architrave height (mm)', Math.round(openingArchitrave(inst, selO)), (v) => {
    pushHistory(); selO.architraveHeight = Math.max(1, v); renderProperties(); invalidate();
  }, { commit: true }));
  if (selO.id && ceilingCuts(inst).has(selO.id)) body.appendChild(noteRow('Cut by the ceiling'));
  body.appendChild(deleteRow(() => {
    pushHistory();
    inst.openings = openingsOf(inst).filter((o) => o !== selO);
    if (!inst.openings.length) delete inst.openings;
    dropFeature(state, 'opening');
    regenerateSauna(inst); renderProperties(); invalidate();
  }));
}

/** The MULTI-opening panel: Width / Sill / Architrave edit every selected opening at
 *  once, plus the shared equal-length toggle. */
function renderOpeningMultiPanel(body: HTMLElement, opens: { inst: ProductInstance; seg: GlassSegment }[]): void {
  const inst = opens[0].inst;
  const segs = opens.map((p) => p.seg);
  body.appendChild(sectionTitle('Selected openings'));
  body.appendChild(infoRow('Openings', String(segs.length)));
  body.appendChild(buttonRow('← Back to room', () => {
    dropFeature(state, 'opening');
    renderProperties(); invalidate();
  }));
  body.appendChild(numberRow('Width (mm)', Math.round(Math.abs(segs[0].end - segs[0].start)), (v) => {
    pushHistory();
    for (const seg of segs) {
      const runLen = glassRunLength(inst, seg);
      const w = Math.max(GLASS_MIN, v);
      const lo = Math.max(0, Math.min(Math.min(seg.start, seg.end), Math.max(0, runLen - w)));
      seg.start = lo; seg.end = runLen > 0 ? Math.min(runLen, lo + w) : lo + w;
    }
    regenerateSauna(inst); renderProperties(); invalidate();
  }, { commit: true }));
  body.appendChild(heightFromGroundRow(openingSill(segs[0]), (v) => { for (const s of segs) s.heightFromGround = v; }, 0));
  body.appendChild(numberRow('Architrave height (mm)', Math.round(openingArchitrave(inst, segs[0])), (v) => {
    pushHistory(); for (const s of segs) s.architraveHeight = Math.max(1, v); renderProperties(); invalidate();
  }, { commit: true }));
  body.appendChild(equalToggleRow(inst, segs));
  body.appendChild(deleteRow(() => {
    pushHistory();
    inst.openings = openingsOf(inst).filter((o) => !segs.includes(o));
    if (!inst.openings.length) delete inst.openings;
    dropFeature(state, 'opening');
    regenerateSauna(inst); renderProperties(); invalidate();
  }));
}

/** Group each section (a `.prop-section-title` and the rows following it up to
 *  the next title) into a faint `.prop-card`, so sections read as cards. Content
 *  before the first section (tab bar, headings) stays loose. Then drop a "Copy
 *  colours from…" button into the object's colour card (the first card with a
 *  colour picker), wired to whatever single object is selected. */
function groupPropCards(body: HTMLElement): void {
  let card: HTMLElement | null = null;
  for (const el of Array.from(body.children)) {
    if (el.classList.contains('prop-section-title')) {
      card = document.createElement('div');
      card.className = 'prop-card';
      body.insertBefore(card, el);
      card.appendChild(el);
    } else if (card && !el.classList.contains('prop-tabs') && !el.classList.contains('prop-delete')) {
      card.appendChild(el);
    } else {
      card = null;
    }
  }
  for (const del of Array.from(body.querySelectorAll('.prop-delete'))) body.appendChild(del);
  const dests = selectedColorTargets();
  if (!dests.length) return;
  const colourCard = Array.from(body.querySelectorAll('.prop-card'))
    .find((c) => c.querySelector('input[type="color"]')) as HTMLElement | undefined;
  if (colourCard) colourCard.appendChild(buttonRow('🎨 Copy colours from…', () => startCopyColors(dests)));
}

function renderPropertiesBody(): void {
  propRenderPending = false;
  // A layer just hidden / locked takes its objects out of the selection FIRST,
  // so the buttons below are enabled for what is still selected.
  if (!activeSheet()) dropUnpickableSelection();
  // An armed pick that no longer fits the selection ends (a Pick on plan whose
  // room is no longer the one selected, a relation whose segment is not).
  settleArmed();
  updateToolButtons(); // refresh context-dependent toolbar buttons on selection changes
  updateArrangeButtons(); // enable/disable the toolbar Arrange (z-order) buttons
  const body = $('properties-body');
  body.innerHTML = '';

  const sheet = activeSheet();
  if (sheet) { renderSheetProperties(body, sheet); return; }

  // Multiple instances selected: show only the properties they share, plus an
  // action to build a new product from the selection.
  reconcileSelection();

  // The 3D section tool: the section's own card, whatever is selected.
  if (state.tool === 'section-3d' && state.viewTab === '3d') { renderSection3dPanel(body); return; }

  // A tool that needs pre-click choices (its row's `tab`: fillet / arc / transform /
  // arrays) puts its inputs in a temporary "Drawing" tab so they never clutter the
  // current selection tab. The tab is opened automatically when such a tool is picked.
  const drawTool = TOOL_LIFE[state.tool].tab;
  let drawTabBarShown = false;
  if (drawTool && !drawingEditor.drawingEditId) {
    appendPropTabBar(body, state.propTab, !!selectedInstance() || !!state.selectedPolyId, true);
    drawTabBarShown = true;
    if (state.propTab === 'drawing') { drawTool(body); return; }
  }

  // Two or more objects, of one kind or many: what they all have in common.
  if (selectedCount(state, MULTI_KINDS) >= 2) {
    renderMultiSelectionPanel(body);
    return;
  }

  // A constraint picked by its badge or its Parts-tab row.
  const selCon = selectedConstraint();
  if (selCon) { renderConstraintPanel(body, selCon); return; }

  // A selected free-standing arc: radius readout + colour, width, delete.
  const selArc = state.selectedArcId ? (state.drawing.arcs || []).find((a) => a.id === state.selectedArcId) : null;
  if (renderArcPanel(body, selArc)) return;

  // A selected leader: its label text, colour, delete.
  const selLeader = state.selectedLeaderId ? (state.drawing.leaders || []).find((l) => l.id === state.selectedLeaderId) : null;
  if (renderLeaderPanel(body, selLeader)) return;

  // A selected flooring area: type, area, and (sloping only) the drain chooser.
  const selFlr = selectedFloor();
  if (selFlr) { renderFloorPanel(body, selFlr); return; }

  // A sub-selected glazed pane REPLACES the room's panel with its own — selecting a
  // pane edits the pane, not its room. Resolves against the selected room, so it
  // clears itself when the room is deselected. A "back to room" button returns.
  // Two or more panes (Shift/Ctrl-click) show the multi-pane panel instead.
  const gpanes = selectedGlassPanes();
  if (gpanes.length >= 2) { renderGlassMultiPanel(body, gpanes); return; }
  if (gpanes.length === 1) { renderGlassPanePanel(body, gpanes[0].inst, gpanes[0].seg); return; }
  // A sub-selected opening does the same, with its own panel.
  const opens = selectedOpenings();
  if (opens.length >= 2) { renderOpeningMultiPanel(body, opens); return; }
  if (opens.length === 1) { renderOpeningPanel(body, opens[0].inst, opens[0].seg); return; }
  // And a sub-selected bench support.
  const selSup = selectedSupportNow();
  if (selSup) { renderSupportPanel(body, selSup.inst, selSup.sup); return; }
  // And a sub-selected backrest.
  if (renderFixturePanel(body)) return;

  // What is edited in place, its own panel (EDIT_LIFE): the drawing editor's
  // shape, the underlay, the room's picked wall or corner, the imported plan.
  if (renderEditModePanel(body)) return;

  // An individually-selected hidden heater wall.
  if (renderHeaterWallPanel(body)) return;

  // An individually-selected interior partition.
  if (renderPartitionPanel(body)) return;

  // An individually-selected bench.
  if (renderBenchPanel(body)) return;

  // Text comment selected.
  const tc = (state.drawing.texts || []).find((t) => t.id === state.selectedTextId);
  if (renderTextBoxPanel(body, tc)) return;

  // Schedule table selected: its text height (world mm) scales the whole table.
  if (renderScheduleTablePanel(body)) return;

  // Polygon (or ellipse) selected — same tabbed UI as a placed part, so a shape
  // can carry connections and preparations too.
  const poly = (state.drawing.polygons || []).find((p) => p.id === state.selectedPolyId);
  if (renderPolygonPanel(body, drawTool, drawTabBarShown, poly)) return;

  // A drawn wall's door / pane / opening replaces the wall's panel with its own.
  const selWf = selectedWallFeature();
  if (selWf) { renderWallFeaturePanel(body, selWf.wall, selWf.f); return; }

  // Wall selected.
  const wall = (state.drawing.walls || []).find((w) => w.id === state.selectedWallId);
  if (renderLinePanel(body, wall)) return;
  if (renderWallPanel(body, wall)) return;

  // Connection selected: its own panel.
  const selConn = state.selectedConnId ? state.drawing.connections.find((c) => c.id === state.selectedConnId) : null;
  if (selConn) { renderConnectionPanel(body, selConn); return; }

  // Section selected.
  if (renderViewTitlePanel(body)) return;
  if (renderSectionPanel(body)) return;
  if (renderRcpPanel(body)) return;

  // Revision cloud selected.
  if (renderRevisionCloudPanel(body)) return;

  // Dimension selected.
  const dim = state.drawing.dimensions.find((d) => d.id === state.selectedDimId);
  if (renderDimensionPanel(body, dim)) return;

  const inst = selectedInstance();
  // Tabbed view. The Parts tab works with or without a selection and is the
  // default when nothing is selected; the others need a selected part. The
  // 'drawing' tab only exists while a drawing tool is active (handled above), so
  // fall back to a normal tab here.
  const baseTab = state.propTab === 'drawing' ? 'properties' : state.propTab;
  // Parts AND Connections both work with no selection (Connections shows the whole
  // project's table then); the other tabs still need a selected part.
  const effTab: PropTab = inst ? baseTab
    : baseTab === 'connections' || baseTab === 'preparations' || baseTab === 'constraints' ? baseTab
      : baseTab === 'properties' && drawingSettingsTab ? 'properties' : 'parts';
  if (!drawTabBarShown) appendPropTabBar(body, effTab, !!inst, !!drawTool);
  if (effTab === 'parts') { renderPartsTab(body); return; }
  if (effTab === 'constraints') { renderConstraintsTab(body); return; }
  if (effTab === 'connections') { if (inst) renderConnectionsTab(body, inst); else renderAllConnections(body); return; }
  if (effTab === 'preparations') { if (inst) renderPreparationsTab(body, inst); else renderAllPreparations(body); return; }
  // 'properties' tab: the selected instance, or the drawing's own settings.
  if (!inst) { renderDrawingSettings(body); return; }

  if (renderInstancePanel(body, inst)) return;
}

// ---- the properties panel of each kind of selection, in the order renderPropertiesBody tries them ----

function renderArcPanel(body: HTMLElement, selArc: Arc | null | undefined): boolean {
  if (selArc) {
    body.appendChild(sectionTitle('Arc'));
    body.appendChild(infoRow('Radius', `${Math.round(selArc.radius)} mm`));
    body.appendChild(colorRow('Colour', selArc.stroke, (hex) => { selArc.stroke = hex; invalidate(); }));
    body.appendChild(numberRow('Line width (mm)', selArc.strokeWidth, (v) => { pushHistory(); selArc.strokeWidth = Math.max(1, v); invalidate(); }, { commit: true }));
    body.appendChild(deleteRow());
    return true;
  }
  return false;
}

function renderLeaderPanel(body: HTMLElement, selLeader: Leader | null | undefined): boolean {
  if (selLeader) {
    body.appendChild(sectionTitle('Leader'));
    const row = document.createElement('div'); row.className = 'prop-row';
    const lab = document.createElement('label'); lab.textContent = tr('Text');
    const ta = document.createElement('textarea');
    ta.rows = 2; ta.value = selLeader.text; ta.style.resize = 'vertical';
    ta.addEventListener('input', () => { selLeader.text = ta.value; invalidate(); });
    ta.addEventListener('change', () => pushHistory());
    row.append(lab, ta); body.appendChild(row);
    body.appendChild(colorRow('Colour', selLeader.color ?? ANNOTATION_COLOR, (hex) => { selLeader.color = hex; invalidate(); }));
    body.appendChild(deleteRow());
    return true;
  }
  return false;
}

/** What is edited in place draws its own panel, ahead of the selection's (EDIT_LIFE). */
function renderEditModePanel(body: HTMLElement): boolean {
  const m = state.editing;
  return !!m && EDIT_LIFE[m.kind].panel(body);
}

/** The figures a session's host draws on the canvas (a profile's depth and heights). */
function drawEditDims(): Array<{ a: Vec2; b: Vec2 }> | null {
  const host = drawEditHost(), inst = drawEditInst();
  return host?.dims && inst ? host.dims(inst) : null;
}

function renderDrawingEditorPanel(body: HTMLElement): boolean {
  if (drawingEditor.drawingEditId) {
    const deInst = drawEditInst();
    const host = drawEditHost();
    // A session's host (a steam profile) puts its card first, with its own way out.
    if (host?.panel && deInst) host.panel(body, deInst);
    const deTitle = sectionTitle('Drawing Editor');
    if (deInst) deTitle.textContent += ` — ${productName(deInst, getUiLang())}`;
    if (!host?.panel) body.appendChild(deTitle);
    if (deInst && state.drawEditSelectedPolyId) {
      const lp = deInst.drawPolygons?.find((p) => p.id === state.drawEditSelectedPolyId);
      if (lp) {
        if (lp.ellipse) {
          body.appendChild(sectionTitle(lp.points.length > 4 ? 'Circle / Ellipse' : 'Circle'));
        } else {
          body.appendChild(sectionTitle('Polygon'));
          body.appendChild(buttonRow(
            state.drawEditVerticesId === lp.id ? 'Stop editing vertices' : 'Edit vertices',
            () => {
              state.drawEditVerticesId = state.drawEditVerticesId === lp.id ? null : lp.id;
              renderProperties();
              invalidate();
            }
          ));
          // Click a polygon edge on the canvas to lock it horizontal/vertical.
          if (drawingEditor.drawEditSelectedEdge?.id === lp.id && drawingEditor.drawEditSelectedEdge.edge < lp.points.length) {
            renderEdgeProperties(body, lp, drawingEditor.drawEditSelectedEdge.edge);
          } else {
            const tip = document.createElement('div');
            tip.className = 'prop-empty';
            tip.textContent = tr('Click an edge to lock it horizontal / vertical.');
            body.appendChild(tip);
          }
        }
        body.appendChild(sectionTitle('Fill'));
        body.appendChild(colorRow('Colour', lp.fill, (hex) => { lp.fill = hex; invalidate(); }));
        body.appendChild(opacityRow('Opacity', lp.fillOpacity, (v) => { lp.fillOpacity = v; invalidate(); }));
        body.appendChild(sectionTitle('Outline'));
        body.appendChild(colorRow('Colour', lp.stroke ?? '#222222', (hex) => { lp.stroke = hex; invalidate(); }));
        body.appendChild(opacityRow('Opacity', lp.strokeOpacity ?? 1, (v) => { lp.strokeOpacity = v; invalidate(); }));
        body.appendChild(deleteRow(() => {
          pushHistory();
          deInst.drawPolygons = (deInst.drawPolygons ?? []).filter((p) => p.id !== lp.id);
          if (state.drawEditVerticesId === lp.id) state.drawEditVerticesId = null;
          state.drawEditSelectedPolyId = null;
          renderProperties();
          invalidate();
        }));
      }
    } else if (deInst && state.drawEditSelectedWallId) {
      const lw = deInst.drawWalls?.find((w) => w.id === state.drawEditSelectedWallId);
      if (lw) {
        body.appendChild(sectionTitle(lw.style === 'line' ? 'Line' : 'Wall'));
        body.appendChild(buttonRow(
          state.drawEditVerticesId === lw.id ? 'Stop editing vertices' : 'Edit vertices',
          () => {
            state.drawEditVerticesId = state.drawEditVerticesId === lw.id ? null : lw.id;
            renderProperties();
            invalidate();
          }
        ));
        body.appendChild(colorRow('Colour', lw.fill, (hex) => { lw.fill = hex; invalidate(); }));
        body.appendChild(opacityRow('Opacity', lw.fillOpacity, (v) => { lw.fillOpacity = v; invalidate(); }));
        if (lw.style !== 'line') {
          body.appendChild(numberRow('Thickness (mm)', lw.thickness, (v) => { pushHistory(); lw.thickness = Math.max(1, v); invalidate(); }, { commit: true }));
        } else {
          body.appendChild(numberRow('Line width (mm)', lw.thickness, (v) => { pushHistory(); lw.thickness = Math.max(0.5, v); invalidate(); }, { commit: true }));
        }
        body.appendChild(deleteRow(() => {
          pushHistory();
          deInst.drawWalls = (deInst.drawWalls ?? []).filter((w) => w.id !== lw.id);
          if (state.drawEditVerticesId === lw.id) state.drawEditVerticesId = null;
          state.drawEditSelectedWallId = null;
          renderProperties();
          invalidate();
        }));
      }
    } else if (!host?.closedOnly) {
      const hint = document.createElement('div');
      hint.className = 'prop-empty';
      hint.style.marginTop = '8px';
      hint.textContent = tr('Click a shape to select it, or use polygon / circle / wall / line tools to add shapes.');
      body.appendChild(hint);
    }
    if (!host?.panel) body.appendChild(buttonRow('Exit Drawing Editor', exitDrawingEdit));
    return true;
  }
  return false;
}

function renderUnderlayEditPanel(body: HTMLElement): boolean {
  if (state.editPdf && editedBg()) {
    const bi = editedBg()!;
    body.appendChild(sectionTitle('PDF underlay'));
    const infoRow = (label: string, value: string): void => {
      const row = document.createElement('div');
      row.className = 'prop-row';
      const l = document.createElement('label');
      l.textContent = tr(label);
      const v = document.createElement('span');
      v.textContent = value;
      v.style.flex = '1';
      row.append(l, v);
      body.appendChild(row);
    };
    body.appendChild(sectionTitle('Placement'));
    body.appendChild(numberRow('X (mm)', Math.round(bi.x), (v) => { pushHistory(); bi.x = v; invalidate(); }, { commit: true }));
    body.appendChild(numberRow('Y (mm)', Math.round(bi.y), (v) => { pushHistory(); bi.y = v; invalidate(); }, { commit: true }));
    body.appendChild(sectionTitle('Size'));
    body.appendChild(dimChainToggle());
    body.appendChild(numberRow('Width (mm)', Math.round(bi.widthMm), (v) => {
      if (v > 0) { pushHistory(); const f = bi.widthMm > 0 ? v / bi.widthMm : 1; if (dimChained) bi.heightMm *= f; bi.widthMm = v; renderProperties(); invalidate(); }
    }, { commit: true }));
    body.appendChild(numberRow('Height (mm)', Math.round(bi.heightMm), (v) => {
      if (v > 0) { pushHistory(); const f = bi.heightMm > 0 ? v / bi.heightMm : 1; if (dimChained) bi.widthMm *= f; bi.heightMm = v; renderProperties(); invalidate(); }
    }, { commit: true }));
    if (bi.paperWidthMm && bi.paperHeightMm) {
      infoRow('Paper size', paperLabel(bi.paperWidthMm, bi.paperHeightMm));
      infoRow('Scale', formatPdfScale(bi));
    }
    body.appendChild(opacityRow('Opacity', bi.opacity ?? 1, (v) => { bi.opacity = v; invalidate(); }));
    const hint = document.createElement('div');
    hint.className = 'prop-empty';
    hint.style.marginTop = '10px';
    hint.textContent = tr('Drag to move · drag a handle to resize / rotate · Enter or Esc to finish.');
    body.appendChild(hint);
    return true;
  }
  return false;
}

function renderRoomEdgePanel(body: HTMLElement): boolean {
  if (state.polyRoomVertexEditId && state.polyRoomSelectedEdge != null) {
    const inst = polyRoomEditInst();
    if (inst?.polygon && state.polyRoomSelectedEdge < inst.polygon.length) {
      renderPolyRoomEdgeProperties(body, inst, state.polyRoomSelectedEdge);
      return true;
    }
  }
  return false;
}

function renderRoomVertexPanel(body: HTMLElement): boolean {
  if (state.polyRoomVertexEditId && state.polyRoomSelectedVertex != null) {
    const inst = polyRoomEditInst();
    if (inst?.polygon && state.polyRoomSelectedVertex < inst.polygon.length) {
      renderPolyRoomVertexProperties(body, inst, state.polyRoomSelectedVertex);
      return true;
    }
  }
  return false;
}

function renderImportedPlanPanel(body: HTMLElement): boolean {
  if (state.editBg && bgGeomBounds()) {
    const b = bgGeomBounds()!;
    body.appendChild(sectionTitle('Imported drawing'));
    const row = (label: string, value: string): void => {
      const r = document.createElement('div');
      r.className = 'prop-row';
      const l = document.createElement('label');
      l.textContent = label;
      const v = document.createElement('span');
      v.textContent = value;
      v.style.flex = '1';
      r.append(l, v);
      body.appendChild(r);
    };
    row(tr('Size'), `${Math.round(b.max.x - b.min.x)} × ${Math.round(b.max.y - b.min.y)} mm`);
    body.appendChild(deleteRow());
    const hint = document.createElement('div');
    hint.className = 'prop-empty';
    hint.style.marginTop = '10px';
    hint.textContent = tr('Drag to move · Enter or Esc to finish.');
    body.appendChild(hint);
    return true;
  }
  return false;
}

function renderHeaterWallPanel(body: HTMLElement): boolean {
  if (featureOf(state, 'heaterWall')) {
    const room = state.drawing.instances.find((i) => i.id === featureOf(state, 'heaterWall')!.instId);
    const hw = room ? heaterWallsOf(room).find((h) => h.id === featureOf(state, 'heaterWall')!.wallId) ?? null : null;
    if (room && hw) {
      body.appendChild(sectionTitle('Hidden heater wall'));
      body.appendChild(numberRow('Distance (mm)', Math.round(hw.distance), (v) => {
        pushHistory();
        // Clamp so gap + thickness always leaves the carved interior valid (the
        // false wall can never cross the room); the panel re-renders the value.
        const cap = Math.max(0, maxHeaterWallInset(room, hw.edge) - hw.thickness);
        hw.distance = Math.min(Math.max(0, Math.round(v)), cap);
        regenerateSauna(room);
        renderProperties();
        invalidate();
      }, { commit: true }));
      body.appendChild(numberRow('Thickness (mm)', Math.round(hw.thickness), (v) => {
        editRoom(room, () => {
          const cap = Math.max(1, maxHeaterWallInset(room, hw.edge) - hw.distance);
          hw.thickness = Math.min(Math.max(1, Math.round(v)), cap);
        });
      }, { commit: true }));
      body.appendChild(numberRow('Height (mm)', Math.round(hw.height), (v) => {
        pushHistory();
        hw.height = Math.max(0, Math.round(v));
        renderProperties();
        invalidate();
      }, { commit: true }));
      body.appendChild(heightFromGroundRow(heaterWallBase(hw), (v) => { hw.heightFromGround = v; }, 0));
      if (ceilingCuts(room).has(hw.id)) body.appendChild(noteRow('Cut by the ceiling'));
      body.appendChild(colorRow('Colour', hw.color, (hex) => {
        hw.color = hex;
        renderProperties();
        invalidate();
      }));
      body.appendChild(deleteRow(() => {
        editRoom(room, () => removeHeaterWall(room, hw.id));
      }));
      return true;
    }
    dropFeature(state, 'heaterWall'); // stale selection (room or wall gone)
  }
  return false;
}

function renderPartitionPanel(body: HTMLElement): boolean {
  if (featureOf(state, 'innerWall')) {
    const room = state.drawing.instances.find((i) => i.id === featureOf(state, 'innerWall')!.instId);
    const iw = room ? innerWallsOf(room).find((w) => w.id === featureOf(state, 'innerWall')!.wallId) ?? null : null;
    if (room && iw) {
      body.appendChild(sectionTitle('Inner wall'));
      body.appendChild(numberRow('Thickness (mm)', Math.round(innerWallThickness(room, iw)), (v) => {
        editRoom(room, () => {
          const faces = partitionFaces(room);
          iw.thickness = Math.max(1, Math.round(v));
          benchesFollowPartitions(room, faces);
        });
      }, { commit: true }));
      // A partition with no height of its own follows the ceiling (heights.ts
      // innerWallTop): the field is empty, and clearing it follows again.
      body.appendChild(numberRow('Height (mm)', iw.height == null ? null : Math.round(iw.height), (v) => {
        pushHistory();
        iw.height = Math.max(0, Math.round(v));
        renderProperties();
        invalidate();
      }, {
        commit: true,
        placeholder: tr('Follows the ceiling'),
        onClear: () => { if (iw.height == null) return; pushHistory(); delete iw.height; renderProperties(); invalidate(); },
      }));
      body.appendChild(heightFromGroundRow(innerWallBase(iw), (v) => { iw.heightFromGround = v; }, 0));
      if (iw.id && ceilingCuts(room).has(iw.id)) body.appendChild(noteRow('Cut by the ceiling'));
      body.appendChild(colorRow('Colour', iw.color ?? defaultWallColor(room), (hex) => {
        iw.color = hex;
        renderProperties();
        invalidate();
      }));
      body.appendChild(deleteRow(() => {
        editRoom(room, () => removeInnerWall(room, iw.id!));
      }));
      return true;
    }
    dropFeature(state, 'innerWall'); // stale selection (room or wall gone)
  }
  return false;
}

function renderTextBoxPanel(body: HTMLElement, tc: TextComment | undefined): boolean {
  if (tc) {
    body.appendChild(sectionTitle('Text comment'));
    const textRow = document.createElement('div');
    textRow.className = 'prop-row';
    const tLab = document.createElement('label');
    tLab.textContent = tr('Text');
    const ta = document.createElement('textarea');
    ta.rows = 4;
    ta.value = tc.text;
    ta.style.resize = 'vertical';
    ta.addEventListener('input', () => {
      tc.text = ta.value;
      invalidate();
    });
    ta.addEventListener('change', () => pushHistory());
    textRow.append(tLab, ta);
    body.appendChild(textRow);
    body.appendChild(sectionTitle('Size'));
    // The BOX, independent of the type size below it: the text re-wraps to the
    // new width and overflows when it no longer fits.
    body.appendChild(numberRow('Width (mm)', tc.w, (v) => {
      if (!(v > 0)) return;
      pushHistory(); tc.w = v; renderProperties(); invalidate();
    }, { commit: true }));
    body.appendChild(numberRow('Height (mm)', tc.h, (v) => {
      if (!(v > 0)) return;
      pushHistory(); tc.h = v; renderProperties(); invalidate();
    }, { commit: true }));
    // Turned about the box's centre, like the knob.
    body.appendChild(numberRow('Rotation Z (°)', tc.rotation ?? 0, (v) => {
      if (!Number.isFinite(v)) return;
      pushHistory(); rotateTextBoxTo(tc, v); renderProperties(); invalidate();
    }, { commit: true }));
    body.appendChild(sectionTitle('Text'));
    body.appendChild(numberRow('Text height (mm)', tc.heightMm ?? COMMENT_FONT_MM, (v) => {
      if (v > 0) setTextCommentHeight(tc, v);
    }, { commit: true }));
    body.appendChild(colorRow('Colour', tc.textColor ?? ANNOTATION_COLOR, (hex) => (tc.textColor = hex)));
    body.appendChild(sectionTitle('Fill'));
    body.appendChild(colorRow('Colour', tc.fill, (hex) => (tc.fill = hex)));
    body.appendChild(opacityRow('Opacity', tc.fillOpacity, (v) => (tc.fillOpacity = v)));
    body.appendChild(sectionTitle('Outline & arrow'));
    body.appendChild(colorRow('Colour', tc.color ?? ANNOTATION_COLOR, (hex) => (tc.color = hex)));
    body.appendChild(opacityRow('Opacity', tc.strokeOpacity ?? 1, (v) => (tc.strokeOpacity = v)));
    body.appendChild(sliderRow('Corner radius (mm)', tc.cornerRadius ?? TEXTBOX_CORNER_DEFAULT, 0, Math.max(1, Math.round(Math.min(tc.w, tc.h) / 2)), 1, (v) => { tc.cornerRadius = v; }));
    const hint = document.createElement('div');
    hint.className = 'prop-empty';
    hint.style.marginTop = '10px';
    hint.textContent = tr('Double-click the box to edit text inline · drag to move.');
    body.appendChild(hint);
    return true;
  }
  return false;
}

function renderScheduleTablePanel(body: HTMLElement): boolean {
  if (state.selectedScheduleTable) {
    const st = tableFor(state.selectedScheduleTable);
    if (st) {
      body.appendChild(sectionTitle('Schedule table'));
      body.appendChild(numberRow('Text height (mm)', st.heightMm ?? COMMENT_FONT_MM, (v) => {
        if (v > 0) { pushHistory(); st.heightMm = v; renderProperties(); invalidate(); }
      }, { commit: true }));
      return true;
    }
  }
  return false;
}

function renderPolygonPanel(body: HTMLElement, drawTool: ToolLife['tab'], drawTabBarShown: boolean, poly: Polygon | undefined): boolean {
  if (poly) {
    const polyTab = state.propTab === 'drawing' ? 'properties' : state.propTab;
    if (!drawTabBarShown) appendPropTabBar(body, polyTab, true, !!drawTool);
    if (polyTab === 'parts') { renderPartsTab(body); return true; }
    if (polyTab === 'constraints') { renderConstraintsTab(body); return true; }
    if (polyTab === 'connections') { renderPolyConnections(body, poly); return true; }
    if (polyTab === 'preparations') { renderPreparationsTab(body, poly); return true; }
    if (poly.ellipse) { renderEllipseProperties(body, poly); return true; }
    // Edge selected: its own properties take the top of the panel.
    const edgeSel = state.selectedPolyEdge && state.selectedPolyEdge.id === poly.id ? state.selectedPolyEdge.edge : -1;
    if (edgeSel >= 0 && edgeSel < poly.points.length) renderEdgeProperties(body, poly, edgeSel);
    body.appendChild(sectionTitle('Polygon'));
    body.appendChild(nameRow(poly.name, dictDefaults(polygonWord(poly), trIn), (v) => { poly.name = v.trim() || undefined; invalidate(); }));
    body.appendChild(infoRow('Vertices', String(poly.points.length)));
    if (poly.holes && poly.holes.length) {
      body.appendChild(infoRow('Holes', String(poly.holes.length)));
      body.appendChild(buttonRow('Remove holes', () => {
        pushHistory();
        poly.holes = undefined;
        renderProperties();
        invalidate();
      }));
    }
    // Size: width/height of the bounding box; editing either scales the whole
    // polygon proportionally about its centre (keeps its shape).
    const xs = poly.points.map((p) => p.x), ys = poly.points.map((p) => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
    const pw = maxX - minX, ph = maxY - minY;
    const pcx = (minX + maxX) / 2, pcy = (minY + maxY) / 2;
    // Scale about the centroid, per axis independently so Width and Height each
    // change only their own dimension.
    const scaleAxis = (fx: number, fy: number): void => {
      pushHistory();
      const s = (p: Vec2): Vec2 => ({ x: pcx + (p.x - pcx) * fx, y: pcy + (p.y - pcy) * fy });
      poly.points = poly.points.map(s);
      if (poly.holes) poly.holes = poly.holes.map((h) => h.map(s));
      syncDimensions();
      renderProperties();
      invalidate();
    };
    // Position: the bounding box's top-left corner; editing it moves the whole
    // polygon (translation keeps any edge locks and follows anchored dimensions).
    const translatePoly = (dx: number, dy: number): void => {
      if (dx === 0 && dy === 0) return;
      pushHistory();
      const t = (p: Vec2): Vec2 => ({ x: p.x + dx, y: p.y + dy });
      poly.points = poly.points.map(t);
      if (poly.holes) poly.holes = poly.holes.map((h) => h.map(t));
      syncDimensions();
      renderProperties();
      invalidate();
    };
    body.appendChild(sectionTitle('Position'));
    body.appendChild(numberRow('X (mm)', Math.round(minX), (v) => translatePoly(v - minX, 0), { commit: true }));
    body.appendChild(numberRow('Y (mm)', Math.round(minY), (v) => translatePoly(0, v - minY), { commit: true }));
    body.appendChild(sectionTitle('Size'));
    body.appendChild(dimChainToggle());
    body.appendChild(numberRow('Width (mm)', Math.round(pw), (v) => { if (v > 0 && pw > 0) scaleAxis(v / pw, dimChained ? v / pw : 1); }, { commit: true }));
    body.appendChild(numberRow('Depth (mm)', Math.round(ph), (v) => { if (v > 0 && ph > 0) scaleAxis(dimChained ? v / ph : 1, v / ph); }, { commit: true }));
    body.appendChild(sectionTitle('Fill'));
    body.appendChild(colorRow('Colour', poly.fill, (hex) => (poly.fill = hex)));
    body.appendChild(opacityRow('Opacity', poly.fillOpacity, (v) => (poly.fillOpacity = v)));
    body.appendChild(sectionTitle('Outline'));
    body.appendChild(colorRow('Colour', poly.stroke, (hex) => (poly.stroke = hex)));
    body.appendChild(opacityRow('Opacity', poly.strokeOpacity, (v) => (poly.strokeOpacity = v)));
    appendShapeHeightRows(body, poly);
    const hint = document.createElement('div');
    hint.className = 'prop-empty';
    hint.style.marginTop = '10px';
    hint.textContent =
      state.editVerticesId === poly.id
        ? tr('Editing vertices: drag handles to move · double-click an edge to add · right-click a vertex to remove · Esc to finish.')
        : tr('Drag to move · click an edge to edit it · drag the knob to rotate · double-click to edit vertices.');
    body.appendChild(hint);
    return true;
  }
  return false;
}

function renderLinePanel(body: HTMLElement, wall: Wall | undefined): boolean {
  if (wall && wall.style === 'line') {
    body.appendChild(sectionTitle('Line'));
    body.appendChild(nameRow(wall.name, dictDefaults(wallWord(wall), trIn), (v) => { wall.name = v.trim() || undefined; invalidate(); }));
    body.appendChild(colorRow('Colour', wall.fill, (hex) => (wall.fill = hex)));
    body.appendChild(opacityRow('Opacity', wall.fillOpacity, (v) => (wall.fillOpacity = v)));
    body.appendChild(numberRow('Line width (mm)', wall.thickness, (v) => { pushHistory(); wall.thickness = Math.max(1, v); invalidate(); }, { commit: true }));
    body.appendChild(numberRow('Height (mm)', wall.height ?? DEFAULT_WALL_HEIGHT, (v) => { pushHistory(); wall.height = Math.max(0, v); invalidate(); }, { commit: true }));
    body.appendChild(heightFromGroundRow(wallBase(wall), (v) => { wall.heightFromGround = v; }));
    const hint = document.createElement('div');
    hint.className = 'prop-empty';
    hint.style.marginTop = '10px';
    hint.textContent = tr('Double-click to edit vertices.');
    body.appendChild(hint);
    return true;
  }
  return false;
}

function renderWallPanel(body: HTMLElement, wall: Wall | undefined): boolean {
  if (wall) {
    body.appendChild(sectionTitle('Wall'));
    body.appendChild(nameRow(wall.name, dictDefaults(wallWord(wall), trIn), (v) => { wall.name = v.trim() || undefined; invalidate(); }));
    body.appendChild(numberRow('Thickness (mm)', wall.thickness, (v) => { pushHistory(); wall.thickness = Math.max(1, v); invalidate(); }, { commit: true }));
    body.appendChild(numberRow('Height (mm)', wall.height ?? DEFAULT_WALL_HEIGHT, (v) => { pushHistory(); wall.height = Math.max(0, v); invalidate(); }, { commit: true }));
    body.appendChild(heightFromGroundRow(wallBase(wall), (v) => { wall.heightFromGround = v; }));
    body.appendChild(sectionTitle('Fill'));
    body.appendChild(colorRow('Colour', wall.fill, (hex) => (wall.fill = hex)));
    body.appendChild(opacityRow('Opacity', wall.fillOpacity, (v) => (wall.fillOpacity = v)));
    body.appendChild(sectionTitle('Outline'));
    body.appendChild(colorRow('Colour', wall.stroke ?? darkenHex(wall.fill, 0.5), (hex) => (wall.stroke = hex)));
    body.appendChild(opacityRow('Opacity', wall.strokeOpacity ?? wall.fillOpacity, (v) => (wall.strokeOpacity = v)));
    body.appendChild(sectionTitle('Placement'));
    body.appendChild(selectRow('Alignment', [['center', 'Centred'], ['left', 'Left of line'], ['right', 'Right of line']], wall.align, (v) => {
      pushHistory();
      wall.align = v as Wall['align'];
      invalidate();
    }));
    // Its doors, panes and openings, one row each (a click selects it).
    const feats = wallFeatureSpans(wall);
    if (feats.length) {
      body.appendChild(sectionTitle('Doors, panes and openings'));
      feats.forEach((sp, i) => {
        body.appendChild(buttonRow(`${tr(WALL_FEATURE_LABEL[sp.f.kind])} ${i + 1} — ${formatLen(sp.e - sp.s)}`, () => pickWallFeature(wall, sp.f)));
      });
    }
    const hint = document.createElement('div');
    hint.className = 'prop-empty';
    hint.style.marginTop = '10px';
    hint.textContent = tr('Double-click to edit vertices.');
    body.appendChild(hint);
    return true;
  }
  return false;
}

/** A selected reflected ceiling plan: its room, the mirror's height (one undo
 *  step, nothing recorded when it would draw the same), Delete. */
/** A view's title selected on its own: what it says, Reset title position, Delete. */
function renderViewTitlePanel(body: HTMLElement): boolean {
  const t = state.selectedViewTitle, v = t ? viewOfTitle(t) : undefined;
  if (!t || !v) return false;
  body.appendChild(sectionTitle(t.kind === 'section' ? 'Section title' : 'Ceiling plan title'));
  body.appendChild(infoRow('Title', viewTitleText(t)));
  viewTitleRows(body, v);
  body.appendChild(deleteRow(() => deleteViewTitle()));
  const hint = document.createElement('div');
  hint.className = 'prop-empty';
  hint.style.marginTop = '10px';
  hint.textContent = tr('Drag the title to move it. Deleted, its view’s panel shows it again.');
  body.appendChild(hint);
  return true;
}

function renderRcpPanel(body: HTMLElement): boolean {
  const views = state.drawing.ceilingPlans || [];
  const v = state.selectedRcpId ? views.find((x) => x.id === state.selectedRcpId) : undefined;
  if (!v) return false;
  const room = rcpRoom(v);
  if (v.kind === 'above') return renderAboveCeilingPanel(body, v, room);
  body.appendChild(sectionTitle('Reflected ceiling plan'));
  if (room) body.appendChild(infoRow('Room', numberedInstanceNames().get(room.id) ?? ''));
  body.appendChild(numberRow('Mirror height (mm)', rcpHeightOf(v), (mm) => {
    beginGesture();
    const msg = setRcpHeight(v, room, mm);
    commitGesture();
    renderProperties();
    invalidate();
    if (msg) flashStatus(tr(msg.text).replace('{min}', String(msg.min)).replace('{max}', String(msg.max)));
  }, { commit: true }));
  viewTitleRows(body, v);
  body.appendChild(deleteRow(() => { pushHistory(); state.drawing.ceilingPlans = views.filter((x) => x.id !== v.id); state.selectedRcpId = null; renderProperties(); invalidate(); }));
  const hint = document.createElement('div');
  hint.className = 'prop-empty';
  hint.style.marginTop = '10px';
  hint.textContent = tr('Drag the drawing to move it: it snaps in line with its room.');
  body.appendChild(hint);
  return true;
}

/** The plan above a room's ceiling: its room, how many parts lie over the
 *  ceiling, its title rows, Delete. No mirror: it is seen from above. */
function renderAboveCeilingPanel(body: HTMLElement, v: CeilingPlanView, room: ProductInstance | null): boolean {
  body.appendChild(sectionTitle('Plan above the ceiling'));
  if (room) {
    body.appendChild(infoRow('Room', numberedInstanceNames().get(room.id) ?? ''));
    body.appendChild(infoRow('Parts over the ceiling', String(state.drawing.instances.filter((p) => !objHidden(p.id) && liesAboveCeiling(room, p)).length)));
  }
  viewTitleRows(body, v);
  body.appendChild(deleteRow(() => { pushHistory(); state.drawing.ceilingPlans = (state.drawing.ceilingPlans || []).filter((x) => x.id !== v.id); state.selectedRcpId = null; renderProperties(); invalidate(); }));
  const hint = document.createElement('div');
  hint.className = 'prop-empty';
  hint.style.marginTop = '10px';
  hint.textContent = tr('Drag the drawing to move it: it snaps in line with its room.');
  body.appendChild(hint);
  return true;
}

function renderSectionPanel(body: HTMLElement): boolean {
  if (state.selectedSectionId) {
    const secs = state.drawing.sections || [];
    const idx = secs.findIndex((s) => s.id === state.selectedSectionId);
    const sec = secs[idx];
    if (sec) {
      body.appendChild(sectionTitle(`${tr('Section')} ${sectionLabel(idx)}`));
      body.appendChild(infoRow('Length', formatLen(Math.hypot(sec.b.x - sec.a.x, sec.b.y - sec.a.y))));
      body.appendChild(numberRow('Offset (mm)', sec.offset, (v) => { pushHistory(); sec.offset = Math.max(0, v); invalidate(); }, { commit: true }));
      body.appendChild(buttonRow('Flip view direction', () => { pushHistory(); sec.side = (sec.side === 1 ? -1 : 1); invalidate(); }));
      body.appendChild(buttonRow(isFixedObject(sec.id) ? 'Unfix' : 'Fix in place', () => toggleFix(sec.id)));
      const below = sectionIsBelow(sec);
      body.appendChild(buttonRow(below ? 'Bring above objects' : 'Send below objects', () => {
        pushHistory();
        if (below) moveToLayer(state.drawing, LAYER_ENV, [sec.id], layers().layers.find((l) => l.home === 'annotations')?.id ?? layers().layers[layers().layers.length - 1].id);
        else sendSectionBelow(state.drawing, LAYER_ENV, sec.id);
        renderProperties();
        invalidate();
      }));
      if (sec.viewOrigin || sec.viewAngle != null) {
        body.appendChild(buttonRow('Reset drawing position', () => { pushHistory(); delete sec.viewOrigin; delete sec.viewAngle; invalidate(); }));
      }
      viewTitleRows(body, sec);
      body.appendChild(deleteRow(() => { pushHistory(); state.drawing.sections = secs.filter((s) => s.id !== state.selectedSectionId); state.selectedSectionId = null; renderProperties(); invalidate(); }));
      const hint = document.createElement('div');
      hint.className = 'prop-empty';
      hint.style.marginTop = '10px';
      hint.textContent = tr('Drag the drawing to move it · drag the round handle to rotate.');
      body.appendChild(hint);
      const drill = document.createElement('div');
      drill.className = 'prop-empty';
      drill.textContent = tr('Click the drawing again to pick what it shows.');
      body.appendChild(drill);
      return true;
    }
  }
  return false;
}

function renderRevisionCloudPanel(body: HTMLElement): boolean {
  if (state.selectedRevCloudId) {
    const clouds = state.drawing.revisionClouds || [];
    const cloud = clouds.find((c) => c.id === state.selectedRevCloudId);
    if (cloud) {
      const n = state.selectedRevCloudIds.length > 1 ? state.selectedRevCloudIds.length : 1;
      body.appendChild(sectionTitle(n > 1 ? `${n} revision clouds` : 'Revision cloud'));
      const revs = state.drawing.revisions || [];
      const revRow = document.createElement('div');
      revRow.className = 'prop-row';
      const revLab = document.createElement('label');
      revLab.textContent = tr('Revision');
      const revSel = document.createElement('select');
      for (const r of revs) {
        const opt = document.createElement('option');
        opt.value = r.id;
        opt.textContent = r.tag || tr('(untitled)');
        revSel.appendChild(opt);
      }
      revSel.value = cloud.revisionId;
      revSel.addEventListener('change', () => {
        pushHistory();
        const ids = new Set(state.selectedRevCloudIds.length ? state.selectedRevCloudIds : [cloud.id]);
        for (const c of clouds) if (ids.has(c.id)) c.revisionId = revSel.value;
        invalidate();
      });
      revRow.append(revLab, revSel);
      body.appendChild(revRow);
      body.appendChild(colorRow('Colour', cloud.color ?? REVCLOUD_COLOR, (hex) => { cloud.color = hex; invalidate(); }));
      body.appendChild(buttonRow(n > 1 ? 'Delete clouds' : 'Delete cloud', deleteSelected));
      const hint = document.createElement('div');
      hint.className = 'prop-empty';
      hint.style.marginTop = '10px';
      hint.textContent = state.editVerticesId === cloud.id
        ? tr('Editing shape — drag a corner to reshape · double-click an edge to add a corner · right-click a corner to remove · drag inside to move · Enter/Esc to finish.')
        : tr('Drag to move · double-click to edit its shape.');
      body.appendChild(hint);
      return true;
    }
  }
  return false;
}

function renderDimensionPanel(body: HTMLElement, dim: DimensionLine | undefined): boolean {
  if (dim) {
    const geom = dimensionGeometry(dim.a, dim.b, dim.offset, dim.kind);
    body.appendChild(sectionTitle('Dimension line'));
    body.appendChild(infoRow('Length', formatLen(geom ? geom.length : distance(dim.a, dim.b))));
    body.appendChild(infoRow('ΔX', formatLen(Math.abs(dim.b.x - dim.a.x))));
    body.appendChild(infoRow('ΔY', formatLen(Math.abs(dim.b.y - dim.a.y))));
    // What it measures. Normally chosen by where the third click landed; this is
    // how you change your mind without redrawing it. Switching kind re-reads the
    // offset from the line's CURRENT position, so it stays where it was put
    // instead of jumping to a distance measured along a different axis.
    const typeRow = document.createElement('div');
    typeRow.className = 'prop-row';
    const typeLab = document.createElement('label');
    typeLab.textContent = tr('Measures');
    const typeSel = document.createElement('select');
    for (const [val, txt] of [['aligned', 'Aligned'], ['horizontal', 'Horizontal'], ['vertical', 'Vertical']] as const) {
      const opt = document.createElement('option');
      opt.value = val;
      opt.textContent = tr(txt);
      typeSel.appendChild(opt);
    }
    typeSel.value = dim.kind ?? 'aligned';
    typeSel.addEventListener('change', () => {
      const next = typeSel.value as DimKind;
      pushHistory();
      const here = geom ? { x: (geom.a.x + geom.b.x) / 2, y: (geom.a.y + geom.b.y) / 2 } : null;
      if (next === 'aligned') delete dim.kind; else dim.kind = next;
      if (here) dim.offset = dimensionOffset(dim.a, dim.b, here, next);
      renderProperties();
      invalidate();
    });
    typeRow.append(typeLab, typeSel);
    body.appendChild(typeRow);
    body.appendChild(numberRow('Offset (mm)', dim.offset, (v) => { pushHistory(); dim.offset = v; invalidate(); }, { commit: true }));
    // Dimension style — how every dimension value in the drawing is formatted
    // (drawing-wide, saved with the file).
    body.appendChild(selectRow('Style', DIM_STYLE_OPTIONS, drawingDimStyle(state.drawing), (v) => {
      pushHistory();
      state.drawing.dimStyle = v as DimStyle;
      renderProperties();
      invalidate();
    }));
    body.appendChild(colorRow('Colour', dim.color ?? ANNOTATION_COLOR, (hex) => (dim.color = hex)));
    // A dimension spanning two corners of one ring shape can be pinned as a
    // persistent GCS distance constraint: the solver then holds that distance
    // while other corners are dragged, and editing the length drives the shape.
    const rr = dimRingRef(dim);
    if (rr) {
      const relIdx = findDistanceRelation(ringRefRelations(rr), rr.ai, rr.bi);
      const locked = relIdx >= 0;
      const btn = document.createElement('button');
      btn.textContent = tr(locked ? '🔒 Distance locked' : '🔓 Lock distance');
      btn.className = 'toggle' + (locked ? ' active' : '');
      btn.style.width = '100%'; btn.style.fontSize = '12px'; btn.style.padding = '6px 4px'; btn.style.margin = '6px 0 4px';
      btn.addEventListener('click', () => {
        if (locked) removeRingRelation(rr, relIdx);
        // The TRUE point-to-point distance, not the figure on the label: a
        // horizontal dimension reads only ΔX, but the constraint it pins is the
        // distance between the two corners.
        else addRingRelation(rr, { type: 'distancePP', verts: [rr.ai, rr.bi], value: Math.round(distance(dim.a, dim.b)) });
        syncDimensions(); renderProperties(); invalidate();
      });
      body.appendChild(btn);
      appendConstraintStatus(body, ringRefPts(rr), ringRefCons(rr));
    }
    const hint = document.createElement('div');
    hint.className = 'prop-empty';
    hint.style.marginTop = '10px';
    hint.textContent = rr
      ? tr('Double-click to type a new length · Lock the distance to hold it while dragging.')
      : tr('Double-click to type a new length.');
    body.appendChild(hint);
    return true;
  }
  return false;
}

function renderInstancePanel(body: HTMLElement, inst: ProductInstance): boolean {
  const badge = document.createElement('span');
  badge.className = 'badge';
  const traits = traitsOf(inst.category);
  badge.style.background = traits.color;
  badge.textContent = traits.name ? tr(traits.name) : String(inst.category);

  const title = document.createElement('div');
  title.style.display = 'flex';
  title.style.alignItems = 'center';
  title.style.justifyContent = 'flex-end';
  title.append(badge);
  body.appendChild(title);

  // Its name: entered over its product's (shown as the label in the centre of it).
  body.appendChild(nameRow(inst.enteredName, inst.nameI18n, (v) => { setPartName(inst.id, v); invalidate(); }, { base: inst.name }));

  // The variant the project uses, when its product is sold in several.
  appendVariantRow(body, [inst]);

  // Polyline-bench: simplified panel. Editing the total length scales the whole
  // polyline about its centre; a heated bench's depth is editable (a salt wall's
  // cross-section height is fixed, so it stays read-only).
  if (appendNodeRunCard(body, inst)) return true;

  body.appendChild(sectionTitle('Placement'));
  // A typed X / Y moves it as the keys do: what a room carries goes along.
  const placeAt = (x: number, y: number): void => moveSelectionOnce((s) => s.moveTo({ x: x - inst.x, y: y - inst.y }), [{ kind: 'instance', obj: inst }]);
  body.appendChild(numberRow('X (mm)', inst.x, (v) => placeAt(v, inst.y), { commit: true }));
  body.appendChild(numberRow('Y (mm)', inst.y, (v) => placeAt(inst.x, v), { commit: true }));
  body.appendChild(heightFromGroundRow(instanceBase(inst), (v) => editRoomTop(inst, () => { inst.heightFromGround = v; })));

  body.appendChild(sectionTitle('Size & area'));
  const locked = resizeLocked(inst);
  if (!inst.autoBenches) body.appendChild(lockResizeToggle(inst, locked));
  if (!locked) body.appendChild(dimChainToggle());
  body.appendChild(numberRow('Width (mm)', inst.width, (v) => {
    pushHistory();
    const f = inst.width > 0 ? v / inst.width : 1;
    resizeInstance(inst, v, dimChained ? inst.depth * f : inst.depth); // chained: scale depth too
    renderProperties(); invalidate();
  }, { commit: true, disabled: locked }));
  body.appendChild(numberRow('Depth (mm)', inst.depth, (v) => {
    pushHistory();
    const f = inst.depth > 0 ? v / inst.depth : 1;
    resizeInstance(inst, dimChained ? inst.width * f : inst.width, v); // chained: scale width too
    renderProperties(); invalidate();
  }, { commit: true, disabled: locked }));
  const areaMm2 = isPolyRoom(inst) && inst.polygon && inst.polygon.length >= 3
    ? Math.abs(polygonArea(inst.polygon)) / 2 // actual polygon (polygonArea is 2×), not the bounding box
    : inst.width * inst.depth;
  body.appendChild(infoRow('Area', `${(areaMm2 / 1e6).toFixed(2)} m²`));
  // Reset a part back to its product's standard footprint (rooms are sized freely
  // and re-generate their benches, so they don't get this; locked parts can't resize).
  appendStandardSizeReset(body, inst, locked);
  appendRoomHeightRows(body, inst);
  body.appendChild(numberRow('Rotation Z (°)', inst.rotation, (v) => { pushHistory(); inst.rotation = v; invalidate(); }, { commit: true }));
  // A part that tilts: its Rotation X and Y on its own axes, after its Z.
  if (canTilt(inst)) {
    const tilt = (next: { rotationX?: number; rotationY?: number }): void => {
      pushHistory();
      tiltPart(inst, next);
      const room = saunaOfHeater(inst);
      if (room) regenerateSauna(room);
      renderProperties(); invalidate();
    };
    body.appendChild(numberRow('Rotation X (°)', inst.rotationX ?? 0, (v) => tilt({ rotationX: v }), { commit: true }));
    body.appendChild(numberRow('Rotation Y (°)', inst.rotationY ?? 0, (v) => tilt({ rotationY: v }), { commit: true }));
  }
  // Appearance before what only a room has.
  appendRoomColoursCard(body, inst);
  appendRoomWoodCard(body, inst);
  appendRoomFinishCard(body, inst);
  if (hasCeiling(inst)) appendCeilingCard(body, inst);
  appendFloorGrilleCard(body, inst);
  appendLightingCopyCard(body, inst);
  appendLightLevelsCard(body, inst);
  // Saunas (rectangular and polygonal): read-only air volume and the derived
  // heater power (kW) from the sizing table, sized on the effective volume
  // (volume + glass area − 2).
  if (inst.category === 'sauna') {
    const volume = roomVolumeM3(inst);
    const kw = saunaPowerKw(volume + (saunaGlassAreaM2(inst) - 2));
    body.appendChild(sectionTitle('Heater sizing'));
    body.appendChild(infoRow('Volume', `${volume.toFixed(2)} m³`));
    body.appendChild(infoRow('Power', `${kw.toFixed(1)} kW`));
  }

  // Bench-layout summary (recomputed from the current footprint).
  appendRoomBenchLayoutCard(body, inst);

  // Repeatable products: show a Flip button that moves all copies in the group
  // to the other side of their baseline.
  if (inst.category === 'repeatable') appendRowSection(body, inst);

  // A part's category fields (ui/categoryFields.ts): a heater's or a shower's
  // body, a heater's clearance gap and guard cover, a run's power.
  if (!inst.autoBenches) appendCategoryFields(body, inst);

  // Saunas / steam rooms don't expose generic product properties (heater, power,
  // cladding, etc.) — their configuration is the size/bench/door fields above.
  appendProductPropertiesCard(body, inst);
  return false;
}

// ---- the cards of a part's or room's panel, in the order renderInstancePanel appends them ----

/** Where a placed copy keeps the variant it uses: a multi-part product's on its
 *  group's `source`, anything else on the copy. */
function variantHolder(inst: ProductInstance): { variant?: ProductVariant } {
  return productGroupOf(inst)?.source ?? inst;
}

/** The catalogue product a placed copy is sold as, when it has variants. */
function variantProduct(inst: ProductInstance): ProductTemplate | null {
  const tid = productGroupOf(inst)?.source?.templateId ?? inst.templateId;
  const t = state.templates.find((x) => x.id === tid);
  return t?.variants?.length ? t : null;
}

/** The *Variant* row of copies of one product (docs/claude/products.md, *Product
 *  variants*): which of its variants the project uses. A pick copies that variant
 *  onto every one of them (a multi-part product's group once), one undo step.
 *  Nothing when they are not all of one product with variants. */
export function appendVariantRow(body: HTMLElement, insts: ProductInstance[]): void {
  const t = insts[0] ? variantProduct(insts[0]) : null;
  if (!t || insts.some((i) => variantProduct(i) !== t)) return;
  const variants = t.variants ?? [];
  const holders = [...new Set(insts.map(variantHolder))];
  const lang = state.drawing.language ?? 'en';
  const row = document.createElement('div');
  row.className = 'prop-row';
  const lab = document.createElement('label');
  lab.textContent = tr('Variant');
  const sel = document.createElement('select');
  sel.dataset.role = 'variant';
  const add = (value: string, text: string): void => {
    const opt = document.createElement('option');
    opt.value = value;
    opt.textContent = text;
    sel.appendChild(opt);
  };
  for (const v of variants) add(v.id, variantLabel(v, lang) || v.id);
  // A copy's variant its product no longer lists is shown, never changed by opening.
  for (const h of holders) {
    const v = h.variant;
    if (v && !variants.some((x) => x.id === v.id) && ![...sel.options].some((o) => o.value === v.id)) add(v.id, variantLabel(v, lang) || v.id);
  }
  const ids = new Set(holders.map((h) => h.variant?.id ?? ''));
  if (ids.size > 1 || ids.has('')) {
    const opt = document.createElement('option');
    opt.value = '';
    opt.textContent = ids.size > 1 ? tr('(mixed)') : '—';
    opt.disabled = true;
    sel.prepend(opt);
  }
  sel.value = ids.size === 1 ? [...ids][0] : '';
  sel.addEventListener('change', () => {
    const v = variants.find((x) => x.id === sel.value);
    if (!v) return;
    pushHistory();
    for (const h of holders) h.variant = copyVariant(v);
    renderProperties();
    invalidate();
  });
  row.append(lab, sel);
  body.appendChild(row);
}

function appendNodeRunCard(body: HTMLElement, inst: ProductInstance): boolean {
  if (inst.nodes && inst.nodes.length >= 2) {
    const totalLen = polyBenchLength(inst.nodes);
    body.appendChild(sectionTitle('Polyline'));
    body.appendChild(numberRow('Total length (mm)', Math.round(totalLen), (v) => {
      if (v > 0) { pushHistory(); resizeInstance(inst, v, inst.depth); renderProperties(); invalidate(); }
    }, { commit: true }));
    if (inst.category === 'heatedbench') {
      body.appendChild(numberRow('Depth (mm)', inst.depth, (v) => {
        if (v > 0) { pushHistory(); resizeInstance(inst, inst.width, v); renderProperties(); invalidate(); }
      }, { commit: true }));
    } else {
      body.appendChild(infoRow('Depth (mm)', String(inst.depth)));
    }
    if (runTakesHeightRow(inst.category)) {
      body.appendChild(numberRow('Height (mm)', objectHeight(inst), (v) => {
        pushHistory(); setObjectHeight(inst, v); renderProperties(); invalidate();
      }, { commit: true }));
    }
    body.appendChild(heightFromGroundRow(instanceBase(inst), (v) => { inst.heightFromGround = v; }));
    body.appendChild(infoRow('Segments', String(inst.nodes.length - 1)));
    // Heated benches: electrical power per running metre, with the total power
    // derived from the bench's longest running length (front vs back differ at
    // corners; the longer one is used).
    if (inst.category === 'heatedbench') {
      body.appendChild(sectionTitle('Power'));
      body.appendChild(numberRow('Power per running meter (W/m)', inst.powerPerMeter ?? 0, (v) => {
        pushHistory();
        inst.powerPerMeter = Math.max(0, Math.round(v));
        renderProperties(); // refresh the derived total below
        invalidate();
      }, { commit: true }));
      const sides = heatedBenchSideLengths(inst.nodes, inst.depth);
      const runLen = Math.max(sides.back, sides.front); // mm; the longer of front/back
      const total = Math.round((inst.powerPerMeter ?? 0) * (runLen / 1000)); // W
      body.appendChild(infoRow('Running length', `${(runLen / 1000).toFixed(2)} m`));
      body.appendChild(infoRow('Total power', `${total} W`));
    }
    // Salt walls: height + electrical power per square metre, with the total power
    // derived from the surface area (total length × height).
    if (inst.category === 'saltwall') {
      body.appendChild(sectionTitle('Salt wall'));
      body.appendChild(numberRow('Height (mm)', objectHeight(inst), (v) => {
        pushHistory();
        inst.height = Math.max(1, Math.round(v));
        renderProperties(); // refresh the derived total below
        invalidate();
      }, { commit: true }));
      body.appendChild(sectionTitle('Power'));
      body.appendChild(numberRow('Power per square meter (W/m²)', inst.powerPerSqm ?? 0, (v) => {
        pushHistory();
        inst.powerPerSqm = Math.max(0, Math.round(v));
        renderProperties();
        invalidate();
      }, { commit: true }));
      const areaM2 = (polyBenchLength(inst.nodes) / 1000) * (objectHeight(inst) / 1000); // length × height
      const total = Math.round((inst.powerPerSqm ?? 0) * areaM2); // W
      body.appendChild(infoRow('Surface area', `${areaM2.toFixed(2)} m²`));
      body.appendChild(infoRow('Total power', `${total} W`));
    }
    // Sauna bench: the same unified slat (doghe) editor as sauna-room benches;
    // recall a preset or edit slats/gaps directly. The depth (shown above) is
    // derived from the slat layout.
    const bench = inst.category === 'saunabench' ? benchOf(inst, inst.id) : null;
    if (bench) {
      // Its boards are written as a room bench's are (`benchOf`): one undo step.
      const edit = (patch: SlatPatch): void => { pushHistory(); bench.write(patch); renderProperties(); invalidate(); };
      buildSlatSection(body, inst, 1, edit);
      if (inst.slats?.length) appendSkirtingCard(body, inst, edit);
      if (inst.slats?.length && inst.nodes?.length) appendBenchEndsCard(body, inst, inst.id, inst);
    }
    const hint = document.createElement('div');
    hint.className = 'prop-empty';
    hint.style.marginTop = '10px';
    hint.textContent = tr('Drag body to move · drag node handles to reshape · double-click a segment to add a node · right-click a node to delete.');
    body.appendChild(hint);
    return true;
  }
  return false;
}

function appendStandardSizeReset(body: HTMLElement, inst: ProductInstance, locked: boolean): void {
  if (!inst.autoBenches && !locked) {
    const std = standardSizeOf(inst);
    if (std && (std.width !== inst.width || std.depth !== inst.depth)) {
      const reset = document.createElement('button');
      reset.className = 'panel-btn';
      reset.textContent = `↺ ${tr('Reset to standard size')} (${Math.round(std.width)} × ${Math.round(std.depth)} mm)`;
      reset.addEventListener('click', () => {
        pushHistory();
        resizeInstance(inst, std.width, std.depth);
        renderProperties();
        invalidate();
      });
      body.appendChild(reset);
    }
  }
}

function appendRoomHeightRows(body: HTMLElement, inst: ProductInstance): void {
  if (inst.autoBenches) {
    // The crown: under a shaped ceiling only the crown moves, never below the eave.
    body.appendChild(numberRow('Height (mm)', externalHeight(inst), (v) => {
      beginGesture();
      const msg = ceilingNoteText(editRoomTop(inst, () => setCeilingHeight(inst, v)));
      regenerateSauna(inst);
      commitGesture();
      renderProperties();
      invalidate();
      if (msg) flashStatus(msg);
    }, { commit: true }));
  }
}

type PropTab =
  | 'connections'
  | 'constraints'
  | 'drawing'
  | 'parts'
  | 'preparations'
  | 'properties';

/** Tab bar at the top of the properties panel. Non-Parts tabs are disabled when
 *  nothing is selected; the temporary Drawing tab is prepended only while a
 *  drawing tool is active. */
/** A repeat row's section: its copy count, and Flip, which moves every copy of
 *  the row (its group) to the other side of the baseline. */
export function appendRowSection(body: HTMLElement, inst: ProductInstance): void {
  const groupInsts = inst.groupId
    ? state.drawing.instances.filter((i) => i.groupId === inst.groupId && i.category === 'repeatable')
    : [inst];
  body.appendChild(sectionTitle('Row'));
  body.appendChild(infoRow('Copies', String(groupInsts.length)));
  body.appendChild(buttonRow('↕ Flip to other side', () => {
    pushHistory();
    for (const g of groupInsts) {
      const rad = g.rotation * Math.PI / 180;
      g.x = g.x + g.depth * Math.sin(rad);
      g.y = g.y - g.depth * Math.cos(rad);
      g.rotation = (g.rotation + 180) % 360;
    }
    invalidate();
  }));
}

/** The user picked Properties with nothing selected: show the drawing settings
 *  there (not Parts, the no-selection default) until they pick another tab. */
let drawingSettingsTab = false;

function appendPropTabBar(body: HTMLElement, active: PropTab, hasSelection: boolean, showDrawing = false): void {
  const bar = document.createElement('div');
  bar.className = 'prop-tabs';
  const tabs: Array<[PropTab, string]> = [
    ['properties', 'Properties'],
    ['parts', 'Parts'],
    ['constraints', 'Constraints'],
    ['connections', 'Connections'],
    ['preparations', 'Preparations'],
  ];
  if (showDrawing) tabs.unshift(['drawing', 'Drawing']);
  for (const [id, label] of tabs) {
    const b = document.createElement('button');
    b.className = 'prop-tab' + (active === id ? ' active' : '');
    b.dataset.tab = id;
    b.textContent = tr(label);
    // With nothing selected, Properties shows the drawing's own settings.
    b.addEventListener('click', () => { state.propTab = id; drawingSettingsTab = !hasSelection && id === 'properties'; renderProperties(); });
    bar.appendChild(b);
  }
  body.appendChild(bar);
}

/** In-flight viewport zoom animation frame handle (0 when idle). */
let zoomAnim = 0;
/** Cancel any running zoom animation (called when the user takes the view over). */
function cancelZoomAnim(): void { if (zoomAnim) { cancelAnimationFrame(zoomAnim); zoomAnim = 0; } }

/** Ease-in-out the viewport from its current pose to `target` over `durationMs`,
 *  interpolating the screen-centre world point linearly and the zoom factor
 *  geometrically (so the zoom feels even), then settling exactly on the target. */
function animateViewportTo(target: { scale: number; tx: number; ty: number }, durationMs: number): void {
  cancelZoomAnim();
  const W = viewW(), H = viewH();
  const startScale = vp.scale;
  const startCenter = vp.screenToWorld({ x: W / 2, y: H / 2 });
  const targetCenter = { x: (W / 2 - target.tx) / target.scale, y: -(H / 2 - target.ty) / target.scale };
  if (!(startScale > 0) || !(target.scale > 0)) { vp.scale = target.scale; vp.tx = target.tx; vp.ty = target.ty; redrawBackdrop(); invalidate(); return; }
  const t0 = performance.now();
  const ease = (p: number): number => (p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2); // easeInOutQuad
  const step = (): void => {
    const p = Math.min(1, (performance.now() - t0) / durationMs);
    const e = ease(p);
    const scale = startScale * Math.pow(target.scale / startScale, e);
    const cx = startCenter.x + (targetCenter.x - startCenter.x) * e;
    const cy = startCenter.y + (targetCenter.y - startCenter.y) * e;
    vp.scale = scale;
    vp.tx = W / 2 - cx * scale;
    vp.ty = H / 2 + cy * scale;
    redrawBackdrop();
    repaint();
    zoomAnim = p < 1 ? requestAnimationFrame(step) : 0;
  };
  zoomAnim = requestAnimationFrame(step);
}

/** Smoothly zoom the model view to fill the screen with one part (Parts tab ›
 *  right-click › Zoom to). Works from any tab: it switches to the model view
 *  first, then animates over ~1 s. */
/** Animate the model view to frame a set of world points (their bounding box). */
export function zoomToPoints(pts: Vec2[]): void {
  if (state.viewTab !== 'model') setViewTab('model');
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const p of pts) { if (p.x < minX) minX = p.x; if (p.y < minY) minY = p.y; if (p.x > maxX) maxX = p.x; if (p.y > maxY) maxY = p.y; }
  if (!isFinite(minX)) return;
  const tmp = new Viewport();
  tmp.scale = vp.scale; tmp.tx = vp.tx; tmp.ty = vp.ty;
  tmp.fit({ min: { x: minX, y: minY }, max: { x: maxX, y: maxY } }, viewW(), viewH(), 0.12);
  animateViewportTo({ scale: tmp.scale, tx: tmp.tx, ty: tmp.ty }, 1000);
}

export function zoomToInstance(m: ProductInstance): void {
  zoomToPoints(instanceOutline(m));
}

/** Preparations tab with NOTHING selected: the whole project's preparation
 *  schedule as a table (No. / Part / Description), like the in-drawing schedule.
 *  Each description is a multiline cell you can click into and edit; the other
 *  columns are read-only here. Mirrors renderAllConnections. */
function renderAllPreparations(body: HTMLElement): void {
  body.appendChild(sectionTitle('Preparations'));
  const rows = preparationRows(state.drawing);
  if (rows.length === 0) {
    const e = document.createElement('div');
    e.className = 'prop-empty';
    e.textContent = tr('No preparations in this project yet.');
    body.appendChild(e);
    return;
  }
  const s = scheduleStrings(state.drawing.language ?? 'it');
  const table = document.createElement('table');
  table.className = 'conn-table';
  const thead = document.createElement('thead');
  const htr = document.createElement('tr');
  for (const h of [s.no, s.description]) {
    const th = document.createElement('th'); th.textContent = h; htr.appendChild(th);
  }
  thead.appendChild(htr); table.appendChild(thead);
  const tbody = document.createElement('tbody');
  for (const r of rows) {
    const tr2 = document.createElement('tr');
    const numTd = document.createElement('td');
    numTd.className = 'conn-num';
    numTd.textContent = r.num;
    numTd.style.color = CONNECTION_COLORS[r.type] ?? '#cdd6e0';
    const descTd = document.createElement('td');
    const ta = document.createElement('textarea');
    ta.className = 'conn-desc';
    // Its label alone: the power and voltage a schedule adds are not part of it.
    ta.value = labelShown(findPreparation(r.id));
    ta.rows = Math.max(1, ta.value.split('\n').length);
    const grow = (): void => { ta.style.height = 'auto'; ta.style.height = `${ta.scrollHeight}px`; };
    ta.addEventListener('focus', () => { beginGesture(); grow(); });
    ta.addEventListener('input', () => {
      const p = findPreparation(r.id);
      if (p) setTypedLabel(p, ta.value);
      grow();
      invalidate();
    });
    descTd.appendChild(ta);
    tr2.append(numTd, descTd);
    tbody.appendChild(tr2);
  }
  table.appendChild(tbody);
  body.appendChild(table);
}

/** Preparations tab: the selected host's rough-ins, by discipline. The host is a
 *  placed part, a room or a user-drawn polygon — each carries a `preparations`
 *  array. A room is offered no electrical one (`preparationTypes`). */
function renderPreparationsTab(body: HTMLElement, host: { preparations?: Preparation[]; autoBenches?: boolean }): void {
  body.appendChild(sectionTitle('Preparations'));
  const nums = scheduleNumbers(preparationSchedule(state.drawing)); // E#/P#/M#
  const inst = host, room = isElecRoom(host);
  for (const prep of inst.preparations || []) {
    const row = document.createElement('div');
    row.className = 'prep-card';
    // Header row: schedule number, discipline picker, delete — kept compact so
    // the description below gets the full panel width.
    const head = document.createElement('div');
    head.className = 'prep-card-head';
    const num = document.createElement('span');
    num.className = 'conn-num';
    num.style.color = DISCIPLINE_COLORS[prep.type];
    num.textContent = nums.get(prep.id) ?? '—';
    const type = document.createElement('select');
    for (const t of preparationTypes(room, prep.type)) {
      const opt = document.createElement('option');
      opt.value = t;
      opt.textContent = disciplineLabel(t);
      type.appendChild(opt);
    }
    type.value = prep.type;
    type.addEventListener('change', () => { pushHistory(); prep.type = type.value as ConnectionType; renderProperties(); invalidate(); });
    const del = document.createElement('button');
    del.className = 'del mini';
    del.textContent = '✕';
    del.title = tr('Delete preparation');
    del.addEventListener('click', () => {
      pushHistory();
      inst.preparations = (inst.preparations || []).filter((x) => x !== prep);
      renderProperties();
      invalidate();
    });
    head.append(num, type, del);
    // Description on its own row: an auto-growing textarea so the full label is
    // always visible (these run long, e.g. water-conductivity specs).
    const desc = document.createElement('textarea');
    desc.className = 'prep-card-desc';
    desc.rows = 1;
    desc.placeholder = tr('description');
    desc.value = labelShown(prep);
    const autoGrow = (): void => { desc.style.height = 'auto'; desc.style.height = `${desc.scrollHeight}px`; };
    desc.addEventListener('input', () => { setTypedLabel(prep, desc.value); autoGrow(); invalidate(); });
    undoableLiveEdit(desc);
    row.append(head, desc);
    body.appendChild(row);
    requestAnimationFrame(autoGrow); // size to content once laid out
  }
  body.appendChild(buttonRow('＋ Add preparation', () => {
    pushHistory();
    if (!inst.preparations) inst.preparations = [];
    inst.preparations.push({ id: newId('prep'), type: preparationTypes(room)[0], label: '' });
    renderProperties();
    invalidate();
  }));
}

export function formatLen(mm: number): string {
  return formatDimension(mm, drawingDimStyle(state.drawing)); // as the drawing shows it
}

/**
 * Replace the subject and clip polygons with the result of a boolean operation
 * (union / intersection / difference). The result may be several rings (or none,
 * e.g. an empty intersection); each becomes a new polygon inheriting the
 * subject's styling. Dimensions anchored to either source polygon are dropped.
 */
export function runPolyBoolean(op: BoolOp, subjectId: string, clipId: string): void {
  const polys = state.drawing.polygons || [];
  const subject = polys.find((p) => p.id === subjectId);
  const clip = polys.find((p) => p.id === clipId);
  if (!subject || !clip) return;
  // The result sits where the subject did (its group's layer, when grouped).
  const layer = layers().layerOf.get(subjectId);
  const results = polygonBoolean(
    { outer: subject.points, holes: subject.holes || [] },
    { outer: clip.points, holes: clip.holes || [] },
    op,
  );
  pushHistory();
  removeObjectsById(new Set([subjectId, clipId]));
  const newIds: string[] = [];
  for (const rp of results) {
    const np: Polygon = {
      id: newId('poly'),
      points: rp.outer,
      holes: rp.holes.length ? rp.holes : undefined,
      fill: subject.fill,
      fillOpacity: subject.fillOpacity,
      stroke: subject.stroke,
      strokeOpacity: subject.strokeOpacity,
      ...(layer ? { layer } : {}),
    };
    (state.drawing.polygons ??= []).push(np);
    newIds.push(np.id);
    freshCopies.add(np.id);
  }
  selectObjects(state, { polygon: newIds });
  leaveEdit('shape');
  renderProperties();
  invalidate();
}

export type ZOrderOp = 'front' | 'forward' | 'backward' | 'back';

/**
 * Reorder the item with `id` within its array to change draw order. Items later
 * in the array are drawn (and so stacked) on top: 'front'/'back' jump to the far
 * end, 'forward'/'backward' move one step.
 */
function reorderZ<T extends { id: string }>(arr: T[], id: string, op: ZOrderOp): void {
  const i = arr.findIndex((x) => x.id === id);
  if (i < 0) return;
  const [item] = arr.splice(i, 1);
  if (op === 'front') arr.push(item);
  else if (op === 'back') arr.unshift(item);
  else if (op === 'forward') arr.splice(i + 1, 0, item);
  else arr.splice(Math.max(0, i - 1), 0, item);
}

/** On a sheet, the single selected object the toolbar Arrange buttons re-order,
 *  with the array it belongs to. (In the model they restack the selection within
 *  its layer: `selectionUnitIds`.) */
function selectedZTarget(): { arr: Array<{ id: string }>; id: string } | null {
  const sh = activeSheet();
  if (!sh) return null;
  // A sheet paints images, lines, connections, boxes in that order, so an object
  // restacks among its own kind — which is where sheet objects overlap.
  if (titleEditor.titleEdit || !schemSel || schemSelSet.length > 1) return null;
  return { arr: SHEET_OBJECTS[schemSel.kind].of(sh, true) as Array<{ id: string }>, id: schemSel.id };
}

/** The selection's objects that sit on a layer — not a room's door, pane, bench
 *  or inner wall (those belong to the room), not an underlay. */
function selectionLayerIds(): string[] {
  if (roomFeatureSelected()) return []; // a feature is its room's, not a layer's
  const ids = SELECTION_KINDS.flatMap((k) => (k === 'table' ? selectedOf(state, k).map((t) => schedTableId(t as SchedTableKind)) : selectedOf(state, k)));
  const m = layers();
  return ids.filter((id) => m.layerOf.has(id));
}

/** The selection as stacking items: a group wholly selected is one item. */
function selectionUnitIds(): string[] {
  const m = layers();
  const members = (x: LayerUnit): string[] => x.children.flatMap((c) => (c.kind === 'group' ? members(c) : [c.id]));
  const sel = new Set(selectionLayerIds());
  const out = new Set<string>();
  for (const id of sel) {
    let u = id;
    for (let p = m.parentOf.get(u); p; p = m.parentOf.get(p)) {
      const pu = m.unitOf.get(p);
      // A group's MEMBERS (not what stands in a room inside it) make it whole.
      if (!pu || pu.kind !== 'group' || !members(pu).every((x) => sel.has(x))) break;
      u = p;
    }
    out.add(u);
  }
  return [...out];
}

/** Is every one of `ids` already as far up (or down) its own siblings as it goes? */
function stackedAtEnd(ids: string[], top: boolean): boolean {
  const m = layers();
  return ids.every((id) => {
    const p = m.parentOf.get(id);
    const sib = (p ? m.unitOf.get(p)?.children ?? [] : m.units.get(m.layerOf.get(id) ?? '') ?? []).map((u) => u.id);
    const mine = sib.filter((x) => ids.includes(x)).length;
    const i = sib.indexOf(id);
    return top ? i >= sib.length - mine : i < mine;
  });
}

/** How the Arrange buttons restack the selection in one space: where it stands
 *  among what it restacks with (null: nothing here to restack), and the restack. */
interface Restack {
  ends(): { top: boolean; bottom: boolean } | null;
  apply(op: ZOrderOp): void;
}
/** The Arrange buttons act in the Edit commands' space (`liveSpace`), the drawing
 *  editor's included. One row per space: the toolbar's state and its click both
 *  ask it once. */
const RESTACK: Record<EditSpaceKey, Restack> = {
  // The drawing editor: the selected local shape (poly or wall) in the part's
  // shared z-order.
  part: {
    ends: () => {
      const inst = drawEditInst(), id = state.drawEditSelectedPolyId ?? state.drawEditSelectedWallId;
      if (!inst || !id) return null;
      const ordered = editorShapesZOrdered(inst), i = ordered.findIndex((x) => x.id === id);
      return { top: i < 0 || i === ordered.length - 1, bottom: i <= 0 };
    },
    apply: (op) => {
      const inst = drawEditInst(), id = state.drawEditSelectedPolyId ?? state.drawEditSelectedWallId;
      if (inst && id) reorderEditorShape(inst, id, op);
    },
  },
  // The title block's master view restacks nothing.
  title: { ends: () => null, apply: () => {} },
  // A sheet: one object among its own kind.
  sheet: {
    ends: () => {
      const t = selectedZTarget();
      if (!t) return null;
      const i = t.arr.findIndex((x) => x.id === t.id);
      return { top: i === t.arr.length - 1, bottom: i <= 0 };
    },
    apply: (op) => { const t = selectedZTarget(); if (t) reorderZ(t.arr, t.id, op); },
  },
  // The model: the selection within its own layer (a group's member within its group).
  model: {
    ends: () => {
      const ids = selectionUnitIds();
      return ids.length ? { top: stackedAtEnd(ids, true), bottom: stackedAtEnd(ids, false) } : null;
    },
    apply: (op) => arrangeLayers(state.drawing, LAYER_ENV, selectionUnitIds(), op),
  },
};

/** Enable/disable the toolbar Arrange buttons for the current selection. */
function updateArrangeButtons(): void {
  // Where the selection stands among what it restacks with, in the space it is in.
  const ends = RESTACK[liveSpace()].ends();
  const atTop = !ends || ends.top, atBottom = !ends || ends.bottom;
  const set = (id: string, disabled: boolean, reason: string): void => {
    const b = document.getElementById(id) as HTMLButtonElement | null;
    if (b) setButtonEnabled(b, !disabled, reason);
  };
  const none = !ends;
  const front = none ? 'Select something first' : 'Already at the front';
  const back = none ? 'Select something first' : 'Already at the back';
  set('btn-front', atTop, front);
  set('btn-forward', atTop, front);
  set('btn-backward', atBottom, back);
  set('btn-back', atBottom, back);
  const noFlip = !hasFlippableSelection();
  const flipWhy = activeSheet() && schemSelSet.length ? 'Nothing selected can be flipped' : 'Select something first';
  set('btn-flip-h', noFlip, flipWhy);
  set('btn-flip-v', noFlip, flipWhy);
  const selCount = selectedItemCount();
  const noAlign = selCount < 2, noDistribute = selCount < 3;
  const TWO = 'Select two or more objects first', THREE = 'Select three or more objects first';
  set('btn-align-left', noAlign, TWO);
  set('btn-align-right', noAlign, TWO);
  set('btn-align-top', noAlign, TWO);
  set('btn-align-bottom', noAlign, TWO);
  set('btn-distribute-h', noDistribute, THREE);
  set('btn-distribute-v', noDistribute, THREE);
  set('btn-attach-left', noAlign, TWO);
  set('btn-attach-right', noAlign, TWO);
  set('btn-attach-top', noAlign, TWO);
  set('btn-attach-bottom', noAlign, TWO);
}

/** A row with a chain toggle that links width & height (proportional resize). */
function dimChainToggle(): HTMLElement {
  const row = document.createElement('div');
  row.className = 'prop-row';
  const l = document.createElement('label');
  l.textContent = tr('Link W & H');
  const btn = document.createElement('button');
  btn.className = 'chain-toggle' + (dimChained ? ' active' : '');
  btn.textContent = dimChained ? '🔒' : '🔓';
  btn.title = dimChained
    ? tr('Width and height are linked — changing one scales the other. Click to unlink.')
    : tr('Width and height are independent. Click to link them proportionally.');
  btn.addEventListener('click', () => { dimChained = !dimChained; renderProperties(); });
  row.append(l, btn);
  return row;
}

/** A row with a padlock toggle that locks/unlocks resizing of one part. When
 *  locked the size fields grey out and no resize handles show. */
function lockResizeToggle(inst: ProductInstance, locked: boolean): HTMLElement {
  const row = document.createElement('div');
  row.className = 'prop-row';
  const l = document.createElement('label');
  l.textContent = tr('Lock resize');
  const btn = document.createElement('button');
  btn.className = 'chain-toggle' + (locked ? ' active' : '');
  btn.textContent = locked ? '🔒' : '🔓';
  btn.title = locked
    ? tr('Resizing is locked — the size fields and handles are disabled. Click to unlock.')
    : tr('Resizing is allowed. Click to lock the size (prevents accidental resizing).');
  btn.addEventListener('click', () => {
    pushHistory();
    inst.lockResize = !locked; // explicit per-part override (over the catalog default)
    renderProperties();
    invalidate();
  });
  row.append(l, btn);
  return row;
}

/** Numeric Move / Copy / Rotate / Scale for the current selection. Shown at the
 *  top of the properties panel whenever something transformable is selected. */

/** How dimension values are formatted, drawing-wide. */
const DIM_STYLE_OPTIONS = [['mm', 'Millimetres (mm)'], ['auto', 'Auto (mm / cm / m)'], ['cm', 'Centimetres (cm)'], ['m', 'Metres (m)'], ['usa', 'USA (mm + ft-in)']] as const;

/** The Properties tab with nothing selected: the drawing's own settings — the
 *  File ▸ Drawing Properties fields, edited in place (each change undoable). */
function renderDrawingSettings(body: HTMLElement): void {
  body.appendChild(sectionTitle('Drawing Properties'));
  const m = state.drawing.meta || {};
  const setMeta = (k: keyof DrawingMeta, v: string): void => {
    pushHistory();
    const meta: DrawingMeta = { ...(state.drawing.meta || {}), [k]: v.trim() || undefined };
    state.drawing.meta = Object.values(meta).some(Boolean) ? meta : undefined;
    updateTitle();
    renderProperties();
  };
  body.appendChild(textFieldRow('Customer', m.commessa || '', 'e.g. Belvedere Hotel', (v) => setMeta('commessa', v)));
  body.appendChild(textFieldRow('Project no.', m.descrizione || '', 'e.g. 2026-014', (v) => setMeta('descrizione', v)));
  body.appendChild(textFieldRow('Version', m.versione || '', 'e.g. Rev. 02', (v) => setMeta('versione', v)));
  body.appendChild(textFieldRow('Date', m.data || '', 'e.g. 28/06/2026', (v) => setMeta('data', v)));
  body.appendChild(textFieldRow('Author', m.autore || '', 'e.g. M. Rossi', (v) => setMeta('autore', v)));
  body.appendChild(selectRow('Drawing language', (['it', 'en', 'fr'] as const).map((l) => [l, UI_LANG_NAMES[l]] as const), state.drawing.language ?? 'it', (v) => {
    pushHistory();
    state.drawing.language = v as Lang;
    renderProductList(); // product names follow the drawing language
    renderProperties();
    invalidate();
  }));
  body.appendChild(selectRow('Dimension style', DIM_STYLE_OPTIONS, drawingDimStyle(state.drawing), (v) => {
    pushHistory();
    state.drawing.dimStyle = v as DimStyle;
    renderProperties();
    invalidate();
  }));
  const hint = document.createElement('div');
  hint.className = 'prop-empty';
  hint.textContent = tr('Select an object to see its own properties.');
  body.appendChild(hint);
}

export function propRow(prop: PropertyDef): HTMLElement {
  const row = document.createElement('div');
  row.className = 'prop-row';
  const l = document.createElement('label');
  l.textContent = prop.unit ? `${prop.label} (${prop.unit})` : prop.label;
  const input = document.createElement('input');
  if (typeof prop.value === 'number') {
    input.type = 'text';
    input.inputMode = 'decimal';
    input.value = String(prop.value);
    const unit = /^(mm|cm|m)$/i.test(prop.unit ?? '') ? (prop.unit!.toLowerCase() as LenUnit) : null;
    input.addEventListener('change', () => {
      const v = unit ? parseMeasure(input.value, unit) : evalFormula(input.value);
      if (v !== null) { pushHistory(); prop.value = v; input.value = String(Math.round(v * 100) / 100); invalidate(); }
    });
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') input.blur(); });
  } else {
    input.type = 'text';
    input.value = String(prop.value);
    input.addEventListener('change', () => { pushHistory(); prop.value = input.value; invalidate(); });
  }
  row.append(l, input);
  return row;
}

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));
}

// =====================================================================
// Open drawings / projects
// =====================================================================
async function openDrawing(): Promise<void> {
  const res = await platform.openDrawing();
  if (!res.ok) {
    if (res.error && res.error !== 'cancelled') void showAlert(res.error);
    return;
  }
  await applyImportedDrawing(res);
}

/**
 * Bring an imported drawing into the scene: a PDF (rasterised to a background
 * underlay) or a DXF (parsed to background geometry). Shared by the Import
 * button and by drag-and-drop of files from the file explorer.
 */
async function applyImportedDrawing(data: { fileName?: string; filePath?: string; dxfText?: string; pdfBase64?: string; imageDataUrl?: string }): Promise<void> {
  // PDF (rasterised) or a raster image (PNG/JPG): keep it as a background
  // underlay. Both go through the PDF-style transform/scale/delete tools.
  if (data.pdfBase64 || data.imageDataUrl) {
    try {
      const bgImage = await (data.pdfBase64
        ? renderPdfToBackground(data.pdfBase64)
        : imageToBackground(data.imageDataUrl!));
      pushHistory();
      revealImported();
      // Several underlays coexist; a new import is added, not replacing the
      // others. Cascade its position so repeated imports don't perfectly overlap.
      const n = bgImages().length;
      bgImage.id = genBgId();
      bgImage.name = data.fileName;
      if (n > 0) { bgImage.x += n * bgImage.widthMm * 0.06; bgImage.y -= n * bgImage.heightMm * 0.06; }
      (state.drawing.backgroundImages ??= []).push(bgImage);
      state.drawing.fileName = data.fileName;
      state.drawing.filePath = data.filePath;
      clearSelection(state);
      // Fit to ALL underlays, not just the new one, so an import never zooms away
      // from (and appears to lose) the images already on the work area.
      vp.fit(bgImagesBounds(), viewW(), viewH());
      renderProperties();
      invalidate();
    } catch (err) {
      void showAlert(tr('Failed to load image') + ': ' + (err as Error).message);
    }
    return;
  }
  if (data.dxfText != null) {
    try {
      const geom = parseDxf(data.dxfText);
      pushHistory(); // after a successful parse, before mutating — so the import is its own undo step
      revealImported();
      state.drawing.background = geom;
      bumpBackground();
      state.drawing.fileName = data.fileName;
      state.drawing.filePath = data.filePath;
      clearSelection(state);
      if (isFiniteBounds(geom.bounds)) {
        vp.fit(geom.bounds, viewW(), viewH());
      }
      renderProperties();
      invalidate();
    } catch (err) {
      void showAlert(tr('Failed to parse drawing') + ': ' + (err as Error).message);
    }
  }
}

/** Build a background underlay from a raster image data URL (its natural pixel
 *  size maps 1:1 to mm; the user can rescale it with the PDF-scale tool). */
function imageToBackground(dataUrl: string): Promise<BackgroundImage> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ dataUrl, x: 0, y: 0, widthMm: img.naturalWidth || 1, heightMm: img.naturalHeight || 1 });
    img.onerror = () => reject(new Error('Could not load the image.'));
    img.src = dataUrl;
  });
}

/** Add a raster image (data URL) to the drawing as a background underlay, exactly
 *  like an imported image: cascaded so repeated adds don't perfectly overlap, then
 *  the view fits all underlays. Unlike a file import it leaves the project's own
 *  file name/path alone (a paste isn't "opening" a document). */
/** An import never lands out of sight (`reconcileNewObjects`' rule, for the
 *  Imported layer it can't see): a hidden Imported layer is shown again, a
 *  locked one is named. Call after the import's `pushHistory`. */
function revealImported(): void {
  const l = importedLayer();
  if (l.hidden) {
    setLayerFlag(state.drawing, LAYER_ENV, l.id, 'hidden', false);
    dropLayerCache();
    flashToolStatus(tr('“{name}” was hidden: it is shown again, with what you just added.').replace('{name}', layerLabel(l)));
  } else if (l.locked) {
    flashToolStatus(tr('“{name}” is locked: what you just added can’t be selected until you unlock it.').replace('{name}', layerLabel(l)));
  }
}

async function addImageUnderlay(dataUrl: string, name: string): Promise<void> {
  const bgImage = await imageToBackground(dataUrl);
  pushHistory();
  revealImported();
  const n = bgImages().length;
  bgImage.id = genBgId();
  bgImage.name = name;
  if (n > 0) { bgImage.x += n * bgImage.widthMm * 0.06; bgImage.y -= n * bgImage.heightMm * 0.06; }
  (state.drawing.backgroundImages ??= []).push(bgImage);
  clearSelection(state);
  vp.fit(bgImagesBounds(), viewW(), viewH());
  renderProperties();
  invalidate();
}

/** Paste an image from the system clipboard as a background underlay. */
async function pasteImageFile(file: File): Promise<void> {
  try {
    await addImageUnderlay(await fileToDataUrl(file), 'Pasted image');
  } catch (err) {
    void showAlert(tr('Failed to paste image') + ': ' + (err as Error).message);
  }
}

/** Read a dropped File as a data URL (for raster images). */
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(new Error('Could not read the file.'));
    r.readAsDataURL(file);
  });
}

/** Base64-encode an ArrayBuffer (for handing a dropped PDF to the PDF renderer). */
function arrayBufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunk = 0x8000; // build in chunks to avoid call-stack limits on big files
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

/** Import a DXF/PDF/image File dropped from the file explorer (same as Import). */
export async function importDroppedFile(file: File): Promise<void> {
  const name = file.name;
  const dot = name.lastIndexOf('.');
  const ext = dot >= 0 ? name.slice(dot).toLowerCase() : '';
  // On a sheet, images and PDFs (first page) drop in as placed schematic images,
  // not as a model background.
  const shSch = activeSheet();
  if (shSch && ['.pdf', '.png', '.jpg', '.jpeg'].includes(ext)) {
    if (ext === '.pdf') { const bg = await renderPdfToBackground(arrayBufferToBase64(await file.arrayBuffer())); addSchematicImageFromData(shSch, bg.dataUrl, bg.widthMm, bg.heightMm); }
    else { const dataUrl = await fileToDataUrl(file); const sz = await imageSize(dataUrl); addSchematicImageFromData(shSch, dataUrl, sz.w, sz.h); }
    return;
  }
  if (ext === '.pdf') {
    await applyImportedDrawing({ fileName: name, pdfBase64: arrayBufferToBase64(await file.arrayBuffer()) });
  } else if (ext === '.png' || ext === '.jpg' || ext === '.jpeg') {
    await applyImportedDrawing({ fileName: name, imageDataUrl: await fileToDataUrl(file) });
  } else if (ext === '.dxf') {
    await applyImportedDrawing({ fileName: name, dxfText: await file.text() });
  } else if (ext === '.dwg') {
    try {
      const dxfText = await platform.dwgToDxf(await file.arrayBuffer(), name);
      await applyImportedDrawing({ fileName: name, dxfText });
    } catch (err) {
      void showAlert(trMessage(err instanceof Error ? err.message : String(err)));
    }
  } else {
    void showAlert(`${tr('Unsupported file')}: ${name}\n${tr('Drop a .dxf, .dwg, .pdf, .png or .jpg file to import.')}`);
  }
}

function openScaleDialog(): void {
  const input = $<HTMLInputElement>('scale-input');
  input.value = '';
  $('scale-error').textContent = '';
  $('scale-modal').classList.remove('hidden');
  setTimeout(() => input.focus(), 0);
}
function closeScaleDialog(): void {
  $('scale-modal').classList.add('hidden');
}
/** Resize the imported PDF so the two picked points span `mm` (scaling about the first). */
function applyPdfScale(): void {
  const bg = scaleTargetBg();
  const mm = parseMeasure($<HTMLInputElement>('scale-input').value, 'mm') ?? NaN;
  if (!bg || state.scalePts.length < 2) {
    closeScaleDialog();
    return;
  }
  if (!(mm > 0)) {
    $('scale-error').textContent = tr('Enter a positive distance (mm, or add a unit: cm / m / ft / in).');
    return;
  }
  const [p1, p2] = state.scalePts;
  const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
  if (dist < 1e-6) {
    $('scale-error').textContent = tr('The two points are too close together.');
    return;
  }
  const factor = mm / dist;
  pushHistory();
  bg.widthMm *= factor;
  bg.heightMm *= factor;
  bg.x = p1.x + (bg.x - p1.x) * factor; // scale about the first point
  bg.y = p1.y + (bg.y - p1.y) * factor;
  state.scalePts = [];
  closeScaleDialog();
  setTool('select');
  renderProperties();
  invalidate();
}

/** Open the "scale 1:X" dialog, prefilled with the current factor (or 50). */
function openScaleFactorDialog(): void {
  const bg = scaleTargetBg();
  if (!bg) return;
  const input = $<HTMLInputElement>('scalef-input');
  const paperW = bg.paperWidthMm ?? bg.widthMm;
  const cur = paperW > 0 ? bg.widthMm / paperW : 1;
  // Default to 50; if the PDF has already been scaled, show that factor instead.
  input.value = String(cur > 1.5 ? Math.round(cur) : 50);
  $('scalef-error').textContent = '';
  $('scalef-modal').classList.remove('hidden');
  setTimeout(() => { input.focus(); input.select(); }, 0);
}
function closeScaleFactorDialog(): void {
  $('scalef-modal').classList.add('hidden');
}
/** Set the PDF underlay's real-world size to its paper size × X (scale 1:X),
 *  keeping it centred so re-applying a factor doesn't compound. */
function applyScaleFactor(): void {
  const bg = scaleTargetBg();
  const x = parseFloat($<HTMLInputElement>('scalef-input').value);
  if (!bg) { closeScaleFactorDialog(); return; }
  if (!(x > 0)) {
    $('scalef-error').textContent = tr('Enter a positive scale factor.');
    return;
  }
  const paperW = bg.paperWidthMm ?? bg.widthMm;
  const paperH = bg.paperHeightMm ?? bg.heightMm;
  pushHistory();
  bg.widthMm = paperW * x;
  bg.heightMm = paperH * x; // centred at (bg.x, bg.y), so no position change
  closeScaleFactorDialog();
  renderProperties();
  invalidate();
}

// ---- Handles (docs/claude/handles.md, *Handles in 3D and in sections*) ----
// The selected object's handles — a room's or its selected feature's, a part's or
// a node object's, a drawn wall's or its feature's, an extruded shape's —, over
// the 3D view and in the plan's section views: press and drag one — or click it
// to arm it, and it follows the pointer until a click drops it — or type its
// figure and press Enter. Each is one undo step; Esc cancels and records none.
// What each handle edits, and the maths, are handles.ts's.

/** Export the current 3D scene as glTF (.glb), Wavefront OBJ, 3D DXF, STL, or
 *  USDZ (File ▸ Export). It always rebuilds from the live drawing into a
 *  throwaway viewer, disposed afterward, so the preview's own state (a pending
 *  refresh, hidden ceilings) never reaches the file. */
async function exportModel3d(format: 'glb' | 'obj' | 'dxf' | 'stl' | 'usdz'): Promise<void> {
  const name = exportName(state.drawing.fileName || doc.savedProjectName || 'model');
  const own = makeOffscreenViewer3d();
  if (!own) { flashStatus(tr('3D export needs WebGL, which is unavailable here.')); return; }
  try {
    let saved: string | null;
    if (format === 'glb') {
      saved = await platform.exportModel3d(new Uint8Array(await own.exportGLB()), name, 'glb');
    } else if (format === 'obj') {
      const { obj, mtl } = own.exportOBJ();
      saved = await platform.exportModel3d(obj, name, 'obj', mtl);
    } else if (format === 'dxf') {
      saved = await platform.exportModel3d(own.exportDxf3d(), name, 'dxf');
    } else if (format === 'stl') {
      saved = await platform.exportModel3d(own.exportSTL(), name, 'stl');
    } else {
      saved = await platform.exportModel3d(await own.exportUSDZ(), name, 'usdz');
    }
    if (saved) flashStatus(tr('Exported 3D {fmt}.').replace('{fmt}', format.toUpperCase()));
  } finally {
    own.dispose();
  }
}

/** Build a hidden Viewer3D loaded with the current scene, for exporting without
 *  opening the 3D overlay. Returns null if WebGL is unavailable. */
function makeOffscreenViewer3d(): Viewer3D | null {
  let v: Viewer3D | null = null;
  try {
    const cv = document.createElement('canvas');
    cv.width = 64; cv.height = 64;
    v = new Viewer3D(cv);
    const getTemplate = (templateId: string): ProductTemplate | undefined =>
      state.templates.find((t) => t.id === templateId);
    v.setScene(buildScene(state.drawing, getTemplate, view3dHiddenCategories(), hiddenLayerObjectIds()), false);
    return v;
  } catch {
    v?.dispose(); // don't leak the WebGL context if the scene build failed
    return null;
  }
}

// ======================================================================
// The view tabs and loading a drawing (a sheet itself: ui/sheetSpace.ts)
// ======================================================================
/** Return to the Model tab and rebuild the bottom tab bar (after new/open). */
export function resetViewTabs(): void {
  if (state.viewTab !== 'model') { closeView3d(); document.body.classList.remove('sheet-mode'); }
  view3d.view3dNeedsFrame = true; // another drawing: the 3D view frames it on its next open
  view3d.view3dTempHidden = new Set(); // and shows it whole
  view3d.view3dIsolatedParts = new Map();
  state.viewTab = 'model';
  setSchemSel(null);
  elecLetGo();
  sheetSpace.sheetEnterId = null;
  sheetSpace.sheetDrag = null;
  redrawBackdrop();
  renderViewTabs();
}

/** After loading a project, switch to the saved active tab if it still exists. */
function restoreActiveView(view: string | undefined): void {
  if (!view || view === 'model') return;
  if (view === '3d' || drawingSheets().some((s) => s.id === view)) setViewTab(view);
}

/** What bringing a drawing up to date (drawingUpgrade.ts) needs from the app. */
const UPGRADE_ENV: UpgradeEnv = {
  product: (inst) => productOf(inst),
  room: ROOM_ENV,
  layers: LAYER_ENV,
  get lang() { return curLang(); },
  trIn,
  elecModel: () => elecModelNow(),
  newId,
};

/** Install a loaded drawing into app state (shared by disk-open and cloud-load):
 *  spread every persisted field, run the load-time migrations, reset the view and
 *  clear the dirty flag. `name` is the display name for the title bar. */
export function applyLoadedDrawing(d: Drawing, name: string): void {
  dropUnderWay();
  doc.savedProjectName = name;
  clearSharpCrops(); // drop crops from the previous project
  // Spread the loaded drawing first so every persisted field survives the round
  // trip (sections, leaders, arcs, revision clouds/history, schedule tables, …);
  // then apply defaults for the required fields. A field-by-field allowlist here
  // silently dropped whatever wasn't listed.
  state.drawing = drawingWithDefaults(d);
  resetViewTabs();
  restoreActiveView(d.activeView);
  leaveEdit('plan', 'underlay');
  updateTitle();
  bumpBackground();
  // Every migration of an older file, its parts as a paste's: drawingUpgrade.ts.
  upgradeDrawing(state.drawing, UPGRADE_ENV);
  partsTab.currentLayerId = null; // the current layer belongs to the drawing it was chosen in
  clearSelection(state);
  constraintSolved(); // as saved is the baseline: opening a drawing moves nothing
  supportsBaseline();
  // …and so is what sits on its layers: the first object drawn after opening is
  // new (it lands on the current layer, a hidden layer receiving it is shown).
  dropLayerCache();
  layerKnown = { drawing: state.drawing, ids: layerIdSet() };
  freshCopies.clear();
  zoomFit();
  state.docDirty = false; // freshly opened project matches its saved copy
  updateTitle(); // no `*`
  renderProperties();
  invalidate();
  void regeneratePdfBaseRasters(); // rebuild PDF underlay rasters saved as source-only
}

/** Bounds enclosing all drawing content (background, instances, dimensions). */
export function contentBounds(): Bounds {
  const b = emptyBounds();
  // What is on a hidden layer is not drawn, so it does not count.
  const shown = <T extends { id: string }>(arr: T[] | undefined): T[] => (arr ?? []).filter((o) => !objHidden(o.id));
  if (!importedHidden()) {
    // Imported DXF background geometry (at its moved position).
    const dxf = bgGeomBounds();
    if (dxf) {
      expandBounds(b, dxf.min);
      expandBounds(b, dxf.max);
    }
    // Imported image / PDF underlays (their rotated corners).
    for (const bi of bgImages()) for (const c of pdfBoxCorners(bi)) expandBounds(b, c);
  }
  // Every drawing object, each kind by its adapter's outline (a new kind counts
  // once its adapter says its outline); a text box's leader besides.
  for (const kind of STORED_KINDS) {
    for (const o of shown(objectsOf(state.drawing, kind))) for (const c of outlineOf(OBJECTS, kind, o, o.id) ?? []) expandBounds(b, c);
  }
  for (const t of shown(state.drawing.texts)) if (t.leader) expandBounds(b, t.leader);
  return b;
}

/** Fill the view with the window between two world corners (instant, as
 *  AutoCAD's). A window too small to mean anything zooms in 2× on its centre. */
function zoomWindow(a: Vec2, b: Vec2): void {
  if (Math.abs(a.x - b.x) * vp.scale < 4 || Math.abs(a.y - b.y) * vp.scale < 4) {
    vp.zoomAt(vp.worldToScreen({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 }), 2);
  } else {
    vp.fit(boundsOfPoints(a, b), viewW(), viewH(), 0);
  }
  invalidate();
}

// ---- EXPLODE / JOIN ----

/** The plan's points of the current selection (none: nothing selected). */
function selectionPoints(): Vec2[] {
  const pts: Vec2[] = [];
  for (const m of selectedMovables()) pts.push(...m.verts());
  const dim = state.selectedDimId ? state.drawing.dimensions.find((d) => d.id === state.selectedDimId) : null;
  if (dim) pts.push(dim.a, dim.b);
  const conn = state.selectedConnId ? state.drawing.connections.find((c) => c.id === state.selectedConnId) : null;
  if (conn) pts.push(...(connectionPath(conn) ?? []));
  for (const id of [state.selectedSectionId, state.selectedRcpId]) if (id) pts.push(...(objectOutline(id) ?? []));
  return pts;
}

/** Frame the current selection on the plan (ZOOM ▸ Object). */
export function zoomToSelection(): boolean {
  const pts = selectionPoints();
  if (!pts.length) return false;
  zoomToPoints(pts);
  return true;
}

/** Zoom to selection (the Views toolbar): on the plan, or the 3D view's
 *  camera. */
function zoomToSelectionButton(): void {
  if (state.viewTab === '3d') { zoomToSelection3d(); return; }
  if (!zoomToSelection()) flashStatus(tr('Select something first'));
}

function zoomFit(): void {
  if (state.viewTab === '3d') { viewer3d?.reframe(); return; } // the 3D camera, not the hidden plan
  if (activeSheet()) { resetPaperView(); redrawBackdrop(); invalidate(); return; } // fit the page to the window
  const b = contentBounds();
  if (isFiniteBounds(b)) {
    vp.fit(b, viewW(), viewH());
  } else {
    vp.scale = 0.1;
    vp.tx = viewW() / 2;
    vp.ty = viewH() / 2;
  }
  invalidate();
}

// =====================================================================
// Printing
// =====================================================================
function boundsOfPoints(a: Vec2, b: Vec2): Bounds {
  return {
    min: { x: Math.min(a.x, b.x), y: Math.min(a.y, b.y) },
    max: { x: Math.max(a.x, b.x), y: Math.max(a.y, b.y) },
  };
}
function visibleBounds(): Bounds {
  const tl = vp.screenToWorld({ x: 0, y: 0 });
  const br = vp.screenToWorld({ x: viewW(), y: viewH() });
  return boundsOfPoints(tl, br);
}

/** Step 1: ask which region to print. */
function openPrintDialog(): void {
  // With a sheet tab active, Print outputs that sheet (WYSIWYG page) directly.
  const sheet = activeSheet();
  if (sheet) { void printSheet(sheet); return; }
  const body = $('print-body');
  body.innerHTML = '';
  const title = document.createElement('div');
  title.className = 'print-q';
  title.textContent = tr('What would you like to print?');
  body.appendChild(title);

  const choices: Array<[string, string, () => void]> = [
    ['🖥  Visible screen', 'Print exactly what is on screen now.', () => openPrintPreview(visibleBounds())],
    ['🗺  Whole drawing', 'Fit the entire drawing onto the page.', () => {
      const b = contentBounds();
      if (isFiniteBounds(b)) openPrintPreview(b);
      else $('print-error').textContent = tr('There is nothing to print yet.');
    }],
    ['▭  Window…', 'Draw a rectangle on the plan to print just that area.', () => {
      closePrintModal();
      setTool('print-window');
    }],
  ];
  for (const [label, desc, fn] of choices) {
    const btn = document.createElement('button');
    btn.className = 'print-choice';
    const b = document.createElement('b'), sp = document.createElement('span');
    b.textContent = tr(label);
    sp.textContent = tr(desc);
    btn.append(b, sp);
    btn.addEventListener('click', fn);
    body.appendChild(btn);
  }
  $('print-error').textContent = '';
  $('print-actions').innerHTML = '';
  const cancel = document.createElement('button');
  cancel.className = 'ghost';
  cancel.dataset.role = 'cancel';
  cancel.textContent = tr('Cancel');
  cancel.addEventListener('click', closePrintModal);
  $('print-actions').appendChild(cancel);
  $('print-title').textContent = tr('Print');
  $('print-modal').querySelector('.modal')?.classList.remove('preview-wide');
  $('print-modal').classList.remove('hidden');
}

/** Step 2: render the chosen region and preview it before printing. */
function openPrintPreview(bounds: Bounds): void {
  const img = renderRegion(state.drawing, bounds, {
    showGrid: state.showGrid,
    showSchedule: state.showSchedule,
    showHatches: state.showHatches,
    showBeams: state.showBeams,
    showCeilings: state.showCeilings,
    disciplineFilters: state.disciplineFilters,
    backgroundImgs: bgImageMap(),
    paint: layers().paint,
    hideImported: importedHidden(),
  });
  $('print-title').textContent = tr('Print preview');
  $('print-modal').querySelector('.modal')?.classList.add('preview-wide'); // fill ~80% of the screen
  const body = $('print-body');
  body.innerHTML = '';
  const wrap = document.createElement('div');
  wrap.className = 'print-preview';
  const el = document.createElement('img');
  el.src = img.dataUrl;
  wrap.appendChild(el);
  body.appendChild(wrap);
  $('print-error').textContent = '';

  $('print-actions').innerHTML = '';
  const back = document.createElement('button');
  back.className = 'ghost';
  back.dataset.role = 'cancel'; // Esc steps back to the page choice
  back.textContent = tr('Back');
  back.addEventListener('click', openPrintDialog);
  const print = document.createElement('button');
  print.className = 'primary';
  print.dataset.role = 'confirm';
  print.textContent = tr('Print');
  print.addEventListener('click', async () => {
    print.disabled = true;
    print.textContent = tr('Printing…');
    try {
      await platform.printImage(img.dataUrl, img.landscape, exportName(doc.savedProjectName || 'StenalCAD'));
    } catch (err) {
      $('print-error').textContent = tr('Print failed') + ': ' + (err as Error).message;
    }
    closePrintModal();
  });
  $('print-actions').append(back, print);
  $('print-modal').classList.remove('hidden');
}

function closePrintModal(): void {
  $('print-modal').classList.add('hidden');
}

/** Line colour per discipline — mirrors CONNECTION_COLORS in render.ts. */
export const DISCIPLINE_COLORS: Record<Discipline, string> = {
  electrical: '#ff8c1a', // orange (distinct from the yellow selection highlight)
  plumbing: '#4aa3ff',
  mechanical: '#9fb0c4',
  drainage: '#4db891',
  flooring: '#c88a5a', // warm floor-finish tone
  lighting: LIGHT_AMBER,
};
// The toggleable disciplines: the four connection disciplines plus flooring
// (which carries no connections but is shown/hidden the same way).
export const DISCIPLINES: readonly Discipline[] = DISCIPLINE_KINDS;
/** A discipline's name and its status-bar toggle's tooltip, in English. */
const DISCIPLINE_WORDS: Record<Discipline, string> = { electrical: 'Electrical', plumbing: 'Plumbing', mechanical: 'Mechanical', drainage: 'Drainage', flooring: 'Flooring', lighting: 'Lighting' };
const DISCIPLINE_TITLES: Record<Discipline, string> = {
  electrical: 'Show electrical connections & preparations',
  plumbing: 'Show plumbing connections & preparations',
  mechanical: 'Show mechanical connections & preparations',
  drainage: 'Show drainage connections & preparations',
  flooring: 'Show flooring areas',
  lighting: 'Show the lights, their tags and the Lighting table',
};
export function disciplineLabel(t: Discipline): string {
  return tr(DISCIPLINE_WORDS[t]);
}

/** A discipline picker, optionally with a colour dot that tracks the line colour. */
export function disciplineSelect(value: ConnectionType, onChange: (v: ConnectionType) => void, showDot = true): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'disc-select';
  const dot = document.createElement('span');
  dot.className = 'disc-dot';
  const sel = document.createElement('select');
  for (const t of CONNECTION_TYPES) {
    const opt = document.createElement('option');
    opt.value = t;
    opt.textContent = disciplineLabel(t);
    sel.appendChild(opt);
  }
  sel.value = value;
  const paint = () => (dot.style.background = DISCIPLINE_COLORS[sel.value as ConnectionType]);
  sel.addEventListener('change', () => {
    paint();
    onChange(sel.value as ConnectionType);
  });
  paint();
  if (showDot) wrap.append(dot, sel);
  else wrap.append(sel);
  return wrap;
}


// =====================================================================
// Wiring
// =====================================================================
function wire(): void {
  installIcons(); // the one icon set, before the layout measures the buttons
  installDialogKeys(); // Esc / Enter / backdrop, the same for every dialog
  // The empty model's start panel: a room, a plan, or a saved project.
  wireStartPanel();
  setPaperDimFormat((mm) => formatDimension(mm, drawingDimStyle(state.drawing))); // paper dimensions read like the model's
  initToolbar(); // build the dockable toolbar sections before wiring their buttons
  wireFileButtons();
  wireDrawEditBanner(); // the drawing editor's Done / Cancel over the canvas
  // In 3D they move the camera, not the hidden plan.
  wireZoomButtons();
  applyStaticI18n(); // cache the English source of the static chrome up front
  updateUndoButtons(); // greyed out from the start, so they say why from the start
  updateToolButtons();
  updateArrangeButtons();
  wireViewToggles();
  // Tab (or Shift+Tab) always moves to the next/previous input within the
  // properties panel, even when committing a value rebuilds the panel.
  wirePropertiesKeys();
  $('btn-delete').addEventListener('click', () => SPACES[liveSpace()].del());
  // Toolbar Arrange (z-order) buttons act on the selected instance / polygon.
  wireArrangeButtons();
  $('btn-undo').addEventListener('click', undo);
  $('btn-redo').addEventListener('click', redo);

  // Capture history around property-field edits (recorded only if something changed).
  wirePanelGestures();
  wireToolButtons();
  wireEditButtons();
  $('changelog-close').addEventListener('click', () => $('changelog-modal').classList.add('hidden'));
  $('deleted-close').addEventListener('click', () => $('deleted-modal').classList.add('hidden'));
  $('dp-apply').addEventListener('click', applyDrawingProperties);
  $('dp-cancel').addEventListener('click', closeDrawingProperties);
  wirePaperButtons();
  wireScaleDialogs();
  // One toggle button per discipline (all on by default). Each shows/hides that
  // discipline's connections AND preparations directly; the schedule overlay is
  // visible whenever at least one discipline is on (no separate master toggle).
  wireDisciplineFilters();
  wireSnapOrthoPolar();

  wireProductModal();

  // Command bar input: Enter or Space runs what was typed (Space = Enter, as in
  // AutoCAD — no command contains one); empty repeats the last tool; a distance /
  // coordinate goes to the drawing tool in progress (runCommand). Esc clears.
  // Either way focus goes back to the drawing, so the next click / keystroke acts
  // there.
  wireCommandBar();
  wireDimEditor();
  wireSpaceRelease();
  wireGlobalKeys();

  // Menu events from the main process.
  wireDocument();
  wirePermissions();
  wireMenuChannels();
  wire3dView();
  wireStandards();
  // A cloud-sync hiccup must never interrupt the user's work: surface it as a
  // transient status message (the app keeps running on the last cached copy).
  wireWindowEvents();
}

// ---- wire(): what each part of the chrome listens to, called in this order (an element's listeners fire in the order they were added) ----

function wireStartPanel(): void {
  $('es-room').addEventListener('click', () => {
    const room = state.templates.find((t) => t.polyRoom && t.category === 'sauna') ?? state.templates.find((t) => t.polyRoom);
    if (room) armPlacement(room);
  });
  $('es-import').addEventListener('click', () => { void openDrawing(); });
  $('es-tour-autocad').addEventListener('click', () => runTour('autocad'));
  $('es-tour-powerpoint').addEventListener('click', () => runTour('powerpoint'));
}

function wireFileButtons(): void {
  $('btn-open').addEventListener('click', openDrawing);
  $('btn-print').addEventListener('click', openPrintDialog);
}

function wireZoomButtons(): void {
  $('btn-zoom-in').addEventListener('click', () => {
    if (state.viewTab === '3d') { viewer3d?.dolly(1 / 1.2); return; }
    vp.zoomAt({ x: viewW() / 2, y: viewH() / 2 }, 1.2);
    invalidate();
  });
  $('btn-zoom-out').addEventListener('click', () => {
    if (state.viewTab === '3d') { viewer3d?.dolly(1.2); return; }
    vp.zoomAt({ x: viewW() / 2, y: viewH() / 2 }, 1 / 1.2);
    invalidate();
  });
  $('btn-zoom-fit').addEventListener('click', zoomFit);
  $('btn-zoom-selection').addEventListener('click', zoomToSelectionButton);
  $('btn-view3d').addEventListener('click', openStandardViews);
  $('btn-show-all-3d').addEventListener('click', showAll3d);
  $('btn-isolate-3d').addEventListener('click', isolateSelection3d);
  $('btn-isolate-reset-3d').addEventListener('click', showAll3d);
}

function wireViewToggles(): void {
  $('btn-grid-snap').addEventListener('click', () => {
    state.gridSnap = !state.gridSnap;
    $('btn-grid-snap').classList.toggle('active', state.gridSnap);
    persistViewPrefs();
  });
  $('btn-grid').addEventListener('click', () => {
    state.showGrid = !state.showGrid;
    $('btn-grid').classList.toggle('active', state.showGrid);
    persistViewPrefs();
    invalidate();
  });
  $('btn-hatches').addEventListener('click', () => {
    state.showHatches = !state.showHatches;
    $('btn-hatches').classList.toggle('active', state.showHatches);
    persistViewPrefs();
    invalidate();
  });
  $('btn-labels').addEventListener('click', () => {
    state.showLabels = !state.showLabels;
    $('btn-labels').classList.toggle('active', state.showLabels);
    persistViewPrefs();
    invalidate();
  });
  $('btn-beams').addEventListener('click', () => {
    state.showBeams = !state.showBeams;
    $('btn-beams').classList.toggle('active', state.showBeams);
    persistViewPrefs();
    invalidate();
  });
  $('btn-lux').addEventListener('click', () => toggleLux());
  $('btn-show-constraints').addEventListener('click', () => {
    state.showConstraints = !state.showConstraints;
    $('btn-show-constraints').classList.toggle('active', state.showConstraints);
    persistViewPrefs();
    invalidate();
  });
}

function wirePropertiesKeys(): void {
  $('properties-body').addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    const cur = document.activeElement;
    if (!cur || cur.tagName !== 'INPUT') return;
    const fields = () => [...$('properties-body').querySelectorAll('input')].filter((i) => !(i as HTMLInputElement).disabled);
    const before = fields();
    const idx = before.indexOf(cur as HTMLInputElement);
    if (idx < 0 || before.length === 0) return;
    e.preventDefault();
    const next = ((e.shiftKey ? idx - 1 : idx + 1) + before.length) % before.length;
    (cur as HTMLInputElement).blur(); // commit (may rebuild the panel synchronously)
    const el = fields()[next] as HTMLInputElement | undefined;
    if (el) { el.focus(); el.select?.(); }
  });
}

function wireArrangeButtons(): void {
  const zbtn = (id: string, op: ZOrderOp): void => $(id).addEventListener('click', () => {
    const r = RESTACK[liveSpace()];
    const ends = r.ends();
    if (!ends || (op === 'front' || op === 'forward' ? ends.top : ends.bottom)) return; // nothing to do: no undo step
    pushHistory();
    r.apply(op);
    renderProperties();
    invalidate();
  });
  zbtn('btn-front', 'front');
  zbtn('btn-forward', 'forward');
  zbtn('btn-backward', 'backward');
  zbtn('btn-back', 'back');
  $('btn-flip-h').addEventListener('click', () => flipSelection('h'));
  $('btn-flip-v').addEventListener('click', () => flipSelection('v'));
  $('btn-align-left').addEventListener('click', () => alignSelection('left'));
  $('btn-align-right').addEventListener('click', () => alignSelection('right'));
  $('btn-align-top').addEventListener('click', () => alignSelection('top'));
  $('btn-align-bottom').addEventListener('click', () => alignSelection('bottom'));
  $('btn-distribute-h').addEventListener('click', () => distributeSelection('h'));
  $('btn-distribute-v').addEventListener('click', () => distributeSelection('v'));
  $('btn-attach-left').addEventListener('click', () => attachSelection('left'));
  $('btn-attach-right').addEventListener('click', () => attachSelection('right'));
  $('btn-attach-top').addEventListener('click', () => attachSelection('top'));
  $('btn-attach-bottom').addEventListener('click', () => attachSelection('bottom'));
}

function wirePanelGestures(): void {
  $('properties-body').addEventListener('focusin', (e) => {
    const el = e.target as HTMLElement;
    if (el.tagName === 'INPUT' || el.tagName === 'SELECT') beginGesture();
  });
  $('properties-body').addEventListener('focusout', () => {
    commitGesture();
    // If a rebuild was deferred while this field was being edited, run it now that
    // focus has left (unless it just moved to another field in the panel).
    setTimeout(() => {
      if (!propRenderPending) return;
      const ae = document.activeElement as HTMLElement | null;
      const stillEditing = ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA') && $('properties-body').contains(ae);
      if (!stillEditing) { propRenderPending = false; renderProperties(); }
    }, 0);
  });
}

/** Every tool's button, one listener each from TOOL_BUTTONS: what a click does
 *  is its row's (`clickToolButton`), so no tool's button is wired by hand. */
function wireToolButtons(): void {
  for (const t of TOOLS) {
    const id = TOOL_BUTTONS[t];
    if (id) $(id).addEventListener('click', () => clickToolButton(t));
  }
}

/** The buttons that edit the selection itself: a room's or a shape's vertex
 *  edit (as a double-click enters it), Explode and Join. */
function wireEditButtons(): void {
  $('btn-edit-polygon').addEventListener('click', () => {
    if (editingVertexShape()) { leaveEdit('shape'); renderProperties(); invalidate(); return; }
    const shape = selectedEditableShape();
    if (!shape) return;
    if (state.tool !== 'select') setTool('select');
    enterShapeVertexEdit(shape);
  });
  $('btn-edit-room').addEventListener('click', () => {
    if (polyRoomEditInst()) { exitPolyRoomVertexEdit(); return; }
    const room = selectedInstance();
    if (!room || !isPolyRoom(room)) return;
    if (state.tool !== 'select') setTool('select');
    enterPolyRoomVertexEdit(room);
  });
  $('btn-explode').addEventListener('click', explodeSelection);
  $('btn-join').addEventListener('click', joinSelection);
}

/** A sheet's own buttons. Like the model toolbar: no explicit Select tool —
 *  clicking the active tool again toggles it off, back to selection mode. */
function wirePaperButtons(): void {
  $('btn-sch-text').addEventListener('click', () => toggleSchTool('text'));
  $('btn-sch-line').addEventListener('click', () => toggleSchTool('line'));
  $('btn-sch-conn').addEventListener('click', () => toggleSchTool('connection'));
  $('btn-sch-viewport').addEventListener('click', () => toggleSchTool('viewport'));
  $('btn-sch-image').addEventListener('click', () => insertSchematicImage());
  $('btn-sch-elec').addEventListener('click', () => insertElecView());
  $('btn-sch-elec-box').addEventListener('click', () => armElecTool('elecBox'));
  $('btn-sch-elec-wire').addEventListener('click', () => armElecTool('elecWire'));
}

function wireScaleDialogs(): void {
  $('scale-apply').addEventListener('click', applyPdfScale);
  $('scale-cancel').addEventListener('click', () => {
    closeScaleDialog();
    state.scalePts = [];
    invalidate();
  });
  $('scale-input').addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') applyPdfScale();
  });
  $('btn-pdf-scale-factor').addEventListener('click', openScaleFactorDialog);
  $('scalef-apply').addEventListener('click', applyScaleFactor);
  $('scalef-cancel').addEventListener('click', closeScaleFactorDialog);
  $('scalef-input').addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') applyScaleFactor();
  });
}

function wireDisciplineFilters(): void {
  const discWrap = $('discipline-filters');
  for (const d of DISCIPLINES) {
    const b = document.createElement('button');
    b.className = 'toggle active';
    // Built once, maybe before the saved language is applied: each language
    // switch's static pass translates them again from the English kept here.
    b.dataset.enTitle = DISCIPLINE_TITLES[d];
    b.title = tr(DISCIPLINE_TITLES[d]);
    const dot = document.createElement('span');
    dot.className = 'disc-dot';
    dot.style.background = DISCIPLINE_COLORS[d];
    const label = document.createElement('span');
    label.className = 'tb-label';
    label.dataset.en = DISCIPLINE_WORDS[d];
    label.textContent = tr(DISCIPLINE_WORDS[d]);
    b.append(dot, label);
    b.addEventListener('click', () => {
      state.disciplineFilters[d] = !state.disciplineFilters[d];
      b.classList.toggle('active', state.disciplineFilters[d]);
      // The connection-schedule overlay follows the connection disciplines only.
      state.showSchedule = CONNECTION_TYPES.some((t) => state.disciplineFilters[t]);
      // Hide/show this discipline's placed objects (e.g. plumbing drains).
      if (state.selectedId && isDisciplineHidden(selectedInstance()?.category ?? 'custom')) {
        state.selectedId = null; state.selectedIds = []; renderProperties();
      }
      // A hidden flooring discipline can't keep a floor (or its drain) selected.
      if (d === 'flooring' && state.disciplineFilters.flooring === false && state.selectedFloorId) {
        state.selectedFloorId = null; state.selectedFloorDrain = false; renderProperties();
      }
      // Nor hidden lights a light.
      if (d === 'lighting' && state.disciplineFilters.lighting === false && selectedFixtureNow()?.light) {
        dropFeature(state, 'fixture'); renderProperties();
      }
      invalidate();
    });
    discWrap.appendChild(b);
  }
  platform.onMenu('menu:copySchedule', () => { void copySchedule(); });
}

function wireSnapOrthoPolar(): void {
  $('btn-snap').addEventListener('click', () => {
    state.snapEnabled = !state.snapEnabled;
    $('btn-snap').classList.toggle('active', state.snapEnabled);
    if (!state.snapEnabled) { state.snapIndicator = null; state.centroidGuides = []; }
    persistViewPrefs();
    invalidate();
  });
  const POLAR_STEPS = [0, 45, 30, 15]; // click cycles; label shows the active step
  function setOrtho(on: boolean): void {
    state.orthoEnabled = on;
    $('btn-ortho').classList.toggle('active', on);
    persistViewPrefs();
    invalidate();
  }
  function setPolarStep(step: number): void {
    state.polarStep = step;
    const b = $('btn-polar');
    showPolarStep(b, step);
    persistViewPrefs();
    invalidate();
  }
  $('btn-ortho').addEventListener('click', () => setOrtho(!state.orthoEnabled));
  $('btn-polar').addEventListener('click', () => {
    const i = POLAR_STEPS.indexOf(state.polarStep);
    setPolarStep(POLAR_STEPS[(i + 1) % POLAR_STEPS.length]);
  });
  window.addEventListener('keydown', (e) => {
    const tgt = e.target as HTMLElement | null;
    if (tgt && (tgt.tagName === 'INPUT' || tgt.tagName === 'TEXTAREA' || tgt.tagName === 'SELECT') && !/^F\d+$/.test(e.key)) return;
    if (matchesShortcut(e, 'osnap')) { e.preventDefault(); $('btn-snap').click(); flashToolStatus(tr(state.snapEnabled ? 'Object snap on' : 'Object snap off')); }
    else if (matchesShortcut(e, 'grid')) { e.preventDefault(); $('btn-grid').click(); }
    else if (matchesShortcut(e, 'gridSnap')) { e.preventDefault(); $('btn-grid-snap').click(); flashToolStatus(tr(state.gridSnap ? 'Grid snap on' : 'Grid snap off')); }
    else if (matchesShortcut(e, 'dynInput')) {
      e.preventDefault();
      state.dynInput = !state.dynInput;
      setCursorReadout(state.dynInput);
      updateDistEntry();
      persistViewPrefs();
      flashToolStatus(tr(state.dynInput ? 'Cursor readout on' : 'Cursor readout off'));
      invalidate();
    }
    else if (matchesShortcut(e, 'properties')) {
      e.preventDefault();
      $('app').classList.toggle('props-hidden');
      persistViewPrefs();
    }
    else if (matchesShortcut(e, 'ortho')) { e.preventDefault(); setOrtho(!state.orthoEnabled); }
    else if (matchesShortcut(e, 'polar')) { e.preventDefault(); const i = POLAR_STEPS.indexOf(state.polarStep); setPolarStep(POLAR_STEPS[(i + 1) % POLAR_STEPS.length]); }
  });
  // Right-click the Snap button to choose which object-snap kinds are active.
  $('btn-snap').addEventListener('contextmenu', (e) => {
    e.preventDefault();
    const menu = $('ctx-menu');
    menu.innerHTML = '';
    // The same wording the AutoSnap tooltip shows, so a toggle and the marker it
    // governs can never name a snap differently.
    const kinds: SnapKind[] = ['endpoint', 'midpoint', 'center', 'intersection', 'quadrant', 'perpendicular', 'nearest', 'node'];
    for (const k of kinds) {
      const b = document.createElement('button');
      const label = tr(SNAP_KIND_LABEL[k]);
      const sync = () => { b.textContent = `${state.osnap[k] ? '☑' : '☐'} ${label}`; };
      sync();
      b.addEventListener('click', (ev) => { ev.stopPropagation(); state.osnap[k] = !state.osnap[k]; sync(); invalidate(); });
      menu.appendChild(b);
    }
    menu.style.left = `${Math.min(e.clientX, window.innerWidth - 190)}px`;
    menu.style.top = `${Math.min(e.clientY, window.innerHeight - 240)}px`;
    menu.classList.remove('hidden');
  });
}

function wireProductModal(): void {
  $('btn-new-product').addEventListener('click', () => openProductModal());
  // Read-only users (no `edit` claim) can't create catalogue products.
  if (!canEditStandards()) $('btn-new-product').style.display = 'none';
  $('np-add-part').addEventListener('click', npAddPart);
  $('np-cancel').addEventListener('click', closeProductModal);
  $('np-save').addEventListener('click', saveNewProduct);
  // The editor is a focused workspace: clicking the dimmed backdrop must NOT
  // discard it. Close only via Cancel or Create.

  $<HTMLInputElement>('product-search').addEventListener('input', (e) => {
    state.search = (e.target as HTMLInputElement).value;
    renderProductList();
  });
}

/** Space released: the end of a Space-pan, Enter for a handle or grip being
 *  worked, else the last tool again. (Undo/redo are handled by the Edit-menu
 *  accelerators, which fire even while editing fields.) */
function wireSpaceRelease(): void {
  window.addEventListener('keyup', (e) => {
    if (e.key !== ' ' || !spaceHeld) return;
    spaceHeld = false;
    if (canvas.style.cursor === 'grab') canvas.style.cursor = '';
    const target = e.target as HTMLElement;
    // A focused toolbar button would take the Space release as a click too.
    if (target.tagName === 'BUTTON') { e.preventDefault(); target.blur(); }
    if (spacePanned || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.tagName === 'SELECT') return;
    // A handle or a grip being worked: Space is Enter.
    if (state.distEntry != null ? commitTypedUnderWay() : finishUnderWay()) return;
    if (state.tool === 'select' && !activeSheet()) repeatLastTool();
  });
}

/** Space: held, a left drag pans; tapped (released with no pan), it repeats the
 *  last tool as Enter does — so the repeat waits for the release (the keyup,
 *  wireSpaceRelease). */
function keySpace(e: KeyboardEvent): boolean {
  if (e.key === ' ' && !e.ctrlKey && !e.metaKey && !e.altKey) {
    e.preventDefault();
    if (!e.repeat) { spaceHeld = true; spacePanned = false; }
    if (state.dragMode === 'none') canvas.style.cursor = 'grab';
    return true;
  }
  return false;
}

/** The plan's keys, one dispatcher: each key…() (ui/globalKeys.ts; Space's
 *  above) takes the key (true) or passes it on, tried in this order. A new key
 *  is its own function at its place here, never a branch inside another's. */
function wireGlobalKeys(): void {
  window.addEventListener('keydown', (e) => {
    if (keyEscArmedPick(e)) return;
    if (keyEscUnderWay(e)) return;
    if (keyInField(e)) return;
    if (keyEnterUnderWay(e)) return;
    if (keyHelp(e)) return;
    if (keySheet(e)) return;
    if (keyTitleEditor(e)) return;
    if (keyTypedEntry(e)) return;
    if (keyUndoRedo(e)) return;
    if (keyCut(e)) return;
    if (keyDuplicate(e)) return;
    if (keySelectAll(e)) return;
    if (keyGroup(e)) return;
    if (keyCopy(e)) return;
    if (keyNudge(e)) return;
    if (keyNudgeUpDown(e)) return;
    if (keyTextBoxTyping(e)) return;
    if (keySpace(e)) return;
    if (keyCycleSelection(e)) return;
    if (keyCycleConstraintPiece(e)) return;
    if (keyCommandLetter(e)) return;
    if (keyFinishWork(e)) return;
    if (keyEditMode(e)) return;
    if (keyDelete(e)) return;
    if (keyRotate(e)) return;
    if (keyRepeatLast(e)) return;
    keyEscapeTool(e);
  });
}

function wirePermissions(): void {
  if (!window.stenal) {
    // Help → Update permissions re-checked the claims with stenal.net; re-render
    // the permission-gated UI so a changed `edit` claim takes effect immediately.
    window.addEventListener('stenal-permissions', () => {
      const np = document.getElementById('btn-new-product');
      if (np) np.style.display = canEditStandards() ? '' : 'none';
      renderProductList();
      renderProperties();
    });
  }
}

function wireMenuChannels(): void {
  platform.onMenu('menu:newProduct', () => openProductModal());
  platform.onMenu('menu:print', openPrintDialog);
  platform.onMenu('menu:printAll', () => { void printAllSheets(); });
  platform.onMenu('menu:exportPdf', () => { void exportSheetPdf(); });
  platform.onMenu('menu:exportPdfAll', () => { void exportAllSheetsPdf(); });
  platform.onMenu('menu:exportGlb', () => { void exportModel3d('glb'); });
  platform.onMenu('menu:exportObj', () => { void exportModel3d('obj'); });
  platform.onMenu('menu:exportDxf3d', () => { void exportModel3d('dxf'); });
  platform.onMenu('menu:exportStl', () => { void exportModel3d('stl'); });
  platform.onMenu('menu:exportUsdz', () => { void exportModel3d('usdz'); });
  platform.onMenu('menu:openDrawing', openDrawing);
  platform.onMenu('menu:drawingProperties', openDrawingProperties);
  platform.onMenu('menu:undo', () => undo());
  platform.onMenu('menu:redo', () => redo());
  platform.onMenu('menu:copySelection', () => SPACES[liveSpace()].copy());
  platform.onMenu('menu:paste', () => SPACES[liveSpace()].paste());
  platform.onMenu('menu:delete', () => SPACES[liveSpace()].del());
  platform.onMenu('menu:cut', cutSelection);
  platform.onMenu('menu:duplicate', duplicateSelection);
  platform.onMenu('menu:selectAll', selectAll);
  platform.onMenu('menu:group', groupSelection);
  platform.onMenu('menu:ungroup', () => { void ungroupSelection(); });
  platform.onMenu('menu:zoomFit', zoomFit);
  // Every toolbar command in the menu (src/shared/menus.ts) clicks its button.
  for (const ch of menuChannels()) if (ch.startsWith(CMD_CHANNEL)) platform.onMenu(ch, () => runMenuButton(ch.slice(CMD_CHANNEL.length)));
  platform.onMenu('menu:focusCatalog', () => {
    if (activeSheet()) setViewTab('model');
    const search = $<HTMLInputElement>('product-search');
    search.focus();
    search.select();
    flashStatus(tr('Pick a product and drag it onto the drawing'));
  });
  platform.onMenu('menu:newSheet', () => (document.querySelector('.view-tab-add') as HTMLElement | null)?.click());
  platform.onMenu('menu:updateCatalog', () => { void updateCatalog(); });
  platform.onMenu('menu:deletedProducts', () => { void openDeletedProducts(); });
  platform.onMenu('menu:view3d', () => setViewTab('3d'));
  platform.onMenu('menu:view2d', () => setViewTab('model'));
  platform.onMenu('menu:resetToolbars', () => resetToolbar());
  platform.onMenu('menu:toolbarStyle', () => setToolbarStyle(toolbarStyle() === 'tabs' ? 'sections' : 'tabs'));
  platform.onMenu('menu:toolbarCallouts', () => {
    setToolbarCallouts(!toolbarCalloutsEnabled());
    flashStatus(tr(toolbarCalloutsEnabled() ? 'Button names now show when you rest on a toolbar.' : 'Button names on hover are off.'));
  });
  // A view preference, remembered across launches: no undo step.
  platform.onMenu('menu:showCeilings', () => {
    state.showCeilings = !state.showCeilings;
    persistViewPrefs();
    invalidate();
    flashStatus(state.showCeilings ? tr('Ceilings are now shown on every room.') : tr('Ceilings show on the selected room only.'));
  });
  platform.onMenu('menu:shortcuts', () => showShortcutsHelp());
  platform.onMenu('menu:tourAutocad', () => runTour('autocad'));
  platform.onMenu('menu:tourPowerpoint', () => runTour('powerpoint'));
  platform.onMenu('menu:lang-en', () => applyUiLang('en'));
  platform.onMenu('menu:lang-it', () => applyUiLang('it'));
  platform.onMenu('menu:lang-fr', () => applyUiLang('fr'));
}

function wire3dView(): void {
  $('btn-ceilings-3d').addEventListener('click', () => {
    view3d.view3dCeilingsHidden = !view3d.view3dCeilingsHidden;
    viewer3d?.setCeilingsHidden(view3d.view3dCeilingsHidden);
    $('btn-ceilings-3d').classList.toggle('active', view3d.view3dCeilingsHidden);
  });
  $('btn-xray-3d').addEventListener('click', toggleXray3d);
  $('btn-lights-3d').addEventListener('click', () => {
    if (viewer3d?.lightsBusy) return;
    view3d.view3dLightsOn = !view3d.view3dLightsOn;
    viewer3d?.setLightsOn(view3d.view3dLightsOn);
    $('btn-lights-3d').classList.toggle('active', view3d.view3dLightsOn);
  });
  window.addEventListener('resize', () => viewer3d?.resize());
  window.addEventListener('keydown', (e) => {
    // The 3D view's own keys, ahead of the plan's: Esc never leaves it (the
    // Model tab or Ctrl+2 does) — it ends an in-place tool, then deselects —, and
    // Tab cycles what is under the pointer. A dialog, a panel field and the
    // command bar keep theirs.
    if (state.viewTab === '3d') {
      const t = e.target as HTMLElement | null;
      if (e.defaultPrevented || topDialog() || (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable))) return;
      if (e.key === 'Escape') {
        e.preventDefault(); e.stopPropagation();
        // What is under way first: a figure typed for it, then the work itself.
        const esc = escUnderWay(state.distEntry != null);
        if (esc === 'entry') { state.distEntry = null; updateDistEntry(); }
        if (esc) return;
        escapeTool(); syncView3dSelection(); hover3dNow(); return;
      }
      if (matchesShortcut(e, 'cycleSelection') && state.tool === 'select' && cycle3d(e.shiftKey ? -1 : 1)) { e.preventDefault(); e.stopPropagation(); }
      return;
    }
    if (e.key !== 'Escape') return;
    if (activeSheet()) { if (sheetSpace.sheetEnterId) { sheetSpace.sheetEnterId = null; invalidate(); } else if (selectedViewport()) { setSchemSel(null); renderProperties(); invalidate(); } }
  }, true);
  renderViewTabs();
}

function wireWindowEvents(): void {
  platform.onBaserowError((message) => { baserowErrorSeq++; flashStatus(trMessage(message)); });
  // Spinner while the product catalogue syncs with the database.
  platform.onBaserowBusy((busy) => {
    $('db-spinner').classList.toggle('hidden', !busy);
    // Busy cursor while the database responds (e.g. after pressing Create).
    document.body.classList.toggle('busy', busy);
  });

  window.addEventListener('resize', resizeCanvas);
  // The dockable toolbar can add/remove rows, which grows/shrinks the canvas
  // grid area without firing a window resize. Watch the canvas box so its backing
  // store is re-sized to match — otherwise the freed strip keeps stale pixels
  // (a "ghost" of the old drawing) that never repaint.
  if (typeof ResizeObserver !== 'undefined') {
    let pending = false;
    const ro = new ResizeObserver(() => {
      if (pending) return;
      pending = true;
      requestAnimationFrame(() => { pending = false; resizeCanvas(); });
    });
    ro.observe(canvas);
  }
  // Dismiss the product context menu on any outside interaction.
  window.addEventListener('mousedown', (e) => {
    if (!(e.target as HTMLElement).closest('#ctx-menu')) hideCtxMenu();
  });
  window.addEventListener('blur', hideCtxMenu);
}

// =====================================================================
// Boot
// =====================================================================

function boot(): void {
  // The lights — their panels, a section, a sheet, the Lighting table — read the
  // catalogue's lighting products (the built-in generics until it has loaded).
  setLightCatalogue((id) => lightTemplate(id));
  wire();
  updateTitle();
  resizeCanvas();
  // Default view centred on origin.
  vp.scale = 0.1;
  vp.tx = viewW() / 2;
  vp.ty = viewH() / 2;
  // Reveal the main window (dismiss the splash) once the first catalogue load
  // settles — success or failure.
  loadProducts().finally(() => platform.notifyReady());
  void loadStandards();
  void loadViewPrefs(); // restore persisted grid / snap / ortho / polar toggles
  window.addEventListener('beforeunload', (e) => {
    if (!state.docDirty) return;
    e.preventDefault();
    e.returnValue = ''; // shows the browser/Electron "unsaved changes" prompt
  });
  renderProperties();
  requestAnimationFrame(frame);
  // The app is up: drop the boot screen (index.html's dark loading overlay). The
  // app's own dark background is under it, so there is no flash before the first
  // frame paints. On the web the login overlay already removed it if a sign-in was
  // needed; this covers the authenticated path and the desktop build.
  document.getElementById('boot-loading')?.remove();
}

boot();

